import Phaser from 'phaser';
import type { Hero, EnemyInstance, ConsumableDef, TomeId, TomeDefinition, GameStartPayload, MultiplayerGameDelta, GameEndPayload, OpponentDisconnectedPayload, CrossroadsModifierId } from '@roe/shared';
import { MultiplayerBattleAdapter } from './MultiplayerBattleAdapter.js';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import {
  generateWaveEnemies,
  scaleEnemy,
  BASE_ENEMIES,
  getAllBossDefIds,
  getZoneBossIds,
  rollShrineOffering,
  applyBoon,
  aggregateBoonEffects,
  onKillTickBoons,
  getBoonDef,
  CROSSROADS_MODIFIERS,
  scaleCrossroadsModifier,
  getCrossroadsScale,
  getBoonOrAltarDef,
  rollAltarReward,
  applyAltarReward,
  ALTAR_TIER_MULT,
  type BoonDef,
  type AltarRewardDef,
  type AltarTier,
  type ActiveBoon,
  type ActiveBoonEffects,
  getZoneForLevel,
  getZoneForInferno,
  ZONES,
  heroAttackEnemy,
  enemyAttackHero,
  getAttackInterval,
  getEffectiveStats,
  addXp,
  getRespawnTime,
  rollLoot,
  regenMana,
  generateSigil,
  generateSpecificSigil,
  getArchivistResistBonus,
  isStoryQuestUnlocked,
  formatAffix,
  WAVES_PER_ZONE,
  RARITY_COLORS,
  RARITY_SELL_MULT,
  ENEMY_SPAWN_DELAY,
  applyDebuff,
  processDebuffs,
  getEffectiveSpeed,
  isStunned,
  getBonusSpellDamageMult,
  HERO_CLASS_NAMES,
  XP_SHARE_MULT,
  GOLD_REVIVE_COST_PER_LEVEL,
  getTomeBonus,
  generateEquipment,
  TOME_DEFINITIONS,
  TOME_IDS,
  rollTomeChoices,
  TOME_ELITE_DROP_CHANCE,
  getTomeLevel,
  getAvailableTalentPoints,
  getSpentTalentPoints,
  MAX_TALENT_POINTS,
  getTalentBonusValue,
  getTalentBonusForStat,
  getTalentUpgradeInfo,
  getNextRankLabel,
  TALENT_UPGRADES,
  MAX_TOME_TYPES,
  allChosenTomesMaxed,
  CONSUMABLE_DEFS,
  getConsumableDef,
  generateTrophy,
  generateGoblinKingpin,
  getRelicScaledEffect,
  WARRIOR_MELEE_RANGE,
  SPELL_CAST_RANGES,
  ACHIEVEMENTS,
  ARMOR_CLASS_RESTRICTIONS,
  WEAPON_CLASS_RESTRICTIONS,
  TWO_HANDED_WEAPON_TYPES,
  getPassiveBonuses,
  calculateMitigation,
  getPassiveDef,
} from '@roe/shared';
import { GameStateManager, postLeaderboardToServer } from '../state/GameStateManager';
import { AuthService } from '../services/AuthService';
import { VFXManager } from '../effects/VFXManager';
import { SoundManager } from '../effects/SoundManager';
import { attachPendingBadge, attachFriendOnlineToasts } from '../ui/SocialNotifications';
import { MultiplayerService } from '../services/MultiplayerService';
import { InviteToast } from '../ui/InviteToast';
import { SocialHub } from '../ui/SocialHub.js';
import { FEATURE_MULTIPLAYER } from '../featureFlags.js';
import { drawSocialIcon, drawBanIcon, drawBattleCardGlyph, drawElitePackCardGlyph, drawHordeCardGlyph, drawShrineCardGlyph, drawGildedAltarCardGlyph, drawBoonRune, drawBaseRuneStoneSymbol, drawEternalRuneStoneSymbol, drawRuneStoneButtonFrame } from '../utils/glyphs';
import { wireButton } from '../utils/buttons';
import { buildEquipStatusAccent, formatEquipmentTooltipLines, getArmorTypeLabel as equipArmorLabel, getWeaponTypeLabel as equipWeaponLabel } from '../utils/heroEquip';
import { createItemTooltip } from '../utils/tooltip';
import { drawHeroFrameOrnament } from '../utils/heroFrameOrnament';
import { armoryFrameTier, CLASS_BASE_STATS, CLASS_STAT_GROWTH } from '@roe/shared';
import FRAME_DATA from '../animations/spriteFrameData.json';

interface HeroSprite extends Phaser.GameObjects.Sprite {
  heroRef: Hero;
  hpBar: Phaser.GameObjects.Rectangle;
  hpBarBg: Phaser.GameObjects.Rectangle;
  shieldGraphics: Phaser.GameObjects.Graphics;
  mpBar: Phaser.GameObjects.Rectangle;
  xpBar: Phaser.GameObjects.Rectangle;
  xpBarBg: Phaser.GameObjects.Rectangle;
  buffIconText: Phaser.GameObjects.Text;
  nameText: Phaser.GameObjects.Text;
  shadow: Phaser.GameObjects.Ellipse;
  /** Pixellab heroes with tapered silhouettes need the shadow lifted so the ellipse
   *  sits under the visible body mass rather than the thin toe-tips at row 127.
   *  0 for normal heroes; negative values raise the shadow. */
  shadowYOffset: number;
  lastAttackTime: number;
  lastAttackAnimTime: number;
  baseX: number;
  baseY: number;
  reviveBtn?: Phaser.GameObjects.Text;
  reviveTimerText?: Phaser.GameObjects.Text;
  auraSprites?: Record<string, Phaser.GameObjects.Sprite>;
  petrifyOverlay?: Phaser.GameObjects.Sprite;
  soulText?: Phaser.GameObjects.Text;
}

/** Hero pet / summon that occupies a formation slot but isn't a hero proper —
 *  no XP, no items, no talent procs. First user is the Sorcerer's Copper Golem.
 *  Shape mirrors the relevant subset of HeroSprite so the existing slot /
 *  positioning / aggro pipeline can handle both with minimal forking. */
interface SummonSprite {
  defId: 'copper_golem';
  ownerHeroId: string;
  sprite: Phaser.GameObjects.Sprite;        // real spritesheet body — idle/attack/hurt/dead anims
  hpBar: Phaser.GameObjects.Rectangle;
  hpBarBg: Phaser.GameObjects.Rectangle;
  nameText: Phaser.GameObjects.Text;
  shadow: Phaser.GameObjects.Ellipse;
  x: number;                                // mirror of sprite.x (kept in sync) so callers can read like a HeroSprite
  y: number;
  baseX: number;
  baseY: number;
  slotIdx: number;
  hp: number;
  maxHp: number;
  atk: number;
  defense: number;
  atkTimer: number;
  atkInterval: number;
  isDead: boolean;
  /** Animation phase — drives which anim is currently playing. Used to gate
   *  hurt-flash/attack-swing transitions so they don't stomp each other mid-frame. */
  animState: 'idle' | 'attack' | 'attack_heavy' | 'hurt' | 'dead';
  animLockUntil: number;                    // ms timestamp; while > now, idle won't auto-restart
  /** Light-swing counter — every Nth swing triggers the heavy chain-slam
   *  (Attack_2 anim) which deals more damage and rolls crit. */
  lightSwingCount: number;
  /** Last enemy this summon attacked — used by Sorcerer Arc Conduit synergy. */
  lastTargetId?: string;
}

interface PetSprite extends Phaser.GameObjects.Sprite {
  ownerRef: Hero;
  hpBar: Phaser.GameObjects.Rectangle;
  lastAttackTime: number;
}

interface EnemySprite extends Phaser.GameObjects.Sprite {
  enemyRef: EnemyInstance;
  hpBar: Phaser.GameObjects.Rectangle;
  hpBarBg: Phaser.GameObjects.Rectangle;
  nameText?: Phaser.GameObjects.Text;
  shadow: Phaser.GameObjects.Ellipse;
  targetX: number;
  /** Which lane (0=top, 1=mid, 2=bot) */
  lane: number;
  /** True if this is the Goblino event mob */
  isGoblino?: boolean;
  /** True if this is the Rot Bomber event mob (stage 10+ random walker that explodes on death) */
  isRotBomber?: boolean;
  /** Per-sprite right-edge clamp (tier-aware). Keeps the entire sprite on-screen
   *  for both initial spawn x and ranged back-off targetX. */
  maxRightX: number;
  /** Per-sprite left-edge clamp (tier-aware). Mirrors maxRightX so rear-flank
   *  ranged enemies that want to back off past x=0 still stand fully on-screen
   *  instead of hiding at x=-60 with half their sprite off the map. */
  minLeftX: number;
}

// Lane Y positions (3 lanes) — pushed down so characters stand on the battlefield ground
// Enemy spawn / movement lanes — aligned to the 4 grid rows (even-column Y positions)
// so incoming enemies visually face the front-row heroes in the same row.
const LANE_Y = [262, 318, 374, 430];

/** Lieutenant proper-names by faction. Each promoted Lieutenant adopts
 *  the corresponding name on its nameplate. Goblin is for the Horde of
 *  Goblins event lieutenant promotions if the system ever extends to
 *  events (currently event mobs aren't promoted). */
const LIEUTENANT_NAMES: Record<string, string> = {
  beast:   'Dreadfang',
  undead:  'Sevrak',
  orc:     'Vargûz',
  demon:   'Solbrand',
  goblin:  'Stinkfoot',
};
const BASE_X = 80;
const HERO_ARC_CENTER_X = 160;
const HERO_ARC_CENTER_Y = 400;
const HERO_ARC_RADIUS = 110;

// HoMM3-style hex grid — flat-top hexagons, offset layout.
// 4-row × 6-col grid (15 slots). Scaled 0.9× from the legacy 3-row layout so the extra
// cells fit the canvas and the painterly bg reads more. Row 0 of the earlier 5-row
// draft was cut — it ran off the top of several bgs. Grid also shifted right one hex
// and down half a hex so it plants squarely in the foreground of the painted terrain.
const HEX_R = 32;
const HEX_HS = 49;  // horizontal center-to-center (0.9× legacy)
const HEX_VS = 56;  // vertical center-to-center (0.9× legacy)
const HEX_OFS = 28; // odd-column vertical offset (HEX_VS / 2, matches flat-top tiling)
// Grid baseline: row 0 sits at y=262 (even cols) / y=290 (odd cols). Row 3 (bottom) lands
// at y=430 for even cols. Whole grid is shifted one hex right (x=109) for bg composition.
const GRID_BASE_Y = 262;
const GRID_BASE_X = 109;
// All hex cells: [col, row] pairs → actual (x, y)
const _HC = (ci: number, ri: number) => ({
  x: GRID_BASE_X + ci * HEX_HS,
  y: (ci % 2 === 0 ? GRID_BASE_Y : GRID_BASE_Y + HEX_OFS) + ri * HEX_VS,
});
// 19 hero slots across a 4-row, 6-col flat-top hex grid.
// Even columns (0, 2, 4) have 4 rows (0-3); odd columns (1, 3) have 3 rows (staggered);
// col5 has a single vanguard slot at its middle row.
// Layout + modifiers:
//   col0  back+ / back / back / back+   (−20% / −10% / −10% / −20% damage dealt)
//   col1  N, N, N                        all −5% damage, −5% incoming
//   col2  N, M, M, N                     N rows take less dmg + deal less; M is neutral
//   col3  M, M, M                        all neutral
//   col4  F, F-, F-, F                   F = +5% dmg + charge/leap, F- = charge/leap only
//   col5  V (row 1 only)                 −5% incoming + charge/leap
/** Build a 19-slot hex formation with configurable X anchor + column spacing.
 *  Y positions stay locked to the battlefield envelope (even-col rows: 262 /
 *  318 / 374 / 430; odd-col rows staggered by HEX_OFS) so every variant keeps
 *  every hex inside the playable area regardless of biome. Slot indices are
 *  preserved across all variants — SLOT_ROW_MULT, FRONT_ROW_SLOTS, keyboard
 *  F-keys, and save-slot hero assignments all continue to map correctly.
 *
 *  opts.gridX   — x anchor for col 0 (default 109)
 *  opts.hexHS   — horizontal center-to-center spacing (default 49)
 *  opts.zigzag  — if true, odd columns shift +20 px right (horizontal stagger) */
function buildFormation(opts: { gridX?: number; hexHS?: number; zigzag?: boolean } = {}): { x: number; y: number }[] {
  const gridX = opts.gridX ?? GRID_BASE_X;
  const hexHS = opts.hexHS ?? HEX_HS;
  const hc = (ci: number, ri: number) => ({
    x: gridX + ci * hexHS + (opts.zigzag && ci % 2 === 1 ? 20 : 0),
    y: (ci % 2 === 0 ? GRID_BASE_Y : GRID_BASE_Y + HEX_OFS) + ri * HEX_VS,
  });
  return [
    // col0 (×4)  —  0..3 : back+ / back / back / back+
    hc(0, 0), hc(0, 1), hc(0, 2), hc(0, 3),
    // col1 (×3)  —  4..6 : N
    hc(1, 0), hc(1, 1), hc(1, 2),
    // col2 (×4)  —  7..10 : N / M / M (main = slot 9) / N
    hc(2, 0), hc(2, 1), hc(2, 2), hc(2, 3),
    // col3 (×3)  —  11..13 : M
    hc(3, 0), hc(3, 1), hc(3, 2),
    // col4 (×4)  —  14..17 : F / F- / F- / F
    hc(4, 0), hc(4, 1), hc(4, 2), hc(4, 3),
    // col5 (×1)  —  18 : V (vanguard, row 1)
    hc(5, 1),
  ];
}

/** Forest grid prototype — 28-slot zigzag with Orcish-Wastes-style wider
 *  column spacing (gridX=89, hexHS=65), an added neutral middle column
 *  between the classic mids and the front, and a wider front row. Columns
 *  alternate 4 rows (even) / 3 rows (odd) the same way classic does, but
 *  with two extra columns (col6 front-proper and col7 vanguard). Total 28
 *  slots. Slot-number map:
 *    col0 (4 rows) —  0..3    back+ / back / back / back+
 *    col1 (3 rows) —  4..6    N
 *    col2 (4 rows) —  7..10   N / M / M / N
 *    col3 (3 rows) — 11..13   M
 *    col4 (4 rows) — 14..17   NEW neutral middle column (1.00 dmg / 1.00 inc)
 *    col5 (3 rows) — 18..20   F-  (front, charge/leap eligible, 1.00 dmg)
 *    col6 (4 rows) — 21..24   F / F+ (front, rows 0/3 = 1.05 dmg, rows 1/2 = 1.00)
 *    col7 (3 rows) — 25..27   V (advanced vanguard, 0.95 incoming)
 *  Existing Forest saves with slotIdx 0..18 still load to in-bounds slots —
 *  positions shift with the wider spacing but the save stays valid. Slot 18
 *  semantically shifts from classic "vanguard" to new "F-" (still forward);
 *  slot 14-17 shifts from classic "front F/F+" to new "neutral middle". Both
 *  changes are prototype trade-offs for the expanded strategic grid. */
const FOREST_GRID_X = 89;
const FOREST_HEX_HS = 65;
function buildForestFormation(): { x: number; y: number }[] {
  // Local hex-cell helper using Forest's wider spacing. Y stays on the same
  // 4-row / 3-row zigzag as classic so every hex stays inside the playable
  // envelope (y ∈ [262, 430]).
  const hc = (ci: number, ri: number): { x: number; y: number } => ({
    x: FOREST_GRID_X + ci * FOREST_HEX_HS,
    y: (ci % 2 === 0 ? GRID_BASE_Y : GRID_BASE_Y + HEX_OFS) + ri * HEX_VS,
  });
  return [
    // col0 (×4) — 0..3 : back+ / back / back / back+
    hc(0, 0), hc(0, 1), hc(0, 2), hc(0, 3),
    // col1 (×3) — 4..6 : N
    hc(1, 0), hc(1, 1), hc(1, 2),
    // col2 (×4) — 7..10 : N / M / M / N
    hc(2, 0), hc(2, 1), hc(2, 2), hc(2, 3),
    // col3 (×3) — 11..13 : M
    hc(3, 0), hc(3, 1), hc(3, 2),
    // col4 (×4) — 14..17 : NEW neutral middle (the "added row" the user wanted)
    hc(4, 0), hc(4, 1), hc(4, 2), hc(4, 3),
    // col5 (×3) — 18..20 : F- (front, charge/leap)
    hc(5, 0), hc(5, 1), hc(5, 2),
    // col6 (×4) — 21..24 : F+ / F / F / F+ (advanced front)
    hc(6, 0), hc(6, 1), hc(6, 2), hc(6, 3),
    // col7 (×3) — 25..27 : V (advanced vanguard)
    hc(7, 0), hc(7, 1), hc(7, 2),
  ];
}

/** Per-biome layout descriptor. Decouples slot positions, front-row
 *  eligibility, per-slot damage / incoming multipliers, fill order, and
 *  the per-wave swap budget so a single biome can opt into the wider
 *  Forest grid + move limit while every other biome keeps the legacy
 *  19-slot shape with unlimited moves. */
interface BiomeFormation {
  slots: readonly { x: number; y: number }[];
  frontRow: ReadonlySet<number>;
  dmgMult: Record<number, number>;
  incMult: Record<number, number>;
  fillOrder: readonly number[];
  /** Per-wave hero swap cap. Infinity = legacy unlimited swapping. */
  movesPerWave: number;
}

/** Per-biome slot positions. Forest is the prototype 28-slot expanded grid;
 *  every other biome stays at the classic 19. Anchor+spacing biomes call
 *  buildFormation() at module load; biomes with bespoke shapes (Demon Rift's
 *  spear-tip) declare the array inline. Slot indices + semantics preserved
 *  across every variant, y values always stay in the allowed envelope. */
const BIOME_FORMATIONS: Record<string, readonly { x: number; y: number }[]> = {
  // Forest — 28-slot expanded grid with Wastes-style spacing, neutral middle
  // column (14-17), wider front cluster (col5 F-, col6 F/F+, col7 F+ with
  // vanguard), and 1-move-per-wave cap. User's primary playground.
  zone_forest: buildForestFormation(),
  // All generic biomes now share the expanded 28-slot layout — user wanted
  // the new grid shape on every map to try it out in context. Overrides in
  // BIOME_*_OVERRIDE maps apply the same front-row / dmg / inc / moves-per-
  // wave config to all four. Only bespoke-shape biomes (rift, shadow) keep
  // their unique 19-slot layouts until they're redesigned too.
  zone_crypt:  buildForestFormation(),
  zone_wastes: buildForestFormation(),
  // Demon Rift — was the bespoke SPEAR-TIP layout (col0 back / col5 vanguard).
  // Swapped to the expanded 28-slot grid per user request so every biome
  // shares one spatial language. We can carve out a distinct Rift variant
  // later if it needs its own identity back.
  zone_rift:   buildForestFormation(),
  // Shadow Realm — was the bespoke FOUR-CORNER VANGUARDS layout. Same reason:
  // unified expanded grid across all biomes for consistency.
  zone_shadow: buildForestFormation(),
  // Compressed AoE nightmare → switched to 28-slot expanded grid for
  // consistency with the other generic biomes.
  zone_labyrinth: buildForestFormation(),
};

/** Per-biome rear-flank spawn chance (0-1). Rear flankers spawn at x=-30 and
 *  walk right toward the back row. Only Shadow Realm + Demon Rift carry real
 *  flank pressure for v1; other biomes stay right-side-only. */
const BIOME_REAR_FLANK_CHANCE: Record<string, number> = {
  zone_forest:    0,
  zone_crypt:     0,
  zone_wastes:    0,
  zone_rift:      0.30,
  zone_shadow:    0.45,
  zone_labyrinth: 0,
};

/** Default (baseline / fallback) — used by module-level lookups that haven't
 *  been re-wired yet to the scene-owned per-biome formation. */
const FORMATION_SLOTS = buildFormation();
// Charge / leap eligibility — only F (15, 16) and F+ (14, 17) slots. V (18) is
// purely defensive: -5% incoming, NO charge. The amber ➤ chevron in the formation
// overlay + the "Charge / Jump enabled" tooltip line both derive from this set.
const FRONT_ROW_SLOTS = new Set([14, 15, 16, 17]);

/** Shared overrides for every generic biome that uses the 28-slot
 *  buildForestFormation layout (Forest / Crypt / Wastes / Labyrinth).
 *  Bespoke-shape biomes (rift, shadow) still use the module-level legacy
 *  constants because their slot positions don't match the 28-slot grid. */
const EXPANDED_GRID_FRONT_ROW = new Set([
  // col5 F- (18-20) — front-row eligible, charge/leap/shield-bash
  18, 19, 20,
  // col6 F/F+ (21-24) — front-row eligible
  21, 22, 23, 24,
  // col7 most-right front row (25-27) — F+ damage + charge per user design
  25, 26, 27,
]);

/** Outgoing damage per slot for the expanded 28-slot grid.
 *  Steeper gradient per user balance pass — back rows now feel like a
 *  meaningful trade (more safety, much less damage) instead of a tiny
 *  -5/-10 % nudge. Latest re-classification: col1 joins the back zone,
 *  col2 unifies as pure N, col4 picks up the legacy N/M/M/N stagger.
 *
 *  Tiers (outgoing damage):
 *    back+ : 0.50 (-50 %)   col0 outer (0, 3)
 *    back  : 0.70 (-30 %)   col0 inner (1, 2), col1 (4, 5, 6)
 *    N     : 0.85 (-15 %)   col2 (7-10) full, col4 outer (14, 17)
 *    M     : 0.95 (-5 %)    col3, col4 inner (15, 16)
 *    F-    : 1.00 (base)    col5
 *    F     : 1.05 (+5 %)    col6 inner — vanguard rows (also -10 % inc)
 *    F+    : 1.10 (+10 %)   col6 outer — exposed forward
 *    Elite : 1.15 (+15 %)   col7 — most-forward, charge/leap eligible
 */
const EXPANDED_GRID_DMG_MULT: Record<number, number> = {
  // col0 — back+ (outer, deepest) / back (inner)
  0: 0.50, 3: 0.50,
  1: 0.70, 2: 0.70,
  // col1 — RECLASSIFIED back (was N). All three rows at -30 % so col1
  // reads as deep-back together with col0 instead of sitting one tier
  // ahead.
  4: 0.70, 5: 0.70, 6: 0.70,
  // col2 — RECLASSIFIED full N (was N/M/M/N). All four rows at -15 %.
  7: 0.85, 8: 0.85, 9: 0.85, 10: 0.85,
  // col3 — M
  11: 0.95, 12: 0.95, 13: 0.95,
  // col4 — RECLASSIFIED N/M/M/N (was all N). Outer rows neutral, inner
  // rows middle — picks up the stagger col2 used to carry, giving the
  // strategic mid-row a real spine.
  14: 0.85, 15: 0.95, 16: 0.95, 17: 0.85,
  // col5 — F- (front, baseline)
  18: 1.00, 19: 1.00, 20: 1.00,
  // col6 — F+ outer / F (vanguard) inner. Inner rows (22, 23) keep their
  // -10 % incoming; outer rows (21, 24) are exposed-forward and reward
  // the risk with bigger damage.
  21: 1.10, 22: 1.05, 23: 1.05, 24: 1.10,
  // col7 — ELITE ROW: most forward, biggest damage, charge/jump eligible.
  25: 1.15, 26: 1.15, 27: 1.15,
};

/** Incoming damage per slot for the expanded 28-slot grid. Vanguard reduction
 *  is now -10% globally (was -5% — user bumped it). Vanguard cells:
 *    - col6 rows 1, 2 (slots 22, 23) — "2 hexa in the middle of the fronts"
 *      per user clarification. Sit inside the front cluster so they remain
 *      charge/leap eligible; tank anchors hold the middle of the front line.
 *    - col7 (slots 25-27) — advanced vanguard, also F+ outgoing (stacked) */
const EXPANDED_GRID_INC_MULT: Record<number, number> = {
  0: 1.00, 1: 1.00, 2: 1.00, 3: 1.00,
  // col1 — RECLASSIFIED back (was N): no incoming reduction, matches
  // col0 back/back+ tier (back rows are already insulated by distance,
  // not by armour, so they don't get the -5 % the N tier carries).
  4: 1.00, 5: 1.00, 6: 1.00,
  // col2 — RECLASSIFIED full N (was N outer / M inner): -5 % incoming
  // across all four rows.
  7: 0.95, 8: 0.95, 9: 0.95, 10: 0.95,
  11: 1.00, 12: 1.00, 13: 1.00,
  // col4 — RECLASSIFIED N/M/M/N (was all N): outer rows (14, 17) keep
  // the -5 % N incoming; inner rows (15, 16) match the M tier's neutral
  // 1.00 incoming.
  14: 0.95, 15: 1.00, 16: 1.00, 17: 0.95,
  // col5 F- (18-20) — 1.00 incoming
  18: 1.00, 19: 1.00, 20: 1.00,
  // col6 F+/F (21-24) — outer rows stay neutral incoming; middle rows (22, 23)
  // are the NEW VANGUARD cells at -10% ("the two in the middle of the fronts").
  21: 1.00, 22: 0.90, 23: 0.90, 24: 1.00,
  // col7 ELITE ROW — -5% incoming (was -10%; user trimmed the defense in
  // exchange for the +10% outgoing buff above, so the slot reads as
  // offensive forward-elite rather than full vanguard).
  25: 0.95, 26: 0.95, 27: 0.95,
};

const BIOME_FRONT_ROW_OVERRIDE: Record<string, ReadonlySet<number>> = {
  zone_forest:    EXPANDED_GRID_FRONT_ROW,
  zone_crypt:     EXPANDED_GRID_FRONT_ROW,
  zone_wastes:    EXPANDED_GRID_FRONT_ROW,
  zone_rift:      EXPANDED_GRID_FRONT_ROW,
  zone_shadow:    EXPANDED_GRID_FRONT_ROW,
  zone_labyrinth: EXPANDED_GRID_FRONT_ROW,
};

const BIOME_DMG_MULT_OVERRIDE: Record<string, Record<number, number>> = {
  zone_forest:    EXPANDED_GRID_DMG_MULT,
  zone_crypt:     EXPANDED_GRID_DMG_MULT,
  zone_wastes:    EXPANDED_GRID_DMG_MULT,
  zone_rift:      EXPANDED_GRID_DMG_MULT,
  zone_shadow:    EXPANDED_GRID_DMG_MULT,
  zone_labyrinth: EXPANDED_GRID_DMG_MULT,
};

const BIOME_INC_MULT_OVERRIDE: Record<string, Record<number, number>> = {
  zone_forest:    EXPANDED_GRID_INC_MULT,
  zone_crypt:     EXPANDED_GRID_INC_MULT,
  zone_wastes:    EXPANDED_GRID_INC_MULT,
  zone_rift:      EXPANDED_GRID_INC_MULT,
  zone_shadow:    EXPANDED_GRID_INC_MULT,
  zone_labyrinth: EXPANDED_GRID_INC_MULT,
};

/** Per-wave swap budget. Every biome now caps at 1 swap per wave so the
 *  positioning mechanic applies everywhere. Goblin Kingpin trophy adds +2
 *  on top of the biome cap. */
const BIOME_MOVES_PER_WAVE: Record<string, number> = {
  zone_forest:    1,
  zone_crypt:     1,
  zone_wastes:    1,
  zone_rift:      1,
  zone_shadow:    1,
  zone_labyrinth: 1,
};

/**
 * Archon's Focus per-item max stacks. Each dropped copy ROLLS a cap within
 * its rarity band at generation time (stored on item.maxStacksRoll):
 *   rare      → 30-50
 *   epic      → 50-75
 *   legendary → 75-99
 * Two legendaries can end up at different caps (one rolled 82, another 94)
 * so the roll itself feels like part of the loot — rarity is the floor+ceiling,
 * the actual number is the prize. This helper prefers the per-item roll and
 * falls back to rarity midpoints for older saves dropped before the roll
 * system was added.
 */
function archonFocusMaxStacks(rarity: string | undefined, maxStacksRoll?: number): number {
  if (typeof maxStacksRoll === 'number' && maxStacksRoll > 0) return maxStacksRoll;
  // Back-compat: pre-roll drops land here. Use rarity midpoint so they aren't
  // punished for being old.
  switch (rarity) {
    case 'rare':       return 40;
    case 'epic':       return 62;
    case 'legendary':  return 87;
    default:           return 40;
  }
}
/** Outgoing-damage multiplier per slot — legacy 19-slot fallback.
 *   back+ (0.50) | back (0.70) | N (0.85) | M (0.95) | F+ (1.10) | F (1.05) | V (1.00)
 *  Re-tuned to match the steeper gradient applied to EXPANDED_GRID_DMG_MULT
 *  (back rows take a real -30/-50 % cost, middle/front tiers spread evenly
 *  through to elite-tier +15 %). Bespoke biomes still pull from this map
 *  via the per-biome override system. */
const SLOT_ROW_MULT: Record<number, number> = {
  // col0 back+ / back
  0: 0.50, 3: 0.50,
  1: 0.70, 2: 0.70,
  // col1 N
  4: 0.85, 5: 0.85, 6: 0.85,
  // col2 N (rows 0, 3) / M (rows 1, 2)
  7: 0.85, 10: 0.85,
  8: 0.95, 9: 0.95,
  // col3 M
  11: 0.95, 12: 0.95, 13: 0.95,
  // col4 F+ (rows 0, 3) / F (rows 1, 2)
  14: 1.10, 17: 1.10,
  15: 1.05, 16: 1.05,
  // col5 V
  18: 1.00,
};
/** Incoming-damage multiplier per slot.
 *   Front row (col4) — 0.90 (−10 % incoming) so melee classes can stay in
 *   melee without being shredded. Was 1.00 before the durability pass.
 *   Vanguard (col5) — 0.85 (−15 % incoming, was −10 %) — extra reward for
 *   committing to the deepest defensive slot.
 *   Other rows unchanged. */
const SLOT_INCOMING_MULT: Record<number, number> = {
  // col0 back+ / back
  0: 1.00, 3: 1.00, 1: 1.00, 2: 1.00,
  // col1 N
  4: 0.95, 5: 0.95, 6: 0.95,
  // col2 N (rows 0, 3) / M (rows 1, 2)
  7: 0.95, 10: 0.95,
  8: 1.00, 9: 1.00,
  // col3 M
  11: 1.00, 12: 1.00, 13: 1.00,
  // col4 F / F- — now -10 % incoming (was 1.00)
  14: 0.90, 15: 0.90, 16: 0.90, 17: 0.90,
  // col5 V — now -15 % incoming (was -10 %)
  18: 0.85,
};
/** Multiplier applied to damage a hero deals based on their formation slot. */
function getSlotDamageMult(slotIdx: number | undefined): number {
  if (slotIdx === undefined) return 1;
  return SLOT_ROW_MULT[slotIdx] ?? 1;
}
/** Multiplier applied to damage a hero RECEIVES based on their formation slot. */
function getSlotIncomingMult(slotIdx: number | undefined): number {
  if (slotIdx === undefined) return 1;
  return SLOT_INCOMING_MULT[slotIdx] ?? 1;
}
// Fill order by hero count (center-out). Default deploy starts at mid-center (slot 9, col2 row 2)
// and spreads through the middle columns before reaching F-/V, then N ring, then back/F corners.
const SLOT_FILL_ORDER = [
  9, 15, 18,              // core: mid-center, F- middle, V
  8, 12, 13, 16,           // inner mids + F- bottom
  11, 5, 6,                // col3 row 0, col1 mids
  14, 17, 7, 10, 4,        // F corners, col2 N rows, col1 top
  1, 2,                    // col0 back (rows 1, 2)
  0, 3,                    // col0 back+ corners
] as const;
// Only the 17 slot cells exist in the new grid — the old "full 6-col" visual background
// used for hover highlights now draws directly from FORMATION_SLOTS.
const HEX_GRID_CELLS: { x: number; y: number; slotIdx: number | null }[] =
  FORMATION_SLOTS.map((s, i) => ({ x: s.x, y: s.y, slotIdx: i }));
const ENEMY_SPAWN_X = 880;

/** Spell DEF mitigation — 80 % of physical mitigation. Used by boss magic
 *  abilities (hellfire, frost nova, void lance, shadow bolt, etc.) so that
 *  school resist (fire/frost/shadow/nature) carries meaningful weight instead
 *  of DEF covering most of the damage. Physical specials (Spearman javelin,
 *  Molten Shell counter, Retaliatory Strike) keep full `calculateMitigation`.
 *  DoT ticks (poison, bleed, non-fire burns) skip DEF entirely — unchanged. */
function spellMitigation(defense: number): number {
  return calculateMitigation(defense) * 0.8;
}

/**
 * Stay-scaling table. First stay is unchanged ("50 % more enemies, same
 * rewards"); subsequent stays pile on enemy HP/ATK + gold per kill so the
 * risk pays off without making stays a farm mode. Stay 5+ scales linearly
 * from the stay-4 row.
 *
 * enemyCountMult  — total multiplier on wave size (1.5× = +50 % enemies)
 * enemyHpMult     — applied to each spawned enemy's HP
 * enemyAtkMult    — applied to each spawned enemy's attack
 * goldRewardMult  — applied to gold credited per kill
 *
 * NOTE: per-kill loot-drop-chance bonus was removed — extra enemies per wave
 * already give more roll attempts, so a multiplier on top compounded into an
 * infinite-farm incentive. Item + sigil drop rates now match the base stage's.
 */
function getStayBonuses(stayCount: number): {
  enemyCountMult: number;
  enemyHpMult: number;
  enemyAtkMult: number;
  enemySpeedMult: number;
  enemyAtkSpeedMult: number;
  goldRewardMult: number;
} {
  if (stayCount <= 0) {
    return {
      enemyCountMult: 1, enemyHpMult: 1, enemyAtkMult: 1,
      enemySpeedMult: 1, enemyAtkSpeedMult: 1, goldRewardMult: 1,
    };
  }
  // Exponential per-stay scaling — every stay compounds on top of the last,
  // no cap. Move speed bumped to 1.15^n (was 1.10) so stays feel genuinely
  // frantic — doubles every ~5 stays instead of ~7. Attack speed stays on
  // the gentler 1.10^n curve so fast attackers don't machine-gun at high
  // stays. Gold reward flat ×1 — staying is a pure difficulty dial, not a
  // farming knob.
  // +20 % flat scaler on count / HP / ATK per user "make stay 20% harder"
  // pass — applied OUTSIDE the pow so each stay level is 20 % nastier than
  // the previous tuning instead of compounding into a cliff at stay 5+.
  // Speed / atk-speed left alone so the encounter doesn't go off the rails
  // at high stay counts.
  const STAY_DIFFICULTY = 1.20;
  return {
    enemyCountMult:    Math.pow(1.50, stayCount) * STAY_DIFFICULTY,
    enemyHpMult:       Math.pow(1.50, stayCount) * STAY_DIFFICULTY,
    enemyAtkMult:      Math.pow(1.30, stayCount) * STAY_DIFFICULTY,
    enemySpeedMult:    Math.pow(1.15, stayCount),
    enemyAtkSpeedMult: Math.pow(1.10, stayCount),
    goldRewardMult:    1,
  };
}

export class BattleScene extends Phaser.Scene {
  /** Persisted across scene restarts */
  // Public so MenuScene's volume slider (in the ESC overlay) can read/write
  // the same value the battle scene loads BGM at. Persisted to localStorage
  // (key `roe_volume`) so the player's volume preference survives F5 — the
  // static field initializer reads it once on script load. Default 0.45.
  public static savedVolume: number = (() => {
    try {
      const v = parseFloat(localStorage.getItem('roe_volume') ?? '');
      if (!Number.isNaN(v) && v >= 0 && v <= 1) return v;
    } catch { /* localStorage unavailable */ }
    return 0.45;
  })();
  private static savedMuted = false;
  /** 3-state audio cycle: 0 = music + SFX (default), 1 = music only, 2 = all off. */
  private static audioMode: 0 | 1 | 2 = 0;
  /** Static battle BGM reference — persists across scene restarts so music doesn't reset per level */
  private static battleBgm: Phaser.Sound.BaseSound | null = null;
  /** Chat overlay reference — lazy-loaded so SocialHub onWhisperRequested can
   *  call openWhisper() without routing through a global. */
  private chatOverlay: import('../ui/ChatOverlay.js').ChatOverlay | null = null;

  private gsm!: GameStateManager;
  private heroSprites: HeroSprite[] = [];
  /** Hero summons (golems / pets / etc.) that occupy a formation slot but
   *  aren't proper heroes. Targeted by enemies, deal damage, take damage,
   *  but don't level up, hold items, or trigger hero-only talents. */
  private summonSprites: SummonSprite[] = [];
  private enemySprites: EnemySprite[] = [];
  private petSprites: PetSprite[] = [];
  /** Active minions raised by Morveth's Raise Dead */
  private minionSprites: { sprite: Phaser.GameObjects.Sprite; cursedOverlay: Phaser.GameObjects.Sprite; hpBarBg: Phaser.GameObjects.Rectangle; hpBar: Phaser.GameObjects.Rectangle; hp: number; maxHp: number; atk: number; expiry: number; ownerId: string; atkTimer: number; atkInterval: number; animPrefix: string; smokeTimer: number }[] = [];

  // UI elements
  private goldText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  /** Top-bar Crossroads-path indicator — shows the active path name + how
   *  many zones remain in the 5-zone window. Hidden when no path is active. */
  private pathText!: Phaser.GameObjects.Text;
  private xpPenaltyText!: Phaser.GameObjects.Text;
  /** Per-wave swap-budget pip — visible only when the active biome has a
   *  finite cap (Forest = 1, classic biomes = Infinity → hidden). */
  private movesPipText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private lootText!: Phaser.GameObjects.Text;
  /** Last loot value refreshUI observed — used to detect deltas and flash the HUD on rune pickup */
  private prevLootValue = 0;
  /** Timestamp of the last rune-flash effect so rapid drops merge into one visible pulse */
  private lastRuneFlashMs = 0;
  private autoRunText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  /** Cached XP multiplier from gap penalty — recomputed each UI tick, used in killEnemy */
  private currentXpMult = 1;
  private lootBadge!: Phaser.GameObjects.Text;
  private questBadge!: Phaser.GameObjects.Text;
  private trophyDisplayElements: Phaser.GameObjects.GameObject[] = [];
  private baseSprite!: Phaser.GameObjects.Sprite;
  // (baseHpBar removed — merchant has no HP)
  /** Zone-cleared popup elements */
  private zoneClearedPopup: Phaser.GameObjects.GameObject[] = [];

  // DPS tracking
  private dpsWaveDamage: Map<string, Map<string, number>> = new Map(); // heroId → source → total dmg
  private dpsWaveStart = 0;
  private dpsHudObjects: Phaser.GameObjects.GameObject[] = [];

  // Battle state
  private waveActive = false;
  private waveSpawnQueue: EnemyInstance[] = [];
  private spawnTimer = 0;
  private manaRegenTimer = 0;
  private divineWrathTimer = 0;
  private autoSaveTimer = 0;
  /** Virtual game clock that respects speedMultiplier (used for attack timing) */
  private gameTime = 0;
  /** True when zone is cleared and player must manually advance/retreat (non-auto-run) */
  private zonePaused = false;
  private zoneClearedText: Phaser.GameObjects.Text | null = null;
  private heroTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  private baseTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  private vfx!: VFXManager;
  private sfx!: SoundManager;

  // Run tracking (for auto-run summary)
  private runGold = 0;
  private runLoot = 0;
  private runLootStart = 0;  // resources.loot snapshot at run start
  private runLootSpent = 0;  // runes spent during run (tome skips)
  private runXp = 0;
  private runItems = 0;
  /** Accumulated score — persists across zone transitions, only resets on death/new run */
  private static persistedRunScore = 0;
  /** Elite Pack node state. Set when the player picks the Elite Pack card at
   *  zone-clear, then read back on scene.restart so the flag survives the
   *  Battle scene reboot without going through serialization. Cleared when
   *  the pack completes (and reset on death/new run via resetRun hooks).
   *   count           — total elites rolled for this pack (14-28)
   *   wavesRemaining  — 2 at entry, ticks down on each wave clear
   *   waveOneSize     — fixed split so wave 1 + wave 2 sum to count even after a scene reload
   *   bossIds         — 2 distinct boss def ids rolled from the cross-zone pool,
   *                     one spawned with each wave (capstone "pack leaders")
   *   xpMult          — 2× during an elite pack to reward the extra risk */
  private static elitePackPending: {
    count: number;
    wavesRemaining: number;
    waveOneSize: number;
    bossIds: [string, string];
    xpMult: number;
  } | null = null;
  /** Horde of Goblins special-event state. Set when the player picks the Horde
   *  card on a milestone zone-clear, then read back on scene.restart. One wave
   *  only, spawned bidirectionally (mooks + elites streaming from BOTH edges),
   *  staggered over ~8 s so the opener isn't a single instant pile. Cleared on
   *  completion — `hordeClearedThisRun` on the player save then locks the card
   *  out of the choice pool for the rest of the run (one-shot event).
   *   mobCount     — 50 → 100 goblin mooks (stage 5 → 55+)
   *   eliteCount   — 10 → 20 goblin warlords (stage 5 → 55+)
   *   xpMult       — 2× for the risk
   *   spawnedMob   — runtime counter, ticked by the stagger timer
   *   spawnedElite — runtime counter, ticked by the stagger timer */
  private static hordePending: {
    mobCount: number;
    eliteCount: number;
    xpMult: number;
    spawnedMob: number;
    spawnedElite: number;
  } | null = null;
  /** Telurion's Lair special-event state. Set when the player picks the
   *  Lair card on a stage-30+ zone-clear (every 5 stages). Spawns N gold-
   *  tinted skeleton elites + 1 Telurion boss; on first kill drops the
   *  Stormcoil Charm sigil. Reward: 10× gold of normal stage gold.
   *   eliteCount  — 5 + 5 per 5-stage tier past 30 (5/10/15/...)
   *   bossSpawned — flips true once Telurion is in the wave
   *   spawnedElite — runtime counter for staggered spawn */
  private static telurionPending: {
    eliteCount: number;
    spawnedElite: number;
    bossSpawned: boolean;
  } | null = null;

  /** Shadowrealm endboss + endless-mode state. Set when the player advances
   *  into stage 50; clears when the run ends (wipe / new game). Lifecycle:
   *    1. enter stage 50 → flag set, background swaps, Shadowlord spawns
   *    2. boss dies      → endlessActive flips true, portal VFX plays,
   *                        wave system stops, ghost spawner takes over
   *    3. party wipe in endless → run ends like any other wipe (whole run)
   *  shadowlordKills tracks total ghost kills across the endless run for
   *  the per-mob +0.1 % HP/ATK ramp. */
  private static shadowrealmPending: {
    bossSpawned: boolean;
    bossDefeated: boolean;
    endlessActive: boolean;
    ghostKills: number;
    waveCount: number;
    nextSpawnTime: number;
  } | null = null;
  /** Accumulated runes earned during the run (persists across zone transitions, reset on defeat) */
  private static persistedRunLoot = 0;
  /** Loot snapshot at run start — persists across zone transitions so finalTokens counts the whole run */
  private static persistedRunLootStart = 0;
  /** Safety belt for the Divine Wrath cooldown expiry timestamp. `player._dwCdExpiry`
   *  is the primary source of truth, but underscore-prefixed transient fields have
   *  historically been stripped by some save codepaths (see CLAUDE.md gotcha). This
   *  static mirror survives scene.restart() without going through serialization and
   *  wins the greater-of compare on restore, so advance/stay never reads a stale 0. */
  private static persistedDwCdExpiry = 0;
  /** Stage level the player last cast Time Pull on. Static so it survives
   *  scene.restart() (stays + F5) — the once-per-zone gate must persist
   *  through stays. Reset to -1 (never used) when the player advances to a
   *  different stage (NEW zone) so the cast is available again. */
  private static persistedTimePullStage = -1;
  /** Last biome themeKey rendered. Survives scene.restart() so the biome
   *  transition cinematic only fires when entering a NEW biome (not on
   *  every wave / stay / Ctrl+Shift+R within the same biome). Cleared on
   *  fresh-run detection so the first biome of a new run still announces. */
  private static lastBiomeThemeKey: string | null = null;
  private runScore = 0;
  /** Wall-clock timestamp when the current zone started (`Date.now()`).
   *  Set at scene create and on `handleAdvance`; read on zone clear to
   *  evaluate the Speed-Run mastery challenges. */
  private zoneStartedAtMs = 0;
  // ── Focus target system ───────────────────────────────────────────
  // Click an enemy to mark it as the focus target. All heroes redirect
  // to the marked target until it dies or the player clears it (clicks
  // the same enemy again). Heroes have no range gate on auto-attacks
  // (see `performHeroAttack`) so distance doesn't matter — front-row
  // and back-row heroes alike swing/shoot at the focus. The marker is
  // a rotating gold ring + "▼ FOCUS" glyph drawn above the enemy.
  private focusedEnemyId: string | null = null;
  private focusMarker: { ring: Phaser.GameObjects.Graphics; label: Phaser.GameObjects.Text } | null = null;
  private lastKillerName = '';
  private lastDeathSource = '';

  // Base arrow attack timer
  private baseAttackTimer = 0;
  private speedMultiplier = 1;
  /** Whether combat is manually paused (Space bar or Pause button) */
  private isPaused = false;
  /** When x2/x5/x10 is active, gold/resources get a penalty; x2=10% x5=30% x10=40% */
  private speedPenaltyActive = false;
  /** Prevents repeated death overlay calls */
  private gameOver = false;
  /** True while tome choice popup is showing (pauses combat) */
  private tomeChoiceActive = false;
  /** Session-scoped log of recent book/talent choices (oldest first, cap 50). */
  private static choiceLog: Array<{ kind: 'book' | 'talent'; icon: string; label: string; detail: string; wave: number; time: number }> = [];
  /** True while talent choice popup is showing (pauses combat) */
  private talentChoiceActive = false;
  /** True while zone-cleared popup is showing */
  private zoneClearedActive = false;
  /** Key marking which (stage, wave, mode) tuple has already had its zone-clear
   *  score bonus applied. Prevents popup rebuilds (path-card click flow) from
   *  re-adding the bonus on every re-render. */
  private _zoneScoreCountedKey: string | null = null;
  /** Idempotency key for the per-wave score bonus — prevents double-counting
   *  if the wave-complete path fires twice for the same (level, wave, mode)
   *  tuple (race during stay / restart / F5). Cleared on resetRun + zone
   *  advance just like `_zoneScoreCountedKey`. */
  private _waveScoreCountedKey: string | null = null;
  /** Sticky cache for the zone-clear event-card roll. Each call to
   *  `showZoneClearedPopup` previously re-rolled `Math.random() < 0.10`, so a
   *  popup rebuild (path-card cancel, event-clear, etc.) silently dropped the
   *  card the player could see one frame earlier. Keying the roll to the same
   *  (stage, wave, mode) tuple as the score bonus means the event picker
   *  decides ONCE per zone clear and every re-show reuses that decision. */
  private _eventRollCache: { key: string; chosenIdx: number | null; pityFired: boolean } | null = null;
  /** Accumulator for the 1s popup-overlay watchdog in update(). */
  private _popupWatchdogTimer = 0;
  /** Zone-cleared popup was requested while a tome/talent choice was open — show it after */
  private pendingZoneCleared = false;
  /** Auto-run zone advance was requested while a tome/talent choice was open — execute after */
  private pendingAutoAdvance = false;
  // Elite Pack state lives as a static class field — see BattleScene.elitePackPending
  // below so it survives scene.restart() after the player picks the Elite Pack card.
  /** Queued talent choices (heroIndex, hero) for sequential popups */
  private pendingTalentChoices: { heroIndex: number; hero: Hero }[] = [];
  /** Number of tome choices that were blocked by another popup and must be shown after */
  private pendingTomeChoices = 0;
  // Per-popup reroll counter removed — Tome Insight is now per-RUN. The
  // reroll button reads directly from player.tomeRerollsUsed.
  /** Tome HUD icons (bottom center) */
  private tomeIconObjects: Phaser.GameObjects.GameObject[] = [];
  /** Snapshot of tome levels to detect changes (avoid rebuilding every frame) */
  private lastTomeSnapshot = '';
  /** Tome id the player just picked — read by refreshTomeIconRow on the next
   *  rebuild to fire a pulse animation on that specific HUD icon, then cleared.
   *  Survives scene.restart() via a static so the pulse still fires after the
   *  popup-chain + scene restart sequence that follows a boss-clear book pick. */
  private static justPickedTomeId: string | null = null;
  /** Separate tooltip for tome icons (avoids conflict with heroTooltip) */
  private tomeTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  /** Active consumable buff icons (top-left) */
  private consumableIconObjects: Phaser.GameObjects.GameObject[] = [];
  /** Snapshot to avoid rebuilding every frame */
  private lastConsumableSnapshot = '';
  /** Tooltip for consumable icons */
  private consumableTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  /** Tooltip for trophy icons */
  private trophyTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  /** Party buff countdown badge text objects — updated in-place each frame (avoids icon rebuild) */
  private partyBuffBadges: Phaser.GameObjects.Text[] = [];
  /** Potion bar slot objects (bottom HUD, between recruit button and tome icons) */
  private potionBarObjects: Phaser.GameObjects.GameObject[] = [];
  /** Current hover tooltip for the potion bar. Promoted to a class field so
   *  hotkey use (1/2/3/4) can clear it — refreshPotionBar destroys the bar
   *  objects but the closure-local tooltip would otherwise leak and stay
   *  painted over the new bar until the next mouse movement. */
  private potionBarTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  /** Per-frame-updated cooldown overlay for the Potion of Vitality / Mana
   *  slots — dim rect + countdown text shown while the CD is active.
   *  Recreated by `refreshPotionBar` whenever the bar layout changes.
   *  Pause-aware: the CD ticks down via `update()` only when `isPaused`
   *  is false, so opening a menu / pausing freezes the timer. */
  private vitalityCdOverlay: { dim: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } | null = null;
  private manaCdOverlay: { dim: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } | null = null;
  private static readonly POTION_CD_MS = 8000;
  /** Backwards-compat alias — older call sites still reference the old name. */
  private static readonly VITALITY_POTION_CD_MS = 8000;
  /** Remaining cooldown in ms — counts down to 0 in `update()` while
   *  the game isn't paused. Set to `POTION_CD_MS` when the corresponding
   *  potion is consumed. */
  private vitalityCdRemainingMs = 0;
  private manaCdRemainingMs = 0;
  private checkpointBtnGroup: Phaser.GameObjects.GameObject[] = [];
  private checkpointGlowTween: Phaser.Tweens.Tween | null = null;
  private deepCheckpointBtnGroup: Phaser.GameObjects.GameObject[] = [];
  private deepCheckpointGlowTween: Phaser.Tweens.Tween | null = null;
  // ── Divine Wrath activatable button ──
  private divineWrathBtnGroup: Phaser.GameObjects.GameObject[] = [];
  private divineWrathGlowTween: Phaser.Tweens.Tween | null = null;
  private divineWrathCooldown = 0;           // ms remaining on CD
  private divineWrathCdLabel: Phaser.GameObjects.Text | null = null;
  private readonly DIVINE_WRATH_CD = 210000; // 210s cooldown
  // ── Invocation cast bar (Aegis of Light, Time Pull) — bottom-right cluster.
  // MMO-style icon buttons that gate behind the matching keystone in the rune
  // tree (`aegisOfLightFlag` / `timePullFlag`). Both render as locked/greyed
  // when the talent isn't picked, and on cooldown after a cast. Cooldowns
  // tick down via the same delta loop as Divine Wrath. Once-per-zone gates
  // (e.g. Time Pull) use a separate flag that resets at startNextWave when
  // wave 1 spawns.
  private invocationBtnGroup: Phaser.GameObjects.GameObject[] = [];
  private avengingWrathCooldown = 0;
  private avengingWrathCdLabel: Phaser.GameObjects.Text | null = null;
  private avengingWrathUntil = 0;            // timestamp (Date.now ms) until which the +50 % damage buff applies
  private aegisOfLightCooldown = 0;
  private aegisCdLabel: Phaser.GameObjects.Text | null = null;
  private timePullCooldown = 0;
  private timePullCdLabel: Phaser.GameObjects.Text | null = null;
  private timePullUsedThisZone = false;
  private readonly AVENGING_WRATH_CD = 150000; // 150s — doubled per user feedback so the burst feels deliberate, not a per-wave reflex
  private readonly AVENGING_WRATH_DURATION = 8000; // 8s damage-buff window
  private readonly AEGIS_OF_LIGHT_CD = 120000; // 120s — doubled per user feedback (was 60s)
  private readonly TIME_PULL_CD = 180000;      // 180s baseline cooldown — doubled per user feedback (was 90s); once-per-zone gate still stacks on top
  /** Snapshot to avoid rebuilding potion bar every frame */
  private lastPotionBarSnapshot = '';
  /** Active party-wide buffs (from talent abilities like Eagle Eye group, etc.) */
  private partyBuffs: Array<{ id: string; name: string; icon: string; description: string; remainingMs: number; totalMs: number }> = [];
  /** Overlay graphics for party buff timer drain — updated each frame alongside badges */
  private partyBuffOverlays: Phaser.GameObjects.Graphics[] = [];
  /** Empty hero slot visual objects (for cleanup) */
  private emptySlotObjects: Phaser.GameObjects.GameObject[] = [];
  /** Active spell-priority popups — keyed by HeroSprite so every deployed hero can keep one open at once. */
  private spellPriorityPopups: Map<HeroSprite, {
    objects: Phaser.GameObjects.GameObject[];
    cdOverlays: { ability: import('@roe/shared').HeroAbility; text: Phaser.GameObjects.Text; dim: Phaser.GameObjects.Rectangle }[];
  }> = new Map();
  /** Blinking reminder shown while talent/book choices are pending */
  private pendingChoiceReminder: Phaser.GameObjects.Text | null = null;
  /** Left-side hero portrait frames */
  private heroPortraits: Array<{
    hs: HeroSprite;
    bg: Phaser.GameObjects.Rectangle;
    /** Stepped-ring vignette behind the sprite — lighter stone rim, near-black
     *  center. Drawn between `bg` (depth 20) and `portrait` (depth 22). */
    bgVignette: Phaser.GameObjects.Graphics;
    gfx: Phaser.GameObjects.Graphics;
    maskGfx: Phaser.GameObjects.Graphics;
    portrait: Phaser.GameObjects.Sprite;
    classIcon: Phaser.GameObjects.Text;
    hpStrip: Phaser.GameObjects.Rectangle;
    deadOverlay: Phaser.GameObjects.Rectangle;
    costText: Phaser.GameObjects.Text;
    /** Per-hero "X/Y" moves-left indicator overlaid on the portrait's top-right
     *  corner. Hidden when the active biome has Infinity move cap. */
    movesIndicator: Phaser.GameObjects.Text;
    /** Small ▶ at the top-left corner — affordance hint that the portrait
     *  can be clicked to open the hero's spell-priority popup. Pulses
     *  gently. Hidden when the hero is dead (click goes to gold-revive
     *  instead, which has its own coin glyph and doesn't need this hint). */
    priorityHint: Phaser.GameObjects.Text;
    wasDeadLastFrame: boolean;
  }> = [];
  /** Active poison traps placed by Sylvari — persistent until triggered or wave ends */
  private activePoisonTraps: Array<{
    sprite: Phaser.GameObjects.Sprite;
    x: number; y: number;
    damage: number; dotFraction: number; dotDuration: number;
    casterHs: HeroSprite;
  }> = [];
  /** Background music */
  private bgm: Phaser.Sound.BaseSound | null = null;
  /** False until full scene setup completes (prevents update() running on incomplete scene) */
  private sceneReady = false;
  /** Exclamation badge shown above vendor on inventory refresh */
  private merchantExclamBadge: Phaser.GameObjects.Text | null = null;
  /** Progressive "stay vignette" — each stay pushes the map edges deeper into
   *  shadow (thicker + darker rings). Redrawn on create() via drawBattlefield
   *  so it refreshes automatically after every stay / advance. */
  private stayVignetteGfx: Phaser.GameObjects.Graphics | null = null;
  /** Active Goblino sprite (null = none spawned) */
  private goblino: EnemySprite | null = null;
  /** Goblino lifetime remaining in ms */
  private goblinoTimer = 0;
  /** True while Goblino shop popup is active */
  private goblinoShopActive = false;
  /** True while merchant shop popup is active */
  private merchantShopActive = false;
  /** Rogue combo point tracker: heroId -> { targetRef, hits } */
  private comboTracker: Map<string, { targetRef: object; hits: number }> = new Map();
  /** Debounce timestamp for toggleMenu to prevent double-firing from ESC handlers */
  private lastToggleTime = 0;
  /** Hero selected for position swap / slot move */
  private swapSelectedSprite: HeroSprite | null = null;
  /** SummonSprite (Vorrak / golem) selected for position swap. Stored on
   *  its own field rather than widening swapSelectedSprite — the hero swap
   *  logic stays untouched, summon swap runs in a parallel branch in
   *  `handleSlotClick`. Set by clicking the golem; cleared by clicking
   *  empty space or completing the swap. */
  private swapSelectedSummon: SummonSprite | null = null;
  /** Per-wave move budget for summons. Vorrak gets 1 reposition per wave
   *  on his own counter — does NOT stack with hero move-budget tomes /
   *  trophies / talents. Reset to 0 each wave start. */
  private summonMovesUsedThisWave = 0;
  /** Top-left readout of the currently-selected hero's name during a swap.
   *  Visible only while `swapSelectedSprite` is set so the player can quickly
   *  confirm WHO they're moving when sprites overlap or hover obscures the
   *  portrait column. Cleared in `clearSwapSelection`. */
  private swapNameText: Phaser.GameObjects.Text | null = null;
  /** Tween driving the white glow pulse on the selected swap hero */
  private swapHighlight: Phaser.Tweens.Tween | null = null;
  /** Interactive zones over each formation slot, shown when a hero is selected */
  private formationSlotZones: Array<{ zone: Phaser.GameObjects.Zone; highlight: Phaser.GameObjects.Graphics; slotIdx: number }> = [];
  /** Persistent amber chevrons on F / F- / V slots — mark charge / jump eligibility.
   *  Alpha tracks formationGridGraphics (shown while a hero is selected for swap). */
  private frontRowChevrons: Phaser.GameObjects.Text[] = [];
  /** Grid graphics object — hidden by default, shown only when moving a hero */
  private formationGridGraphics: Phaser.GameObjects.Graphics | null = null;
  /** Active formation layout for this run's biome. 19 slots, same indices as
   *  the module-level FORMATION_SLOTS default — only (x, y) positions differ.
   *  Set in `create()` via `setBiomeFormation()` based on current zone theme. */
  private formationSlots: { x: number; y: number }[] = FORMATION_SLOTS.map((s) => ({ x: s.x, y: s.y }));
  /** Hex grid cells derived from `formationSlots` — same shape as the module-level
   *  HEX_GRID_CELLS default. Drives drawFormationGrid. */
  private hexGridCells: { x: number; y: number; slotIdx: number | null }[] =
    FORMATION_SLOTS.map((s, i) => ({ x: s.x, y: s.y, slotIdx: i }));
  /** Slots destroyed by Malakhar's Void Hex spell. Cleared on scene init.
   *  A slot in this set is permanently disabled for the run: no new hero
   *  can be placed into it, any hero already there is auto-evicted to the
   *  nearest open slot, and the grid renders the cell with a darkened /
   *  cracked overlay instead of the standard pale-white hex. */
  private voidHexedSlots: Set<number> = new Set();
  /** Per-slot Phaser graphics for the Void Hex destroyed-tile overlay.
   *  Indexed by slotIdx so we can clean each one up if the grid rebuilds
   *  during the run (e.g. biome change at zone advance). */
  private voidHexOverlays: Map<number, Phaser.GameObjects.Graphics> = new Map();
  /** Rear-flank spawn chance for this biome. 0 = right-side only. Sampled
   *  per spawn in `spawnEnemy`. Set by `setBiomeFormation()`. */
  private biomeRearFlankChance = 0;
  /** Active per-biome front-row eligibility (charge / leap / shield-bash gate)
   *  + outgoing/incoming damage multipliers + per-wave swap budget. Forest
   *  uses the 28-slot expanded grid; every other biome inherits the legacy
   *  19-slot defaults. Repopulated by `setBiomeFormation()` on scene init. */
  private activeFrontRow: ReadonlySet<number> = FRONT_ROW_SLOTS;
  private activeDmgMult: Record<number, number> = SLOT_ROW_MULT;
  private activeIncMult: Record<number, number> = SLOT_INCOMING_MULT;
  /** Per-wave swap cap. Infinity = unlimited (legacy biomes). Forest = 1. */
  private movesPerWaveCap = Infinity;
  /** Swaps used so far this wave. Reset to 0 in startNextWave. */
  private movesUsedThisWave = 0;
  /** Bobbing arrow above selected hero */
  private swapArrow: Phaser.GameObjects.Text | null = null;
  private swapArrowTween: Phaser.Tweens.Tween | null = null;
  /** "N left" tag floating under the swap arrow — shows the player's
   *  remaining per-wave move budget in-context when a hero is selected. */
  private swapArrowTag: Phaser.GameObjects.Text | null = null;

  // ── Inferno Mode ──
  /** True when scene was launched with inferno:true */
  private infernoMode = false;
  /** Current Inferno zone (1-based) */
  private infernoZone = 1;
  /** Score accumulated this Inferno run (resets on death, not on zone advance) */
  private infernoScore = 0;
  /** Base game level at which heroes entered permanent inferno (controls mob scaling) */
  private infernoEntryLevel = 39;

  // ── Multiplayer Mode ──
  /** True when launched in co-op or PVP multiplayer mode */
  private isMultiplayer = false;
  /** Which player slot this client occupies (1 = left, 2 = right) */
  private localPlayerIndex: 1 | 2 = 1;
  /** Adapter bridging socket events to scene callbacks */
  private mpAdapter: MultiplayerBattleAdapter | null = null;
  /** Last received multiplayer state snapshot */
  private mpLastDelta: MultiplayerGameDelta | null = null;
  /** Hero definitions for both players (from game-start payload) */
  private mpP1Heroes: import('@roe/shared').Hero[] = [];
  private mpP2Heroes: import('@roe/shared').Hero[] = [];
  /** Guard: ensures PlacementOverlay is only launched once per game session */
  private _placementLaunched = false;

  constructor() {
    super({ key: 'BattleScene' });
  }

  init(data?: { inferno?: boolean; infernoZone?: number; infernoScore?: number; infernoEntryLevel?: number; multiplayerPayload?: GameStartPayload }): void {
    this.isMultiplayer = !!(data?.multiplayerPayload);
    this.localPlayerIndex = data?.multiplayerPayload?.playerIndex ?? 1;
    this._placementLaunched = false;
    if (this.isMultiplayer) this.isPaused = false; // ensure clean state on each game start
    this.mpLastDelta = data?.multiplayerPayload?.gameState ?? null;
    this.mpP1Heroes = data?.multiplayerPayload?.player1Heroes ?? [];
    this.mpP2Heroes = data?.multiplayerPayload?.player2Heroes ?? [];
    // Tear down previous adapter (e.g. scene restart)
    this.mpAdapter?.stop();
    if (this.isMultiplayer) {
      this.mpAdapter = new MultiplayerBattleAdapter();
      this.mpAdapter.start({
        onStateUpdate: (delta: MultiplayerGameDelta) => this.applyMultiplayerDelta(delta),
        onGameEnd: (payload: GameEndPayload) => this._handleMultiplayerGameEnd(payload),
        onOpponentDisconnect: (payload: OpponentDisconnectedPayload) => {
          this._showDisconnectOverlay(payload.timeoutSeconds);
        },
      }, this.localPlayerIndex);
    } else {
      this.mpAdapter = null;
    }
    // Inferno trigger — either the legacy mid-run "Inferno Descent"
    // pathway (`data?.inferno === true`) OR the new save-type flag
    // (`gsm.player.infernoSave === true`). Both flip `this.infernoMode`
    // so every existing zone-selection / spawn / scoring branch keeps
    // working without a parallel code path.
    this.infernoMode = (data?.inferno ?? false) || GameStateManager.getInstance().player.infernoSave === true;
    // Safety: if launched as normal battle while an inferno run was left active (e.g. stale save flag),
    // abandon the run so the pre-inferno hero snapshot is restored before combat begins.
    if (!this.infernoMode) {
      const gsm = GameStateManager.getInstance();
      if (gsm.player.infernoRunActive) gsm.abandonInfernoRun();
    }
    // Preserve inferno progression across zone restarts (scene.restart passes data through)
    if (this.infernoMode) {
      // Inferno-save flow vs legacy mid-run inferno: when this is a
      // save-type inferno run, the player's currentLevel IS the inferno
      // zone number (stage 1 = inferno zone 1) and the entry level is 1
      // (heroes start at lv1, content already at endgame difficulty).
      // The legacy "Inferno Descent" path keeps its 39-default entry level.
      // NB: `init()` runs BEFORE `create()`, so `this.gsm` isn't assigned
      // yet on a fresh scene entry — read via the singleton directly so a
      // first-time "Enter Inferno" click doesn't crash with
      // `Cannot read properties of undefined (reading 'player')`.
      const gsmInit = GameStateManager.getInstance();
      const isInfernoSave = gsmInit.player.infernoSave === true;
      this.infernoZone       = data?.infernoZone       ?? (isInfernoSave ? Math.max(1, gsmInit.player.currentLevel) : 1);
      this.infernoScore      = data?.infernoScore      ?? 0;
      this.infernoEntryLevel = data?.infernoEntryLevel ?? gsmInit.player.infernoEntryLevel ?? (isInfernoSave ? 1 : 39);
    }
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.gsm = GameStateManager.getInstance();
    if (!this.gsm.isInitialized) throw new Error('BattleScene entered before GameStateManager.init() completed');
    this.cameras.main.setBackgroundColor('#0c0a12');

    // Fire off the score-panel leaderboard fetch — fills the cached top-6
    // entries shown in the in-battle score widget. Cached for the scene's
    // lifetime so we don't hit the API every frame.
    this.scoreLeaderboardCache = [];
    this.scoreLeaderboardLoaded = false;
    this.lastScorePanelSnapshot = '';
    this.fetchScoreLeaderboard();

    // Refresh daily / weekly quest sets if their UTC reset window has elapsed.
    // MenuScene also calls this on entry; doing it here too catches the case
    // where a player's run spans midnight UTC without ever bouncing back to
    // the lobby.
    this.gsm.maybeRollResets();

    // Mastery — start the zone clear-time stopwatch. Reset on each
    // `handleAdvance` so each new zone gets its own clock.
    this.zoneStartedAtMs = Date.now();
    // Focus target — fresh scene = fresh state. The previous scene's
    // marker objects were destroyed in the scene tear-down, but the
    // refs survive across `scene.restart()` so explicitly null them
    // out here to avoid leaking a stale focus into a new run.
    this.focusedEnemyId = null;
    this.focusMarker = null;

    // Chat overlay — right-side strip. The floating speech-bubble icon is
    // suppressed in-battle: chat is now driven by the menu-cluster button at
    // the top-right HUD so the area between Sage and the bottom bar reads
    // clean. Collapsed peek hides too (see ChatOverlay.renderLines).
    import('../ui/ChatOverlay.js').then(({ ChatOverlay }) => {
      if (!this.scene.isActive()) return;
      this.chatOverlay = new ChatOverlay(this, { anchor: 'right', sceneWidth: GAME_W, sceneHeight: GAME_H });
      this.chatOverlay.setIconVisible(false);
    }).catch((e) => console.warn('[chat] overlay load failed', e));
    this.heroSprites = [];
    this.enemySprites = [];
    this.petSprites = [];
    // Destroy any leftover minions from previous run
    for (const m of this.minionSprites) { try { m.sprite.destroy(); m.cursedOverlay.destroy(); m.hpBarBg.destroy(); m.hpBar.destroy(); } catch { /* ignore */ } }
    this.minionSprites = [];
    for (const ss of this.summonSprites) { try { ss.sprite.destroy(); ss.hpBar.destroy(); ss.hpBarBg.destroy(); ss.nameText.destroy(); ss.shadow.destroy(); } catch { /* ignore */ } }
    this.summonSprites = [];
    this.runGold = 0;
    this.runLoot = BattleScene.persistedRunLoot;  // carry over across zones within a run
    this.runLootStart = BattleScene.persistedRunLootStart;
    this.runLootSpent = 0;
    this.runXp = 0;
    this.runItems = 0;
    // Restore accumulated score from previous zones (only resets on death/new run)
    // In Inferno mode, runScore is local to this zone; infernoScore accumulates across zones via init()
    this.runScore = this.infernoMode ? 0 : BattleScene.persistedRunScore;
    this.gameOver = false;
    this.gameTime = 0;
    this.tomeChoiceActive = false;
    this.talentChoiceActive = false;
    this.zoneClearedActive = false;
    this.pendingZoneCleared = false;
    this.pendingAutoAdvance = false;
    this.goblinoShopActive = false;
    this.merchantShopActive = false;
    this._sigilEffectsCache = null;
    this._boonEffectsCache = null;
    this.pendingTalentChoices = [];
    // Drain any talent picks owed to heroes recruited at level 5/10/… (set by GSM.purchaseHero).
    // These must be queued before battle starts so the popup fires naturally between waves.
    for (let i = 0; i < this.gsm.player.heroes.length; i++) {
      const hero = this.gsm.player.heroes[i];
      const owed = hero.pendingRecruitTalents ?? 0;
      for (let k = 0; k < owed; k++) {
        this.pendingTalentChoices.push({ heroIndex: i, hero });
      }
    }
    this.pendingTomeChoices = 0;
    this.tomeIconObjects = [];
    this.lastTomeSnapshot = '';
    this.consumableIconObjects = [];
    this.lastConsumableSnapshot = '';
    this.partyBuffBadges = [];
    this.partyBuffOverlays = [];
    // Run-scoped party buffs (Arcane Might, Eagle Eye, War Cry, Regrowth-all) must reset per run —
    // switching save slots must not leak HoT icons or other party buffs from a prior run.
    this.partyBuffs = [];
    this.potionBarObjects = [];
    this.lastPotionBarSnapshot = '';
    this.checkpointBtnGroup = [];
    this.checkpointGlowTween = null;
    this.deepCheckpointBtnGroup = [];
    this.deepCheckpointGlowTween = null;
    this.divineWrathBtnGroup = [];
    this.divineWrathGlowTween = null;
    // Restore CD from persisted expiry timestamp. Read both the player-state
    // value AND the static mirror so advance/stay scene restarts don't reset
    // it even if the underscore field got scrubbed mid-save.
    const _dwExpiry = Math.max(
      (this.gsm.player as any)._dwCdExpiry ?? 0,
      BattleScene.persistedDwCdExpiry,
    );
    this.divineWrathCooldown = Math.max(0, _dwExpiry - Date.now());
    // Sister invocations (Avenging Wrath / Aegis of Light / Time Pull) live
    // as plain instance fields without persisted expiry timestamps. Phaser
    // reuses the scene singleton across scene.start, so without an explicit
    // reset here their cooldowns survive a defeat → Try Again restart and
    // the player respawns mid-cooldown. Per user "after defeat, reset the
    // cooldown of invocations etc.." — zero them out at every init() pass.
    // For mid-run scene.restart (advance/stay) this is also harmless since
    // those flows already cast the abilities at full speed (no persistence
    // path stored their values either way). Time Pull's once-per-zone gate
    // is handled separately below via persistedTimePullStage.
    this.avengingWrathCooldown = 0;
    this.aegisOfLightCooldown = 0;
    this.timePullCooldown = 0;
    // Void Hex destroyed-slot tracker — clear the set + tear down all
    // overlay graphics on every scene init so a defeat → Try Again or
    // a zone advance starts the next run with a fresh, fully-usable
    // formation grid. Scene singleton preserves field state otherwise.
    this.voidHexedSlots = new Set();
    for (const g of this.voidHexOverlays.values()) {
      const rune = g.getData('rune') as Phaser.GameObjects.Text | undefined;
      rune?.destroy();
      g.destroy();
    }
    this.voidHexOverlays.clear();
    // Restore persistent choice log so F5/reload/back-to-menu keeps the run's history
    BattleScene.choiceLog = Array.isArray(this.gsm.player.choiceLog)
      ? [...this.gsm.player.choiceLog]
      : [];
    this.divineWrathCdLabel = null;
    this.waveActive = false;
    this.waveSpawnQueue = [];
    this.spawnTimer = 0;
    this.zonePaused = false;
    this.goblino = null;
    this.goblinoTimer = 0;
    this.emptySlotObjects = [];
    this.comboTracker = new Map();
    this.sceneReady = false;

    this.vfx = new VFXManager(this);
    this.sfx = new SoundManager(this);
    this.startBgm();

    // ESC key: single handler via Phaser keyboard plugin (no DOM fallback to prevent double-fire)
    const canvas = this.game.canvas;
    canvas.setAttribute('tabindex', '1');
    canvas.focus();
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-ESC', () => this.toggleMenu());
      this.input.keyboard.on('keydown-SPACE', () => this.togglePause());
      // Invocation keybinds — Q/W/E/R for the 4 active-ability slots in the
      // bottom bar (Wrath / Avenge / Aegis / Time-Pull). Bound via the
      // canvas keydown event below using `event.code` (physical scan codes)
      // so AZERTY's A/Z/E/R positions trigger the same slots — Phaser's
      // keydown-<letter> events go through `event.keyCode` which is
      // layout-dependent on most browsers and breaks AZERTY.
      // Potion keybinds — 1/2/3/4 uses consumable slots. Phaser's ONE..FOUR events fire on
      // the physical key codes (Digit1..Digit4), so French AZERTY (& é " ') works the same.
      this.input.keyboard.on('keydown-ONE',   () => this.useConsumableSlot(0));
      this.input.keyboard.on('keydown-TWO',   () => this.useConsumableSlot(1));
      this.input.keyboard.on('keydown-THREE', () => this.useConsumableSlot(2));
      this.input.keyboard.on('keydown-FOUR',  () => this.useConsumableSlot(3));
      // Numpad variants for laptop users who prefer the numeric pad
      this.input.keyboard.on('keydown-NUMPAD_ONE',   () => this.useConsumableSlot(0));
      this.input.keyboard.on('keydown-NUMPAD_TWO',   () => this.useConsumableSlot(1));
      this.input.keyboard.on('keydown-NUMPAD_THREE', () => this.useConsumableSlot(2));
      this.input.keyboard.on('keydown-NUMPAD_FOUR',  () => this.useConsumableSlot(3));
      // Hero-select hotkeys — F1/F2/F3 route to handleHeroSwapClick so users can pick
      // the right hero even when sprites overlap. Preventing the browser default is
      // crucial (F1 = Help, F3 = Find) so the game keeps focus.
      const selectHeroByIndex = (i: number, ev?: KeyboardEvent) => {
        ev?.preventDefault();
        const deployedIdx = this.gsm.player.deployedHeroes[i];
        if (deployedIdx === undefined) return;
        const hero = this.gsm.player.heroes[deployedIdx];
        if (!hero) return;
        const hs = this.heroSprites.find((h) => h.heroRef.id === hero.id);
        if (hs && !hs.heroRef.permanentlyDead) this.handleHeroSwapClick(hs);
      };
      this.input.keyboard.on('keydown-F1', (ev: KeyboardEvent) => selectHeroByIndex(0, ev));
      this.input.keyboard.on('keydown-F2', (ev: KeyboardEvent) => selectHeroByIndex(1, ev));
      this.input.keyboard.on('keydown-F3', (ev: KeyboardEvent) => selectHeroByIndex(2, ev));
    }
    // Ensure keyboard focus when user clicks the game
    this.input.on('pointerdown', () => canvas.focus());

    // Right-click anywhere on the canvas cancels an active swap selection.
    // Distinct from clicking your own portrait/sprite (left-click toggles)
    // so a player who picked the wrong hero can quickly bail without
    // accidentally moving them by left-clicking somewhere on the grid.
    // Also: suppress the browser context menu over the game canvas so the
    // native right-click menu doesn't pop up while playing.
    canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown() && (this.swapSelectedSprite || this.swapSelectedSummon)) {
        this.clearSwapSelection();
      }
    });

    this.setBiomeFormation();
    this.drawBattlefield();
    this.createUI();

    // Invocation keybinds — Q/W/E/R bound to the 4 invocation slots
    // (Wrath / Avenge / Aegis / Time-Pull) using `event.code` so the
    // physical top-row keys trigger the same slots regardless of keyboard
    // layout (AZERTY's A/Z/E/R sit at KeyQ/KeyW/KeyE/KeyR). Suppressed
    // while a menu overlay (Heroes / Inventory / Quests / Tree / Books /
    // Shop) is active so a player typing Q to flip from inventory back to
    // battle doesn't accidentally fire Wrath. The legacy menu-nav
    // shortcuts on Q/W/E/R/T were retired per user feedback — those nav
    // buttons are mouse-only now so the invocation slots have a clean
    // claim on the top-row keys.
    const OVERLAY_SCENES = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene', 'PassiveTreeScene', 'BookBanScene', 'QuestScene'];
    const isOverlayOpen = () => OVERLAY_SCENES.some((k) => this.scene.isActive(k));
    const handleInvocationKey = (e: KeyboardEvent) => {
      if (this.tomeChoiceActive || this.talentChoiceActive || this.zoneClearedActive || this.gameOver) return;
      // Invocations now usable in inferno mode (player request — keystone
      // bursts make endgame waves more recoverable). Divine Wrath / Avenge
      // / Aegis / Time-Pull all gated only by their own per-cast cooldowns.
      if (isOverlayOpen()) return;
      switch (e.code) {
        case 'KeyQ':
          if (this.divineWrathCooldown <= 0) this.fireDivineWrath();
          break;
        case 'KeyW':
          this.castAvengingWrath();
          break;
        case 'KeyE':
          this.castAegisOfLight();
          break;
        case 'KeyR':
          this.castTimePull();
          break;
        default:
          return;
      }
    };
    canvas.addEventListener('keydown', handleInvocationKey);
    this.events.once('shutdown', () => {
      canvas.removeEventListener('keydown', handleInvocationKey);
      this.closeSpellPriorityPopup();
      this.mpAdapter?.stop();
      for (const p of this.heroPortraits) {
        p.bg.destroy(); p.gfx.destroy(); p.maskGfx.destroy(); p.portrait.destroy(); p.classIcon.destroy(); p.hpStrip.destroy();
        p.deadOverlay.destroy(); p.costText.destroy(); p.movesIndicator.destroy(); p.priorityHint.destroy();
      }
      this.heroPortraits = [];
    });

    this.spawnHeroes();
    this.drawEmptyHeroSlots();
    this.createTomeIcons();
    this.refreshPotionBar();
    this.refreshTrophyDisplay();
    {
      const lvl = this.gsm.player.currentLevel;
      // Snapshots only matter for standard saves (rune-stone respec from
      // stage 10/30). Inferno saves use a different progression curve and
      // the snapshot points don't apply.
      if (!this.infernoMode) {
        if (lvl === 10) this.gsm.captureStage10Snapshot();
        if (lvl === 30) this.gsm.captureStage30Snapshot();
      }
      // Checkpoint + invocation HUD buttons render in BOTH standard and
      // inferno modes now (per user request — endgame players want their
      // keystone bursts and rune-stone checkpoints available in inferno
      // too). Divine Wrath HUD button stays disabled regardless of mode —
      // see comment below.
      this.createCheckpointButton(); this.createDeepCheckpointButton();
      // Divine Wrath HUD button DISABLED — superseded by the Avenging Wrath
      // invocation in the rune tree (same offensive-burst niche). The
      // underlying keystone (`keystoneDivineWrath`) + F keybind still work
      // for players who already invested points, so casts are not lost; the
      // button just doesn't render so the bottom-left HUD slot (x=310, y=517)
      // is free for future repurposing (NPC sprite, blacksmith, etc.).
      // this.createDivineWrathButton();
      this.createInvocationButtons();
    }

    // Inferno zone transitions always start fresh at wave 1
    if (this.infernoMode) {
      this.gsm.battle.waveNumber = 0;
      this.gsm.battle.bossDefeated = false;
    }

    // Restore run session from server cache (survives F5 / page refresh)
    let sessionRestored = false;
    let sessionWaveJustCompleted = false;
    const session = this.gsm.loadRunSession();
    if (!this.infernoMode && session && !session.infernoMode) {
      this.runScore = session.runScore;
      BattleScene.persistedRunScore = session.runScore;
      BattleScene.persistedRunLootStart = this.gsm.player.resources.loot;
      this.gsm.battle.waveNumber = session.waveNumber;
      // Restore run-scoped counters so Ctrl+Shift+R keeps the "gold earned this run",
      // loot earned, xp, items, etc. The actual account resources.gold / .loot are
      // persisted separately via SyncService and have already been reloaded in init.
      if (session.runGold !== undefined)       this.runGold      = session.runGold;
      if (session.runLoot !== undefined)       this.runLoot      = session.runLoot;
      if (session.runLootSpent !== undefined)  this.runLootSpent = session.runLootSpent;
      if (session.runXp !== undefined)         this.runXp        = session.runXp;
      if (session.runItems !== undefined)      this.runItems     = session.runItems;
      sessionRestored = true;
      sessionWaveJustCompleted = session.waveJustCompleted ?? false;
    } else if (this.infernoMode && session && session.infernoMode) {
      // Inferno: restore zone/score from session if init() data was lost to F5
      this.infernoZone  = session.infernoZone  || this.infernoZone;
      this.infernoScore = session.infernoScore || this.infernoScore;
      this.gsm.battle.waveNumber = session.waveNumber;
      if (session.runGold !== undefined)       this.runGold      = session.runGold;
      if (session.runLoot !== undefined)       this.runLoot      = session.runLoot;
      if (session.runLootSpent !== undefined)  this.runLootSpent = session.runLootSpent;
      if (session.runXp !== undefined)         this.runXp        = session.runXp;
      if (session.runItems !== undefined)      this.runItems     = session.runItems;
      sessionRestored = true;
      sessionWaveJustCompleted = session.waveJustCompleted ?? false;
    }

    // Fix: continue current game state, don't start a new wave on re-entry.
    // Ctrl+Shift+R / F5 should land on the SAME wave the player was on,
    // not advance them. The previous "wave just completed → start next
    // wave fresh" branch was advancing players past the wave they had
    // just been fighting. Now: zone-cleared popup if boss is down,
    // otherwise restart the current wave (whether mid-wave or freshly
    // completed pre-save) so the F5 lands the player back in the same
    // combat. The score-farming guard from line 11908's "save at wave
    // completion" is intentionally relaxed per user feedback.
    if (this.gsm.battle.bossDefeated || this.gsm.battle.waveNumber >= WAVES_PER_ZONE) {
      // Already cleared all waves — show zone cleared popup
      this.gsm.battle.bossDefeated = true;
      this.zonePaused = true;
      this.showZoneClearedPopup();
    } else if (this.gsm.battle.waveNumber === 0) {
      // Fresh zone — start wave 1
      this.startNextWave();
    } else {
      // Resuming mid-zone — restart current wave (don't increment), even
      // if sessionWaveJustCompleted was set. Keeps F5 pinned to the same
      // wave the player was fighting / had just cleared.
      const enemies = this.generateWaveEnemiesForStory();
      this.waveSpawnQueue = enemies;
      this.spawnTimer = 0;
      this.waveActive = true;
    }
    // sessionWaveJustCompleted is read above purely for legacy session-
    // shape compat; the branch is intentionally collapsed so F5 never
    // auto-advances the wave counter.
    void sessionWaveJustCompleted;

    this.sceneReady = true;

    // If a tome choice popup was open when F5 was pressed, restore the exact same choices
    // so the player cannot reroll by refreshing.
    const pendingTomeRaw = localStorage.getItem(`roe_pending_tomes_${this.gsm.player.saveSlot}`);
    if (pendingTomeRaw) {
      try {
        const savedChoices = JSON.parse(pendingTomeRaw) as TomeId[];
        if (Array.isArray(savedChoices) && savedChoices.length > 0) {
          // Per-run rerolls — counter persists in player.tomeRerollsUsed,
          // no popup-scoped state to restore here.
          this.time.delayedCall(600, () => this.showTomeChoice(savedChoices));
        } else {
          localStorage.removeItem(`roe_pending_tomes_${this.gsm.player.saveSlot}`);
        }
      } catch {
        localStorage.removeItem(`roe_pending_tomes_${this.gsm.player.saveSlot}`);
      }
    }

    // Fresh run — reset persisted score and track run start
    // stayCount guard: if player stayed on level 1, don't treat the restart as a new run
    // sessionRestored guard: if restoring from F5, never wipe the score
    if (!sessionRestored && this.gsm.player.currentLevel === 1 && this.gsm.battle.waveNumber <= 1 && (this.gsm.player.stayCount || 0) === 0) {
      BattleScene.persistedRunScore = 0;
      BattleScene.persistedRunLootStart = this.gsm.player.resources.loot;
      BattleScene.persistedDwCdExpiry = 0;
      BattleScene.lastBiomeThemeKey = null; // re-announce the first biome of a new run
      BattleScene.elitePackPending = null; // never carry stale pack state into a new run
      BattleScene.hordePending = null;
      BattleScene.telurionPending = null;
      BattleScene.shadowrealmPending = null;
      this.gsm.player.activeCrossroadsMod = null;
      this.gsm.player.crossroadsModAppliedAtStage = undefined;
      this.runScore = 0;
      this.gsm.clearRunSession();
      localStorage.removeItem(this.pendingTomesKey());
      this.gsm.trackRunStart();
    }
    // Crossroads modifier auto-expiry — paths cover 5 consecutive zones from
    // the stage they were applied at. Clear once `currentLevel >= applied + 5`
    // (player has finished the 5-zone window). Stays don't advance currentLevel,
    // so a freshly-picked path survives a stay without prematurely expiring.
    // resetRun clears the path explicitly.
    {
      const applied = this.gsm.player.crossroadsModAppliedAtStage ?? 0;
      const cur = this.gsm.player.currentLevel;
      if (this.gsm.player.activeCrossroadsMod && cur >= applied + 5) {
        this.gsm.player.activeCrossroadsMod = null;
        this.gsm.player.crossroadsModAppliedAtStage = undefined;
      }
    }
    // Time Pull once-per-zone gate. Mirrored on a static field so the gate
    // survives scene.restart() (stays + F5). Cleared only when the player has
    // advanced to a different stage from the one they cast on.
    if (BattleScene.persistedTimePullStage !== this.gsm.player.currentLevel) {
      this.timePullUsedThisZone = false;
      BattleScene.persistedTimePullStage = -1;
    } else {
      this.timePullUsedThisZone = true;
    }
    // Crossroads — one-line zone-start announcement so the player remembers
    // what modifier is active for the zone they just entered.
    if (this.gsm.player.activeCrossroadsMod) {
      const baseMod = CROSSROADS_MODIFIERS[this.gsm.player.activeCrossroadsMod];
      const appliedStage = this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel;
      const mod = baseMod ? scaleCrossroadsModifier(baseMod, appliedStage) : null;
      if (mod) {
        // Defer slightly so the wave-1 telegraph isn't crowded out.
        this.time.delayedCall(800, () => {
          this.showAnnouncement(`✦ ${mod.name} — ${mod.description}`);
        });
      }
    }
    // Periodic achievement check
    this.gsm.checkAchievements();

    // Multiplayer placement phase — launch drag-drop overlay before battle starts
    if (this.isMultiplayer && this.mpLastDelta?.phase === 'placement' && !this._placementLaunched) {
      this._placementLaunched = true;
      const myHeroes = this.localPlayerIndex === 1
        ? (this.mpLastDelta.player1Heroes ?? [])
        : (this.mpLastDelta.player2Heroes ?? []);
      const allyHeroes = this.localPlayerIndex === 1
        ? (this.mpLastDelta.player2Heroes ?? [])
        : (this.mpLastDelta.player1Heroes ?? []);
      this.scene.launch('PlacementOverlay', {
        playerIndex: this.localPlayerIndex,
        heroes: myHeroes,
        allyHeroes,
        roomId: this.mpLastDelta.roomId ?? '',
      });
    }
  }

  update(_time: number, delta: number): void {
    // Skip update if scene not fully initialized (e.g. showing auto-run summary)
    if (!this.sceneReady) return;

    // ── Shadowrealm endless ticker. Once the Shadowlord falls, this is the
    //    sole spawn source — wave system is entirely sidelined. Spawn rate
    //    starts at one ghost / 2.0 s and shortens by 30 ms per ghost killed
    //    until it caps at 600 ms. Burst size = 1 + floor(kills/40), capped
    //    at 5. Both density (rate × burst) and per-ghost stats (+0.1 % HP/
    //    ATK per kill) compound so the wall is reached before kill counts
    //    get absurd.
    if (BattleScene.shadowrealmPending && BattleScene.shadowrealmPending.endlessActive
        && !this.gameOver && !this.isPaused) {
      const sp = BattleScene.shadowrealmPending;
      if (this.time.now >= sp.nextSpawnTime) {
        const burst = Math.min(5, 1 + Math.floor(sp.ghostKills / 40));
        const intervalMs = Math.max(600, 2000 - sp.ghostKills * 30);
        sp.nextSpawnTime = this.time.now + intervalMs;
        for (let i = 0; i < burst; i++) {
          this.time.delayedCall(i * 120, () => {
            if (!BattleScene.shadowrealmPending || !BattleScene.shadowrealmPending.endlessActive) return;
            if (this.gameOver) return;
            const ghostBase = BASE_ENEMIES.find((e) => e.id === 'endless_ghost');
            if (!ghostBase) return;
            const inst = scaleEnemy(ghostBase, this.battleLevel, false);
            // Per-kill ramp: every kill bumps HP/ATK by 0.1 % multiplicatively
            // on the spawn instance (compounds on top of normal level scaling).
            const ramp = 1 + sp.ghostKills * 0.001;
            inst.maxHp     = Math.max(1, Math.floor(inst.maxHp     * ramp));
            inst.currentHp = inst.maxHp;
            inst.attack    = Math.max(1, Math.floor(inst.attack    * ramp));
            // Alternate spawn sides so the player gets pinched from both edges
            // instead of just the right rim. Pseudo-random check keeps the
            // distribution balanced over time without being a strict 1-1
            // ABAB pattern (which would feel scripted on a 5-burst flush).
            const fromLeft = ((i * 7 + sp.ghostKills * 3) % 2) === 1;
            this.spawnEnemy(inst, fromLeft ? 'left' : 'right');
          });
        }
      }
    }

    // Pause-aware potion cooldown ticker — runs ONLY while combat is
    // running (`!isPaused`). Speed multiplier scales the tick the same
    // way it scales ability cooldowns, so 5× play burns the CD 5× as
    // fast (matches the rest of the game). Without this, opening menus
    // / pausing would burn the timer while the player can't act.
    if (!this.isPaused) {
      const dt = delta * (this.speedMultiplier ?? 1);
      if (this.vitalityCdRemainingMs > 0) {
        this.vitalityCdRemainingMs = Math.max(0, this.vitalityCdRemainingMs - dt);
      }
      if (this.manaCdRemainingMs > 0) {
        this.manaCdRemainingMs = Math.max(0, this.manaCdRemainingMs - dt);
      }
    }

    // Focus target marker — track the focused enemy and spin the ring.
    // updateFocusMarkerPosition can null the marker out if the target
    // just died, so re-check the ref before touching it for the spin.
    if (this.focusMarker && this.focusedEnemyId) {
      this.updateFocusMarkerPosition();
      if (this.focusMarker) {
        // Slow rotation (1 turn / 2.4 s) so the ring reads as a tracking
        // reticle without being a visual distraction.
        this.focusMarker.ring.angle = (this.focusMarker.ring.angle + delta * 0.15) % 360;
      }
    }

    // Popup-overlay watchdog — every ~1s, destroy any stale full-screen dim rectangles at depth 60+
    // that remain after their controlling popup's active flag went false. This catches rare leaks
    // where a cleanup callback missed an element or the close path exited early.
    this._popupWatchdogTimer = (this._popupWatchdogTimer ?? 0) + delta;
    if (this._popupWatchdogTimer >= 1000) {
      this._popupWatchdogTimer = 0;
      const noPopupActive = !this.merchantShopActive
        && !this.goblinoShopActive
        && !this.tomeChoiceActive
        && !this.talentChoiceActive
        && !this.zoneClearedActive
        && !this.gameOver;
      if (noPopupActive) {
        // Use game-coordinate dimensions — cameras.main.width/height return the zoomed viewport size
        // after HD-upscale, which would break the full-screen rectangle check.
        const W = GAME_W;
        const H = GAME_H;
        for (const c of [...this.children.list]) {
          // Always kill any lingering merchant popup children
          if ((c as any).name === 'merchant_popup_el') { c.destroy(); continue; }
          const r = c as Phaser.GameObjects.Rectangle;
          if (!r.active || typeof (r as any).width !== 'number') continue;
          const isFullScreen = r.width >= W - 4 && r.height >= H - 4;
          const isDimOverlay = r.fillAlpha !== undefined && r.fillAlpha > 0.2 && r.fillAlpha < 1.0;
          const isDeep = ((c as any).depth ?? 0) >= 50;
          if (isFullScreen && isDimOverlay && isDeep) {
            r.destroy();
          }
        }
      }
    }

    // Stop all game logic after game over (death overlay / auto-run restart pending)
    if (this.gameOver) return;

    // Apply speed multiplier so x2 actually speeds up all game logic
    const dt = delta * this.speedMultiplier;

    // Manual pause (Space bar / Pause button) — disabled in multiplayer.
    // Before bailing on pause, sync visual attachments (auras, HP bars, shadows) to current
    // hero positions so they never drift when the hero gets repositioned mid-pause (formation
    // swap, respawn, scene resume). Without this the aura glow hangs behind at the pre-pause
    // slot instead of following the sprite.
    if (this.isPaused && !this.isMultiplayer) {
      this.syncHeroSpriteAttachments();
      return;
    }

    this.gameTime += dt;

    // Pause combat while popup is active (merchant shop intentionally excluded — combat runs while browsing vendor)
    if (this.tomeChoiceActive || this.talentChoiceActive || this.goblinoShopActive) return;

    this.updateHeroes(this.gameTime, dt);
    this.updateHeroPortraits();
    this.updateDpsHud();
    this.updateSpellPriorityCooldowns();
    this.updateVitalityCooldownOverlay();
    this.updatePets(this.gameTime, dt);
    this.updateMinions(this.gameTime, dt);
    this.updateSummons(this.gameTime, dt);

    // Cancel swap selection if the selected hero died
    if (this.swapSelectedSprite) {
      if (this.swapSelectedSprite.heroRef.permanentlyDead || !this.swapSelectedSprite.active) {
        this.clearSwapSelection();
      } else if (this.swapArrow) {
        // Keep arrow tracking the hero's x position (y is managed by the bob tween)
        this.swapArrow.x = this.swapSelectedSprite.x;
        // Recolor based on current CC state: red while locked, white while movable.
        const hero = this.swapSelectedSprite.heroRef as any;
        const ccLocked = !!(hero._petrified || hero._frostRooted);
        this.swapArrow.setColor(ccLocked ? '#ef4444' : '#ffffff');
      }
    }
    // Same arrow-follow logic for Vorrak — clean up if he died mid-selection,
    // otherwise track his x. Color reflects whether he's already moved this
    // wave (red = budget exhausted, white = movable).
    if (this.swapSelectedSummon) {
      if (this.swapSelectedSummon.isDead) {
        this.clearSwapSelection();
      } else if (this.swapArrow) {
        this.swapArrow.x = this.swapSelectedSummon.x;
        const outOfMoves = this.summonMovesUsedThisWave >= 1;
        this.swapArrow.setColor(outOfMoves ? '#ef4444' : '#ffffff');
      }
    }

    // Pending choice reminder: blink when talent or book choices are queued
    // Positioned under the zone title (y=48) to avoid overlap with top HUD
    const hasPending = this.pendingTalentChoices.length > 0 || this.pendingTomeChoices > 0;
    if (hasPending && !this.talentChoiceActive && !this.tomeChoiceActive && !this.pendingChoiceReminder) {
      const parts: string[] = [];
      if (this.pendingTalentChoices.length > 0) parts.push(`✦ ${this.pendingTalentChoices.length} TALENT`);
      if (this.pendingTomeChoices > 0) parts.push(`📖 ${this.pendingTomeChoices} BOOK`);
      const label = parts.join('  •  ') + ' READY';
      const width = GAME_W;
      this.pendingChoiceReminder = this.add.text(width / 2, 72, label, {
        fontSize: '11px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
        color: '#f0c040', stroke: '#1a0a00', strokeThickness: 3,
        backgroundColor: '#1a0a00',
        padding: { x: 8, y: 4 },
      }).setOrigin(0.5, 0).setDepth(60).setInteractive({ useHandCursor: true });
      // Stays at full alpha — the previous yoyo alpha tween from 0.5 to
      // 1 read as the badge "fading out" half the time, which the user
      // flagged as a readability issue ("make sure book ready are not
      // fading"). A small scale pulse still draws the eye without
      // making the CTA disappear.
      this.tweens.add({
        targets: this.pendingChoiceReminder,
        scale: { from: 1, to: 1.08 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.pendingChoiceReminder.on('pointerdown', () => {
        if (this.pendingTalentChoices.length > 0) this.processNextTalentChoice();
        else if (this.pendingTomeChoices > 0) { this.pendingTomeChoices--; this.showTomeChoice(); }
      });
    } else if (!hasPending && this.pendingChoiceReminder) {
      this.pendingChoiceReminder.destroy();
      this.pendingChoiceReminder = null;
    }
    if (!this.zonePaused) {
      this.updateEnemies(this.gameTime, dt);
      this.updateGoblino(dt);
      this.updateRotBombers(dt);
      this.spawnQueuedEnemies(dt);
      this.updatePoisonTraps();
      this.checkWaveComplete();
    }
    this.updateRespawns(dt);

    this.manaRegenTimer += dt;
    if (this.manaRegenTimer >= 1000) {
      this.manaRegenTimer -= 1000;
      const regenBonus = Math.floor(getTomeBonus(this.gsm.player.tomes, 'regen'));
      // Trophy: Lifeblood Vial (+3 HP regen/s for all heroes), Mana Crystal (+50% mana regen for all)
      const hasLifeblood = this.hasTrophy('HP regen');
      const hasManaCrystal = this.hasTrophy('mana regen');
      // Sigil mpRegenPct — party-wide % of maxMp added each regen tick.
      const sigMpRegen = this.getSigilEffects().mpRegenPct;
      for (const hs of this.heroSprites) {
        if (!hs.heroRef.isDead) {
          regenMana(hs.heroRef);
          // Sigil mana regen: +X% of maxMp per second (applies to casters only).
          if (sigMpRegen > 0 && hs.heroRef.heroClass !== 'warrior' && hs.heroRef.heroClass !== 'assassin') {
            const maxMp = this.effStats(hs.heroRef).maxMp;
            if (maxMp > 0) {
              hs.heroRef.stats.mp = Math.min(maxMp, hs.heroRef.stats.mp + Math.round(maxMp * sigMpRegen / 100 / 10));
            }
          }
          // Robe armor (light sub-type) — flat MP per second from mpRegenPerSec.
          // Layered after sigil regen so each source compounds independently.
          const armorMpRegen = this.effStats(hs.heroRef).mpRegenPerSec ?? 0;
          if (armorMpRegen > 0 && hs.heroRef.heroClass !== 'warrior' && hs.heroRef.heroClass !== 'assassin') {
            const maxMp = this.effStats(hs.heroRef).maxMp;
            if (maxMp > 0) {
              hs.heroRef.stats.mp = Math.min(maxMp, hs.heroRef.stats.mp + armorMpRegen);
            }
          }
          // Focus Mana Flow: +X% mana regen per second — self only (off-hand focus on this hero).
          for (const w of [hs.heroRef.equipment.weapon1, hs.heroRef.equipment.weapon2]) {
            const manaFlow = w?.specialEffect?.match(/Mana Flow.*\+(\d+)% mana regen/);
            if (manaFlow) {
              const pct = parseInt(manaFlow[1]) / 100;
              const maxMp = this.effStats(hs.heroRef).maxMp;
              if (maxMp > 0) hs.heroRef.stats.mp = Math.min(maxMp, hs.heroRef.stats.mp + Math.round(maxMp * pct / 10));
            }
          }
          // Staff Arcane Flow: +X% mana regen per second — party-wide (any hero with this staff benefits all)
          for (const ally of this.heroSprites) {
            if (ally.heroRef.isDead) continue;
            for (const w of [hs.heroRef.equipment.weapon1, hs.heroRef.equipment.weapon2]) {
              const flowMatch = w?.specialEffect?.match(/Arcane Flow.*\+(\d+)% mana regen/);
              if (flowMatch) {
                const pct = parseInt(flowMatch[1]) / 100;
                const maxMp = this.effStats(ally.heroRef).maxMp;
                if (maxMp > 0) ally.heroRef.stats.mp = Math.min(maxMp, ally.heroRef.stats.mp + Math.round(maxMp * pct / 10));
                break;
              }
            }
          }
          // Mana Crystal: extra +50% mana regen (add 3 mp/energy per second)
          if (hasManaCrystal && hs.heroRef.heroClass !== 'warrior') {
            const stats = this.effStats(hs.heroRef);
            if (hs.heroRef.heroClass === 'assassin') {
              hs.heroRef.stats.energy = Math.min(stats.maxEnergy, hs.heroRef.stats.energy + 5);
            } else {
              hs.heroRef.stats.mp = Math.min(stats.maxMp, hs.heroRef.stats.mp + 3);
            }
          }
          // Regen Tome: heal HP each second
          if (regenBonus > 0) {
            const maxHp = this.effStats(hs.heroRef).maxHp;
            hs.heroRef.stats.hp = Math.min(maxHp, hs.heroRef.stats.hp + regenBonus);
          }
          // Lifeblood Vial: +25 HP regen/s for all heroes
          if (hasLifeblood) {
            const maxHp = this.effStats(hs.heroRef).maxHp;
            hs.heroRef.stats.hp = Math.min(maxHp, hs.heroRef.stats.hp + 25);
          }
          // Potion of Regeneration: HP/s scales with hero level (level * 0.5, min 10)
          if (this.gsm.hasActiveConsumable('potion_regen')) {
            const maxHpR2 = this.effStats(hs.heroRef).maxHp;
            const regenAmt = Math.max(10, Math.floor(hs.heroRef.level * 0.5));
            hs.heroRef.stats.hp = Math.min(maxHpR2, hs.heroRef.stats.hp + regenAmt);
          }
          // Soul Leech: passive HP regen per second based on soul stack (necromancer only)
          if ((hs.heroRef.heroKey ?? hs.heroRef.heroClass) === 'necromancer') {
            const souls = (hs.heroRef as any)._soulCount ?? 0;
            const hasSoulLeech = hs.heroRef.abilities.some((a) => a.id === 'soul_leech');
            if (hasSoulLeech && souls > 0) {
              const rank = getTalentBonusValue(hs.heroRef.talentBonuses ?? {}, 'soul_leech');
              const maxHpSL = this.effStats(hs.heroRef).maxHp;
              // Base coefficient 0.00030 (= 0.03 % max HP/s per soul). At 100
              // souls rank 0 = 3 % max HP/s; rank 6 = 8.4 %. Buff pass — was
              // 0.00020 (0.02 %), too thin at low soul counts.
              const regenPct = souls * 0.00030 * (1 + rank * 0.3);
              const regenAmt = Math.max(1, Math.floor(maxHpSL * regenPct));
              hs.heroRef.stats.hp = Math.min(maxHpSL, hs.heroRef.stats.hp + regenAmt);
            }
            // Necrotic Aura passive: every 5 s, enemies within 200 px take
            // (soulCount × 8) pure damage. Uses a per-hero tick counter so
            // it fires on seconds 5, 10, 15, … rather than every frame.
            if (this.hasArmoryPassive(hs.heroRef, 'passive_morveth_necrotic_aura') && souls > 0) {
              const h = hs.heroRef as any;
              h._necroticAuraTicks = ((h._necroticAuraTicks as number) ?? 0) + 1;
              if (h._necroticAuraTicks >= 5) {
                h._necroticAuraTicks = 0;
                const auraDmg = souls * 8;
                for (const es of this.enemySprites) {
                  if (!es.active || es.enemyRef.currentHp <= 0) continue;
                  if (Phaser.Math.Distance.Between(hs.x, hs.y, es.x, es.y) > 200) continue;
                  es.enemyRef.currentHp -= auraDmg;
                  this.showDamageNumber(es.x + (Math.random() - 0.5) * 12, es.y - 18, `${auraDmg}`, '#a855f7', '10px');
                  if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
                }
              }
            }
          }
          // Relic: Chalice of Renewal — % of max HP per second (scales by
          // relic level, see getRelicBonuses).
          const rbRegen = this.getRelicBonuses(hs.heroRef);
          if (rbRegen.hpRegen > 0) {
            const maxHpR = this.effStats(hs.heroRef).maxHp;
            const tick = Math.max(1, Math.round(maxHpR * rbRegen.hpRegen));
            hs.heroRef.stats.hp = Math.min(maxHpR, hs.heroRef.stats.hp + tick);
          }
          // v2 talent: Slow Recovery — % maxHP regen per second.
          // Steady Heart amplifies all healing (regen + heals + lifesteal).
          {
            const pbReg = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
            const maxHpR = this.effStats(hs.heroRef).maxHp;
            let regen = 0;
            if (pbReg.hpRegenPerSec > 0) regen += pbReg.hpRegenPerSec;
            if (pbReg.hpRegenPctPerSec > 0) regen += maxHpR * pbReg.hpRegenPctPerSec;
            if (regen > 0) {
              const healMult = 1 + pbReg.healingReceivedPct;
              hs.heroRef.stats.hp = Math.min(maxHpR, hs.heroRef.stats.hp + regen * healMult);
            }
          }
          // Relic: Overflow Crystal — +80% extra resource regen per second
          if (rbRegen.hasOverflowCrystal && hs.heroRef.heroClass !== 'warrior') {
            const statsOC = this.effStats(hs.heroRef);
            if (hs.heroRef.heroClass === 'assassin') {
              hs.heroRef.stats.energy = Math.min(statsOC.maxEnergy, hs.heroRef.stats.energy + 9); // +80% of 10/s
            } else {
              const baseRegen = hs.heroRef.heroClass === 'wizard' ? 4 : 5;
              hs.heroRef.stats.mp = Math.min(statsOC.maxMp, hs.heroRef.stats.mp + Math.round(baseRegen * 0.8));
            }
          }
        }
      }
    }

    // Divine Wrath keystone: lightning bolt on strongest enemy every 28s
    if (getPassiveBonuses(this.gsm.player.passiveTree ?? {}).keystoneDivineWrath) {
      this.divineWrathTimer += dt;
      if (this.divineWrathTimer >= 28000) {
        this.divineWrathTimer = 0;
        const strongest = [...this.enemySprites.filter((e) => e.active)]
          .sort((a, b) => b.enemyRef.currentHp - a.enemyRef.currentHp)[0];
        if (strongest) {
          strongest.enemyRef.currentHp -= 80;
          this.showDamageNumber(strongest.x, strongest.y - 30, '⚡ 80', '#5090c0', '16px');
          this.vfx.playExplosionBurst(strongest.x, strongest.y - 20);
          if (strongest.enemyRef.currentHp <= 0 && this.heroSprites[0]) {
            this.killEnemy(strongest, this.heroSprites[0]);
          }
        }
      }
    }

    // Decrement archer reload timers by real elapsed ms
    for (const hs of this.heroSprites) {
      if (!hs.heroRef.isDead && hs.heroRef.stats.reloadTimer > 0) {
        hs.heroRef.stats.reloadTimer = Math.max(0, hs.heroRef.stats.reloadTimer - dt);
      }
    }

    // (Base arrow attack removed — merchant has no combat role)

    this.autoSaveTimer += delta; // auto-save uses real time, not sped up
    if (this.autoSaveTimer >= 30000) {
      this.autoSaveTimer = 0;
      this.gsm.save();
    }

    // Decrement party buff timers (Eagle Eye group, etc.)
    const hadPartyBuffs = this.partyBuffs.length;
    for (const pb of this.partyBuffs) pb.remainingMs -= dt;
    this.partyBuffs = this.partyBuffs.filter((pb) => pb.remainingMs > 0);
    if (this.partyBuffs.length !== hadPartyBuffs) this.lastConsumableSnapshot = ''; // force refresh

    // Divine Wrath button cooldown countdown — Wrath is now rendered inside
    // `createInvocationButtons` (4th icon in the action-bar row), so the
    // CD-expiry rebuild fires that helper instead of the old standalone
    // `createDivineWrathButton`.
    if (this.divineWrathCooldown > 0) {
      const prevSec = Math.ceil(this.divineWrathCooldown / 1000);
      this.divineWrathCooldown = Math.max(0, this.divineWrathCooldown - dt);
      const newSec = Math.ceil(this.divineWrathCooldown / 1000);
      if (newSec !== prevSec) {
        if (this.divineWrathCooldown <= 0) {
          this.createInvocationButtons();
        } else if (this.divineWrathCdLabel) {
          this.divineWrathCdLabel.setText(`${newSec}s`);
        }
      }
    }

    // Invocation cooldowns (Avenging Wrath, Aegis of Light, Time Pull) — same
    // pattern as Divine Wrath: tick second-precision label, full rebuild when
    // CD hits zero so the icon transitions from greyed to ready.
    if (this.avengingWrathCooldown > 0) {
      const prevSec = Math.ceil(this.avengingWrathCooldown / 1000);
      this.avengingWrathCooldown = Math.max(0, this.avengingWrathCooldown - dt);
      const newSec = Math.ceil(this.avengingWrathCooldown / 1000);
      if (newSec !== prevSec) {
        if (this.avengingWrathCooldown <= 0) this.createInvocationButtons();
        else if (this.avengingWrathCdLabel) this.avengingWrathCdLabel.setText(`${newSec}s`);
      }
    }
    if (this.aegisOfLightCooldown > 0) {
      const prevSec = Math.ceil(this.aegisOfLightCooldown / 1000);
      this.aegisOfLightCooldown = Math.max(0, this.aegisOfLightCooldown - dt);
      const newSec = Math.ceil(this.aegisOfLightCooldown / 1000);
      if (newSec !== prevSec) {
        if (this.aegisOfLightCooldown <= 0) this.createInvocationButtons();
        else if (this.aegisCdLabel) this.aegisCdLabel.setText(`${newSec}s`);
      }
    }
    if (this.timePullCooldown > 0) {
      const prevSec = Math.ceil(this.timePullCooldown / 1000);
      this.timePullCooldown = Math.max(0, this.timePullCooldown - dt);
      const newSec = Math.ceil(this.timePullCooldown / 1000);
      if (newSec !== prevSec) {
        // Note: even when the CD hits zero, the button stays locked if
        // `timePullUsedThisZone` is true — only zone advance clears that flag.
        // Rebuild either way so the label updates / button greys correctly.
        if (this.timePullCooldown <= 0) this.createInvocationButtons();
        else if (this.timePullCdLabel) this.timePullCdLabel.setText(`${newSec}s`);
      }
    }

    this.refreshUI();
  }

  // ==================== SETUP ====================

  /** Resolve the active zone, respecting an active Crossroads path's forced
   *  biome. Falls back to the natural `getZoneForLevel` result when no path
   *  is active or the path's stored zone id can't be found. Difficulty +
   *  faction tier scaling are still derived from `currentLevel` so a forced
   *  biome at stage 25 still spawns stage-25-tier enemies. */
  private resolveActiveZone(): ReturnType<typeof getZoneForLevel> {
    const baseZone = getZoneForLevel(this.gsm.player.currentLevel);
    const forcedId = this.gsm.player.crossroadsPathZoneId;
    if (!forcedId || !this.gsm.player.activeCrossroadsMod) return baseZone;
    const overrideTemplate = ZONES.find((z) => z.id === forcedId);
    if (!overrideTemplate) return baseZone;
    // Borrow factions / themeKey / minEnemies from the forced biome but keep
    // baseZone's difficulty + per-cycle tier so progression scaling holds.
    return {
      ...baseZone,
      name: overrideTemplate.name,
      factions: [...overrideTemplate.factions],
      themeKey: overrideTemplate.themeKey,
      minEnemies: overrideTemplate.minEnemies,
      maxEnemies: overrideTemplate.maxEnemies,
    };
  }

  private drawBattlefield(): void {
    const zone = this.infernoMode ? getZoneForInferno(this.infernoZone, this.infernoEntryLevel) : this.resolveActiveZone();

    // Zone background — zone backgrounds (960×576)
    const bgKeys: Record<string, string> = {
      zone_forest:    'zone_background_forest',
      zone_crypt:     'zone_background_crypt',
      zone_wastes:    'zone_background_wastes',
      zone_rift:      'zone_background_rift',
      zone_shadow:    'zone_background_shadow',
      zone_labyrinth: 'zone_background_labyrinth',
    };
    // Lava tints per zone — only Ashen Labyrinth is recolored; Orcish Wastes uses its natural palette
    const bgTints: Record<string, number | null> = {
      zone_labyrinth: 0xcc2200, // deep crimson lava for Ashen Labyrinth
    };
    // Special-event backgrounds — swap in the event-specific background regardless
    // of the source biome so the event visually reads as its own self-contained
    // side-quest scene.
    const bgKey = BattleScene.hordePending
      ? 'zone_background_horde_treasure'
      : BattleScene.telurionPending
        ? 'zone_background_telurion'
        : BattleScene.shadowrealmPending
          ? 'zone_background_shadowrealm'
          : (bgKeys[zone.themeKey] || 'zone_background_forest');
    if (this.textures.exists(bgKey)) {
      const bg = this.add.image(480, 288, bgKey);
      // Inferno keeps the biome-bg tint readable per user feedback ("less
      // reddish, keep enemy colors"). Was alpha 0.45 + crimson 0x7a1a00 +
      // a 0x1a0200/0.55 vignette layered on top — net was a near-opaque
      // red wash that erased every biome's character. Now: alpha 0.65 +
      // a warm bronze tint (0x6a4818) so the player still reads "this is
      // the harsh mode" without losing the biome art.
      bg.setDisplaySize(960, 576).setAlpha(this.infernoMode ? 0.65 : 0.7).setDepth(-1);
      const tint = this.infernoMode ? 0x6a4818 : (bgTints[zone.themeKey] ?? null);
      if (tint != null) bg.setTint(tint);

      // Shadow Realm gargoyle foreground overlay removed — the Craftpix layer cropped
      // too much playable area on the left (and mildly on the right), and the alternative
      // mask-hack from earlier passes had the same problem. Mobs walking over where the
      // statues are painted is a minor aesthetic compromise that's cheaper than fighting
      // the layering.
    }
    // Inferno: subtle ember vignette so the mode reads warm without
    // covering the biome. Was a heavy crimson (0x1a0200, alpha 0.55) per
    // earlier pass; user "less reddish" rebuild drops alpha to 0.20 and
    // shifts hue to a near-black warm (0x0a0604).
    if (this.infernoMode) {
      this.add.rectangle(480, 288, 960, 576, 0x0a0604, 0.20).setDepth(-0.5);
    }

    // Biome transition cinematic — fires once when entering a NEW biome (themeKey
    // change) and only on a fresh-zone state (waveNumber === 0 && stayCount === 0)
    // so the card doesn't re-play on every Ctrl+Shift+R or stay within the same
    // biome. Skipped during event modes — those have their own bespoke cards.
    {
      const isFreshZone = this.gsm.battle.waveNumber === 0 && (this.gsm.player.stayCount ?? 0) === 0;
      const isHordeOrElitePack = !!(BattleScene.hordePending || BattleScene.elitePackPending);
      const biomeChanged = BattleScene.lastBiomeThemeKey !== zone.themeKey;
      // Telurion's Lair and Shadowrealm Breach get their OWN cinematic with
      // their event-specific banner name instead of the underlying biome.
      // Hordes / Elite Packs still skip — they pop their own card flow.
      if (isFreshZone && !isHordeOrElitePack) {
        if (BattleScene.telurionPending) {
          this.playBiomeCinematic('zone_telurion', "Telurion's Lair");
        } else if (BattleScene.shadowrealmPending && !BattleScene.shadowrealmPending.endlessActive) {
          this.playBiomeCinematic('zone_shadowrealm', 'Shadowrealm Breach');
        } else if (biomeChanged) {
          const biomeName = zone.name.replace(/ \(Tier \d+\)$/, '');
          this.playBiomeCinematic(zone.themeKey, biomeName);
        }
      }
      BattleScene.lastBiomeThemeKey = zone.themeKey;
    }

    // Lane shadow strips and perspective guide lines removed — they clashed with the
    // painterly HD bgs (visible horizontal banding across the ground texture).

    // Formation grid — subtle checkerboard showing hero placement slots
    this.drawFormationGrid();

    // Merchant NPC — bottom-left, smaller. Feet sit EXACTLY on the bar's
    // top edge per user spec: bar top = y=526 (rectangle 960×50 anchored at
    // origin (0.5, 1) at y=576 → top edge 526). Feet at y=526 means
    // touching but not sinking.
    const MERCHANT_Y = 526;
    const MERCHANT_BASE_X = 45;
    const merchantKey = this.textures.exists('npc_merchant') ? 'npc_merchant'
      : this.textures.exists('base_intact') ? 'base_intact' : '';
    if (merchantKey) {
      // Shadow ellipse removed per user feedback — NPCs read better as
      // characters standing on the bar than as sprites floating with a
      // contrast-y shadow underneath.
      this.baseSprite = this.add.sprite(MERCHANT_BASE_X, MERCHANT_Y, merchantKey).setOrigin(0.5, 1.0).setDepth(2);
      if (merchantKey === 'npc_merchant') {
        this.baseSprite.setDisplaySize(132, 132);
        // Idle cycle: play idle1 most of the time (80%), occasionally slip into idle2 or idle3
        // as one-shot variations (10% each) for extra life before returning to idle1.
        const playMerchantIdle1 = (): void => {
          this.playMerchantAnim('npc_merchant_idle');
        };
        const merchantIdleDecide = (): void => {
          if (!this.baseSprite?.active) return;
          const r = Math.random();
          const pick = r < 0.8 ? 'npc_merchant_idle'
                     : r < 0.9 ? 'npc_merchant_idle2'
                     : 'npc_merchant_idle3';
          if (!this.playMerchantAnim(pick)) { playMerchantIdle1(); return; }
          if (pick === 'npc_merchant_idle') {
            // Looping — schedule the next decision on a delay.
            this.time.delayedCall(2200 + Math.random() * 2800, merchantIdleDecide);
          } else {
            // One-shot variant: return to idle1 on completion, then schedule next decision.
            this.baseSprite.once('animationcomplete', () => {
              playMerchantIdle1();
              this.time.delayedCall(2500 + Math.random() * 2500, merchantIdleDecide);
            });
          }
        };
        merchantIdleDecide();
      } else {
        this.baseSprite.setDisplaySize(96, 160);
        this.baseSprite.setTint(0xdaa520);
      }
    } else {
      // Fallback rectangle if assets not loaded
      const g = this.add.graphics();
      g.fillStyle(0x8b6914, 0.6);
      g.fillRoundedRect(20, LANE_Y[0] - 50, 80, LANE_Y[LANE_Y.length - 1] - LANE_Y[0] + 100, 6);
      g.lineStyle(2, 0xdaa520, 0.6);
      g.strokeRoundedRect(20, LANE_Y[0] - 50, 80, LANE_Y[LANE_Y.length - 1] - LANE_Y[0] + 100, 6);
    }

    // Exclamation badge — RPG quest-giver style, shown on merchant inventory refresh
    this.merchantExclamBadge = this.add.text(MERCHANT_BASE_X, MERCHANT_Y - 88, '!', {
      fontSize: '18px', fontFamily: 'Palatino Linotype, Palatino, Georgia, serif', fontStyle: 'bold',
      color: '#ffe033',
      stroke: '#7a3800',
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 6, fill: true },
    }).setOrigin(0.5).setDepth(12).setAlpha(0);

    // Merchant click handler — hit area sized tight to the sprite body so
    // players don't accidentally click the merchant when aiming at nearby UI
    // (previously 100 × 96 made an oversized invisible rectangle around the
    // sprite that swallowed stray clicks).
    const merchantHitArea = this.add.rectangle(MERCHANT_BASE_X, MERCHANT_Y - 40, 56, 70, 0x000000, 0)
      .setInteractive({ useHandCursor: true }).setDepth(12);
    merchantHitArea.on('pointerover', () => {
      this.baseTooltip?.destroy();
      const lines = [
        'Merchant',
        'Click to buy potions & items',
        `🪙 ${this.gsm.player.resources.gold}`,
        `◆ Loot: ${this.gsm.player.resources.loot}`,
      ].join('\n');
      this.baseTooltip = this.createHudTooltip(
        MERCHANT_BASE_X + 48, MERCHANT_Y - 68, lines,
        0, 0.5, { fontSize: '10px' },
      );
    });
    merchantHitArea.on('pointerout', () => {
      this.baseTooltip?.destroy();
      this.baseTooltip = null;
    });
    merchantHitArea.on('pointerdown', () => {
      this.baseTooltip?.destroy();
      this.baseTooltip = null;
      // Play the full dialogue animation when clicked; return to the idle rotation on completion.
      if (this.baseSprite?.active) {
        this.baseSprite.off('animationcomplete');
        if (this.playMerchantAnim('npc_merchant_dialogue')) {
          this.baseSprite.once('animationcomplete', () => {
            this.playMerchantAnim('npc_merchant_idle');
          });
        }
      }
      this.showMerchantShop();
    });

    // ── In-battle Whiskers cat (Animal_1) — sits on the forest floor to the far right of the
    // battlefield, mostly idle, occasionally wandering a few steps with the walk sprite.
    // Clicking her opens the deposit popup (bag → account collection). ──
    if (this.textures.exists('whiskers_battle_idle')) {
      const WHISKERS_BASE_X = 720;         // far-right ground, clear of the Books nav button + fight lanes
      const WHISKERS_Y = MERCHANT_Y;       // feet on the same ground line as the merchant
      const WANDER_MIN = 680, WANDER_MAX = 780;
      // Shadow intentionally omitted — the cat should blend into the terrain instead of reading
      // as a floating sprite with a high-contrast ellipse beneath her.
      const whiskers = this.add.sprite(WHISKERS_BASE_X, WHISKERS_Y, 'whiskers_battle_idle')
        .setOrigin(0.5, 1.0).setDisplaySize(72, 72).setDepth(2);
      if (this.anims.exists('whiskers_battle_idle')) whiskers.play('whiskers_battle_idle');
      whiskers.setInteractive({ useHandCursor: true });

      // Idle-heavy patrol: every tick there's a 20% chance she gets up and walks to a new spot.
      // When she moves, the walk sprite plays and she flips to face the destination; otherwise
      // she sits still on the idle loop.
      let patrolTimer: Phaser.Time.TimerEvent | null = null;
      const scheduleNextDecision = (minMs: number, maxMs: number): void => {
        patrolTimer?.remove();
        patrolTimer = this.time.delayedCall(minMs + Math.random() * (maxMs - minMs), tickPatrol);
      };
      const tickPatrol = (): void => {
        if (!whiskers.active) return;
        // 80% idle, 20% take a short stroll.
        if (Math.random() < 0.8) {
          scheduleNextDecision(2000, 4500);
          return;
        }
        const targetX = WANDER_MIN + Math.random() * (WANDER_MAX - WANDER_MIN);
        const dist = Math.abs(targetX - whiskers.x);
        if (dist < 20) { scheduleNextDecision(2000, 4500); return; }
        whiskers.setFlipX(targetX < whiskers.x);
        if (this.anims.exists('whiskers_battle_walk')) whiskers.play('whiskers_battle_walk');
        // Cat-pace walk: ~30 px/s so she reads as a leisurely lurker, not a sprinter.
        this.tweens.add({
          targets: whiskers, x: targetX,
          duration: (dist / 30) * 1000,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            if (whiskers.active && this.anims.exists('whiskers_battle_idle')) {
              whiskers.play('whiskers_battle_idle');
            }
            scheduleNextDecision(2500, 5000);
          },
        });
      };
      scheduleNextDecision(1500, 3000);

      whiskers.on('pointerover', () => {
        this.baseTooltip?.destroy();
        const n = this.gsm.countUnequippedBagSigils();
        const lines = [
          'Rudy',
          n === 0
            ? 'No sigils to bank right now.'
            : `Deposit ${n} sigil${n === 1 ? '' : 's'} to your collection (${GameStateManager.SIGIL_DEPOSIT_PRICE}◆ each).`,
        ];
        this.baseTooltip = this.createHudTooltip(
          whiskers.x, WHISKERS_Y - 80, lines.join('\n'),
          0.5, 1, { fontSize: '10px' },
        );
      });
      whiskers.on('pointerout', () => { this.baseTooltip?.destroy(); this.baseTooltip = null; });
      whiskers.on('pointerdown', () => {
        this.baseTooltip?.destroy();
        this.baseTooltip = null;
        // Stop patrol during the greeting animation so she doesn't drift mid-cast.
        this.tweens.killTweensOf(whiskers);
        if (whiskers.active && this.anims.exists('whiskers_battle_special')) {
          whiskers.play('whiskers_battle_special');
          whiskers.once('animationcomplete', () => {
            if (whiskers.active && this.anims.exists('whiskers_battle_idle')) {
              whiskers.play('whiskers_battle_idle');
            }
            // Resume the idle-heavy patrol after the greeting.
            scheduleNextDecision(1500, 3000);
          });
        } else {
          scheduleNextDecision(1500, 3000);
        }
        this.showWhiskersBattlePopup();
      });
    }

    // ── Blacksmith NPC (Peasants_1) — unlocks at stay 30. Stands between Rudy and
    // Whiskers, plays the hammer-loop on the anvil by default. Click handler +
    // weapon-upgrade modal land in Wave 2; for now this is a visual unlock so the
    // player sees the "you reached stay 30" reward land. ──
    if ((this.gsm.player.stayCount ?? 0) >= 30 && this.textures.exists('npc_blacksmith_working')) {
      const SMITH_X = 200;
      const SMITH_Y = MERCHANT_Y;     // share the bottom-row baseline with Rudy + Whiskers
      // Shadow ellipse removed per user feedback (matches merchant + sage).
      const smith = this.add.sprite(SMITH_X, SMITH_Y, 'npc_blacksmith_working')
        .setOrigin(0.5, 1.0).setDisplaySize(96, 96).setDepth(2);
      if (this.anims.exists('npc_blacksmith_working')) smith.play('npc_blacksmith_working');
      smith.setInteractive({ useHandCursor: true });
      smith.on('pointerover', () => {
        this.baseTooltip?.destroy();
        this.baseTooltip = this.createHudTooltip(
          SMITH_X, SMITH_Y - 100,
          'Blacksmith\nClick to upgrade weapons (◆ runes)',
          0.5, 1, { fontSize: '10px' },
        );
        // Stop hammering — drop into standing pose while the player is interacting.
        if (smith.active && this.anims.exists('npc_blacksmith_idle')) {
          smith.play('npc_blacksmith_idle');
        }
      });
      smith.on('pointerout', () => {
        this.baseTooltip?.destroy();
        this.baseTooltip = null;
        // Resume the hammer-loop.
        if (smith.active && this.anims.exists('npc_blacksmith_working')) {
          smith.play('npc_blacksmith_working');
        }
      });
      smith.on('pointerdown', () => {
        this.baseTooltip?.destroy();
        this.baseTooltip = null;
        // Drop into standing pose while the panel is open.
        if (smith.active && this.anims.exists('npc_blacksmith_idle')) {
          smith.play('npc_blacksmith_idle');
        }
        this.showBlacksmithPopup(smith);
      });
    }

    // ── Rune Sage NPC (Priests_3) — quest-unlocked via "The Sage's Pilgrimage".
    // Spawns to the right of Whiskers once the quest is claimed. Click plays
    // her channel-pose Special anim. Services modal lands in a follow-up
    // pass; this iteration ships the unlock + visual only. ──
    const sageQuest = (this.gsm.player.storyQuests ?? []).find((q) => q.id === 'sages_pilgrimage');
    const sageUnlocked = !!sageQuest?.claimed
      || (this.gsm.account?.claimedStoryQuestIds ?? []).includes('sages_pilgrimage');
    if (sageUnlocked && this.textures.exists('npc_sage_idle')) {
      const SAGE_X = 800;
      const SAGE_Y = MERCHANT_Y;
      // Shadow ellipse removed per user feedback (matches merchant + smith).
      const sage = this.add.sprite(SAGE_X, SAGE_Y, 'npc_sage_idle')
        .setOrigin(0.5, 1.0).setDisplaySize(96, 96).setDepth(2)
        .setFlipX(true); // face the OTHER way per user feedback (toward the formation, not away)
      if (this.anims.exists('npc_sage_idle')) sage.play('npc_sage_idle');
      // Tighter custom hit area — was the full 96×96 sprite bounds, which
      // covered Rudy when he wandered nearby. Use the source frame size and
      // crop the hit zone to a centred ~40 % rectangle (just the sage's body).
      const sageSrcW = sage.frame?.realWidth ?? 64;
      const sageSrcH = sage.frame?.realHeight ?? 64;
      const sageHitW = sageSrcW * 0.45;
      const sageHitH = sageSrcH * 0.65;
      sage.setInteractive({
        useHandCursor: true,
        hitArea: new Phaser.Geom.Rectangle(
          (sageSrcW - sageHitW) / 2,
          (sageSrcH - sageHitH) * 0.6, // slight vertical offset so the body, not the hat, drives the hitbox
          sageHitW, sageHitH,
        ),
        hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      });
      sage.on('pointerover', () => {
        this.baseTooltip?.destroy();
        this.baseTooltip = this.createHudTooltip(
          SAGE_X, SAGE_Y - 100,
          `Rune Sage\nForget a Book — ${GameStateManager.SAGE_FORGET_BOOK_COST} ◆`,
          0.5, 1, { fontSize: '10px' },
        );
      });
      sage.on('pointerout', () => {
        this.baseTooltip?.destroy();
        this.baseTooltip = null;
      });
      sage.on('pointerdown', () => {
        // Channel-pose flourish on click — return to idle when complete.
        if (sage.active && this.anims.exists('npc_sage_special')) {
          sage.play('npc_sage_special');
          sage.once('animationcomplete', () => {
            if (sage.active && this.anims.exists('npc_sage_idle')) sage.play('npc_sage_idle');
          });
        }
        this.showSagePopup(sage);
      });
    }

    // Zone name
    this.add.text(480, 48, zone.name, {
      fontSize: '16px', fontFamily: 'monospace', color: '#e2e8f0', fontStyle: 'bold',
      stroke: '#1a3050', strokeThickness: 3,
    }).setOrigin(0.5);

    // Story-quest enter-zone hook. Engine is idempotent — re-entering the same zone won't
    // re-trigger already-shown lore. Result's `started` list drives the opening popup.
    // Strip the " (Tier N)" suffix so Tier-2+ entries still match the quest's base zone
    // name ("Shadow Realm (Tier 2)" would otherwise never match quest.zone = "Shadow Realm"
    // and the beat would silently never trigger).
    const zoneName = zone.name.replace(/ \(Tier \d+\)$/, '');
    const zoneEvt = this.gsm.trackStoryQuestEvent({
      kind: 'enter_zone', zoneName, stage: this.gsm.player.currentLevel,
    });
    this.queueStoryLorePopups(zoneEvt);

    this.createStayVignette();
  }

  /** Draw / redraw the progressive stay vignette — map edges darken as
   *  `stayCount` grows, communicating "the world is closing in" on infinite
   *  stays. Rendered right above the biome background (depth -0.5) so it dims
   *  the painted terrain but leaves enemies/heroes at full brightness.
   *
   *  Strategy: many thin overlapping bands stacked from the edge inward. Each
   *  band adds a tiny alpha increment; the accumulation produces a smooth
   *  exponential-ish fade from opaque edge → transparent centre. With 48
   *  bands over 180 px, every step is ≤ 4 px thick and ≤ 0.015 α — below the
   *  perceptual threshold for banding. Four-band version looked striped.
   *  No-op at stay 0. */
  private createStayVignette(): void {
    if (this.stayVignetteGfx) {
      this.stayVignetteGfx.destroy();
      this.stayVignetteGfx = null;
    }
    const stayCount = this.gsm.player.stayCount || 0;
    if (stayCount <= 0) return;

    const g = this.add.graphics().setDepth(-0.5);
    const W = GAME_W, H = GAME_H;
    // Overall darkness + reach scale with stayCount. Clamps so even a 20-
    // stay run doesn't fully swallow the battlefield interior.
    const maxAlpha = Math.min(0.70, 0.08 * stayCount);
    const maxThick = Math.min(180, 40 + 14 * stayCount);
    const bands = 48;
    for (let i = 0; i < bands; i++) {
      // Additive stacking: each band fills a 1-px-wider strip than the one
      // before at a small per-band alpha. The inner edges of successive
      // bands overlap by 1 px so the compositor sees a uniform blend
      // instead of 48 discrete rectangles.
      const t = i / (bands - 1);              // 0 at outer edge → 1 at inner edge
      const thick = maxThick * (1 - t);       // remaining depth of THIS band
      if (thick <= 0) continue;
      // Quadratic falloff makes the outer edge clearly dark and the inner
      // edge feather out smoothly to zero — closer to a photographic
      // vignette than a linear ramp.
      const bandAlpha = maxAlpha * Math.pow(1 - t, 2) / bands * 8;
      g.fillStyle(0x000000, Math.min(0.25, bandAlpha));
      // Top + bottom strips span full width; left + right fill only the
      // mid-height gap so corners receive the top/bottom strip alpha too
      // (double coverage = naturally darker corners, like a real vignette).
      g.fillRect(0, 0, W, thick);
      g.fillRect(0, H - thick, W, thick);
      g.fillRect(0, thick, thick, H - 2 * thick);
      g.fillRect(W - thick, thick, thick, H - 2 * thick);
    }
    this.stayVignetteGfx = g;
  }

  private createUI(): void {
    // Top bar
    const topBarColor = this.infernoMode ? 0x110202 : 0x0a0806;
    const topBarBorder = this.infernoMode ? 0x8b1000 : 0x5a3600;
    this.add.rectangle(480, 0, 960, 36, topBarColor, 0.97).setOrigin(0.5, 0).setDepth(10);
    this.add.rectangle(480, 36, 960, 1, topBarBorder, 1).setOrigin(0.5, 0).setDepth(11);
    // Inferno mode tag — the "INFERNO  INF-${zone}" text in the level slot at ~line 2034
    // already identifies the mode; the triple-triangle flame glyph it sat next to was
    // visual noise and has been removed.

    const w = GAME_W;

    // ── Score display — top bar, vertically centered ──
    // Volume bar is gone, so the top row spreads more — Score nudged from
    // 0.62 → 0.66 to fill the freed space and breathe better against Wave
    // (now 0.46) and the music-cluster start (~x=785).
    this.scoreText = this.add.text(w * 0.66, 18, '0', {
      fontSize: '13px', fontFamily: "'Consolas', 'Courier New', monospace", color: '#c8973a', fontStyle: 'bold',
      stroke: '#2a1a04', strokeThickness: 2,
    }).setOrigin(0, 0.5).setDepth(11).setInteractive();
    this.scoreText.on('pointerover', () => {
      this.heroTooltip?.destroy();
      this.heroTooltip = this.add.text(w * 0.71, 38, '⭐ Score — earned from kills & survival', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
        backgroundColor: '#0d0a08', padding: { x: 8, y: 6 },
      }).setDepth(55);
    });
    this.scoreText.on('pointerout', () => { this.heroTooltip?.destroy(); this.heroTooltip = null; });

    // Per user request: runes (◆ persistent loot) sit leftmost, gold (🪙 per-run)
    // sits to its right. Animations stay bound to each Text object — `playRuneFlash`
    // targets `this.lootText` and the flying-coin deposit reads `this.goldText.x`,
    // so swapping just the x coords + tooltip anchors keeps every effect attached
    // to the resource it represents.
    const RUNES_X = 8;
    const GOLD_X  = w * 0.13;
    this.lootText = this.add.text(RUNES_X, 18, '', { fontSize: '12px', fontFamily: "'Consolas', 'Courier New', monospace", color: '#a07840', stroke: '#2a1004', strokeThickness: 2, fontStyle: 'bold' }).setOrigin(0, 0.5).setDepth(11);
    this.lootText.setInteractive();
    this.lootText.on('pointerover', () => {
      this.heroTooltip?.destroy();
      this.heroTooltip = this.add.text(RUNES_X, 38, '◆ Loot runes — persist across defeats', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
        backgroundColor: '#0d0a08', padding: { x: 8, y: 6 },
      }).setDepth(55);
    });
    this.lootText.on('pointerout', () => { this.heroTooltip?.destroy(); this.heroTooltip = null; });

    this.goldText = this.add.text(GOLD_X, 18, '', { fontSize: '12px', fontFamily: "'Consolas', 'Courier New', monospace", color: '#d4952a', stroke: '#2a1004', strokeThickness: 2, fontStyle: 'bold' }).setOrigin(0, 0.5).setDepth(11);
    this.goldText.setInteractive();
    this.goldText.on('pointerover', () => {
      this.heroTooltip?.destroy();
      // Expanded tooltip — new players were lost on what gold actually
      // does in the run. Lists the four spend categories so the value of
      // grinding gold reads at a glance.
      this.heroTooltip = this.add.text(GOLD_X, 38,
        '🪙 Gold\nSpend on: heroes (revive, recruit),\nmerchants, base upgrades, gambling.\nResets on defeat.', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
        backgroundColor: '#0d0a08', padding: { x: 8, y: 6 }, lineSpacing: 3,
      }).setDepth(55);
    });
    this.goldText.on('pointerout', () => { this.heroTooltip?.destroy(); this.heroTooltip = null; });
    // Top-bar text fields — re-spaced after the volume bar was removed.
    // Runes (0.0) + Gold (0.13) kept; Level / Wave / Lives / Score spread
    // across the freed area so the row reads as evenly-paced.
    this.levelText = this.add.text(w * 0.30, 18, '', { fontSize: '12px', fontFamily: "'Consolas', 'Courier New', monospace", color: '#c8973a', fontStyle: 'bold' }).setOrigin(0, 0.5).setDepth(11);
    this.waveText = this.add.text(w * 0.46, 18, '', { fontSize: '12px', fontFamily: "'Consolas', 'Courier New', monospace", color: '#a07840', fontStyle: 'bold' }).setOrigin(0, 0.5).setDepth(11);
    // Active path indicator — hidden by default, populated by refreshUI when
    // a Crossroads modifier is active. Sits on a second line at y=34
    // anchored under the **Stage** label per user feedback (was under wave —
    // crowded the wave/score columns). Pale amethyst matches the Crossroads
    // picker accent so the player connects "this banner came from that pick".
    this.pathText = this.add.text(w * 0.30, 34, '', {
      fontSize: '10px', fontFamily: "'Consolas', 'Courier New', monospace",
      color: '#a78bfa', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(11).setVisible(false);
    // XP penalty + moves pip — relocated under the DPS-meter stack at x=27.
    // Per user feedback: with 3 heroes the last DPS row sat at y=336 (text
    // bottom ~y=347) and the old XP at y=350 was crowding it. Pushed XP /
    // moves down to y=362 / y=378 so there's a clear ~14 px gap below the
    // 3-hero DPS column before this status block starts.
    this.xpPenaltyText = this.add.text(27, 362, '', {
      fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace",
      color: '#ef4444', stroke: '#1a0000', strokeThickness: 2, fontStyle: 'bold',
      backgroundColor: '#00000066', padding: { x: 2, y: 1 },
    }).setOrigin(0.5, 0).setDepth(11).setResolution(2)
      .setVisible(false).setInteractive({ useHandCursor: false });
    this.movesPipText = this.add.text(27, 378, '', {
      fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace",
      color: '#fbbf24', stroke: '#1a0a00', strokeThickness: 2, fontStyle: 'bold',
      backgroundColor: '#00000066', padding: { x: 2, y: 1 },
    }).setOrigin(0.5, 0).setDepth(11).setResolution(2).setVisible(false);
    this.xpPenaltyText.on('pointerover', () => {
      const pct = parseInt(this.xpPenaltyText.text.replace('XP ', '')) || 100;
      // Tooltip blooms to the right of the pill, vertically centred so it
      // doesn't crowd the DPS rows above or the moves pip below.
      const tip = this.add.text(this.xpPenaltyText.x + 24, this.xpPenaltyText.y - 2,
        `XP rate: ${pct}%\nHeroes gain reduced XP when\nfar below the current stage level.\n(100% = full XP, lower = penalised)`,
        { fontSize: '9px', fontFamily: 'Georgia, serif', color: '#e8a030', backgroundColor: '#0a0804', padding: { x: 6, y: 4 }, lineSpacing: 4 }
      ).setOrigin(0, 0).setDepth(55).setName('xp_tip');
      this.add.existing(tip);
      this.xpPenaltyText.setData('tip', tip);
    });
    this.xpPenaltyText.on('pointerout', () => {
      const tip = this.xpPenaltyText.getData('tip') as Phaser.GameObjects.Text | undefined;
      tip?.destroy();
    });

    // Speed indicator
    this.livesText = this.add.text(w * 0.58, 18, '', { fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a' }).setOrigin(0, 0.5).setDepth(11);


    // (Consumable buff icons rendered via refreshConsumableIcons)

    // ── Fullscreen + Music + Log/Social/Menu cluster — top-right HUD ──
    // Speed cycle moved BACK to the bottom bar (next to Auto). Chat icon
    // lives in the bottom-right corner of the bottom bar, NOT this cluster.
    // Widths: [fs 28] [music 28] [log 28] [social 28] [menu 46], gap = 12.
    // Anchor: menu's right edge sits at w - 9.
    const HUD_GAP = 12;
    const HUD_RIGHT = w - 9;
    const MENU_W = 46, SOCIAL_W = 28, LOG_W = 28, MUSIC_W = 28, FS_W = 28;
    const menuX   = HUD_RIGHT - MENU_W / 2;
    const socialX = menuX   - MENU_W / 2 - HUD_GAP - SOCIAL_W / 2;
    const logX    = socialX - SOCIAL_W / 2 - HUD_GAP - LOG_W / 2;
    const musicX  = logX    - LOG_W / 2    - HUD_GAP - MUSIC_W / 2;
    const fsX     = musicX  - MUSIC_W / 2  - HUD_GAP - FS_W / 2;
    const barCenterY = 18; // center of 36px top bar

    // Audio mode icon — 3-state cycle: music+SFX ("♫♪"), music only ("♫"), all off ("♩" dim).
    const AUDIO_GLYPHS = ['♫♪', '♫', '♩'] as const;
    const AUDIO_COLORS = ['#c8973a', '#b58030', '#6b7280'] as const;
    const AUDIO_STROKE = [0x7a5020, 0x5a3610, 0x3a3a3a] as const;
    const musicIconBg = this.add.rectangle(musicX, barCenterY, 28, 22, 0x0d0a08, 0.95)
      .setStrokeStyle(1, AUDIO_STROKE[BattleScene.audioMode]).setDepth(11);
    const musicIcon = this.add.text(musicX, barCenterY + 1, AUDIO_GLYPHS[BattleScene.audioMode], {
      fontSize: '13px', fontFamily: 'Georgia, serif',
      color: AUDIO_COLORS[BattleScene.audioMode],
    }).setOrigin(0.5).setDepth(12).setInteractive({ useHandCursor: true });

    // (Chat icon moved to the bottom-right corner of the bottom menu bar.
    // See the dedicated block immediately after the bottom-bar nav loop.)

    /** Apply the current audioMode — pause/resume BGM and mute SFX accordingly. */
    const applyAudioMode = (mode: 0 | 1 | 2) => {
      BattleScene.audioMode = mode;
      // Mode 2 = all off, modes 0/1 = music on. Keep savedMuted in sync for legacy checks.
      const musicOn = mode !== 2;
      BattleScene.savedMuted = !musicOn;
      if (musicOn) this.bgm?.resume(); else this.bgm?.pause();
      // SFX: on only in mode 0
      SoundManager.muted = mode !== 0;
      // Visuals
      musicIcon.setText(AUDIO_GLYPHS[mode]);
      musicIcon.setColor(AUDIO_COLORS[mode]);
      musicIconBg.setStrokeStyle(1, AUDIO_STROKE[mode]);
    };
    // Keep SoundManager.muted synced even on scene restart (constructor default is false)
    applyAudioMode(BattleScene.audioMode);

    musicIcon.on('pointerover', () => musicIconBg.setStrokeStyle(1, 0xc8973a));
    musicIcon.on('pointerout', () => musicIconBg.setStrokeStyle(1, AUDIO_STROKE[BattleScene.audioMode]));
    musicIcon.on('pointerdown', () => {
      // Cycle: 0 → 1 → 2 → 0
      const next = ((BattleScene.audioMode + 1) % 3) as 0 | 1 | 2;
      applyAudioMode(next);
      this.showAnnouncement(
        next === 0 ? '♫♪ Music + SFX'
        : next === 1 ? '♫ Music only'
        : '♩ Audio off'
      );
    });

    // ── Fullscreen toggle — sits LEFT of the music icon. Same Phaser-
    // drawn corner-bracket glyph as MenuScene (no emoji) so the toggle
    // reads consistent across screens. Wired through Phaser's
    // scale.startFullscreen / stopFullscreen API; the icon flips between
    // outward (enter) and inward (exit) brackets via the
    // enterfullscreen / leavefullscreen events.
    const fsBg = this.add.rectangle(fsX, barCenterY, FS_W, 22, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(11).setInteractive({ useHandCursor: true });
    const fsGfx = this.add.graphics().setDepth(12);
    const drawFsIcon = (color: number, expanded: boolean): void => {
      fsGfx.clear();
      fsGfx.lineStyle(1.5, color, 1);
      if (!expanded) {
        fsGfx.lineBetween(fsX - 6, barCenterY - 1, fsX - 6, barCenterY - 6); fsGfx.lineBetween(fsX - 6, barCenterY - 6, fsX - 1, barCenterY - 6);
        fsGfx.lineBetween(fsX + 1, barCenterY - 6, fsX + 6, barCenterY - 6); fsGfx.lineBetween(fsX + 6, barCenterY - 6, fsX + 6, barCenterY - 1);
        fsGfx.lineBetween(fsX - 6, barCenterY + 1, fsX - 6, barCenterY + 6); fsGfx.lineBetween(fsX - 6, barCenterY + 6, fsX - 1, barCenterY + 6);
        fsGfx.lineBetween(fsX + 1, barCenterY + 6, fsX + 6, barCenterY + 6); fsGfx.lineBetween(fsX + 6, barCenterY + 6, fsX + 6, barCenterY + 1);
      } else {
        fsGfx.lineBetween(fsX - 6, barCenterY - 1, fsX - 1, barCenterY - 1); fsGfx.lineBetween(fsX - 1, barCenterY - 1, fsX - 1, barCenterY - 6);
        fsGfx.lineBetween(fsX + 6, barCenterY - 1, fsX + 1, barCenterY - 1); fsGfx.lineBetween(fsX + 1, barCenterY - 1, fsX + 1, barCenterY - 6);
        fsGfx.lineBetween(fsX - 6, barCenterY + 1, fsX - 1, barCenterY + 1); fsGfx.lineBetween(fsX - 1, barCenterY + 1, fsX - 1, barCenterY + 6);
        fsGfx.lineBetween(fsX + 6, barCenterY + 1, fsX + 1, barCenterY + 1); fsGfx.lineBetween(fsX + 1, barCenterY + 1, fsX + 1, barCenterY + 6);
      }
    };
    drawFsIcon(0xc8973a, this.scale.isFullscreen);
    fsBg.on('pointerover', () => {
      fsBg.setStrokeStyle(1, 0xc8973a);
      drawFsIcon(0xe8b75a, this.scale.isFullscreen);
    });
    fsBg.on('pointerout', () => {
      fsBg.setStrokeStyle(1, 0x5a3600);
      drawFsIcon(0xc8973a, this.scale.isFullscreen);
    });
    fsBg.on('pointerdown', () => {
      if (this.scale.isFullscreen) this.scale.stopFullscreen();
      else this.scale.startFullscreen();
    });
    const onEnterFs = (): void => drawFsIcon(0xc8973a, true);
    const onLeaveFs = (): void => drawFsIcon(0xc8973a, false);
    this.scale.on('enterfullscreen', onEnterFs);
    this.scale.on('leavefullscreen', onLeaveFs);
    this.events.once('shutdown', () => {
      this.scale.off('enterfullscreen', onEnterFs);
      this.scale.off('leavefullscreen', onLeaveFs);
    });

    // Auto-run indicator (hidden — state shown via bottom nav button border)
    this.autoRunText = this.add.text(-100, -100, '', {
      fontSize: '9px', fontFamily: 'monospace', color: '#22c55e',
    }).setDepth(11);

    // ── Bottom navigation bar — single 50 px row containing scene nav
    //    buttons on the left + active abilities (3 invocations + 2 rune
    //    stones) on the right. Reverted from the two-tier 90 px design per
    //    user feedback ("second bar is useless, floods the page"). Active
    //    abilities are drawn inside this same row at y=556, just shrunk so
    //    they fit alongside the wider nav buttons.
    this.add.rectangle(480, 576, 960, 50, 0x0a0806, 0.97).setOrigin(0.5, 1).setDepth(10);
    this.add.rectangle(480, 527, 960, 1, 0x5a3600, 0.9).setOrigin(0.5, 0).setDepth(11);

    // ── Log button — 3rd from the right. Wider gaps per feedback: 18 px between
    // centers so the Log / Social / Menu row reads as distinct buttons, not a mush.
    const logBg = this.add.rectangle(logX, 18, 28, 24, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(20).setInteractive({ useHandCursor: true });
    const logBtn = this.add.text(logX, 18, '📜', {
      fontSize: '13px', fontFamily: 'Georgia, serif',
    }).setOrigin(0.5).setDepth(21).setInteractive({ useHandCursor: true });
    const logOver = () => { logBg.setStrokeStyle(1, 0xc8973a); };
    const logOut  = () => { logBg.setStrokeStyle(1, 0x5a3600); };
    const openLog = () => this.showChoiceLog();
    logBg.on('pointerover', logOver).on('pointerout', logOut).on('pointerdown', openLog);
    logBtn.on('pointerover', logOver).on('pointerout', logOut).on('pointerdown', openLog);

    // ── Social Hub button — between Log and Menu. Opens modal in-game. ──
    // Emoji 👥 swapped for a Phaser-drawn two-people glyph per the no-emoji rule + so
    // the button's hitbox doesn't follow the emoji's inconsistent glyph width.
    const socialBg = this.add.rectangle(socialX, 18, 28, 24, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(20).setInteractive({ useHandCursor: true });
    const socialIconGfx = this.add.graphics().setDepth(21);
    drawSocialIcon(socialIconGfx, socialX, 18, 0xc8973a);
    const socialOver = () => { socialBg.setStrokeStyle(1, 0xc8973a); };
    const socialOut  = () => { socialBg.setStrokeStyle(1, 0x5a3600); };
    // Defensive: track the live SocialHub instance on the scene so repeat
    // clicks toggle instead of stacking. Previously each click opened a NEW
    // modal; if the Hub's dim rect wasn't cleaned up (scene shutdown races,
    // double-open from queued clicks, etc.) the leaked interactive overlay
    // would block subsequent clicks on the HUD button until page reload.
    const openSocial = () => {
      if (!this.scene.isActive()) return;
      const existing = (this as any)._activeSocialHub;
      // Zombie-ref guard — if scene shutdown destroyed the hub directly via
      // `scene.events.once('shutdown', () => this.destroy())` (which skips
      // onClose), `_activeSocialHub` stayed pointing at a dead instance and
      // the next click "closed" the ghost and returned without opening.
      // `isDestroyed()` surfaces that so we fall through to a fresh open.
      const alive = existing && typeof existing.isDestroyed === 'function'
        ? !existing.isDestroyed()
        : !!existing;
      if (alive && typeof existing.close === 'function') {
        try { existing.close(); } catch { /* already torn down */ }
        (this as any)._activeSocialHub = null;
        return;
      }
      // Stale / zombie pointer — clear and open fresh.
      (this as any)._activeSocialHub = null;
      // Static import — previously this was a dynamic import, which produced a
      // separate chunk hashed by content. When the user's page session held an
      // older index-*.js still referencing a now-removed SocialHub-*.js hash,
      // the fetch failed with 'Failed to fetch dynamically imported module'
      // and the hub silently never opened. Bundling into the main chunk makes
      // the reference resolve synchronously and never goes stale.
      const hub = new SocialHub(this, {
        mode: 'modal', sceneWidth: GAME_W, sceneHeight: GAME_H,
        onClose: () => { (this as any)._activeSocialHub = null; },
        onWhisperRequested: (userId, name) => {
          this.chatOverlay?.openWhisper(userId, name);
        },
      });
      (this as any)._activeSocialHub = hub;
    };
    socialBg.on('pointerover', socialOver).on('pointerout', socialOut).on('pointerdown', openSocial);
    // Belt-and-suspenders: if the scene shuts down while a Hub is open, null
    // the reference so a fresh scene doesn't inherit a stale pointer that
    // makes the first click no-op.
    this.events.once('shutdown', () => { (this as any)._activeSocialHub = null; });

    // Pending-request pip anchored to the top-right corner of the button (above the
    // bg rect so it can't absorb clicks meant for the button itself).
    attachPendingBadge(this, { x: w - 66, y: 6 });
    attachFriendOnlineToasts(this);
    // Invite toast — bound at every scene that should surface incoming
    // multiplayer invitations. Without this, a player in BattleScene
    // (solo OR multiplayer) would never see a friend's invite. Cleared
    // on scene shutdown. Gated behind FEATURE_MULTIPLAYER for the Steam build.
    if (FEATURE_MULTIPLAYER) {
      const inviteHandler = (payload: { inviteId: string; fromUserId: string; fromName: string; mode: 'pvp' | 'coop'; expiresAt: number }): void => {
        InviteToast.getInstance().showInvite(this, payload);
      };
      MultiplayerService.getInstance().bindInviteToast(inviteHandler);
      this.events.once('shutdown', () => MultiplayerService.getInstance().unbindInviteToast(inviteHandler));
      this.events.once('destroy', () => MultiplayerService.getInstance().unbindInviteToast(inviteHandler));
    }

    // ── Menu button (top-right) — above HUD but below in-game popups (depth 70+) ──
    const gearBg = this.add.rectangle(menuX, 18, MENU_W, 24, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(20).setInteractive({ useHandCursor: true });
    const gearBtn = this.add.text(menuX, 18, '≡ Menu', {
      fontSize: '10px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: '#c8a850', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(21).setInteractive({ useHandCursor: true });
    const gearOver = () => { gearBtn.setStyle({ color: '#ffe8a0' }); gearBg.setStrokeStyle(1, 0xc8973a); };
    const gearOut  = () => { gearBtn.setStyle({ color: '#a07840' }); gearBg.setStrokeStyle(1, 0x5a3600); };
    gearBg.on('pointerover', gearOver).on('pointerout', gearOut).on('pointerdown', () => this.toggleMenu());
    gearBtn.on('pointerover', gearOver).on('pointerout', gearOut).on('pointerdown', () => this.toggleMenu());

    // Bottom nav bar — 4 visually-separated sections per user feedback so
    // it reads like a proper RPG/MMO action bar:
    //   ┊ Heroes / Items / Quests / Tree ┊ Auto / Speed ┊ Invocations (×4) ┊ Checkpoints ┊
    // Each separator now carries 12 px of breathing room on either side
    // (was 18). Tightened per user feedback that the menu/section gap
    // read too wide compared to the invocation row's near-flush spacing,
    // and that the checkpoints sat too close to the canvas edge. Net: the
    // four sections still read as distinct groups but the chrome stops
    // dominating the layout, and the right margin grows from 2 → 26 px.
    // - Section 1 (nav 4 buttons): Heroes 49 / Items 149 / Quests 249 / Tree 349
    //   (stride 100; right edge 384 → sep1 at 396, +12 gap)
    // - Section 2 (Auto + Speed): Auto x=443 (range 408-478 → 12 px gap from sep1),
    //   Speed x=580 (range 510-650 → 12 px gap to sep2)
    // - Section 3 (invocations): Wrath 692 (range 674-710 → 12 px gap from sep2),
    //   Avenge 732, Aegis 772, Time 812 (stride 40, range 794-830 → 12 px gap to sep3)
    // - Section 4 (checkpoints): Rune 872 (range 854-890 → 12 px gap from sep3),
    //   Deep 916 (stride 44, range 898-934, right margin 26)
    // Vertical separators at x=396 / 662 / 842.
    const navButtons: { label: string; scene: string | null; x: number; action?: string }[] = [
      { label: 'Heroes',  scene: 'HeroScene',         x: 49  },
      { label: 'Items',   scene: 'InventoryScene',    x: 149 },
      { label: 'Quests',  scene: 'AchievementScene',  x: 249 },
      { label: 'Tree',    scene: 'PassiveTreeScene',  x: 349 },
      { label: 'Auto',    scene: null,                x: 443, action: 'autorun' },
      { label: '',        scene: null,                x: 580, action: 'speedCycle' },
    ];

    // Section dividers — 2 px bronze hairlines between the 4 functional groups.
    for (const sx of [396, 662, 842]) {
      this.add.rectangle(sx, 556, 2, 32, 0xa07840, 0.7).setDepth(11);
    }

    for (const nav of navButtons) {
      const isAction = !!nav.action;
      const isSpeedBtn = nav.action === 'speedCycle';
      // Nav buttons shrunk from 86 → 70 px so the bottom bar fits 6 nav
      // entries on the left + 5 active ability icons centred in the middle.
      const btnW = isSpeedBtn ? 140 : 70;
      // Dark stone button background — also interactive so full area is clickable
      const btnBg = this.add.rectangle(nav.x, 556, btnW, 28, 0x0d0a08, 1)
        .setStrokeStyle(1, 0x5a3600).setDepth(10)
        .setInteractive({ useHandCursor: true })
        .setName(isAction ? `actionBg_${nav.action}` : '');
      // Top and bottom 1px gold accent lines
      this.add.rectangle(nav.x, 556 - 14, btnW, 1, 0x5a3600, 0.5).setDepth(10);
      this.add.rectangle(nav.x, 556 + 14, btnW, 1, 0x5a3600, 0.5).setDepth(10);
      // For the speed cycle button, draw vertical separators between the 4 sections
      if (isSpeedBtn) {
        for (let k = 1; k < 4; k++) {
          const sx = nav.x - btnW / 2 + (btnW / 4) * k;
          this.add.rectangle(sx, 556, 1, 22, 0x5a3600, 0.4).setDepth(10);
        }
      }

      const btn = this.add.text(nav.x, 556, nav.label, {
        fontSize: isSpeedBtn ? '14px' : '11px',
        fontFamily: isSpeedBtn ? "'Consolas', 'Courier New', monospace" : "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#b38a5a',
        padding: { x: 6, y: 6 }, fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11)
        .setName(isAction ? `actionTxt_${nav.action}` : '')
        .setVisible(!isSpeedBtn); // speed button uses 4 separate labels below

      // For the speed button, create 4 individually-positioned labels (one per section)
      // so each one centers in its click-zone quarter. The `btn` above (invisible) stays as the
      // named hit target for refreshUI's color-cycling lookup.
      if (isSpeedBtn) {
        const sectionW = btnW / 4;
        // Play = simple chevron (was ▶ which looked too much like a media player button),
        // double-chevron for fast, lightning for max, pause bars.
        const labels = ['>', '≫', '⚡', '❙❙'];
        for (let si = 0; si < 4; si++) {
          const sx = nav.x - btnW / 2 + sectionW * (si + 0.5);
          const subTxt = this.add.text(sx, 556, labels[si], {
            fontSize: si === 0 ? '17px' : '15px',
            fontFamily: "'Palatino Linotype', 'Cinzel Decorative', Georgia, serif",
            color: '#7a5e38', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(12).setResolution(2).setName(`speedSub_${si}`);
          subTxt.setInteractive({ useHandCursor: true });
          subTxt.on('pointerdown', () => this.gotoSpeedState(si as 0 | 1 | 2 | 3));
        }
      }

      // "needs a hero" = has fewer heroes than current deploy capacity — same check used by refreshUI
      // to decide whether to pulse. Pool cap (5) is only the hard ceiling for clicking.
      const recruitNeedsHero = () => this.gsm.player.heroes.length < this.gsm.getMaxDeploySlots();
      const recruitPoolOpen  = () => this.gsm.player.heroes.length < 5;
      const onOver = () => {
        // Recruit: green hover only when a recruit would actually slot in; otherwise standard bronze highlight.
        if (nav.action === 'recruit' && recruitNeedsHero()) {
          btn.setStyle({ color: '#a0e080' });
          btnBg.setStrokeStyle(2, 0x7ad050);
          return;
        }
        btn.setStyle({ color: '#e0b48c' });
        btnBg.setStrokeStyle(1, 0xb78658);
      };
      const onOut = () => {
        // Recruit owns its own state machine so the shared bronze onOut doesn't clobber the green glow.
        if (nav.action === 'recruit') {
          if (recruitNeedsHero()) {
            btn.setStyle({ color: '#7acc60' });
            btnBg.setStrokeStyle(2, 0x4a9a30);
          } else {
            // Neutral bronze when pool has room but no immediate need, dim bronze when pool is full.
            btn.setStyle({ color: recruitPoolOpen() ? '#b38a5a' : '#6a5030' });
            btnBg.setStrokeStyle(1, 0x5a3600);
          }
          return;
        }
        const autoMode = this.gsm.getAutoMode();
        const active = nav.action === 'autorun' ? autoMode !== 'off'
          : nav.action === 'speedCycle' ? (this.isPaused || this.speedMultiplier !== 1)
          : false;
        // Full Auto gets a brighter ember tint so the user can see at a glance
        // which of the two ON states they're in. Auto-Run keeps the standard
        // active bronze.
        const isFullAuto = nav.action === 'autorun' && autoMode === 'full';
        if (nav.action === 'autorun') {
          btn.setText(autoMode === 'full' ? 'Auto+' : 'Auto');
        }
        btn.setStyle({ color: isFullAuto ? '#ffb060' : (active ? '#d4a070' : '#b38a5a') });
        btnBg.setStrokeStyle(1, isFullAuto ? 0xff9030 : (active ? 0xb78658 : 0x5a3600));
      };
      // Speed-cycle button: 4-section hover tip that shows what each section does.
      // Button layout (left→right): > Normal / ≫ 2× / ⚡ 5× / ❙❙ Pause.
      // Tooltip is positioned directly above the hovered section so it's visually tied to that button.
      if (nav.action === 'speedCycle') {
        let sectionTip: Phaser.GameObjects.Text | null = null;
        let lastSection = -1;
        const SECTION_LABELS = ['▶ Normal speed (×1)', '▶▶ Fast (×2)', '⚡ Very fast (×5)', '⏸ Pause [Space]'];
        const renderTip = (section: 0 | 1 | 2 | 3) => {
          if (section === lastSection && sectionTip?.active) return; // skip redundant redraws on pointermove
          lastSection = section;
          sectionTip?.destroy();
          const tipX = nav.x - 70 + section * 35 + 17.5; // centre of this section
          sectionTip = this.add.text(tipX, 535, SECTION_LABELS[section], {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
            backgroundColor: '#0d0a0e', padding: { x: 8, y: 5 }, align: 'center',
          }).setOrigin(0.5, 1).setDepth(55).setResolution(2);
        };
        const onMove = (p: Phaser.Input.Pointer) => {
          const rel = p.x - (nav.x - 70);
          const section = Math.max(0, Math.min(3, Math.floor(rel / 35))) as 0 | 1 | 2 | 3;
          renderTip(section);
        };
        const hideTip = () => { sectionTip?.destroy(); sectionTip = null; lastSection = -1; };
        btn.on('pointerover', onMove).on('pointermove', onMove).on('pointerout', hideTip);
        btnBg.on('pointerover', onMove).on('pointermove', onMove).on('pointerout', hideTip);
        // The sub-glyph text objects ("▶ / ≫ / ⚡ / ❙❙") sit at depth 12 on
        // top of btnBg, which means Phaser routes pointer events to them
        // instead of the underlying bar. Wire each glyph to show its own
        // section tooltip so hover matches the clicked state 1:1, not just
        // "Pause" for every icon.
        for (let si = 0; si < 4; si++) {
          const subTxt = this.children.getByName(`speedSub_${si}`) as Phaser.GameObjects.Text | null;
          if (!subTxt) continue;
          const section = si as 0 | 1 | 2 | 3;
          subTxt.on('pointerover', () => renderTip(section));
          subTxt.on('pointerout',  hideTip);
        }
      }
      // Auto-run button: explain what auto does on hover
      if (nav.action === 'autorun') {
        let autoTip: Phaser.GameObjects.Text | null = null;
        const showAutoTip = () => {
          const cur = this.gsm.getAutoMode();
          const stateLine = cur === 'full' ? 'NOW: Full Auto'
            : cur === 'auto' ? 'NOW: Auto-Run' : 'NOW: Off';
          const msg = `Click to cycle:\nOff → Auto → Full → Off\n\nAuto: auto-picks books & talents.\nFull: also auto-advances stages.\n\n${stateLine}`;
          autoTip = this.add.text(nav.x, 535, msg, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
            backgroundColor: '#0d0a0e', padding: { x: 8, y: 5 }, lineSpacing: 3,
            align: 'center',
          }).setOrigin(0.5, 1).setDepth(55).setResolution(2);
        };
        const hideAutoTip = () => { autoTip?.destroy(); autoTip = null; };
        btn.on('pointerover', showAutoTip); btn.on('pointerout', hideAutoTip);
        btnBg.on('pointerover', showAutoTip); btnBg.on('pointerout', hideAutoTip);
      }
      const onDown = (pointer?: Phaser.Input.Pointer) => {
        // ui_click SFX disabled — clip reads off-tone, re-enable when a
        // better one is dropped in.
        // this.sfx.play('ui_click', { volume: 0.55 });
        if (nav.scene) {
          this.launchOverlay(nav.scene);
        } else if (nav.action === 'autorun') {
          // Cycle Off → Auto → Full → Off. Auto auto-picks books / talents
          // within a zone; Full also auto-advances the zone-cleared popup.
          const next = this.gsm.cycleAutoMode();
          const msg = next === 'off' ? 'Auto-Run OFF'
            : next === 'auto' ? 'AUTO ON — auto-picks books & talents'
            : 'FULL AUTO — auto-picks + auto-advance stages';
          this.showAnnouncement(msg);
          this.refreshUI();
        } else if (nav.action === 'recruit') {
          const MAX = 5;
          if (this.gsm.player.heroes.length >= MAX) {
            this.showAnnouncement('All hero slots are filled. Upgrade Barracks to unlock more.');
            return;
          }
          this.scene.pause();
          this.scene.launch('ShopScene', { gsm: this.gsm, battleScene: this });
        } else if (nav.action === 'speedCycle') {
          // Split 140px-wide button into 4 sections so user can click any state directly
          if (pointer) {
            const rel = pointer.x - (nav.x - 70); // 0..140
            const section = Math.max(0, Math.min(3, Math.floor(rel / 35))) as 0 | 1 | 2 | 3;
            this.gotoSpeedState(section);
          } else {
            this.cycleSpeedState();
          }
        }
      };
      btn.on('pointerover', onOver).on('pointerout', onOut).on('pointerdown', onDown);
      btnBg.on('pointerover', onOver).on('pointerout', onOut).on('pointerdown', onDown);

      // Notification text-pulse — per user feedback the bg/border pulse was
      // too loud. Now it's a soft alpha tween on the LABEL itself (Items /
      // Quests text), 0.65 ↔ 1.0 over a slow yoyo. The button's bg/stroke
      // stay calm; the eye still catches the pulsing letters. `lootBadge` /
      // `questBadge` are kept as offscreen no-op text objects for the legacy
      // refreshUI setText calls.
      if (nav.scene === 'InventoryScene' || nav.scene === 'AchievementScene') {
        const tween = this.tweens.add({
          targets: btn,
          alpha: { from: 0.65, to: 1.0 },
          duration: 900,
          yoyo: true,
          repeat: -1,
          paused: true,
        });
        btn.setData('notifyTween', tween);
        btn.setName(`navTxt_${nav.scene}`);
        btnBg.setName(`navBg_${nav.scene}`);
        const placeholder = this.add.text(-1000, -1000, '', {
          fontSize: '9px', fontFamily: 'monospace',
        }).setVisible(false);
        if (nav.scene === 'InventoryScene') this.lootBadge = placeholder;
        else this.questBadge = placeholder;
      }
    }

    // ── Chat icon — floats ABOVE the bottom bar's right edge, not inside it.
    // Per user request — keeping the bar tidy at 4 sections while the chat
    // toggle stays accessible just above it. 28×28 frame with the same
    // bronze-stone treatment as the in-bar icons.
    {
      const chatX = 940;
      const chatY = 504; // bar top is 526 → 22 px floating gap
      const SIZE = 28;
      const chatBg = this.add.rectangle(chatX, chatY, SIZE, SIZE, 0x0d0a08, 0.95)
        .setStrokeStyle(1, 0x8b6914).setDepth(12).setInteractive({ useHandCursor: true });
      const chatBevel = this.add.graphics().setDepth(12);
      chatBevel.lineStyle(1, 0x5a3a1a, 0.55);
      chatBevel.strokeRect(chatX - SIZE / 2 + 2, chatY - SIZE / 2 + 2, SIZE - 4, SIZE - 4);
      const chatGlyph = this.add.graphics().setDepth(13);
      chatGlyph.lineStyle(1.2, 0xc8973a, 1);
      chatGlyph.strokeRoundedRect(chatX - 9, chatY - 7, 18, 11, 3);
      chatGlyph.lineBetween(chatX - 4, chatY + 4, chatX - 6, chatY + 8);
      chatGlyph.lineBetween(chatX - 6, chatY + 8, chatX - 2, chatY + 4);
      chatGlyph.fillStyle(0xc8973a, 1);
      chatGlyph.fillCircle(chatX - 4, chatY - 1.5, 1.1);
      chatGlyph.fillCircle(chatX,     chatY - 1.5, 1.1);
      chatGlyph.fillCircle(chatX + 4, chatY - 1.5, 1.1);
      chatBg.on('pointerover', () => chatBg.setStrokeStyle(1, 0xffe8a0));
      chatBg.on('pointerout',  () => chatBg.setStrokeStyle(1, 0x8b6914));
      chatBg.on('pointerdown', () => this.chatOverlay?.toggle());
      void chatBevel; void chatGlyph;
    }

    // Bottom-bar speed cycle: highlight the active section glyph (gold + 1.4×
    // scale) so the player can read the current state at a glance. Wired to
    // `speedStateChanged` so SPACE pause / scene-internal cycles update too.
    const refreshSpeedSubColors = (): void => {
      for (let j = 0; j < 4; j++) {
        const subTxt = this.children.getByName(`speedSub_${j}`) as Phaser.GameObjects.Text | null;
        if (!subTxt) continue;
        const isActive =
          (j === 0 && !this.isPaused && this.speedMultiplier === 1) ||
          (j === 1 && !this.isPaused && this.speedMultiplier === 2) ||
          (j === 2 && !this.isPaused && this.speedMultiplier === 5) ||
          (j === 3 && this.isPaused);
        // Desaturated active gold (ffd060 → d8b070) per user feedback so
        // the speed cycle reads as worn bronze, not bright neon yellow.
        subTxt.setColor(isActive ? '#d8b070' : '#7a5e38');
        subTxt.setScale(isActive ? 1.4 : 1.0);
      }
    };
    refreshSpeedSubColors();
    this.events.on('speedStateChanged', refreshSpeedSubColors);
  }

  private refreshUI(): void {
    const p = this.gsm.player;
    this.scoreText.setText(this.infernoMode ? `${this.infernoScore}` : `${this.runScore}`);
    this.goldText.setText(`🪙 ${p.resources.gold}`);
    // Flash the rune counter when loot goes up. Debounce so a burst of drops in one
    // frame still reads as one pulse rather than a seizure. First frame primes the baseline.
    const now = this.time.now;
    if (this.prevLootValue > 0 && p.resources.loot > this.prevLootValue && now - this.lastRuneFlashMs > 300) {
      this.vfx.playRuneFlash(this.lootText);
      this.lastRuneFlashMs = now;
    }
    this.prevLootValue = p.resources.loot;
    this.lootText.setText(`◆ ${p.resources.loot}`);
    this.levelText.setText(this.infernoMode ? `INFERNO  INF-${this.infernoZone}` : `Stage ${p.currentLevel}`);
    // Active Crossroads path readout — shows "✦ <Path Name> · N zones left"
    // for as long as the modifier is in effect. Path expires at applied + 5
    // (matches scene init's auto-clear), so zonesLeft = applied + 5 − cur.
    // When no path is active we hide the text to keep the top bar clean.
    if (!this.infernoMode && p.activeCrossroadsMod) {
      const mod = CROSSROADS_MODIFIERS[p.activeCrossroadsMod];
      const applied = p.crossroadsModAppliedAtStage ?? p.currentLevel;
      const zonesLeft = Math.max(0, applied + 5 - p.currentLevel);
      const zoneWord = zonesLeft === 1 ? 'zone' : 'zones';
      if (mod && zonesLeft > 0) {
        this.pathText.setText(`✦ ${mod.name} · ${zonesLeft} ${zoneWord} left`);
        this.pathText.setVisible(true);
      } else {
        this.pathText.setVisible(false);
      }
    } else {
      this.pathText.setVisible(false);
    }
    // Wave label — includes the live enemy count when mid-wave so the player
    // can track pack density (especially important on Elite Pack where count
    // is random 14-28). Elite Pack wave text is set separately in startNextWave.
    // Enemies-left counter must include both:
    //   1. Currently visible enemies (active & alive)
    //   2. Unspawned enemies still queued (normal waves use waveSpawnQueue,
    //      Horde uses delayedCall scheduling so we derive the remainder from
    //      the pending event's total minus the spawned counters).
    const aliveOnField = this.enemySprites.filter((e) => e.active && e.enemyRef.currentHp > 0).length;
    let unspawned = this.waveSpawnQueue.length;
    if (BattleScene.hordePending) {
      const hp = BattleScene.hordePending;
      unspawned = Math.max(0, (hp.mobCount + hp.eliteCount) - hp.spawnedMob - hp.spawnedElite);
    }
    const enemiesLeft = aliveOnField + unspawned;
    if (BattleScene.telurionPending) {
      // Telurion's Lair — single wave: N Sentinels + dragon. Show live count
      // so the player tracks progress through the wave instead of seeing the
      // generic "Wave 1/5" stomp every HUD tick.
      const tp = BattleScene.telurionPending;
      const dragonLeft = tp.bossSpawned ? this.enemySprites.some((e) => e.active && e.enemyRef.defId === 'boss_telurion' && e.enemyRef.currentHp > 0) : true;
      const phase = dragonLeft && tp.bossSpawned ? 'dragon' : (enemiesLeft > 0 ? `${enemiesLeft} sentinels left` : 'descent…');
      this.waveText.setText(`Telurion's Lair  ·  ${phase}  ·  ×10 gold`);
    } else if (BattleScene.hordePending) {
      // Horde of Goblins — single wave with N total spawns. Live count so the
      // banner tracks the swarm shrinking instead of holding the static intro
      // string forever.
      const hp = BattleScene.hordePending;
      this.waveText.setText(`Horde of Goblins  ·  ${enemiesLeft} left  ·  ✦ ${hp.xpMult}× XP`);
    } else if (BattleScene.elitePackPending === null) {
      const waveStr = `Wave ${this.gsm.battle.waveNumber}/${WAVES_PER_ZONE}`;
      this.waveText.setText(this.waveActive && enemiesLeft > 0 ? `${waveStr}  ·  ${enemiesLeft} left` : waveStr);
    } else {
      // Elite Pack — live enemy count appended to the pack banner so the
      // player can see progress within each of the 2 pack waves.
      const ep = BattleScene.elitePackPending;
      this.waveText.setText(`Elite Pack ${this.gsm.battle.waveNumber}/2  ·  ${enemiesLeft} left  ·  ✦ ${ep.xpMult}× XP`);
    }

    // XP gap penalty indicator — recompute and cache for use in killEnemy
    {
      const alive = this.heroSprites.filter((h) => !h.heroRef.isDead);
      if (alive.length > 0) {
        const avgLevel = alive.reduce((s, h) => s + h.heroRef.level, 0) / alive.length;
        const gap = Math.max(0, avgLevel - this.battleLevel);
        this.currentXpMult = gap >= 60 ? 0 : 1 - gap / 60;
      } else {
        this.currentXpMult = 1;
      }
      if (this.currentXpMult < 1) {
        const pct = Math.round(this.currentXpMult * 100);
        // 3-stop colour gradient: green (≥99%) → yellow (≈50%) → red (≤10%)
        const t = Math.max(0, Math.min(1, (this.currentXpMult - 0.1) / 0.9));
        let xpColor: string;
        if (t >= 0.5) {
          const s = (t - 0.5) * 2; // 0→0.5 maps to 0→1
          const r = Math.round(234 + (34  - 234) * s);
          const g = Math.round(179 + (197 - 179) * s);
          const b = Math.round(8   + (94  -   8) * s);
          xpColor = `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
        } else {
          const s = t * 2; // 0→0.5 maps to 0→1
          const r = Math.round(239 + (234 - 239) * s);
          const g = Math.round(68  + (179 -  68) * s);
          const b = Math.round(68  + (8   -  68) * s);
          xpColor = `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
        }
        this.xpPenaltyText.setText(`XP ${pct}%`).setColor(xpColor).setVisible(true);
      } else {
        this.xpPenaltyText.setVisible(false);
      }
    }

    // Per-hero move-budget indicator. The old global `movesPipText` pool was
    // replaced by an independent budget per hero — each portrait carries
    // its own `left/cap` text in the top-right corner. Indicator hides on
    // biomes with Infinity cap (every legacy biome) and on dead heroes.
    {
      const cap = this.getEffectiveMovesPerWave();
      this.movesPipText.setVisible(false); // legacy global pip permanently muted
      for (const p of this.heroPortraits) {
        if (cap === Infinity || p.hs.heroRef.isDead) {
          p.movesIndicator.setVisible(false);
          continue;
        }
        const used = ((p.hs.heroRef as any)._movesUsedThisWave as number) ?? 0;
        const left = Math.max(0, cap - used);
        const color = left === 0 ? '#ef4444' : '#fbbf24';
        p.movesIndicator.setText(`${left}/${cap}`).setColor(color).setVisible(true);
      }
    }

    this.livesText.setText('');

    // Active consumable buff icons (top-left, 2x tome size)
    // refreshConsumableIcons handles all buff types (consumables, relics, party buffs, trophies)
    // Do NOT call refreshTrophyDisplay here — it clears the snapshot every frame and destroys tooltips
    this.refreshConsumableIcons();
    this.refreshPartyBuffTimers(); // update countdown badges without rebuilding icons

    // Recruit nav button — green pulse only when the player has fewer heroes than their current
    // deploy-slot capacity. Early game the pool cap is 5 but deploy slots start at 1, so the glow
    // would otherwise nag the player at level 1 when extra heroes can't even be deployed.
    const recruitTxt = this.children.getByName('actionTxt_recruit') as Phaser.GameObjects.Text | null;
    const recruitBg  = this.children.getByName('actionBg_recruit')  as Phaser.GameObjects.Rectangle | null;
    if (recruitTxt) {
      const POOL_MAX = 5;
      const heroCount = this.gsm.player.heroes.length;
      const deployMax = this.gsm.getMaxDeploySlots();
      const poolOpen = heroCount < POOL_MAX;
      const needsHero = heroCount < deployMax;  // only glow when recruiting would actually fill a slot
      recruitTxt.setText(poolOpen ? 'Recruit' : `${heroCount}/${POOL_MAX}`);
      if (needsHero) {
        recruitTxt.setColor('#7acc60');
        recruitBg?.setStrokeStyle(2, 0x4a9a30);
        if (!this.tweens.isTweening(recruitTxt)) {
          this.tweens.add({ targets: recruitTxt, alpha: 0.55, duration: 650, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
        }
      } else {
        this.tweens.killTweensOf(recruitTxt);
        recruitTxt.setAlpha(1);
        recruitTxt.setColor(poolOpen ? '#b38a5a' : '#6a5030');
        recruitBg?.setStrokeStyle(1, poolOpen ? 0x5a3600 : 0x5a3600);
      }
    }

    // Sync the combined speed-cycle button label with current state
    const speedTxt = this.children.getByName('actionTxt_speedCycle') as Phaser.GameObjects.Text | null;
    if (speedTxt) speedTxt.setText(this.speedCycleLabel());

    // Highlight the active speed sub-label and dim the others
    let activeIdx = 0;
    if (this.isPaused) activeIdx = 3;
    else if (this.speedMultiplier === 5) activeIdx = 2;
    else if (this.speedMultiplier === 2) activeIdx = 1;
    for (let si = 0; si < 4; si++) {
      const sub = this.children.getByName(`speedSub_${si}`) as Phaser.GameObjects.Text | null;
      if (!sub) continue;
      if (si === activeIdx) {
        sub.setColor('#d4a070');
        sub.setFontSize(si === 0 ? 19 : 17);
      } else {
        sub.setColor('#6a5030');
        sub.setFontSize(si === 0 ? 15 : 13);
      }
    }

    // Compact action button borders — gold border when active. Previously
    // pulsed via an alpha yoyo tween, but the speed-cycle button (×2 / ×5 /
    // pause states) sits in active-mode for the entire fight, so the constant
    // blink was visual noise. Active state now reads via the thicker gold
    // stroke alone — autorun still works, speedCycle no longer blinks at you.
    const actionStates: Record<string, boolean> = {
      autorun: p.autoRun,
      speedCycle: this.isPaused || this.speedMultiplier !== 1,
    };
    for (const [action, active] of Object.entries(actionStates)) {
      const bg = this.children.getByName(`actionBg_${action}`) as Phaser.GameObjects.Rectangle | null;
      const txt = this.children.getByName(`actionTxt_${action}`) as Phaser.GameObjects.Text | null;
      if (bg) {
        this.tweens.killTweensOf(bg);
        bg.setAlpha(1);
        bg.setStrokeStyle(active ? 2 : 1, active ? 0xc8973a : 0x5a3600);
      }
      if (txt) txt.setColor(active ? '#d4a070' : '#b38a5a');
    }

    // Notification text-pulse — soft alpha yoyo on the button's label so the
    // eye catches "something to claim" without the loud bg/border pulse.
    const driveNotifyTween = (sceneKey: string, active: boolean): void => {
      const txt = this.children.getByName(`navTxt_${sceneKey}`) as Phaser.GameObjects.Text | null;
      if (!txt) return;
      const tween = txt.getData('notifyTween') as Phaser.Tweens.Tween | undefined;
      if (!tween) return;
      if (active) {
        if (tween.paused) tween.resume();
      } else {
        if (!tween.paused) {
          tween.pause();
          txt.setAlpha(1);
        }
      }
    };
    driveNotifyTween('InventoryScene', (p.newLootCount ?? 0) > 0);
    driveNotifyTween('AchievementScene', this.gsm.getUnclaimedAchievementCount() > 0);

    this.refreshTomeIcons();
    this.refreshPotionBar();
    this.refreshScorePanel();
  }

  // ==================== HEROES (Half-circle placement) ====================

  private spawnHeroes(): void {
    // Hero Armory: snapshot the master passive list onto each deployed hero's
    // run-scoped `activePassiveIds` so mid-run respecs don't retro-apply.
    this.gsm.snapshotActiveArmoryPassives();
    const heroes = this.gsm.getDeployedHeroes();
    heroes.forEach((hero) => {
      // Temp position — repositionHeroes() will place them correctly
      this.createHeroSprite(hero, HERO_ARC_CENTER_X, HERO_ARC_CENTER_Y);
      this.gsm.trackClassUsed(hero.heroClass);
      this.gsm.trackHeroLevel(hero.level);
    });
    this.repositionHeroes();
    // Multiplayer: also spawn the OTHER player's heroes as visible-but-
    // non-interactive sprites so each client can see the partner/opponent.
    // Without this, mpP1Heroes/mpP2Heroes were stored in init() but never
    // rendered (only `gsm.getDeployedHeroes()` was used above) — the user
    // could never see the other player. Positions come from the initial
    // gameState delta the server sent in game-start.
    if (this.isMultiplayer) {
      const remoteHeroes = this.localPlayerIndex === 1 ? this.mpP2Heroes : this.mpP1Heroes;
      const remoteState = this.localPlayerIndex === 1
        ? this.mpLastDelta?.player2Heroes
        : this.mpLastDelta?.player1Heroes;
      this._spawnRemoteHeroes(remoteHeroes, remoteState);
    }

    // Correct HP to include equipment bonuses — hero.stats.maxHp is base-only;
    // getEffectiveStats().maxHp adds equipment. Heroes that were at full base HP
    // should be at full effective HP; otherwise clamp to effective ceiling.
    for (const hs of this.heroSprites) {
      const effectiveMaxHp = this.effStats(hs.heroRef).maxHp;
      if (hs.heroRef.stats.hp >= hs.heroRef.stats.maxHp) {
        hs.heroRef.stats.hp = effectiveMaxHp;
      } else {
        hs.heroRef.stats.hp = Math.min(hs.heroRef.stats.hp, effectiveMaxHp);
      }
    }

    this.createHeroPortraits();

    // Restore any saved summons (e.g. Sorcerer's Copper Golem persisted
    // across scene restarts). The owner's `_summon` field holds defId +
    // saved hp + saved slotIdx; we recreate the SummonSprite at that slot
    // so the player isn't punished by a scene transition for having a
    // pet alive. If the saved slot is somehow occupied (shouldn't happen,
    // but guard against it), summonCopperGolem will pick a fresh slot.
    for (const hs of this.heroSprites) {
      const saved = (hs.heroRef as any)._summon as { defId: string; hp: number; slotIdx: number } | undefined;
      if (saved && saved.defId === 'copper_golem') {
        const restored = this.summonCopperGolem(hs);
        if (restored && saved.hp > 0 && saved.hp < restored.maxHp) {
          restored.hp = Math.min(saved.hp, restored.maxHp);
        }
      }
    }
  }

  /** Sprites for the OTHER player's heroes in multiplayer. Visible but
   *  non-interactive — no click handlers, no portrait HUD entries. Updated
   *  via `lobby-state-update` deltas (positions only) so the local player
   *  can see partner/opponent movement and life state. */
  private remoteHeroSprites: Phaser.GameObjects.Sprite[] = [];

  private _spawnRemoteHeroes(
    heroes: import('@roe/shared').Hero[],
    state: import('@roe/shared').MultiplayerHeroState[] | undefined,
  ): void {
    if (!heroes || heroes.length === 0) return;
    // Tear down any pre-existing remote sprites (shouldn't happen on first
    // spawn but defensive against repeated spawnHeroes calls e.g. on round
    // restart in PVP).
    for (const s of this.remoteHeroSprites) s.destroy();
    this.remoteHeroSprites = [];
    heroes.forEach((hero, i) => {
      const heroKey = hero.heroKey ?? hero.heroClass;
      const textureKey = `hero_${heroKey}`;
      if (!this.textures.exists(textureKey)) return;
      const pos = state?.[i]?.position ?? { x: 760, y: 100 + i * 60 };
      const sprite = this.add.sprite(pos.x, pos.y, textureKey)
        .setDisplaySize(64, 64)
        .setOrigin(0.5)
        .setDepth(2);
      // Mirror PVP: opponent faces left
      if (this.localPlayerIndex === 1) sprite.setFlipX(true);
      const idleKey = `hero_${heroKey}_idle`;
      if (this.anims.exists(idleKey)) sprite.play(idleKey);
      this.remoteHeroSprites.push(sprite);
    });
  }

  // ── Hero Portrait Frames (left side HUD) ──────────────────────────────────
  /** Build the hero info tooltip (name + HP + Rage/Mana/Energy + ATK + DEF +
   *  SPD + CRIT + optional SP + Status) anchored near the portrait frame.
   *  Shared by the portrait-hover handler — the sprite-hover path was removed
   *  so this tooltip only surfaces on the HUD frame now. */
  private showHeroInfoTooltip(hero: Hero, anchorX: number, anchorY: number): void {
    const overlayScenesOpen = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene', 'BaseScene']
      .some((key) => this.scene.isActive(key));
    if (overlayScenesOpen || this.merchantShopActive || this.gameOver) return;
    if (this.swapSelectedSprite) return;
    this.heroTooltip?.destroy();
    const stats = this.effStats(hero);
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    const effAtk     = Math.floor(stats.attack + pb.atk);
    const effCrit    = Math.round((stats.critChance + pb.crit + ((hero as any)._eagleEyeCrit || 0)) * 100);
    const effCritDmg = ((stats.critDamage + pb.critDmg) * 100).toFixed(0);
    const effSpd     = stats.speed + pb.atkSpd;
    const effSp      = stats.spellPower > 0 ? stats.spellPower + Math.floor(pb.sp) : 0;
    const resourceLabel = hero.heroClass === 'warrior' ? 'Rage' : hero.heroClass === 'assassin' ? 'Energy' : 'Mana';
    const resourceCur   = hero.heroClass === 'warrior' ? Math.floor(hero.stats.rage) : hero.heroClass === 'assassin' ? Math.floor(hero.stats.energy) : Math.floor(hero.stats.mp);
    const resourceMax   = hero.heroClass === 'warrior' ? stats.maxRage : hero.heroClass === 'assassin' ? stats.maxEnergy : stats.maxMp;
    const status = hero.isDead
      ? (hero.goldReviveTimer ?? 0) > 0 ? `Reviving ${((hero.goldReviveTimer ?? 0) / 1000).toFixed(1)}s`
      : hero.respawnTimer > 0 ? `Respawning ${(hero.respawnTimer / 1000).toFixed(1)}s`
      : 'FALLEN'
      : 'Alive';
    const lines: string[] = [
      `${hero.name}  Lv${hero.level}`,
      `HP   ${Math.floor(hero.stats.hp)} / ${stats.maxHp}`,
      `${resourceLabel.padEnd(4)} ${resourceCur} / ${resourceMax}`,
      `─────────────────`,
      `ATK  ${effAtk}`,
      `DEF  ${parseFloat(stats.defense.toFixed(2))}`,
      `SPD  ${effSpd.toFixed(2)}`,
      `CRIT ${effCrit}%  x${effCritDmg}%`,
    ];
    if (effSp > 0) lines.push(`SP   ${effSp}`);
    lines.push(`─────────────────`);
    lines.push(`Status  ${status}`);
    // Permadeath explainer — new players read "FALLEN" and don't realize
    // the hero is actually GONE until they try to recruit a replacement
    // mid-run. Spell out what it means + how to bring them back so the
    // gold-revive affordance reads as a real choice, not a mystery cost.
    if (hero.permanentlyDead && !hero.isDead) {
      const heroIdx = this.gsm.player.heroes.indexOf(hero);
      const reviveCost = heroIdx >= 0 ? this.gsm.getGoldReviveCost(heroIdx) : 0;
      lines.push(`Permanently dead.`);
      lines.push(`Click to revive — 🪙 ${reviveCost}`);
    } else if (hero.permanentlyDead && hero.isDead && (hero.goldReviveTimer ?? 0) === 0) {
      lines.push(`Permanently dead — click to revive.`);
    }
    this.heroTooltip = this.createHudTooltip(
      anchorX, anchorY, lines.join('\n'),
      0, 0.5, { fontSize: '10px' },
    );
    // Clamp so the tooltip never slides off the right edge of the scene.
    const tw = this.heroTooltip.width;
    const clampedX = Math.min(anchorX, 960 - tw - 4);
    if (clampedX !== anchorX) this.heroTooltip.setX(clampedX);
  }

  private createHeroPortraits(): void {
    // Destroy existing portraits
    for (const o of this.dpsHudObjects) o.destroy();
    this.dpsHudObjects = [];
    if ((this as any)._dpsTip) { (this as any)._dpsTip.destroy(); (this as any)._dpsTip = null; }
    for (const p of this.heroPortraits) {
      p.bg.destroy(); p.bgVignette.destroy(); p.gfx.destroy(); p.maskGfx.destroy(); p.portrait.destroy(); p.hpStrip.destroy();
      p.deadOverlay.destroy(); p.costText.destroy(); p.movesIndicator.destroy(); p.priorityHint.destroy();
    }
    this.heroPortraits = [];

    const PW = 50; const PH = 52;
    const GAP = 5;
    // Below buff row-2 (bottom edge at y2=103+19=122) plus one icon-gap (~14px) plus half-height
    const startY = 162;
    const px = 27; // left edge center x

    this.heroSprites.forEach((hs, i) => {
      const py = startY + i * (PH + GAP);

      // ── Stone background (base fill + outer stroke) ───────────────────────
      // Near-black base so the portrait pops; a stepped-ring vignette drawn on
      // top of `bg` but below the sprite lightens the perimeter toward a
      // stone-warm tone. Reads as "mostly black going lighter on the edges".
      const bg = this.add.rectangle(px, py, PW, PH, 0x050302, 1)
        .setStrokeStyle(2, 0x4a3010, 1).setDepth(20);
      const bgVignette = this.add.graphics().setDepth(20.5);
      // Smooth-vignette via many small concentric rects with interpolated
      // colors. Each step's rect COVERS the previous, leaving only a 1 px
      // ring of the previous shade visible — the eye reads the stack as a
      // continuous gradient. 12 steps at 1 px increments is enough to look
      // smooth on the 50×52 frame; Phaser.Graphics doesn't have a native
      // radial gradient so this is the cleanest workaround.
      const RING_STEPS = 12;
      const outerR = 0x3a, outerG = 0x2a, outerB = 0x14; // warm stone
      const innerR = 0x06, innerG = 0x03, innerB = 0x02; // near-black
      for (let s = 0; s < RING_STEPS; s++) {
        const t = s / (RING_STEPS - 1); // 0 = outer, 1 = inner
        const r = Math.round(outerR + (innerR - outerR) * t);
        const g = Math.round(outerG + (innerG - outerG) * t);
        const b = Math.round(outerB + (innerB - outerB) * t);
        const color = (r << 16) | (g << 8) | b;
        const inset = 2 + s; // start just inside the outer stroke
        const w = PW - inset * 2;
        const h = PH - inset * 2;
        if (w <= 0 || h <= 0) break;
        bgVignette.fillStyle(color, 1);
        bgVignette.fillRect(px - PW / 2 + inset, py - PH / 2 + inset, w, h);
      }

      // ── Full hero sprite — clipped to frame, fades into stone at bottom ─────
      const texKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}`;
      const portrait = this.textures.exists(texKey)
        ? this.add.sprite(px, py - 10, texKey, 0)
            .setDisplaySize(88, 88) // zoomed to face/shoulder — lower body fades behind gradient
            .setDepth(22)
        : this.add.sprite(px, py - 10, '__DEFAULT').setDisplaySize(60, 60).setDepth(22);
      // Clip sprite to frame bounds so it never overflows
      const maskGfx = this.add.graphics().setVisible(false);
      maskGfx.fillStyle(0xffffff);
      maskGfx.fillRect(px - PW / 2, py - PH / 2, PW, PH);
      portrait.setMask(maskGfx.createGeometryMask());

      // ── Overlay: grit + bottom-fade gradient + inset border + brackets ─────
      const gfx = this.add.graphics().setDepth(23);
      const rng = (seed: number, n: number) => (seed * 2654435761 >>> 0) % n;
      const L = px - PW / 2; const T = py - PH / 2;
      // Sparse grit on edges only (not centre where portrait is)
      gfx.fillStyle(0x2a1f0d, 0.22);
      for (let d = 0; d < 14; d++) {
        const s1 = rng(i * 97 + d * 31 + 7, PW - 6);
        const s2 = rng(i * 53 + d * 17 + 3, 6); // only top/bottom strips
        const side = rng(d * 7 + i, 2);
        gfx.fillRect(L + 3 + s1, side === 0 ? T + 3 + s2 : T + PH - 9 + s2, rng(d * 13 + 1, 2) + 1, 1);
      }
      // Bottom-fade gradient: transparent → stone bg, covering lower ~55% of frame
      gfx.fillGradientStyle(0x15110a, 0x15110a, 0x15110a, 0x15110a, 0, 0, 1, 1);
      gfx.fillRect(L, T + Math.round(PH * 0.42), PW, Math.round(PH * 0.58));
      // Top-edge sheen
      gfx.fillStyle(0x6a5025, 0.20);
      gfx.fillRect(L + 3, T + 2, PW - 6, 1);
      // Inset border
      gfx.lineStyle(1, 0x7a5518, 0.55);
      gfx.strokeRect(L + 3, T + 3, PW - 6, PH - 6);
      // Corner accent brackets (bright gold)
      gfx.lineStyle(1, 0xc08030, 0.85);
      const bL = L + 4; const bT = T + 4; const bR = L + PW - 4; const bB = T + PH - 4;
      const bS = 5;
      gfx.beginPath();
      gfx.moveTo(bL, bT + bS); gfx.lineTo(bL, bT); gfx.lineTo(bL + bS, bT); // TL
      gfx.moveTo(bR - bS, bT); gfx.lineTo(bR, bT); gfx.lineTo(bR, bT + bS); // TR
      gfx.moveTo(bL, bB - bS); gfx.lineTo(bL, bB); gfx.lineTo(bL + bS, bB); // BL
      gfx.moveTo(bR - bS, bB); gfx.lineTo(bR, bB); gfx.lineTo(bR, bB - bS); // BR
      gfx.strokePath();

      // ── Armory-tier frame ornament ─────────────────────────────────────
      // Baseline tier 1 (gold corner pips) always renders from Armory Lv 1 so
      // every hero has *some* frame ornament from the very first run instead
      // of the raw stone frame. Higher tiers (2-8) then add detail at the
      // milestones 10 / 20 / 30 / 50 / 100 / 200 / 500 / 1000 — each tier is
      // additive, so lv 100 draws tier 1+2+3+4+5+6 together.
      {
        const armoryInfo = this.gsm.getArmoryInfo(hs.heroRef.heroKey ?? hs.heroRef.heroClass);
        const tier = Math.max(1, armoryFrameTier(armoryInfo.level));
        drawHeroFrameOrnament(this, px, py, PW, PH, tier, 24);
      }

      // ── Class badge (REMOVED 2026-04-24) ──────────────────────────────────
      // Previously the top-right corner showed a small class-themed symbol
      // circle (sword / staff / shield / etc.). Replaced by the armory-tier
      // ornament above so the frame's visual identity scales with hero
      // investment instead of being dominated by a static class icon.
      // Anchor coords kept for the invisible `classIcon` placeholder below so
      // the heroPortraits struct stays shape-compatible. No actual badge is
      // drawn here any more — the armory-tier ornament owns the frame edges.
      const bdx = L + PW - 8; const bdy = T + 8;

      // Invisible placeholder — kept only so the cleanup system can call .destroy()
      // and the existing heroPortraits struct stays shape-compatible. The full
      // class-specific symbol drawing block (healer cross, wizard staff, tank
      // shield, etc.) was removed when the class badge was retired.
      const classIcon = this.add.text(bdx, bdy, '', { fontSize: '1px' }).setAlpha(0).setDepth(26);

      // ── HP strip at bottom inside the frame ───────────────────────────────
      const hpStrip = this.add.rectangle(px, py + PH / 2 - 5, PW - 6, 3, 0x22c55e, 1).setDepth(24);

      // ── Dead overlay (hidden by default) ──────────────────────────────────
      const deadOverlay = this.add.rectangle(px, py, PW, PH, 0x000000, 0).setDepth(24);

      // ── Cost / timer text ─────────────────────────────────────────────────
      const costText = this.add.text(px, py + 7, '', {
        fontSize: '8px', fontFamily: 'Georgia, serif',
        color: '#e8a030', stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(25).setAlpha(0);

      // Hover portrait: show the hero info tooltip (name / HP / ATK / DEF /
      // CRIT / Status …) anchored just to the right of the frame so it never
      // overlaps the portrait column. Moved here from the battlefield hero
      // sprite hover — the frame is a stabler hover target than a sprite
      // that can be mid-charge / mid-leap / off-screen.
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => {
        this.showHeroInfoTooltip(hs.heroRef, px + PW / 2 + 6, py);
      });
      bg.on('pointerout', () => {
        this.heroTooltip?.destroy();
        this.heroTooltip = null;
      });
      bg.on('pointerdown', () => {
        const heroIdx = this.gsm.player.heroes.indexOf(hs.heroRef);
        if (hs.heroRef.permanentlyDead && heroIdx >= 0) {
          // Reaper's Pact — no gold-revive either; the curse wants heroes
          // staying dead so the damage-per-dead-hero stack keeps climbing.
          if (this.getBoonEffects().cannotResurrect) {
            this.showAnnouncement('Reaper\'s Pact — resurrection blocked');
            return;
          }
          if (this.gsm.goldRevive(heroIdx)) {
            this.destroyHeroGrave(hs);
          } else {
            this.showAnnouncement('Not enough 🪙!');
          }
        } else if (!hs.heroRef.isDead) {
          // Toggle this hero's spell priority popup — others stay open.
          if (this.spellPriorityPopups.has(hs)) {
            this.closeSpellPriorityPopup(hs);
          } else {
            this.openSpellPriorityPopup(hs, px + 29, py);
          }
        }
      });

      // Per-hero "X/Y" moves indicator anchored to top-right of the portrait.
      // Tiny monospace amber number — hidden until the per-frame refresh
      // populates it (and stays hidden on biomes with Infinity cap).
      const movesIndicator = this.add.text(px + PW / 2 - 3, py - PH / 2 + 2, '', {
        fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace",
        color: '#fbbf24', stroke: '#000000', strokeThickness: 3, fontStyle: 'bold',
      }).setOrigin(1, 0).setDepth(25).setVisible(false).setResolution(2);

      // Spell-priority affordance hint — small ▶ at the top-left corner so
      // the player knows the portrait can be clicked to bring up the
      // spell-priority popup. Bronze gold matches the frame's corner
      // brackets. Non-interactive: clicks pass through to `bg` which owns
      // the pointerdown handler that toggles the popup. A subtle alpha
      // pulse draws the eye on first sight without being noisy.
      const priorityHint = this.add.text(px - PW / 2 + 4, py - PH / 2 + 2, '▶', {
        fontSize: '9px', fontFamily: 'Arial, sans-serif', color: '#c8973a',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0, 0).setDepth(25);
      this.tweens.add({
        targets: priorityHint, alpha: 0.55, duration: 1400,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });

      this.heroPortraits.push({ hs, bg, bgVignette, gfx, maskGfx, portrait, classIcon, hpStrip, deadOverlay, costText, movesIndicator, priorityHint, wasDeadLastFrame: false });
    });

    // ── Recruit badge — small "+" icon BELOW the portrait column AND below the
    // DPS label stack so the two never overlap. DPS labels render at
    // `startY + 2*(PH+GAP) + PH/2 + 8` + i*13 up to (n-1) heroes, so at the 3-
    // hero deploy cap they reach ~343. Portrait column at 3 heroes ends at
    // ~302. 380 sits safely below both with ~160 px of clearance before the
    // bottom nav at y=556. Replaces the bottom-nav Recruit button.
    this.createRecruitBadge(px, 380);
  }

  /** Draw/redraw the recruit badge anchored below the hero portrait column.
   *  Called from `createHeroPortraits` each time portraits rebuild so the
   *  badge stays current with the hero count. */
  private createRecruitBadge(cx: number, cy: number): void {
    // Destroy any previous badge AND its lingering tooltip before redrawing.
    // Without the tooltip destroy here, a rebuild-while-hovering (e.g. a
    // hero recruited / dismissed) left the floating tip stuck on screen
    // because the new badge's pointerout never fires for the old tooltip.
    const existing = this.children.getByName('recruitBadge_container') as Phaser.GameObjects.Container | null;
    existing?.destroy();
    const staleTip = this.children.getByName('recruitBadge_tooltip') as Phaser.GameObjects.Container | null;
    staleTip?.destroy();

    // Only render the badge when the player can actually recruit right now —
    // i.e. has fewer heroes than the current stage's deploy cap AND the pool
    // isn't already at 5 / 5. Previously we drew a greyed-out version in the
    // "pool has room but deploy slots full" state, which just took up space
    // and could overlap the DPS label stack without ever being clickable.
    const needsHero = this.gsm.player.heroes.length < this.gsm.getMaxDeploySlots();
    if (!needsHero) return;

    const w = 44; const h = 34;
    const fill = 0x0d2a10;
    const stroke = 0x7ad050;
    const iconColor = '#a0e080';

    const container = this.add.container(cx, cy).setDepth(24).setName('recruitBadge_container');

    const bg = this.add.rectangle(0, 0, w, h, fill, 1).setStrokeStyle(2, stroke, 0.95).setOrigin(0.5);
    const icon = this.add.text(0, -1, '+', {
      fontSize: '22px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: iconColor, fontStyle: 'bold',
    }).setOrigin(0.5).setResolution(2);
    container.add([bg, icon]);

    bg.setInteractive({ useHandCursor: true });
    let recruitTip: Phaser.GameObjects.Container | null = null;
    const clearTip = () => { recruitTip?.destroy(); recruitTip = null; };
    bg.on('pointerover', () => {
      bg.setStrokeStyle(2, 0x9ae070, 1);
      icon.setColor('#c0ff90');
      clearTip();
      // Single state now — the badge only exists when recruiting is possible,
      // so no need for the old deploy-slot / pool-full variants.
      recruitTip = this.createHudTooltip(cx + w / 2 + 6, cy,
        'Recruit — slot open, click to hire a hero',
        0, 0.5, { fontSize: '10px', color: iconColor, depth: 55, wrap: 240 },
      ).setName('recruitBadge_tooltip');
    });
    bg.on('pointerout', () => {
      bg.setStrokeStyle(2, stroke, 0.95);
      icon.setColor(iconColor);
      clearTip();
    });
    // Kill the tooltip if the scene shuts down or the badge container itself
    // is destroyed mid-hover — otherwise it survives across scene restarts.
    container.once('destroy', clearTip);
    bg.on('pointerdown', () => {
      clearTip();
      // ui_click SFX disabled — re-enable when a better clip lands.
      // this.sfx.play('ui_click', { volume: 0.55 });
      const MAX = 5;
      if (this.gsm.player.heroes.length >= MAX) {
        this.showAnnouncement('All hero slots are filled. Upgrade Barracks to unlock more.');
        return;
      }
      this.scene.pause();
      this.scene.launch('ShopScene', { gsm: this.gsm, battleScene: this });
    });
  }

  // ──────────────────────────────────────────────
  // Multiplayer hooks
  // ──────────────────────────────────────────────

  /**
   * Called each time the server broadcasts a game-state-update delta.
   * Syncs hero HP and isDead status from authoritative server state.
   */
  private applyMultiplayerDelta(delta: MultiplayerGameDelta): void {
    this.mpLastDelta = delta;

    // Sync ONLY positions (and the player-2 flip cosmetic) from the server.
    // The server is a position broadcaster + win referee; it does NOT modify
    // hero HP or isDead — those are owned by the client-local combat
    // simulation. Overwriting local HP from server deltas here would freeze
    // local damage every 50ms in both PVP and coop (Codex stop-time finding).
    // Positions in the delta are already mirror-transformed by the adapter for player 2.
    const allServerHeroes = [
      ...(delta.player1Heroes ?? []),
      ...(delta.player2Heroes ?? []),
    ];

    for (const hs of this.heroSprites) {
      const serverHero = allServerHeroes.find(h => h.heroId === hs.heroRef.id);
      if (!serverHero) continue;
      // Sync position only (HP/isDead intentionally NOT synced — see comment above).
      if (!hs.heroRef.isDead) {
        hs.baseX = serverHero.position.x;
        hs.baseY = serverHero.position.y;
      }
    }

    // In PVP player 2 view, flip all local hero sprites so they face right → left
    if (this.localPlayerIndex === 2) {
      for (const hs of this.heroSprites) {
        if (!hs.heroRef.isDead) {
          hs.setFlipX(true);
        }
      }
    }

    // Check if game ended via phase transition
    if (delta.phase === 'finished' && !this.gameOver) {
      this._handleMultiplayerGameEnd({ winner: delta.winner ?? 1, reason: 'defeat' });
    }
  }

  private _showDisconnectOverlay(timeoutSeconds: number): void {
    const W = GAME_W;
    const msg = `Opponent disconnected\nReconnecting... (${timeoutSeconds}s)`;
    const overlay = this.add.text(W / 2, 40, msg, {
      fontSize: '14px', fontFamily: 'monospace', color: '#fbbf24',
      stroke: '#1a0a00', strokeThickness: 3,
      backgroundColor: '#1a0a00',
      padding: { x: 12, y: 6 },
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(62);

    // Auto-hide after the timeout window
    this.time.delayedCall(timeoutSeconds * 1000, () => overlay.destroy());
  }

  private _handleMultiplayerGameEnd(payload: GameEndPayload): void {
    if (this.gameOver) return;
    this.gameOver = true;
    const W = GAME_W, H = GAME_H;
    this.add.rectangle(W / 2, H / 2, W, H, 0x050402, 0.88).setDepth(60).setInteractive();
    const isWinner = payload.winner === this.localPlayerIndex;
    const label = isWinner ? '⚔ VICTORY' : '💀 DEFEAT';
    const color = isWinner ? '#f0c040' : '#c04040';
    this.add.text(W / 2, H / 2 - 40, label, {
      fontSize: '48px', fontFamily: 'Georgia, serif', fontStyle: 'bold', color,
    }).setOrigin(0.5).setDepth(61);

    // PVP rewards sub-text (Elo + runes)
    if (payload.eloChange) {
      const myElo = this.localPlayerIndex === 1 ? payload.eloChange.player1 : payload.eloChange.player2;
      const eloStr = myElo >= 0 ? `+${myElo} Elo` : `${myElo} Elo`;
      const parts: string[] = [eloStr];
      if (isWinner && payload.runesAwarded) parts.push(`+${payload.runesAwarded} Runes`);
      this.add.text(W / 2, H / 2 + 20, parts.join('   '), {
        fontSize: '24px', fontFamily: 'Georgia, serif', color: isWinner ? '#fde68a' : '#f87171',
      }).setOrigin(0.5).setDepth(61);
    }
  }

  private updateHeroPortraits(): void {
    // When a rear-flank enemy is close to the left HUD column, fade the portrait
    // frames so the approaching mob is visible through them. Only applies in
    // biomes with rear flanking (Shadow Realm, Demon Rift). Portraits sit at
    // px=27, width 50 → right edge ≈ 52, so a rear flanker at x<150 overlaps.
    const rearFlankNearHud = this.biomeRearFlankChance > 0 && this.enemySprites.some((es) =>
      es.active && es.enemyRef.currentHp > 0
      && es.getData('spawnSide') === 'rear'
      && es.x < 150,
    );
    const portraitAlpha = rearFlankNearHud ? 0.55 : 1;
    for (const p of this.heroPortraits) {
      const { hs, bg, portrait, hpStrip, deadOverlay, costText } = p;
      if (!hs.active) continue;
      // Apply / restore alpha each frame so it keeps syncing as rear flankers enter/leave.
      bg.setAlpha(portraitAlpha);
      p.gfx.setAlpha(portraitAlpha);
      portrait.setAlpha(portraitAlpha);
      p.classIcon.setAlpha(portraitAlpha);
      hpStrip.setAlpha(portraitAlpha);

      const isDead = hs.heroRef.isDead || hs.heroRef.permanentlyDead;
      // Hide the spell-priority hint while the hero is dead — clicking the
      // frame in that state goes to gold-revive (handled by the cost text
      // glyph), not the spell-priority popup, so the ▶ would mislead.
      p.priorityHint.setVisible(!isDead);
      const isReviving = !hs.heroRef.permanentlyDead && hs.heroRef.isDead && (hs.heroRef.respawnTimer ?? 0) > 0;
      const isPermanentlyDead = hs.heroRef.permanentlyDead;
      const isGoldReviving = !isPermanentlyDead && isDead && (hs.heroRef as any).goldReviveTimer > 0;

      // HP strip width
      const stats = this.effStats(hs.heroRef);
      const hpPct = isDead ? 0 : Math.max(0, hs.heroRef.stats.hp / stats.maxHp);
      const stripW = Math.max(0, Math.round((50 - 6) * hpPct));
      hpStrip.setSize(stripW || 1, 3);
      hpStrip.setX(p.bg.x - (50 - 6) / 2 + stripW / 2);
      const hpColor = hpPct > 0.5 ? 0x22c55e : hpPct > 0.25 ? 0xf59e0b : 0xef4444;
      hpStrip.setFillStyle(hpColor);

      if (isDead) {
        portrait.setTint(0x444444);
        deadOverlay.setFillStyle(0x000000, 0.55);
        bg.setStrokeStyle(1, isPermanentlyDead ? 0x8b0000 : 0x4a3010, 0.9);

        if (isGoldReviving) {
          const timer = (hs.heroRef as any).goldReviveTimer as number;
          costText.setText(`${(timer / 1000).toFixed(1)}s`).setColor('#5090c0').setAlpha(1);
        } else if (isPermanentlyDead) {
          const heroIdx = this.gsm.player.heroes.indexOf(hs.heroRef);
          const cost = this.gsm.getGoldReviveCost(heroIdx);
          costText.setText(`🪙${cost}`).setColor('#e8a030').setAlpha(1);
        } else if (isReviving) {
          const timer = hs.heroRef.respawnTimer ?? 0;
          costText.setText(`${(timer / 1000).toFixed(1)}s`).setColor('#aaaaaa').setAlpha(1);
        } else {
          costText.setAlpha(0);
        }
      } else {
        portrait.clearTint();
        deadOverlay.setFillStyle(0x000000, 0);
        bg.setStrokeStyle(1, 0x5a4020, 0.7);
        costText.setAlpha(0);
      }
    }
  }

  // ── Spell Priority Popup ─────────────────────────────────────────────────

  /** Close the spell-priority popup for a specific hero, or all popups if none given. */
  private closeSpellPriorityPopup(hs?: HeroSprite): void {
    if (hs) {
      const p = this.spellPriorityPopups.get(hs);
      if (!p) return;
      for (const obj of p.objects) obj.destroy();
      this.spellPriorityPopups.delete(hs);
      this.hideSpellRangePreview();
      return;
    }
    for (const [, p] of this.spellPriorityPopups) {
      for (const obj of p.objects) obj.destroy();
    }
    this.spellPriorityPopups.clear();
    this.hideSpellRangePreview();
  }

  /** Live spell-range preview ring rendered on the battlefield while
   *  hovering a spell icon in the spell-priority popup. Tier-coloured by
   *  the cast-range bucket (melee orange, mid amber, extended blue,
   *  ranged cyan). Cleaned up on hover-out and popup-close. */
  private spellRangePreview: Phaser.GameObjects.Graphics | null = null;
  private showSpellRangePreview(hs: HeroSprite, range: number): void {
    this.hideSpellRangePreview();
    const g = this.add.graphics().setDepth(70);
    let color = 0xff8030;                 // melee — orange-red
    if (range >= 750) color = 0x60c0ff;       // ranged — cyan
    else if (range >= 550) color = 0x80a0ff;  // extended — light blue
    else if (range >= 400) color = 0xffd060;  // mid — amber
    g.lineStyle(2, color, 0.55);
    g.strokeCircle(hs.x, hs.y, range);
    g.fillStyle(color, 0.05);
    g.fillCircle(hs.x, hs.y, range);
    this.spellRangePreview = g;
  }
  private hideSpellRangePreview(): void {
    if (this.spellRangePreview) {
      this.spellRangePreview.destroy();
      this.spellRangePreview = null;
    }
  }

  /** Refresh cooldown overlays on every open spell-priority popup. Called each frame from update(). */
  /** Per-frame visibility / text update for the Potion of Vitality + Mana
   *  cooldown overlays. Mirrors the spell-priority popup pattern — hides
   *  the dim + numeral when CD has elapsed, otherwise shows the remaining
   *  seconds (one-decimal under 10 s, integer above). Cheap; bails
   *  immediately when no overlay exists or the CD isn't running. */
  private updateVitalityCooldownOverlay(): void {
    this.updatePotionCdOverlay(this.vitalityCdOverlay, this.vitalityCdRemainingMs);
    this.updatePotionCdOverlay(this.manaCdOverlay, this.manaCdRemainingMs);
  }
  private updatePotionCdOverlay(
    o: { dim: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } | null,
    remainingMs: number,
  ): void {
    if (!o || !o.dim?.active || !o.text?.active) return;
    if (remainingMs <= 0) {
      if (o.text.visible) { o.text.setVisible(false); o.dim.setVisible(false); }
      return;
    }
    const secs = remainingMs / 1000;
    const label = secs >= 10 ? `${Math.ceil(secs)}` : secs.toFixed(1);
    if (!o.text.visible) { o.text.setVisible(true); o.dim.setVisible(true); }
    if (o.text.text !== label) o.text.setText(label);
  }

  private updateSpellPriorityCooldowns(): void {
    if (this.spellPriorityPopups.size === 0) return;
    for (const [, p] of this.spellPriorityPopups) {
      for (const o of p.cdOverlays) {
        // Skip overlay entries whose text/dim has already been destroyed
        // (popup closed mid-update, or scene tear-down). Touching setText
        // on a destroyed object crashes the renderer ("Cannot read
        // properties of null (reading 'drawImage')").
        if (!o.text?.active || !o.dim?.active) continue;
        const cdMs = Math.max(0, o.ability.currentCooldown || 0);
        if (cdMs <= 0) {
          if (o.text.visible) { o.text.setVisible(false); o.dim.setVisible(false); }
        } else {
          const secs = cdMs / 1000;
          const label = secs >= 10 ? `${Math.ceil(secs)}` : secs.toFixed(1);
          if (!o.text.visible) { o.text.setVisible(true); o.dim.setVisible(true); }
          if (o.text.text !== label) o.text.setText(label);
        }
      }
    }
  }

  private _dpsHudTick = 0;
  private _dpsTip: Phaser.GameObjects.Text | null = null;
  private updateDpsHud(): void {
    this._dpsHudTick += 1;
    if (this._dpsHudTick < 12) return;
    this._dpsHudTick = 0;

    for (const o of this.dpsHudObjects) o.destroy();
    this.dpsHudObjects = [];

    if (this.heroSprites.length === 0) return;

    const elapsed = Math.max(1, (this.gameTime - this.dpsWaveStart) / 1000);
    const px = 27;
    const startY = 162;
    const PH = 52; const GAP = 5;
    const blockY = startY + 2 * (PH + GAP) + PH / 2 + 8;

    const fmt = (v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`;
    // Display-name overrides for ability ids whose internal id doesn't match
    // the in-game spell name. Add new entries here when a new talent reshape
    // changes the spell's display name without renaming the ability id.
    const SRC_DISPLAY_NAME: Record<string, string> = {
      whirlwind_bludgeon: 'Tempest Slam',
      shield_bash_charge: 'Shield Bash',
      magmarath_ember:    "Magmarath's Ember",
      raise_dead:         'Raise Dead',
    };
    const srcLabel = (src: string) => SRC_DISPLAY_NAME[src]
      ?? src.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    this.heroSprites.forEach((hs, i) => {
      const sources = this.dpsWaveDamage.get(hs.heroRef.id) ?? new Map<string, number>();
      const total = [...sources.values()].reduce((s, v) => s + v, 0);
      const dps = total / elapsed;

      const txt = this.add.text(px, blockY + i * 13, `H${i + 1}: ${fmt(dps)}`, {
        fontSize: '9px', fontFamily: 'monospace', color: '#c8973a',
        backgroundColor: '#00000066', padding: { x: 2, y: 1 },
      }).setOrigin(0.5, 0).setDepth(24).setResolution(2).setInteractive({ useHandCursor: false });

      txt.on('pointerover', () => {
        if (this._dpsTip) { this._dpsTip.destroy(); this._dpsTip = null; }
        const lines = [...sources.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([src, amt]) => `${srcLabel(src)}: ${fmt(amt)}`);
        if (lines.length === 0) return;
        this._dpsTip = this.add.text(px + 32, blockY + i * 13, lines.join('\n'), {
          fontSize: '8px', fontFamily: 'monospace', color: '#e8d0a0',
          backgroundColor: '#0d0a07ee', padding: { x: 5, y: 4 }, lineSpacing: 3,
        }).setOrigin(0, 0).setDepth(60).setResolution(2);
      });
      txt.on('pointerout', () => {
        if (this._dpsTip) { this._dpsTip.destroy(); this._dpsTip = null; }
      });

      this.dpsHudObjects.push(txt);
    });
  }

  private openSpellPriorityPopup(hs: HeroSprite, anchorX: number, anchorY: number): void {
    this.closeSpellPriorityPopup();

    const hero = hs.heroRef;
    // Filter out passive abilities — player can't meaningfully reorder them
    const castable = hero.abilities.filter((a) =>
      a.effectType !== 'passive_resurrect' &&
      a.effectType !== 'passive_loot' &&
      a.effectType !== 'passive_spear'
    );
    if (castable.length === 0) return;

    // Ensure spellPriority + spellDisabled are initialised
    if (!hero.spellPriority || hero.spellPriority.length === 0) {
      hero.spellPriority = castable.map((a) => a.id);
    }
    if (!hero.spellDisabled) hero.spellDisabled = [];

    // ── Horizontal layout: strip of cells, one per ability ─────────────────
    // Compacted so all three deployed heroes can open their popups at once.
    // Title removed — the row already lives next to the hero portrait, so the
    // owning hero is obvious. PAD applies on every side of the cells (top
    // included) so the outer panel border reads as centred around the cell
    // strip — earlier the cells were flush against the top edge with PAD
    // only on the bottom, which made the box look top-heavy.
    const CELL_W = 48;
    const CELL_H = 58;
    const PAD = 4;
    const PERK_TILE = 22;
    const PERK_ROW_H = PERK_TILE + 8;
    const activePerks = (hero.activePassiveIds ?? [])
      .map((id) => getPassiveDef(id))
      .filter((def): def is import('@roe/shared').PassiveDef => !!def);
    const hasPerks = activePerks.length > 0;
    const PW = castable.length * CELL_W + PAD * 2;
    const PH = PAD + CELL_H + PAD + (hasPerks ? PERK_ROW_H + PAD : 0);

    // Anchor the strip just right of the portrait and centered vertically on the portrait.
    // Clamp so the panel stays on screen.
    const SW = GAME_W, SH = GAME_H;
    const px = Math.min(anchorX + 4, SW - PW - 4);
    const py = Phaser.Math.Clamp(anchorY - PH / 2, 4, SH - PH - 4);

    const DEPTH = 80;
    const objects: Phaser.GameObjects.GameObject[] = [];
    const cdOverlays: { ability: import('@roe/shared').HeroAbility; text: Phaser.GameObjects.Text; dim: Phaser.GameObjects.Rectangle }[] = [];
    const addObj = <T extends Phaser.GameObjects.GameObject>(o: T): T => { objects.push(o); return o; };

    // No click-outside backdrop: with multiple popups open, a global backdrop would fight the other
    // popups' hit areas. Each popup is closed by clicking the owning hero portrait a second time.

    // Panel background
    const panelBg = addObj(this.add.graphics().setDepth(DEPTH));
    panelBg.fillStyle(0x0e0b07, 0.96);
    panelBg.fillRect(px, py, PW, PH);
    panelBg.lineStyle(1, 0x7a5518, 0.8);
    panelBg.strokeRect(px, py, PW, PH);
    // Corner brackets
    panelBg.lineStyle(1, 0xc08030, 0.9);
    const bS = 4;
    panelBg.beginPath();
    panelBg.moveTo(px, py + bS); panelBg.lineTo(px, py); panelBg.lineTo(px + bS, py);
    panelBg.moveTo(px + PW - bS, py); panelBg.lineTo(px + PW, py); panelBg.lineTo(px + PW, py + bS);
    panelBg.moveTo(px, py + PH - bS); panelBg.lineTo(px, py + PH); panelBg.lineTo(px + bS, py + PH);
    panelBg.moveTo(px + PW - bS, py + PH); panelBg.lineTo(px + PW, py + PH); panelBg.lineTo(px + PW, py + PH - bS);
    panelBg.strokePath();

    // Hover tooltip (single shared text obj — built on cell hover)
    let cellTooltip: Phaser.GameObjects.Text | null = null;
    const clearTip = () => { cellTooltip?.destroy(); cellTooltip = null; };

    const rebuild = () => {
      // Keep [panelBg] = 1 object (backdrop + header removed in the compact layout)
      const keep = 1;
      for (let k = objects.length - 1; k >= keep; k--) {
        objects[k].destroy();
        objects.splice(k, 1);
      }
      cdOverlays.length = 0;
      clearTip();

      // Reorder castable to match current spellPriority
      const ordered = [...castable].sort((a, b) => {
        const ai = hero.spellPriority!.indexOf(a.id);
        const bi = hero.spellPriority!.indexOf(b.id);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });

      ordered.forEach((ability, idx) => {
        const cellX = px + PAD + idx * CELL_W;
        const cellCx = cellX + CELL_W / 2;
        const cellTop = py + PAD;
        const cellCy = cellTop + CELL_H / 2;
        const isFirst = idx === 0;
        const isLast = idx === ordered.length - 1;
        const isDisabled = hero.spellDisabled!.includes(ability.id);

        // Cell background tint — highlighted for priority 1, dimmed when disabled
        const cellBg = addObj(this.add.graphics().setDepth(DEPTH + 1));
        cellBg.fillStyle(isDisabled ? 0x1a0e06 : (idx === 0 ? 0x2a1f10 : 0x140e06), 0.85);
        cellBg.fillRect(cellX, cellTop, CELL_W - 2, CELL_H);
        cellBg.lineStyle(1, isDisabled ? 0x4a2a10 : (idx === 0 ? 0xa07030 : 0x3a2a18), 0.7);
        cellBg.strokeRect(cellX, cellTop, CELL_W - 2, CELL_H);

        // Priority number — top-left
        addObj(this.add.text(cellX + 4, cellTop + 2, `${idx + 1}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
          color: idx === 0 ? '#f0c040' : idx === 1 ? '#c0c0c0' : '#a0806a',
        }).setResolution(2).setOrigin(0, 0).setDepth(DEPTH + 3));

        // Spell icon (center, slightly upper)
        const iconY = cellCy - 10;
        let iconObj: Phaser.GameObjects.Image | Phaser.GameObjects.Graphics;
        if (this.textures.exists(ability.iconKey ?? '')) {
          iconObj = addObj(this.add.image(cellCx, iconY, ability.iconKey!)
            .setDisplaySize(26, 26).setOrigin(0.5).setDepth(DEPTH + 2));
        } else {
          iconObj = addObj(this.add.graphics().setDepth(DEPTH + 2));
          (iconObj as Phaser.GameObjects.Graphics).fillStyle(0x404040, 0.8).fillRect(cellCx - 13, iconY - 13, 26, 26);
        }
        if (isDisabled) (iconObj as Phaser.GameObjects.Image).setAlpha?.(0.35);

        // Cooldown overlay — darker dim + seconds-remaining text centered on the icon.
        // Hidden when CD is 0; per-frame updateSpellPriorityCooldowns() toggles visibility.
        const cdDim = addObj(this.add.rectangle(cellCx, iconY, 26, 26, 0x000000, 0.55)
          .setOrigin(0.5).setDepth(DEPTH + 2.5).setVisible(false));
        const cdText = addObj(this.add.text(cellCx, iconY, '', {
          fontSize: '12px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
          color: '#ffe0a0', stroke: '#1a0f00', strokeThickness: 2,
        }).setResolution(2).setOrigin(0.5).setDepth(DEPTH + 3).setVisible(false));
        cdOverlays.push({ ability, text: cdText, dim: cdDim });

        // Disabled cross-out strike through icon
        if (isDisabled) {
          const strike = addObj(this.add.graphics().setDepth(DEPTH + 3));
          strike.lineStyle(2, 0xc82020, 0.95);
          strike.beginPath();
          strike.moveTo(cellCx - 13, iconY - 13);
          strike.lineTo(cellCx + 13, iconY + 13);
          strike.strokePath();
        }

        // Rank badge bottom-right of icon
        const rankStr = (hero.talentBonuses?.[ability.id] ?? 0) > 0
          ? `${hero.talentBonuses![ability.id]}/${ability.maxLevel ?? 1}`
          : `Lv${ability.level}`;
        addObj(this.add.text(cellCx + 13, iconY + 13, rankStr, {
          fontSize: '7px', fontFamily: 'Georgia, serif', color: '#ffffff',
          backgroundColor: '#000000dd', padding: { x: 2, y: 0 },
        }).setResolution(2).setOrigin(1, 1).setDepth(DEPTH + 3));

        // Row of control buttons below the icon — [◀] [on/off] [▶]
        const ctrlY = cellTop + CELL_H - 14;

        // ◀ shift earlier in priority
        if (!isFirst) {
          const left = addObj(this.add.text(cellX + 6, ctrlY, '◀', {
            fontSize: '11px', fontFamily: 'Arial', color: '#80c0a0',
          }).setResolution(2).setOrigin(0, 0.5).setDepth(DEPTH + 3).setInteractive({ useHandCursor: true }));
          left.on('pointerover', () => (left as Phaser.GameObjects.Text).setColor('#b0ffd0'));
          left.on('pointerout',  () => (left as Phaser.GameObjects.Text).setColor('#80c0a0'));
          left.on('pointerdown', () => {
            const p = hero.spellPriority!;
            const i = p.indexOf(ability.id);
            if (i > 0) { [p[i - 1], p[i]] = [p[i], p[i - 1]]; rebuild(); }
          });
        }

        // On/Off toggle center
        const toggleSymbol = isDisabled ? '✕' : '●';
        const toggleColor = isDisabled ? '#c04040' : '#4ade80';
        const toggle = addObj(this.add.text(cellCx, ctrlY, toggleSymbol, {
          fontSize: '12px', fontFamily: 'Georgia, serif', fontStyle: 'bold', color: toggleColor,
        }).setResolution(2).setOrigin(0.5, 0.5).setDepth(DEPTH + 3).setInteractive({ useHandCursor: true }));
        toggle.on('pointerover', () => {
          clearTip();
          cellTooltip = this.add.text(cellCx, cellTop - 4,
            isDisabled ? 'Click to enable' : 'Click to disable', {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
              backgroundColor: '#0d0a0e', padding: { x: 6, y: 3 },
            }).setResolution(2).setOrigin(0.5, 1).setDepth(DEPTH + 10);
        });
        toggle.on('pointerout', () => clearTip());
        toggle.on('pointerdown', () => {
          const dis = hero.spellDisabled!;
          const i = dis.indexOf(ability.id);
          if (i === -1) dis.push(ability.id);
          else dis.splice(i, 1);
          rebuild();
        });

        // ▶ shift later in priority
        if (!isLast) {
          const right = addObj(this.add.text(cellX + CELL_W - 8, ctrlY, '▶', {
            fontSize: '11px', fontFamily: 'Arial', color: '#c09060',
          }).setResolution(2).setOrigin(1, 0.5).setDepth(DEPTH + 3).setInteractive({ useHandCursor: true }));
          right.on('pointerover', () => (right as Phaser.GameObjects.Text).setColor('#f0b070'));
          right.on('pointerout',  () => (right as Phaser.GameObjects.Text).setColor('#c09060'));
          right.on('pointerdown', () => {
            const p = hero.spellPriority!;
            const i = p.indexOf(ability.id);
            if (i < p.length - 1) { [p[i], p[i + 1]] = [p[i + 1], p[i]]; rebuild(); }
          });
        }

        // Name hover tooltip on the icon itself + spell range preview ring
        // on the battlefield (when the spell has a range entry).
        const nameHit = addObj(this.add.rectangle(cellCx, iconY, 26, 26, 0, 0)
          .setDepth(DEPTH + 4).setInteractive({ useHandCursor: false }));
        const abilityRange = SPELL_CAST_RANGES[ability.id];
        const rangeLabel = abilityRange === undefined ? ''
          : abilityRange >= 750 ? '  ·  Ranged'
          : abilityRange >= 550 ? '  ·  Extended'
          : abilityRange >= 400 ? '  ·  Mid range'
          : '  ·  Melee';
        nameHit.on('pointerover', () => {
          clearTip();
          cellTooltip = this.add.text(cellCx, cellTop - 4,
            `${ability.name}${isDisabled ? ' (off)' : ''}${rangeLabel}`, {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
              backgroundColor: '#0d0a0e', padding: { x: 6, y: 3 },
            }).setResolution(2).setOrigin(0.5, 1).setDepth(DEPTH + 10);
          if (abilityRange !== undefined) this.showSpellRangePreview(hs, abilityRange);
        });
        nameHit.on('pointerout', () => {
          clearTip();
          this.hideSpellRangePreview();
        });
      });
    };

    rebuild();

    // Elite Perks row — small rune tiles below the abilities row, hover for
    // name + description tooltip. Reads `hero.activePassiveIds` (the run-start
    // snapshot) so the popup shows what's actually firing in this run, even
    // if the player respec'd in the Armory mid-run.
    if (hasPerks) {
      const perksRowY = py + PAD + CELL_H + PAD;
      const totalPerksW = activePerks.length * PERK_TILE + (activePerks.length - 1) * 4;
      const perksStartX = px + (PW - totalPerksW) / 2 + PERK_TILE / 2;
      activePerks.forEach((def, i) => {
        const ix = perksStartX + i * (PERK_TILE + 4);
        const iy = perksRowY + PERK_TILE / 2;
        const tileBg = addObj(this.add.rectangle(ix, iy, PERK_TILE, PERK_TILE, 0x0d0a08, 0.95)
          .setStrokeStyle(1, 0xc8973a, 0.85).setDepth(DEPTH + 2)
          .setInteractive({ useHandCursor: false }));
        const icon = addObj(this.add.text(ix, iy, def.icon, {
          fontSize: '14px', fontFamily: "'Cinzel Decorative', Georgia, serif",
          color: '#e8c060', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(DEPTH + 3));
        void icon;
        tileBg.on('pointerover', () => {
          clearTip();
          cellTooltip = this.add.text(ix, perksRowY - 4, `${def.name}\n${def.description}`, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
            backgroundColor: '#0d0a0e', padding: { x: 6, y: 4 },
            wordWrap: { width: 200 },
          }).setResolution(2).setOrigin(0.5, 1).setDepth(DEPTH + 12);
          tileBg.setStrokeStyle(1, 0xffe8a0, 1);
        });
        tileBg.on('pointerout', () => {
          clearTip();
          tileBg.setStrokeStyle(1, 0xc8973a, 0.85);
        });
      });
    }

    this.spellPriorityPopups.set(hs, { objects, cdOverlays });
    // Seed cooldown overlays with their current values on open
    this.updateSpellPriorityCooldowns();
  }

  private spawnPet(owner: Hero): void {
    if (!owner.pet) return;
    // Pet goes to the frontline (middle of the battlefield)
    const x = 450 + Math.random() * 60;
    const y = LANE_Y[1] + (Math.random() - 0.5) * 40;
    const sprite = this.add.sprite(x, y, 'hero_archer') as PetSprite;
    sprite.setScale(0.6).setTint(0x22c55e);
    sprite.ownerRef = owner;
    sprite.lastAttackTime = 0;
    sprite.hpBar = this.add.rectangle(x, y - 15, 20, 2, 0x22c55e);
    this.petSprites.push(sprite);
  }

  /** Generate flat-top hexagon points around (cx, cy) with radius R (HoMM3 style). */
  private hexPoints(cx: number, cy: number, R: number): { x: number; y: number }[] {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i; // flat-top: no offset
      pts.push({ x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) });
    }
    return pts;
  }

  /** Draw HoMM3-style connected hex formation grid with interactive hero slots. */
  /** Pick the active formation layout + rear-flank chance based on the current
   *  biome theme. Called once per scene create() BEFORE drawBattlefield / hero
   *  sprite placement so everything downstream reads the right coordinates. */
  private setBiomeFormation(): void {
    const zone = this.infernoMode
      ? getZoneForInferno(this.infernoZone, this.infernoEntryLevel)
      : this.resolveActiveZone();
    let theme = zone.themeKey ?? 'zone_forest';
    // Special-event override: Horde of Goblins and Elite Pack force the
    // CLASSIC 19-slot layout regardless of the underlying biome, because the
    // event balance was tuned for the tighter grid. Without this, picking the
    // Horde card from a Forest stage would inherit the 28-slot expanded grid
    // and the +9 forward cells would let mooks slip past the front line.
    if (BattleScene.hordePending || BattleScene.elitePackPending) {
      theme = 'zone_crypt'; // any classic 19-slot biome works; crypt is neutral
    }
    const slots = BIOME_FORMATIONS[theme] ?? BIOME_FORMATIONS.zone_forest;
    this.formationSlots = slots.map((s) => ({ x: s.x, y: s.y }));
    this.hexGridCells = this.formationSlots.map((s, i) => ({ x: s.x, y: s.y, slotIdx: i }));
    this.biomeRearFlankChance = BIOME_REAR_FLANK_CHANCE[theme] ?? 0;
    // Layered overrides — module defaults first, biome overrides on top.
    const frontOverride = BIOME_FRONT_ROW_OVERRIDE[theme];
    this.activeFrontRow = frontOverride ?? FRONT_ROW_SLOTS;
    const dmgOverride = BIOME_DMG_MULT_OVERRIDE[theme];
    this.activeDmgMult = dmgOverride ? { ...SLOT_ROW_MULT, ...dmgOverride } : SLOT_ROW_MULT;
    const incOverride = BIOME_INC_MULT_OVERRIDE[theme];
    this.activeIncMult = incOverride ? { ...SLOT_INCOMING_MULT, ...incOverride } : SLOT_INCOMING_MULT;
    this.movesPerWaveCap = BIOME_MOVES_PER_WAVE[theme] ?? Infinity;
  }

  /** Slot front-row gate (charge / leap / shield-bash eligibility). Reads the
   *  ACTIVE biome's front-row set so Forest can grant front-row status to the
   *  +9 forward cells without polluting other biomes. */
  private isFrontRow(slotIdx: number | undefined): boolean {
    if (slotIdx === undefined) return false;
    return this.activeFrontRow.has(slotIdx);
  }

  /** Outgoing-damage multiplier for the hero in this slot, biome-aware. */
  private getActiveDmgMult(slotIdx: number | undefined): number {
    if (slotIdx === undefined) return 1;
    return this.activeDmgMult[slotIdx] ?? 1;
  }

  /** Incoming-damage multiplier for the hero in this slot, biome-aware. */
  private getActiveIncMult(slotIdx: number | undefined): number {
    if (slotIdx === undefined) return 1;
    return this.activeIncMult[slotIdx] ?? 1;
  }

  private drawFormationGrid(): void {
    for (const sz of this.formationSlotZones) { sz.zone.destroy(); sz.highlight.destroy(); }
    this.formationSlotZones = [];
    this.formationGridGraphics?.destroy();
    for (const ch of this.frontRowChevrons) ch.destroy();
    this.frontRowChevrons = [];
    // Clear any prior row-modifier labels before redrawing
    for (const lbl of this.rowModifierLabels) lbl.destroy();
    this.rowModifierLabels = [];

    const g = this.add.graphics().setDepth(0.5).setAlpha(0);
    this.formationGridGraphics = g;

    for (const cell of this.hexGridCells) {
      const pts = this.hexPoints(cell.x, cell.y, HEX_R);
      const isSlot = cell.slotIdx !== null;

      g.fillStyle(0xffffff, isSlot ? 0.07 : 0.03);
      g.fillPoints(pts, true);
      g.lineStyle(1, 0xffffff, isSlot ? 0.55 : 0.25);
      g.strokePoints(pts, true);

      if (isSlot) {
        const slotIdx = cell.slotIdx!;
        const hl = this.add.graphics().setDepth(0.8).setAlpha(0);
        hl.fillStyle(0xffffff, 0.22);
        hl.fillPoints(pts, true);
        hl.lineStyle(2, 0xffffff, 0.9);
        hl.strokePoints(pts, true);

        // Front-row / vanguard slots (F, F-, V) show a small amber chevron to
        // mark charge/jump eligibility across every biome. Chevrons live in a
        // dedicated array so their alpha can sync with formationGridGraphics
        // (fades in while a hero is selected for swap, out otherwise).
        if (this.isFrontRow(slotIdx)) {
          const chev = this.add.text(cell.x, cell.y - HEX_R + 4, '➤', {
            fontSize: '11px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
          }).setOrigin(0.5, 0).setDepth(0.9).setAlpha(0);
          this.frontRowChevrons.push(chev);
        }

        // Generous circular hit area — avoids dead spots along hex edges / top row.
        // Kept at depth 1 so it sits UNDER the hero sprite (depth 2) — a filled-slot click still routes to the hero.
        const zone = this.add.zone(cell.x, cell.y, HEX_R * 2.4, HEX_R * 2.4)
          .setDepth(1)
          .setInteractive(new Phaser.Geom.Circle(HEX_R * 1.2, HEX_R * 1.2, HEX_R + 4), Phaser.Geom.Circle.Contains);
        zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          // Right-click anywhere is "cancel swap" — handled by the global
          // input listener. Don't drop a hero into the slot on right-click.
          if (pointer.rightButtonDown()) return;
          this.handleSlotClick(slotIdx);
        });
        zone.on('pointerover', () => {
          // Hover preview also fires for summon swap (Forge Vorrak) so the
          // golem's destination hex highlights the same way as a hero swap.
          const inSwap = !!this.swapSelectedSprite || !!this.swapSelectedSummon;
          if (inSwap) {
            // If the slot is occupied by a hero who's mid-charge / jump /
            // shield-bash, paint the hex RED to signal "drop blocked" — swap
            // would corrupt their baseX/baseY mid-flight.
            const occ = this.heroSprites.find(h => h !== this.swapSelectedSprite && h.getData('slotIdx') === slotIdx);
            const blocked = !!occ && this.isHeroBusy(occ);
            // Bumped fill 0.22 → 0.42 so the pale-white pointer hex stands
            // out clearly against the resting grid (0.07 fill). User asked
            // for a clearer hover preview while moving a hero in the grid.
            hl.clear();
            hl.fillStyle(blocked ? 0xef4444 : 0xffffff, blocked ? 0.32 : 0.42);
            hl.fillPoints(pts, true);
            hl.lineStyle(2, blocked ? 0xef4444 : 0xffffff, 1);
            hl.strokePoints(pts, true);
            hl.setAlpha(1);
            this.showRowModifierTooltip(cell.x, cell.y, slotIdx);
          }
        });
        zone.on('pointerout', () => {
          // Reset hex to its default white before fading out so a future hover
          // doesn't briefly flash the previous red state.
          hl.clear();
          hl.fillStyle(0xffffff, 0.22);
          hl.fillPoints(pts, true);
          hl.lineStyle(2, 0xffffff, 0.9);
          hl.strokePoints(pts, true);
          hl.setAlpha(0);
          this.hideRowModifierTooltip();
        });
        this.formationSlotZones.push({ zone, highlight: hl, slotIdx });
      }
    }
    // (Column badges removed — row info is shown on hover when a hero is selected for swap.)
  }

  /** Floating tooltip shown while hovering a formation slot — explains the row's malus / bonus. */
  private rowModifierTooltip: Phaser.GameObjects.Text | null = null;
  private rowModifierTooltipBg: Phaser.GameObjects.Rectangle | null = null;
  private rowModifierLabels: Phaser.GameObjects.GameObject[] = [];
  private showRowModifierTooltip(cx: number, cy: number, slotIdx: number): void {
    this.hideRowModifierTooltip();
    // Tooltip text derived from the ACTIVE biome's modifiers (not the module-
    // level defaults) so expanded-grid slots (14-27) read their real values —
    // col6 middles (22, 23) show Vanguard, col7 (25-27) stacks Front+ + Vanguard.
    const dmgMult = this.activeDmgMult[slotIdx] ?? 1;
    const incMult = this.activeIncMult[slotIdx] ?? 1;
    const isCharge = this.isFrontRow(slotIdx);
    const isVanguard = incMult < 1 && incMult <= 0.90; // -10% or better incoming

    // Compose the label from the stacked attributes rather than a single fall-
    // through branch. A slot can be Front+Vanguard, Neutral+dodge, etc.
    const parts: string[] = [];
    // Header name tier (priority: Front+ > Vanguard-Front > Front > Vanguard > Neutral > Back > Middle)
    if (dmgMult > 1 && isCharge && isVanguard) {
      parts.push('ELITE ROW');
    } else if (dmgMult > 1 && isCharge) {
      parts.push('FRONT+ ROW');
    } else if (isCharge && isVanguard) {
      parts.push('VANGUARD FRONT');
    } else if (isCharge) {
      parts.push('FRONT ROW');
    } else if (isVanguard) {
      parts.push('VANGUARD');
    } else if (dmgMult <= 0.55) {
      // Steeper gradient — back+ is now -50 % (was -20 %).
      parts.push('BACK+ ROW');
    } else if (dmgMult <= 0.75) {
      // back is -30 % (was -10 %).
      parts.push('BACK ROW');
    } else if (dmgMult <= 0.90) {
      // N tier sits at -15 %.
      parts.push('NEUTRAL ROW');
    } else {
      parts.push('MIDDLE ROW');
    }
    // Effect lines — surfaced in order: damage out, damage in, charge gate.
    if (dmgMult > 1) {
      parts.push(`+${Math.round((dmgMult - 1) * 100)}% damage dealt`);
    } else if (dmgMult < 1) {
      parts.push(`-${Math.round((1 - dmgMult) * 100)}% damage dealt`);
    }
    if (incMult < 1) {
      parts.push(`-${Math.round((1 - incMult) * 100)}% incoming damage`);
    }
    if (isCharge) {
      parts.push('➤ Charge / Jump enabled');
    }
    if (parts.length === 1) parts.push('No modifier');
    const line = parts.join('\n');
    if (!line) return;
    const ty = cy - HEX_R - 36;
    const tx = this.add.text(cx, ty, line, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#e8c870', fontStyle: 'bold',
      align: 'center', lineSpacing: 2,
    }).setResolution(2).setOrigin(0.5, 1).setDepth(50).setAlpha(0.85);
    const w = tx.width + 12;
    const h = tx.height + 8;
    const bg = this.add.rectangle(cx, ty - h / 2 + 4, w, h, 0x0d0a08, 0.45)
      .setStrokeStyle(1, 0xc8973a, 0.45).setDepth(49.9).setOrigin(0.5, 0.5);
    this.rowModifierTooltip = tx;
    this.rowModifierTooltipBg = bg;
  }
  private hideRowModifierTooltip(): void {
    this.rowModifierTooltip?.destroy();
    this.rowModifierTooltipBg?.destroy();
    this.rowModifierTooltip = null;
    this.rowModifierTooltipBg = null;
  }

  /** Assign initial slot indices to all heroes — restores from hero.formationSlot if set.
   *  Migrates stale slot indices that fall outside the active biome's layout
   *  (e.g. a hero saved at slot 23 in Forest landing on a 19-slot Crypt biome).
   *  Also guards against two heroes claiming the same saved slot (possible if
   *  save data got corrupted or a bug in an earlier swap path wrote duplicate
   *  slotIdx values) — the second hero with the same saved slot falls through
   *  to the fallback fill-order search. */
  private assignInitialSlots(): void {
    const slotCount = this.formationSlots.length;
    const usedSlots = new Set<number>();
    // First pass: restore persisted slots, clamping any saved index that's
    // out of bounds for the active layout AND skipping any slot already
    // claimed by an earlier hero (duplicate-save guard). Either condition
    // leaves slotIdx undefined so the next pass picks a fresh slot.
    this.heroSprites.forEach((hs) => {
      const saved = hs.heroRef.formationSlot;
      if (saved === undefined || saved < 0 || saved >= slotCount) return;
      if (usedSlots.has(saved)) {
        // Another hero already took this slot — clear the stale saved index
        // on this hero so the fallback pass reassigns it (and the fallback
        // write persists so the collision doesn't reappear next scene load).
        hs.heroRef.formationSlot = undefined;
        return;
      }
      hs.setData('slotIdx', saved);
      usedSlots.add(saved);
    });
    // Second pass: assign slots to heroes without a saved slot OR whose
    // saved slot was out of bounds for the active biome's layout.
    this.heroSprites.forEach((hs, i) => {
      if (hs.getData('slotIdx') === undefined) {
        // Clamp the fill order to the active layout's slot count so we never
        // assign a slot that doesn't exist in the smaller layouts.
        const validFillOrder = SLOT_FILL_ORDER.filter((s) => s < slotCount);
        const fallback = validFillOrder[Math.min(i, validFillOrder.length - 1)] ?? 0;
        const available = validFillOrder.find((s) => !usedSlots.has(s)) ?? fallback;
        hs.setData('slotIdx', available);
        hs.heroRef.formationSlot = available;
        usedSlots.add(available);
      }
    });
  }

  /** Persist a hero's slot to their hero object so it survives scene restarts. */
  private persistSlot(hs: HeroSprite, slotIdx: number): void {
    hs.setData('slotIdx', slotIdx);
    hs.heroRef.formationSlot = slotIdx;
    this.gsm.save();
  }

  /** Recalculate and apply 1-3-1 diamond formation positions for all current hero sprites. */
  private repositionHeroes(): void {
    const count = this.heroSprites.length;
    if (count === 0) return;
    this.assignInitialSlots();
    this.heroSprites.forEach((hs) => {
      const slotIdx = (hs.getData('slotIdx') as number) ?? 2;
      const slot = this.formationSlots[slotIdx] ?? FORMATION_SLOTS[slotIdx];
      const x = slot.x;
      const y = slot.y;
      // Always update the logical base position so it stays accurate
      hs.baseX = x;
      hs.baseY = y;
      // Skip visual repositioning for warriors/amazons mid-charge/jump — their update loop handles x/y
      const isCharger = hs.heroRef.heroClass === 'warrior';
      const warriorChargeState = isCharger ? ((hs.getData('chargeState') as string) || 'idle') : 'idle';
      const amazonJumpState = (hs.heroRef.heroKey ?? hs.heroRef.heroClass) === 'amazon' ? ((hs.getData('jumpState') as string) || 'idle') : 'idle';
      if (warriorChargeState !== 'idle' || amazonJumpState !== 'idle') return;
      hs.x = x;
      hs.y = y;
      hs.setDepth(2 + y * 0.001);
      hs.hpBarBg.setPosition(x, y - 94);
      hs.hpBar.setPosition(x - 22.5, y - 94);
      hs.mpBar.setPosition(x, y - 88);
      hs.xpBarBg.setPosition(x, y - 99);
      hs.xpBar.setPosition(x - 22.5, y - 99);
      if (hs.buffIconText.alpha > 0) hs.buffIconText.setPosition(x + 26, y - 94);
      // shieldGraphics redraws itself each frame — no position tracking needed
      hs.nameText.setPosition(x, y + 6);
      hs.shadow.setPosition(x, y + hs.shadowYOffset);
    });
  }

  /** Handle click on a hero sprite for position swapping */
  private clearSwapSelection(): void {
    if (!this.swapSelectedSprite && !this.swapSelectedSummon) return;
    this.swapHighlight?.stop();
    this.swapHighlight = null;
    if (this.swapSelectedSprite) {
      this.swapSelectedSprite.setAlpha(1);
      this.swapSelectedSprite.clearTint();
      this.swapSelectedSprite = null;
    }
    if (this.swapSelectedSummon) {
      this.swapSelectedSummon.sprite.setAlpha(1);
      this.swapSelectedSummon.sprite.clearTint();
      this.swapSelectedSummon = null;
    }
    this.formationGridGraphics?.setAlpha(0);
    for (const ch of this.frontRowChevrons) ch.setAlpha(0);
    for (const sz of this.formationSlotZones) {
      sz.highlight.setAlpha(0);
      // Restore default under-hero depth so normal hero clicks work outside swap mode
      sz.zone.setDepth(1);
    }
    this.swapArrowTween?.stop();
    this.swapArrowTween = null;
    this.swapArrow?.destroy();
    this.swapArrow = null;
    this.swapArrowTag?.destroy();
    this.swapArrowTag = null;
    this.swapNameText?.destroy();
    this.swapNameText = null;
  }

  /** True if the hero is currently mid-charge / mid-jump / mid-shield-bash /
   *  mid-wraith-strike — i.e. the sprite isn't at its slot and an in-flight
   *  state machine + tweens are driving the position. Swapping such a hero
   *  (or swapping another hero into their slot) corrupts both heroes' base
   *  positions and creates the "stuck after swap" bug. */
  private isHeroBusy(hs: HeroSprite): boolean {
    const h = hs.heroRef as any;
    if (h._charging || h._leapStriking || h._shieldBashCharging) return true;
    if ((hs as any)._leapStriking || (hs as any)._wraithStriking) return true;
    const chargeState = hs.getData('chargeState') as string | undefined;
    if (chargeState && chargeState !== 'idle') return true;
    const jumpState = hs.getData('jumpState') as string | undefined;
    if (jumpState && jumpState !== 'idle') return true;
    return false;
  }

  private handleHeroSwapClick(sprite: HeroSprite): void {
    if (sprite.heroRef.permanentlyDead) return;
    // Block selection while the hero is hard-CC'd (petrified / frost-rooted).
    // They literally can't move on their own, so the player-driven swap is
    // paused too until the CC expires. Stun/slow/silence are NOT blocked —
    // those are handled separately (silence gates casts, stun freezes anim,
    // slow reduces speed), but they don't lock the hero's formation slot.
    const h = sprite.heroRef as any;
    const ccLocked = !!(h._petrified || h._frostRooted);
    if (!this.swapSelectedSprite && ccLocked) {
      this.showAnnouncement(`${sprite.heroRef.name} is locked — wait for CC to end`);
      return;
    }
    // Block selection while the hero is mid-charge / jump / shield-bash. Their
    // sprite isn't at the slot, their tweens are in flight, and reassigning
    // baseX/baseY mid-action leaves the hero stuck on return.
    if (!this.swapSelectedSprite && this.isHeroBusy(sprite)) {
      this.showAnnouncement(`${sprite.heroRef.name} is mid-action — wait for it to finish`);
      return;
    }
    // Selected → trying to swap with a busy hero: also blocked.
    if (this.swapSelectedSprite && this.swapSelectedSprite !== sprite && this.isHeroBusy(sprite)) {
      this.showAnnouncement(`${sprite.heroRef.name} is mid-action — wait for it to finish`);
      return;
    }

    if (!this.swapSelectedSprite) {
      // Select hero — arrow above head is the indicator (no tint/pulse)
      this.swapSelectedSprite = sprite;
      this.heroTooltip?.destroy();
      this.heroTooltip = null;
      // Name banner anchored just to the RIGHT of the selected hero's
      // portrait frame (per user feedback — was top-left, easy to miss).
      // Vertically centered on the portrait so the eye links banner ↔ frame
      // immediately. Falls back to the runes-row position if the portrait
      // isn't found (defensive — shouldn't happen in normal flow).
      this.swapNameText?.destroy();
      const portraitEntry = this.heroPortraits.find((p) => p.hs === sprite);
      // Portrait frame edges: px=27, PW=50 → right edge at x≈52. Anchor
      // banner at x=56 (4 px gap) so it sits cleanly outside the frame.
      const bannerX = 56;
      const bannerY = portraitEntry ? portraitEntry.bg.y : 56;
      this.swapNameText = this.add.text(bannerX, bannerY, `↕ Moving: ${sprite.heroRef.name}`, {
        fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#ffe8a0', fontStyle: 'bold', stroke: '#1a0f00', strokeThickness: 3,
        backgroundColor: '#0d0a0eaa', padding: { x: 6, y: 3 },
      }).setOrigin(0, 0.5).setDepth(60).setResolution(2);
      // Show all slot hexes while a hero is selected so the player can see every
      // valid drop target at once (not just the one they're hovering).
      this.formationGridGraphics?.setAlpha(1);
      for (const ch of this.frontRowChevrons) ch.setAlpha(0.9);
      // Raise slot zones above hero depth so clicking a slot always routes to the slot,
      // even when another hero's sprite overlaps it (e.g. clicking front-top while a hero
      // stands at front-center whose sprite extends upward over the top slot area).
      for (const sz of this.formationSlotZones) sz.zone.setDepth(10);
      // Bobbing arrow above selected hero. White by default = "movable"; flips
      // red while the hero is control-locked (petrify / frost root) OR when the
      // per-wave move budget is exhausted (Forest grid prototype) to signal
      // "selected but can't move right now".
      const ccLocked = !!((sprite.heroRef as any)._petrified || (sprite.heroRef as any)._frostRooted);
      const cap = this.getEffectiveMovesPerWave();
      // Per-hero remaining moves — pulled from the SELECTED hero's counter,
      // not the old global pool. Arrow + tag now reflect this hero's budget.
      const movesLeft = this.getHeroMovesLeft(sprite);
      const outOfMoves = movesLeft === 0;
      const arrowColor = (ccLocked || outOfMoves) ? '#ef4444' : '#ffffff';
      this.swapArrow = this.add.text(sprite.x, sprite.y - 140, '▼', {
        fontSize: '22px', fontFamily: 'Arial, sans-serif', fontStyle: 'bold',
        color: arrowColor, stroke: '#000000', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(60);
      this.swapArrowTween = this.tweens.add({
        targets: this.swapArrow,
        y: { from: sprite.y - 140, to: sprite.y - 128 },
        duration: 450, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
      // "N left" tag under the arrow — renders only on biomes with a finite
      // move cap. Turns red with the arrow when fully spent.
      if (cap !== Infinity) {
        const tagColor = outOfMoves ? '#ef4444' : '#fbbf24';
        const tagText = outOfMoves ? '0 left' : `${movesLeft} left`;
        this.swapArrowTag = this.add.text(sprite.x, sprite.y - 118, tagText, {
          fontSize: '10px', fontFamily: "'Consolas', 'Courier New', monospace",
          color: tagColor, stroke: '#000000', strokeThickness: 3, fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(60);
      }
      const hint = this.heroSprites.length > 1
        ? `${sprite.heroRef.name} selected — click a slot or hero to move`
        : `${sprite.heroRef.name} selected — click a slot to move`;
      this.showAnnouncement(hint);
    } else if (this.swapSelectedSprite === sprite) {
      this.clearSwapSelection();
    } else {
      // Per-hero move-budget gate. A 2-hero swap moves BOTH heroes, so we
      // verify both can move BEFORE mutating either counter (avoids a
      // half-applied swap where one hero's budget burns but the other was
      // already exhausted). If either is out, the swap aborts cleanly.
      const prev = this.swapSelectedSprite;
      if (!this.canHeroMove(prev)) {
        this.showAnnouncement(`${prev.heroRef.name} already moved this wave`);
        this.clearSwapSelection();
        return;
      }
      if (!this.canHeroMove(sprite)) {
        this.showAnnouncement(`${sprite.heroRef.name} already moved this wave`);
        this.clearSwapSelection();
        return;
      }
      this.incrementHeroMoves(prev);
      this.incrementHeroMoves(sprite);
      // Swap slot indices between two heroes
      const slotA = prev.getData('slotIdx') as number;
      const slotB = sprite.getData('slotIdx') as number;
      this.persistSlot(prev, slotB);
      this.persistSlot(sprite, slotA);
      this.repositionHeroes();
      this.showAnnouncement(`Swapped ${prev.heroRef.name} ↔ ${sprite.heroRef.name}`);
      this.clearSwapSelection();
    }
  }

  /** Effective per-wave move cap PER HERO = biome base + Goblin Kingpin trophy
   *  bonus + tree bonuses. Was a shared global pool; now applies independently
   *  to each hero (and Vorrak's summon path). Trophy / tree bonuses still
   *  scale the cap so a high-level player with both bonuses gets 1 + 2 + 2 = 5
   *  moves per wave PER HERO. */
  private getEffectiveMovesPerWave(): number {
    if (this.movesPerWaveCap === Infinity) return Infinity;
    const trophyBonus = this.hasTrophy('crit damage and +2 swap') ? 2 : 0;
    // v2 talent: Rune Stride / Wind Stride add +1 move per wave each (cap +2 from tree).
    const treeBonus = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).bonusMovesPerWave;
    return this.movesPerWaveCap + trophyBonus + treeBonus;
  }

  /** Moves remaining this wave for the given hero. Returns Infinity for
   *  unlimited biomes. */
  private getHeroMovesLeft(hs: HeroSprite): number {
    const cap = this.getEffectiveMovesPerWave();
    if (cap === Infinity) return Infinity;
    const used = ((hs.heroRef as any)._movesUsedThisWave as number) ?? 0;
    return Math.max(0, cap - used);
  }

  /** Predicate: can this hero perform a swap right now? Used for transactional
   *  pre-checks before mutating state in 2-hero swaps (must verify BOTH can
   *  move before incrementing either, otherwise a half-failed swap leaves one
   *  hero's counter wrongly burned). */
  private canHeroMove(hs: HeroSprite): boolean {
    return this.getHeroMovesLeft(hs) > 0;
  }

  /** Increment the hero's per-wave move counter. No-op when the biome has
   *  an infinite cap. Caller must have already checked `canHeroMove`. */
  private incrementHeroMoves(hs: HeroSprite): void {
    if (this.movesPerWaveCap === Infinity) return;
    const cur = ((hs.heroRef as any)._movesUsedThisWave as number) ?? 0;
    (hs.heroRef as any)._movesUsedThisWave = cur + 1;
  }

  /** Mark a slot as destroyed by Malakhar's Void Hex spell. Idempotent —
   *  re-marking a slot is a no-op so the boss can re-target without leaking
   *  overlay graphics. Side effects:
   *   - Adds the slot to `voidHexedSlots`.
   *   - Draws a darkened/cracked hex overlay over the slot center.
   *   - Auto-evicts any hero currently standing in the slot to the nearest
   *     un-hexed open slot. If no slot is open, the hero stays put (eats
   *     the next tick of damage from the trap before they can be moved).
   */
  private markSlotVoidHexed(slotIdx: number): void {
    if (this.voidHexedSlots.has(slotIdx)) return;
    this.voidHexedSlots.add(slotIdx);

    // Visual: darkened, cracked hex overlay anchored to the slot center.
    const slot = this.formationSlots[slotIdx] ?? FORMATION_SLOTS[slotIdx];
    if (slot) {
      const overlay = this.add.graphics().setDepth(0.7);
      const pts = this.hexPoints(slot.x, slot.y, HEX_R);
      // Dark fill — near-black so the destroyed tile reads at a glance.
      overlay.fillStyle(0x0a0010, 0.85);
      overlay.fillPoints(pts, true);
      // Violet crack outline — pulled from the boss's signature palette.
      overlay.lineStyle(2, 0x4a0066, 0.95);
      overlay.strokePoints(pts, true);
      // Diagonal crack lines — give the tile a "shattered" texture instead
      // of a flat fill. Two short crossing strokes is enough to read.
      overlay.lineStyle(1.5, 0x9d4dff, 0.65);
      overlay.beginPath();
      overlay.moveTo(slot.x - HEX_R * 0.55, slot.y - HEX_R * 0.30);
      overlay.lineTo(slot.x + HEX_R * 0.10, slot.y + HEX_R * 0.45);
      overlay.lineTo(slot.x + HEX_R * 0.50, slot.y - HEX_R * 0.10);
      overlay.strokePath();
      overlay.beginPath();
      overlay.moveTo(slot.x - HEX_R * 0.30, slot.y + HEX_R * 0.10);
      overlay.lineTo(slot.x + HEX_R * 0.45, slot.y + HEX_R * 0.50);
      overlay.strokePath();
      // Flash-in tween so the destruction reads as an event, not an
      // overlay that just appeared.
      overlay.setAlpha(0);
      this.tweens.add({
        targets: overlay,
        alpha: 1,
        duration: 350,
        ease: 'Quad.easeOut',
        onComplete: () => {
          // Once the overlay has fully faded in, drop it into a slow
          // alpha pulse so the destroyed tile reads as actively cursed
          // instead of dead static. Yoyo forever — it'll be killed by
          // the overlay's own destruction (Phase 3 restoration or the
          // scene-init clear).
          this.tweens.add({
            targets: overlay,
            alpha: { from: 1, to: 0.55 },
            duration: 1200,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        },
      });
      // Floating violet rune above the hex — slowly rotating so the tile
      // never reads as static. Anchored as a separate sprite so it can
      // tween independently of the cracked-fill graphic underneath.
      const rune = this.add.text(slot.x, slot.y, '✦', {
        fontSize: '24px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#c080ff',
      }).setOrigin(0.5).setDepth(0.85).setAlpha(0);
      this.tweens.add({
        targets: rune,
        alpha: 0.85,
        duration: 500, delay: 200,
        ease: 'Quad.easeOut',
      });
      this.tweens.add({
        targets: rune,
        rotation: Math.PI * 2,
        duration: 5200,
        repeat: -1,
        ease: 'Linear',
      });
      // Track the rune on the overlay graphic so a later destroy() call
      // (Phase 3 restoration) cleans it up too.
      overlay.setData('rune', rune);
      this.voidHexOverlays.set(slotIdx, overlay);
    }

    // Auto-evict the hero (if any) standing in this slot. They're moved to
    // the nearest non-hexed open slot. If no open slot exists, they keep
    // their (now-destroyed) slot — the next void-hex tick will be their
    // problem to solve via a manual swap.
    const occupant = this.heroSprites.find((h) => h.heroRef.formationSlot === slotIdx);
    if (occupant) {
      // Build the open-slot list excluding hexed and currently-occupied slots.
      const occupied = new Set<number>();
      for (const h of this.heroSprites) {
        if (h === occupant) continue;
        if (h.heroRef.formationSlot != null) occupied.add(h.heroRef.formationSlot);
      }
      let bestSlot: number | null = null;
      let bestDist = Infinity;
      for (let i = 0; i < this.formationSlots.length; i++) {
        if (this.voidHexedSlots.has(i)) continue;
        if (occupied.has(i)) continue;
        const s = this.formationSlots[i];
        const d = Phaser.Math.Distance.Between(slot?.x ?? 0, slot?.y ?? 0, s.x, s.y);
        if (d < bestDist) { bestDist = d; bestSlot = i; }
      }
      if (bestSlot != null) {
        occupant.heroRef.formationSlot = bestSlot;
        const dest = this.formationSlots[bestSlot];
        // Tween the hero to the new slot so the eviction reads as a kick,
        // not a teleport.
        this.tweens.add({
          targets: occupant, x: dest.x, y: dest.y, duration: 280, ease: 'Quad.easeOut',
          onUpdate: () => {
            if (occupant.shadow) occupant.shadow.setPosition(occupant.x, occupant.y);
          },
        });
        occupant.baseX = dest.x;
        occupant.baseY = dest.y;
        this.showDamageNumber(occupant.x, occupant.y - 60, '⌬ EVICTED', '#a040ff', '11px');
      }
    }
  }

  private handleSlotClick(slotIdx: number): void {
    // Void-hexed slots are permanently disabled — Malakhar destroyed them.
    // Reject placement attempts before the rest of the swap state machine
    // runs so neither the move-budget nor the deploy-picker triggers on a
    // dead tile.
    if (this.voidHexedSlots.has(slotIdx)) {
      this.showAnnouncement('That tile is broken — pick another');
      return;
    }
    // Summon (Vorrak) swap path — runs in parallel to the hero swap so
    // the existing hero state machine stays untouched. If the destination
    // slot holds a hero, the two trade slots; if a summon, summons swap;
    // if empty, the golem moves there. No move-budget gate — Vorrak isn't
    // a deployed hero and shouldn't consume the per-wave swap allowance.
    if (this.swapSelectedSummon) {
      const ss = this.swapSelectedSummon;
      if (ss.slotIdx === slotIdx) {                      // clicked own slot — cancel
        this.clearSwapSelection();
        return;
      }
      // Vorrak per-wave move budget — hard 1 per wave, no addups from
      // items / talents / trophies. Cancels selection if exhausted.
      if (this.summonMovesUsedThisWave >= 1) {
        this.showAnnouncement('Vorrak already moved this wave — wait for the next one');
        this.clearSwapSelection();
        return;
      }
      const occHero = this.heroSprites.find(h => h.getData('slotIdx') === slotIdx);
      const occSummon = this.summonSprites.find(s => s !== ss && !s.isDead && s.slotIdx === slotIdx);
      if (occHero && this.isHeroBusy(occHero)) {
        this.showAnnouncement(`${occHero.heroRef.name} is mid-action — wait for it to finish`);
        this.clearSwapSelection();
        return;
      }
      this.summonMovesUsedThisWave++;
      const prevSlot = ss.slotIdx;
      // Move the summon to the new slot
      const newSlot = this.formationSlots[slotIdx];
      ss.slotIdx = slotIdx;
      ss.baseX = newSlot.x;
      ss.baseY = newSlot.y;
      ss.sprite.x = newSlot.x;
      ss.sprite.y = newSlot.y;
      ss.x = newSlot.x;
      ss.y = newSlot.y;
      // Persist the slot on the owner's _summon flag so save/load round-trips.
      const owner = this.heroSprites.find(h => h.heroRef.id === ss.ownerHeroId);
      if (owner) {
        const summonState = (owner.heroRef as any)._summon as { defId: string; hp: number; slotIdx: number } | undefined;
        if (summonState) summonState.slotIdx = slotIdx;
        this.gsm.save();
      }
      // If the destination held something else, push it into the previous slot.
      if (occHero) {
        this.persistSlot(occHero, prevSlot);
        this.repositionHeroes();
      } else if (occSummon) {
        const oldSlot = this.formationSlots[prevSlot];
        occSummon.slotIdx = prevSlot;
        occSummon.baseX = oldSlot.x;
        occSummon.baseY = oldSlot.y;
        occSummon.sprite.x = oldSlot.x;
        occSummon.sprite.y = oldSlot.y;
        occSummon.x = oldSlot.x;
        occSummon.y = oldSlot.y;
      }
      this.clearSwapSelection();
      return;
    }
    if (!this.swapSelectedSprite) return;
    const selected = this.swapSelectedSprite;
    // Block dropping a HERO onto a slot held by a summon — Vorrak's slot
    // is reserved. The summon-swap path handles his repositioning, but a
    // hero swap can't displace him. Cancels the selection silently with
    // a quick announcement so the player understands why it didn't work.
    const blockingSummon = this.summonSprites.find(s => !s.isDead && s.slotIdx === slotIdx);
    if (blockingSummon) {
      this.showAnnouncement('Vorrak\'s slot — click him to move him instead');
      this.clearSwapSelection();
      return;
    }
    // If the selected hero got CC'd between the initial pick and the drop click,
    // abort the swap — matches handleHeroSwapClick's block on selection.
    const sh = selected.heroRef as any;
    if (sh._petrified || sh._frostRooted) {
      this.showAnnouncement(`${selected.heroRef.name} is locked — wait for CC to end`);
      this.clearSwapSelection();
      return;
    }
    // Clicking the selected hero's own slot cancels the selection (useful since the
    // slot zone now sits on top of the sprite during swap mode).
    if (selected.getData('slotIdx') === slotIdx) {
      this.clearSwapSelection();
      return;
    }
    const occupant = this.heroSprites.find(hs => hs !== selected && hs.getData('slotIdx') === slotIdx);
    if (occupant) {
      // Block dropping onto a slot whose occupant is mid-action — the swap would
      // corrupt the in-flight hero's baseX/baseY and leave them stuck.
      if (this.isHeroBusy(occupant)) {
        this.showAnnouncement(`${occupant.heroRef.name} is mid-action — wait for it to finish`);
        this.clearSwapSelection();
        return;
      }
      // Per-hero move-budget gate — both heroes physically move so both
      // budgets are checked / consumed transactionally.
      if (!this.canHeroMove(selected)) {
        this.showAnnouncement(`${selected.heroRef.name} already moved this wave`);
        this.clearSwapSelection();
        return;
      }
      if (!this.canHeroMove(occupant)) {
        this.showAnnouncement(`${occupant.heroRef.name} already moved this wave`);
        this.clearSwapSelection();
        return;
      }
      this.incrementHeroMoves(selected);
      this.incrementHeroMoves(occupant);
      const prevSlot = selected.getData('slotIdx') as number;
      this.persistSlot(selected, slotIdx);
      this.persistSlot(occupant, prevSlot);
      this.showAnnouncement(`Swapped ${selected.heroRef.name} ↔ ${occupant.heroRef.name}`);
    } else {
      // Move into an empty slot — only the moving hero consumes a move.
      if (!this.canHeroMove(selected)) {
        this.showAnnouncement(`${selected.heroRef.name} already moved this wave`);
        this.clearSwapSelection();
        return;
      }
      this.incrementHeroMoves(selected);
      this.persistSlot(selected, slotIdx);
      this.showAnnouncement(`${selected.heroRef.name} moved`);
    }
    this.repositionHeroes();
    this.clearSwapSelection();
  }

  /** Show empty slots where more heroes could be deployed — uses same arc as repositionHeroes.
   *  Only visible during wave 1; after that, deploy from the Heroes menu. */
  private drawEmptyHeroSlots(): void {
    // Clean up old empty slot visuals
    for (const obj of this.emptySlotObjects) obj.destroy();
    this.emptySlotObjects = [];

    // Only show placement indicators during wave 0-1 (pre-battle / first wave)
    if (this.gsm.battle.waveNumber > 1) return;

    // Hide the "+" deploy markers during boss events (Telurion's Lair,
    // Shadowrealm Breach, Horde, Elite Pack). These events are single-wave
    // self-contained encounters — letting the recruit "+" icons leak onto
    // the boss arena read as stray UI debris (user reported "still having
    // those crosses on the map" with empty deploy slots).
    if (BattleScene.telurionPending
        || BattleScene.shadowrealmPending
        || BattleScene.hordePending
        || BattleScene.elitePackPending) return;

    const deployed = this.gsm.player.deployedHeroes.length;
    const maxSlots = this.gsm.getMaxDeploySlots();
    const emptySlots = maxSlots - deployed;
    if (emptySlots <= 0) return;

    const angleStart = -Math.PI * 0.4;
    const angleEnd = Math.PI * 0.4;

    for (let i = 0; i < emptySlots; i++) {
      // Position within the full arc — deployed heroes fill first, empties fill remaining
      const slotIndex = deployed + i;
      const totalSlots = maxSlots;
      const angle = totalSlots === 1
        ? 0
        : angleStart + (slotIndex / (totalSlots - 1)) * (angleEnd - angleStart);
      const x = Math.round(HERO_ARC_CENTER_X + Math.cos(angle) * HERO_ARC_RADIUS);
      const y = Math.round(HERO_ARC_CENTER_Y + Math.sin(angle) * HERO_ARC_RADIUS * 0.6);

      const plus = this.add.text(x, y, '＋', {
        fontSize: '18px', fontFamily: 'monospace', color: '#4b5563',
      }).setOrigin(0.5);
      this.emptySlotObjects.push(plus);

      plus.setInteractive({ useHandCursor: true });
      const handleClick = () => {
        // Check if player has undeployed heroes
        const undeployed: { hero: Hero; idx: number }[] = [];
        this.gsm.player.heroes.forEach((h, idx) => {
          if (!this.gsm.player.deployedHeroes.includes(idx)) undeployed.push({ hero: h, idx });
        });
        if (undeployed.length > 0) {
          this.showDeployPicker(undeployed);
        } else {
          this.scene.launch('ShopScene', { gsm: this.gsm, battleScene: this, infernoMode: this.infernoMode });
          this.showAnnouncement('Recruit a hero!');
        }
      };
      plus.on('pointerdown', handleClick);
    }
  }

  /** In-battle popup to quickly deploy an available hero */
  private showDeployPicker(undeployed: { hero: Hero; idx: number }[]): void {
    this.scene.pause();

    const width = GAME_W, height = GAME_H;
    const popupH = 60 + undeployed.length * 40;
    const popupW = 260;
    const popupX = width / 2;
    const popupY = height / 2;

    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.4)
      .setDepth(200).setInteractive();
    const bg = this.add.rectangle(popupX, popupY, popupW, popupH, 0x1e293b, 0.98)
      .setStrokeStyle(2, 0xb07820).setDepth(201);
    const title = this.add.text(popupX, popupY - popupH / 2 + 18, 'Deploy Hero', {
      fontSize: '13px', fontFamily: 'monospace', color: '#818cf8', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(202);

    const items: Phaser.GameObjects.GameObject[] = [overlay, bg, title];
    const startY = popupY - popupH / 2 + 46;

    undeployed.forEach((entry, i) => {
      const y = startY + i * 40;
      const className = (HERO_CLASS_NAMES as Record<string, string>)[entry.hero.heroClass] || entry.hero.heroClass;
      const label = `${entry.hero.name} (${className}) Lv${entry.hero.level}`;
      const btn = this.add.text(popupX, y, label, {
        fontSize: '11px', fontFamily: 'monospace', color: '#e2e8f0',
        backgroundColor: '#374151', padding: { x: 12, y: 6 },
      }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });
      items.push(btn);

      btn.on('pointerover', () => btn.setStyle({ backgroundColor: '#7a4010' }));
      btn.on('pointerout', () => btn.setStyle({ backgroundColor: '#374151' }));
      btn.on('pointerdown', () => {
        this.gsm.deployHero(entry.idx);
        this.gsm.save();
        cleanup();
        // Sync sprites to include the new hero without resetting existing positions
        this.resumeBattle();
        this.drawEmptyHeroSlots();
      });
    });

    // Close button
    const closeBtn = this.add.text(popupX + popupW / 2 - 16, popupY - popupH / 2 + 6, 'X', {
      fontSize: '14px', fontFamily: 'monospace', color: '#ef4444',
    }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });
    items.push(closeBtn);

    const cleanup = () => items.forEach((o) => o.destroy());
    closeBtn.on('pointerdown', () => { cleanup(); this.resumeBattle(); });
    overlay.on('pointerdown', () => { cleanup(); this.resumeBattle(); });
  }

  /** Refresh deployed heroes and empty slots after deploying a new hero */
  private refreshDeployedHeroes(): void {
    // Remove existing hero sprites
    for (const hs of this.heroSprites) {
      hs.hpBar.destroy();
      hs.hpBarBg.destroy();
      hs.shieldGraphics.destroy();
      hs.mpBar.destroy();
      hs.xpBar.destroy();
      hs.xpBarBg.destroy();
      hs.buffIconText.destroy();
      hs.nameText.destroy();
      hs.shadow.destroy();
      hs.reviveBtn?.destroy();
      hs.reviveTimerText?.destroy();
      if (hs.auraSprites) { for (const spr of Object.values(hs.auraSprites)) spr?.destroy(); }
      this.destroyHeroGrave(hs);
      hs.destroy();
    }
    this.heroSprites = [];
    // Re-spawn all heroes and slots (drawEmptyHeroSlots cleans up its own old objects)
    this.spawnHeroes();
    this.drawEmptyHeroSlots();
  }

  private createHeroSprite(hero: Hero, x: number, y: number): HeroSprite {
    // ── Sanitize any stale transient buff state from a previous session ──────
    // Flags that carry NO stat mutation — just zero the flag
    const h = hero as any;
    h._eagleEyeCrit   = 0;
    h._warCryBuff     = 0;
    h._warCryTimer    = null;
    h._shieldHp       = 0;
    // Valka — Rally Standard / Last Rally transients. Always zeroed on
    // spawn / slot-switch so Rally crit-damage and Last Rally's defence
    // bonus never leak into unintended contexts.
    h._rallyCritDmgBuff = 0;
    h._lastRallyDefBonus = 0;
    h._lastRallyUsed    = false;
    h._shieldBashCharging = false;
    // Runtime stat re-sync — catches any hero whose serialized `stats.attack`
    // or `baseStats.attack` drifted to 0 / a tiny number. If the value is
    // noticeably below its CLASS_BASE_STATS baseline, reconstitute it from
    // base + level growth right here so the live session heals even before
    // a save-reload cycle.
    {
      const bKey = (hero.heroKey ?? hero.heroClass) as keyof typeof CLASS_BASE_STATS;
      const baseEntry = CLASS_BASE_STATS[bKey] ?? CLASS_BASE_STATS[hero.heroClass as keyof typeof CLASS_BASE_STATS];
      const growthEntry = CLASS_STAT_GROWTH[bKey as keyof typeof CLASS_STAT_GROWTH] ?? CLASS_STAT_GROWTH[hero.heroClass as keyof typeof CLASS_STAT_GROWTH];
      if (baseEntry && growthEntry) {
        const lvl = Math.max(1, hero.level);
        const expectedAtk = baseEntry.attack + (lvl - 1) * (growthEntry.attack || 0);
        const atkFloor = Math.max(1, Math.floor(baseEntry.attack * 0.5));
        if ((hero.stats.attack ?? 0) < atkFloor) {
          hero.stats.attack = Math.round(expectedAtk);
        }
        if (!hero.baseStats || (hero.baseStats.attack ?? 0) < atkFloor) {
          hero.baseStats = { ...(hero.baseStats ?? hero.stats), attack: baseEntry.attack };
        }
        if ((hero.baseStats.maxHp ?? 0) < Math.floor(baseEntry.maxHp * 0.5)) {
          hero.baseStats.maxHp = baseEntry.maxHp;
        }
      }
    }
    h._shieldSources  = new Set();
    h._reflectActive  = false;
    h._evasionActive  = false;
    h._radiantSurge   = false;
    h._bloodlustActive = false;
    h._slayerActive   = false;
    h._berserkActive  = false;
    h._berserkCritDmgBuff = 0;
    h._berserkSpdMult = 1;
    h._enrageActive   = false;
    h._enrageTimer    = 0;
    h._battleFuryActive = false;
    h._battleFuryTimer  = 0;
    h._forgefireCount   = 0;
    h._petrified      = false;
    h._frostRooted    = false;
    h._frostRootedTimer = 0;
    h._movesUsedThisWave = 0; // per-wave swap budget — sanitized at scene init
    // Flags with stat mutations — revert the accumulated value then zero the flag
    h._arcaneMightResistBuff = 0; // flag-based: no stat mutation to revert
    h._resistanceBuff = 0;        // elixir_resistance: flag-based
    h._challengeResistTimer = 0;  // Challenge 15% all-resist timer (tank only)
    h._challengeWalking = false;  // Challenge walk-to-cast state
    h._challengeWalkTarget = null;
    h._soulCount = 0;             // Necromancer soul charge — resets on biome change
    h._lastCorpse = null;         // Necromancer raise dead corpse — stale after biome change
    h._boneShieldVfxUntil = 0;    // Bone Shield orbit VFX timestamp — scene.time.now resets per scene
    // Shaman totems: Map doesn't survive JSON round-trip, so a saved _activeTotems comes
    // back as a plain {} and crashes .get(). Always reset to a fresh Map on scene init.
    h._activeTotems = new Map();
    // Shaman buff/shield transient flags — reset on scene init so stale state from a
    // previous run doesn't leak buffs or phantom shield absorbs.
    h._shamanicBuff = undefined;
    h._shamanShield = undefined;
    h._shamanShieldKind = undefined;
    h._totemCycleIdx = 0;
    h._blessActive = false;       // Priest Bless / Druid Regrowth HoT — run-scoped
    h._silencedUntil = 0;         // White Claw Howl of Silence / future silencers
    h._lichFormActive = false;    // Necromancer Lich Form — run-scoped
    h._lichFormTimer = null;
    h._bearFormActive = false;    // Druid Bear Form — run-scoped
    h._bearFormAtkMult = 0;
    h._bearFormDrPct = 0;
    h._aegisDrUntil = 0;          // Aegis of Light invocation halo — timestamp-based, must clear so a stale value doesn't grant pre-buffed DR on a fresh slot
    if (h._stoneFormActive && h._stoneFormDefBonus) {
      hero.stats.defense = Math.max(0, hero.stats.defense - (h._stoneFormDefBonus as number));
      h._stoneFormActive = false;
      h._stoneFormDefBonus = 0;
      h._stoneFormTimer = 0;
    }
    if (h._magmaShieldActive) {
      hero.stats.defense = Math.round(hero.stats.defense / 2);
      h._magmaShieldActive = false;
      h._magmaShieldTimer = null;
    }
    if (h._furyActive && h._furyOriginalAttack !== undefined) {
      hero.stats.attack = h._furyOriginalAttack;
      h._furyActive = false;
      h._furyOriginalAttack = undefined;
    }
    // Casters start every battle with a full mana pool so the first abilities
    // fire promptly. Previously a stale 0-mp value could persist from a mid-drain
    // save, leaving Zhorak unable to cast until his 6/s regen caught up.
    if (hero.stats.maxMp > 0 && hero.heroClass !== 'warrior' && hero.heroClass !== 'assassin') {
      hero.stats.mp = hero.stats.maxMp;
    }
    // Pick the best-matching texture — heroKey first (unique character sheet), then heroClass
    // (base class sheet). If neither is loaded (stale save with a renamed class, missing asset,
    // etc.) fall back to hero_warrior so the slot still shows a real sprite instead of Phaser's
    // green __DEFAULT placeholder, and log which key is missing so we can track it down.
    let texKey = `hero_${hero.heroKey ?? hero.heroClass}`;
    if (!this.textures.exists(texKey)) {
      const classKey = `hero_${hero.heroClass}`;
      if (this.textures.exists(classKey)) {
        console.warn(`[BattleScene] Missing hero texture "${texKey}" — falling back to "${classKey}"`);
        texKey = classKey;
      } else if (this.textures.exists('hero_warrior')) {
        console.warn(`[BattleScene] Missing hero textures "${texKey}" and "${classKey}" — falling back to hero_warrior`);
        texKey = 'hero_warrior';
      }
    }

    // Ground shadow — thin dark line under hero feet (119px display, 0.9× scale).
    // Offset priority (first match wins):
    //   1. HERO_SHADOW_Y_OFFSET manual override — locked-in tuning for sheets
    //      whose auto-measured footY disagrees with the visually correct placement
    //      (e.g. Pixellab sheets with baked drop-shadows that fool pixel scanning).
    //   2. spriteFrameData.footY auto-measure — bottom-most opaque body row in the
    //      idle strip, produced by scripts/measureSpriteFeet.cjs. Lift is derived
    //      from the gap between feet and frame bottom, scaled to the 119px display.
    //   3. 0 — default: shadow sits at the sprite's display bottom.
    const heroKeyForShadow = hero.heroKey ?? hero.heroClass;
    const HERO_SHADOW_Y_OFFSET: Record<string, number> = {
      shaman: -6,
    };
    const frameData = (FRAME_DATA as Record<string, { frameSize?: number; footY?: number } | undefined>)[texKey];
    const autoLift = (() => {
      if (!frameData || typeof frameData.footY !== 'number' || !frameData.frameSize) return 0;
      const displayScale = 119 / frameData.frameSize;
      return -((frameData.frameSize - 1 - frameData.footY) * displayScale);
    })();
    const shadowOffset = HERO_SHADOW_Y_OFFSET[heroKeyForShadow] ?? autoLift;
    const shadow = this.add.ellipse(x, y + shadowOffset, 45, 5, 0x000000, 0.82).setDepth(1.5);

    const sprite = this.add.sprite(x, y, texKey) as HeroSprite;
    sprite.setDisplaySize(119, 119).setOrigin(0.5, 1.0).setDepth(2 + y * 0.001);
    sprite.heroRef = hero;
    sprite.shadow = shadow;
    sprite.shadowYOffset = shadowOffset;
    sprite.lastAttackTime = 0;
    sprite.lastAttackAnimTime = 0;
    sprite.baseX = x;
    sprite.baseY = y;

    // Passive tree flat MP/SP bonuses (applied once at spawn, idempotent via flag)
    { const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
      if (pb.mp > 0 && hero.stats.maxMp > 0 && !(hero as any)._passiveMpApplied) {
        hero.stats.maxMp += pb.mp;
        hero.stats.mp = Math.min(hero.stats.mp + pb.mp, hero.stats.maxMp);
        (hero as any)._passiveMpApplied = true;
      }
      // Glass Cannon — applies -10% max HP per rank (tradeoff for +dmg%). Applied once per spawn.
      if (pb.hpPenalty > 0 && !(hero as any)._glassCannonApplied) {
        const hpReduce = Math.floor(hero.stats.maxHp * pb.hpPenalty);
        hero.stats.maxHp = Math.max(1, hero.stats.maxHp - hpReduce);
        hero.stats.hp = Math.min(hero.stats.hp, hero.stats.maxHp);
        (hero as any)._glassCannonApplied = true;
      }
    }

    // Apply Tome of Vitality (health) bonus — scales with current level's maxHp, not level-1 base
    { const currentVitalityBonus = getTomeBonus(this.gsm.player.tomes, 'health');
      if (currentVitalityBonus > 0) {
        // Remove the previously-applied tome HP first, then compute the new contribution off
        // the PURE base so vitality idol / elixir_fortitude can't compound with the tome.
        const prevAppliedHp = (hero as any)._vitalityAppliedHp as number || 0;
        hero.stats.maxHp -= prevAppliedHp;
        (hero as any)._vitalityAppliedHp = 0;
        const pureBase = this.getPureMaxHp(hero);
        const newVitalityHp = Math.floor(pureBase * currentVitalityBonus);
        hero.stats.maxHp += newVitalityHp;
        const hpDelta = newVitalityHp - prevAppliedHp;
        if (hpDelta !== 0) hero.stats.hp = Math.max(1, Math.min(hero.stats.hp + hpDelta, hero.stats.maxHp));
        (hero as any)._vitalityAppliedHp = newVitalityHp;
        (hero as any)._vitalityTomeLevel = currentVitalityBonus;
      }
    }


    // Bars near hero head level (inside sprite)
    sprite.hpBarBg = this.add.rectangle(x, y - 94, 45, 4, 0x333333).setDepth(3);
    sprite.hpBar = this.add.rectangle(x - 22.5, y - 94, 45, 4, 0x22c55e).setDepth(3).setOrigin(0, 0.5);
    // Shield bar: separate row above HP, drawn each frame via Graphics (pixel-perfect, no overflow possible)
    sprite.shieldGraphics = this.add.graphics().setDepth(4);
    const mpBarColor = hero.heroClass === 'warrior' ? 0xef4444 : hero.heroClass === 'assassin' ? 0xeab308 : 0x3b82f6;
    sprite.mpBar = this.add.rectangle(x, y - 88, 45, 2, mpBarColor).setDepth(3);
    // Thin XP bar (yellow) just above HP bar — same width/alignment as HP bar
    sprite.xpBarBg = this.add.rectangle(x, y - 99, 45, 2, 0x1a1a0a).setDepth(2);
    sprite.xpBar = this.add.rectangle(x - 22.5, y - 99, 0, 2, 0xeab308).setDepth(3).setOrigin(0, 0.5);
    // Self-buff icon display — anchored to right of HP/MP/XP bars, does not follow sprite
    sprite.buffIconText = this.add.text(x + 26, y - 94, '', {
      fontSize: '9px',
    }).setOrigin(0, 0.5).setDepth(4).setAlpha(0);

    // Hero name just below the feet (feet are at y)
    sprite.nameText = this.add.text(x, y + 6, `${hero.name} Lv${hero.level}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#9ca3af',
    }).setOrigin(0.5).setVisible(false);

    // Soul counter — shown to the right of Morveth's health bar, hidden at 0
    if ((hero.heroKey ?? hero.heroClass) === 'necromancer') {
      sprite.soulText = this.add.text(x + 28, y - 94, '☠ 0', {
        fontSize: '11px', fontFamily: "'Cinzel Decorative', Palatino, Georgia, serif", fontStyle: 'bold',
        color: '#6b21a8', stroke: '#1a0030', strokeThickness: 3,
      }).setResolution(2).setOrigin(0, 0.5).setDepth(6).setVisible(false);
    }

    // Tall rectangular hit area covering the hero's full body silhouette
    // — head down to feet — but narrow enough to avoid eating clicks from
    // adjacent columns. Replaces the old tight body-circle (r=fh*0.23 at
    // y=fh*0.62) which only covered chest/waist; the user couldn't click
    // legs/feet, and the head was missed too.
    //
    // Stacking still works correctly: heroes use depth = 2 + y * 0.001,
    // so the lower-on-screen hero is rendered last and wins overlapping
    // clicks. With this rect, hero N's hit area extends DOWN into hero
    // N+1's body region — N+1 captures those clicks (correct, since
    // N+1's body is what's visible there). Hero N's EXCLUSIVE click zone
    // is the upper portion of its hit rect not overlapped by N+1, which
    // contains the head/shoulders/torso that ARE visible. Bottom hero in
    // a stack has no N+1 → full body including legs is clickable.
    //
    // Width 40 % of frame keeps the hit rect inside the body silhouette
    // and away from neighbouring grid columns (slot spacing on the X
    // axis is wider, so 40 % is safe). Vertical 20 %→100 % covers head
    // to feet without padding into the empty space above the frame.
    const fw = sprite.frame?.width ?? 128;
    const fh = sprite.frame?.height ?? 128;
    const hitW = Math.round(fw * 0.40);
    const hitH = Math.round(fh * 0.80);
    const hitX = Math.round((fw - hitW) / 2);
    const hitY = Math.round(fh * 0.20);
    sprite.setInteractive(
      new Phaser.Geom.Rectangle(hitX, hitY, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    // NOTE: hero info tooltip (name / HP / ATK / DEF / CRIT / Status …) has
    // moved from the battlefield sprite hover onto the HUD portrait frame
    // — see `createHeroPortraits` where `bg.on('pointerover', …)` now calls
    // `showHeroInfoTooltip`. Keeping the sprite pointerdown for swap clicks.
    sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Right-click is reserved for "cancel swap" (global handler) — never
      // toggle / select a hero on a right-click swing.
      if (pointer.rightButtonDown()) return;
      this.handleHeroSwapClick(sprite);
    });

    this.heroSprites.push(sprite);

    // Start idle/resting animation — every hero uses their `_idle` loop. Zayla
    // (heroKey 'amazon') used to rest on her looping throw stance, but that left
    // Idle.png + Idle_2.png as dead assets. Now she breathes like everyone else
    // and the throw plays only as a one-shot per ranged auto-attack.
    const hkInit = hero.heroKey ?? hero.heroClass;
    const startAnim = `hero_${hkInit}_idle`;
    if (this.anims.exists(startAnim)) sprite.play(startAnim);


    return sprite;
  }

  private updateHeroes(time: number, _delta: number): void {
    // Wolf's Mantle sigil: keep Thorne permanently in Bear Form — applies to ANY druid
    // hero in the party (main or recruit). The ability auto-cast block below also keys
    // off the sigil, so the two checks must match scope: both party-wide, both druid-only.
    if (this.getSigilEffects().specials.has('wolfs_mantle')) {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.heroClass !== 'druid' || hs.heroRef.isDead) continue;
        if ((hs.heroRef as any)._bearFormActive) continue;
        (hs.heroRef as any)._bearFormActive = true;
        (hs.heroRef as any)._bearFormAtkMult = 0.20;
        (hs.heroRef as any)._bearFormDrPct = 0.25;
        const prevTex = hs.texture.key;
        if (!hs.getData('natureFormPrevTex')) hs.setData('natureFormPrevTex', prevTex);
        if (this.textures.exists('hero_druid_bear')) {
          hs.setTexture('hero_druid_bear');
          if (this.anims.exists('hero_druid_nature_idle')) hs.play('hero_druid_nature_idle');
          // Bear sprite has its own brown coloring — no tint needed; only the slight scale
          // boost so the form reads as bigger than human Thorne.
        }
        hs.setScale(1.12);
        // Bear has a wider footprint — stretch the shadow horizontally. Don't inflate Y:
        // the source frame has ~4–8 px of transparent pad below the feet, so a fat/low
        // shadow reads as detached. Per-frame sync lifts it by BEAR_SHADOW_Y_OFFSET.
        hs.shadow.setScale(2.0, 1.0);
      }
    }
    for (const hs of this.heroSprites) {
      if (hs.heroRef.isDead) {
        // Death animation plays at full alpha; once it finishes, hero is fully hidden
        const deathKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_death`;
        const isPlayingDeath = hs.anims.isPlaying && hs.anims.currentAnim?.key === deathKey;
        if (!isPlayingDeath) {
          hs.setAlpha(0);
          hs.hpBar.setAlpha(0);
          hs.hpBarBg.setAlpha(0);
          hs.mpBar.setAlpha(0);
          hs.xpBar.setAlpha(0);
          hs.xpBarBg.setAlpha(0);
          hs.buffIconText.setAlpha(0);
          hs.shadow.setAlpha(0);
        } else {
          // Shadow fades out in the second half of the death animation
          const deathProgress = hs.anims.getProgress();
          hs.shadow.setAlpha(deathProgress < 0.45 ? 0.82 : Math.max(0, 0.82 * (1 - (deathProgress - 0.45) / 0.35)));
        }
        // Clear all persistent aura sprites so they don't linger after death
        if (hs.auraSprites) {
          for (const spr of Object.values(hs.auraSprites)) spr?.destroy();
          hs.auraSprites = {};
        }
        continue;
      }

      // ── Ability cooldown tick (real-time) ──
      // Tick every ability's currentCooldown by the actual frame delta (already speed-
      // multiplier scaled). Prior code ticked this inside performHeroAttack by
      // getAttackInterval(speed) per auto-attack, which was broken in two ways:
      //   1) silenced / petrified / no-target heroes never drained CDs at all
      //   2) the tick amount used speed-only base interval, but actual attacks can fire
      //      6–10× faster than that when atk-speed sources stack (potion_haste, armor,
      //      relic speedPct, sigil, trophy, haste surge, etc.), so CDR drained 6–10×
      //      faster than real time — Resurrect's clamped 18s CD could empty in ~2-3s.
      // Real-delta tick here gives deterministic wall-clock cooldowns regardless of
      // attack speed.
      for (const ab of hs.heroRef.abilities) {
        if (ab.currentCooldown > 0) ab.currentCooldown = Math.max(0, ab.currentCooldown - _delta);
      }

      // Shield Bash watchdog — if `_shieldBashCharging` has been true longer
      // than the maximum legitimate cycle (forward charge ~0.5s + 9s stay +
      // 0.3s return ≈ 10s, +2s slack = 12s), force-clear and snap back to
      // slot. Belt-and-suspenders against any tween/timer interruption that
      // skips the onComplete that normally clears the flag.
      {
        const hh = hs.heroRef as any;
        if (hh._shieldBashCharging) {
          const startedAt = hh._shieldBashStartedAt as number | undefined;
          if (startedAt === undefined) {
            hh._shieldBashStartedAt = time;
          } else if (time - startedAt > 12000) {
            hh._shieldBashCharging = false;
            hh._shieldBashStartedAt = undefined;
            const sCall = hh._sbStayCall as Phaser.Time.TimerEvent | undefined;
            if (sCall && typeof sCall.remove === 'function') sCall.remove();
            hh._sbStayCall = null;
            this.tweens.killTweensOf(hs);
            hs.setPosition(hs.baseX, hs.baseY);
            hs.shadow?.setPosition(hs.baseX, hs.baseY + hs.shadowYOffset);
            hs.setFlipX(false);
          }
        } else if (hh._shieldBashStartedAt !== undefined) {
          hh._shieldBashStartedAt = undefined;
        }
      }

      // Fade the hero slightly when a rear-flank enemy is CLOSE behind them —
      // lets the player see the incoming flanker when the hero sprite blocks
      // the view. Skipped entirely during Horde of Goblins events (half the
      // horde spawns left-flank so this was leaving heroes permanently
      // semi-transparent for the whole event) and tightened to a 60 px
      // proximity check so only genuinely blocked heroes fade.
      const rearFlankerBehind = !BattleScene.hordePending && this.enemySprites.some((es) =>
        es.active && es.enemyRef.currentHp > 0
        && es.getData('spawnSide') === 'rear'
        && es.x < hs.x - 20
        && hs.x - es.x < 60,
      );
      hs.setAlpha(rearFlankerBehind ? 0.55 : 1);
      hs.shadow.setAlpha(0.82);

      // ── Hero Armory: per-frame movement bookkeeping for Mountain Roots /
      //    Wild Step. We compare the sprite's current x/y against the
      //    cached previous frame's position; even a 0.5 px delta counts as
      //    "moving" so any active charge / re-positioning toggles the buff.
      {
        const h = hs.heroRef as any;
        const lastX = h._lastFrameX as number | undefined;
        const lastY = h._lastFrameY as number | undefined;
        const moved = lastX === undefined || Math.abs(hs.x - lastX) > 0.5 || Math.abs(hs.y - (lastY ?? hs.y)) > 0.5;
        h._lastFrameX = hs.x;
        h._lastFrameY = hs.y;
        if (moved) {
          h._stillSinceMs = undefined; // reset stand-still timer
          // Wild Step — refresh the +7 % dodge / +15 % attack-speed buff window.
          if (this.hasArmoryPassive(hs.heroRef, 'passive_sylvari_wild_step')) {
            h._wildStepBuffUntil = Date.now() + 3000;
          }
        } else if (h._stillSinceMs === undefined) {
          h._stillSinceMs = Date.now();
        }
        // Lumiel Purifying Light — every 15 s, all living allies recover a
        // small percentage of max HP (no dedicated hero-debuff system to
        // cleanse from, so re-purposed as a passive sanctified heal tick;
        // mirrors the "purifying" theme without needing a debuff registry).
        if (this.hasArmoryPassive(hs.heroRef, 'passive_lumiel_purifying_light')) {
          const lastTick = (h._purifyingLastMs as number) ?? 0;
          if (Date.now() - lastTick >= 15_000) {
            h._purifyingLastMs = Date.now();
            const sp = this.effStats(hs.heroRef).spellPower ?? 0;
            // v2 talent Steady Heart amplifies the Purifying Light tick.
            const sh = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).healingReceivedPct;
            const healAmt = Math.max(1, Math.floor((20 + Math.floor(sp * 0.5)) * (1 + sh)));
            for (const ally of this.heroSprites) {
              if (ally.heroRef.isDead) continue;
              const aMax = this.effStats(ally.heroRef).maxHp;
              ally.heroRef.stats.hp = Math.min(aMax, ally.heroRef.stats.hp + healAmt);
              this.showDamageNumber(ally.x, ally.y - 30, `+${healAmt}`, '#cfe7ff', '11px');
            }
          }
        }
      }

      // Warrior rage-cap procs — countdown + tint. Two perks share this
      // pipeline now (Goomar Enrage = red, Valka Battle Fury = gold) and
      // they are mutually exclusive (only one can be active at once because
      // each consumes the rage cap on activation).
      const isRager = hs.heroRef.heroClass === 'warrior';
      if (isRager && (hs.heroRef as any)._enrageActive) {
        (hs.heroRef as any)._enrageTimer -= _delta;
        hs.setTint(0xff2222); // Full red
        if ((hs.heroRef as any)._enrageTimer <= 0) {
          (hs.heroRef as any)._enrageActive = false;
          (hs.heroRef as any)._enrageTimer = 0;
          hs.clearTint();
          this.showDamageNumber(hs.x, hs.y - 40, 'ENRAGE ENDED', '#9ca3af');
        }
      } else if (isRager && (hs.heroRef as any)._battleFuryActive) {
        (hs.heroRef as any)._battleFuryTimer -= _delta;
        hs.setTint(0xffd060); // Gold — distinct from Enrage red
        if ((hs.heroRef as any)._battleFuryTimer <= 0) {
          (hs.heroRef as any)._battleFuryActive = false;
          (hs.heroRef as any)._battleFuryTimer = 0;
          hs.clearTint();
          this.showDamageNumber(hs.x, hs.y - 40, 'FURY FADES', '#9ca3af');
        }
      } else if (isRager && (hs.heroRef as any)._berserkActive) {
        hs.setTint(0xcc3399); // Red/violet berserk tint
      } else if (isRager && !(hs.heroRef as any)._stoneFormActive) {
        hs.clearTint();
      }

      // Warrior Charge — PERK-GATED. Only warriors who have taken a charge
      // armory passive (Goomar's Berserker Rush or Lootfi's Dwarven Charge)
      // ever charge forward. Previously the charge fired unconditionally for
      // every warrior and the perks only tweaked CD/damage numbers, which
      // meant unperked warriors still leapt forward every 18 s. Perk flags
      // reset each frame so mid-run perk swaps behave correctly.
      if (hs.heroRef.heroClass === 'warrior') {
        const h = hs.heroRef as any;
        h._chargeCdOverride = undefined;
        h._chargeBurstMult = undefined;
        h._chargeShockwave = undefined;
        const hasBerserkerRush = this.hasArmoryPassive(hs.heroRef, 'passive_goomar_berserker_rush');
        const hasDwarvenCharge = this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_dwarven_charge');
        if (hasBerserkerRush) {
          h._chargeCdOverride = 18000;
          h._chargeBurstMult = 1.6;
          h._chargeShockwave = 0;
        } else if (hasDwarvenCharge) {
          h._chargeCdOverride = 24000;
          h._chargeBurstMult = 1.9;
          h._chargeShockwave = 0.80; // 80 % of the landing hit splashed to enemies within 160 px
        }
        // No perk → no charge. Make sure any stale state-machine data from a
        // previous perk is cleared so the hero can't get stuck mid-charge.
        // Skip 'idle' — it's the resting state, not stale. resetHeroToSlotOnRevive
        // sets chargeState='idle' for every warrior on revive (Valka has no charge
        // perk but goes through the warrior branch); without this guard the cleanup
        // ran every frame post-rez and `tweens.killTweensOf(hs)` killed Shield Bash
        // mid-slide, leaving `_shieldBashCharging=true` until the 12 s watchdog.
        if (!hasBerserkerRush && !hasDwarvenCharge) {
          const chState = hs.getData('chargeState') as string | undefined;
          if (chState && chState !== 'idle') {
            hs.setData('chargeState', 'idle');
            this.resetChargeToBase(hs);
          }
        } else {
          this.updateChargeHero(hs, time, _delta);
        }
      }

      // ── Amazon Jump Cycle ──
      if ((hs.heroRef.heroKey ?? hs.heroRef.heroClass) === 'amazon') {
        this.updateAmazonJump(hs, time, _delta);
      }

      // ── Tank: stationary — holds formation slot, never advances or retreats ──
      // Ironheld is an immovable wall: he fights entirely from his slot using ranged auto-attacks and Challenge/Taunt.
      if (hs.heroRef.heroClass === 'tank') {
        // Clear any legacy walk state from previous builds
        (hs.heroRef as any)._challengeWalking = false;
        (hs.heroRef as any)._challengeWalkTarget = null;
        // Snap to base slot and stay facing forward
        hs.x = hs.baseX;
        hs.y = hs.baseY;
        hs.setFlipX(false);
        const curAnim = hs.anims.currentAnim?.key;
        const isAttackingOrHurt = curAnim === 'hero_tank_attack' || curAnim === 'hero_tank_hurt';
        if (this.anims.exists('hero_tank_idle') && !isAttackingOrHurt && curAnim !== 'hero_tank_idle') {
          hs.play('hero_tank_idle');
        }

        // Challenge resist buff timer
        if ((hs.heroRef as any)._challengeResistTimer > 0) {
          (hs.heroRef as any)._challengeResistTimer -= _delta;
          if ((hs.heroRef as any)._challengeResistTimer <= 0) {
            (hs.heroRef as any)._challengeResistTimer = 0;
            (hs.heroRef as any)._resistanceBuff = Math.max(0, ((hs.heroRef as any)._resistanceBuff || 0) - 15);
          }
        }
      }

      // ── Stone Form timer ──
      if ((hs.heroRef as any)._stoneFormActive) {
        (hs.heroRef as any)._stoneFormTimer -= _delta;
        if ((hs.heroRef as any)._stoneFormTimer <= 0) {
          (hs.heroRef as any)._stoneFormActive = false;
          // Revert the stored defense bonus
          const storedBonus = (hs.heroRef as any)._stoneFormDefBonus as number || 0;
          hs.heroRef.stats.defense = Math.max(0, hs.heroRef.stats.defense - storedBonus);
          (hs.heroRef as any)._stoneFormDefBonus = 0;
          hs.clearTint();
          this.showDamageNumber(hs.x, hs.y - 40, 'Stone Form ended', '#9ca3af');
        } else {
          hs.setTint(0x4a5a6e); // dark stone tint while active
        }
      }

      // ── Frost Root timer (Archivist lich's frost nova) ──
      if ((hs.heroRef as any)._frostRooted) {
        (hs.heroRef as any)._frostRootedTimer -= _delta;
        if ((hs.heroRef as any)._frostRootedTimer <= 0) {
          (hs.heroRef as any)._frostRooted = false;
          hs.clearTint();
        } else {
          hs.setTint(0x8ad4ff); // icy-blue tint while rooted
        }
      }

      // ── Gorgon Petrify timer ──
      if ((hs.heroRef as any)._petrified) {
        (hs.heroRef as any)._petrifiedTimer -= _delta;
        if ((hs.heroRef as any)._petrifiedTimer <= 0) {
          (hs.heroRef as any)._petrified = false;
          hs.clearTint();
          // Remove petrify FX overlay
          hs.petrifyOverlay?.destroy();
          hs.petrifyOverlay = undefined;
        } else {
          hs.setTint(0x8a8a8a); // grey stone tint while petrified
          // Spawn overlay on first tick of petrify
          if (!hs.petrifyOverlay && this.textures.exists('effect_petrify')) {
            hs.petrifyOverlay = this.add.sprite(hs.x, hs.y - 66, 'effect_petrify')
              .setDisplaySize(110, 110).setDepth(5).setAlpha(0.85);
            if (this.anims.exists('fx_petrify')) hs.petrifyOverlay.play('fx_petrify');
          }
          // Keep overlay locked to hero body center
          hs.petrifyOverlay?.setPosition(hs.x, hs.y - 66);
        }
      } else if (hs.petrifyOverlay) {
        // Guard: clean up if flag cleared externally (e.g. anti-CC item)
        hs.petrifyOverlay.destroy();
        hs.petrifyOverlay = undefined;
        hs.clearTint();
      }

      // Always sync HUD positions — hero can be moving even when no enemies are present
      hs.hpBarBg.setPosition(hs.x, hs.y - 94);
      hs.hpBar.setPosition(hs.x - 22.5, hs.y - 94);
      hs.mpBar.setPosition(hs.x, hs.y - 88);
      hs.xpBarBg.setPosition(hs.x, hs.y - 99);
      hs.xpBar.setPosition(hs.x - 22.5, hs.y - 99);
      hs.nameText.setPosition(hs.x, hs.y + 6);
      // Bear form: the bear sheet has transparent pad below the feet, so the shadow
      // would otherwise render below the visible paws. Lift it so it sits right under
      // the bear's belly line.
      // -12 was too high (shadow visibly behind the bear's rear legs); -4 settles the
      // ellipse just under the belly/paw line where it reads as ground contact.
      const BEAR_SHADOW_Y_OFFSET = -4;
      const shadowY = hs.y + ((hs.heroRef as any)._bearFormActive ? BEAR_SHADOW_Y_OFFSET : hs.shadowYOffset);
      hs.shadow.setPosition(hs.x, shadowY);
      if (hs.buffIconText.alpha > 0) hs.buffIconText.setPosition(hs.x + 26, hs.y - 94);
      // Shield bar — redraw every frame BEFORE the no-target early-exit so the
      // bar follows the sprite during post-wave return tweens (charge / leap /
      // shield-bash). Without this, the graphics layer freezes at the last
      // drawn position when all enemies are dead and the hero is sliding back.
      {
        const statsForShield = this.effStats(hs.heroRef);
        this.redrawHeroShieldBar(hs, statsForShield.maxHp);
      }

      // ── Persistent aura sprites — follow hero, show while buff is active ──
      // origin(0.5, 0.5): center of sprite placed at hs.y + yOffset.
      // Ground auras: yOffset=0 → center at feet/shadow → glow ring sits around the ground.
      // Berserk floats at mid-body (yOffset=-50).
      this.updateHeroAura(hs, 'arcane', 'effect_aura_arcane', 'fx_aura_arcane',
        ((hs.heroRef as any)._arcaneMightResistBuff || 0) > 0, 96, -50);
      this.updateHeroAura(hs, 'berserk', 'effect_aura_berserk', 'fx_aura_berserk',
        !!(hs.heroRef as any)._berserkActive, 80, -80);
      // Only show the bless aura sprite when the buff came from Lumiel's Bless (not Thorne's Regrowth).
      this.updateHeroAura(hs, 'bless', 'effect_aura_bless', 'fx_aura_bless',
        !!(hs.heroRef as any)._blessActive && !(hs.heroRef as any)._blessFromRegrowth, 80, -22, 0, -1);
      this.updateHeroAura(hs, 'shield', 'effect_aura_shield', 'fx_aura_shield',
        ((hs.heroRef as any)._shieldHp ?? 0) > 0, 96, -40);
      // Bone orbit VFX is Morveth-exclusive — other heroes / relics keep the standard blue shield aura.
      const isMorveth = (hs.heroRef.heroKey ?? hs.heroRef.heroClass) === 'necromancer';
      // VFX fades ~2s after cast even though the shield itself absorbs damage for longer —
      // the perpetual orbit was visually noisy.
      const boneVfxUntil = (hs.heroRef as any)._boneShieldVfxUntil ?? 0;
      this.updateBoneShieldOrbit(hs, isMorveth && ((hs.heroRef as any)._shieldHp ?? 0) > 0 && time < boneVfxUntil, time);
      // Shaman Spirit Shield — 5 orbiting orbs (mana blue or lightning bolt) circle the
      // hero, updated per-frame like bone shield. Expires on its own timer; break is
      // handled in the enemyAttackHero intercept which zeroes _shamanShield directly.
      {
        const ss = (hs.heroRef as any)._shamanShield as { kind: 'mana' | 'lightning'; expiry: number } | undefined;
        if (ss && time > ss.expiry) {
          (hs.heroRef as any)._shamanShield = undefined;
        }
        const current = (hs.heroRef as any)._shamanShield as { kind: 'mana' | 'lightning' } | undefined;
        this.updateShamanShieldOrbit(hs, current?.kind ?? null, time);
      }
      // Vitality Idol: keep the +0.2%/stack maxHp bonus in sync with the hero's current stack count.
      this.updateVitalityIdolHp(hs.heroRef);

      const target = this.findNearestEnemy(hs);
      if (!target) continue;

      const stats = this.effStats(hs.heroRef);
      const baseInterval = getAttackInterval(stats.speed);
      // Speed Tome + passive tree attack speed
      const speedBonus = getTomeBonus(this.gsm.player.tomes, 'speed');
      const passiveAtkSpd = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).atkSpd;
      const totalSpeedBonus = Math.min(speedBonus + passiveAtkSpd, 0.75);
      let interval = totalSpeedBonus > 0 ? baseInterval * (1 - totalSpeedBonus) : baseInterval;
      // Potion of Haste: +30% attack speed
      if (this.gsm.hasActiveConsumable('potion_haste')) interval *= 0.7;
      // Trophy: Bloodfury Totem — +10% attack speed for all heroes
      if (this.hasTrophy('attack speed')) interval *= 0.9;
      // Sigil attack-speed affix — additive with other speed sources.
      const sigEff0 = this.getSigilEffects();
      if (sigEff0.attackSpeedPct > 0) interval *= Math.max(0.3, 1 - sigEff0.attackSpeedPct / 100);
      // Hunter armor (medium sub-type) — armor-rolled attackSpeedPct stacks
      // multiplicatively with sigil/relic atk-speed sources.
      const armorAtkSpd = stats.attackSpeedPct ?? 0;
      if (armorAtkSpd > 0) interval *= Math.max(0.3, 1 - armorAtkSpd / 100);
      // Relic: Idol of Swiftness, Slayer Emblem active buff
      { const rb = this.getRelicBonuses(hs.heroRef);
        if (rb.speedPct > 0) interval *= Math.max(0.3, 1 - rb.speedPct);
        if (rb.hasSlayer && (hs.heroRef as any)._slayerActive) interval *= 0.75; }
      // Berserk: +20% attack speed inline (never touches stats.speed)
      if ((hs.heroRef as any)._berserkActive) {
        interval /= (hs.heroRef as any)._berserkSpdMult || 1.2;
      }
      // Valka — Battle Fury perk: +35 % attack speed while active. Same
      // shape as Berserk (inline mult, no stat write-back).
      if ((hs.heroRef as any)._battleFuryActive) {
        interval /= 1.35;
      }
      // War Cry buff (Amazon): reduces attack interval for all heroes
      if ((hs.heroRef as any)._warCryBuff > 0) {
        interval *= Math.max(0.4, 1 - (hs.heroRef as any)._warCryBuff);
      }
      // Staff Haste Surge proc: +30% attack speed for 4s
      if ((hs.heroRef as any)._hasteSurgeBuff) interval *= 0.70;
      // v2 talent: Bloodfrenzy — kill grants stacking +X% atk speed for 5s (max 5 stacks).
      {
        const pbBF = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).bloodfrenzyAtkSpd;
        if (pbBF > 0) {
          const stacks = ((hs.heroRef as any)._bloodfrenzyStacks as { exp: number }[] | undefined) ?? [];
          const now = Date.now();
          const live = stacks.filter((s) => s.exp > now);
          (hs.heroRef as any)._bloodfrenzyStacks = live;
          if (live.length > 0) {
            const totalPct = Math.min(0.5, pbBF * live.length); // hard cap +50%
            interval *= Math.max(0.3, 1 - totalPct);
          }
        }
      }

      if (time - hs.lastAttackTime >= interval
          && !(hs.heroRef as any)._petrified
          && !(hs.heroRef as any)._frostRooted
          && !(hs as any)._wraithStriking      // Kael is mid-teleport on Dirty Blades — auto-attacks would fire at the teleport-in position and look like redundant double-strikes
          && !(hs as any)._leapStriking        // same for Zayla's Leap Strike
          && !(hs.heroRef as any)._shieldBashCharging  // Valka mid Shield Bash slide — auto-attacks would fire at the travel-in position and look like doubled hits
      ) {
        hs.lastAttackTime = time;
        this.performHeroAttack(hs, target, time);
      }

      // Per-frame safety clamp: if HP somehow exceeds the effective max
      // (e.g. equipment swap dropped a +HP item without clamping, or a
      // boon multiplier shrank), bring it back in line so the HP-bar
      // doesn't overflow and the tooltip never reads "7244 / 5388".
      if (hs.heroRef.stats.hp > stats.maxHp) {
        hs.heroRef.stats.hp = stats.maxHp;
      }
      // Update bars — all HUD elements follow sprite position every frame
      const hpPct = hs.heroRef.stats.hp / stats.maxHp;
      hs.hpBar.width = 45 * Math.max(0, hpPct);
      hs.hpBar.setFillStyle(hpPct > 0.5 ? 0x22c55e : hpPct > 0.25 ? 0xeab308 : 0xef4444);
      // Shield bar — extracted so the pause-sync can redraw it at the current hero
      // position too (otherwise the shield bar hangs in empty space if the sprite moves
      // mid-pause). Reads absolute x/y from the sprite every call.
      this.redrawHeroShieldBar(hs, stats.maxHp);
      const xpPct = hs.heroRef.xpToNextLevel > 0 ? hs.heroRef.xp / hs.heroRef.xpToNextLevel : 0;
      hs.xpBar.width = 45 * Math.max(0, Math.min(1, xpPct));
      hs.xpBar.setAlpha(1);
      hs.xpBarBg.setAlpha(1);
      hs.buffIconText.setAlpha(0);
      const mpPct = hs.heroRef.heroClass === 'warrior'
        ? hs.heroRef.stats.rage / (stats.maxRage || 1)
        : hs.heroRef.heroClass === 'assassin'
          ? hs.heroRef.stats.energy / (stats.maxEnergy || 1)
          : hs.heroRef.stats.mp / stats.maxMp;
      hs.mpBar.width = 45 * Math.max(0, mpPct);
      const reloading = hs.heroRef.heroClass === 'archer' && hs.heroRef.stats.reloadTimer > 0;
      const enraging = hs.heroRef.heroClass === 'warrior' && (hs.heroRef as any)._enrageActive;
      const battleFurying = hs.heroRef.heroClass === 'warrior' && (hs.heroRef as any)._battleFuryActive;
      const stoneForming = (hs.heroRef as any)._stoneFormActive && !enraging && !battleFurying;
      const petrified = !!(hs.heroRef as any)._petrified;
      hs.nameText.setText(
        reloading ? 'Reloading...'
          : enraging ? 'ENRAGED!'
          : battleFurying ? 'BATTLE FURY!'
          : stoneForming ? 'Stone Form!'
          : petrified ? '🪨 Petrified!'
          : `${hs.heroRef.name} Lv${hs.heroRef.level}`,
      );

      // Soul counter to the right of health bar — hidden when 0
      if (hs.soulText) {
        const souls = (hs.heroRef as any)._soulCount ?? 0;
        hs.soulText.setPosition(hs.x + 28, hs.y - 94);
        if (souls > 0) {
          hs.soulText.setText(`☠ ${souls}`);
          hs.soulText.setColor(souls >= 10 ? '#e879f9' : souls >= 5 ? '#c084fc' : '#a855f7');
          hs.soulText.setVisible(true);
        } else {
          hs.soulText.setVisible(false);
        }
      }
    }
  }

  // ── Warrior Berserker Rush ────────────────────────────────────────────────
  private readonly WARRIOR_CHARGE_CD    = 18000; // ms between charges
  private readonly WARRIOR_CHARGE_SPEED = 500;   // px/s while charging forward
  private readonly WARRIOR_RETURN_SPEED = 400;   // px/s while returning
  private readonly WARRIOR_CHARGE_STAY  = 9000;  // ms spent at enemy before returning

  /** Charge update for warrior-class heroes */
  private updateChargeHero(hs: HeroSprite, _time: number, delta: number): void {
    const hk = hs.heroRef.heroKey ?? hs.heroRef.heroClass;
    const h = hs.heroRef as any;
    // Passive overrides set by the caller (Goomar vs Lootfi variants). Any
    // unset override falls back to the constant defaults.
    const chargeCd   = (h._chargeCdOverride as number | undefined) ?? this.WARRIOR_CHARGE_CD;
    const burstMult  = (h._chargeBurstMult   as number | undefined) ?? 1.6;
    const shockwave  = (h._chargeShockwave   as number | undefined) ?? 0;
    const chargeSpd  = this.WARRIOR_CHARGE_SPEED;
    const returnSpd  = this.WARRIOR_RETURN_SPEED;
    const stayTime   = this.WARRIOR_CHARGE_STAY;
    const runAnim    = `hero_${hk}_run_anim`;
    const chargeAnim = `hero_${hk}_charge_anim`;
    const idleAnim   = `hero_${hk}_idle`;
    const rushLabel  = hk === 'dwarf' ? 'DWARVEN CHARGE!' : 'BERSERKER RUSH!';

    const state = (hs.getData('chargeState') as string) || 'idle';

    // Shadow follows sprite feet; all other UI stays anchored at base position
    hs.shadow.setPosition(hs.x, hs.y + hs.shadowYOffset);

    if (state === 'idle') {
      let cd = ((hs.getData('chargeCd') as number) ?? chargeCd) - delta;
      hs.setData('chargeCd', cd);
      if (cd > 0) return;

      // Front-row gate: charge only triggers from front column / vanguard slots
      const slotIdx = hs.getData('slotIdx') as number | undefined;
      if (slotIdx === undefined || !this.isFrontRow(slotIdx)) return;

      const enemy = this.findNearestEnemy(hs);
      if (!enemy) return;

      // ── Start the charge ──
      hs.setData('chargeState', 'charging');
      hs.setData('chargeCd', chargeCd);
      hs.setData('chargeDestX', enemy.x - 70);
      hs.setData('chargeDestY', enemy.y);
      hs.setData('chargeTargetRef', enemy);
      hs.setData('chargeStayTimer', null);
      hs.setFlipX(false);
      this.showDamageNumber(hs.x, hs.y - 60, rushLabel, '#f97316');
      if (this.anims.exists(runAnim)) hs.play(runAnim);

    } else if (state === 'charging') {
      // Track the enemy live — both X and Y
      const trackEnemy = hs.getData('chargeTargetRef') as EnemySprite | null;
      if (trackEnemy && trackEnemy.active && trackEnemy.enemyRef.currentHp > 0) {
        hs.setData('chargeDestX', trackEnemy.x - 40);
        hs.setData('chargeDestY', trackEnemy.y);
      }
      const destX = (hs.getData('chargeDestX') as number) ?? hs.baseX;
      const destY = (hs.getData('chargeDestY') as number) ?? hs.baseY;
      const step  = chargeSpd * (delta / 1000);
      hs.x = Math.min(hs.x + step, destX);
      // Slide Y toward enemy lane at same speed
      if (hs.y < destY) hs.y = Math.min(hs.y + step, destY);
      else if (hs.y > destY) hs.y = Math.max(hs.y - step, destY);
      hs.shadow.setPosition(hs.x, hs.y + hs.shadowYOffset);

      if (hs.x >= destX) {
        if (hs.heroRef.isDead) { this.resetChargeToBase(hs); return; }

        hs.setData('chargeStayTimer', stayTime);
        hs.setData('chargeState', 'at_enemy');

        if (this.anims.exists(chargeAnim)) {
          hs.off('animationcomplete');
          hs.play(chargeAnim);
          hs.once('animationcomplete', () => {
            if (!hs.heroRef.isDead && this.anims.exists(idleAnim)) hs.play(idleAnim);
          });
        }

        const targetEnemy = hs.getData('chargeTargetRef') as EnemySprite | null;
        if (targetEnemy && targetEnemy.active && targetEnemy.enemyRef.currentHp > 0) {
          const stats = this.effStats(hs.heroRef);
          const chargeRowMult = this.getActiveDmgMult(hs.getData('slotIdx') as number | undefined);
          const burstDmg = Math.max(1, Math.floor(stats.attack * burstMult * chargeRowMult));
          targetEnemy.enemyRef.currentHp -= burstDmg;
          this.showDamageNumber(targetEnemy.x, targetEnemy.y - 30, `${burstDmg}`, '#f97316', '16px');
          if (targetEnemy.enemyRef.currentHp <= 0) this.killEnemy(targetEnemy, hs);

          // Lootfi's Dwarven Charge shockwave — landing also splashes nearby
          // enemies for a fraction of the burst damage. Goomar's Berserker
          // Rush has shockwave=0, so the loop is a no-op for him.
          if (shockwave > 0) {
            const splashDmg = Math.max(1, Math.floor(burstDmg * shockwave));
            for (const es of this.enemySprites) {
              if (es === targetEnemy || !es.active || es.enemyRef.currentHp <= 0) continue;
              if (Math.abs(es.x - targetEnemy.x) <= 160 && Math.abs(es.y - targetEnemy.y) <= 80) {
                es.enemyRef.currentHp -= splashDmg;
                this.showDamageNumber(es.x, es.y - 24, `${splashDmg}`, '#fbbf24', '12px');
                if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
              }
            }
            // Inline expanding ring — private helper on VFXManager isn't public.
            {
              const ring = this.add.circle(targetEnemy.x, targetEnemy.y + 10, 10, 0xfbbf24, 0)
                .setStrokeStyle(2, 0xfbbf24, 0.9).setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
              this.tweens.add({
                targets: ring, radius: 80, alpha: 0, duration: 360, ease: 'Cubic.easeOut',
                onComplete: () => ring.destroy(),
              });
            }
          }
        }
      }

    } else if (state === 'at_enemy') {
      // Pin to enemy's X and Y while staying
      const pinEnemy = hs.getData('chargeTargetRef') as EnemySprite | null;
      if (pinEnemy && pinEnemy.active && pinEnemy.enemyRef.currentHp > 0) {
        hs.x = Math.max(hs.baseX, pinEnemy.x - 40);
        hs.y = pinEnemy.y;
      }
      hs.shadow.setPosition(hs.x, hs.y + hs.shadowYOffset);
      const rawStay = hs.getData('chargeStayTimer') as number | null;
      if (rawStay === null || rawStay === undefined) return;
      const stay = rawStay - delta;
      hs.setData('chargeStayTimer', stay);
      if (stay <= 0) this.returnChargeToBase(hs);

    } else if (state === 'returning') {
      const step = returnSpd * (delta / 1000);
      hs.x = Math.max(hs.x - step, hs.baseX);
      // Also return Y to base lane
      if (hs.y < hs.baseY) hs.y = Math.min(hs.y + step, hs.baseY);
      else if (hs.y > hs.baseY) hs.y = Math.max(hs.y - step, hs.baseY);
      hs.shadow.setPosition(hs.x, hs.y + hs.shadowYOffset);

      if (hs.x <= hs.baseX) {
        this.resetChargeToBase(hs);
      }
    }
  }

  private returnChargeToBase(hs: HeroSprite): void {
    const runAnim = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_run_anim`;
    hs.setData('chargeState', 'returning');
    hs.setFlipX(true); // face left while running back
    if (this.anims.exists(runAnim)) hs.play(runAnim);
  }

  /**
   * Apply a shield to a hero from a named source.
   * Returns false and does nothing if the same source already has a shield active on this hero.
   * Clears the source registry when the shield pool reaches 0.
   */
  /**
   * Show or hide a persistent looping aura sprite on a hero.
   * key: unique id (e.g. 'arcane'). texKey/animKey: spritesheet + animation.
   * active: whether the buff is currently on. size: display size in px.
   * originY: vertical anchor (0.5 = center, 0.7 = slightly below center to cover body).
   */
  /** Trigger the RPG-styled resurrection effect: golden flash + rising light pillar + particle sparkle. */
  private showResurrectAura(hs: HeroSprite): void {
    const { x, y } = hs;
    // 1) Bright golden flash at hero position (quick burst)
    const flash = this.add.circle(x, y - 35, 46, 0xffe080, 0.85).setDepth(hs.depth + 2).setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({ targets: flash, alpha: 0, scale: 2.2, duration: 520, onComplete: () => flash.destroy() });
    // 2) Thin warm light pillar rising from feet
    const pillar = this.add.rectangle(x, y - 40, 28, 110, 0xffc854, 0.55).setDepth(hs.depth + 1).setBlendMode(Phaser.BlendModes.ADD);
    pillar.setOrigin(0.5, 0.5);
    this.tweens.add({ targets: pillar, alpha: 0, scaleY: 1.4, duration: 1100, onComplete: () => pillar.destroy() });
    // 3) Rising golden sparkles — small triangles floating up
    for (let i = 0; i < 9; i++) {
      const sx = x + (Math.random() - 0.5) * 30;
      const sy = y - 4 - Math.random() * 14;
      const sp = this.add.star(sx, sy, 4, 1.2, 3, 0xfff0b0, 1).setDepth(hs.depth + 2).setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: sp,
        y: sy - 50 - Math.random() * 30,
        alpha: 0,
        duration: 900 + Math.random() * 300,
        delay: i * 35,
        ease: 'Sine.easeOut',
        onComplete: () => sp.destroy(),
      });
    }
  }

  private updateHeroAura(
    hs: HeroSprite, key: string, texKey: string, animKey: string,
    active: boolean, size: number, yOffset = -30,
    cropTop = 0, depthOffset = 1,
  ): void {
    if (!hs.auraSprites) hs.auraSprites = {};
    const existing = hs.auraSprites[key];
    const ax = hs.x;
    const ay = hs.y + yOffset;
    const depth = hs.depth + depthOffset;
    if (active) {
      if (!existing || !existing.active) {
        const aura = this.add.sprite(ax, ay, texKey)
          .setDisplaySize(size, size)
          .setOrigin(0.5, 0.5)
          .setDepth(depth)
          .setAlpha(0.75);
        if (cropTop > 0) aura.setCrop(0, cropTop, size, size - cropTop);
        if (this.anims.exists(animKey)) aura.play({ key: animKey, repeat: -1 });
        hs.auraSprites[key] = aura;
      } else {
        existing.setPosition(ax, ay);
        existing.setDepth(depth);
      }
    } else if (existing?.active) {
      existing.destroy();
      delete hs.auraSprites[key];
    }
  }

  /** Position-only sync for hero-attached visuals (aura sprites, HP/MP/XP bars, name text,
   *  shadow, buff icon, shield bar). Used to keep attachments glued to the hero sprite even
   *  while the main update loop is paused — prevents aura glows and shield bars hanging in
   *  empty space when formation swaps/respawns reposition the sprite during a manual pause
   *  or a scene-resume frame. Creation / teardown of aura sprites (based on buff state)
   *  still happens in the main update loop; this helper only moves what already exists. */
  private syncHeroSpriteAttachments(): void {
    for (const hs of this.heroSprites) {
      if (!hs.active) continue;
      const x = hs.x;
      const y = hs.y;
      hs.hpBarBg?.setPosition(x, y - 94);
      hs.hpBar?.setPosition(x - 22.5, y - 94);
      hs.mpBar?.setPosition(x, y - 88);
      hs.xpBarBg?.setPosition(x, y - 99);
      hs.xpBar?.setPosition(x - 22.5, y - 99);
      hs.nameText?.setPosition(x, y + 6);
      const BEAR_SHADOW_Y_OFFSET = -4;
      const shadowY = y + ((hs.heroRef as any)._bearFormActive ? BEAR_SHADOW_Y_OFFSET : hs.shadowYOffset);
      hs.shadow?.setPosition(x, shadowY);
      if (hs.buffIconText && hs.buffIconText.alpha > 0) hs.buffIconText.setPosition(x + 26, y - 94);
      if (hs.auraSprites) {
        // yOffset map mirrors the calls in updateHeroes — arcane/bless/shield/berserk.
        const AURA_Y_OFFSETS: Record<string, number> = { arcane: -50, berserk: -80, bless: -22, shield: -40 };
        for (const [key, spr] of Object.entries(hs.auraSprites)) {
          if (!spr?.active) continue;
          const yo = AURA_Y_OFFSETS[key] ?? -30;
          spr.setPosition(x, y + yo);
          spr.setDepth(hs.depth + (key === 'bless' ? -1 : 1));
        }
      }
      // Shield bar uses Graphics.fillRect at absolute coords — redraw so it tracks too.
      if (hs.shieldGraphics) {
        const maxHp = this.effStats(hs.heroRef).maxHp;
        this.redrawHeroShieldBar(hs, maxHp);
      }
    }
  }

  /** Redraw the shield overlay bar + Shaman Spirit Shield pips at the hero's current
   *  position. Called every frame from updateHeroes AND from syncHeroSpriteAttachments
   *  (so a paused scene still keeps the shield bar glued to the sprite). */
  private redrawHeroShieldBar(hs: HeroSprite, maxHp: number): void {
    const shieldHp = (hs.heroRef as any)._shieldHp ?? 0;
    const g = hs.shieldGraphics;
    g.clear();
    if (shieldHp > 0 && maxHp > 0) {
      const lx = hs.x - 22.5;   // same left edge as HP bar
      const ty = hs.y - 106;    // same top edge as HP bar (centered at y-104, height 4)
      // White-grey fill overlay: capped at full bar width
      const mainW = Math.min(45, Math.round(45 * shieldHp / maxHp));
      g.fillStyle(0xc8cfe0, 0.88);
      g.fillRect(lx, ty, mainW, 4);
      // Bright blue-white tint when shield > maxHp (overflow pool)
      if (shieldHp > maxHp) {
        const ovW = Math.min(45, Math.round(45 * (shieldHp - maxHp) / maxHp));
        g.fillStyle(0x90c8ff, 0.95);
        g.fillRect(lx, ty, ovW, 4);
      }
    }
    // Shaman Spirit Shield — 3 stack pips next to the HP bar's right edge. Blue for
    // mana shield, amber for lightning; dim pip means the stack has been consumed.
    const ss = (hs.heroRef as any)._shamanShield as { kind: 'mana' | 'lightning'; stacks: number } | undefined;
    if (ss) {
      const pipColor = ss.kind === 'mana' ? 0x60a5fa : 0xfde68a;
      const pipDim = 0x3a3a3a;
      const pipY = hs.y - 95;       // vertically centered on HP bar
      const pipStartX = hs.x + 25;  // just right of HP bar (right edge at x+22.5)
      for (let i = 0; i < 3; i++) {
        const filled = i < ss.stacks;
        g.fillStyle(filled ? pipColor : pipDim, filled ? 0.95 : 0.6);
        g.fillRect(pipStartX + i * 4, pipY, 3, 3);
      }
    }
  }

  /** Return the hero's level-base maxHp — current maxHp with every additive HP buff
   *  (tome vitality, vitality idol, elixir of fortitude) removed. Prevents buffs from
   *  compounding each other (e.g. elixir_fortitude +50% of a vitality-inflated pool). */
  private getPureMaxHp(hero: Hero): number {
    const h = hero as any;
    const tomeApplied = (h._vitalityAppliedHp as number) || 0;
    const idolApplied = (h._vitalityIdolAppliedHp as number) || 0;
    const fortApplied = (h._fortitudeBoost as number) || 0;
    return Math.max(1, hero.stats.maxHp - tomeApplied - idolApplied - fortApplied);
  }

  /** Vitality Idol: apply +0.2% maxHp per stack as a persistent buff (visible in HUD).
   *  Idempotent: subtracts previously-applied HP before re-adding, so it can be called freely.
   *  On increase, heals the hero by the delta so new capacity is immediately usable.
   *  Base is the hero's pure maxHp (other buffs stripped) so the idol can't compound with
   *  vitality tome or elixir of fortitude.
   *
   *  HP cap uses `effStats(hero).maxHp` (the displayed max, which INCLUDES equipment HP
   *  bonuses + passive-tree HP + sigil hpPct + Warmaiden's Grit + boons). Capping at the
   *  raw `hero.stats.maxHp` here was the bug behind "heroes never reach full HP" — every
   *  kill bumped maxHp by a few points and clamped current HP to (base + buffs) instead of
   *  the displayed total, chopping off the equipment-HP portion of current HP per kill.
   */
  private updateVitalityIdolHp(hero: Hero): void {
    // Sum per-item runtimeStacks across every equipped Vitality Idol on this hero.
    let stacks = 0;
    for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
      const it = hero.equipment[slot];
      if (it?.name === 'Vitality Idol') stacks += it.runtimeStacks ?? 0;
    }
    const prevApplied = (hero as any)._vitalityIdolAppliedHp as number ?? 0;
    const baseMaxHp = this.getPureMaxHp(hero);
    const targetApplied = Math.floor(baseMaxHp * stacks * 0.002);
    if (targetApplied === prevApplied) return; // no-op fast path
    const delta = targetApplied - prevApplied;
    hero.stats.maxHp += delta;
    // Cap at the FULL displayed max (effStats), not raw hero.stats.maxHp — see jsdoc above.
    const effMaxHp = this.effStats(hero).maxHp;
    if (delta > 0) {
      hero.stats.hp = Math.min(effMaxHp, hero.stats.hp + delta);
    } else if (hero.stats.hp > effMaxHp) {
      hero.stats.hp = effMaxHp;
    }
    (hero as any)._vitalityIdolAppliedHp = targetApplied;
  }

  /** Spin 6 bone glyphs around the hero while Bone Shield is up. Tracks hs each frame.
   *  Called per-frame from the hero update loop. */
  private updateBoneShieldOrbit(hs: HeroSprite, active: boolean, time: number): void {
    const BONES = ['☠', '🦴', '☠', '🦴', '☠', '🦴'];
    let orbit = hs.getData('boneShieldOrbit') as Phaser.GameObjects.Text[] | null;
    if (active) {
      if (!orbit || orbit.length === 0 || !orbit[0].active) {
        orbit = [];
        for (let k = 0; k < 6; k++) {
          const bone = this.add.text(hs.x, hs.y, BONES[k], {
            fontSize: '16px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
            color: '#f5efdc', stroke: '#2a0f30', strokeThickness: 3,
          }).setOrigin(0.5).setDepth(hs.depth + 2);
          orbit.push(bone);
        }
        hs.setData('boneShieldOrbit', orbit);
      }
      // Full revolution every 1800ms; elliptical (radius 44 × 22) with waist-level drop of -10.
      const phase = (time / 1800) * Math.PI * 2;
      for (let k = 0; k < orbit.length; k++) {
        const bone = orbit[k];
        if (!bone.active) continue;
        const a = (k / 6) * Math.PI * 2 + phase;
        bone.x = hs.x + Math.cos(a) * 44;
        bone.y = hs.y - 10 + Math.sin(a) * 22;
        bone.rotation = a + Math.PI / 2;
        // Depth swap: sin(a) < 0 means the bone is on the far side of the hero — render behind + fade.
        const behind = Math.sin(a) < 0;
        bone.setDepth(hs.depth + (behind ? -1 : 2));
        bone.setAlpha(behind ? 0.35 : 0.95);
        bone.setScale(behind ? 0.75 : 0.95);
      }
    } else if (orbit && orbit.length > 0) {
      // Fade out so the orbit doesn't pop — then destroy.
      const toFade = orbit;
      hs.setData('boneShieldOrbit', null);
      for (const bone of toFade) {
        if (!bone.active) continue;
        this.tweens.add({ targets: bone, alpha: 0, duration: 300, ease: 'Sine.easeOut',
          onComplete: () => { if (bone.active) bone.destroy(); } });
      }
    }
  }

  /** Shaman Spirit Shield — a single orb circling the hero.
   *   Mana kind = layered blue orb (arcane energy read).
   *   Lightning kind = dark-blue orb with crackling lightning arcs around it.
   *  Orb is redrawn each frame only when crackle needs refresh (lightning kind),
   *  or just repositioned (mana kind). */
  private updateShamanShieldOrbit(hs: HeroSprite, kind: 'mana' | 'lightning' | null, time: number): void {
    const existing = hs.getData('shamanShieldOrb') as Phaser.GameObjects.Graphics | null;
    if (!kind) {
      if (existing) {
        hs.setData('shamanShieldOrb', null);
        if (existing.active) {
          this.tweens.add({ targets: existing, alpha: 0, duration: 260, ease: 'Sine.easeOut',
            onComplete: () => { if (existing.active) existing.destroy(); } });
        }
      }
      return;
    }
    let orb = existing;
    const curKind = hs.getData('shamanShieldOrbKind') as 'mana' | 'lightning' | null;
    if (!orb || !orb.active || curKind !== kind) {
      if (orb && orb.active) orb.destroy();
      orb = this.add.graphics().setDepth(hs.depth + 2);
      hs.setData('shamanShieldOrb', orb);
      hs.setData('shamanShieldOrbKind', kind);
    }
    // Elliptical orbit (radius 40 horizontal, 18 vertical) — slower sweep than bone shield.
    const a = (time / 1600) * Math.PI * 2;
    orb.x = hs.x + Math.cos(a) * 40;
    orb.y = hs.y - 42 + Math.sin(a) * 18;
    const behind = Math.sin(a) < 0;
    orb.setDepth(hs.depth + (behind ? -1 : 2));
    orb.setAlpha(behind ? 0.55 : 1);
    // Redraw the orb each frame. Mana = static layered spheres; Lightning = dark-blue
    // sphere with random crackle arcs for a storm-in-a-bottle read.
    orb.clear();
    if (kind === 'mana') {
      // Bright mana sphere — glow halo, body, core highlight.
      orb.fillStyle(0x1d4ed8, 0.30); orb.fillCircle(0, 0, 14);
      orb.fillStyle(0x3b82f6, 0.65); orb.fillCircle(0, 0, 10);
      orb.fillStyle(0x93c5fd, 0.95); orb.fillCircle(0, 0, 6);
      orb.fillStyle(0xdbeafe, 1.0);  orb.fillCircle(-2, -3, 2.5);
    } else {
      // Lightning kind — dark-blue charged sphere.
      orb.fillStyle(0x0c1e4a, 0.55); orb.fillCircle(0, 0, 14);
      orb.fillStyle(0x1e3a8a, 0.85); orb.fillCircle(0, 0, 10);
      orb.fillStyle(0x3b82f6, 0.9);  orb.fillCircle(0, 0, 5);
      orb.fillStyle(0xbfdbfe, 1.0);  orb.fillCircle(-2, -2, 1.8);
      // Crackling arcs — 2 short zig-zag bolts radiating outward, random angles each frame.
      orb.lineStyle(1.5, 0xbfdbfe, 0.9);
      for (let k = 0; k < 2; k++) {
        const ang = Math.random() * Math.PI * 2;
        const r0 = 10, r1 = 18, r2 = 22;
        const x0 = Math.cos(ang) * r0,            y0 = Math.sin(ang) * r0;
        const x1 = Math.cos(ang + 0.25) * r1 + 2, y1 = Math.sin(ang + 0.25) * r1 + 2;
        const x2 = Math.cos(ang + 0.15) * r2 - 2, y2 = Math.sin(ang + 0.15) * r2 - 2;
        orb.beginPath();
        orb.moveTo(x0, y0);
        orb.lineTo(x1, y1);
        orb.lineTo(x2, y2);
        orb.strokePath();
      }
    }
  }

  /** Poison Trap VFX — two dark-green ellipses at the target's feet that follow
   *  for durationMs. Wild Growth has its own separate VFX (playWildGrowth). */
  private showPoisonTrapRoots(es: EnemySprite, durationMs: number): void {
    const rootGfx = this.add.graphics().setDepth(es.depth + 0.5);
    const drawRoots = (ex: number, ey: number) => {
      rootGfx.clear();
      rootGfx.lineStyle(3, 0x2d5016, 0.9);
      rootGfx.strokeEllipse(ex, ey, 50, 16);
      rootGfx.lineStyle(2, 0x3a6a1c, 0.8);
      rootGfx.strokeEllipse(ex, ey - 3, 42, 12);
    };
    drawRoots(es.x, es.y);
    const follow = this.time.addEvent({
      delay: 50, loop: true,
      callback: () => {
        if (!es.active) { follow.remove(); rootGfx.destroy(); return; }
        drawRoots(es.x, es.y);
      },
    });
    this.time.delayedCall(durationMs, () => {
      follow.remove();
      this.tweens.add({
        targets: rootGfx, alpha: 0, duration: 240,
        onComplete: () => rootGfx.destroy(),
      });
    });
  }

  private applyShield(hero: Hero, amount: number, sourceId: string): boolean {
    const h = hero as any;
    if (!h._shieldSources) h._shieldSources = new Set<string>();
    if (!h._shieldAmounts) h._shieldAmounts = {} as Record<string, number>;
    if (h._shieldSources.has(sourceId)) return false;
    h._shieldSources.add(sourceId);
    // v2 talent: Aegis (shieldEffPct) — boost every shield amount granted.
    const shieldEff = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).shieldEffPct;
    const boosted = shieldEff > 0 ? Math.round(amount * (1 + shieldEff)) : amount;
    // Track per-source contribution so a future "single-source" rule (e.g.
    // Holy Shield's one-at-a-time policy in `applyHolyShieldExclusive`) can
    // subtract the right amount when transferring the shield.
    h._shieldAmounts[sourceId] = boosted;
    h._shieldHp = (h._shieldHp || 0) + boosted;
    h._shieldMaxHp = h._shieldHp;
    return true;
  }

  /** Remove a specific shield source from a hero (and subtract its
   *  remaining amount from the absorption pool, clamped at 0 so a
   *  partially-depleted shield can't go negative). Used to enforce
   *  Holy Shield's one-at-a-time rule across the party. */
  private clearShieldSource(hero: Hero, sourceId: string): void {
    const h = hero as any;
    if (!h._shieldSources?.has(sourceId)) return;
    const remaining = (h._shieldAmounts?.[sourceId] as number | undefined) ?? 0;
    h._shieldSources.delete(sourceId);
    if (h._shieldAmounts) delete h._shieldAmounts[sourceId];
    h._shieldHp = Math.max(0, (h._shieldHp ?? 0) - remaining);
    h._shieldMaxHp = h._shieldHp;
  }

  /** Snap a hero back to their formation slot and clear every transient charge /
   *  jump / shield-bash flag. Used on revive (gold + ability resurrect) — without
   *  this, a hero who died mid-charge or mid-jump comes back at the in-flight
   *  position with stale flags and refuses to attack until the next state-machine
   *  tick clears them, which can take a full ability cycle. */
  private resetHeroToSlotOnRevive(hs: HeroSprite): void {
    const h = hs.heroRef as any;
    // Clear charge / jump / shield-bash / wraith transients across all classes.
    // The auto-attack gate (~line 4156) blocks attacks if ANY of these is true,
    // so a stale flag = silent sword post-revive.
    h._charging = false;
    h._leapStriking = false;
    h._shieldBashCharging = false;
    (hs as any)._leapStriking = false;
    (hs as any)._wraithStriking = false; // Kael's Dirty Blades teleport flag — also gates auto-attacks
    // Reset Zayla jump state machine to idle so the AI can fire a fresh leap.
    if (hs.getData('jumpState')) {
      hs.setData('jumpState', 'idle');
      hs.setData('jumpCd', 3000);
      hs.setData('jumpStayTimer', null);
      hs.setData('jumpTargetRef', null);
    }
    // Cancel any in-flight Shield Bash stay-phase timer so its callback can't
    // fire after revive and re-lock _shieldBashCharging to true.
    if (h._sbStayCall && typeof h._sbStayCall.remove === 'function') {
      h._sbStayCall.remove();
    }
    h._sbStayCall = null;
    // Force the auto-attack gate to fire immediately on the next tick. Without
    // this, lastAttackTime carried over from before death keeps the hero idle
    // for a beat after revive — small but enough to read as "stuck".
    hs.lastAttackTime = 0;
    hs.lastAttackAnimTime = 0;
    // Drop any stale animationcomplete listeners that survived the death anim
    // — a once-handler from the attack that triggered the killing blow can
    // still be queued, and would re-fire when the revived hero plays a new
    // animation, bouncing them back into a wrong pose.
    hs.off('animationcomplete');
    // Warrior charge state machine — same path the warrior already uses on death.
    if (hs.heroRef.heroClass === 'warrior') {
      this.resetChargeToBase(hs);
      hs.setData('chargeCd', 3000);
    } else {
      // Non-warrior: kill any pending tweens and snap the sprite + bars back to slot.
      this.tweens.killTweensOf(hs);
      hs.setFlipX(false);
      hs.setPosition(hs.baseX, hs.baseY);
      hs.hpBarBg.setPosition(hs.baseX, hs.baseY - 104);
      hs.hpBar.setPosition(hs.baseX - 22.5, hs.baseY - 104);
      hs.mpBar.setPosition(hs.baseX, hs.baseY - 98);
      hs.xpBarBg.setPosition(hs.baseX, hs.baseY - 110);
      hs.xpBar.setPosition(hs.baseX - 22.5, hs.baseY - 110);
      if (hs.buffIconText.alpha > 0) hs.buffIconText.setPosition(hs.baseX + 26, hs.baseY - 104);
      hs.nameText.setPosition(hs.baseX, hs.baseY + 6);
      hs.shadow.setPosition(hs.baseX, hs.baseY + hs.shadowYOffset);
    }
    // Force-play the idle animation so the hero never lingers on the death
    // frame after revive — the call sites also play idle but a stale Phaser
    // anim state (e.g. from the death anim's last frame) sometimes ignores
    // the second play() if the key matches. Stop first, then restart.
    const idleKey = `hero_${h.heroKey ?? hs.heroRef.heroClass}_idle`;
    if (this.anims.exists(idleKey)) {
      hs.anims.stop();
      hs.play(idleKey);
    }
  }

  private resetChargeToBase(hs: HeroSprite): void {
    const idleAnim = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_idle`;
    // Kill any lingering recoil tweens that might still be driving hs.x
    this.tweens.killTweensOf(hs);
    hs.setData('chargeState', 'idle');
    hs.setData('chargeStayTimer', null);
    hs.setData('chargeDestX', null);
    hs.setData('chargeTargetRef', null);
    hs.setFlipX(false);
    hs.setPosition(hs.baseX, hs.baseY);
    hs.hpBarBg.setPosition(hs.baseX, hs.baseY - 104);
    hs.hpBar.setPosition(hs.baseX - 22.5, hs.baseY - 104);
    hs.mpBar.setPosition(hs.baseX, hs.baseY - 98);
    hs.xpBarBg.setPosition(hs.baseX, hs.baseY - 110);
    hs.xpBar.setPosition(hs.baseX - 22.5, hs.baseY - 110);
    if (hs.buffIconText.alpha > 0) hs.buffIconText.setPosition(hs.baseX + 26, hs.baseY - 104);
      // shieldGraphics redraws itself each frame — no position tracking needed
    hs.nameText.setPosition(hs.baseX, hs.baseY + 6);
    hs.shadow.setPosition(hs.baseX, hs.baseY + hs.shadowYOffset);
    if (!hs.heroRef.isDead && this.anims.exists(idleAnim)) hs.play(idleAnim);
  }

  // ── Amazon constants ──────────────────────────────────────────────────────
  private readonly AMAZON_JUMP_CD   = 36000; // ms between jump charges (2× slower than warrior)
  private readonly AMAZON_STAY_TIME = 4500;  // ms spent in melee (half warrior's stay)
  private readonly AMAZON_JUMP_SPEED  = 480; // px/s approaching
  private readonly AMAZON_RETURN_SPEED = 380; // px/s returning

  /** Amazon jump/melee state machine — runs every frame when heroKey === 'amazon' (Zayla) */
  private updateAmazonJump(hs: HeroSprite, time: number, delta: number): void {
    hs.shadow.setPosition(hs.x, hs.y + hs.shadowYOffset);
    // While a Leap Strike spell is in progress, let the spell tween handle positioning
    if ((hs as any)._leapStriking) return;

    const state = (hs.getData('jumpState') as string) || 'idle';
    const idleAnim    = 'hero_amazon_idle';
    const jumpAnim    = 'hero_amazon_jump_anim';
    const runAnim     = 'hero_amazon_run_anim';
    const specialAnim = 'hero_amazon_special_anim';

    if (state === 'idle') {
      let cd = ((hs.getData('jumpCd') as number) ?? this.AMAZON_JUMP_CD) - delta;
      hs.setData('jumpCd', cd);
      // 80 / 20 idle cycle: most of the time Zayla plays the standard idle loop;
      // every 2-4 s she may shift to the breathing-variation loop for ambient life.
      // Skip while a one-shot (throw / leap / melee strike) is playing — its
      // animationcomplete handler returns to the idle cycle.
      const currentKey = hs.anims.currentAnim?.key ?? '';
      const isPlayingOneShot = currentKey.includes('attack') || currentKey === jumpAnim
        || currentKey === specialAnim;
      const idleAlt = 'hero_amazon_idle2';
      const altPickReady = ((hs.getData('idleSwitchAt') as number) ?? 0) <= time;
      if (altPickReady) {
        const pick = Math.random() < 0.2 && this.anims.exists(idleAlt) ? idleAlt : idleAnim;
        if (!hs.heroRef.isDead && this.anims.exists(pick)
            && currentKey !== pick
            && !(hs as any)._leapStriking
            && !isPlayingOneShot) {
          hs.play(pick);
        }
        // 2-4 s before next pick — long enough to let either loop play through.
        hs.setData('idleSwitchAt', time + 2000 + Math.random() * 2000);
      }
      if (cd > 0) return;

      // Player-disabled Leap Strike also disables the passive front-row auto-jump,
      // so toggling the spell off lets Zayla stay in her ranged stance even in a front slot.
      if (hs.heroRef.spellDisabled?.includes('leap_strike')) return;

      // Front-row gate: leap only triggers from front column / vanguard slots
      const slotIdx = hs.getData('slotIdx') as number | undefined;
      if (slotIdx === undefined || !this.isFrontRow(slotIdx)) return;

      const enemy = this.findNearestEnemy(hs);
      if (!enemy) return;

      hs.setData('jumpState', 'jumping');
      hs.setData('jumpCd', this.AMAZON_JUMP_CD);
      hs.setData('jumpDestX', enemy.x - 60);
      hs.setData('jumpTargetRef', enemy);
      hs.setData('jumpStayTimer', null);
      hs.setFlipX(false);
      (hs as any)._amazonUseAtk2 = true; // kept for legacy compat — melee always uses attack2_anim now
      if (this.anims.exists(jumpAnim)) {
        hs.play(jumpAnim);
        // Do NOT register animationcomplete here — attack processing clears all listeners
        // for this event in the same frame, leaving jumpState stuck at 'jumping' forever.
        // The 'jumping' branch below polls animation state each tick instead.
      } else {
        hs.setData('jumpState', 'approaching');
      }
      this.showDamageNumber(hs.x, hs.y - 55, '⚡ LEAP!', '#f472b6');

    } else if (state === 'jumping') {
      // Poll animation completion — animationcomplete callbacks get cleared by attack processing
      // in the same frame they're registered, so we can't rely on them here.
      const isJumpPlaying = hs.anims.currentAnim?.key === jumpAnim && hs.anims.isPlaying;
      if (!isJumpPlaying) {
        hs.setData('jumpState', 'approaching');
      }

    } else if (state === 'approaching') {
      const trackEnemy = hs.getData('jumpTargetRef') as EnemySprite | null;
      if (trackEnemy && trackEnemy.active && trackEnemy.enemyRef.currentHp > 0) {
        hs.setData('jumpDestX', trackEnemy.x - 60);
        hs.setData('jumpDestY', trackEnemy.y);
      }
      const destX = (hs.getData('jumpDestX') as number) ?? hs.baseX;
      const destY = (hs.getData('jumpDestY') as number) ?? hs.baseY;
      const step = this.AMAZON_JUMP_SPEED * (delta / 1000);
      hs.x = Math.min(hs.x + step, destX);
      // Slide Y toward the enemy lane at the same speed as X
      if (Math.abs(destY - hs.y) > 1) {
        hs.y += Math.sign(destY - hs.y) * Math.min(step, Math.abs(destY - hs.y));
      }

      if (hs.x >= destX) {
        if (hs.heroRef.isDead) { this.resetAmazonToBase(hs); return; }
        hs.setData('jumpStayTimer', this.AMAZON_STAY_TIME);
        hs.setData('jumpState', 'at_enemy');
        // Play attack2 immediately on arrival — don't flash idle between landing and first hit
        if (this.anims.exists('hero_amazon_attack2_anim')) hs.play('hero_amazon_attack2_anim');
        else if (this.anims.exists(idleAnim)) hs.play(idleAnim);
      }

    } else if (state === 'at_enemy') {
      const pinEnemy = hs.getData('jumpTargetRef') as EnemySprite | null;
      if (pinEnemy && pinEnemy.active && pinEnemy.enemyRef.currentHp > 0) {
        hs.x = Math.max(hs.baseX, pinEnemy.x - 60);
        hs.y = pinEnemy.y;
      }
      let stayTimer = ((hs.getData('jumpStayTimer') as number) ?? this.AMAZON_STAY_TIME) - delta;
      hs.setData('jumpStayTimer', stayTimer);

      if (hs.heroRef.isDead || stayTimer <= 0) {
        // Switch to Attack_1 for last hit then return
        (hs as any)._amazonUseAtk2 = false;
        this.returnAmazonToBase(hs);
      }

    } else if (state === 'returning') {
      const step = this.AMAZON_RETURN_SPEED * (delta / 1000);
      hs.x = Math.max(hs.x - step, hs.baseX);
      // Slide Y back to base lane at the same rate
      if (Math.abs(hs.baseY - hs.y) > 1) {
        hs.y += Math.sign(hs.baseY - hs.y) * Math.min(step, Math.abs(hs.baseY - hs.y));
      } else {
        hs.y = hs.baseY;
      }
      // Use epsilon comparison so she always lands cleanly even when float precision sneaks in;
      // strict `hs.y === hs.baseY` could wedge the state if an outside tween shifted y by <1px.
      if (hs.x <= hs.baseX && Math.abs(hs.y - hs.baseY) < 0.5) this.resetAmazonToBase(hs);
    }
  }

  private returnAmazonToBase(hs: HeroSprite): void {
    hs.setData('jumpState', 'returning');
    hs.setFlipX(true);
    if (this.anims.exists('hero_amazon_run_anim')) hs.play('hero_amazon_run_anim');
  }

  private resetAmazonToBase(hs: HeroSprite): void {
    this.tweens.killTweensOf(hs);
    hs.setData('jumpState', 'idle');
    hs.setData('jumpStayTimer', null);
    hs.setData('jumpDestX', null);
    hs.setData('jumpTargetRef', null);
    hs.setFlipX(false);
    hs.setPosition(hs.baseX, hs.baseY);
    hs.hpBarBg.setPosition(hs.baseX, hs.baseY - 104);
    hs.hpBar.setPosition(hs.baseX - 22.5, hs.baseY - 104);
    hs.mpBar.setPosition(hs.baseX, hs.baseY - 98);
    hs.xpBarBg.setPosition(hs.baseX, hs.baseY - 110);
    hs.xpBar.setPosition(hs.baseX - 22.5, hs.baseY - 110);
      // shieldGraphics redraws itself each frame — no position tracking needed
    hs.nameText.setPosition(hs.baseX, hs.baseY + 6);
    hs.shadow.setPosition(hs.baseX, hs.baseY + hs.shadowYOffset);
    if (!hs.heroRef.isDead) {
      // After leap returns to base, snap back to the idle cycle (formerly returned
      // to the looping throw stance — see updateAmazonJump for the full design).
      const next = Math.random() < 0.2 && this.anims.exists('hero_amazon_idle2')
        ? 'hero_amazon_idle2' : 'hero_amazon_idle';
      if (this.anims.exists(next)) hs.play(next);
    }
  }

  /**
   * Checks if Goomar's "Undying Fury" passive is available and triggers it.
   * Returns true if the resurrection fires (caller should return early from death handler).
   */
  private triggerUndyingFury(hs: HeroSprite): boolean {
    const ability = hs.heroRef.abilities.find((a) => a.id === 'undying_fury');
    if (!ability || ability.currentCooldown > 0) return false;
    // Reaper's Pact — "heroes can't be resurrected" extends to self-rez too.
    // Blocks the revive from firing; the ability's CD still ticks normally so
    // it'll be ready again if the boon is replaced later.
    if (this.getBoonEffects().cannotResurrect) return false;

    // Put ability on cooldown
    ability.currentCooldown = ability.cooldown;

    // Resurrect with 30% HP + 5% per talent rank
    hs.heroRef.isDead = false;
    hs.heroRef.permanentlyDead = false;
    hs.reviveBtn?.destroy();
    hs.reviveBtn = undefined;
    this.destroyHeroGrave(hs);
    const stats = this.effStats(hs.heroRef);
    const furyTalentBonus = getTalentBonusValue(hs.heroRef.talentBonuses ?? {}, 'undying_fury');
    hs.heroRef.stats.hp = Math.max(1, Math.floor(stats.maxHp * (0.3 + furyTalentBonus)));
    hs.setAlpha(1);
    hs.hpBar.setAlpha(1); hs.hpBarBg.setAlpha(1); hs.mpBar.setAlpha(1); hs.xpBar.setAlpha(1); hs.xpBarBg.setAlpha(1); // buffIconText alpha controlled by update loop
    // Snap back + cancel any in-flight charge/jump/shield-bash state so the
    // self-rezzed warrior can swing immediately (Fury sets stats.attack ×2
    // below — useless if the hero is gated by a stale charge flag).
    this.resetHeroToSlotOnRevive(hs);

    // Apply ×2 attack buff and 1.5× display size for the ability duration
    const originalAttack = hs.heroRef.stats.attack;
    hs.heroRef.stats.attack = Math.floor(originalAttack * 2);
    // Store original on hero so cleanupBattle can revert if scene restarts during fury
    (hs.heroRef as any)._furyActive = true;
    (hs.heroRef as any)._furyOriginalAttack = originalAttack;
    hs.setScale(1.5);

    const furyIdleKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_idle`;
    if (this.anims.exists(furyIdleKey)) hs.play(furyIdleKey);
    this.vfx.playResurrectPillar(hs.x, hs.y);
    this.showDamageNumber(hs.x, hs.y - 50, '🔥 UNDYING FURY!', '#ef4444');

    // Revert buff after 8 seconds — always revert stats even if hero dies
    this.time.delayedCall(8000, () => {
      (hs.heroRef as any)._furyActive = false;
      hs.heroRef.stats.attack = originalAttack;
      if (hs.active) hs.setScale(1.0);
    });

    return true;
  }

  private performHeroAttack(hs: HeroSprite, target: EnemySprite, time: number): void {
    // Play attack animation once, then return to idle
    const hk = hs.heroRef.heroKey ?? hs.heroRef.heroClass;
    // Thorne in Nature Form — use the wolf's idle/attack animations so the swap persists between hits.
    const inNatureForm = !!(hs.heroRef as any)._bearFormActive && hs.heroRef.heroClass === 'druid';
    const idleKey = inNatureForm ? 'hero_druid_nature_idle' : `hero_${hk}_idle`;
    const isRogue = hs.heroRef.heroClass === 'assassin';
    const isWarrior = hs.heroRef.heroClass === 'warrior';
    const isAmazon = hk === 'amazon'; // supports both new saves (heroKey='amazon') and legacy saves (heroClass='amazon')
    // Rogue: throttle attack animation to at most once every 280ms so rapid hits don't flicker
    const canPlayAttackAnim = !isRogue || (time - hs.lastAttackAnimTime >= 280);
    // Don't interrupt charge/run animations; skip anim (not damage) while charger is moving
    const chargerChargeState = isWarrior
      ? ((hs.getData('chargeState') as string) || 'idle')
      : 'idle';
    const chargeAnimKey = `hero_${hk}_charge_anim`;
    const amazonJumpMoving = isAmazon && ((hs.getData('jumpState') as string) === 'jumping' || (hs.getData('jumpState') as string) === 'approaching' || (hs.getData('jumpState') as string) === 'returning');
    const blockAttackAnim = chargerChargeState === 'charging' || chargerChargeState === 'returning'
      || hs.anims.currentAnim?.key === chargeAnimKey || amazonJumpMoving;
    // Warrior (Goomar only, not Lootfi): rotate between regular attack and Attack_3 every 3rd hit
    // Lootfi shares heroClass='warrior' but has heroKey='dwarf' — never use warrior-specific sheets on it
    (hs.heroRef as any)._attackCounter = (((hs.heroRef as any)._attackCounter as number) || 0) + 1;
    const isGoomar = isWarrior && !hs.heroRef.heroKey;
    const useAlt = isGoomar && (hs.heroRef as any)._attackCounter % 3 === 0
      && this.anims.exists('hero_warrior_attack3_anim');

    // ── Amazon: Spear Volley passive — every 10th auto-attack fires a javelin ──
    let amazonSpearVolleyFired = false;
    if (isAmazon && (hs.heroRef as any)._attackCounter % 10 === 0) {
      const atk = this.effStats(hs.heroRef).attack || 0;
      const javDmg = Math.floor(atk * 3.5 + 30); // ignores armor — applied directly
      target.enemyRef.currentHp -= javDmg;
      this.vfx.playJavelin(hs.x + 54, hs.y - 80, target.x, target.y);
      this.showDamageNumber(target.x, target.y - 40, `🔱 ${javDmg}`, '#f97316', '16px');
      this.showDamageNumber(target.x, target.y - 56, 'ARMOR PIERCE', '#fb923c');
      this.flashSprite(target);
      amazonSpearVolleyFired = true;
      if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
    }

    // Amazon: use Special animation as ranged auto-attack unless in melee jump state
    const amazonInMelee = isAmazon && ((hs.getData('jumpState') as string) === 'at_enemy');
    // Always use attack2_anim in melee — there is no separate attack1 sheet for Zayla
    const amazonAttackKey = amazonInMelee ? 'hero_amazon_attack2_anim' : 'hero_amazon_special_anim';
    // Cyrith (sorcerer) cycles 1 → 2 → 3 → 1 on auto-attacks so consecutive
    // swings never repeat the same cast pose. Counter lives on the sprite
    // and survives across waves; only resets on hero respawn / slot swap.
    const isCyrith = hs.heroRef.heroKey === 'sorcerer';
    const cyrithAttackKey = (() => {
      if (!isCyrith) return '';
      const idx = ((hs.getData('cyrithAtkIdx') as number | undefined) ?? 0) % 3;
      hs.setData('cyrithAtkIdx', idx + 1);
      return `hero_sorcerer_attack${idx + 1}`;
    })();
    const attackKey = inNatureForm
      ? 'hero_druid_nature_attack'
      : isAmazon ? amazonAttackKey
      : isCyrith ? cyrithAttackKey
      : (useAlt ? 'hero_warrior_attack3_anim' : `hero_${hk}_attack`);
    // Mirror the hero to face the target's side. Skip while charging/jumping (those handle their own flip)
    // and while returning to base (flip is scripted for the return animation).
    const returningToBase = isWarrior && (hs.getData('chargeState') === 'returning');
    if (!blockAttackAnim && !returningToBase) hs.setFlipX(target.x < hs.x);
    if (!blockAttackAnim && this.anims.exists(attackKey) && (hs.anims.currentAnim?.key !== attackKey || !hs.anims.isPlaying) && canPlayAttackAnim) {
      hs.lastAttackAnimTime = time;
      hs.off('animationcomplete'); // clear any stale listeners before adding a new one
      hs.play(attackKey);
      // Every attack anim is a one-shot now (special_anim was switched from
      // looping to repeat:0 when the idle redesign landed) so always register
      // an animationcomplete handler to return to the right resting pose.
      hs.once('animationcomplete', () => {
        if (hs.heroRef.isDead) return;
        if (isAmazon) {
          const stillInMelee = (hs.getData('jumpState') as string) === 'at_enemy';
          const stillHasEnemies = this.enemySprites.some((e) => e.active && e.enemyRef.currentHp > 0);
          if (stillInMelee && stillHasEnemies) {
            // Stay on last frame of attack2 between hits — next auto-attack will replay it.
            // Don't switch to a ranged-stance idle while still in melee.
          } else {
            // 80 / 20 idle pick mirroring updateAmazonJump's resting cycle.
            const next = Math.random() < 0.2 && this.anims.exists('hero_amazon_idle2')
              ? 'hero_amazon_idle2' : 'hero_amazon_idle';
            if (this.anims.exists(next)) hs.play(next);
          }
        } else if (this.anims.exists(idleKey)) {
          hs.play(idleKey);
        }
      });
    }

    // If Spear Volley already killed the target this tick, skip abilities against it
    if (amazonSpearVolleyFired && !target.active) return;

    let usedAbility = false;
    // Focus Conduit: temporarily boost spellPower for ability calculations
    const conduitSp = (() => {
      for (const w of [hs.heroRef.equipment.weapon1, hs.heroRef.equipment.weapon2]) {
        const m = w?.specialEffect?.match(/Conduit.*\+(\d+)% spell power/);
        if (m) return Math.floor(hs.heroRef.stats.spellPower * parseInt(m[1]) / 100);
      }
      return 0;
    })();
    if (conduitSp > 0) hs.heroRef.stats.spellPower += conduitSp;
    // Sort by player-defined priority first; ties broken by cooldown descending
    const _prio = hs.heroRef.spellPriority;
    const abilitiesByPriority = [...hs.heroRef.abilities].sort((a, b) => {
      const ai = _prio?.indexOf(a.id) ?? -1;
      const bi = _prio?.indexOf(b.id) ?? -1;
      if (ai !== -1 && bi !== -1) return ai - bi;       // both in priority list
      if (ai !== -1) return -1;                          // a is listed, b is not
      if (bi !== -1) return 1;                           // b is listed, a is not
      return b.cooldown - a.cooldown;                    // neither listed: fallback
    });
    for (const ability of abilitiesByPriority) {
      // Passive abilities are triggered by events, not by the auto-cast loop.
      // Adding `passive_counter` here fixed the Valka 1-damage bug: without
      // it, Counter-Strike was being treated as an active cast every tick
      // (manaCost/cooldown both 0, so it always passed the gate), setting
      // `usedAbility = true` and blocking her real auto-attack path. Damage
      // hit the default max(1, …) floor, and `heroAttackEnemy` was never
      // called so the per-swing HIT= diagnostic stayed silent.
      if (ability.effectType === 'passive_resurrect' || ability.effectType === 'passive_loot' || ability.effectType === 'passive_spear' || ability.effectType === 'soul_leech' || ability.effectType === 'passive_counter') continue;
      // Player-disabled abilities are skipped outright (toggled off in spell priority popup)
      if (hs.heroRef.spellDisabled?.includes(ability.id)) continue;
      const resourceAvailable = hs.heroRef.heroClass === 'warrior'
        ? hs.heroRef.stats.rage
        : hs.heroRef.heroClass === 'assassin'
          ? hs.heroRef.stats.energy
          : hs.heroRef.stats.mp;
      const relicCostReduce = this.getRelicBonuses(hs.heroRef).manaCostReduce;
      const effectiveCost = Math.max(0, Math.round(ability.manaCost * (1 + (ability.level - 1) * 0.15) * (1 - Math.min(relicCostReduce, 0.40))));
      // Guard: don't cast a HoT if every alive hero already has THIS specific
      // ability active. Per-ability check so Lumiel's Bless and Thorne's
      // Regrowth don't block each other (they stack on the same hero).
      const blessBlocked = ability.effectType === 'hot' &&
        this.heroSprites.filter((h) => !h.heroRef.isDead).every((h) => (h.heroRef as any)[`_hotActive_${ability.id}`]);
      // Thorne's Nature Form locks out his healing / AoE nature kit — feral wolf can only claw & bite.
      const inNatureForm = !!(hs.heroRef as any)._bearFormActive;
      const natureFormBlocked = inNatureForm && (ability.id === 'regrowth' || ability.id === 'wild_growth');
      // Guard: Vine Snare is a point-blank ground spell — don't cast if no enemies are in melee range
      const vineSnareBlocked = ability.effectType === 'vine_snare'
        && !this.enemySprites.some((e) => e.active && e.enemyRef.currentHp > 0 && Math.abs(e.x - hs.x) <= WARRIOR_MELEE_RANGE * 2.5);
      // Spell cast-range guard. If the ability has a hard cast range
      // (see `SPELL_CAST_RANGES` in shared/balance) and there's no living
      // enemy within that distance, skip the cast — mana isn't spent and
      // the cooldown doesn't tick. Lets the player position heroes for
      // their range; melee abilities require melee enemies, ranged spells
      // can fire across the screen, etc. Spells without an entry in the
      // map are unrestricted (buffs / heals / summons / global AoE).
      const castRange = SPELL_CAST_RANGES[ability.id];
      const castRangeBlocked = castRange !== undefined
        && !this.hasEnemyInRange(hs, castRange);
      // Silence debuff — hero can't cast spells while _silencedUntil is in the future.
      const silenced = ((hs.heroRef as any)._silencedUntil as number ?? 0) > this.gameTime;
      // Wolf's Mantle sigil: Bear Form is locked ON permanently, so the ability itself
      // must not auto-cast — its 8 s delayed-revert timer would flicker Thorne back to
      // human form every rotation.
      const wolfsMantleBlocks = ability.effectType === 'bear_form'
        && this.getSigilEffects().specials.has('wolfs_mantle');
      // Resurrection — don't auto-cast when there's no ally "down" in any form
      // (permanently-dead OR waiting-to-respawn). Previously only permanentlyDead was
      // checked, so a hero bouncing through the respawn window + a stale-cached bundle
      // could briefly let the cast slip through. Also: a full-party scenario where no
      // hero is dead in ANY sense always blocks the cast.
      // Reaper's Pact — cannotResurrect blocks every resurrect cast outright,
      // no matter how many allies are down. Tested after the no-target guard so
      // the cooldown-refund branch below doesn't leak resources if both conditions
      // trigger (no dead hero AND pact active → both blocked, single code path).
      const boonEffRes = this.getBoonEffects();
      const resurrectBlocked = ability.effectType === 'resurrect'
        && (boonEffRes.cannotResurrect
          || !this.heroSprites.some((h) => h !== hs
            && (h.heroRef.permanentlyDead || h.heroRef.isDead)));
      if (ability.currentCooldown <= 0 && resourceAvailable >= effectiveCost && !blessBlocked && !vineSnareBlocked && !natureFormBlocked && !silenced && !wolfsMantleBlocks && !resurrectBlocked && !castRangeBlocked) {
        // Hero Armory — Zyphara Arcane Overflow needs the pre-cast mp value
        // to detect "was at full mana?" since the spend below drops it.
        (hs.heroRef as any)._mpBeforeCast = hs.heroRef.stats.mp;
        if (hs.heroRef.heroClass === 'warrior') hs.heroRef.stats.rage -= effectiveCost;
        else if (hs.heroRef.heroClass === 'assassin') hs.heroRef.stats.energy -= effectiveCost;
        else if (!(hs.heroRef as any)._lichFormActive) hs.heroRef.stats.mp -= effectiveCost; // Lich Form: free casts
        const cdrBonus = getTomeBonus(this.gsm.player.tomes, 'cooldown');
        const relicCdr = this.getRelicBonuses(hs.heroRef).cdrPct;
        const passiveCdr = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).cdr;
        const swiftnessCdr = this.gsm.hasActiveConsumable('tonic_swiftness') ? 0.20 : 0;
        // Robe armor (light sub-type) — armor-rolled cdrPct stacks with relic CDR.
        const armorCdr = this.effStats(hs.heroRef).cdrPct ?? 0;
        // Hourglass of Stasis sigil — −25% ability CD (its core effect, paired
        // with the crit-set-to-0 penalty applied at attack time).
        const hourglassCdr = this.getSigilEffects().specials.has('hourglass_of_stasis') ? 0.25 : 0;
        // Hero Armory — Thorne Swift Growth halves Wild Growth's effective
        // cooldown so it re-erupts twice as often. Applied alongside the
        // standard CDR multipliers.
        const swiftGrowthMult = (ability.id === 'wild_growth'
          && this.hasArmoryPassive(hs.heroRef, 'passive_thorne_swift_growth'))
          ? 0.5 : 1;
        ability.currentCooldown = Math.round(ability.cooldown * (1 - Math.min(cdrBonus + relicCdr + armorCdr + passiveCdr + swiftnessCdr + hourglassCdr, 0.90)) * swiftGrowthMult);
        // v2 talent: Swift Steps — % chance for the cast to skip its cooldown entirely.
        {
          const swiftSkip = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).spellNoCdChance;
          if (swiftSkip > 0 && Math.random() < swiftSkip) {
            ability.currentCooldown = 0;
            this.showDamageNumber(hs.x, hs.y - 60, '↻ NO CD', '#80c8ff', '11px');
          }
        }
        this.gsm.trackAbilityUse();
        // Per-ability-type cast SFX — heals get a chime, buffs a soft force-field whoosh, offensive spells a bolt.
        const et = ability.effectType;
        const castSfx = (et === 'heal' || et === 'hot' || et === 'resurrect') ? 'heal'
                      : (et === 'buff' || et === 'bear_form' || et === 'berserk' || et === 'stone_form'
                         || et === 'magma_shield' || et === 'frost_shield' || et === 'evasion'
                         || et === 'taunt' || et === 'bone_shield' || et === 'lich_form'
                         || et === 'shamanic_buff' || et === 'shaman_shield') ? 'buff'
                      : 'spell_cast';
        this.sfx.play(castSfx, { volume: 0.55 });

        // Spellpower Tome: boost spell power for abilities. Book of the Archivist also
        // contributes +2 SP per rank — previously that portion was dead content.
        const spTome = Math.floor(
          getTomeBonus(this.gsm.player.tomes, 'spellpower')
          + getTomeBonus(this.gsm.player.tomes, 'book_archivist'),
        );
        const rbsInner = this.getRelicBonuses(hs.heroRef);
        const baseSpPre = (this.effStats(hs.heroRef).spellPower || 0) + spTome;
        // Archon's Focus: +1% SP per kill stack, aggregate-capped at +99%. Applied
        // inside the spell-cast branch so this relic actually boosts spell damage
        // (the auto-attack orbSpBuff path doesn't feed through to casts).
        // No aggregate cap: the per-item rarity-based max (see archonFocusMaxStacks)
        // already bounds a SINGLE relic at 30-99 stacks; multiple equipped copies
        // genuinely compound as the reward for rare same-name rolls.
        const archonCastPct = rbsInner.archonFocusStacksTotal * 0.01;
        // Shrine boon spell power — Caster's Covenant (+50%), Inner Flame stacks
        // (+1% per kill up to 100). Fold into the SP multiplier.
        const boonEffCast = this.getBoonEffects();
        const boonSpellPct = boonEffCast.spellPowerPct;
        // Orb of Arcane Fury: flat +12 % multiplier on spell power for spells (previously
        // only boosted SP-scaling auto-attacks, which was a bug players never noticed).
        const orbCastMult = 1 + (rbsInner.spellPct ?? 0);
        const baseSpRaw = Math.floor(baseSpPre * orbCastMult * (1 + archonCastPct) * (1 + boonSpellPct));
        // Lich Form: +500% SP for 3s (×6 multiplier)
        const lichFormMult = (hs.heroRef as any)._lichFormActive ? 6.0 : 1.0;
        const baseSp = baseSpRaw * lichFormMult;
        // Relic: Spellfire Lens — spell damage * (1 + SP/200); approximated by scaling SP
        const spellfireMult = rbsInner.hasSpellfireLens ? (1 + baseSp / 200) : 1;
        // Relic: Mana Flux Crystal — bonus fires if resource was full BEFORE this drain (use resourceAvailable)
        const statsForFlux = this.effStats(hs.heroRef);
        const maxResource = hs.heroRef.heroClass === 'warrior'
          ? (statsForFlux.maxRage || 100)
          : hs.heroRef.heroClass === 'assassin' ? statsForFlux.maxEnergy : statsForFlux.maxMp;
        if (rbsInner.hasManaFluxCrystal && resourceAvailable >= maxResource) {
          (hs.heroRef as any)._manaFluxReady = true;
        }
        const manaFluxActive = rbsInner.hasManaFluxCrystal && !!(hs.heroRef as any)._manaFluxReady;
        if (manaFluxActive) {
          (hs.heroRef as any)._manaFluxReady = false;
          // Drain ALL remaining resource
          if (hs.heroRef.heroClass === 'warrior') hs.heroRef.stats.rage = 0;
          else if (hs.heroRef.heroClass === 'assassin') hs.heroRef.stats.energy = 0;
          else hs.heroRef.stats.mp = 0;
        }
        // DPS tracking: snapshot total enemy HP before ability fires
        const _dpsHpBefore = this.enemySprites.reduce((s, e) => s + Math.max(0, e.enemyRef.currentHp), 0);
        // Spellbinder's Ring: aggregate double-cast chance hard-capped at 25% so
        // stacking 3+ copies can't trivialise every cast. When it procs we
        // double the spell's damage output in-place (re-running the entire
        // cast path would require refactoring every per-spell branch; this
        // keeps the feel — big popup + 2× damage — without that surgery).
        const spellbinderCap = Math.min(0.25, rbsInner.spellbinderChance ?? 0);
        const spellbinderProc = spellbinderCap > 0 && Math.random() < spellbinderCap;
        // sp: apply lens multiplier + mana flux boost + passive spellDmg % + spellbinder +
        // shrine damagePct (Make it Easy / Blood Price stacks). damagePct multiplies
        // all damage, so spells get the same damagePct multiplier as auto-attacks.
        const passiveSpellDmg = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).spellDmg;
        const sp = Math.round(baseSp * spellfireMult * (manaFluxActive ? 1.8 : 1) * (spellbinderProc ? 2 : 1) * (1 + passiveSpellDmg) * (1 + boonEffCast.damagePct));
        // Talent bonus for this ability
        const talentBonus = getTalentBonusValue(hs.heroRef.talentBonuses, ability.id);
        const talentInfo = getTalentUpgradeInfo(ability.id);
        const talentStat = talentInfo?.stat || '';
        // Spell crit: all non-DoT spells can crit using the hero's crit stats
        const critTome = getTomeBonus(this.gsm.player.tomes, 'crit');
        const critDmgTome = getTomeBonus(this.gsm.player.tomes, 'critDamage');
        // Trophy: Goblin Kingpin (Horde event reward) — +10% crit damage party-wide
        // applies to spell crits as well as auto-attacks.
        const kingpinSpellCritDmg = this.hasTrophy('crit damage and +2 swap') ? 0.10 : 0;
        // v2 talents: Sorcerer's Edge adds spell crit chance, Spellbound adds spell crit dmg.
        const pbSpell = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
        const spellCritChance = this.effStats(hs.heroRef).critChance + critTome + pbSpell.spellCritChance;
        const spellCritMult = this.effStats(hs.heroRef).critDamage + critDmgTome + kingpinSpellCritDmg + pbSpell.spellCritDmg;
        const rollSpellCrit = (): boolean => Math.random() < spellCritChance;
        // All bosses resist 20% of incoming spell damage (all schools). Applied BEFORE the crit roll.
        const applyBossSpellResist = (dmg: number, en?: EnemySprite): number => {
          if (en && en.enemyRef.tier === 'boss') return Math.max(1, Math.floor(dmg * 0.80));
          return dmg;
        };
        // Row multiplier for the caster's formation slot — applied to all spell / ability damage
        const rowMult = this.getActiveDmgMult(hs.getData('slotIdx') as number | undefined);
        const applySpellCrit = (dmg: number, en?: EnemySprite): { dmg: number; wasCrit: boolean } => {
          const rowed = Math.max(1, Math.round(dmg * rowMult));
          const resisted = applyBossSpellResist(rowed, en);
          const wasCrit = rollSpellCrit();
          // Magmarath's Ember sigil — spell crits also leave the 3 s ashen burn on the target.
          if (wasCrit && en) this.maybeApplyEmberBurn(en, hs);
          // Critical Striker daily + lifetime crit counter — `trackCrit`
          // forwards through to `trackCritHit` so both stats stay in sync.
          if (wasCrit) this.gsm.trackCrit();
          return { dmg: wasCrit ? Math.round(resisted * spellCritMult) : resisted, wasCrit };
        };

        if (ability.effectType === 'heal') {
          // Heal lowest HP allies — talent adds +1 ally per level (max 3)
          const healTargets = 1 + (talentStat === 'targets' ? Math.min(Math.floor(talentBonus), 2) : 0);
          const sortedHeroes = this.heroSprites
            .filter((h) => !h.heroRef.isDead)
            .sort((a, b) => (a.heroRef.stats.hp / this.effStats(a.heroRef).maxHp) - (b.heroRef.stats.hp / this.effStats(b.heroRef).maxHp));
          // Blood Price — "heroes heal 50% less" pulls healReceivedPct = -0.5.
          // Clamp the multiplier min at 0 so a future curse that goes below
          // -100% can't flip into negative healing.
          // v2 talent Steady Heart amplifies all healing received.
          const steadyHeart = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).healingReceivedPct;
          const healMult = Math.max(0, 1 + this.getBoonEffects().healReceivedPct) * (1 + steadyHeart);
          const healAmtBase = ability.healAmount + sp * 3 + (talentStat === 'healAmount' ? talentBonus : 0);
          const healAmt = Math.max(1, Math.floor(healAmtBase * healMult));
          // Hero Armory — Lumiel: per-cast passive flags resolved once per heal.
          const casterRef = hs.heroRef as any;
          const aegisActive = this.hasArmoryPassive(hs.heroRef, 'passive_lumiel_aegis_shield')
            && !casterRef._aegisShieldUsed;
          const chainActive = this.hasArmoryPassive(hs.heroRef, 'passive_lumiel_chain_of_grace')
            && hs.heroRef.stats.hp < (this.effStats(hs.heroRef).maxHp ?? 1) * 0.40;
          for (let hi = 0; hi < Math.min(healTargets, sortedHeroes.length); hi++) {
            const h = sortedHeroes[hi];
            const maxHp = this.effStats(h.heroRef).maxHp;
            // Heal crit — same crit chance / crit-damage stats as spells.
            // Per-target roll so two heals from the same cast can crit
            // independently.
            const healCrit = rollSpellCrit();
            const finalHeal = healCrit ? Math.round(healAmt * spellCritMult) : healAmt;
            h.heroRef.stats.hp = Math.min(maxHp, h.heroRef.stats.hp + finalHeal);
            this.showDamageNumber(h.x, h.y - 30, `+${finalHeal}`, healCrit ? '#86efac' : '#22c55e', healCrit ? '20px' : '14px');
            this.vfx.playHealGlow(h.x, h.y);

            // Aegis Shield (Lumiel) — first heal each wave layers a (150 + SP)
            // HP shield on the heal target for ~5s. Mark used so subsequent
            // heals don't re-apply; flag clears at wave start.
            if (aegisActive && hi === 0) {
              const shieldHp = 150 + sp;
              const tgtRef = h.heroRef as any;
              tgtRef._shieldHp = Math.max(tgtRef._shieldHp ?? 0, shieldHp);
              tgtRef._shieldMaxHp = Math.max(tgtRef._shieldMaxHp ?? 0, shieldHp);
              casterRef._aegisShieldUsed = true;
              this.showDamageNumber(h.x + 16, h.y - 44, `ᚷ +${shieldHp} shield`, '#a8d8ff', '11px');
              this.time.delayedCall(5000, () => {
                if (tgtRef._shieldHp > 0) {
                  tgtRef._shieldHp = Math.max(0, tgtRef._shieldHp - shieldHp);
                  if (tgtRef._shieldHp <= 0) tgtRef._shieldMaxHp = 0;
                }
              });
            }

            // Relic: Radiant Surge — healed target gains +80% damage for 3s
            if (rbsInner.hasRadiantSurge) {
              (h.heroRef as any)._radiantSurge = true;
              this.time.delayedCall(3000, () => { (h.heroRef as any)._radiantSurge = false; });
            }
          }

          // Chain of Grace (Lumiel) — splash 40% of the heal to nearest other
          // ally if Lumiel was below 40% HP at cast time.
          if (chainActive) {
            const splashAmt = Math.floor(healAmt * 0.40);
            const splashTgt = this.heroSprites
              .filter((o) => o !== hs && !o.heroRef.isDead && !sortedHeroes.slice(0, healTargets).includes(o))
              .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))[0];
            if (splashTgt && splashAmt > 0) {
              const sMax = this.effStats(splashTgt.heroRef).maxHp;
              splashTgt.heroRef.stats.hp = Math.min(sMax, splashTgt.heroRef.stats.hp + splashAmt);
              this.showDamageNumber(splashTgt.x, splashTgt.y - 30, `+${splashAmt}`, '#86efac', '11px');
            }
          }
        } else if (ability.effectType === 'resurrect') {
          // Paladin / healer resurrect: revive first permanently fallen ally — talent adds +10% revive HP per level.
          // Belt-and-suspenders: if somehow the cast slipped past the resurrectBlocked guard
          // (stale client, race condition between death state writes), the handler refunds
          // the cost + resets the cooldown so no resources are lost on an empty cast.
          const deadHero = this.heroSprites.find((h) => h.heroRef.permanentlyDead && h !== hs);
          if (!deadHero) {
            if (hs.heroRef.heroClass === 'warrior') hs.heroRef.stats.rage = Math.min(hs.heroRef.stats.maxRage, hs.heroRef.stats.rage + effectiveCost);
            else if (hs.heroRef.heroClass === 'assassin') hs.heroRef.stats.energy = Math.min(hs.heroRef.stats.maxEnergy, hs.heroRef.stats.energy + effectiveCost);
            else hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + effectiveCost);
            ability.currentCooldown = 0;
            continue;
          }
          if (deadHero) {
            const maxHp = this.effStats(deadHero.heroRef).maxHp;
            // Base revive 50 % HP (was 30 %). Talent still adds +10 % per rank
            // on top so rank 5 talent = 100 % revive.
            const revivePct = 0.5 + (talentStat === 'reviveHp' ? talentBonus : 0);
            deadHero.heroRef.isDead = false;
            deadHero.heroRef.permanentlyDead = false;
            deadHero.heroRef.stats.hp = Math.floor(maxHp * revivePct);
            deadHero.heroRef.respawnTimer = 0;
            deadHero.setAlpha(1);
            deadHero.hpBar.setAlpha(1); deadHero.hpBarBg.setAlpha(1); deadHero.mpBar.setAlpha(1); deadHero.xpBar.setAlpha(1); deadHero.xpBarBg.setAlpha(1); // buffIconText controlled by update loop
            // Remove gold revive popup if it was showing
            deadHero.reviveBtn?.destroy();
            deadHero.reviveBtn = undefined;
            this.destroyHeroGrave(deadHero);
            // Snap back to slot and clear charge/jump/shield-bash flags — a hero
            // who fell mid-charge would otherwise come back at the in-flight
            // position with stale flags and skip auto-attacks.
            this.resetHeroToSlotOnRevive(deadHero);
            const idleKey = `hero_${deadHero.heroRef.heroKey ?? deadHero.heroRef.heroClass}_idle`;
            if (this.anims.exists(idleKey)) deadHero.play(idleKey);
            this.vfx.playSunStrike(deadHero.x, deadHero.y - 30);
            this.vfx.playResurrectPillar(deadHero.x, deadHero.y);
            this.showResurrectAura(deadHero);
            this.showDamageNumber(deadHero.x, deadHero.y - 30, 'RESURRECTED!', '#40b880');
          }
        } else if (ability.effectType === 'taunt') {
          // Challenge: Ironheld stays put and roars — taunts every enemy within a radius around HIM.
          // Skip enemies that are already taunted (by any tank) so mana/cooldown isn't wasted.
          const TAUNT_RANGE = 450;
          const isAlreadyTaunted = (e: EnemySprite): boolean => {
            const timer = (e.enemyRef as any)._tauntTimer ?? 0;
            return timer > 0 && (e.enemyRef as any)._tauntedTarget != null;
          };
          const aliveEnemies = this.enemySprites.filter((e) => e.active && e.enemyRef.currentHp > 0);
          const inRange = aliveEnemies.filter((e) => Math.abs(e.x - hs.x) < TAUNT_RANGE && !isAlreadyTaunted(e));
          if (inRange.length === 0) {
            ability.currentCooldown = 0;
            hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + effectiveCost);
            continue;
          }
          const tauntDuration = ability.effectDuration || 4000;
          // Central taunt shockwave on Ironheld himself
          this.vfx.playTaunt(hs.x, hs.y, TAUNT_RANGE);
          this.showDamageNumber(hs.x, hs.y - 40, '⚔ CHALLENGE!', '#d8b060');
          // Hero Armory — Ironheld Sanctified Duty: stamp a taunt-active expiry on
          // this hero so allies' applyDamageToHero can read it for the +10 % DR
          // window. Cleared automatically once the wall-clock passes the expiry.
          (hs.heroRef as any)._tauntActiveUntil = Date.now() + tauntDuration;
          // Talent (aoeChallenge) taunts every enemy in range; otherwise just the nearest one
          const targets = (talentStat === 'aoeChallenge' && talentBonus >= 1)
            ? inRange
            : [inRange.sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))[0]];
          for (const es of targets) {
            (es.enemyRef as any)._tauntedTarget = hs;
            (es.enemyRef as any)._tauntTimer    = tauntDuration;
            // Ember tether + red glare on each taunted enemy
            this.vfx.playTauntMark(es.x, es.y);
            this.showDamageNumber(es.x, es.y - 30, 'TAUNTED', '#ff8030');
          }
          if (talentStat === 'aoeChallenge' && talentBonus >= 1 && targets.length > 1) {
            this.showDamageNumber(hs.x, hs.y - 56, '⚔ MASS CHALLENGE!', '#ffc060');
          }
          // 15% all-resist buff on Ironheld for the taunt duration
          (hs.heroRef as any)._resistanceBuff = ((hs.heroRef as any)._resistanceBuff || 0) + 15;
          (hs.heroRef as any)._challengeResistTimer = tauntDuration;
          this.showDamageNumber(hs.x, hs.y - 24, '+15% RESIST', '#80c0ff');
        } else if (ability.effectType === 'fear') {
          // Fear: makes closest enemy run backward; no effect on already-feared targets
          const duration = ability.effectDuration || 3000;
          const alreadyFeared = target.enemyRef.debuffs.some((d) => d.type === 'fear');
          if (!alreadyFeared) {
            const fearDebuff = {
              type: 'fear' as const, value: ability.bonusDamageMult || 0.25,
              duration, remaining: duration,
            };
            applyDebuff(target.enemyRef, fearDebuff);
            this.maybeBrambleSpread(target, fearDebuff);
            this.vfx.playFearWave(target.x, target.y);
            this.showDamageNumber(target.x, target.y - 20, 'FEAR!', '#5090c0');
            this.flashSprite(target);
          }
          // Talent: chance to fear nearby enemies — 16% per rank, max 5 ranks → 80% at rank 5.
          // Hard 80% cap at usage as a safety net so any future re-tuning of perLevel
          // can't push past the agreed ceiling.
          if (talentStat === 'extraFearChance' && talentBonus > 0) {
            const aoeChance = Math.min(0.80, talentBonus);
            for (const es of this.enemySprites) {
              if (es !== target && es.enemyRef.currentHp > 0 && Math.abs(es.x - target.x) < 140) {
                if (!es.enemyRef.debuffs.some((d) => d.type === 'fear') && Math.random() < aoeChance) {
                  applyDebuff(es.enemyRef, {
                    type: 'fear', value: ability.bonusDamageMult || 0.25,
                    duration, remaining: duration,
                  });
                  this.vfx.playFearWave(es.x, es.y);
                  this.showDamageNumber(es.x, es.y - 20, 'FEAR!', '#5090c0');
                  this.flashSprite(es);
                }
              }
            }
          }
        } else if (ability.effectType === 'stun_cone') {
          // Earthquake: stun primary target + cone of enemies behind it (expanding cone shape).
          // Stun is pure CC — no direct damage, no crit roll. The bonus_spell_damage
          // debuff makes follow-up spells hit harder, which is the real payoff.
          const stunDuration = (ability.effectDuration || 2000) + (talentStat === 'duration' ? talentBonus : 0);
          this.vfx.playStunQuake(hs.x, hs.y);
          // Cone: primary target + enemies where x > target.x AND abs(dy) <= (dx * coneWidth)
          const coneWidth = 1.2; // ~50° half-angle
          const coneTargets = this.enemySprites.filter((es) => {
            if (es.enemyRef.currentHp <= 0) return false;
            if (es === target) return true;
            const dx = es.x - target.x;
            const dy = Math.abs(es.y - target.y);
            return dx > 0 && dy <= dx * coneWidth;
          });
          for (const es of coneTargets) {
            applyDebuff(es.enemyRef, {
              type: 'stun', value: 1,
              duration: stunDuration, remaining: stunDuration,
            });
            if (ability.bonusDamageMult) {
              applyDebuff(es.enemyRef, {
                type: 'bonus_spell_damage', value: ability.bonusDamageMult,
                duration: stunDuration, remaining: stunDuration,
              });
            }
            // Small label — no number, no crit; stun is a status, not damage.
            this.showDamageNumber(es.x, es.y - 20, 'STUN', '#eab308', '11px');
            this.flashSprite(es);
            this.vfx.playSpikes(es.x, es.y);
          }
        } else if (ability.id === 'wraith_strike') {
          // Wraith Strike: teleport behind priority target (elite/boss preferred), extreme burst damage, return instantly.
          // Targeting was broken — checked `isElite` / `isBoss` properties that don't exist on EnemyInstance, so priority
          // always fell through to priorities[0] (first enemy — often a random minion). Fixed to read `tier` directly.
          const priorities = this.enemySprites.filter((e) => e.active && e.enemyRef.currentHp > 0);
          const strikeTgt = priorities.find((e) => e.enemyRef.tier === 'boss')
            ?? priorities.find((e) => e.enemyRef.tier === 'elite')
            ?? priorities[0];
          if (!strikeTgt) {
            ability.currentCooldown = 0;
            continue;
          }
          const atk = this.effStats(hs.heroRef).attack || 0;
          const damageMult = 1 + (talentStat === 'damageMult' ? talentBonus : 0);
          const baseDmg = Math.floor((ability.damage + sp * 1.5 + atk * 0.8) * damageMult);
          // Hero Armory — Kael Shadowed Steps: Dirty Blades is always a crit.
          // Guaranteed-crit path now routes through the full applySpellCrit pipeline
          // (rowMult + boss spell resist) instead of bypassing them — was producing
          // inconsistent damage where a crit'd strike could hit HARDER against a boss
          // than the same strike on a minion (skipping boss resist).
          const dirtyBladesGuaranteedCrit = this.hasArmoryPassive(hs.heroRef, 'passive_kael_shadowed_steps');
          const dbRolled = dirtyBladesGuaranteedCrit
            ? {
                dmg: Math.round(applyBossSpellResist(Math.max(1, Math.round(baseDmg * rowMult)), strikeTgt) * spellCritMult),
                wasCrit: true,
              }
            : applySpellCrit(baseDmg, strikeTgt);
          const wraithDmg = dbRolled.dmg;
          const wasCrit = dbRolled.wasCrit;

          // ── Teleport-out at hero position ──
          this.tweens.killTweensOf(hs);
          (hs as any)._wraithStriking = true;
          this.vfx.playShadowTeleportOut(hs.x, hs.y);
          hs.setAlpha(0); // hero vanishes

          const returnX = hs.baseX;
          const returnY = hs.baseY;

          // After 220ms: re-pick a live priority target, appear behind, deal damage.
          // Re-target at teleport-in time because the original strikeTgt might have
          // died during the 220 ms vanish window (another hero's attack, a DoT tick,
          // etc.) — before this, Kael would warp to a corpse and the strike hit
          // nothing. Now: prefer boss > elite > first-alive. If NO enemies are
          // alive any more, just skip the hit and teleport straight back.
          this.time.delayedCall(220, () => {
            if (!hs.active) return;
            const stillAlive = this.enemySprites.filter((e) => e.active && e.enemyRef.currentHp > 0);
            const liveTgt = stillAlive.find((e) => e.enemyRef.tier === 'boss')
              ?? stillAlive.find((e) => e.enemyRef.tier === 'elite')
              ?? stillAlive[0];
            if (!liveTgt) {
              // Empty battlefield — skip the hit, start the return-teleport early.
              this.time.delayedCall(50, () => {
                if (!hs.active) { (hs as any)._wraithStriking = false; return; }
                this.tweens.killTweensOf(hs);
                hs.setPosition(returnX, returnY);
                hs.setAlpha(1);
                this.vfx.playShadowTeleportIn(returnX, returnY);
                (hs as any)._wraithStriking = false;
              });
              return;
            }
            const tgtX = liveTgt.x - 55;
            const tgtY = liveTgt.y;
            hs.setPosition(tgtX, tgtY);
            hs.setAlpha(1);
            this.vfx.playShadowTeleportIn(tgtX, tgtY);
            // Deal damage to the live target
            liveTgt.enemyRef.currentHp -= wraithDmg;
            const label = `${wraithDmg}`;
            this.showDamageNumber(liveTgt.x, liveTgt.y - 30, label, wasCrit ? '#e0a030' : '#5090c0', wasCrit ? '27px' : '16px');
            this.flashSprite(liveTgt);
            this.applySpellSigils(hs, liveTgt, wraithDmg);
            if (liveTgt.enemyRef.currentHp <= 0) this.killEnemy(liveTgt, hs);

            // After 350ms: teleport back to base
            this.time.delayedCall(350, () => {
              if (!hs.active) return;
              this.vfx.playShadowTeleportOut(tgtX, tgtY);
              hs.setAlpha(0);
              this.time.delayedCall(180, () => {
                if (!hs.active) { (hs as any)._wraithStriking = false; return; }
                this.tweens.killTweensOf(hs);
                hs.setPosition(returnX, returnY);
                hs.setAlpha(1);
                this.vfx.playShadowTeleportIn(returnX, returnY);
                (hs as any)._wraithStriking = false;
              });
            });
          });
          this.showDamageNumber(hs.baseX, hs.baseY - 50, '☠ DIRTY BLADES', '#4090c0');

        } else if (ability.id === 'leap_strike') {
          // Front-row gate: Leap Strike only fires from front column (slot 4-6) or vanguard (slot 7-8)
          const leapSlotIdx = hs.getData('slotIdx') as number | undefined;
          if (leapSlotIdx === undefined || !this.isFrontRow(leapSlotIdx)) {
            // Refund so she doesn't waste mana/cooldown sitting in back ranks
            ability.currentCooldown = 0;
            hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + effectiveCost);
            continue;
          }
          // Leap Strike must be cast from ranged position — if Zayla is in melee, send her back and retry
          const jumpState = (hs.getData('jumpState') as string) || 'idle';
          if (jumpState !== 'idle') {
            // Force return to ranged; reset cooldown to a short window so she casts once back
            if (jumpState === 'at_enemy') this.returnAmazonToBase(hs);
            ability.currentCooldown = 4000;
            break;
          }
          // Leap Strike: parabolic arc to target, jump anim during flight
          const damageMult = 1 + (talentStat === 'damageMult' ? talentBonus : 0);
          const tgt = target;
          const rawDmg = Math.floor((ability.damage + sp + this.effStats(hs.heroRef).attack * 0.6) * damageMult);
          const reducedDef = Math.floor((tgt.enemyRef.defense ?? 0) * 0.30);
          const mitPct = reducedDef / (reducedDef + 50);
          const { dmg: leapDmg, wasCrit } = applySpellCrit(Math.floor(rawDmg * (1 - mitPct)));

          this.tweens.killTweensOf(hs);
          (hs as any)._leapStriking = true;

          const startX = hs.x;
          const startY = hs.baseY;
          const destX  = tgt.x - 60;
          const destY  = hs.baseY; // land at same ground level
          const jumpH  = 90;       // peak height above ground
          const leapDur = 370;

          // Start jump animation immediately
          if (this.anims.exists('hero_amazon_jump_anim')) hs.play('hero_amazon_jump_anim');

          // Tween a progress counter; onUpdate drives the parabolic arc
          const prog = { t: 0 };
          this.tweens.add({
            targets: prog,
            t: 1,
            duration: leapDur,
            ease: 'Linear',
            onUpdate: () => {
              const t = prog.t;
              hs.x = startX + (destX - startX) * t;
              // Parabolic Y: 4·h·t·(1-t) gives the arc peak at t=0.5
              hs.y = startY - jumpH * 4 * t * (1 - t);
              // Shadow follows ground position (x only, fixed at baseY)
              hs.shadow.setPosition(hs.x, startY + hs.shadowYOffset);
            },
            onComplete: () => {
              if (!hs.active) { (hs as any)._leapStriking = false; return; }
              hs.setPosition(destX, destY);
              hs.shadow.setPosition(destX, destY + hs.shadowYOffset);
              if (this.anims.exists('hero_amazon_attack2_anim')) hs.play('hero_amazon_attack2_anim');
              tgt.enemyRef.currentHp -= leapDmg;
              const label = `${leapDmg}`;
              this.showDamageNumber(tgt.x, tgt.y - 30, label, wasCrit ? '#fbbf24' : '#f472b6', wasCrit ? '27px' : '16px');
              this.showDamageNumber(tgt.x, tgt.y - 50, '🗡 ARMOR BREAK', '#f9a8d4');
              this.flashSprite(tgt);
              this.vfx.playGroundSlamStrike(tgt.x, tgt.y);
              this.applySpellSigils(hs, tgt, leapDmg);
              if (tgt.enemyRef.currentHp <= 0) this.killEnemy(tgt, hs);
              // Run back to base position after brief impact pause.
              // Target hs.baseX/baseY (not the captured startX/startY) so a slot swap mid-leap
              // still leaves her at her current formation slot.
              this.time.delayedCall(380, () => {
                if (!hs.active) { (hs as any)._leapStriking = false; return; }
                hs.setFlipX(true);
                if (this.anims.exists('hero_amazon_run_anim')) hs.play('hero_amazon_run_anim');
                this.tweens.add({
                  targets: hs,
                  x: hs.baseX,
                  y: hs.baseY,
                  duration: 320,
                  ease: 'Cubic.easeOut',
                  onUpdate: () => { hs.shadow.setPosition(hs.x, hs.y + hs.shadowYOffset); },
                  onComplete: () => {
                    if (!hs.active) { (hs as any)._leapStriking = false; return; }
                    this.tweens.killTweensOf(hs);
                    hs.setFlipX(false);
                    hs.setPosition(hs.baseX, hs.baseY);
                    hs.shadow.setPosition(hs.baseX, hs.baseY + hs.shadowYOffset);
                    (hs as any)._leapStriking = false;
                    // Resume the idle cycle (idle / idle2 80-20 mix) after Leap Strike
                    // touchdown, instead of dropping into the old looping throw stance.
                    const nextZ = Math.random() < 0.2 && this.anims.exists('hero_amazon_idle2')
                      ? 'hero_amazon_idle2' : 'hero_amazon_idle';
                    if (this.anims.exists(nextZ)) hs.play(nextZ);
                  },
                });
              });
            },
          });

        } else if (ability.id === 'freezing_shot') {
          // Freezing Shot: progressive slow — 100% at impact, fades to 0% over duration.
          // Damage scales with spell power + spell crit so the hit is meaningful at any
          // stage; previous handler used raw ability.damage (15) and read as 0 dmg in
          // late-game DPS tallies vs auto-attacks doing millions.
          const freezeDuration = (ability.effectDuration || 3000) + (talentStat === 'duration' ? talentBonus : 0);
          const baseDmg = ability.damage + sp;
          const { dmg: finalDmg, wasCrit } = applySpellCrit(baseDmg, target);
          target.enemyRef.currentHp -= finalDmg;
          this.applySpellSigils(hs, target, finalDmg);
          this.showDamageNumber(target.x, target.y - 40, `${finalDmg}`, wasCrit ? '#fbbf24' : '#93c5fd', wasCrit ? '27px' : '16px');
          if (target.enemyRef.currentHp <= 0) { this.killEnemy(target, hs); continue; }
          // Store frost slow state for progressive decay in the movement loop
          (target.enemyRef as any)._frozen = true;
          (target.enemyRef as any)._frozenStartTime = time;
          (target.enemyRef as any)._frozenDuration = freezeDuration;
          target.setTint(0x93c5fd); // icy blue tint
          this.time.delayedCall(freezeDuration, () => {
            (target.enemyRef as any)._frozen = false;
            if (target.active) this.restoreEnemyTint(target);
          });
          this.vfx.playFreezeHit(target.x, target.y);
          this.showDamageNumber(target.x, target.y - 25, '❄ SLOWED', '#93c5fd');
          this.flashSprite(target);

        } else if (ability.id === 'war_cry') {
          // War Cry: all heroes gain attack speed buff for effectDuration
          // Each talent rank: +10% attack speed AND +2s duration
          const warCryRank = hs.heroRef.talentBonuses?.['war_cry'] || 0;
          const buffDuration = (ability.effectDuration || 8000) + warCryRank * 2000;
          const atkSpdBonus = (ability.attackSpeedBuff || 0.10) + (talentStat === 'atkSpdMult' ? talentBonus : 0);
          for (const ally of this.heroSprites) {
            if (!ally.heroRef.isDead) {
              // Cancel any existing timer so a refreshed cast doesn't expire early
              ((ally.heroRef as any)._warCryTimer as Phaser.Time.TimerEvent | undefined)?.remove(false);
              (ally.heroRef as any)._warCryBuff = atkSpdBonus;
              (ally.heroRef as any)._warCryTimer = this.time.delayedCall(buffDuration, () => {
                (ally.heroRef as any)._warCryBuff = 0;
                (ally.heroRef as any)._warCryTimer = null;
              });
            }
          }
          this.vfx.playWarCry(hs.x, hs.y);
          const warCryPct = Math.round(atkSpdBonus * 100);
          const warCryDurS = Math.round(buffDuration / 1000);
          this.showDamageNumber(hs.x, hs.y - 45, `⚡ WAR CRY ${warCryPct}% FASTER ${warCryDurS}s`, '#fbbf24');
          // `_warCryBuff` cuts the attack interval (interval *= 1 - buff), so
          // the player-facing copy now describes that directly instead of the
          // misleading "+X% Attack Speed" label.
          this.registerPartyBuff('war_cry', 'War Cry', 'spell_war_cry', `All heroes attack ${warCryPct}% faster`, buffDuration);

        } else if (ability.id === 'poison_cloud') {
          // Poison Cloud: close-range AoE poison, no energy cost, hits all nearby enemies.
          // Phase-2 damageMult (rank 4+) scales the direct hit, not just the DOT.
          const phase2DmgMult = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'poison_cloud', 'damageMult');
          const abilityDmg = Math.floor((ability.damage + sp) * (1 + phase2DmgMult));
          const dotDuration = ability.effectDuration || 10000;
          const dotFraction = (ability.dotFraction || 0.1) + (talentStat === 'dotFraction' ? talentBonus : 0);
          for (const es of this.enemySprites) {
            if (es.enemyRef.currentHp > 0 && Math.abs(es.x - hs.x) < 280) {
              es.enemyRef.currentHp -= abilityDmg;
              if (es.enemyRef.currentHp <= 0) { this.killEnemy(es, hs); continue; }
              applyDebuff(es.enemyRef, {
                type: 'dot', value: abilityDmg * dotFraction,
                duration: dotDuration, remaining: dotDuration,
              });
              this.vfx.playPoisonCloud(es.x, es.y);
              this.showDamageNumber(es.x, es.y - 20, `${abilityDmg} ☁`, '#22c55e', '16px');
              this.flashSprite(es);
            }
          }
        } else if (ability.effectType === 'dot') {
          // DoT abilities (Sinister Strike, Whirlwind bleed, Word of Death, Thornlash) — talent adds damageMult or DoT%
          // Thornlash range gate is form-dependent:
          //   • Bear form (feral bite) — restricted to 2× warrior melee, no enemy → refund.
          //   • Human / moonfire form — true ranged spell, hits anywhere on the field.
          const THORNLASH_RANGE = WARRIOR_MELEE_RANGE * 2;
          if (ability.id === 'thornlash' && !!(hs.heroRef as any)._bearFormActive) {
            const inMelee = this.enemySprites.some((e) => e.active && e.enemyRef.currentHp > 0 && Math.abs(e.x - hs.x) <= THORNLASH_RANGE);
            if (!inMelee) {
              ability.currentCooldown = 0;
              hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + effectiveCost);
              continue;
            }
          }
          const targets = (ability.targetCount || 1) + (talentStat === 'targets' ? Math.floor(talentBonus) : 0);
          // damageMult talent: Whirlwind (+10%/rank) and Word of Death (+15%/rank).
          // Sinister Strike has a phase-2 damageMult (rank 4+) on top of its
          // phase-1 dotFraction — read it explicitly so the direct strike scales
          // at high rank instead of just the bleed.
          let damageMult = talentStat === 'damageMult' ? (1 + talentBonus) : 1;
          if (ability.id === 'sinister_strike') {
            const phase2Mult = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'sinister_strike', 'damageMult');
            if (phase2Mult > 0) damageMult *= (1 + phase2Mult);
          }
          // Nature Form empowers Thornlash by +50% (feral wolf mode bites harder).
          const isThornlashBear = ability.id === 'thornlash' && !!(hs.heroRef as any)._bearFormActive;
          const isThornlashMoon = ability.id === 'thornlash' && !(hs.heroRef as any)._bearFormActive;
          const natureFormBonus = isThornlashBear ? 1.5 : 1;
          // Whirlwind uses ATK at 60% weight (dropped from 0.72) + 18s CD.
          // Light trim per user pick — at-range damage penalty was removed
          // in part 274, so the 0.72 coef + full damage from any slot was
          // running Whirlwind ~1.5× peer DPS at lvl 30.
          // All other DoTs / spells scale off spell power.
          const heroAtk = ability.id === 'whirlwind' ? (this.effStats(hs.heroRef).attack || 0) * 0.60 : 0;
          // Moonfire (non-bear Thornlash) — single-target ranged spell that stacks on the
          // target for escalating damage. 40 base + 0.5·SP + 15 per existing stack (max 3
          // stacks, refreshes on every cast, decays 8 s after last hit). Intentionally
          // weaker per-cast than Fireball's flat 70 + SP so fresh-target first hit is a
          // lighter tap, but a fully-stacked third hit out-damages a single fireball.
          // Lunar Reverence (Thorne Elite Perk) raises the Moonfire stack cap
          // from 3 to 5 so the per-cast bonus keeps growing past the default.
          const moonfireCap = this.hasArmoryPassive(hs.heroRef, 'passive_thorne_lunar_reverence') ? 5 : 3;
          const moonfireStacks = isThornlashMoon
            ? (() => {
                const tgt = this.enemySprites
                  .filter((e) => e.active && e.enemyRef.currentHp > 0)
                  .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))[0];
                if (!tgt) return 0;
                const now = Date.now();
                const exp = (tgt.enemyRef as any)._moonfireStackExpiry ?? 0;
                return now < exp ? Math.min(moonfireCap, (tgt.enemyRef as any)._moonfireStacks ?? 0) : 0;
              })()
            : 0;
          const abilityDmg = ability.id === 'whirlwind'
            ? Math.floor((ability.damage + heroAtk) * damageMult)
            : isThornlashMoon
              ? Math.floor((40 + 0.5 * sp + moonfireStacks * 15) * damageMult)
              : Math.floor((ability.damage + sp) * damageMult * natureFormBonus);
          const dotDuration = ability.effectDuration || 10000;
          const dotFraction = (ability.dotFraction || 0.1) + (talentStat === 'dotFraction' ? talentBonus : 0);
          // Word of Death talent: can crit at max rank (rank 6)
          const wodRank = ability.id === 'word_of_death' ? ((hs.heroRef.talentBonuses || {})[ability.id] || 0) : 0;
          const wodCanCrit = wodRank >= 6;
          const dotTickDmg = abilityDmg * dotFraction;
          // Thornlash target pool — Bear form is melee-range single-target (the
          // feral bite). Moonfire (human form) is a TRUE LONG-RANGE spell —
          // targets the FARTHEST enemy on the field (back-line caster
          // priority) so Thorne can reach over the front row to chip
          // ranged threats. User-driven: "moonfire should be max range".
          const enemyTargets = ability.id === 'thornlash'
            ? (isThornlashBear
                ? this.enemySprites
                    .filter((e) => e.enemyRef.currentHp > 0 && Math.abs(e.x - hs.x) <= THORNLASH_RANGE)
                    .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))
                    .slice(0, 1)
                : this.enemySprites
                    .filter((e) => e.enemyRef.currentHp > 0)
                    .sort((a, b) => Math.abs(b.x - hs.x) - Math.abs(a.x - hs.x))
                    .slice(0, 1))
            : this.enemySprites
                .filter((e) => e.enemyRef.currentHp > 0)
                .slice(0, targets);
          // Druid Thornlash — play the attack anim on cast. In Nature Form use the wolf attack so
          // the feral silhouette is preserved for the duration of the buff.
          if (ability.id === 'thornlash') {
            const nature = !!(hs.heroRef as any)._bearFormActive;
            const druidAttack = nature ? 'hero_druid_nature_attack' : 'hero_druid_attack';
            const druidIdle   = nature ? 'hero_druid_nature_idle'   : 'hero_druid_idle';
            if (this.anims.exists(druidAttack) && hs.anims.currentAnim?.key !== druidAttack) {
              hs.off('animationcomplete');
              hs.play(druidAttack);
              hs.once('animationcomplete', () => {
                if (!hs.heroRef.isDead && this.anims.exists(druidIdle)) hs.play(druidIdle);
              });
            }
          }
          // Whirlwind visual — big spinning blades at the hero's position
          if (ability.id === 'whirlwind') {
            this.vfx.playWhirlwind(hs.x, hs.y - 50);
          }
          for (const es of enemyTargets) {
            // Whirlwind and Sinister Strike direct hits can crit (physical strike, not DoT tick).
            // Word of Death's TICK can crit at max rank (handled below).
            let hitDmg = abilityDmg;
            let hitWasCrit = false;
            if (ability.id === 'whirlwind' || ability.id === 'sinister_strike' || ability.id === 'thornlash') {
              const rolled = applySpellCrit(abilityDmg, es);
              hitDmg = rolled.dmg;
              hitWasCrit = rolled.wasCrit;
            }
            es.enemyRef.currentHp -= hitDmg;
            // Hero Armory — Thorne Nature's Cycle: a Thornlash hit that kills
            // restores 5 % of Thorne's max mana. Resolved here BEFORE killEnemy
            // so we can read maxMp cleanly.
            if (ability.id === 'thornlash'
              && es.enemyRef.currentHp <= 0
              && this.hasArmoryPassive(hs.heroRef, 'passive_thorne_natures_cycle')) {
              const maxMp = this.effStats(hs.heroRef).maxMp ?? 0;
              const restore = Math.max(1, Math.floor(maxMp * 0.05));
              hs.heroRef.stats.mp = Math.min(maxMp, (hs.heroRef.stats.mp ?? 0) + restore);
              this.showDamageNumber(hs.x, hs.y - 30, `ᛇ +${restore} MP`, '#86efac', '11px');
            }
            // Moonfire variant of Thornlash (non-bear) deals direct damage only — skip
            // the bleed DoT and instead bump the per-target stack counter so the NEXT
            // cast scales higher. Bear-form Thornlash keeps the 30 % bleed.
            if (isThornlashMoon) {
              const now = Date.now();
              const prevStacks = (now < ((es.enemyRef as any)._moonfireStackExpiry ?? 0))
                ? Math.min(moonfireCap, (es.enemyRef as any)._moonfireStacks ?? 0)
                : 0;
              (es.enemyRef as any)._moonfireStacks = Math.min(moonfireCap, prevStacks + 1);
              (es.enemyRef as any)._moonfireStackExpiry = now + 8000;
            } else {
              applyDebuff(es.enemyRef, {
                type: 'dot',
                value: wodCanCrit
                  ? (rollSpellCrit() ? Math.round(dotTickDmg * spellCritMult) : dotTickDmg)
                  : dotTickDmg,
                duration: dotDuration, remaining: dotDuration,
              });
            }
            // Sinister Strike = bleed (red); Whirlwind = blade (yellow); Word of Death = dark curse; others = poison
            let dotColor = '#22c55e'; // default poison green
            let dotIcon = '☠';
            if (ability.id === 'sinister_strike') {
              (es.enemyRef as any)._bleeding = true;
              this.vfx.playBleed(es.x, es.y);
              this.vfx.playSinisterStrike(es.x, es.y);
              dotColor = '#ef4444'; // red blood
              dotIcon = '🩸';
            } else if (ability.id === 'whirlwind') {
              (es.enemyRef as any)._bleeding = true;
              this.vfx.playBleed(es.x, es.y);
              dotColor = '#fbbf24'; // yellow blade
              dotIcon = '⚔';
            } else if (ability.id === 'word_of_death') {
              this.vfx.playWordOfDeath(es.x, es.y);
              dotColor = '#c080ff'; // violet curse
              dotIcon = '☠';
            } else if (ability.id === 'thornlash') {
              if (isThornlashMoon) {
                // Moonfire variant — dedicated crescent-moon drop VFX, plus a
                // small stack readout so the player can count their escalation.
                const curStacks = Math.min(moonfireCap, (es.enemyRef as any)._moonfireStacks ?? 0);
                this.vfx.playMoonfireStrike(es.x, es.y);
                this.showDamageNumber(es.x + 14, es.y - 46, `★${curStacks}`, '#93c5fd', '10px');
                dotColor = '#8bc8f0'; // moonfire blue — no DoT actually applied, but used below for null-ops
                dotIcon = '🌙';
              } else {
                this.vfx.playThornlashClaw(es.x, es.y - 30);
                dotColor = '#4ade80'; // nature green
                dotIcon = '🌿';
              }
              // Verdant Spread (rank 6 talent) stays bear-form only — moonfire has no bleed to spread.
              if (isThornlashMoon) continue;
              // Verdant Spread (rank 6 talent): the bleed jumps to the 2 nearest other
              // enemies for the same per-tick value + duration.
              const verdantUnlocked = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'thornlash', 'verdantSpread') > 0;
              if (verdantUnlocked) {
                const others = this.enemySprites
                  .filter((e) => e !== es && e.active && e.enemyRef.currentHp > 0)
                  .sort((a, b) => Math.abs(a.x - es.x) - Math.abs(b.x - es.x))
                  .slice(0, 2);
                for (const o of others) {
                  applyDebuff(o.enemyRef, {
                    type: 'dot', value: dotTickDmg, duration: dotDuration, remaining: dotDuration,
                  });
                  this.vfx.playThornlashClaw(o.x, o.y - 30);
                  this.showDamageNumber(o.x, o.y - 24, '🌿 SPREAD', '#86efac', '10px');
                }
              }
            } else {
              this.vfx.playPoisonDrip(es.x, es.y);
            }
            // Non-crit hits keep the flavour emoji; crits drop the emoji and render in gold
            // at a smaller size than direct-damage ability crits (which are 27px) so it's easy
            // to tell DoT crits apart at a glance.
            this.showDamageNumber(
              es.x, es.y - 20,
              hitWasCrit ? `${hitDmg} CRIT!` : `${hitDmg} ${dotIcon}`,
              hitWasCrit ? '#fbbf24' : dotColor,
              hitWasCrit ? '16px' : '12px',
            );
            this.flashSprite(es);
            // Prism/Echo sigils — only fire on the single-target DoT spells (word_of_death,
            // sinister_strike) to stay in the "single-target spell" spirit of the sigils.
            // Whirlwind (AoE) and thornlash (multi-target talent) are excluded.
            if (ability.id === 'word_of_death' || ability.id === 'sinister_strike') {
              this.applySpellSigils(hs, es, hitDmg);
            }
            if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
          }
        } else if (ability.effectType === 'buff') {
          if (ability.id === 'eagle_eye') {
          // Ranger Eagle Eye: crit buff — base: self only; talent: buff all allies
          const duration = ability.effectDuration || 8000;
          const critBoost = 0.15;
          const buffGroup = talentStat === 'groupBuff' && talentBonus > 0;
          const targets = buffGroup ? this.heroSprites.filter((h) => !h.heroRef.isDead) : [hs];
          this.vfx.playSunStrike(hs.x, hs.y);
          const label = buffGroup ? 'EAGLE EYE (ALL)!' : 'EAGLE EYE!';
          this.showDamageNumber(hs.x, hs.y - 30, label, '#f59e0b');
          for (const h of targets) {
            (h.heroRef as any)._eagleEyeCrit = ((h.heroRef as any)._eagleEyeCrit || 0) + critBoost;
            this.time.delayedCall(duration, () => {
              (h.heroRef as any)._eagleEyeCrit = Math.max(0, ((h.heroRef as any)._eagleEyeCrit || 0) - critBoost);
            });
          }
          if (buffGroup) {
            this.registerPartyBuff('eagle_eye', 'Eagle Eye', 'spell_eagle_eye', '+15% Crit Chance (All Heroes)', duration);
          }
          } else if (ability.id === 'arcane_might') {
          // Arcane Might: grant mana regen buff to all allies for duration
          this.vfx.playArcaneMight(hs.x, hs.y);
          const regenDuration = ability.effectDuration || 10000;
          const manaPerTick = 5; // mana per second tick
          const tickInterval = 1000;
          const ticks = Math.floor(regenDuration / tickInterval);
          this.showDamageNumber(hs.x, hs.y - 30, 'MANA SURGE!', '#4090c0');
          for (const h of this.heroSprites) {
            if (!h.heroRef.isDead) {
              let ticksDone = 0;
              const regenTimer = this.time.addEvent({
                delay: tickInterval,
                repeat: ticks - 1,
                callback: () => {
                  if (!h.heroRef.isDead) {
                    const hStats = this.effStats(h.heroRef);
                    if (h.heroRef.heroClass === 'assassin') {
                      h.heroRef.stats.energy = Math.min(hStats.maxEnergy, h.heroRef.stats.energy + manaPerTick);
                    } else if (h.heroRef.heroClass !== 'warrior') {
                      h.heroRef.stats.mp = Math.min(hStats.maxMp, h.heroRef.stats.mp + manaPerTick);
                    }
                  }
                  ticksDone++;
                  if (ticksDone >= ticks) regenTimer.destroy();
                },
              });
            }
          }
          // Resist all buff — base +1%, +1% per talent rank
          {
            const resistPerRank = talentStat === 'resistBonus' ? Math.round(talentBonus * 100) : 0;
            const totalResist = 1 + resistPerRank;
            const resistDuration = ability.effectDuration || 10000;
            for (const h of this.heroSprites) {
              if (!h.heroRef.isDead) {
                // Flag-based: never mutate hero.stats.*Resist (would corrupt saves if gsm.save() fires mid-buff)
                (h.heroRef as any)._arcaneMightResistBuff = ((h.heroRef as any)._arcaneMightResistBuff || 0) + totalResist;
                this.time.delayedCall(resistDuration, () => {
                  (h.heroRef as any)._arcaneMightResistBuff = Math.max(0, ((h.heroRef as any)._arcaneMightResistBuff || 0) - totalResist);
                });
              }
            }
            this.showDamageNumber(hs.x, hs.y - 45, `+${totalResist}% RESIST ALL`, '#4090c0');
            this.registerPartyBuff('arcane_might_resist', 'Arcane Might', 'spell_arcane_might', `+${totalResist}% All Resist (All Heroes)`, resistDuration);
          }
          } else {
            // Shield / Holy Shield: absorb HP. Numbers retuned after a balance
            // pass — the 200+8×SP × ×4 talent multiplier was producing 16K+ HP
            // shields at modest 500 SP investment (≈ 9× Ironheld's L30 HP),
            // letting tanks ignore a full boss rotation off one cast. New
            // baseline (100+3×SP) × +100% talent caps the same scenario at
            // ≈ 3 200 HP (~1.8× HP) — a meaningful bubble without the
            // god-mode feel.
            const shieldMult = 1 + (talentStat === 'shieldMult' ? talentBonus : 0);
            const shieldHeal = Math.floor((100 + sp * 3) * shieldMult);
            const lowestHero = this.heroSprites
              .filter((h) => !h.heroRef.isDead)
              .sort((a, b) => (a.heroRef.stats.hp / this.effStats(a.heroRef).maxHp) - (b.heroRef.stats.hp / this.effStats(b.heroRef).maxHp))[0];
            if (lowestHero) {
              // Holy Shield is single-target party-wide — only one hero can
              // carry the 'shield' source at any time. Sweep the entire
              // party and strip the source first so the new cast can
              // refresh on the same hero (or transfer to a different one).
              // Other shield sources (Bone Shield, Magma Shield, Shaman
              // Shield, ward / relic shields) stay independent — only
              // `'shield'` is gated.
              if (ability.id === 'shield') {
                for (const h of this.heroSprites) {
                  if (!h.heroRef.isDead) {
                    this.clearShieldSource(h.heroRef, 'shield');
                  }
                }
              }
              if (this.applyShield(lowestHero.heroRef, shieldHeal, ability.id)) {
                this.showDamageNumber(lowestHero.x, lowestHero.y - 30, `🛡${shieldHeal}`, '#dde6f0');
              }
            }
          }
        } else if (ability.effectType === 'bear_form') {
          // Druid (Thorne) Nature Form — shift into a feral wolf for 8s (60s CD).
          // +20% ATK, -25% damage taken, Thornlash +50% damage, Regrowth & Wild Growth disabled.
          // Talent (bearFormBonus, doubling) adds +5% → +10% → +20% → +40% → +80% on top of ATK + DR.
          const bearBonus = talentStat === 'bearFormBonus' ? talentBonus : 0;
          const atkMult = 0.20 + bearBonus;
          const drPct = 0.25 + bearBonus;
          const bearDuration = ability.effectDuration || 8000;
          this.showDamageNumber(hs.x, hs.y - 30, '🐺 NATURE FORM!', '#86efac');
          this.vfx.playBearForm(hs.x, hs.y);
          (hs.heroRef as any)._bearFormActive = true;
          (hs.heroRef as any)._bearFormAtkMult = atkMult;
          (hs.heroRef as any)._bearFormDrPct = drPct;
          // Swap sprite to the bear form — store prev texture so we can restore on expiry.
          const prevTex = hs.texture.key;
          hs.setData('natureFormPrevTex', prevTex);
          if (this.textures.exists('hero_druid_bear')) {
            hs.setTexture('hero_druid_bear');
            if (this.anims.exists('hero_druid_nature_idle')) hs.play('hero_druid_nature_idle');
            // Bear sprite has its own coloring — no tint or alpha overlay.
          } else {
            hs.setTint(0x94a000);
          }
          hs.setScale(1.12);
          // Bear has a wider footprint — stretch X only. See Wolf's Mantle branch above
          // for why Y stays at 1.0 and the shadow is lifted via BEAR_SHADOW_Y_OFFSET.
          hs.shadow.setScale(2.0, 1.0);
          this.time.delayedCall(bearDuration, () => {
            (hs.heroRef as any)._bearFormActive = false;
            (hs.heroRef as any)._bearFormAtkMult = 0;
            (hs.heroRef as any)._bearFormDrPct = 0;
            hs.setScale(1.0);
            hs.shadow.setScale(1.0, 1.0);
            hs.clearTint();
            hs.setAlpha(1);
            const restore = hs.getData('natureFormPrevTex') as string | undefined;
            if (restore && this.textures.exists(restore)) {
              hs.setTexture(restore);
              if (this.anims.exists(`${restore}_idle`)) hs.play(`${restore}_idle`);
            }
          });
        } else if (ability.effectType === 'berserk') {
          // Goomar Berserk: 1.1× size, red/violet tint, +20% crit dmg, +10% atk spd — talent doubles both bonuses
          // NOTE: stats are NOT mutated — bonuses are applied inline at calculation time to prevent
          // inflated values from being saved mid-berserk via gsm.save() calls during battle.
          const berserkBonus = talentStat === 'berserkBonus' ? talentBonus : 0;
          const critDmgBuff = 0.20 + berserkBonus;
          const spdMult = 1 + 0.10 + berserkBonus;
          const berserkDuration = ability.effectDuration || 8000;
          this.showDamageNumber(hs.x, hs.y - 30, '⚔ BERSERK!', '#ef4444');
          hs.setScale(1.1);
          (hs.heroRef as any)._berserkActive = true;
          (hs.heroRef as any)._berserkCritDmgBuff = critDmgBuff;
          (hs.heroRef as any)._berserkSpdMult = spdMult;
          this.time.delayedCall(berserkDuration, () => {
            (hs.heroRef as any)._berserkActive = false;
            (hs.heroRef as any)._berserkCritDmgBuff = 0;
            (hs.heroRef as any)._berserkSpdMult = 1;
            hs.setScale(1.0); // always reset scale regardless of alive/dead state
          });
        } else if (ability.effectType === 'reflect') {
          // Paladin Divine Reflect: talent adds +5% reflect per rank (max +20% at rank 4)
          const reflectPct = 0.3 + (talentStat === 'reflectPct' ? talentBonus : 0);
          this.showDamageNumber(hs.x, hs.y - 30, `REFLECT ${Math.round(reflectPct * 100)}%!`, '#5090c0');
          (hs.heroRef as any)._reflectActive = true;
          (hs.heroRef as any)._reflectPct = reflectPct;
          this.time.delayedCall(ability.effectDuration || 4000, () => {
            (hs.heroRef as any)._reflectActive = false;
          });
        } else if (ability.effectType === 'magma_shield') {
          // Mage Magma Shield: +100% DEF for duration. While active, any enemy
          // that strikes Zyphara is stunned for 0.5 s (+0.1 s per talent rank) —
          // retaliatory, not on-cast. The stun proc fires from applyDamageToHero.
          const shieldDuration = ability.effectDuration || 4000;
          const stunBonus = talentStat === 'duration' ? talentBonus : 0; // ms (perLevel:100)
          this.showDamageNumber(hs.x, hs.y - 30, 'MAGMA SHIELD!', '#f97316');
          this.vfx.playFireWall(hs.x, hs.y);
          // If shield is already active, cancel the old timer first to avoid double-stacking
          if ((hs.heroRef as any)._magmaShieldActive) {
            (hs.heroRef as any)._magmaShieldTimer?.remove(false);
            // Remove previously applied bonus before re-applying
            hs.heroRef.stats.defense = Math.round(hs.heroRef.stats.defense / 2);
          }
          // Double current DEF — snapshot AFTER any prior shield removal so base is clean
          const defBonus = hs.heroRef.stats.defense;
          hs.heroRef.stats.defense += defBonus;
          (hs.heroRef as any)._magmaShieldActive = true;
          // Stamp retaliation stun duration so applyDamageToHero can read it
          // without having to re-walk talents every hit.
          (hs.heroRef as any)._magmaShieldStunMs = 500 + stunBonus;
          const shieldTimer = this.time.delayedCall(shieldDuration, () => {
            // Remove exactly the bonus that was applied — not the current stat (tomes may have changed it)
            hs.heroRef.stats.defense -= defBonus;
            (hs.heroRef as any)._magmaShieldActive = false;
            (hs.heroRef as any)._magmaShieldStunMs = 0;
            (hs.heroRef as any)._magmaShieldTimer = null;
          });
          (hs.heroRef as any)._magmaShieldTimer = shieldTimer;
        } else if (ability.effectType === 'evasion') {
          // Rogue Evasion: talent adds +1s per level
          const evasionDur = (ability.effectDuration || 4000) + (talentStat === 'duration' ? talentBonus : 0);
          this.vfx.playEvasionBlur(hs.x, hs.y);
          this.showDamageNumber(hs.x, hs.y - 30, 'EVASION!', '#eab308');
          (hs.heroRef as any)._evasionActive = true;
          this.time.delayedCall(evasionDur, () => {
            (hs.heroRef as any)._evasionActive = false;
          });
        } else if (ability.effectType === 'hot') {
          // Priest Bless: HoT — talent adds +1 target per rank (rank 1: 2 heroes, rank 2: all 3)
          const hotTargets = 1 + (talentStat === 'targets' ? Math.floor(talentBonus) : 0);
          const blessAll = hotTargets >= this.heroSprites.filter((h) => !h.heroRef.isDead).length;
          // Re-cast block is per-ABILITY now — Lumiel's Bless and Thorne's
          // Regrowth no longer block each other, so the same hero can carry
          // both HoTs simultaneously. The shared `_blessActive` flag is still
          // set for the aura visual; the per-ability flag (`_hotActive_<id>`)
          // only gates re-cast of the SAME ability.
          const hotKey = `_hotActive_${ability.id}`;
          const sortedAllies = this.heroSprites
            .filter((h) => !h.heroRef.isDead && !(h.heroRef as any)[hotKey])
            .sort((a, b) => (a.heroRef.stats.hp / this.effStats(a.heroRef).maxHp) - (b.heroRef.stats.hp / this.effStats(b.heroRef).maxHp));
          // v2 talent Steady Heart amplifies all healing received (HoT included).
          const hotSteadyHeart = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).healingReceivedPct;
          const hotHealMult = Math.max(0, 1 + this.getBoonEffects().healReceivedPct) * (1 + hotSteadyHeart);
          for (let ai = 0; ai < Math.min(hotTargets, sortedAllies.length); ai++) {
          const lowestHero = sortedAllies[ai];
          if (lowestHero) {
            const hotDuration = ability.effectDuration || 10000;
            // Blood Price — scale HoT total heal by healReceivedPct (−50% on Blood Price).
            const totalHeal = Math.max(1, Math.floor((ability.healAmount + sp * 2) * hotHealMult));
            const tickInterval = 1000;
            const ticks = Math.floor(hotDuration / tickInterval);
            const healPerTick = Math.floor(totalHeal / ticks);
            (lowestHero.heroRef as any)._blessActive = true; // shared aura flag (any HoT active)
            (lowestHero.heroRef as any)[hotKey] = true;       // per-ability re-cast block
            // Flag so the aura-sprite check skips Regrowth targets. Regrowth also skips the HoT-glow
            // burst VFX (Lumiel Bless/Resurrect share that visual and it was visually conflating them).
            if (ability.id === 'regrowth') {
              (lowestHero.heroRef as any)._blessFromRegrowth = true;
            } else {
              this.vfx.playHoTGlow(lowestHero.x, lowestHero.y);
            }
            // Announce by ability name so druid Regrowth doesn't say "BLESS!"
            this.showDamageNumber(lowestHero.x, lowestHero.y - 30, ability.name.toUpperCase() + '!', '#22c55e');
            // Regrowth ranks 3-4 (talent `endOfHotBurst`): deliver a bonus burst heal at
            // the end of the HoT = burstFrac × total HoT healing done. perLevel2 = 0.25,
            // splitAtRank = 2 → rank 3: 0.25×1 = 25 %, rank 4: 0.25×2 = 50 %.
            const endBurstFrac = ability.id === 'regrowth'
              ? getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'regrowth', 'endOfHotBurst')
              : 0;
            let totalHealed = 0;
            let tickCount = 0;
            const hotTimer = this.time.addEvent({
              delay: tickInterval,
              repeat: ticks - 1,
              callback: () => {
                if (!lowestHero.heroRef.isDead) {
                  const maxHp = this.effStats(lowestHero.heroRef).maxHp;
                  const before = lowestHero.heroRef.stats.hp;
                  // Per-tick crit roll on the HoT — uses the caster's spell
                  // crit chance / crit damage (snapshotted at cast time).
                  const tickCrit = rollSpellCrit();
                  const tickHeal = tickCrit ? Math.round(healPerTick * spellCritMult) : healPerTick;
                  lowestHero.heroRef.stats.hp = Math.min(maxHp, before + tickHeal);
                  totalHealed += lowestHero.heroRef.stats.hp - before;
                  this.showDamageNumber(lowestHero.x, lowestHero.y - 30, `+${tickHeal}`, tickCrit ? '#86efac' : '#22c55e', tickCrit ? '16px' : '13px');
                }
                tickCount++;
                if (tickCount >= ticks) {
                  (lowestHero.heroRef as any)[hotKey] = false; // per-ability HoT done — re-cast allowed
                  // Only clear the shared aura flag if NO other HoT is still
                  // ticking on this hero (so dual Bless+Regrowth keeps the
                  // aura visible until both expire).
                  const stillHot = ['bless', 'regrowth'].some((id) =>
                    id !== ability.id && (lowestHero.heroRef as any)[`_hotActive_${id}`]);
                  if (!stillHot) {
                    (lowestHero.heroRef as any)._blessActive = false;
                    (lowestHero.heroRef as any)._blessFromRegrowth = false;
                  }
                  hotTimer.destroy();
                  // End-of-HoT burst heal (Regrowth rank 3+)
                  if (endBurstFrac > 0 && totalHealed > 0 && !lowestHero.heroRef.isDead) {
                    const maxHp = this.effStats(lowestHero.heroRef).maxHp;
                    const burst = Math.floor(totalHealed * endBurstFrac);
                    if (burst > 0) {
                      lowestHero.heroRef.stats.hp = Math.min(maxHp, lowestHero.heroRef.stats.hp + burst);
                      this.showDamageNumber(lowestHero.x, lowestHero.y - 40, `+${burst} BLOOM`, '#86efac', '14px');
                      this.vfx.playHoTGlow(lowestHero.x, lowestHero.y);
                    }
                  }
                }
              },
            });
          }
          } // end for hotTargets
          if (blessAll) {
            const hotDur = ability.effectDuration || 10000;
            const totalHeal = ability.healAmount + sp * 2;
            const healPerTick = Math.floor(totalHeal / Math.floor(hotDur / 1000));
            // Party buff keyed by ability id so druid Regrowth and priest Bless don't clash and use their own icons
            this.registerPartyBuff(`hot_all_${ability.id}`, ability.name, ability.iconKey || 'spell_bless', `+${healPerTick} HP/s (All Heroes)`, hotDur);
          }
        } else if (ability.id === 'piercing_shot') {
          // Sylvari Piercing Shot: devastating single-target, ignores 50% DEF, armor shred per rank for 3s
          const basePierceDmg = ability.damage + sp;
          // Ignore 50% of target defense
          const effectiveDef = Math.floor(target.enemyRef.defense * 0.5);
          const preDefDmg = Math.max(1, basePierceDmg - effectiveDef);
          const { dmg: pierceDmg, wasCrit } = applySpellCrit(preDefDmg, target);
          target.enemyRef.currentHp -= pierceDmg;
          // Bolt starts from top-right of caster sprite, travels to target
          this.vfx.playPiercingBolt(hs.x + 22, hs.y - 30, target.x, target.y - 20);
          this.showDamageNumber(target.x, target.y - 20, `${pierceDmg}`, wasCrit ? '#fbbf24' : '#f59e0b', wasCrit ? '27px' : '16px');
          this.flashSprite(target);
          this.applySpellSigils(hs, target, pierceDmg);
          // Armor shred: 10% per rank (up to 70%) — reduce target defense for 3s then restore
          const armorReduceFrac = Math.min(0.7, ability.level * 0.1);
          if (armorReduceFrac > 0) {
            const defReduction = Math.floor(target.enemyRef.defense * armorReduceFrac);
            target.enemyRef.defense -= defReduction;
            this.showDamageNumber(target.x, target.y - 35, `ARMOR -${Math.round(armorReduceFrac * 100)}%`, '#f59e0b');
            this.time.delayedCall(3000, () => {
              if (target.active) target.enemyRef.defense += defReduction;
            });
          }
          // Talent: add stun. Bosses are normally fully stun-immune (debuffs.ts),
          // but Piercing Shot is a hard-earned single-target spec spell — it bypasses
          // the immunity at 50% duration so the rank investment pays off vs bosses.
          if (talentStat === 'stunDuration' && talentBonus > 0) {
            const isBoss = target.enemyRef.tier === 'boss';
            const stunDur = isBoss ? Math.round(talentBonus * 0.5) : talentBonus;
            const stunDebuff = {
              type: 'stun' as const, value: 1,
              duration: stunDur, remaining: stunDur,
            };
            if (isBoss) {
              // applyDebuff would drop this entirely on bosses — push manually.
              const existing = target.enemyRef.debuffs.find((d) => d.type === 'stun');
              if (existing) {
                existing.remaining = stunDur; existing.duration = stunDur;
              } else {
                target.enemyRef.debuffs.push(stunDebuff);
              }
            } else {
              applyDebuff(target.enemyRef, stunDebuff);
            }
            this.maybeBrambleSpread(target, stunDebuff);
            this.showDamageNumber(target.x, target.y - 48, 'STUNNED!', '#fbbf24');
            this.vfx.playStunStars(target, stunDur);
          }
        } else if (ability.effectType === 'stone_form') {
          // Dwarf Stone Form: +150% defense buff for duration — talent adds +1s per rank
          // Don't stack if already active — reset timer only
          const stoneDuration = (ability.effectDuration || 6000) + (talentStat === 'duration' ? talentBonus : 0);
          this.showDamageNumber(hs.x, hs.y - 30, '🪨 STONE FORM!', '#94a3b8');
          this.vfx.playStoneForm(hs.x, hs.y);
          if (!(hs.heroRef as any)._stoneFormActive) {
            const baseDefense = this.effStats(hs.heroRef).defense;
            const defBonus = Math.floor(baseDefense * 1.5);
            hs.heroRef.stats.defense += defBonus;
            (hs.heroRef as any)._stoneFormDefBonus = defBonus;
            (hs.heroRef as any)._stoneFormActive = true;
          }
          (hs.heroRef as any)._stoneFormTimer = stoneDuration; // reset/extend timer
        } else if (ability.effectType === 'thunder_strike') {
          // Dwarf Thunder Strike: MID-range — guaranteed crit. Per user
          // direction the previous melee-only gate (`> WARRIOR_MELEE_RANGE`)
          // was too restrictive; back-row Lootfi could never land it.
          // Range bumped to `WARRIOR_MELEE_RANGE * 2` (= 320 px), matching
          // the Thornlash mid-range convention so the bolt has reach
          // without becoming a full ranged spell.
          // Talent phase 1 (ranks 1-3): +30%/rank double-strike chance (max 90%)
          // Talent phase 2 (rank 4+): +30%→60%→120%… bonus damage (doubling each rank)
          const distToThunder = Math.abs(hs.x - target.x);
          if (distToThunder > WARRIOR_MELEE_RANGE * 2) break; // wait until in mid-range
          const statsT = this.effStats(hs.heroRef);
          const thunderDmgBonus = getTalentBonusForStat(hs.heroRef.talentBonuses, ability.id, 'thunderDmg');
          const thunderBase = Math.max(1, Math.round((ability.damage + sp) * statsT.critDamage));
          const thunderDmg = Math.round(thunderBase * (1 + thunderDmgBonus));
          target.enemyRef.currentHp -= thunderDmg;
          this.showDamageNumber(target.x, target.y - 20, `${thunderDmg}`, '#fbbf24', '27px');
          this.flashSprite(target);
          this.applySpellSigils(hs, target, thunderDmg);
          // VFX: lightning bolt strike on the enemy — bolt height matches enemy sprite 1:1.
          if (this.anims.exists('fx_lightning_bolt_strike')) {
            const enemyH = Math.max(48, target.displayHeight || 96);
            // Bolt art is vertical, so keep width narrower than height so the aspect is preserved.
            const boltW = Math.round(enemyH * 0.7);
            const boltH = Math.round(enemyH);
            const boltY = target.y - enemyH / 2; // center on the enemy body
            const boltFx = this.add.sprite(target.x, boltY, 'effect_lightning_bolt_strike')
              .setDisplaySize(boltW, boltH).setDepth(25).setAlpha(0.95).setOrigin(0.5, 0.5);
            boltFx.play('fx_lightning_bolt_strike');
            boltFx.once('animationcomplete', () => boltFx.destroy());
          }
          if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
          // Talent phase 1: double strike chance (0%–90%)
          const doubleChance = getTalentBonusForStat(hs.heroRef.talentBonuses, ability.id, 'doubleStrike');
          if (doubleChance > 0 && Math.random() < doubleChance) {
            target.enemyRef.currentHp -= thunderDmg;
            this.showDamageNumber(target.x, target.y - 40, `${thunderDmg}`, '#f97316', '14px');
            this.flashSprite(target);
            if (target.enemyRef.currentHp <= 0 && target.active) this.killEnemy(target, hs);
          }
        } else if (ability.effectType === 'mace_throw') {
          // Dwarf Mace Throw: thrown attack — distance-based damage scaling
          // + talent adds damage. Now picks up Lootfi's effective ATK on
          // top of the spell-power baseline (was `damage + sp` only),
          // mirroring Zayla's Spear Volley pattern (`atk * 3.5 + 30`) so
          // a fully-geared dwarf at mid/late game still feels the throw.
          // Coefficient is ATK * 2 — lower than Spear Volley because
          // (a) Mace Throw is a 16-s cooldown spell (Spear Volley fires
          // every 10 autos), (b) distMult / talent / expertise stack
          // multiplicatively on top, and (c) it dazes for 2 s as a CC
          // bonus. At max distance × max talent the multiplier is ~5.2 ×.
          const dist = Math.abs(hs.x - target.x);
          const distMult = Math.max(0.8, Math.min(2.0, dist / 200));
          const maceDmgMult = 1 + (talentStat === 'damageMult' ? talentBonus : 0);
          // Hero Armory — Lootfi Thrown Expertise: +15 % mace-throw damage and
          // can ricochet to one nearby enemy for 50 % of the hit.
          const thrownExpertise = this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_thrown_expertise');
          const expertiseMult = thrownExpertise ? 1.15 : 1;
          const maceAtk = this.effStats(hs.heroRef).attack || 0;
          const maceBase = Math.round((ability.damage + sp + maceAtk * 2) * distMult * maceDmgMult * expertiseMult);
          const maceTargets = 1; // no extra targets
          // Play Lootfi throw animation
          const hkMace = hs.heroRef.heroKey ?? hs.heroRef.heroClass;
          if (this.anims.exists(`hero_${hkMace}_special_anim`)) {
            hs.off('animationcomplete');
            hs.play(`hero_${hkMace}_special_anim`);
            hs.once('animationcomplete', () => {
              if (!hs.heroRef.isDead && this.anims.exists(`hero_${hkMace}_idle`)) hs.play(`hero_${hkMace}_idle`);
            });
          }
          const maceEnemies = this.enemySprites
            .filter((e) => e.enemyRef.currentHp > 0)
            .slice(0, maceTargets);
          for (const es of maceEnemies) {
            const { dmg: maceDmg, wasCrit } = applySpellCrit(maceBase, es);
            // Launch projectile from Lootfi to the target, deal damage on arrival
            const proj = this.add.image(hs.x + 20, hs.y - 10, 'icon_mace')
              .setDisplaySize(36, 36).setDepth(25).setOrigin(0.5).setTint(0xfbbf24);
            const travelMs = Math.max(180, Math.min(480, Math.abs(es.x - hs.x) * 0.55));
            this.tweens.add({
              targets: proj,
              x: es.x,
              y: es.y - 10,
              angle: 360 * 3,
              duration: travelMs,
              ease: 'Linear',
              onComplete: () => {
                proj.destroy();
                if (es.active && es.enemyRef.currentHp > 0) {
                  es.enemyRef.currentHp -= maceDmg;
                  this.showDamageNumber(es.x, es.y - 20, `${maceDmg}`, wasCrit ? '#fbbf24' : '#d97706', wasCrit ? '27px' : '16px');
                  this.flashSprite(es);
                  // Spell sigil hookup (Prism Split / Echo) — Mace Throw is a
                  // single-target spell like Piercing Shot / Thunder Strike,
                  // so it should proc the same sigils. Was missing before.
                  this.applySpellSigils(hs, es, maceDmg);
                  // Daze: stun the target for 2s
                  const dazeDebuff = { type: 'stun' as const, value: 1, duration: 2000, remaining: 2000 };
                  applyDebuff(es.enemyRef, dazeDebuff);
                  this.maybeBrambleSpread(es, dazeDebuff);
                  this.showDamageNumber(es.x, es.y - 38, 'DAZED', '#5090c0');
                  if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);

                  // Hero Armory — Lootfi Thrown Expertise: ricochet to one
                  // nearby enemy for 50 % of the original mace damage.
                  if (thrownExpertise) {
                    const ricochet = this.enemySprites
                      .filter((o) => o !== es && o.active && o.enemyRef.currentHp > 0)
                      .sort((a, b) => Math.abs(a.x - es.x) - Math.abs(b.x - es.x))[0];
                    if (ricochet) {
                      const ricoDmg = Math.max(1, Math.floor(maceDmg * 0.50));
                      ricochet.enemyRef.currentHp -= ricoDmg;
                      this.showDamageNumber(ricochet.x, ricochet.y - 20, `${ricoDmg}`, '#fbbf24', '13px');
                      this.flashSprite(ricochet);
                      if (ricochet.enemyRef.currentHp <= 0) this.killEnemy(ricochet, hs);
                    }
                  }
                }
              },
            });
          }
        } else if (ability.effectType === 'shield_bash_charge') {
          // ── Valka Shield Bash ────────────────────────────────────────────
          // Front-row / charge-grid slot (col 4/5) ONLY. From any other slot
          // the cast is refunded so she can't "in-place swing" the ability —
          // she's a charge specialist by design and the back-row swing
          // variant felt like spammy spell-on-cooldown filler.
          const sbSlotIdx = hs.getData('slotIdx') as number | undefined;
          const sbCanCharge = sbSlotIdx !== undefined && this.isFrontRow(sbSlotIdx);
          if (!sbCanCharge) {
            // Not in a front-row / charge slot — refund cost + clear CD so the
            // ability re-checks next tick (lets the player swap her forward
            // mid-fight and have the Shield Bash fire on the new slot).
            ability.currentCooldown = 0;
            hs.heroRef.stats.rage = Math.min(hs.heroRef.stats.maxRage, hs.heroRef.stats.rage + effectiveCost);
            continue;
          }
          // Front-row: pick the nearest enemy in front (no range cap).
          const targetsInFront = this.enemySprites
            .filter((e) => e.active && e.enemyRef.currentHp > 0
              && e.x > hs.x - 20 && Math.abs(e.y - hs.y) < 140)
            .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x));
          const sbTarget = targetsInFront[0];
          if (!sbTarget) {
            ability.currentCooldown = 0;
            hs.heroRef.stats.rage = Math.min(hs.heroRef.stats.maxRage, hs.heroRef.stats.rage + effectiveCost);
            continue;
          }
          // Pre-compute impact inputs (damage / shield).
          const sbStats = this.effStats(hs.heroRef);
          const sbDmgMult = talentStat === 'damageMult' ? (1 + talentBonus) : 1;
          const sbBase = (ability.damage / 100) * sbStats.attack;
          const sbDmg = Math.max(1, Math.round(sbBase * sbDmgMult));
          const sbShieldDur = ability.effectDuration || 4000;
          const sbShieldFrac = this.hasArmoryPassive(hs.heroRef, 'passive_valka_shieldbearers_oath') ? 0.40 : 0.25;
          const sbShieldHp = Math.max(1, Math.round(hs.heroRef.stats.maxHp * sbShieldFrac));
          const hAny = hs.heroRef as any;
          // Impact body — applies damage + slow + VFX + absorb shield. Shared
          // between the charge path (fires on arrival) and the in-place path
          // (fires immediately).
          const applyShieldBashImpact = (): void => {
            if (sbTarget.active && sbTarget.enemyRef.currentHp > 0) {
              // Shield Bash now rolls crit (matches Valka's other ability,
              // Tempest Slam). Crit hits get a brighter / larger damage number.
              const sbRoll = applySpellCrit(sbDmg, sbTarget);
              const sbFinal = sbRoll.dmg;
              sbTarget.enemyRef.currentHp -= sbFinal;
              this.showDamageNumber(sbTarget.x, sbTarget.y - 20, `${sbFinal}`, sbRoll.wasCrit ? '#fff7b0' : '#c084fc', sbRoll.wasCrit ? '24px' : '18px');
              this.flashSprite(sbTarget);
              applyDebuff(sbTarget.enemyRef, { type: 'slow', value: 0.40, duration: 2000, remaining: 2000 });
              this.showDamageNumber(sbTarget.x, sbTarget.y - 40, 'SLOWED', '#93c5fd');
              if (sbTarget.enemyRef.currentHp <= 0) this.killEnemy(sbTarget, hs);
              this.vfx.playValkaShieldBash(sbTarget.x, sbTarget.y);
            }
            // Absorb shield lands regardless of target survival.
            hAny._shieldHp    = Math.max(hAny._shieldHp ?? 0, sbShieldHp);
            hAny._shieldMaxHp = Math.max(hAny._shieldMaxHp ?? 0, sbShieldHp);
            this.showDamageNumber(hs.x, hs.y - 40, `ᛟ +${sbShieldHp} SHIELD`, '#c084fc', '11px');
            this.time.delayedCall(sbShieldDur, () => {
              if ((hAny._shieldHp ?? 0) > 0) {
                hAny._shieldHp = Math.max(0, (hAny._shieldHp as number) - sbShieldHp);
                if (hAny._shieldHp <= 0) hAny._shieldMaxHp = 0;
              }
            });
          };
          {
            // ── Front-row charge path (only path now) ─────────────────────
            // Valka slides forward to the target, crashes on impact, tweens
            // back. `_shieldBashCharging` blocks auto-attacks mid-slide.
            // Duration scales with travel distance so a near target takes
            // ~180 ms and a far target ~500 ms — reads as a real charge.
            const baseX = hs.baseX ?? hs.x;
            const baseY = hs.baseY ?? hs.y;
            const destX = Math.max(hs.x + 30, sbTarget.x - 50);
            const destY = sbTarget.y;
            const travel = Math.abs(destX - hs.x);
            const chargeDur = Math.max(180, Math.min(500, travel * 0.9));
            hAny._shieldBashCharging = true;
            hs.setFlipX(false);
            if (this.anims.exists('hero_valkyrie_run_anim')) {
              hs.off('animationcomplete');
              hs.play('hero_valkyrie_run_anim');
            }
            this.tweens.killTweensOf(hs);
            this.tweens.add({
              targets: hs, x: destX, y: destY, duration: chargeDur, ease: 'Quad.easeOut',
              onUpdate: () => {
                hs.shadow?.setPosition(hs.x, hs.y + hs.shadowYOffset);
                hs.setDepth(2 + hs.y * 0.001);
              },
              onComplete: () => {
                applyShieldBashImpact();
                // Stay phase — Valka remains at the target for the warrior
                // charge-stay duration (matches Goomar's state-machine stay)
                // so she gets a full melee attack window before returning.
                // Release `_shieldBashCharging` here so auto-attacks fire
                // during the stay — she's in range and should be swinging.
                hAny._shieldBashCharging = false;
                if (!hs.heroRef.isDead && this.anims.exists('hero_valkyrie_idle')) {
                  hs.play('hero_valkyrie_idle');
                }
                // Track the stay→return timer on the hero so death/revive can
                // cancel it. Without this, Valka dying during the 9s stay window
                // left a ghost timer that fired after Lumiel resurrected her —
                // re-set _shieldBashCharging = true and started a stale return
                // tween, which is the "stuck after revive" bug.
                hAny._sbStayCall = this.time.delayedCall(this.WARRIOR_CHARGE_STAY, () => {
                  hAny._sbStayCall = null;
                  if (hs.heroRef.isDead || !hs.active) {
                    hAny._shieldBashCharging = false;
                    return;
                  }
                  // Re-block auto-attacks during the return tween.
                  hAny._shieldBashCharging = true;
                  // Face LEFT (toward her slot) and re-enforce it every frame
                  // of the tween — something else in the update loop is
                  // stomping the flip back to default, so pin it in onUpdate.
                  hs.setFlipX(true);
                  if (this.anims.exists('hero_valkyrie_run_anim')) {
                    hs.off('animationcomplete');
                    hs.play('hero_valkyrie_run_anim');
                  }
                  this.tweens.add({
                    targets: hs, x: baseX, y: baseY, duration: 300, ease: 'Quad.easeIn',
                    onUpdate: () => {
                      if (!hs.flipX) hs.setFlipX(true); // defensive re-flip per frame
                      hs.shadow?.setPosition(hs.x, hs.y + hs.shadowYOffset);
                      hs.setDepth(2 + hs.y * 0.001);
                    },
                    onComplete: () => {
                      hAny._shieldBashCharging = false;
                      hs.setPosition(baseX, baseY);
                      hs.shadow?.setPosition(baseX, baseY + hs.shadowYOffset);
                      hs.setDepth(2 + baseY * 0.001);
                      hs.setFlipX(false);
                      if (!hs.heroRef.isDead && this.anims.exists('hero_valkyrie_idle')) {
                        hs.play('hero_valkyrie_idle');
                      }
                    },
                  });
                });
              },
            });
          }

        } else if (ability.effectType === 'whirlwind_bludgeon') {
          // ── Valka Tempest Slam (ability id kept internally as
          // `whirlwind_bludgeon` so existing talent ranks / save data migrate
          // cleanly). Phase 1: 3 AoE ticks over 1.2 s around her (~55% ATK
          // each). Unique enemies struck are collected in a Set so the Slam
          // finisher can scale its damage by the crowd size.
          const wbStats = this.effStats(hs.heroRef);
          // Phase-1 damageMult: ranks 1-3 doubling +15% → cumulative +105% at rank 3.
          const phase1Mult = talentStat === 'damageMult' ? talentBonus : 0;
          // Phase-2 damageMult: ranks 4-6 each add a flat +30% on top, while
          // also unlocking the Crushing Finisher stun. Both pulled from the
          // single `crushingFinisher` stat so the helper handles cumulative
          // math consistently.
          const crushingFinisherMult = getTalentBonusForStat(hs.heroRef.talentBonuses, ability.id, 'crushingFinisher');
          const wbDmgMult = 1 + phase1Mult + crushingFinisherMult;
          const tickDmg = Math.max(1, Math.round(wbStats.attack * 0.495 * wbDmgMult));
          const hitIds = new Set<string>();
          // Whirlwind reaches like Goomar's Whirlwind — widened spin radius
          // so the storm actually hits enemies that aren't point-blank on
          // Valka. 110 → 220 px.
          const spinRadius = 220;
          // VFX facing — captured once at cast so both the spin and the slam
          // finisher mirror consistently even if the closest enemy changes
          // mid-combo. Uses the nearest enemy relative to her x.
          const wbNearest = this.enemySprites
            .filter((e) => e.active && e.enemyRef.currentHp > 0)
            .sort((a, b) => Math.hypot(a.x - hs.x, a.y - hs.y) - Math.hypot(b.x - hs.x, b.y - hs.y))[0];
          const wbFacingLeft = wbNearest ? wbNearest.x < hs.x : false;
          const spinTick = () => {
            if (hs.heroRef.isDead || !hs.active) return;
            for (const es of this.enemySprites) {
              if (!es.active || es.enemyRef.currentHp <= 0) continue;
              if (Phaser.Math.Distance.Between(hs.x, hs.y, es.x, es.y) > spinRadius) continue;
              // Each spin tick now rolls crit (matching Whirlwind's behavior).
              const tickRoll = applySpellCrit(tickDmg, es);
              const tickFinal = tickRoll.dmg;
              es.enemyRef.currentHp -= tickFinal;
              this.showDamageNumber(es.x, es.y - 20, `${tickFinal}`, tickRoll.wasCrit ? '#ffd060' : '#fbbf24', tickRoll.wasCrit ? '16px' : '13px');
              this.flashSprite(es);
              hitIds.add(es.enemyRef.instanceId);
              if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
            }
          };
          this.vfx.playValkaWhirlwind(hs.x, hs.y, wbFacingLeft);
          spinTick();
          this.time.delayedCall(400,  () => spinTick());
          this.time.delayedCall(800,  () => spinTick());
          // Phase 2: Tempest Slam finisher at 1.2 s. Base 180% ATK + 40% ATK
          // per UNIQUE enemy hit during the spin (no cap per user spec).
          // Stun unlocks once the Crushing Finisher rank is invested (any
          // value > 0 means rank ≥ 4 since perLevel2 = 0.30).
          const stunFromTalent = crushingFinisherMult;
          this.time.delayedCall(1200, () => {
            if (hs.heroRef.isDead || !hs.active) return;
            const N = hitIds.size;
            const baseSlam = wbStats.attack * (1.62 + 0.36 * N) * wbDmgMult;
            // Valka — Crushing Edge: +15 % armour pen per unique spin-hit.
            // Approximated by adding back N × 15 % of the defence-mitigated
            // portion of the hit (mirrors Goomar's Crushing Momentum shape).
            const hasCrushingEdge = this.hasArmoryPassive(hs.heroRef, 'passive_valka_crushing_edge');
            // Tempest Slam now reaches like Goomar's Whirlwind — hits EVERY
            // live enemy in front of Valka (no radius cap). "Front" is the
            // side the nearest enemy is on (wbFacingLeft) so the slam still
            // works when she's swinging left rather than right.
            const slamXMin = wbFacingLeft ? -Infinity : hs.x - 20;
            const slamXMax = wbFacingLeft ? hs.x + 20 : Infinity;
            let slammed = 0;
            for (const es of this.enemySprites) {
              if (!es.active || es.enemyRef.currentHp <= 0) continue;
              if (es.x < slamXMin || es.x > slamXMax) continue;
              let slamDmg = baseSlam;
              if (hasCrushingEdge && N > 0) {
                const defMitigation = 1 - Math.min(0.85, es.enemyRef.defense / (es.enemyRef.defense + 100));
                slamDmg *= (1 + Math.min(0.90, 0.15 * N) * (1 - defMitigation));
              }
              // Slam finisher rolls crit too — closes the parity gap with
              // Whirlwind so Tempest Slam isn't crit-locked-out.
              const slamRoll = applySpellCrit(Math.max(1, Math.round(slamDmg)), es);
              const slamFinal = slamRoll.dmg;
              es.enemyRef.currentHp -= slamFinal;
              this.showDamageNumber(es.x, es.y - 20, `${slamFinal}`, slamRoll.wasCrit ? '#fff7b0' : '#f0c040', slamRoll.wasCrit ? '26px' : '22px');
              this.flashSprite(es);
              // Crushing Finisher talent: if spin hit 4+ enemies AND talent present, stun for 1.5 s.
              if (N >= 4 && stunFromTalent > 0) {
                applyDebuff(es.enemyRef, { type: 'stun', value: 1, duration: 1500, remaining: 1500 });
              }
              if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
              slammed++;
            }
            const slamOffset = wbFacingLeft ? -40 : 40;
            this.vfx.playValkaBludgeonSlam(hs.x + slamOffset, hs.y, wbFacingLeft);
            if (slammed > 0) {
              this.showDamageNumber(hs.x, hs.y - 50, `TEMPEST ×${N + 1}`, '#ffd060', '14px');
            }
          });

        } else if (ability.effectType === 'plant_banner') {
          // ── Valka Banner of Valor ───────────────────────────────────────
          // Cast VFX: single gold→red shockwave ring (no persistent ground
          // ellipse — the old "sticky" circle was visually noisy). While the
          // banner is up, Valka + every ally in range get a subtle battle-red
          // tint so the buffed fighters read as enraged at a glance. `_warCryBuff`
          // is reused for the atk-speed stamp; HUD buff icon uses the Valka
          // banner glyph (spell_valka_banner) so the HUD-row matches the skill.
          const bannerDur = ability.effectDuration || 10000;
          const bannerAtkSpeed = 0.15;
          const bannerRadius = 160;
          this.vfx.playValkaBannerShockwave(hs.x, hs.y);
          // Rally Standard armory perk: banner also grants +30 % crit damage.
          const hasRallyStandard = this.hasArmoryPassive(hs.heroRef, 'passive_valka_rally_standard');
          // Battle-red tint — darker, slightly desaturated red so the sprite
          // still reads but the "in-the-aura" state is obvious. Tracked per
          // affected hero so we only clear the tint on heroes we actually tinted.
          const BANNER_TINT = 0xc24848;
          const tintedHeroes = new Set<HeroSprite>();
          const stamp = () => {
            // 1) Update the buff + tint set for heroes currently inside radius.
            const inside = new Set<HeroSprite>();
            for (const ally of this.heroSprites) {
              if (ally.heroRef.isDead) continue;
              if (Phaser.Math.Distance.Between(ally.x, ally.y, hs.x, hs.y) > bannerRadius) continue;
              inside.add(ally);
              const a = ally.heroRef as any;
              a._warCryBuff = Math.max(a._warCryBuff ?? 0, bannerAtkSpeed);
              if (hasRallyStandard) a._rallyCritDmgBuff = 0.30;
              if (!tintedHeroes.has(ally)) {
                ally.setTint(BANNER_TINT);
                tintedHeroes.add(ally);
              }
            }
            // 2) Drop tint from heroes who left the radius.
            for (const prev of Array.from(tintedHeroes)) {
              if (!inside.has(prev)) {
                if (prev.active) prev.clearTint();
                tintedHeroes.delete(prev);
              }
            }
          };
          // Valka herself always gets the tint for the full duration —
          // she's the banner-bearer, radius-check is redundant for her.
          hs.setTint(BANNER_TINT);
          tintedHeroes.add(hs);
          stamp();
          // Refresh every 500 ms so allies that walk in / out get updated.
          const timer = this.time.addEvent({ delay: 500, loop: true, callback: stamp });
          this.time.delayedCall(bannerDur, () => {
            timer.remove(false);
            for (const ally of this.heroSprites) {
              const a = ally.heroRef as any;
              if ((a._warCryBuff ?? 0) === bannerAtkSpeed) a._warCryBuff = 0;
              if (hasRallyStandard) a._rallyCritDmgBuff = 0;
            }
            // Clear every hero we tinted, whether they're still in range or not.
            for (const prev of tintedHeroes) {
              if (prev.active) prev.clearTint();
            }
            tintedHeroes.clear();
          });
          this.showDamageNumber(hs.x, hs.y - 60, '🚩 BANNER OF VALOR', '#c084fc', '12px');
          // Effect: `interval *= (1 - bannerAtkSpeed)` → e.g. 0.15 = -15%
          // attack interval ≈ +17.6% effective attack rate. Old description
          // said "+15% Attack Speed" which was misleading; the new copy
          // describes what actually happens.
          const intervalCutPct = Math.round(bannerAtkSpeed * 100);
          this.registerPartyBuff('plant_banner', 'Banner of Valor', 'spell_valka_banner',
            `Allies in range attack ${intervalCutPct}% faster`, bannerDur);

        } else if (ability.effectType === 'vine_snare') {
          // Sylvari Vine Snare: root + massive DoT on frontmost 3 enemies
          const damageMult = 1 + (talentStat === 'damageMult' ? talentBonus : 0);
          const snapDmg = Math.round((ability.damage + sp) * damageMult);
          const dotDuration = ability.effectDuration || 6000;
          const dotValue = Math.round(snapDmg * (ability.dotFraction || 0.9));
          // Close-range only: 2.5× melee range from hero
          const vineRange = WARRIOR_MELEE_RANGE * 2.5;
          const rootTargets = this.enemySprites
            .filter((e) => e.active && e.enemyRef.currentHp > 0 && Math.abs(e.x - hs.x) <= vineRange)
            .sort((a, b) => a.x - b.x) // frontmost = lowest X
            .slice(0, 3);
          for (const es of rootTargets) {
            // Initial burst damage
            es.enemyRef.currentHp -= snapDmg;
            this.showDamageNumber(es.x, es.y - 20, `${snapDmg}`, '#4ade80', '16px');
            this.flashSprite(es);
            // Root (stun for 2s)
            const vineStun = { type: 'stun' as const, value: 1, duration: 2000, remaining: 2000 };
            applyDebuff(es.enemyRef, vineStun);
            this.maybeBrambleSpread(es, vineStun);
            // Massive DoT
            applyDebuff(es.enemyRef, { type: 'dot', value: dotValue, duration: dotDuration, remaining: dotDuration });
            // Poison Trap VFX — two dark-green ellipses at the rooted enemy's feet.
            this.showPoisonTrapRoots(es, 2050);
            if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
          }
        } else {
          // Default damage abilities (damage, multishot, etc.)
          // Talent: +targets for multishot, +bonusDamage for consecration, etc.
          // Fireball / Eruption use trimmed SP scaling per balance pass —
          // 1.0 → 0.9 fireball / 0.85 eruption (was both at 1.0 ×). Other
          // wizard / caster spells stay at 1.0× to keep their identity.
          const spScale = ability.id === 'fireball' ? 0.9
            : ability.id === 'eruption' ? 0.85
            : 1.0;
          const abilityDmg = ability.damage + Math.round(sp * spScale);
          const bonusMult = getBonusSpellDamageMult(target.enemyRef);
          // Apply primary-stat damage talents:
          //   • bonusDamage (Consecration) — +X% AoE damage
          //   • damageMult  (Death Coil and any other ability whose phase-1
          //     stat is damageMult that doesn't have a dedicated branch) —
          //     was previously ignored here, leaving the talent dead. Bug
          //     uncovered by the spell-scaling audit.
          let talentDmgMult = 1;
          if (talentStat === 'bonusDamage') talentDmgMult = 1 + talentBonus;
          else if (talentStat === 'damageMult') talentDmgMult = 1 + talentBonus;
          // Phase-2 damageMult — Fireball / Eruption (and Sinister Strike /
          // Poison Cloud below) added a damageMult phase at high ranks so
          // direct hit scales, not just the DOT. Read explicitly via
          // getTalentBonusForStat because the primary `talentStat` only
          // exposes phase-1 (dotFraction).
          if (ability.id === 'fireball' || ability.id === 'eruption') {
            const phase2Mult = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, ability.id, 'damageMult');
            if (phase2Mult > 0) talentDmgMult *= (1 + phase2Mult);
          }

          // ── Hero Armory — Zyphara cast-time multipliers ─────────────────
          // Stack additively into a single perkMult so Fireball/Eruption/etc.
          // pick them up via the standard baseFinalDmg term.
          const casterRef = hs.heroRef as any;
          let perkMult = 1;
          // Arcane Overflow — at full mana before this cast, +20 % dmg, then
          // consumed (mana ticks down on the cast itself, so we check pre-cast).
          if (this.hasArmoryPassive(hs.heroRef, 'passive_zyphara_arcane_overflow')) {
            const maxMp = this.effStats(hs.heroRef).maxMp ?? 0;
            if ((casterRef._mpBeforeCast ?? hs.heroRef.stats.mp) >= maxMp) {
              perkMult *= 1.20;
              this.showDamageNumber(hs.x, hs.y - 50, 'ᚨ OVERFLOW', '#a8c8ff', '11px');
            }
          }
          // Elemental Insight — first spell cast each wave gets +15 % dmg.
          if (this.hasArmoryPassive(hs.heroRef, 'passive_zyphara_elemental_insight')
            && !casterRef._elementalInsightUsed) {
            perkMult *= 1.15;
            casterRef._elementalInsightUsed = true;
            this.showDamageNumber(hs.x, hs.y - 60, 'ᛉ INSIGHT', '#ffe060', '11px');
          }
          const baseFinalDmg = Math.round(abilityDmg * bonusMult * talentDmgMult * perkMult);

          // Pyromancer's Momentum — every cast trims 0.25 s off Zyphara's
          // OTHER spells' active cooldowns. Applied per-cast, not per-target,
          // so multi-hit spells don't double-tick the cooldown reduction.
          if (this.hasArmoryPassive(hs.heroRef, 'passive_zyphara_pyromancer_momentum')) {
            for (const otherAbility of hs.heroRef.abilities) {
              if (otherAbility === ability) continue;
              if ((otherAbility.currentCooldown ?? 0) > 0) {
                otherAbility.currentCooldown = Math.max(0, otherAbility.currentCooldown - 250);
              }
            }
          }
          // Cinder Chain bookkeeping — stamp the Zyphara fire-spell hit so
          // killEnemy can read the splash source within the next ~0.8s.
          if (ability.effectType === 'fireball' || ability.effectType === 'eruption') {
            casterRef._lastFireSpellDmg = baseFinalDmg;
            casterRef._lastFireSpellTime = Date.now();
          }

          const baseTargets = ability.targetCount || 1;
          const targets = baseTargets + (talentStat === 'targets' ? Math.floor(talentBonus) : 0);

          if (targets > 1) {
            // Multi-target: hit multiple enemies — VFX by ability id
            // Wild Growth is a druid ground AoE centered on the hero — only hit enemies within 220px of Thorne,
            // and play the spoke/vine VFX once at Thorne's feet.
            const WILD_GROWTH_RADIUS = 220;
            const talentDmgPctMult = (ability.id === 'wild_growth' || ability.id === 'thornlash') && talentStat === 'damageMult'
              ? (1 + talentBonus)
              : 1;
            let enemyTargets: EnemySprite[];
            if (ability.id === 'wild_growth') {
              enemyTargets = this.enemySprites
                .filter((e) => e.enemyRef.currentHp > 0 && Phaser.Math.Distance.Between(hs.x, hs.y, e.x, e.y) <= WILD_GROWTH_RADIUS)
                .slice(0, targets);
              this.vfx.playWildGrowth(hs.x, hs.y, WILD_GROWTH_RADIUS);
            } else {
              enemyTargets = this.enemySprites
                .filter((e) => e.enemyRef.currentHp > 0)
                .slice(0, targets);
            }
            // Multishot: fire a visual fan of arrows from caster toward all targets at once
            if (ability.id === 'multishot' && enemyTargets.length > 0) {
              this.vfx.playMultishotFan(hs.x + 20, hs.y - 50, enemyTargets.map((e) => ({ x: e.x, y: e.y - 40 })));
            }
            // Multishot phase-2 damage (ranks 4-6): +15% per rank doubling
            const multishotDmgBonus = ability.id === 'multishot'
              ? getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'multishot', 'damageMult')
              : 0;
            for (const es of enemyTargets) {
              let perShotDmg = ability.id === 'multishot'
                ? Math.round((ability.damage + sp) * (1 + multishotDmgBonus) * bonusMult)
                : Math.round(baseFinalDmg * talentDmgPctMult);
              // Hero Armory — Sylvari Piercing Volley: Multishot ignores 50 %
              // of enemy armour. Approximated by adding back 50 % of the
              // defense-mitigated portion to the per-shot damage.
              if (ability.id === 'multishot'
                && this.hasArmoryPassive(hs.heroRef, 'passive_sylvari_piercing_volley')
                && perShotDmg > 0) {
                const defMit = 1 - Math.min(0.85, es.enemyRef.defense / (es.enemyRef.defense + 100));
                perShotDmg = Math.round(perShotDmg * (1 + 0.50 * (1 - defMit)));
              }
              const { dmg: finalDmg, wasCrit } = applySpellCrit(perShotDmg, es);
              es.enemyRef.currentHp -= finalDmg;
              const dmgColor = ability.id === 'wild_growth'
                ? (wasCrit ? '#fbbf24' : '#4ade80')
                : (wasCrit ? '#fbbf24' : '#5090c0');
              this.showDamageNumber(es.x, es.y - 20, `${finalDmg}`, dmgColor, wasCrit && ability.id === 'wild_growth' ? '22px' : '16px');
              this.flashSprite(es);
              if (ability.id === 'wild_growth') {
                // Per-target vines coil up around each rooted enemy — held for the 2 s
                // root debuff then fades. Cast-site ring + thorn ring still play globally.
                this.vfx.playRootBinding(es.x, es.y, 2000);
              } else if (ability.id === 'consecration') {
                this.vfx.playSunStrike(es.x, es.y);
              } else if (ability.id === 'multishot') {
                // Arrow VFX handled by playMultishotFan above; just apply the daze debuff
                const dazeDur = Math.min(ability.level * 200, 1000);
                applyDebuff(es.enemyRef, { type: 'slow', value: 0.9, duration: dazeDur, remaining: dazeDur });
              } else if (ability.id === 'shuriken_barrage') {
                this.vfx.playLightning(es.x, es.y);
              } else if (ability.id === 'wild_growth') {
                // Root for 2s — rooted enemies cannot move but can still attack (counts as a control).
                const rootDebuff = { type: 'root' as const, value: 1, duration: 2000, remaining: 2000 };
                applyDebuff(es.enemyRef, rootDebuff);
                this.maybeBrambleSpread(es, rootDebuff);
                this.showDamageNumber(es.x, es.y - 38, 'ROOTED', '#4ade80');
              }
              if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
            }
          } else if (ability.effectType === 'fireball') {
            // Fireball: immediate damage + burning DoT (+5 dmg per SP), talent adds DoT scale, can crit
            const dotDuration = ability.effectDuration || 4000;
            const baseDotFraction = (ability.dotFraction || 0.30) + (talentStat === 'dotFraction' ? talentBonus : 0);
            const { dmg: finalDmg, wasCrit } = applySpellCrit(baseFinalDmg, target);
            const dotDmg = Math.round(finalDmg * baseDotFraction);
            target.enemyRef.currentHp -= finalDmg;
            if (target.enemyRef.currentHp <= 0) { this.killEnemy(target, hs); continue; }
            applyDebuff(target.enemyRef, { type: 'dot', value: dotDmg, duration: dotDuration, remaining: dotDuration, sourceHeroId: hs.heroRef.id, sourceLabel: 'fireball' });
            this.showDamageNumber(target.x, target.y - 20, `${finalDmg}`, wasCrit ? '#fbbf24' : '#f97316', wasCrit ? '27px' : '16px');
            this.flashSprite(target);
            // Origin: right-hand side of sprite (~x+50), upper body (~y-70 from feet)
            this.vfx.playFireball(hs.x + 50, hs.y - 70, target.x, target.y - 40);
            this.applySpellSigils(hs, target, finalDmg);
          } else if (ability.effectType === 'eruption') {
            // Eruption: all-enemy fire AoE + burning DoT, can crit — boss resistance applied per-target
            const dotDuration = ability.effectDuration || 3000;
            const baseDotFraction = (ability.dotFraction || 0.20) + (talentStat === 'dotFraction' ? talentBonus : 0);
            const { wasCrit } = applySpellCrit(baseFinalDmg);
            const eruptionTargets = this.enemySprites.filter((e) => e.enemyRef.currentHp > 0);
            // AoE crowd falloff — 1-5 enemies: 100%, 6-9: 80%, 10+: 50%.
            // Eruption uniquely had no target cap and a single crit roll for the
            // whole field, so crit casts were screen-wipe buttons in dense waves.
            // Falloff preserves the "hits everyone" feel without unbounded damage.
            const eruptionFalloff = eruptionTargets.length >= 10 ? 0.50
              : eruptionTargets.length >= 6 ? 0.80
              : 1.0;
            const eruptionScaledBase = Math.max(1, Math.round(baseFinalDmg * eruptionFalloff));
            for (const es of eruptionTargets) {
              // Re-apply per-target so bosses get their resistance
              const resisted = es.enemyRef.tier === 'boss' ? Math.max(1, Math.floor(eruptionScaledBase * 0.80)) : eruptionScaledBase;
              const finalDmg = wasCrit ? Math.round(resisted * spellCritMult) : resisted;
              const dotDmg = Math.round(finalDmg * baseDotFraction);
              es.enemyRef.currentHp -= finalDmg;
              this.showDamageNumber(es.x, es.y - 20, `${finalDmg}`, wasCrit ? '#fbbf24' : '#f97316', wasCrit ? '27px' : '16px');
              this.flashSprite(es);
              this.vfx.playEruption(es.x, es.y);
              if (es.enemyRef.currentHp <= 0) { this.killEnemy(es, hs); continue; }
              applyDebuff(es.enemyRef, { type: 'dot', value: dotDmg, duration: dotDuration, remaining: dotDuration, sourceHeroId: hs.heroRef.id, sourceLabel: 'eruption' });
            }

          // ── Necromancer: Death Coil ───────────────────────────────────────
          } else if (ability.effectType === 'death_coil') {
            const { dmg: finalDmg, wasCrit } = applySpellCrit(baseFinalDmg, target);
            target.enemyRef.currentHp -= finalDmg;
            // Heal Morveth for 30% of damage dealt
            const healAmt = Math.round(finalDmg * 0.30);
            const coilStats = this.effStats(hs.heroRef);
            hs.heroRef.stats.hp = Math.min(coilStats.maxHp, hs.heroRef.stats.hp + healAmt);
            this.showDamageNumber(target.x, target.y - 25, `${finalDmg}`, wasCrit ? '#fbbf24' : '#a855f7', wasCrit ? '27px' : '16px');
            this.showDamageNumber(hs.x, hs.y - 30, `+${healAmt}`, '#c084fc', '11px');
            if (this.anims.exists('hero_necromancer_attack')) {
              hs.play('hero_necromancer_attack');
              hs.once('animationcomplete', () => {
                if (!hs.heroRef.isDead && this.anims.exists('hero_necromancer_idle')) hs.play('hero_necromancer_idle');
              });
            }
            this.vfx.playDeathCoilBall(hs.x, hs.y - 40, target.x, target.y - 75);
            this.flashSprite(target);
            this.applySpellSigils(hs, target, finalDmg);
            if (target.enemyRef.currentHp <= 0) {
              this.killEnemy(target, hs);
            } else {
              // Baseline plague — Morveth's wave-clear identity. Target gets a
              // direct DoT and a 1.5s slow; the rot spreads to enemies BEHIND
              // the target at 40% damage. Rank-6 talent (`plagueUnlock`)
              // upgrades the spread to a 200px radius around target, hitting
              // enemies on both sides instead of just behind. Was previously
              // gated entirely behind rank 6 — moving the splash to baseline
              // gives him an actual answer to packed waves before the talent.
              const coilRank = (hs.heroRef.talentBonuses ?? {})['death_coil'] ?? 0;
              const dotDur = ability.effectDuration || 5000;
              const dotDmgBase = Math.round(finalDmg * (ability.dotFraction || 0.25));
              applyDebuff(target.enemyRef, { type: 'dot', value: dotDmgBase, duration: dotDur, remaining: dotDur });
              applyDebuff(target.enemyRef, { type: 'slow', value: 0.30, duration: 1500, remaining: 1500 });
              this.vfx.playPlagueSpread(target.x, target.y - 30);
              const radiusUnlock = coilRank >= 6;
              const spreadDmgFrac = radiusUnlock ? 0.60 : 0.40;
              const spreadDmg = Math.round(dotDmgBase * spreadDmgFrac);
              const spreadCandidates = this.enemySprites.filter((e) =>
                e.enemyRef.currentHp > 0 && e !== target
                && (radiusUnlock
                  ? Phaser.Math.Distance.Between(target.x, target.y, e.x, e.y) <= 200
                  : e.x > target.x));
              for (const es of spreadCandidates) {
                applyDebuff(es.enemyRef, { type: 'dot', value: spreadDmg, duration: dotDur, remaining: dotDur });
                this.showDamageNumber(es.x, es.y - 20, 'PLAGUE', '#a855f7', '10px');
                this.vfx.playPlagueSpread(es.x, es.y - 30);
              }
            }

          // ── Necromancer: Raise Dead ───────────────────────────────────────
          } else if (ability.effectType === 'raise_dead') {
            const corpse = (hs.heroRef as any)._lastCorpse as { defId: string; texKey: string; displaySize: number; x: number; y: number; atk: number; hp: number; atkSpeed?: number; atkSpeedMult?: number } | undefined;
            // No corpse on the field → refund CD + mana so Morveth doesn't
            // burn the spell on an empty cast (was silently consuming both
            // every CD tick when nothing had died yet).
            if (!corpse) {
              ability.currentCooldown = 0;
              hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + effectiveCost);
              continue;
            }
            // No soul cost — corpse alone gates the cast. Souls are still
            // tracked for Undying Legion / Bone Shield / Lich Form scaling.
            const souls = (hs.heroRef as any)._soulCount ?? 0;
            if (this.anims.exists('hero_necromancer_attack')) hs.play('hero_necromancer_attack');
            if (corpse) {
              const minionTalent = getTalentBonusValue(hs.heroRef.talentBonuses ?? {}, 'raise_dead');
              // Cap the talent multiplier so rank 10 doesn't push minions into absurd
              // late-game scaling (1.5× max, down from uncapped 2×+).
              const minionMult = Math.min(1.5, 1 + minionTalent);
              // Undying Legion passive: +5 % HP/ATK per soul Morveth holds at
              // raise time. Hard cap at 20 souls = +100 % so stacked sessions
              // don't go unbounded.
              const undyingLegion = this.hasArmoryPassive(hs.heroRef, 'passive_morveth_undying_legion')
                ? 1 + 0.05 * Math.min(20, souls)
                : 1;
              // Stats rebalance per feedback ("too overpowered"):
              //   HP   1.00 → 0.50 of corpse HP (still scales, still tanky)
              //   ATK  0.70 → 0.40 of corpse ATK
              //   Duration Infinity → 45 s so summons don't pile up across waves
              const minionHp   = Math.max(100, Math.round(corpse.hp * 0.50 * minionMult * undyingLegion));
              const minionAtk  = Math.max(8,   Math.round(corpse.atk * 0.40 * minionMult * undyingLegion));
              const minionAtkInterval = Math.round(getAttackInterval(corpse.atkSpeed ?? 1.5) / (corpse.atkSpeedMult ?? 1));
              // Use resolved sprite key stored at kill time — fallback to skeleton
              const minionTex = (corpse.texKey && this.textures.exists(corpse.texKey)) ? corpse.texKey : 'enemy_skeleton';
              // animPrefix = full texture key — animations are registered as `<texKey>_idle/_walk/_attack`
              // regardless of whether the source was enemy_* or boss_*. Stripping the prefix and
              // re-adding "enemy_" would break boss-derived minions.
              const animPrefix = minionTex;
              const mSprite = this.add.sprite(corpse.x, corpse.y, minionTex)
                .setDisplaySize(corpse.displaySize ?? 120, corpse.displaySize ?? 120)
                .setOrigin(0.5, 1.0).setDepth(2 + corpse.y * 0.001);
              // Face right (toward enemies) — sprites are naturally right-facing
              mSprite.setFlipX(false);
              // Dark necrotic tint + slightly transparent for ghostly look
              mSprite.setTint(0x1a0030);
              mSprite.setAlpha(0.88);
              const idleKey = `${animPrefix}_idle`;
              if (this.anims.exists(idleKey)) mSprite.play(idleKey);
              // HP bar (3× smaller than hero bar: 15×3 px, violet)
              const mBarY = corpse.y - (corpse.displaySize ?? 120) - 4;
              const mHpBarBg = this.add.rectangle(corpse.x, mBarY, 15, 3, 0x1a0030).setDepth(4).setOrigin(0.5, 0.5);
              const mHpBar  = this.add.rectangle(corpse.x - 7.5, mBarY, 15, 3, 0xa855f7).setDepth(5).setOrigin(0, 0.5);
              // Cursed overlay — looping FX sprite that follows the minion, sized to match it
              const cursedOverlay = this.add.sprite(corpse.x, corpse.y, 'effect_cursed')
                .setDisplaySize(corpse.displaySize ?? 120, corpse.displaySize ?? 120)
                .setOrigin(0.5, 1.0).setDepth(3).setAlpha(0.75);
              if (this.anims.exists('fx_cursed')) cursedOverlay.play('fx_cursed');
              // 45s lifetime (was Infinity) — stops minions from stacking up across waves
              // and matches the "summon" feel better than a permanent pet.
              this.minionSprites.push({ sprite: mSprite, cursedOverlay, hpBarBg: mHpBarBg, hpBar: mHpBar, hp: minionHp, maxHp: minionHp, atk: minionAtk, expiry: this.time.now + 45_000, ownerId: hs.heroRef.id, atkTimer: 0, atkInterval: minionAtkInterval, animPrefix, smokeTimer: 0 });
              (hs.heroRef as any)._lastCorpse = undefined;
              this.showDamageNumber(corpse.x, corpse.y - 30, '☠ RISEN', '#a855f7');
              this.vfx.playMagicBolt(hs.x, hs.y - 40, corpse.x, corpse.y - 30, 0x7c3aed);
              // Summoning smoke burst
              this.vfx.playNecroSmoke(corpse.x, corpse.y - 20);
              this.vfx.playNecroSmoke(corpse.x, corpse.y - 10);
            } else {
              this.showDamageNumber(hs.x, hs.y - 30, 'NO CORPSE', '#888888', '11px');
            }

          // ── Necromancer: Bone Shield ──────────────────────────────────────
          } else if (ability.effectType === 'bone_shield') {
            // Cannot re-cast while shield is already active
            if (((hs.heroRef as any)._shieldHp ?? 0) > 0) break;
            if (this.anims.exists('hero_necromancer_attack')) hs.play('hero_necromancer_attack');
            const soulCount = (hs.heroRef as any)._soulCount ?? 0;
            // Shield HP scales via the phase-2 talent stat (ranks 2+). Phase-1 stat (rank 1) unlocks knockback.
            const shieldRankBonus  = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'bone_shield', 'shieldMult');
            // `knockbackUnlock` was rank-1's old gate. Knockback is now baseline
            // (Morveth's only peel), so the rank-1 stat now arms the bone wave
            // with damage instead. Stat name kept the same to avoid migrating
            // saved talent points; only the effect changes.
            const hasBoneWaveDmg   = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'bone_shield', 'knockbackUnlock') > 0;
            const shieldMult = 1 + shieldRankBonus;
            const soulsConsumed = soulCount;
            // Level 1 (SP=28, maxHp=130): 0 souls→58, 10 souls→78, 40 souls→138.
            // Buff pass — base 20→30, soul scale 1.5→2 so the shield holds
            // through more of a fight at mid-stack soul counts.
            const baseShield = Math.round(30 + baseSp * 1.0 + soulsConsumed * 2);
            // Bone Grip passive: +25 % shield absorb. Break-explosion is wired
            // into the applyDamageToHero path via the `_boneGripArmed` flag.
            const boneGripBoost = this.hasArmoryPassive(hs.heroRef, 'passive_morveth_bone_grip') ? 1.25 : 1.0;
            const shieldHp = Math.round(baseShield * shieldMult * boneGripBoost);
            // Shield is its own HP pool — store current and max independently of hero maxHp
            (hs.heroRef as any)._shieldHp = shieldHp;
            (hs.heroRef as any)._shieldMaxHp = shieldHp;
            (hs.heroRef as any)._boneGripArmed = this.hasArmoryPassive(hs.heroRef, 'passive_morveth_bone_grip');
            // Souls are consumed on cast
            (hs.heroRef as any)._soulCount = 0;
            (hs.heroRef as any)._boneShieldVfxUntil = this.time.now + 2000;
            this.showDamageNumber(hs.x, hs.y - 30, `🦴 SHIELD +${shieldHp}`, '#c084fc');
            this.vfx.playBoneShield(hs.x, hs.y);
            this.updateHeroAura(hs, 'shield', 'effect_aura_shield', 'fx_aura_shield', true, 96, -40);
            // Bone wave knockback — fires every cast (was rank-1-talent gated;
            // moved to baseline so Morveth always has peel). Rank-1 talent
            // (`hasBoneWaveDmg`) now arms the wave with damage on top of the
            // shove. Bosses take a half-strength shove (they can't be stunned
            // either — keeps their presence threatening without being cheap).
            {
              const KNOCK_RANGE = 180;
              const KNOCK_DIST  = 110;
              const BOSS_DIST   = 55;
              const pushed: EnemySprite[] = [];
              for (const es of this.enemySprites) {
                if (!es.active || es.enemyRef.currentHp <= 0) continue;
                if (Math.abs(es.x - hs.x) > KNOCK_RANGE) continue;
                pushed.push(es);
                const dir = Math.sign(es.x - hs.x) || 1; // ties push right so formation stays natural
                const dist = es.enemyRef.tier === 'boss' ? BOSS_DIST : KNOCK_DIST;
                // Clamp knockback target to the same on-screen envelope as regular
                // movement — without this, a right-side push at the screen edge could
                // tween a boss half-way off the map.
                const targetX = Phaser.Math.Clamp(es.x + dir * dist, es.minLeftX, es.maxRightX);
                this.tweens.add({ targets: es, x: targetX, duration: 260, ease: 'Cubic.easeOut' });
                if (hasBoneWaveDmg) {
                  const waveDmg = Math.floor(40 + 1.0 * baseSp);
                  const { dmg: bwFinal, wasCrit } = applySpellCrit(waveDmg, es);
                  es.enemyRef.currentHp -= bwFinal;
                  this.showDamageNumber(es.x, es.y - 20, `${bwFinal}`, wasCrit ? '#fbbf24' : '#c084fc', wasCrit ? '20px' : '13px');
                  this.flashSprite(es);
                  if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
                }
              }
              if (pushed.length > 0) {
                const label = hasBoneWaveDmg ? `🦴 BONE WAVE ${pushed.length}` : `🦴 KNOCKBACK ${pushed.length}`;
                this.showDamageNumber(hs.x, hs.y - 50, label, '#c084fc', '11px');
              }
            }

          // ── Necromancer: Lich Form (unlocked at soul_leech rank 7) ────────
          } else if (ability.effectType === 'lich_form') {
            const souls = (hs.heroRef as any)._soulCount ?? 0;
            if (souls < 30) {
              // Refund cooldown — not enough souls
              ability.currentCooldown = 0;
              this.showDamageNumber(hs.x, hs.y - 30, `☠ ${souls}/30 souls`, '#888888', '11px');
              break;
            }
            // Consume 30 souls
            (hs.heroRef as any)._soulCount = souls - 30;
            const lichDuration = 3000;
            (hs.heroRef as any)._lichFormActive = true;
            (hs.heroRef as any)._lichFormTimer = this.time.delayedCall(lichDuration, () => {
              (hs.heroRef as any)._lichFormActive = false;
              hs.clearTint();
            });
            hs.setTint(0x1a0030);
            this.showDamageNumber(hs.x, hs.y - 40, '☠ LICH FORM', '#a855f7', '16px');
            this.vfx.playLichForm(hs.x, hs.y);

          // ── Shaman (Zhorak): Lightning / Healing / Earthbind totems + Ancestral Guidance ──
          // Totems spawn at the midpoint between Zhorak and his nearest enemy target, run on
          // a tick loop, and despawn after `effectDuration` ms. Only one totem of each school
          // can exist per shaman at a time — recasting destroys the prior totem first. Active
          // totems are tracked on `hs.heroRef._activeTotems` (keyed by school), each entry
          // holds {container, tickTimer, expiryTimer, baseTickInterval, agBoost, ...} so
          // Ancestral Guidance can reach in and double tick rate + boost potency in-flight.
          } else if (ability.effectType === 'lightning_totem'
                  || ability.effectType === 'healing_totem'
                  || ability.effectType === 'slowing_totem'
                  || ability.effectType === 'shaman_totems') {
            // shaman_totems: rotates through schools, skipping any already-standing totem.
            // Up to three totems can be on the board at once. If all three are up, the cast
            // refunds cooldown so the player doesn't burn a 7 s CD with no effect.
            let school: 'lightning' | 'healing' | 'slowing';
            if (ability.effectType === 'shaman_totems') {
              const cycleMap = (hs.heroRef as any)._activeTotems;
              const existing = cycleMap instanceof Map ? cycleMap : new Map();
              const order: Array<'lightning' | 'healing' | 'slowing'> = ['lightning', 'healing', 'slowing'];
              const startIdx = ((hs.heroRef as any)._totemCycleIdx ?? 0) % 3;
              let chosen: 'lightning' | 'healing' | 'slowing' | null = null;
              for (let i = 0; i < 3; i++) {
                const candidate = order[(startIdx + i) % 3];
                if (!existing.get(candidate)) { chosen = candidate; break; }
              }
              if (!chosen) {
                ability.currentCooldown = 0;
                this.showDamageNumber(hs.x, hs.y - 30, 'ALL TOTEMS UP', '#888888', '11px');
                break;
              }
              school = chosen;
              (hs.heroRef as any)._totemCycleIdx = ((order.indexOf(chosen) + 1) % 3);
            } else {
              school = ability.effectType === 'lightning_totem' ? 'lightning'
                    : ability.effectType === 'healing_totem'   ? 'healing'
                    : 'slowing';
            }
            // Per-school smart placement — NEVER refuse the cast just because no enemy exists:
            //  - Healing: spawn adjacent to the most-wounded living ally (or Zhorak himself
            //    if nobody's hurt), so heals actually reach their target.
            //  - Lightning / Earthbind: anchor toward the nearest living enemy if there is
            //    one, otherwise drop slightly in front of Zhorak.
            const anchorEnemy = this.enemySprites
              .filter((e) => e.active && e.enemyRef.currentHp > 0)
              .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))[0];
            let totemX: number;
            let totemY: number;
            if (school === 'healing') {
              const alive = this.heroSprites.filter((h) => !h.heroRef.isDead);
              const wounded = alive.sort((a, b) =>
                (a.heroRef.stats.hp / this.effStats(a.heroRef).maxHp)
              - (b.heroRef.stats.hp / this.effStats(b.heroRef).maxHp))[0];
              const anchor = wounded ?? hs;
              totemX = Phaser.Math.Clamp(anchor.x + 28, 40, GAME_W - 40);
              totemY = Phaser.Math.Clamp(anchor.y, 140, GAME_H - 40);
            } else if (anchorEnemy) {
              totemX = Phaser.Math.Clamp((hs.x + anchorEnemy.x) / 2, 40, GAME_W - 40);
              totemY = Phaser.Math.Clamp((hs.y + anchorEnemy.y) / 2, 140, GAME_H - 40);
            } else {
              totemX = Phaser.Math.Clamp(hs.x + 90, 40, GAME_W - 40);
              totemY = Phaser.Math.Clamp(hs.y, 140, GAME_H - 40);
            }
            // Despawn any prior totem of this school so only one stands at a time.
            // Defensive: if _activeTotems somehow isn't a Map (e.g. save-load round-trip
            // produced a plain {}), reset it so .get/.set don't blow up.
            const maybeMap = (hs.heroRef as any)._activeTotems;
            const activeTotems: Map<string, any> = maybeMap instanceof Map ? maybeMap : new Map();
            const prior = activeTotems.get(school);
            if (prior) {
              prior.tickTimer?.destroy();
              prior.expiryTimer?.destroy();
              prior.container?.destroy();
              activeTotems.delete(school);
            }
            // NOTE: shaman_totems allows all 3 schools to coexist — previous version
            // destroyed other-school totems on cast, which made the rotation feel like
            // "one totem at a time" instead of the intended "three-totem board".
            (hs.heroRef as any)._activeTotems = activeTotems;
            // Visual: pixellab-generated totem sprite with per-school tint. Container so
            // aura/shadow can tween and we can destroy the whole thing on despawn.
            const schoolColor = school === 'lightning' ? 0x38bdf8
                              : school === 'healing'   ? 0x10b981
                              : 0xa16207;
            const totem = this.add.container(totemX, totemY).setDepth(1.8 + totemY * 0.001);
            // Shadow sits tight to the feet of the (shorter) totem — was too low before.
            const groundShadow = this.add.ellipse(0, -2, 22, 5, 0x000000, 0.55);
            totem.add(groundShadow);
            // Half the previous display size (64 -> 32) per feedback — also drops it well
            // below hero silhouette height so the totem reads as a world prop.
            const totemSprite = this.add.sprite(0, 0, 'totem_shaman')
              .setOrigin(0.5, 1.0).setDisplaySize(32, 32).setTint(schoolColor);
            if (this.anims.exists('totem_shaman_idle')) totemSprite.play('totem_shaman_idle');
            totem.add(totemSprite);
            // NOTE: removed the colored crystal ellipse that used to float above the totem's
            // top — per feedback, the pixellab sprite already carries the school identity
            // via tint, and the extra circle read as a UI-y marker. Ancestral Guidance now
            // spawns its own smoky aura instead of brightening a glow ref.
            const glow: Phaser.GameObjects.Ellipse | null = null;
            // Spawn pulse — per-school VFX so the totem's placement reads even before the first tick.
            const pulseSchool = school === 'lightning' ? 'lightning' : school === 'healing' ? 'healing' : 'slowing';
            this.vfx.playTotemPulse(totemX, totemY, pulseSchool);

            // Per-school tick config — baseTickInterval, baseDuration, and the per-tick effect closure.
            const baseDuration = ability.effectDuration || 10000;
            let baseTickInterval: number;
            const heroRef = hs.heroRef;
            const talentBonuses = heroRef.talentBonuses ?? {};

            // Unified shaman_totems talent: +10 % potency per rank (linear) across all
            // three schools. Rank 7 unlocks ALL extras (chain lightning, resurrect,
            // attack-speed slow). Read once here so each tick fn closes over the values.
            const totemsPotency = getTalentBonusForStat(talentBonuses, 'shaman_totems', 'totemPotency');
            const extrasUnlocked = (talentBonuses['shaman_totems'] ?? 0) >= 7;
            let tickFn: () => void;
            if (school === 'lightning') {
              baseTickInterval = 1200;
              tickFn = () => {
                const tot = activeTotems.get('lightning'); if (!tot) return;
                const boost = tot.agBoost ? (1.5 + tot.agBonus) : 1.0;
                const baseDmg = (ability.damage + baseSp * 0.5) * (1 + totemsPotency) * boost;
                const primary = this.enemySprites
                  .filter((e) => e.active && e.enemyRef.currentHp > 0)
                  .sort((a, b) => Phaser.Math.Distance.Between(totem.x, totem.y, a.x, a.y) - Phaser.Math.Distance.Between(totem.x, totem.y, b.x, b.y))[0];
                if (!primary) return;
                const dmg = Math.round(baseDmg);
                primary.enemyRef.currentHp -= dmg;
                this.showDamageNumber(primary.x, primary.y - 20, `${dmg}`, '#38bdf8', '14px');
                this.flashSprite(primary);
                this.vfx.playTotemStrike(primary.x, primary.y);
                if (primary.enemyRef.currentHp <= 0) this.killEnemy(primary, hs);
                // Rank 7 extras: Chain Lightning — arc to 4 extra enemies at 50 % damage,
                // same VFX as the primary strike per user feedback.
                if (extrasUnlocked) {
                  const arcs = this.enemySprites
                    .filter((e) => e !== primary && e.active && e.enemyRef.currentHp > 0
                                 && Phaser.Math.Distance.Between(primary.x, primary.y, e.x, e.y) <= 220)
                    .sort((a, b) => Phaser.Math.Distance.Between(primary.x, primary.y, a.x, a.y)
                                  - Phaser.Math.Distance.Between(primary.x, primary.y, b.x, b.y))
                    .slice(0, 4);
                  for (const arc of arcs) {
                    const arcDmg = Math.round(dmg * 0.5);
                    arc.enemyRef.currentHp -= arcDmg;
                    this.showDamageNumber(arc.x, arc.y - 20, `${arcDmg}`, '#7dd3fc', '12px');
                    this.flashSprite(arc);
                    this.vfx.playTotemStrike(arc.x, arc.y);
                    if (arc.enemyRef.currentHp <= 0) this.killEnemy(arc, hs);
                  }
                }
              };
            } else if (school === 'healing') {
              baseTickInterval = 2000;
              // Rank 7 unlocks resurrection with an inner 120 s cooldown so the totem
              // can't spam revives — it consumes itself on each res, same as before.
              const canResurrect = extrasUnlocked;
              const REVIVE_CD_MS = 120_000;
              tickFn = () => {
                const tot = activeTotems.get('healing'); if (!tot) return;
                // Reaper's Pact — block totem revives too (the "no resurrection"
                // rule covers every path). Check per-tick so it reacts if the
                // boon is added/replaced mid-totem.
                const boonTotemRes = this.getBoonEffects().cannotResurrect;
                if (canResurrect && !boonTotemRes) {
                  const lastRes = (hs.heroRef as any)._healingTotemResLast ?? 0;
                  const cdReady = this.time.now - lastRes >= REVIVE_CD_MS;
                  if (cdReady) {
                    const deadHero = this.heroSprites.find((h) => h.heroRef.permanentlyDead || h.heroRef.isDead);
                    if (deadHero) {
                      const maxHp = this.effStats(deadHero.heroRef).maxHp;
                      deadHero.heroRef.isDead = false;
                      deadHero.heroRef.permanentlyDead = false;
                      deadHero.heroRef.stats.hp = Math.floor(maxHp * 0.30);
                      deadHero.heroRef.respawnTimer = 0;
                      deadHero.setAlpha(1);
                      deadHero.hpBar.setAlpha(1); deadHero.hpBarBg.setAlpha(1);
                      deadHero.mpBar.setAlpha(1); deadHero.xpBar.setAlpha(1); deadHero.xpBarBg.setAlpha(1);
                      deadHero.reviveBtn?.destroy();
                      deadHero.reviveBtn = undefined;
                      const idleKey = `hero_${deadHero.heroRef.heroKey ?? deadHero.heroRef.heroClass}_idle`;
                      if (this.anims.exists(idleKey)) deadHero.play(idleKey);
                      this.vfx.playResurrectPillar(deadHero.x, deadHero.y);
                      this.showResurrectAura(deadHero);
                      this.showDamageNumber(deadHero.x, deadHero.y - 30, '✨ TOTEM REVIVE!', '#86efac', '16px');
                      (hs.heroRef as any)._healingTotemResLast = this.time.now;
                      tot.tickTimer?.destroy();
                      tot.expiryTimer?.destroy();
                      tot.container?.destroy();
                      activeTotems.delete('healing');
                      return;
                    }
                  }
                }
                // Standard heal pulse — scales with potency. 250 px radius so placement
                // matters: allies out of range don't get healed. Most-wounded in-range
                // ally is picked each tick.
                const boost = tot.agBoost ? (1.5 + tot.agBonus) : 1.0;
                const healAmt = Math.round((ability.healAmount + baseSp * 0.5) * (1 + totemsPotency) * boost);
                const HEAL_RADIUS = 250;
                const targets = this.heroSprites
                  .filter((h) => !h.heroRef.isDead
                              && Phaser.Math.Distance.Between(totem.x, totem.y, h.x, h.y) <= HEAL_RADIUS)
                  .sort((a, b) => (a.heroRef.stats.hp / this.effStats(a.heroRef).maxHp)
                                - (b.heroRef.stats.hp / this.effStats(b.heroRef).maxHp))
                  .slice(0, 1);
                for (const t of targets) {
                  const maxHp = this.effStats(t.heroRef).maxHp;
                  const before = t.heroRef.stats.hp;
                  t.heroRef.stats.hp = Math.min(maxHp, before + healAmt);
                  const applied = t.heroRef.stats.hp - before;
                  // Match the formatting used by every other hero heal in the scene
                  // (Bless/Regrowth/etc.) — green `#22c55e`, default 12px font, y-30.
                  if (applied > 0) {
                    this.showDamageNumber(t.x, t.y - 30, `+${applied}`, '#22c55e');
                    this.vfx.playHealGlow(t.x, t.y);
                    this.vfx.playHealingTotemPulse(t.x, t.y);
                  }
                }
              };
            } else {
              // Earthbind totem — pulses movement slow within 180 px. Rank 7 also slows attack speed.
              baseTickInterval = 1000;
              const SLOW_RADIUS = 180;
              tickFn = () => {
                const tot = activeTotems.get('slowing'); if (!tot) return;
                const boost = tot.agBoost ? (1.5 + tot.agBonus) : 1.0;
                // Movement slow scales linearly with totem potency: base 35 % × (1 + rank × 10 %).
                const slowPct = Math.min(0.75, 0.35 * (1 + totemsPotency) * boost);
                const slowDuration = 1400;
                const hit: EnemySprite[] = [];
                for (const e of this.enemySprites) {
                  if (!e.active || e.enemyRef.currentHp <= 0) continue;
                  if (Phaser.Math.Distance.Between(totem.x, totem.y, e.x, e.y) > SLOW_RADIUS) continue;
                  applyDebuff(e.enemyRef, { type: 'slow', value: slowPct, duration: slowDuration, remaining: slowDuration });
                  // Rank 7 extra: attack-speed slow (−25 %). Read at enemy-attack-interval
                  // compute site via the `_atkSpdSlowUntil` flag we refresh each tick.
                  if (extrasUnlocked) {
                    (e.enemyRef as any)._atkSpdSlowUntil = this.time.now + slowDuration;
                  }
                  hit.push(e);
                }
                if (hit.length > 0) {
                  this.vfx.playTotemPulse(totem.x, totem.y, 'slowing');
                  for (const e of hit) this.vfx.playEarthbindSlow(e.x, e.y);
                }
              };
            }

            // Register the totem, start the tick loop, and arm the expiry timer.
            const totemState: any = {
              school,
              container: totem,
              glow,
              sprite: totemSprite,
              schoolColor,
              baseTickInterval,
              baseDuration,
              agBoost: false,
              agBonus: 0,
              tickFn,
              tickTimer: null,
              expiryTimer: null,
            };
            totemState.tickTimer = this.time.addEvent({
              delay: baseTickInterval,
              loop: true,
              callback: tickFn,
            });
            totemState.expiryTimer = this.time.delayedCall(baseDuration, () => {
              totemState.tickTimer?.destroy();
              totemState.container?.destroy();
              activeTotems.delete(school);
            });
            activeTotems.set(school, totemState);
            // Visual feedback
            const label = school === 'lightning' ? '⚡ LIGHTNING TOTEM'
                        : school === 'healing'   ? '💚 HEALING TOTEM'
                        : '🌿 EARTHBIND TOTEM';
            this.showDamageNumber(totemX, totemY - 55, label, schoolColor === 0x38bdf8 ? '#38bdf8' : schoolColor === 0x10b981 ? '#10b981' : '#a16207', '12px');

          // ── Shaman: Ancestral Guidance — empowers all standing totems ──
          } else if (ability.effectType === 'ancestral_guidance') {
            const agMaybeMap = (hs.heroRef as any)._activeTotems;
            const activeTotems: Map<string, any> = agMaybeMap instanceof Map ? agMaybeMap : new Map();
            if (activeTotems.size === 0) {
              // No totems to empower — refund CD so player doesn't burn a 50s cd on an empty field.
              ability.currentCooldown = 0;
              this.showDamageNumber(hs.x, hs.y - 30, 'NO TOTEMS', '#888888', '11px');
              break;
            }
            // Talent: phase-1 ancestralBonus (doubling) scales potency; phase-2 adds duration.
            const potBonus = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'ancestral_guidance', 'ancestralBonus');
            const durBonusMs = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'ancestral_guidance', 'ancestralDuration');
            const agDuration = (ability.effectDuration || 8000) + durBonusMs;
            for (const [, tot] of activeTotems) {
              tot.agBoost = true;
              tot.agBonus = potBonus;
              // Retick at double speed during AG
              tot.tickTimer?.destroy();
              tot.tickTimer = this.time.addEvent({
                delay: Math.max(150, Math.round(tot.baseTickInterval / 2)),
                loop: true,
                callback: tot.tickFn,
              });
              // Refresh totem lifespan to full (cancel old expiry + arm a new one)
              tot.expiryTimer?.destroy();
              tot.expiryTimer = this.time.delayedCall(tot.baseDuration, () => {
                tot.tickTimer?.destroy();
                tot.container?.destroy();
                activeTotems.delete(tot.school);
              });
              // Real smoke emitter — spawns rising orange/red puffs every ~180 ms from
              // near the base of the totem, drifting up and fading out. Replaces the old
              // concentric-circle aura which read as a bullseye target, not smoke/fog.
              if (tot.container) {
                const emitTimer = this.time.addEvent({
                  delay: 180,
                  loop: true,
                  callback: () => {
                    if (!tot.container?.active) return;
                    const tx = tot.container.x;
                    const ty = tot.container.y;
                    const jitter = 18;
                    for (let k = 0; k < 2; k++) {
                      const puff = this.add.graphics().setDepth(tot.container.depth - 0.05);
                      const rgb = k === 0
                        ? [0xdc2626, 0xf97316]
                        : [0xf97316, 0xfbbf24];
                      puff.fillStyle(rgb[0], 0.45);
                      puff.fillCircle(0, 0, 10);
                      puff.fillStyle(rgb[1], 0.3);
                      puff.fillCircle(0, 0, 14);
                      const sx = tx + (Math.random() - 0.5) * jitter;
                      const sy = ty - 2 + (Math.random() - 0.5) * 4;
                      puff.setPosition(sx, sy).setScale(0.6).setAlpha(0.9);
                      this.tweens.add({
                        targets: puff,
                        y: sy - 42 - Math.random() * 20,
                        x: sx + (Math.random() - 0.5) * 16,
                        scale: 1.9,
                        alpha: 0,
                        duration: 900 + Math.random() * 300,
                        ease: 'Sine.easeOut',
                        onComplete: () => puff.destroy(),
                      });
                    }
                  },
                });
                tot.agSmokeTimer = emitTimer;
              }
            }
            this.showDamageNumber(hs.x, hs.y - 40, '✨ ANCESTRAL GUIDANCE', '#fde68a', '15px');
            // Cast-burst on Zhorak only — the per-totem empower uses the sprite glow
            // above, not an outward ring (which read as "too round" visually).
            this.vfx.playAncestralGuidance(hs.x, hs.y);
            // After agDuration ms, revert the tick rates back to base speed and restore crystal glow.
            this.time.delayedCall(agDuration, () => {
              for (const [, tot] of activeTotems) {
                if (!tot.agBoost) continue;
                tot.agBoost = false;
                tot.agBonus = 0;
                tot.tickTimer?.destroy();
                tot.tickTimer = this.time.addEvent({
                  delay: tot.baseTickInterval,
                  loop: true,
                  callback: tot.tickFn,
                });
                // AG expired: stop the smoke emitter. Already-spawned puffs finish
                // their individual fade tweens and auto-destroy — no sweep needed.
                if (tot.agSmokeTimer) { tot.agSmokeTimer.destroy(); tot.agSmokeTimer = null; }
              }
            });

          // ── Shaman: Shamanic Call — random party-wide on-hit buff for N seconds ──
          } else if (ability.effectType === 'shamanic_buff') {
            // Pick one of three buffs randomly. On-hit procs are read from
            // hs.heroRef._shamanicBuff in performHeroAttack (see post-attack hook).
            const roll = Math.random();
            const buffKind: 'extra_attack' | 'fire_bonus' | 'slow_on_hit' =
              roll < 0.34 ? 'extra_attack'
            : roll < 0.67 ? 'fire_bonus'
            : 'slow_on_hit';
            // Talent: +1 s per rank (perLevel: 1000 ms) added on top of the 3 s base.
            const durExtend = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'shamanic_buff', 'durationExtend');
            const dur = (ability.effectDuration || 3000) + durExtend;
            const label = buffKind === 'extra_attack' ? '⚔ EXTRA STRIKE'
                        : buffKind === 'fire_bonus'   ? '🔥 FLAME STRIKE'
                        : '🌀 CRIPPLE STRIKE';
            const color = buffKind === 'extra_attack' ? '#fbbf24'
                        : buffKind === 'fire_bonus'   ? '#f97316'
                        : '#93c5fd';
            // Apply to every living party member.
            for (const h of this.heroSprites) {
              if (h.heroRef.isDead) continue;
              (h.heroRef as any)._shamanicBuff = { kind: buffKind, expiry: this.time.now + dur };
              this.vfx.playShamanicBuff(h.x, h.y);
            }
            this.showDamageNumber(hs.x, hs.y - 40, label, color, '14px');
            // Register in the party-buff HUD row so players can see it running + remaining time.
            const buffName = buffKind === 'extra_attack' ? 'Extra Strike'
                           : buffKind === 'fire_bonus'   ? 'Flame Strike'
                           : 'Cripple Strike';
            const buffDesc = buffKind === 'extra_attack' ? '20 % chance on hit: strike again'
                           : buffKind === 'fire_bonus'   ? '20 % chance on hit: bonus fire damage'
                           : '10 % chance on hit: slow 50 % / 1 s';
            this.registerPartyBuff(`shamanic_call_${buffKind}`, buffName, 'spell_shamanic_call', buffDesc, dur);

          // ── Cyrith: Forge Vorrak — storm-forged sentinel. The 40 s
          //    cooldown only starts when Vorrak DIES (forced to 0 after a
          //    successful cast). While he's alive, recasts refund the
          //    mana cost silently — no spam text on any branch.
          //    `continue` (not fall-through) on the refund branches so
          //    the priority loop tries the NEXT ability this tick — without
          //    it Cyrith's Storm Bolt / Static Field / Arc Strike got
          //    starved every tick because Forge Vorrak ate the priority
          //    slot via `usedAbility = true; break` below.
          } else if (ability.effectType === 'summon_copper_golem') {
            const existingGolem = this.getSummonOf(hs.heroRef.id);
            if (existingGolem) {
              ability.currentCooldown = 0;
              hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + (ability.manaCost || 0));
              continue;
            }
            const golem = this.summonCopperGolem(hs);
            if (golem) {
              // CD only starts when Vorrak falls — see despawnSummon
              // for the on-death timer setup. After spawn, force CD to 0.
              ability.currentCooldown = 0;
            } else {
              // No empty slot — silent refund.
              ability.currentCooldown = 0;
              hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + (ability.manaCost || 0));
              continue;
            }

          // ── Cyrith: Storm Bolt — lightning bolt that chains + stuns.
          //    Baseline: nearest single target + 1 chain hop (60 % dmg, 150 px)
          //    + 0.6 s stun on primary. Rank-6 talent (`chainStorm`) adds a
          //    second chain target and extends stun to 1.2 s. ──
          } else if (ability.effectType === 'storm_bolt') {
            // Talent phase-1: damageMult (doubling, +15% per rank up to 5).
            // Phase-2 stat (`chainStorm`) reads independently for rank-6 unlock.
            const sbTalentMult = talentStat === 'damageMult' ? (1 + talentBonus) : 1;
            const stormBoltDmg = Math.floor((ability.damage + 1.4 * sp) * sbTalentMult);
            const { dmg: finalDmg, wasCrit } = applySpellCrit(stormBoltDmg, target);
            if (this.anims.exists('hero_sorcerer_storm_bolt')) {
              hs.off('animationcomplete');
              hs.play('hero_sorcerer_storm_bolt');
            }
            target.enemyRef.currentHp -= finalDmg;
            this.showDamageNumber(target.x, target.y - 20, `${finalDmg}`, wasCrit ? '#fff7b0' : '#80c8ff', wasCrit ? '24px' : '16px');
            this.flashSprite(target);
            this.vfx.playCyrithStormBolt(hs.x + 12, hs.y - 60, target.x, target.y - 20);
            // Stun primary target — bosses get a baseline stun like everyone
            // else (boss CC immunity isn't a project-wide rule; spells that
            // explicitly call out stuns apply them). Rank-6 doubles duration.
            const sbChainStorm = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'storm_bolt', 'chainStorm') > 0;
            const stunMs = sbChainStorm ? 1200 : 600;
            if (target.enemyRef.currentHp > 0) {
              applyDebuff(target.enemyRef, { type: 'stun', value: 1, duration: stunMs, remaining: stunMs });
            }
            if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
            // Chain — 1 hop baseline, 2 at rank 6. 60 % damage, no further chain.
            const sbChainCount = sbChainStorm ? 2 : 1;
            const sbChainCandidates = this.enemySprites
              .filter((e) => e !== target && e.active && e.enemyRef.currentHp > 0
                && Phaser.Math.Distance.Between(target.x, target.y, e.x, e.y) <= 150)
              .sort((a, b) => Phaser.Math.Distance.Between(target.x, target.y, a.x, a.y)
                            - Phaser.Math.Distance.Between(target.x, target.y, b.x, b.y))
              .slice(0, sbChainCount);
            for (const ct of sbChainCandidates) {
              const { dmg: chDmg, wasCrit: chCrit } = applySpellCrit(Math.floor(finalDmg * 0.60), ct);
              ct.enemyRef.currentHp -= chDmg;
              this.showDamageNumber(ct.x, ct.y - 20, `${chDmg}`, chCrit ? '#fff7b0' : '#a8d8ff', chCrit ? '18px' : '13px');
              this.flashSprite(ct);
              this.vfx.playCyrithArcChain(target.x, target.y - 20, ct.x, ct.y - 20);
              if (ct.enemyRef.currentHp <= 0) this.killEnemy(ct, hs);
            }

          // ── Cyrith: Static Field — small electric shield aura wrapped
          //    around the Copper Golem. REQUIRES a live golem. If no golem,
          //    silently refund cost + clear CD (no spam text per user).
          //    The shield itself is rotating dashed rings hugging the golem;
          //    enemies stepping inside its reach take per-second damage.
          } else if (ability.effectType === 'static_field') {
            const golemAnchor = this.getSummonOf(hs.heroRef.id);
            if (!golemAnchor) {
              // Silent refund — no text, no state change. Cast just doesn't fire.
              ability.currentCooldown = 0;
              hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + (ability.manaCost || 0));
            } else {
              // Greater Field — rank-4 talent (`greaterField`) widens the
              // radius and strengthens the slow. Baseline: radius 110, 25%
              // slow per tick (was a tight 60 px ring with no slow).
              const sfGreaterField = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'static_field', 'greaterField') > 0;
              const RADIUS = sfGreaterField ? 150 : 110;
              const SLOW_PCT = sfGreaterField ? 0.40 : 0.25;
              const SLOW_MS = 1500;
              const TICKS = 4;
              const TICK_MS = 1000;
              const TOTAL_MS = TICKS * TICK_MS;
              // Talent phase-1: per-tick damageMult (doubling, +25 % per rank up to 3).
              const sfTalentMult = talentStat === 'damageMult' ? (1 + talentBonus) : 1;
              const tickBase = Math.floor((20 + 0.6 * sp) * sfTalentMult);
              // Spawn the Cyrith shield aura — rotating dashed rings + soft
              // inner glow, hugged tight on the golem's body. Returns a
              // handle with follow() + destroy() that we drive each frame.
              const fieldShield = this.vfx.spawnCyrithFieldShield(golemAnchor.x, golemAnchor.y);
              // Frame-tick follower — keeps the aura glued to the golem.
              const followEvent = this.time.addEvent({
                delay: 16, loop: true,
                callback: () => {
                  const liveGolem = this.getSummonOf(hs.heroRef.id);
                  if (liveGolem && !liveGolem.isDead) {
                    fieldShield.follow(liveGolem.x, liveGolem.y);
                  }
                },
              });
              this.time.delayedCall(TOTAL_MS, () => {
                followEvent.remove();
                fieldShield.destroy();
              });
              for (let t = 0; t < TICKS; t++) {
                this.time.delayedCall(t * TICK_MS, () => {
                  const liveGolem = this.getSummonOf(hs.heroRef.id);
                  if (!liveGolem || liveGolem.isDead) return; // field dies with the anchor
                  const x = liveGolem.x;
                  const y = liveGolem.y - 22;                            // mid-body anchor (matches shield center)
                  for (const es of this.enemySprites) {
                    if (!es.active || es.enemyRef.currentHp <= 0) continue;
                    if (Phaser.Math.Distance.Between(x, y, es.x, es.y) > RADIUS) continue;
                    const { dmg: tickDmg, wasCrit } = applySpellCrit(tickBase, es);
                    es.enemyRef.currentHp -= tickDmg;
                    this.showDamageNumber(es.x, es.y - 25 - t * 4, `${tickDmg}`, wasCrit ? '#fff7b0' : '#80c8ff', wasCrit ? '18px' : '12px');
                    this.flashSprite(es);
                    // Cyrith arc chain — short jagged line from golem mid-
                    // body to the struck enemy. Distinct from generic
                    // lightning bolt, fits the shield aesthetic.
                    this.vfx.playCyrithArcChain(x, y, es.x, es.y - 20);
                    // Slow component — refreshes every tick the enemy is in
                    // the field, so a sustained presence keeps them sluggish.
                    if (es.enemyRef.currentHp > 0) {
                      applyDebuff(es.enemyRef, { type: 'slow', value: SLOW_PCT, duration: SLOW_MS, remaining: SLOW_MS });
                    }
                    if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
                  }
                });
              }
            }

          // ── Cyrith: Arc Strike — sword melee swing with lightning chain.
          //    Hybrid ATK + SP scaling (sword half + storm half). Cyrith
          //    visually dashes 1 hex toward the target then snaps back.
          //    Front-row / charge-grid slot ONLY (per user) — like Goomar's
          //    charge, the leap-strike doesn't trigger from back ranks.
          //    From any other slot the cast refunds cost + clears CD so
          //    moving Cyrith forward mid-fight retries on the next tick. ──
          } else if (ability.effectType === 'arc_strike') {
            const arcSlotIdx = hs.getData('slotIdx') as number | undefined;
            if (arcSlotIdx === undefined || !this.isFrontRow(arcSlotIdx)) {
              ability.currentCooldown = 0;
              hs.heroRef.stats.mp = Math.min(this.effStats(hs.heroRef).maxMp, hs.heroRef.stats.mp + (ability.manaCost || 0));
              continue;
            }
            const arcAtk = this.effStats(hs.heroRef).attack || 0;
            const arcDmg = Math.floor(ability.damage + 1.0 * arcAtk + 0.5 * sp);
            const { dmg: finalDmg, wasCrit } = applySpellCrit(arcDmg, target);
            target.enemyRef.currentHp -= finalDmg;
            this.showDamageNumber(target.x, target.y - 20, `${finalDmg}`, wasCrit ? '#fff7b0' : '#a8d8ff', wasCrit ? '24px' : '17px');
            this.flashSprite(target);
            // Brief dash forward + snap back so the swing reads as a melee
            // commitment, not a ranged tap.
            const baseX = hs.baseX ?? hs.x;
            const baseY = hs.baseY ?? hs.y;
            const destX = target.x - 30;
            const destY = target.y;
            this.tweens.killTweensOf(hs);
            this.tweens.add({
              targets: hs, x: destX, y: destY, duration: 130, ease: 'Quad.easeOut',
              onUpdate: () => { hs.shadow?.setPosition(hs.x, hs.y + hs.shadowYOffset); },
              onComplete: () => {
                // Play the FULL Area_Attack channel anim — 12 frames, ~860 ms
                // total. The user wants the whole cast pose to read on Arc
                // Strike (lightning erupts from the mage's outstretched arms).
                if (this.anims.exists('hero_sorcerer_area_attack')) {
                  hs.off('animationcomplete');
                  hs.play('hero_sorcerer_area_attack');
                } else if (this.anims.exists('hero_sorcerer_attack')) {
                  hs.off('animationcomplete');
                  hs.play('hero_sorcerer_attack');
                }
                // Snap-back delay tuned to clear the area-attack anim
                // duration (~860 ms at 14 fps × 12 frames) so the channel
                // doesn't get stomped by the return tween.
                this.tweens.add({
                  targets: hs, x: baseX, y: baseY, duration: 280, delay: 720, ease: 'Quad.easeIn',
                  onUpdate: () => { hs.shadow?.setPosition(hs.x, hs.y + hs.shadowYOffset); },
                });
              },
            });
            if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
            // Arc chain — strike nearby foe(s) within 160 px for 65 % damage
            // (was 120 px / 50 %). Talent (damageMult) scales the chain hit;
            // rank 4 unlocks a second chain target via the split `extraChain`
            // stat read explicitly via getTalentBonusForStat (the generic
            // talentBonus variable only carries phase-1 stat).
            const chainMult = 0.65 * (talentStat === 'damageMult' ? (1 + talentBonus) : 1);
            const extraChain = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, ability.id, 'extraChain');
            const chainCount = 1 + (extraChain > 0 ? 1 : 0);
            const chainCandidates = this.enemySprites
              .filter((e) => e !== target && e.active && e.enemyRef.currentHp > 0
                && Phaser.Math.Distance.Between(target.x, target.y, e.x, e.y) <= 160)
              .sort((a, b) => Phaser.Math.Distance.Between(target.x, target.y, a.x, a.y)
                            - Phaser.Math.Distance.Between(target.x, target.y, b.x, b.y))
              .slice(0, chainCount);
            for (const chainTarget of chainCandidates) {
              const chainDmg = Math.max(1, Math.floor(finalDmg * chainMult));
              chainTarget.enemyRef.currentHp -= chainDmg;
              this.showDamageNumber(chainTarget.x, chainTarget.y - 20, `${chainDmg}`, '#a8d8ff', '13px');
              this.flashSprite(chainTarget);
              // Cyrith-specific arc chain VFX — pale-blue jagged line
              // from primary target to chain target. Not the shared
              // lightning bolt that other heroes / bosses use.
              this.vfx.playCyrithArcChain(target.x, target.y - 20, chainTarget.x, chainTarget.y - 20);
              if (chainTarget.enemyRef.currentHp <= 0) this.killEnemy(chainTarget, hs);
            }

          // ── Shaman: Spirit Shield — alternates mana / lightning shield, 3-stack flat absorb ──
          } else if (ability.effectType === 'shaman_shield') {
            // Cannot cast a new shield while one is still up — refund CD so the player
            // can try again once stacks deplete or the expiry passes.
            const existing = (hs.heroRef as any)._shamanShield as { stacks: number; expiry: number } | undefined;
            if (existing && existing.stacks > 0 && this.time.now < existing.expiry) {
              ability.currentCooldown = 0;
              break;
            }
            // Alternate kind on each successful cast. Flat absorb scales with SP, and
            // talent (+15 % per rank, linear) boosts the per-stack absorb pool.
            const prevKind = (hs.heroRef as any)._shamanShieldKind;
            const nextKind: 'mana' | 'lightning' = prevKind === 'mana' ? 'lightning' : 'mana';
            const shieldAbsorbBonus = getTalentBonusForStat(hs.heroRef.talentBonuses ?? {}, 'shaman_shield', 'shieldAbsorb');
            const absorbPerStack = Math.round((60 + baseSp * 2) * (1 + shieldAbsorbBonus));
            (hs.heroRef as any)._shamanShield = {
              kind: nextKind,
              stacks: 3,
              perStack: absorbPerStack,
              expiry: this.time.now + (ability.effectDuration || 20000),
            };
            (hs.heroRef as any)._shamanShieldKind = nextKind;
            this.vfx.playSpiritShield(hs.x, hs.y, nextKind);

          } else {
            const { dmg: finalDmg, wasCrit } = applySpellCrit(baseFinalDmg, target);
            target.enemyRef.currentHp -= finalDmg;
            this.showDamageNumber(target.x, target.y - 20, `${finalDmg}`, wasCrit ? '#fbbf24' : '#5090c0', wasCrit ? '27px' : '16px');
            this.flashSprite(target);
          }
        }
        // Relic: Echo Catalyst — 25% chance to deal 50% SP arcane damage to nearest enemy
        if (rbsInner.hasEchoCatalyst && Math.random() < 0.25) {
          const echoTarget = this.enemySprites
            .filter((e) => e.active && e.enemyRef.currentHp > 0)
            .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))[0];
          if (echoTarget) {
            const echoDmg = Math.max(1, Math.floor(baseSp * 0.5));
            echoTarget.enemyRef.currentHp -= echoDmg;
            this.showDamageNumber(echoTarget.x + 10, echoTarget.y - 30, `${echoDmg} ✦`, '#4090c0', '16px');
            this.flashSprite(echoTarget);
            if (echoTarget.enemyRef.currentHp <= 0) this.killEnemy(echoTarget, hs);
          }
        }
        // Relic: Mana Flux Crystal — ability dealt +80% damage bonus (already consumed above)
        // Visual feedback when flux was active
        if (manaFluxActive) {
          this.showDamageNumber(hs.x, hs.y - 55, 'FLUX!', '#22c55e');
        }
        // Relic: Spellbinder's Ring — spell cast twice (implemented as 2× damage)
        if (spellbinderProc) {
          this.showDamageNumber(hs.x, hs.y - 70, '✦ DOUBLE CAST', '#a78bfa');
        }
        // DPS tracking: diff enemy HP to capture spell damage
        const _dpsHpAfter = this.enemySprites.reduce((s, e) => s + Math.max(0, e.enemyRef.currentHp), 0);
        this.trackDps(hs.heroRef.id, Math.max(0, _dpsHpBefore - _dpsHpAfter), ability.id);
        usedAbility = true;
        break;
      }
    }
    if (conduitSp > 0) hs.heroRef.stats.spellPower -= conduitSp;

    // Staff on-cast passives
    if (usedAbility) {
      for (const w of [hs.heroRef.equipment.weapon1, hs.heroRef.equipment.weapon2]) {
        if (!w?.specialEffect) continue;
        // Haste Surge: X% chance to give +30% attack speed for 4s
        const hasteMatch = w.specialEffect.match(/Haste Surge.*?(\d+)% chance/);
        if (hasteMatch && Math.random() * 100 < parseInt(hasteMatch[1])) {
          (hs.heroRef as any)._hasteSurgeBuff = true;
          this.time.delayedCall(4000, () => { (hs.heroRef as any)._hasteSurgeBuff = false; });
          this.showDamageNumber(hs.x, hs.y - 55, 'HASTE!', '#fbbf24');
        }
        // Spell Echo: proc flat bonus spell damage on nearest enemy
        const echoMatch = w.specialEffect.match(/Spell Echo.*\+(\d+) bonus spell damage/);
        if (echoMatch) {
          const echoDmg = parseInt(echoMatch[1]);
          const echoTgt = this.enemySprites.find(e => e.active && e.enemyRef.currentHp > 0);
          if (echoTgt) {
            echoTgt.enemyRef.currentHp -= echoDmg;
            this.showDamageNumber(echoTgt.x + 10, echoTgt.y - 30, `${echoDmg} ECHO`, '#a78bfa', '16px');
            if (echoTgt.enemyRef.currentHp <= 0) this.killEnemy(echoTgt, hs);
          }
        }
      }
    }

    // (CDR tick moved to updateHeroes — ticks by real delta, runs every frame for every
    // living hero so silenced / petrified / no-target heroes still drain CDs and atk-speed
    // buffs can't make CDs drain 6–10× faster than wall time.)

    if (!usedAbility) {
      const distToTarget = Math.abs(hs.x - target.x);
      const wasEnraged = (hs.heroRef as any)._enrageActive;
      // Apply tome + consumable bonuses before attack (injected temporarily)
      const dmgTomeBonus = Math.floor(getTomeBonus(this.gsm.player.tomes, 'damage'));
      const critTomeBonus = getTomeBonus(this.gsm.player.tomes, 'crit');
      const critDmgTomeBonus = getTomeBonus(this.gsm.player.tomes, 'critDamage');
      const spTomeBonus = Math.floor(
        getTomeBonus(this.gsm.player.tomes, 'spellpower')
        + getTomeBonus(this.gsm.player.tomes, 'book_archivist'),
      );
      // Consumable: Scroll of Power +25% damage
      const powerBuff = this.gsm.hasActiveConsumable('scroll_power') ? Math.floor(hs.heroRef.stats.attack * 0.25) : 0;
      // Consumable: Potion of Haste +30% crit
      const hasteCritBuff = this.gsm.hasActiveConsumable('potion_haste') ? 0.30 : 0;
      // Trophy: Elixir of Giants +25% attack damage — equipped hero only
      const giantsBuff = hs.heroRef.equipment.reserved?.specialEffect?.includes('base attack damage')
        ? Math.floor(hs.heroRef.stats.attack * 0.25) : 0;
      // Trophy: Goblin War Horn +12% crit — equipped hero only
      const warHornCrit = hs.heroRef.equipment.reserved?.specialEffect?.includes('crit chance') ? 0.12 : 0;
      // Trophy: Goblin Kingpin (Horde of Goblins event reward) — +10% crit damage
      // for ALL heroes when any deployed hero has the trophy equipped.
      const kingpinCritDmg = this.hasTrophy('crit damage and +2 swap') ? 0.10 : 0;
      // Trophy: per-hero faction bonus (e.g. Beastslayer Fang, Bone Collector)
      const factionMult = this.getTrophyFactionMult(hs.heroRef, target.enemyRef.faction);
      const factionBuff = factionMult > 1 ? Math.floor(hs.heroRef.stats.attack * (factionMult - 1)) : 0;
      // Relics: per-hero auto-attack bonuses
      const rb = this.getRelicBonuses(hs.heroRef);
      const warbladeBuff  = rb.attackPct > 0   ? Math.floor(hs.heroRef.stats.attack * rb.attackPct)   : 0;
      const orbSpBuff     = rb.spellPct > 0    ? Math.floor((hs.heroRef.stats.spellPower || 0) * rb.spellPct) : 0;
      // Archon's Focus: +1% SP per kill stack. Multiple copies accrue in parallel
      // (each relic tracks its own counter), but the aggregate SP boost is hard-
      // capped at +99% to keep the design intent of "max 99 stacks = +99% SP"
      // true even when the player stumbles onto 2-4 copies — otherwise 4 maxed
      // Archons would give +396% SP, trivialising every caster build.
      // Aggregate bonus for SP-scaling auto-attacks — uncapped, per-item rarity
      // ceiling handles the balance (see archonFocusMaxStacks).
      const archonPct = rb.archonFocusStacksTotal * 0.01;
      const archonSpBuff = archonPct > 0 ? Math.floor((hs.heroRef.stats.spellPower || 0) * archonPct) : 0;
      // Crescendo Idol: +1% damage per wave cleared, tallied per equipped item.
      // Multiple copies stack independently — sum runtimeStacks across all equipped idols.
      const crescendoBuff = rb.crescendoStacksTotal > 0
        ? Math.floor(hs.heroRef.stats.attack * 0.01 * rb.crescendoStacksTotal) : 0;
      // Vitality Idol: HP bonus is now persistent (see updateVitalityIdolHp); no transient add here.
      // Bloodlust temp buff (+20% attack)
      const bloodlustBuff = rb.hasBloodlust && (hs.heroRef as any)._bloodlustActive
        ? Math.floor(hs.heroRef.stats.attack * 0.20) : 0;
      // Druid Bear Form (+40% base + talent doubling) — applied as transient atk injection
      const bearFormBuff = (hs.heroRef as any)._bearFormActive
        ? Math.floor(hs.heroRef.stats.attack * ((hs.heroRef as any)._bearFormAtkMult || 0)) : 0;
      // Bloodtally Knife: permanent +N% attack stacked from kills
      const bloodtallyBuff = rb.bloodtallyBonus > 0
        ? Math.floor(hs.heroRef.stats.attack * rb.bloodtallyBonus) : 0;
      // Radiant Surge: +80% damage for 3s after being healed
      const radiantBuff = (hs.heroRef as any)._radiantSurge
        ? Math.floor(hs.heroRef.stats.attack * 0.80) : 0;
      // Forgemaster's Ingot: +40% of weapon stat bonuses to attack
      let forgemasterBuff = 0;
      if (rb.hasForgemasterIngot) {
        for (const wSlot of ['weapon1', 'weapon2'] as const) {
          const w = hs.heroRef.equipment[wSlot];
          if (w?.statBonuses?.attack) forgemasterBuff += Math.floor(w.statBonuses.attack * 0.40);
        }
      }
      // Passive rune tree attack/crit bonuses (injected transiently like tome bonuses)
      const pbAtk = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
      const passiveAtkBonus   = Math.floor(pbAtk.atk + hs.heroRef.stats.attack * pbAtk.dmgPct);
      const passiveCritBonus  = pbAtk.crit;
      const passiveCritDmg    = pbAtk.critDmg;
      const passiveSpBonus    = Math.floor(pbAtk.sp);
      // Berserk Rite keystone: heroes below 30% HP deal +40% damage, take +15% (applied via enrage-style boost)
      const berserkTreeBonus  = pbAtk.keystoneBerserk && hs.heroRef.stats.hp / hs.heroRef.stats.maxHp < 0.30
        ? Math.floor(hs.heroRef.stats.attack * 0.40) : 0;
      // Void Shield / Molten Shell: checked BEFORE any stat mutation so early-return never leaks buffs
      if (target.getData('voidShield')) {
        target.setData('voidShield', false);
        this.restoreEnemyTint(target);
        this.showDamageNumber(target.x, target.y - 40, 'BLOCKED', '#5090c0');
        return; // skip this attack entirely
      }
      // Molten Shell: absorb first hit + counter with fire damage
      if (target.getData('moltenShell')) {
        target.setData('moltenShell', false);
        this.restoreEnemyTint(target);
        this.showDamageNumber(target.x, target.y - 40, '🔥 ABSORBED', '#f97316');
        // Counter-burn: deal fire damage back to the attacker (DEF mitigation + fire resist apply)
        const rawCounter = Math.max(1, Math.floor(target.enemyRef.attack * 0.6 * (1 - calculateMitigation(this.effStats(hs.heroRef).defense))));
        const fireResist = this.getHeroResist(hs.heroRef, 'fire');
        const counterDmg = fireResist > 0 ? Math.max(1, Math.floor(rawCounter * (1 - Math.min(fireResist, 75) / 100))) : rawCounter;
        if (!this.gsm.godMode) {
          this.applyDamageToHero(hs, counterDmg);
          this.showDamageNumber(hs.x, hs.y - 30, `${counterDmg}`, '#f97316');
          if (hs.heroRef.stats.hp <= 0) {
            this.lastDeathSource = `Molten Shell counter (${target.enemyRef.name})`;
            this.handleHeroDeath(hs);
          }
        }
        this.vfx.playExplosionBurst(hs.x, hs.y - 30);
        return; // attack absorbed
      }
      // Apply transient stat bonuses (safe: all early-return paths are above this line)
      const eagleEyeCrit = (hs.heroRef as any)._eagleEyeCrit || 0;
      // Bloody Ledger sigil: first 10 hits this wave are guaranteed crits (force 100% crit
      // chance, revert after, decrement counter). `_bloodyLedgerPrimed` is a number
      // (remaining guaranteed-crit charges).
      const bloodyLedgerForceCrit = ((hs.heroRef as any)._bloodyLedgerPrimed as number ?? 0) > 0;
      if (bloodyLedgerForceCrit) hs.heroRef.stats.critChance += 100; // overshoot cap → always rolls crit
      if (dmgTomeBonus > 0) hs.heroRef.stats.attack += dmgTomeBonus;
      if (passiveAtkBonus > 0) hs.heroRef.stats.attack += passiveAtkBonus;
      if (berserkTreeBonus > 0) hs.heroRef.stats.attack += berserkTreeBonus;
      if (powerBuff > 0) hs.heroRef.stats.attack += powerBuff;
      if (giantsBuff > 0) hs.heroRef.stats.attack += giantsBuff;
      if (factionBuff > 0) hs.heroRef.stats.attack += factionBuff;
      if (warbladeBuff > 0) hs.heroRef.stats.attack += warbladeBuff;
      if (crescendoBuff > 0) hs.heroRef.stats.attack += crescendoBuff;
      // Sigil affixes — applied transiently like the other buffs so they're saved-clean.
      // Hourglass of Stasis (special) forces crit chance to 0.
      const sig = this.getSigilEffects();
      // Shrine of Boons — damage/auto-attack multipliers. Auto-attack path adds
      // both (damagePct applies to every hit, autoAttackPct only on auto-attacks).
      // Stacked-per-kill bonuses (Blood Price) are already inside boonEff.damagePct
      // because aggregateBoonEffects folds the stack total in.
      const boonEffAtk = this.getBoonEffects();
      const boonAtkBuff = boonEffAtk.hasAny
        ? Math.floor(hs.heroRef.stats.attack * (boonEffAtk.damagePct + boonEffAtk.autoAttackPct))
        : 0;
      // Reaper's Pact — +55% damage per dead party hero (damagePerDeadHeroPct).
      const deadHeroCount = this.gsm.player.heroes.filter((h) => h.isDead || h.permanentlyDead).length;
      const reaperBuff = boonEffAtk.damagePerDeadHeroPct > 0 && deadHeroCount > 0
        ? Math.floor(hs.heroRef.stats.attack * boonEffAtk.damagePerDeadHeroPct * deadHeroCount)
        : 0;
      const sigilAtkBuff = sig.atkPct > 0 ? Math.floor(hs.heroRef.stats.attack * sig.atkPct / 100) : 0;
      const sigilSpBuff  = sig.spellPowerPct > 0 ? Math.floor(hs.heroRef.stats.spellPower * sig.spellPowerPct / 100) : 0;
      const sigilCritChance = sig.critChance / 100;
      const sigilCritDmg    = sig.critDmgPct / 100;
      const hourglassZeroCrit = sig.specials.has('hourglass_of_stasis') ? hs.heroRef.stats.critChance + critTomeBonus : 0;
      if (sigilAtkBuff > 0) hs.heroRef.stats.attack += sigilAtkBuff;
      if (sigilSpBuff  > 0) hs.heroRef.stats.spellPower += sigilSpBuff;
      if (boonAtkBuff !== 0) hs.heroRef.stats.attack += boonAtkBuff;
      if (reaperBuff  > 0) hs.heroRef.stats.attack += reaperBuff;
      if (sigilCritChance > 0 && !sig.specials.has('hourglass_of_stasis')) hs.heroRef.stats.critChance += sigilCritChance;
      if (sigilCritDmg    > 0) hs.heroRef.stats.critDamage += sigilCritDmg;
      if (kingpinCritDmg  > 0) hs.heroRef.stats.critDamage += kingpinCritDmg;
      if (hourglassZeroCrit > 0) hs.heroRef.stats.critChance -= hourglassZeroCrit;
      if (bloodlustBuff > 0) hs.heroRef.stats.attack += bloodlustBuff;
      if (bearFormBuff > 0) hs.heroRef.stats.attack += bearFormBuff;
      if (bloodtallyBuff > 0) hs.heroRef.stats.attack += bloodtallyBuff;
      if (radiantBuff > 0) hs.heroRef.stats.attack += radiantBuff;
      if (forgemasterBuff > 0) hs.heroRef.stats.attack += forgemasterBuff;
      if (passiveCritBonus > 0) hs.heroRef.stats.critChance += passiveCritBonus;
      if (passiveCritDmg > 0) hs.heroRef.stats.critDamage += passiveCritDmg;
      if (passiveSpBonus > 0) hs.heroRef.stats.spellPower += passiveSpBonus;
      if (critTomeBonus > 0) hs.heroRef.stats.critChance += critTomeBonus;
      if (hasteCritBuff > 0) hs.heroRef.stats.critChance += hasteCritBuff;
      if (warHornCrit > 0) hs.heroRef.stats.critChance += warHornCrit;
      if (eagleEyeCrit > 0) hs.heroRef.stats.critChance += eagleEyeCrit;
      if (critDmgTomeBonus > 0) hs.heroRef.stats.critDamage += critDmgTomeBonus;
      if (rb.critDmgBonus > 0) hs.heroRef.stats.critDamage += rb.critDmgBonus;
      if (spTomeBonus > 0) hs.heroRef.stats.spellPower += spTomeBonus;
      if (orbSpBuff > 0) hs.heroRef.stats.spellPower += orbSpBuff;
      if (archonSpBuff > 0) hs.heroRef.stats.spellPower += archonSpBuff;
      // Berserk: inject crit damage inline (never stored permanently in stats)
      const berserkCritBuff = (hs.heroRef as any)._berserkActive ? ((hs.heroRef as any)._berserkCritDmgBuff || 0) : 0;
      if (berserkCritBuff > 0) hs.heroRef.stats.critDamage += berserkCritBuff;
      const result = heroAttackEnemy(hs.heroRef, target.enemyRef, time, distToTarget);
      // Sigil multipliers (vs<Faction>/vsBoss/ignoreArmor) + row multiplier — all applied AFTER
      // heroAttackEnemy computes the base damage. ignoreArmor works by folding a fraction of the
      // defense that was mitigated back into the final damage.
      const rowAtkMult = this.getActiveDmgMult(hs.getData('slotIdx') as number | undefined);
      const sigilFactionMult = this.getSigilFactionMult(target);
      let totalMult = rowAtkMult * sigilFactionMult;
      if (sig.ignoreArmorPct > 0 && result.damage > 0) {
        // Approximate: bonus = ignoreArmor% of the defense-mitigated portion. Works in tandem
        // with the above mult since we always adjust the post-result damage.
        const defMitigation = 1 - Math.min(0.85, target.enemyRef.defense / (target.enemyRef.defense + 100));
        const armorBonus = 1 + (sig.ignoreArmorPct / 100) * (1 - defMitigation);
        totalMult *= armorBonus;
      }
      // Hero Armory — Goomar: Bloodied Resolve grants +1 % damage per 10 %
      // missing HP, capped at +8 %. Applied as a post-attack multiplier so it
      // stacks cleanly with row/sigil mults.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_goomar_bloodied_resolve') && result.damage > 0) {
        const maxHp = hs.heroRef.stats.maxHp ?? 1;
        const missingPct = Math.max(0, 1 - hs.heroRef.stats.hp / maxHp);
        const bonus = Math.min(0.08, Math.floor(missingPct * 10) * 0.01);
        if (bonus > 0) totalMult *= (1 + bonus);
      }
      // Hero Armory — Goomar: Giant's Bane grants +10 % damage vs full-HP enemies.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_goomar_giants_bane') && result.damage > 0) {
        if (target.enemyRef.currentHp >= target.enemyRef.maxHp) {
          totalMult *= 1.10;
        }
      }
      // Hero Armory — Goomar: Crushing Momentum — if a kill flag was set, this
      // attack consumes it and gains +10 % armour penetration (approximated by
      // adding back ~10 % of the def-mitigated damage).
      const h = hs.heroRef as any;
      if (this.hasArmoryPassive(hs.heroRef, 'passive_goomar_crushing_momentum') && h._momentumCharge && result.damage > 0) {
        const defMitigation = 1 - Math.min(0.85, target.enemyRef.defense / (target.enemyRef.defense + 100));
        totalMult *= (1 + 0.10 * (1 - defMitigation));
        h._momentumCharge = false;
      }
      // Hero Armory — Sylvari: Eagle Eye, +5 % vs boss/elite tier.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_sylvari_eagle_eye') && result.damage > 0) {
        if (target.enemyRef.tier === 'boss' || target.enemyRef.tier === 'elite') {
          totalMult *= 1.05;
        }
      }
      // Hero Armory — Lootfi Mountain Roots: standing still 10 s+ grants
      // +15 % damage. Stand-still timer is tracked per-frame in the update loop.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_mountain_roots') && result.damage > 0) {
        const stillSince = ((hs.heroRef as any)._stillSinceMs as number | undefined) ?? Infinity;
        if (Date.now() - stillSince >= 10_000) totalMult *= 1.15;
      }
      // Hero Armory — Sylvari: Hunter's Focus — every 5th consecutive attack
      // on the same target deals 3× damage. Track per-attacker hit-counter +
      // last-target-id on the hero so switching targets resets the chain.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_sylvari_hunters_focus') && result.damage > 0) {
        const lastId = h._huntersFocusTargetId;
        const tgtId = target.enemyRef.instanceId;
        if (lastId === tgtId) {
          h._huntersFocusStreak = (h._huntersFocusStreak ?? 0) + 1;
        } else {
          h._huntersFocusStreak = 1;
          h._huntersFocusTargetId = tgtId;
        }
        if ((h._huntersFocusStreak % 5) === 0) {
          totalMult *= 3;
          h._huntersFocusStreak = 0;
        }
      }
      // Hero Armory — Kael Lethal Tempo: +2 % damage per consecutive same-
      // target auto-attack, caps at +10 % (5 hits). Resets on target switch.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_kael_lethal_tempo') && result.damage > 0) {
        const lastId = h._lethalTempoTargetId;
        const tgtId = target.enemyRef.instanceId;
        if (lastId === tgtId) {
          h._lethalTempoStacks = Math.min(5, (h._lethalTempoStacks ?? 0) + 1);
        } else {
          h._lethalTempoStacks = 1;
          h._lethalTempoTargetId = tgtId;
        }
        const tempoBonus = ((h._lethalTempoStacks ?? 0) - 1) * 0.02; // first hit: 0%, 2nd: +2%, ... 5th: +8%
        // Note: cap is +10% per spec — applies once we hit 6+ on same target.
        if (tempoBonus > 0) totalMult *= (1 + Math.min(0.10, tempoBonus));
      }
      // Hero Armory — Kael Phantom Reflexes: stacking +5% damage buff applied
      // whenever Kael takes damage during Evasion. Each stack lasts 5s
      // independently; total bonus capped at +20% (4 stacks max).
      if (this.hasArmoryPassive(hs.heroRef, 'passive_kael_phantom_reflexes') && result.damage > 0) {
        const stacks = (h._phantomReflexStacks as { exp: number }[] | undefined) ?? [];
        const now = Date.now();
        const liveStacks = stacks.filter((s) => s.exp > now);
        h._phantomReflexStacks = liveStacks;
        const reflexMult = 1 + 0.05 * Math.min(4, liveStacks.length);
        if (reflexMult > 1) totalMult *= reflexMult;
      }
      // ── v2 talent tree (WRATH) damage multipliers ─────────────────────
      if (result.damage > 0) {
        const pbW = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
        // Slayer — +X% dmg vs elites/bosses
        if (pbW.dmgVsElitePct > 0 && (target.enemyRef.tier === 'elite' || target.enemyRef.tier === 'boss')) {
          totalMult *= 1 + pbW.dmgVsElitePct;
        }
        // Brutality — +0.4% damage per stage cleared (cap baked into rank ×0.004 × stage)
        if (pbW.dmgPerStagePct > 0) {
          totalMult *= 1 + pbW.dmgPerStagePct * Math.max(0, this.battleLevel);
        }
        // Berserker — heroes <30% HP gain +X% damage
        if (pbW.berserkLowHpPct > 0) {
          const maxHpB = hs.heroRef.stats.maxHp ?? 1;
          if ((hs.heroRef.stats.hp ?? 0) / maxHpB < 0.30) totalMult *= 1 + pbW.berserkLowHpPct;
        }
        // Warmonger — +15% damage per dead ally (cap +60%)
        if (pbW.warmongerPctPerDeadAlly > 0) {
          const deadCount = this.heroSprites.filter((o) => o.heroRef.isDead || o.heroRef.permanentlyDead).length;
          totalMult *= 1 + Math.min(0.60, pbW.warmongerPctPerDeadAlly * deadCount);
        }
        // Avenging Wrath invocation — +50 % damage burst window. Read off the
        // scene-level timestamp set by `castAvengingWrath`; auto-expires by
        // comparison so no per-tick decrement needed.
        if (this.avengingWrathUntil > Date.now()) {
          totalMult *= 1.50;
        }
        // Sundering Strikes — armour pen (fold back the def-mitigated portion)
        if (pbW.armorPenPct > 0) {
          const defM = 1 - Math.min(0.85, target.enemyRef.defense / (target.enemyRef.defense + 100));
          totalMult *= 1 + pbW.armorPenPct * (1 - defM);
        }
      }
      if (totalMult !== 1 && result.damage > 0) {
        const prevDmg = result.damage;
        const adjustedDmg = Math.max(1, Math.round(prevDmg * totalMult));
        target.enemyRef.currentHp += prevDmg;
        target.enemyRef.currentHp -= adjustedDmg;
        result.damage = adjustedDmg;
      }
      // Cyrith's Combat FX row 28 spark now fires from the wizard projectile
      // branch below (replacing the green-ball magic bolt) so the visual is
      // tied to the auto-attack swing itself rather than the damage-resolution
      // hook. Single-source removes the double-flash that would otherwise
      // play when both this block and the projectile branch fired.
      // Hero Armory — Lootfi Forgefire Strikes: every 5th auto-attack ignites
      // the mace, cleaving nearby foes within 120 px for 60 % of the swing's
      // final damage. Counter is per-hero, never resets between waves so the
      // cadence reads as a slow steady drumbeat rather than a wave-1 reroll.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_forgefire_strikes') && result.damage > 0) {
        const cnt = ((h._forgefireCount as number | undefined) ?? 0) + 1;
        h._forgefireCount = cnt;
        if (cnt % 5 === 0) {
          const cleaveDmg = Math.max(1, Math.round(result.damage * 0.60));
          for (const es of this.enemySprites) {
            if (es === target || !es.active || es.enemyRef.currentHp <= 0) continue;
            if (Phaser.Math.Distance.Between(target.x, target.y, es.x, es.y) > 120) continue;
            es.enemyRef.currentHp -= cleaveDmg;
            this.showDamageNumber(es.x, es.y - 20, `${cleaveDmg}`, '#ff8030', '12px');
            this.flashSprite(es);
            if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
          }
          this.vfx.playLootfiForgefireCleave(target.x, target.y);
        }
      }
      if (berserkCritBuff > 0) hs.heroRef.stats.critDamage -= berserkCritBuff;
      if (orbSpBuff > 0) hs.heroRef.stats.spellPower -= orbSpBuff;
      if (archonSpBuff > 0) hs.heroRef.stats.spellPower -= archonSpBuff;
      if (spTomeBonus > 0) hs.heroRef.stats.spellPower -= spTomeBonus;
      if (rb.critDmgBonus > 0) hs.heroRef.stats.critDamage -= rb.critDmgBonus;
      if (critDmgTomeBonus > 0) hs.heroRef.stats.critDamage -= critDmgTomeBonus;
      if (kingpinCritDmg > 0) hs.heroRef.stats.critDamage -= kingpinCritDmg;
      if (warHornCrit > 0) hs.heroRef.stats.critChance -= warHornCrit;
      if (eagleEyeCrit > 0) hs.heroRef.stats.critChance -= eagleEyeCrit;
      if (hasteCritBuff > 0) hs.heroRef.stats.critChance -= hasteCritBuff;
      if (passiveSpBonus > 0) hs.heroRef.stats.spellPower -= passiveSpBonus;
      if (passiveCritDmg > 0) hs.heroRef.stats.critDamage -= passiveCritDmg;
      if (passiveCritBonus > 0) hs.heroRef.stats.critChance -= passiveCritBonus;
      if (critTomeBonus > 0) hs.heroRef.stats.critChance -= critTomeBonus;
      if (forgemasterBuff > 0) hs.heroRef.stats.attack -= forgemasterBuff;
      if (radiantBuff > 0) hs.heroRef.stats.attack -= radiantBuff;
      if (bloodtallyBuff > 0) hs.heroRef.stats.attack -= bloodtallyBuff;
      if (bloodlustBuff > 0) hs.heroRef.stats.attack -= bloodlustBuff;
      if (bearFormBuff > 0) hs.heroRef.stats.attack -= bearFormBuff;
      // Revert sigil affix buffs (mirror of the add block above)
      if (sigilAtkBuff > 0) hs.heroRef.stats.attack -= sigilAtkBuff;
      if (sigilSpBuff  > 0) hs.heroRef.stats.spellPower -= sigilSpBuff;
      if (boonAtkBuff !== 0) hs.heroRef.stats.attack -= boonAtkBuff;
      if (reaperBuff  > 0) hs.heroRef.stats.attack -= reaperBuff;
      if (sigilCritChance > 0 && !sig.specials.has('hourglass_of_stasis')) hs.heroRef.stats.critChance -= sigilCritChance;
      if (sigilCritDmg    > 0) hs.heroRef.stats.critDamage -= sigilCritDmg;
      if (hourglassZeroCrit > 0) hs.heroRef.stats.critChance += hourglassZeroCrit;
      if (crescendoBuff > 0) hs.heroRef.stats.attack -= crescendoBuff;
      if (factionBuff > 0) hs.heroRef.stats.attack -= factionBuff;
      if (warbladeBuff > 0) hs.heroRef.stats.attack -= warbladeBuff;
      if (giantsBuff > 0) hs.heroRef.stats.attack -= giantsBuff;
      if (berserkTreeBonus > 0) hs.heroRef.stats.attack -= berserkTreeBonus;
      if (powerBuff > 0) hs.heroRef.stats.attack -= powerBuff;
      if (passiveAtkBonus > 0) hs.heroRef.stats.attack -= passiveAtkBonus;
      if (dmgTomeBonus > 0) hs.heroRef.stats.attack -= dmgTomeBonus;
      // Revert Bloody Ledger crit bump; consume one charge. When the counter hits 0, the
      // remaining hits this wave roll crit normally.
      if (bloodyLedgerForceCrit) {
        hs.heroRef.stats.critChance -= 100;
        const remaining = ((hs.heroRef as any)._bloodyLedgerPrimed as number) - 1;
        (hs.heroRef as any)._bloodyLedgerPrimed = Math.max(0, remaining);
      }
      // Show Enrage activation announcement
      if ((hs.heroRef as any)._enrageActive && !wasEnraged) {
        this.showDamageNumber(hs.x, hs.y - 50, 'ENRAGE!', '#ff2222');
      }
      const isCrit = result.events.some((e) => e.type === 'crit');
      if (isCrit) this.gsm.trackCrit();
      // Stamp crit state on the hero so downstream killEnemy calls (Soul
      // Harvest passive for Morveth, and anything else that wants to
      // branch on "did this kill come from a crit") can read it without
      // threading the flag through every call site.
      (hs.heroRef as any)._lastKillWasCrit = isCrit;
      // Magmarath's Ember sigil — auto-attack crits leave a 3 s ashen burn (5 % of max HP/sec).
      if (isCrit && result.damage > 0) this.maybeApplyEmberBurn(target, hs);
      // Stormcoil Charm sigil — auto-attack crits chain to a nearby enemy for 60 % crit damage.
      if (isCrit && result.damage > 0) this.maybeApplyStormcoilChain(target, result.damage, hs);
      this.trackDps(hs.heroRef.id, result.damage);
      // SFX: per-class auto-attack sound. Crit gets a punchy override.
      if (result.damage > 0) {
        if (isCrit) {
          this.sfx.play('crit', { volume: 0.9 });
        } else {
          const cls = hs.heroRef.heroClass;
          const key = cls === 'assassin' ? 'hit_blade'
            : cls === 'archer' ? 'ranged_shot'
            : (cls === 'wizard' || cls === 'healer' || cls === 'druid') ? 'spell_bolt'
            : 'hit_medium';
          this.sfx.play(key, { volume: 0.5 });
        }
      }
      this.showDamageNumber(
        target.x + (Math.random() - 0.5) * 20, target.y - 20,
        isCrit ? `${result.damage} CRIT!` : `${result.damage}`,
        isCrit ? '#ffffff' : '#e0e0e0',
        isCrit ? '15px' : '12px',
      );
      this.flashSprite(target);

      // ── v2 talent tree (WRATH) on-hit effects ────────────────────────
      if (result.damage > 0) {
        const pbW2 = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
        // Lifesteal — heal hero for % of damage dealt (Steady Heart amplifies).
        if (pbW2.lifestealPct > 0) {
          const healMult = 1 + pbW2.healingReceivedPct;
          const heal = Math.max(1, Math.round(result.damage * pbW2.lifestealPct * healMult));
          const maxHpL = this.effStats(hs.heroRef).maxHp;
          hs.heroRef.stats.hp = Math.min(maxHpL, hs.heroRef.stats.hp + heal);
        }
        // Headsman — instakill non-boss < 10% HP after damage applied
        if (pbW2.headsmanFlag && target.enemyRef.tier !== 'boss' && target.enemyRef.currentHp > 0
            && target.enemyRef.currentHp / target.enemyRef.maxHp < 0.10) {
          target.enemyRef.currentHp = 0;
          this.showDamageNumber(target.x, target.y - 40, '☠ HEADSMAN', '#e0e0e0', '13px');
        }
        // Twin Fang — every 5th crit hits twice
        if (isCrit && pbW2.twinFangFlag && target.enemyRef.currentHp > 0) {
          const tfCount = ((hs.heroRef as any)._twinFangCritCount ?? 0) + 1;
          (hs.heroRef as any)._twinFangCritCount = tfCount;
          if (tfCount % 5 === 0) {
            const second = Math.max(1, Math.round(result.damage));
            target.enemyRef.currentHp -= second;
            this.showDamageNumber(target.x + 18, target.y - 26, `+${second}`, '#ffe060', '13px');
            this.flashSprite(target);
          }
        }
        // War Fury — flat % chance for auto-attack to fire twice (independent of crit).
        if (pbW2.doubleAttackChance > 0 && target.enemyRef.currentHp > 0
            && Math.random() < pbW2.doubleAttackChance) {
          const dup = Math.max(1, Math.round(result.damage));
          target.enemyRef.currentHp -= dup;
          this.showDamageNumber(target.x - 18, target.y - 26, `+${dup}`, '#f97316', '12px');
          this.flashSprite(target);
          if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
        }
        // Crimson Wound — crits apply 2% maxHp bleed spread over 4 s (DoT,
        // 0.5 % maxHp per second). The standard DoT pipeline shows per-second
        // damage numbers drifting downward; source-stamped so the damage
        // shows in the DPS meter under 'crimson_wound'.
        if (isCrit && pbW2.crimsonWoundFlag && target.enemyRef.currentHp > 0) {
          const tickDmg = Math.max(1, Math.floor(target.enemyRef.maxHp * 0.005));
          applyDebuff(target.enemyRef, {
            type: 'dot', value: tickDmg, duration: 4000, remaining: 4000,
            sourceHeroId: hs.heroRef.id, sourceLabel: 'crimson_wound',
          });
        }
        // Cleave — auto-attack splashes adjacent enemies
        if (pbW2.cleavePct > 0) {
          const splashDmg = Math.max(1, Math.round(result.damage * pbW2.cleavePct));
          for (const other of this.enemySprites) {
            if (other === target || !other.active || other.enemyRef.currentHp <= 0) continue;
            if (Math.abs(other.x - target.x) > 80 || Math.abs(other.y - target.y) > 80) continue;
            other.enemyRef.currentHp -= splashDmg;
            this.showDamageNumber(other.x, other.y - 18, `${splashDmg}`, '#ffaa40', '10px');
            if (other.enemyRef.currentHp <= 0) this.killEnemy(other, hs);
          }
        }
      }

      // Shaman Shamanic Call party buff — on-hit procs. Flag is cleared when expiry passes.
      {
        const sb = (hs.heroRef as any)._shamanicBuff as { kind: string; expiry: number } | undefined;
        if (sb && this.time.now > sb.expiry) (hs.heroRef as any)._shamanicBuff = undefined;
        else if (sb && target.enemyRef.currentHp > 0 && result.damage > 0) {
          if (sb.kind === 'extra_attack' && Math.random() < 0.20) {
            // Repeat the hit at 80% damage. Cheap way to avoid recursive infinite loops.
            const extra = Math.max(1, Math.floor(result.damage * 0.80));
            target.enemyRef.currentHp -= extra;
            this.showDamageNumber(target.x + 14, target.y - 30, `+${extra}`, '#fbbf24', '12px');
            this.flashSprite(target);
            if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
          } else if (sb.kind === 'fire_bonus' && Math.random() < 0.20) {
            const bonus = Math.max(1, Math.floor(result.damage * 0.50));
            target.enemyRef.currentHp -= bonus;
            this.showDamageNumber(target.x - 14, target.y - 30, `🔥${bonus}`, '#f97316', '12px');
            if (target.enemyRef.currentHp <= 0) this.killEnemy(target, hs);
          } else if (sb.kind === 'slow_on_hit' && Math.random() < 0.10) {
            applyDebuff(target.enemyRef, { type: 'slow', value: 0.50, duration: 1000, remaining: 1000 });
            this.showDamageNumber(target.x, target.y - 38, '🌀 SLOWED', '#93c5fd', '10px');
          }
        }
      }

      // Gem of Interrupt sigil — every 6th auto-attack stuns the target for 0.6s.
      // Main-hero-only combat sigil, counter kept on the hero.
      if (this.getSigilEffects().specials.has('gem_of_interrupt') && this.isMainHero(hs) && target.enemyRef.currentHp > 0) {
        const h = hs.heroRef as unknown as { _gemInterruptCount?: number };
        h._gemInterruptCount = ((h._gemInterruptCount ?? 0) + 1) % 6;
        if (h._gemInterruptCount === 0) {
          applyDebuff(target.enemyRef, { type: 'stun', value: 1, duration: 600, remaining: 600 });
          this.showDamageNumber(target.x, target.y - 38, 'STUN', '#eab308', '11px');
        }
      }

      // Tank AOE cleave: 35% splash damage to enemies adjacent to the primary target
      if (hs.heroRef.heroClass === 'tank' && result.damage > 0) {
        const splashDmg = Math.floor(result.damage * 0.35);
        if (splashDmg > 0) {
          for (const se of this.enemySprites) {
            if (se === target || !se.active || se.enemyRef.currentHp <= 0) continue;
            if (Math.abs(se.x - target.x) <= 90) {
              se.enemyRef.currentHp -= splashDmg;
              this.showDamageNumber(se.x + (Math.random() - 0.5) * 20, se.y - 20, `${splashDmg}`, '#d0a060');
              if (se.enemyRef.currentHp <= 0) this.killEnemy(se, hs);
            }
          }
        }
      }

      // Class-specific attack VFX
      // Origin for ranged heroes: right edge of 132×132 sprite at mid-body height
      const projFromX = hs.x + 64;
      const projFromY = hs.y - 62;
      const heroClass = hs.heroRef.heroClass;
      const heroKey = hs.heroRef.heroKey;
      if (heroClass === 'archer' && heroKey !== 'amazon') {
        // Aim for roughly the target's upper-chest — scales with sprite height but caps so big bosses
        // don't pull the arrow impossibly high. Minions: ~48px above feet. Boss (220): ~100px above feet.
        const chestOffset = Math.min(100, (target.displayHeight || 120) * 0.45);
        this.vfx.playArrow(projFromX, projFromY, target.x, target.y - chestOffset);
      } else if (heroKey === 'amazon') {
        const amazonMelee = (hs.getData('jumpState') as string) === 'at_enemy';
        if (amazonMelee) {
          this.vfx.playMeleeSpark(target.x, target.y);
        } else if (!amazonSpearVolleyFired) {
          // Ranged: spear sprite throw from top-right of hero — golden javelin reserved for 10th-strike Spear Volley
          this.vfx.playSpearThrow(hs.x + 54, hs.y - 80, target.x, target.y - 30);
        }
      } else if (heroClass === 'assassin') {
        const w1 = hs.heroRef.equipment.weapon1;
        const w2 = hs.heroRef.equipment.weapon2;
        const hasThrown = w1?.weaponType === 'throwing_knife' || w1?.weaponType === 'shuriken'
          || w2?.weaponType === 'throwing_knife' || w2?.weaponType === 'shuriken';
        if (hasThrown) {
          const iconKey = (w1?.weaponType === 'throwing_knife' || w1?.weaponType === 'shuriken') ? w1?.spriteKey : w2?.spriteKey;
          this.vfx.playThrownWeapon(projFromX, projFromY, target.x, target.y - 40, iconKey);
        } else {
          this.vfx.playMeleeSpark(target.x, target.y);
        }
      } else if (heroKey === 'necromancer') {
        // Necromancer: dark purple shadow bolt; Lich Form makes it red
        const boltColor = (hs.heroRef as any)._lichFormActive ? 0xdc2626 : 0x7c3aed;
        this.vfx.playMagicBolt(projFromX, projFromY, target.x, target.y - 40, boltColor);
        if (this.anims.exists('hero_necromancer_attack') && hs.anims.currentAnim?.key !== 'hero_necromancer_attack') {
          hs.play('hero_necromancer_attack');
          hs.once('animationcomplete', () => {
            if (!hs.heroRef.isDead && this.anims.exists('hero_necromancer_idle')) hs.play('hero_necromancer_idle');
          });
        }
      } else if (heroClass === 'wizard' && heroKey === 'sorcerer') {
        // Cyrith — Combat FX row 28 spark on the target instead of the
        // generic teal magic-bolt projectile. The lightning theme fits her
        // Storm Bolt / Static Field / Arc Strike kit and avoids the
        // "green ball" the user flagged. Single-frame ADD-blend flash; no
        // traveling projectile, just the impact pop on hit.
        this.vfx.playCyrithAutoStrike(target.x, target.y - 30);
      } else if (heroClass === 'wizard') {
        this.vfx.playMagicBolt(projFromX, projFromY, target.x, target.y - 40, 0x4090c0);
      } else if (heroClass === 'healer') {
        this.vfx.playMagicBolt(projFromX, projFromY, target.x, target.y - 40, 0xfbbf24);
      } else if (heroClass === 'druid') {
        // In Nature Form the feral wolf bites raw — no swipe VFX.
        if (!(hs.heroRef as any)._bearFormActive) {
          this.vfx.playDruidAutoswipe(target.x, target.y - 30);
        }
      } else if (heroClass === 'shaman') {
        // Zhorak: spirit-wisp orb projectile — purple/pink arcane, distinct from
        // Lootfi's lightning auto and Kael's boomerang.
        this.vfx.playSpiritWisp(hs.x + 40, hs.y - 60, target.x, target.y - 30);
      } else {
        // Warrior / Tank — melee spark
        this.vfx.playMeleeSpark(target.x, target.y);
      }

      // Weapon special effects — proc on auto-attack
      if (result.damage > 0 && target.enemyRef.currentHp > 0) {
        const w1 = hs.heroRef.equipment.weapon1;
        const w2 = hs.heroRef.equipment.weapon2;
        const effects = [w1?.specialEffect, w2?.specialEffect].filter(Boolean) as string[];
        for (const eff of effects) {
          // Mace stun: "X% chance to stun for Ys"
          const stunMatch = eff.match(/(\d+)% chance to stun/);
          if (stunMatch && Math.random() * 100 < parseInt(stunMatch[1])) {
            const durMatch = eff.match(/(\d+(?:\.\d+)?)s/);
            const dur = durMatch ? parseFloat(durMatch[1]) * 1000 : 1000;
            const maceStun = { type: 'stun' as const, value: 1, duration: dur, remaining: dur };
            applyDebuff(target.enemyRef, maceStun);
            this.maybeBrambleSpread(target, maceStun);
            this.showDamageNumber(target.x, target.y - 40, 'STUNNED', '#fbbf24');
            break;
          }
          // 2H Mace Tremor: "X% chance to AoE stun all enemies for Ys"
          const tremorMatch = eff.match(/(\d+)% chance to AoE stun all enemies for (\d+(?:\.\d+)?)s/);
          if (tremorMatch && Math.random() * 100 < parseInt(tremorMatch[1])) {
            const dur = parseFloat(tremorMatch[2]) * 1000;
            for (const enemy of this.enemySprites) {
              if (!enemy.active || enemy.enemyRef.currentHp <= 0) continue;
              applyDebuff(enemy.enemyRef, { type: 'stun', value: 1, duration: dur, remaining: dur });
            }
            this.showDamageNumber(target.x, target.y - 50, 'TREMOR!', '#d97706', '16px');
            break;
          }
          // 2H Sword Triple Strike: "X% chance to triple attack"
          const tripleMatch = eff.match(/(\d+)% chance to triple attack/);
          if (tripleMatch && Math.random() * 100 < parseInt(tripleMatch[1])) {
            // Deal 2 additional hits (3 total)
            const bonusDmg = result.damage * 2;
            target.enemyRef.currentHp -= bonusDmg;
            this.showDamageNumber(target.x + 10, target.y - 35, `${bonusDmg} TRIPLE!`, '#f59e0b', '14px');
            break;
          }
          // Sword vorpal: "X% chance to deal double/triple damage"
          const vorpalMatch = eff.match(/(\d+)% chance to deal (double|triple) damage/);
          if (vorpalMatch && Math.random() * 100 < parseInt(vorpalMatch[1])) {
            const mult = vorpalMatch[2] === 'triple' ? 3 : 2;
            const bonusDmg = result.damage * (mult - 1);
            target.enemyRef.currentHp -= bonusDmg;
            this.showDamageNumber(target.x + 10, target.y - 35, `${bonusDmg} VORPAL!`, '#5090c0', '14px');
            break;
          }
          // Runewhisper Runic Surge: "Auto-attacks deal +X% SP as bonus damage"
          // Legendary tier appends "can spell-crit" — bonus damage rolls
          // the hero's crit chance, multiplied by crit damage on success.
          const runicMatch = eff.match(/Runic Surge.*\+(\d+)% SP as bonus damage/);
          if (runicMatch) {
            const heroSp = this.effStats(hs.heroRef).spellPower || 0;
            const bonusPct = parseInt(runicMatch[1]) / 100;
            let bonusDmg = Math.round(heroSp * bonusPct);
            if (bonusDmg > 0) {
              const canCrit = /can spell-crit/.test(eff);
              const isCrit = canCrit && Math.random() < (this.effStats(hs.heroRef).critChance || 0);
              if (isCrit) {
                bonusDmg = Math.round(bonusDmg * (this.effStats(hs.heroRef).critDamage || 1.5));
              }
              target.enemyRef.currentHp -= bonusDmg;
              // Runic Surge floating text suppressed per user — the +N RUNIC
              // numbers were spamming the screen on every Cyrith auto-attack.
              // The damage still applies; only the popup is gone. Crit flash
              // (when canCrit) is intentionally also dropped to keep the
              // visual quiet — the bonus rolls into the regular DPS feel.
            }
            // Doesn't break — Runic Surge stacks on top of any other proc
            // since it's a bonus damage rider, not a replace-the-hit effect.
          }
          // Spear pierce: "Attacks ignore X% of enemy armor" — bonus damage
          const pierceMatch = eff.match(/ignore (\d+)% of enemy armor/);
          if (pierceMatch) {
            const ignorePct = parseInt(pierceMatch[1]) / 100;
            const bonusDmg = Math.round(target.enemyRef.defense * ignorePct * 0.5);
            if (bonusDmg > 0) {
              target.enemyRef.currentHp -= bonusDmg;
              this.showDamageNumber(target.x - 10, target.y - 30, `+${bonusDmg}`, '#22d3ee');
            }
            break;
          }
          // Bow venom: "X% chance to poison for Ys"
          const venomMatch = eff.match(/(\d+)% chance to poison/);
          if (venomMatch && Math.random() * 100 < parseInt(venomMatch[1])) {
            const durMatch = eff.match(/(\d+)s/);
            const dur = durMatch ? parseInt(durMatch[1]) * 1000 : 3000;
            applyDebuff(target.enemyRef, { type: 'dot', value: Math.round(result.damage * 0.15), duration: dur, remaining: dur });
            this.showDamageNumber(target.x, target.y - 40, 'POISONED', '#4ade80');
            this.vfx.playPoisonDrip(target.x, target.y);
            break;
          }
          // Bow cripple/slow: "X% chance to slow by Y%"
          const slowMatch = eff.match(/(\d+)% chance to (?:slow|cripple)/);
          if (slowMatch && Math.random() * 100 < parseInt(slowMatch[1])) {
            applyDebuff(target.enemyRef, { type: 'slow', value: 0.4, duration: 3000, remaining: 3000 });
            this.showDamageNumber(target.x, target.y - 40, 'SLOWED', '#93c5fd');
            break;
          }
          // Dagger Lethality: on crit, X% chance to multiply damage.
          // Matches "(x2)", "(x2-x3)", "(x2-x4)", and legacy "(x2-x5)" — captures the max
          // multiplier (last digit inside the parens). Rare = fixed x2 (maxMult=2 collapses
          // the random range to a single value).
          if (isCrit) {
            const lethalMatch = eff.match(/Lethality.*Crits have (\d+)% chance.*\(x[^)]*?(\d)\)/);
            if (lethalMatch && Math.random() * 100 < parseInt(lethalMatch[1])) {
              const maxMult = parseInt(lethalMatch[2]);
              const range = Math.max(1, maxMult - 1);
              const mult = 2 + Math.floor(Math.random() * range);
              const bonusDmg = result.damage * (mult - 1);
              target.enemyRef.currentHp -= bonusDmg;
              this.showDamageNumber(target.x + 10, target.y - 42, `${bonusDmg} LETHAL!`, '#c084fc', '14px');
              break;
            }
          }
          // Throwing Knife Ranged Lethality: bonus damage multiplier at range
          const rangedLethalMatch = eff.match(/Ranged Lethality.*x([\d.]+)-x([\d.]+)/);
          if (rangedLethalMatch && distToTarget > 150) {
            const minM = parseFloat(rangedLethalMatch[1]);
            const maxM = parseFloat(rangedLethalMatch[2]);
            const bonusDmg = Math.floor(result.damage * (minM + Math.random() * (maxM - minM) - 1));
            if (bonusDmg > 0) {
              target.enemyRef.currentHp -= bonusDmg;
              this.showDamageNumber(target.x - 10, target.y - 36, `+${bonusDmg}`, '#fbbf24');
            }
            break;
          }
        }
        // Axe Cleave: always-active AoE splash to enemies near target
        for (const eff of effects) {
          const cleaveMatch = eff.match(/Cleave.*\+(\d+)% AoE/);
          if (cleaveMatch) {
            const pct = parseInt(cleaveMatch[1]) / 100;
            const cleaveDmg = Math.max(1, Math.floor(result.damage * pct));
            for (const se of this.enemySprites) {
              if (se === target || !se.active || se.enemyRef.currentHp <= 0) continue;
              if (Math.abs(se.x - target.x) <= 110) {
                se.enemyRef.currentHp -= cleaveDmg;
                this.showDamageNumber(se.x + (Math.random()-0.5)*15, se.y - 20, `${cleaveDmg}`, '#d97706');
                if (se.enemyRef.currentHp <= 0) this.killEnemy(se, hs);
              }
            }
            break;
          }
        }
        // Shuriken Fan of Blades: always hit N additional nearest enemies at 40% damage
        for (const eff of effects) {
          const fanMatch = eff.match(/Fan of Blades.*Hits (\d+) additional/);
          if (fanMatch) {
            const extra = parseInt(fanMatch[1]);
            const fanDmg = Math.max(1, Math.floor(result.damage * 0.4));
            const others = this.enemySprites
              .filter(se => se !== target && se.active && se.enemyRef.currentHp > 0)
              .sort((a, b) => Math.abs(a.x - hs.x) - Math.abs(b.x - hs.x))
              .slice(0, extra);
            for (const se of others) {
              se.enemyRef.currentHp -= fanDmg;
              this.showDamageNumber(se.x + (Math.random()-0.5)*15, se.y - 20, `${fanDmg}`, '#a78bfa');
              if (se.enemyRef.currentHp <= 0) this.killEnemy(se, hs);
            }
            break;
          }
        }
      }

      // Bloody Tome + Swift Shadow boon: lifesteal. Additive so a player with
      // both Bloody rank 10 (10%) and Swift Shadow (+20%) lifesteals 30% of the
      // hit. Computed ONCE per hit (no double-apply): the `lifesteal` local
      // sums both sources before multiplying, heal cap still clamps to maxHp.
      const bloodyLifesteal = getTomeBonus(this.gsm.player.tomes, 'bloody');
      const boonLifesteal = this.getBoonEffects().lifestealPct;
      const lifesteal = bloodyLifesteal + boonLifesteal;
      if (lifesteal > 0 && result.damage > 0) {
        const heal = Math.max(1, Math.floor(result.damage * lifesteal));
        const maxHp = this.effStats(hs.heroRef).maxHp;
        hs.heroRef.stats.hp = Math.min(maxHp, hs.heroRef.stats.hp + heal);
        this.showDamageNumber(hs.x, hs.y - 25, `+${heal}`, '#7f1d1d');
        // Vampire's Locket sigil: heal the lowest-HP ally for 10% of the lifesteal amount.
        // Only triggers when the main (first-deployed) hero is the one dealing the lifesteal hit.
        if (this.getSigilEffects().specials.has('vampires_locket') && this.isMainHero(hs)) {
          const allies = this.heroSprites.filter((a) => !a.heroRef.isDead && a !== hs);
          if (allies.length > 0) {
            const lowest = allies.sort((a, b) =>
              (a.heroRef.stats.hp / this.effStats(a.heroRef).maxHp)
              - (b.heroRef.stats.hp / this.effStats(b.heroRef).maxHp))[0];
            const spillHeal = Math.max(1, Math.floor(heal * 0.1));
            const allyMax = this.effStats(lowest.heroRef).maxHp;
            lowest.heroRef.stats.hp = Math.min(allyMax, lowest.heroRef.stats.hp + spillHeal);
            this.showDamageNumber(lowest.x, lowest.y - 25, `+${spillHeal}`, '#f472b6');
          }
        }
      }

      // ── Relic on-hit procs ──
      if (result.damage > 0) {
        const rbHit = this.getRelicBonuses(hs.heroRef);
        // Talisman of the Fifth: every Nth hit deals double damage (N = 6 - stacks: 1→5, 2→4, 3→3, 4→2)
        if (rbHit.fifthAtkStacks > 0) {
          const interval = Math.max(2, 6 - rbHit.fifthAtkStacks);
          const hitCount = ((hs.heroRef as any)._fifthAtkCount || 0) + 1;
          (hs.heroRef as any)._fifthAtkCount = hitCount % interval;
          if (hitCount % interval === 0 && target.enemyRef.currentHp > 0) {
            target.enemyRef.currentHp -= result.damage;
            this.showDamageNumber(target.x, target.y - 42, `${result.damage} ×2!`, '#fde68a');
          }
        }
        // Charm of Twin Strikes: 10% chance to hit twice
        if (rbHit.twinChance > 0 && Math.random() < rbHit.twinChance && target.enemyRef.currentHp > 0) {
          target.enemyRef.currentHp -= result.damage;
          this.showDamageNumber(target.x + 12, target.y - 30, `${result.damage} TWIN`, '#fde68a');
        }
        // Manafount Charm: +3 mana per auto-attack
        if (rbHit.manafount > 0 && hs.heroRef.heroClass !== 'warrior') {
          const manaBuff = Math.round(rbHit.manafount);
          const statsM = this.effStats(hs.heroRef);
          if (hs.heroRef.heroClass === 'assassin') hs.heroRef.stats.energy = Math.min(statsM.maxEnergy, hs.heroRef.stats.energy + manaBuff);
          else hs.heroRef.stats.mp = Math.min(statsM.maxMp, hs.heroRef.stats.mp + manaBuff);
        }
        // Soulspark Sigil: on crit, restore 5 resource
        if (isCrit && rbHit.soulspark > 0) {
          const spark = Math.round(rbHit.soulspark);
          const statsS = this.effStats(hs.heroRef);
          if (hs.heroRef.heroClass === 'warrior') hs.heroRef.stats.rage = Math.min(statsS.maxRage, hs.heroRef.stats.rage + spark);
          else if (hs.heroRef.heroClass === 'assassin') hs.heroRef.stats.energy = Math.min(statsS.maxEnergy, hs.heroRef.stats.energy + spark);
          else hs.heroRef.stats.mp = Math.min(statsS.maxMp, hs.heroRef.stats.mp + spark);
        }
        // Sigil of Vampirism: 10% chance to heal 5% max HP on hit
        if (rbHit.vampChance > 0 && Math.random() < rbHit.vampChance) {
          const statsV = this.effStats(hs.heroRef);
          const vHeal = Math.floor(statsV.maxHp * 0.05);
          hs.heroRef.stats.hp = Math.min(statsV.maxHp, hs.heroRef.stats.hp + vHeal);
          this.showDamageNumber(hs.x, hs.y - 30, `+${vHeal}`, '#f472b6');
        }
        // Resonant Prong: crit echo — deal 50% of damage again
        if (isCrit && rbHit.hasResonantProng && target.enemyRef.currentHp > 0) {
          const echo = Math.floor(result.damage * 0.5);
          target.enemyRef.currentHp -= echo;
          this.showDamageNumber(target.x - 10, target.y - 45, `${echo} ↺`, '#22d3ee');
        }
        // Storm Capacitor: 25% chance on auto-attack to arc lightning to a random enemy
        if (rbHit.hasStormCapacitor && Math.random() < 0.25) {
          const aliveEnemies = this.enemySprites.filter((e) => e.active && e.enemyRef.currentHp > 0 && e !== target);
          if (aliveEnemies.length > 0) {
            const arcTarget = aliveEnemies[Math.floor(Math.random() * aliveEnemies.length)];
            const arcDmg = Math.floor(hs.heroRef.stats.attack * 0.80);
            arcTarget.enemyRef.currentHp -= arcDmg;
            this.showDamageNumber(arcTarget.x, arcTarget.y - 20, `${arcDmg} ⚡`, '#60a5fa');
            this.flashSprite(arcTarget);
            if (arcTarget.enemyRef.currentHp <= 0) this.killEnemy(arcTarget, hs);
          }
        }
      }


      // Rogue combo points: 5 hits on same target = bonus attack
      if (hs.heroRef.heroClass === 'assassin' && target.enemyRef.currentHp > 0) {
        const heroId = hs.heroRef.id;
        const combo = this.comboTracker.get(heroId);
        if (combo && combo.targetRef === target.enemyRef) {
          combo.hits++;
          if (combo.hits >= 5) {
            combo.hits = 0;
            const comboDist = Math.abs(hs.x - target.x);
            const comboResult = heroAttackEnemy(hs.heroRef, target.enemyRef, time, comboDist);
            this.showDamageNumber(target.x, target.y - 35, `${comboResult.damage} COMBO!`, '#eab308', '16px');
            this.flashSprite(target);
            this.vfx.playMeleeSpark(target.x, target.y);
          }
        } else {
          this.comboTracker.set(heroId, { targetRef: target.enemyRef, hits: 1 });
        }
      }
    }

    if (target.enemyRef.currentHp <= 0) {
      this.killEnemy(target, hs);
    }
  }

  // ==================== PETS ====================

  /** Update skeleton minions raised by Morveth's Raise Dead. */
  private updateMinions(time: number, _delta: number): void {
    for (let i = this.minionSprites.length - 1; i >= 0; i--) {
      const m = this.minionSprites[i];
      // Expire
      if (time >= m.expiry || m.hp <= 0) {
        // Corpse Explosion: if necro has rank 7 raise_dead, detonate on expiry/death
        const owner = this.heroSprites.find((h) => h.heroRef.id === m.ownerId);
        const raiseRank = (owner?.heroRef.talentBonuses ?? {})['raise_dead'] ?? 0;
        if (raiseRank >= 7 && m.hp <= 0) {
          const ex = m.sprite.x; const ey = m.sprite.y;
          for (const es of this.enemySprites.filter((e) => e.enemyRef.currentHp > 0 && Phaser.Math.Distance.Between(ex, ey, e.x, e.y) < 180)) {
            const explDmg = Math.round(m.maxHp * 1.5);
            es.enemyRef.currentHp -= explDmg;
            this.showDamageNumber(es.x, es.y - 20, `${explDmg} 💀`, '#a855f7', '13px');
            this.flashSprite(es);
            // Detonation VFX on each hit target, not the corpse itself
            this.vfx.playCorpseExplosion(es.x, es.y - 20);
            if (es.enemyRef.currentHp <= 0 && owner) this.killEnemy(es, owner);
          }
        }
        m.sprite.destroy();
        m.cursedOverlay.destroy();
        m.hpBarBg.destroy();
        m.hpBar.destroy();
        this.minionSprites.splice(i, 1);
        continue;
      }
      // Keep cursed overlay + HP bars locked to minion position
      m.cursedOverlay.setPosition(m.sprite.x, m.sprite.y);
      const mBarY = m.sprite.y - m.sprite.displayHeight - 4;
      m.hpBarBg.setPosition(m.sprite.x, mBarY);
      m.hpBar.setPosition(m.sprite.x - 7.5, mBarY);
      m.hpBar.width = Math.max(0, 15 * m.hp / m.maxHp);
      // Periodic smoke puff (every 1800ms) for necromantic atmosphere
      m.smokeTimer += _delta;
      if (m.smokeTimer >= 1800) {
        m.smokeTimer = 0;
        this.vfx.playNecroSmoke(m.sprite.x, m.sprite.y - 20);
      }
      // Find nearest enemy
      const nearest = this.enemySprites
        .filter((e) => e.active && e.enemyRef.currentHp > 0)
        .sort((a, b) => Phaser.Math.Distance.Between(m.sprite.x, m.sprite.y, a.x, a.y) - Phaser.Math.Distance.Between(m.sprite.x, m.sprite.y, b.x, b.y))[0];
      if (!nearest) continue;
      const dist = Phaser.Math.Distance.Between(m.sprite.x, m.sprite.y, nearest.x, nearest.y);
      const ap = m.animPrefix;
      const walkKey  = `${ap}_walk`;
      const idleKey  = `${ap}_idle`;
      const atkKey   = `${ap}_attack`;
      // Fallbacks for sprites that don't have a dedicated walk/attack anim — avoid getting stuck in a missing anim.
      const haveWalk = this.anims.exists(walkKey);
      const haveIdle = this.anims.exists(idleKey);
      const haveAtk  = this.anims.exists(atkKey);
      const motionKey = haveWalk ? walkKey : (haveIdle ? idleKey : null);
      // Move toward target if not in range
      if (dist > 60) {
        const dx = nearest.x - m.sprite.x; const dy = nearest.y - m.sprite.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        m.sprite.x += (dx / len) * 1.2 * this.speedMultiplier;
        m.sprite.y += (dy / len) * 0.5 * this.speedMultiplier;
        // Sprites face RIGHT naturally — flip (face left) only when moving left
        m.sprite.setFlipX(dx < 0);
        if (motionKey && m.sprite.anims.currentAnim?.key !== motionKey && m.sprite.anims.currentAnim?.key !== atkKey) {
          m.sprite.play(motionKey);
        }
      } else {
        // Face the target — flip left only when target is to the left
        m.sprite.setFlipX(nearest.x < m.sprite.x);
        // Block new attacks while the current attack anim is still playing — prevents flicker/spam.
        const attackAnimPlaying = haveAtk
          && m.sprite.anims.currentAnim?.key === atkKey
          && m.sprite.anims.isPlaying;
        if (attackAnimPlaying) {
          // Let the anim finish; don't advance timer so we don't queue up another immediately.
        } else {
          m.atkTimer += _delta * this.speedMultiplier;
          if (m.atkTimer >= m.atkInterval) {
            m.atkTimer = 0;
            nearest.enemyRef.currentHp -= m.atk;
            this.trackDps(m.ownerId, m.atk, 'raise_dead');
            this.showDamageNumber(nearest.x, nearest.y - 15, `${m.atk}`, '#c084fc', '11px');
            this.flashSprite(nearest);
            if (haveAtk) {
              // Off+play+once — previously a stale listener from a prior cycle could
              // still be pending, so the next attack's completion fired the OLD
              // callback and potentially looped idle before the fresh anim finished.
              m.sprite.off('animationcomplete');
              m.sprite.play(atkKey);
              m.sprite.once('animationcomplete', () => {
                if (m.sprite.active && haveIdle) m.sprite.play(idleKey);
              });
            }
            const owner = this.heroSprites.find((h) => h.heroRef.id === m.ownerId);
            if (nearest.enemyRef.currentHp <= 0 && owner) this.killEnemy(nearest, owner);
          } else if (haveIdle && m.sprite.anims.currentAnim?.key !== idleKey) {
            m.sprite.play(idleKey);
          }
        }
      }
    }
  }

  private updatePets(time: number, delta: number): void {
    for (const ps of this.petSprites) {
      if (!ps.ownerRef.pet || !ps.ownerRef.pet.alive) {
        ps.setAlpha(0.2);
        ps.hpBar.width = 0;
        // Revive timer
        if (ps.ownerRef.pet && ps.ownerRef.pet.reviveTimer > 0) {
          ps.ownerRef.pet.reviveTimer -= delta;
          if (ps.ownerRef.pet.reviveTimer <= 0) {
            ps.ownerRef.pet.alive = true;
            ps.ownerRef.pet.hp = ps.ownerRef.pet.maxHp;
            ps.ownerRef.pet.reviveTimer = 0;
            ps.setAlpha(1);
            this.showDamageNumber(ps.x, ps.y - 20, 'PET REVIVED!', '#22c55e');
          }
        }
        continue;
      }

      // Pet attacks nearest enemy
      const target = this.enemySprites
        .filter((e) => e.active && e.enemyRef.currentHp > 0)
        .sort((a, b) => Math.abs(a.x - ps.x) - Math.abs(b.x - ps.x))[0];
      if (target && time - ps.lastAttackTime >= 1500) {
        ps.lastAttackTime = time;
        const dmg = ps.ownerRef.pet.attack + Math.floor(ps.ownerRef.level * 1.5);
        target.enemyRef.currentHp -= dmg;
        this.showDamageNumber(target.x, target.y - 15, `${dmg}`, '#86efac');
        this.flashSprite(target);
        if (target.enemyRef.currentHp <= 0) {
          const killerHs = this.heroSprites.find((h) => h.heroRef === ps.ownerRef);
          if (killerHs) this.killEnemy(target, killerHs);
        }
      }

      // Enemies can attack the pet if close
      for (const es of this.enemySprites) {
        if (!es.active || es.enemyRef.currentHp <= 0) continue;
        if (Math.abs(es.x - ps.x) < 60 && Math.abs(es.y - ps.y) < 40) {
          const petDmgData = ps.getData('lastPetDmg') as number || 0;
          if (time - petDmgData >= 2000) {
            ps.setData('lastPetDmg', time);
            const petDmg = Math.max(1, es.enemyRef.attack - 5);
            ps.ownerRef.pet.hp -= petDmg;
            this.showDamageNumber(ps.x, ps.y - 10, `${petDmg}`, '#ef4444');
            if (ps.ownerRef.pet.hp <= 0) {
              ps.ownerRef.pet.alive = false;
              ps.ownerRef.pet.hp = 0;
              ps.ownerRef.pet.reviveTimer = 30000; // 30s revive
              ps.setAlpha(0.2);
              this.showDamageNumber(ps.x, ps.y - 20, 'PET DIED!', '#ef4444');
            }
          }
          break;
        }
      }

      // Update pet HP bar
      const hpPct = ps.ownerRef.pet.hp / ps.ownerRef.pet.maxHp;
      ps.hpBar.width = 20 * Math.max(0, hpPct);
      ps.hpBar.setPosition(ps.x, ps.y - 15);
    }
  }

  // ==================== ENEMIES (Multi-lane) ====================

  private updateEnemies(time: number, delta: number): void {
    for (const es of this.enemySprites) {
      // Telurion-only safety pin — runs BEFORE the active-skip below so
      // a dying / inactive boss with a stale tween can't slide below the
      // canvas mid-death-anim. User reported "Telurion under-map after
      // killing him" repeatedly even after the per-frame in-loop clamp;
      // moving the clamp out of the `if (!es.active) continue` path
      // closes the last drift window. Belt-and-suspenders: also clamp X
      // into the visible band so the death-pose sprite never anchors
      // off the right edge.
      if (es.scene && es.enemyRef?.defId === 'boss_telurion') {
        const minX = 60;
        const maxX = GAME_W - 60;
        if (es.x < minX || es.x > maxX) es.x = Phaser.Math.Clamp(es.x, minX, maxX);
        const targetY = LANE_Y[1];
        if (Math.abs(es.y - targetY) > 4) es.y = targetY;
        if (es.shadow) es.shadow.setPosition(es.x, es.y);
      }
      if (!es.active) continue;
      // Safety: kill any zombie — enemy at 0 HP that wasn't cleaned up by normal damage paths
      if (es.enemyRef.currentHp <= 0) {
        const killer = this.heroSprites.find((h) => !h.heroRef.isDead) ?? this.heroSprites[0];
        if (killer) { this.killEnemy(es, killer); }
        else {
          const idx = this.enemySprites.indexOf(es);
          if (idx !== -1) this.enemySprites.splice(idx, 1);
          es.setActive(false);
          es.setVisible(false);
          es.shadow?.destroy(); // shadow was leaking when no living hero existed
        }
        continue;
      }

      // Goblino has its own update logic — skip normal enemy AI
      if (es.isGoblino) continue;

      // Process DoT damage before expiring debuffs.
      // Use a float accumulator per dot to avoid rounding to 0 every frame at
      // 60fps, and a separate per-second display accumulator so the floating
      // numbers read as a chunk ("232") rather than a stream of small ticks.
      // Per-dot damage is also fed into the DPS meter (attributed via
      // dot.sourceHeroId / dot.sourceLabel — set on Fireball / Eruption /
      // Magmarath's Ember etc.).
      const dots = es.enemyRef.debuffs.filter((d) => d.type === 'dot');
      let dotKilled = false;
      if (dots.length > 0) {
        let dmgThisFrame = 0;
        for (const dot of dots) {
          const dotFrameDmg = dot.value * (delta / 1000);
          dmgThisFrame += dotFrameDmg;
          // Attribute fractional dot damage to the source hero in the DPS
          // meter. trackDps clamps to 0 internally, but we only call when
          // the slice is meaningful to keep the meter clean.
          if (dot.sourceHeroId && dotFrameDmg > 0) {
            this.trackDps(dot.sourceHeroId, dotFrameDmg, dot.sourceLabel ?? 'dot');
          }
        }
        const prevAccum = (es.enemyRef as any)._dotAccum || 0;
        const newAccum = prevAccum + dmgThisFrame;
        let wholeDmg = Math.floor(newAccum);
        (es.enemyRef as any)._dotAccum = newAccum - wholeDmg;
        // Evil Pact sigil: DoT ticks can crit (10% roll, ×2 damage).
        // Combat-behavior sigil — only active while the main (first-deployed) hero is alive.
        let dotCrit = false;
        if (wholeDmg > 0 && this.getSigilEffects().specials.has('evil_pact')
            && this.getMainHeroSprite() && Math.random() < 0.10) {
          wholeDmg *= 2;
          dotCrit = true;
        }
        if (wholeDmg > 0) {
          es.enemyRef.currentHp -= wholeDmg;
          // Display accumulator: collect ~1 second of DoT damage and emit a
          // single floating number per enemy. Crits flash immediately so the
          // player still sees the proc, normal ticks roll up.
          const enemyAny = es.enemyRef as any;
          if (dotCrit) {
            this.showDamageNumber(es.x, es.y - 20, `${wholeDmg}!`, '#fbbf24', '13px');
          } else {
            enemyAny._dotDisplayAccum = (enemyAny._dotDisplayAccum ?? 0) + wholeDmg;
            const lastShow = enemyAny._dotDisplayLast ?? 0;
            if (time - lastShow >= 1000 && enemyAny._dotDisplayAccum >= 1) {
              this.showDotProc(es.x + (Math.random() - 0.5) * 14, es.y - 20,
                `${enemyAny._dotDisplayAccum}`, '#f97316', '13px');
              enemyAny._dotDisplayAccum = 0;
              enemyAny._dotDisplayLast = time;
            }
          }
          // Bleed VFX: play hemorrhage sprite periodically on bleeding enemies
          if ((es.enemyRef as any)._bleeding && Math.random() < 0.06) {
            this.vfx.playBleed(es.x, es.y);
          }
          if (es.enemyRef.currentHp <= 0) dotKilled = true;
        }
      } else {
        (es.enemyRef as any)._dotAccum = 0;
        (es.enemyRef as any)._dotDisplayAccum = 0;
        (es.enemyRef as any)._bleeding = false; // clear when no active dots
      }
      // If DoT dealt the killing blow, credit the first alive hero (or any hero as fallback)
      if (dotKilled) {
        const dotKiller = this.heroSprites.find((h) => !h.heroRef.isDead) ?? this.heroSprites[0];
        if (dotKiller) {
          this.killEnemy(es, dotKiller);
          continue;
        }
        // No heroes at all — force-deactivate so it can't keep attacking or blocking targeting
        es.setActive(false);
        es.setVisible(false);
        continue;
      }

      processDebuffs(es.enemyRef, delta);

      // Rot Bomber: DoTs + debuff decay already handled above, but no attack AI,
      // no targeting, no movement here (updateRotBombers handles movement + the
      // low-HP special). Skipping the rest of updateEnemies keeps it from ever
      // attacking heroes — its only damage is the death blast.
      if (es.isRotBomber) continue;

      // Taunt timer: tick down and clear when expired
      const tauntRemaining = (es.enemyRef as any)._tauntTimer as number | undefined;
      if (tauntRemaining !== undefined && tauntRemaining > 0) {
        (es.enemyRef as any)._tauntTimer = tauntRemaining - delta;
        if ((es.enemyRef as any)._tauntTimer <= 0) {
          (es.enemyRef as any)._tauntedTarget = undefined;
        }
      }

      // ── Gorgon Queen: strip stun before tint check — immune to stun (slows still apply) ──
      if (es.enemyRef.defId === 'boss_gorgon') {
        const hadStun = es.enemyRef.debuffs.some((d) => d.type === 'stun');
        if (hadStun) {
          es.enemyRef.debuffs = es.enemyRef.debuffs.filter((d) => d.type !== 'stun');
          if (!es.getData('immuneShown')) {
            es.setData('immuneShown', true);
            this.showDamageNumber(es.x, es.y - 70, 'IMMUNE', '#9ca3af');
            this.time.delayedCall(700, () => { if (es.active) es.setData('immuneShown', false); });
          }
        }
      }

      // ── Void Elf Warlord (boss_voidelf): immune to Stun and Fear ──
      if (es.enemyRef.defId === 'boss_voidelf') {
        const hadStunOrFear = es.enemyRef.debuffs.some((d) => d.type === 'stun' || d.type === 'fear');
        if (hadStunOrFear) {
          es.enemyRef.debuffs = es.enemyRef.debuffs.filter((d) => d.type !== 'stun' && d.type !== 'fear');
          if (!es.getData('immuneShown')) {
            es.setData('immuneShown', true);
            this.showDamageNumber(es.x, es.y - 70, 'IMMUNE', '#9ca3af');
            this.time.delayedCall(700, () => { if (es.active) es.setData('immuneShown', false); });
          }
        }
      }

      // ── Shadow Realm bosses (boss_demon1, boss_demon2): immune to Fear ──
      if (es.enemyRef.defId === 'boss_demon1' || es.enemyRef.defId === 'boss_demon2') {
        const hadFear = es.enemyRef.debuffs.some((d) => d.type === 'fear');
        if (hadFear) {
          es.enemyRef.debuffs = es.enemyRef.debuffs.filter((d) => d.type !== 'fear');
          if (!es.getData('immuneShown')) {
            es.setData('immuneShown', true);
            this.showDamageNumber(es.x, es.y - 70, 'IMMUNE', '#9ca3af');
            this.time.delayedCall(700, () => { if (es.active) es.setData('immuneShown', false); });
          }
        }
      }

      // Minotaur 50% stun resistance is applied at debuff creation time (see applyDebuff in debuffs.ts)

      // ── Lava Golem (boss_lava_golem): immune to Stun, Fear, AND Slow ──
      if (es.enemyRef.defId === 'boss_lava_golem') {
        const hadCC = es.enemyRef.debuffs.some((d) => d.type === 'stun' || d.type === 'fear' || d.type === 'slow');
        if (hadCC) {
          es.enemyRef.debuffs = es.enemyRef.debuffs.filter((d) => d.type !== 'stun' && d.type !== 'fear' && d.type !== 'slow');
          if (!es.getData('immuneShown')) {
            es.setData('immuneShown', true);
            this.showDamageNumber(es.x, es.y - 70, 'IMMUNE', '#ff6600');
            this.time.delayedCall(700, () => { if (es.active) es.setData('immuneShown', false); });
          }
        }
      }

      // Stunned enemies can't move or attack
      if (isStunned(es.enemyRef)) {
        es.setTint(0xffff00); // Yellow tint for stun
        if (es.anims.isPlaying) es.anims.pause(); // freeze animation while stunned
      } else {
        // Telurion is locked out of AI movement + attack while he's mid-descent:
        // the y-tween owns sprite.y until landing completes, so any X-walk,
        // target adjustment, or auto-attack would fight the tween or strand him
        // at a partial y. Cleared by the descent's onComplete + watchdog.
        const isDescending = !!es.getData('telurionDescending');

        // Ice-blue tint for frost-slowed or _frozen enemies, clear otherwise (fear overrides below)
        const isSlowed = es.enemyRef.debuffs.some((d) => d.type === 'slow');
        const isFrostFrozen = !!(es.enemyRef as any)._frozen;
        if (isFrostFrozen || isSlowed) {
          es.setTint(0x88bbff); // frost-blue tint
        } else {
          this.restoreEnemyTint(es);
        }
        if (es.anims.isPaused) {
          es.anims.resume();
          // If stun ended mid-attack-anim, restart walk so enemy doesn't loop attack
          const walkKey = `${es.texture.key}_walk`;
          const atkKey  = `${es.texture.key}_attack`;
          if (es.anims.currentAnim?.key === atkKey && this.anims.exists(walkKey)) es.play(walkKey);
        }

        const isRangedEnemy = es.enemyRef.attackRange === 'ranged';
        // Telurion sits at a SEMI-RANGE band (180-280 px) so he reads as a
        // hovering caster — close enough to threaten the front row but far
        // enough that he has room to keep launching Stormcall / Verdant
        // Spike / Quake Roar instead of being mobbed in melee.
        const isTelurion = es.enemyRef.defId === 'boss_telurion';
        // Per-frame safety-net y-pin for Telurion. The 600 ms post-spawn
        // pinning to LANE_Y[1] (y=318) can race against scene events
        // (death-tween restarts, retreat, run-end), and the user has
        // repeatedly reported "Telurion under-map" even after the descent
        // path was ripped out. Lock his y to lane 1 every frame so no
        // future code path can shove him into a lane that clips below the
        // bottom HUD bar at y≈526 (sprite is 290 px, anchor bottom — at
        // lane 3 (y=430) his feet would still be visible but very close
        // to the HUD edge; pinning to lane 1 leaves a comfortable margin).
        if (isTelurion && Math.abs(es.y - LANE_Y[1]) > 4) {
          es.y = LANE_Y[1];
          if (es.shadow) es.shadow.setPosition(es.x, es.y);
        }
        // Ranged enemies keep distance: 380–580 px from target
        // Boss Gorgon gets a wider melee range (large sprite)
        // Malakhar (Shadowlord, stage-50 endboss) sits at a wide caster band
        // 320–520 px so the player faces a "ranged dread caster" silhouette
        // instead of him strolling into the formation for melee swings.
        // The phase abilities (fire-trap, shadow-veil summon, kill-zone)
        // own his offence — the caster band keeps him in the right read.
        // EXCEPT in Phase 3 (<50% HP, `_phase3Melee` flag): he commits to
        // melee, chases the front row, and the kill-zone now actually
        // threatens because heroes are nearby.
        const isShadowlord = es.enemyRef.defId === 'boss_shadowlord';
        const isShadowlordMelee = isShadowlord && !!(es.enemyRef as any)._phase3Melee;
        const atkRange    = isTelurion ? 280
          : (isShadowlord && !isShadowlordMelee) ? 520
          : isShadowlord ? 130              // melee phase: large boss sprite needs a slightly wider melee than 80
          : isRangedEnemy ? 580
          : (es.enemyRef.defId === 'boss_gorgon' || es.enemyRef.defId === 'boss_lava_golem') ? 110
          : 80;
        const minRange    = isTelurion ? 180
          : (isShadowlord && !isShadowlordMelee) ? 320
          : isShadowlord ? 0                // melee phase: no minimum, close the gap
          : isRangedEnemy ? 380
          : 0;   // ranged enemies back off if hero closes in

        // Find target hero and dynamically adjust movement destination
        const hasFear = es.enemyRef.debuffs.some((d) => d.type === 'fear');
        const target = hasFear ? null : this.findNearestHero(es);

        // Boss demon freezes in place while channeling beam
        const isBossBeaming = es.enemyRef.defId === 'boss_demon1' && ((es.getData('beaming') as boolean) || false);

        // Melee enemies walk toward target; ranged enemies hold at atkRange distance.
        // Telurion + (caster-band) Malakhar use the ranged-band path even
        // though their data entries aren't tagged ranged — both are caster
        // bosses whose phase abilities own their offence; melee chase would
        // corrupt the read. Phase 3 Malakhar SWITCHES off the ranged band
        // and chases the front row like a normal melee boss.
        const useRangedBand = isRangedEnemy || (isShadowlord && !isShadowlordMelee);
        // Melee enemies walk toward target; ranged enemies hold at atkRange distance
        if (!isBossBeaming && target && !useRangedBand) {
          // Stop one atkRange "buffer" to the right of the hero — normal approach from spawn.
          // If the enemy has overshot PAST the hero (initial spawn targetX landed past a front-row
          // hero's position), the Math.min clamp used to trap them there forever, unable to walk
          // back to attack. Now: snap targetX to the correct side based on current position so
          // enemies can close the gap from either direction without chasing a charging warrior.
          // Clamp the melee approach points to the on-screen band so rear-flankers
          // attacking a back-left hero (x≈50) don't stop at approachLeftX = -6
          // (half off-screen / invisible). The attack hit check uses atkRange so
          // an enemy pinned to minLeftX (~66) is still inside range of a x=50 hero.
          const approachRightX = Math.min(es.maxRightX, target.x + atkRange * 0.7); // stop here when approaching from the right
          const approachLeftX  = Math.max(es.minLeftX,  target.x - atkRange * 0.7); // stop here if we overshot to the left
          if (es.x > approachRightX) {
            es.targetX = approachRightX;
          } else if (es.x < approachLeftX) {
            es.targetX = approachLeftX;
          } else {
            // already inside the attack band — hold position
            es.targetX = es.x;
          }
        } else if (!isBossBeaming && target && useRangedBand) {
          // Distance check uses baseX to avoid reacting to warrior charge position.
          // Signed distance lets rear-flank ranged enemies back off to the LEFT
          // instead of walking through the hero line; the sign preserves which
          // side of the hero the enemy came from.
          const anchorX = target.baseX ?? target.x;
          const distSigned = es.x - anchorX;
          const absDist = Math.abs(distSigned);
          const sign = distSigned < 0 ? -1 : 1;
          if (absDist < minRange) {
            // Too close — back off to our side (right for right-spawners, left for rear flankers).
            es.targetX = anchorX + sign * (minRange + 20);
          } else if (absDist > atkRange) {
            // Too far — close the gap on our side.
            es.targetX = anchorX + sign * (atkRange - 20);
          }
          // else: already in range — stay put
          // Clamp targetX to the screen so ranged enemies don't walk off the edge
          // when a vanguard is too close. Both edges are tier-aware (sprite half-
          // width + 12 px margin, set at spawn) so a boss 230 px wide never gets
          // pushed with half its body off-screen, AND rear-flank ranged don't
          // settle at x=-60 invisible behind the left edge.
          es.targetX = Math.max(es.minLeftX, Math.min(es.maxRightX, es.targetX));
        }

        // Movement: getEffectiveSpeed handles slow and fear (negative = backward)
        // Relic: Frostgrasp Totem — slow scales with the relic's level (1% per
        // level, max 50% at lv 50). Take the strongest aura among any alive
        // hero so a higher-level copy on a back-row caster wins over a
        // lower-level copy on the tank.
        let frostgraspSlowPct = 0;
        for (const h of this.heroSprites) {
          if (h.heroRef.isDead) continue;
          const pct = this.getRelicBonuses(h.heroRef).frostgraspSlowPct;
          if (pct > frostgraspSlowPct) frostgraspSlowPct = pct;
        }
        const frostgraspMult = 1 - frostgraspSlowPct;
        // Gorgon Queen fury rush: 2× speed for a short window after Stone Gaze (slow still applies)
        const gorgonRushMult = (es.enemyRef as any)._gorgonRush > 0 ? 2.0 : 1.0;
        // Freezing Shot progressive slow: starts at 0 speed, linearly recovers to full over duration
        let frostSlowMult = 1;
        if ((es.enemyRef as any)._frozen) {
          const frozenStart = (es.enemyRef as any)._frozenStartTime as number;
          const frozenDur = (es.enemyRef as any)._frozenDuration as number;
          const elapsed = time - frozenStart;
          frostSlowMult = Math.min(1, elapsed / frozenDur); // 0.0 → 1.0
        }
        // Lieutenant buff: nearby faction-allies get +20 % move speed while the
        // Lieutenant's aura is alive (flag re-stamped per 500 ms tick).
        const lieutSpeedUntil = (es.enemyRef as any)._lieutenantSpeedUntil ?? 0;
        const lieutSpeedMult = time < lieutSpeedUntil ? 1.20 : 1;
        // Global enemy move-speed multiplier. User wants every monster 1.5×
        // faster across the entire game (boss + minion + elite + summon
        // alike). Applied at the consumption site so the shared util's
        // return value stays pure (debuff math + tests still see canonical
        // numbers); only the BattleScene movement loop scales it.
        const GLOBAL_ENEMY_SPEED_MULT = 1.5;
        const speed = getEffectiveSpeed(es.enemyRef) * frostgraspMult * gorgonRushMult * frostSlowMult * lieutSpeedMult * GLOBAL_ENEMY_SPEED_MULT;
        const moveDt = delta / 16.67;
        const texKey = es.texture.key;
        const walkKey = `${texKey}_walk`;
        const idleKey = `${texKey}_idle`;

        if (speed < 0) {
          // Fear — flee back toward the edge this enemy spawned from. Right-side
          // spawners (default) flee right to ENEMY_SPAWN_X + 60; rear flankers
          // flee LEFT toward the left edge so they don't run through the hero line.
          const rearFlank = es.getData('spawnSide') === 'rear';
          if (rearFlank) {
            // Fleeing rear flankers allowed slightly off-screen so they exit the
            // map visually, matching the right-side flee behaviour (enemy leaves).
            es.x = Math.max(-80, es.x + speed * 0.5 * moveDt);
            es.setFlipX(true); // face left while fleeing left
          } else {
            es.x = Math.min(GAME_W + 60, es.x - speed * 0.5 * moveDt);
            es.setFlipX(false); // face right while fleeing right
          }
          es.setTint(0x4090c0);
          if (this.anims.exists(walkKey) && es.anims.currentAnim?.key !== `${texKey}_attack`) es.play(walkKey);
        } else {
          // Face the current target — flip LEFT (flipX = true) when the target is to our left,
          // flip RIGHT (flipX = false) when we've pushed past and the target is behind us.
          // Falls back to facing left (toward hero base) if no target is selected.
          if (target) {
            es.setFlipX(target.x < es.x);
          } else {
            es.setFlipX(true);
          }

          const distToTarget = Math.abs(es.x - es.targetX);
          const isMoving = distToTarget > 4;
          const curAnimKey = es.anims.currentAnim?.key ?? '';
          // Broad detection: any anim key ending in _attack / _charge / _cast, or containing
          // "attack" in it (boss_gorgon_attack1/2/3, wolf_lord_attack etc). Prevents the move
          // block from stomping a specialized attack anim (boss_gorgon_attack3 = stone gaze)
          // which previously wasn't in the hard-coded allow-list, leaving enemies stuck in
          // walk while a boss-cast anim should be playing.
          const isAttackAnim = !!curAnimKey
            && (curAnimKey.endsWith('_attack')
                || curAnimKey.includes('_attack')
                || curAnimKey.endsWith('_charge')
                || curAnimKey.endsWith('_cast')
                // Telurion's landing anim must be treated as uninterruptible —
                // if the AI loop stomps it with walk, animationcomplete never
                // fires, the y-snap inside onLandingDone never runs, and the
                // dragon's body can settle below the lane (rendering "under
                // map" because the bottom HUD bar at depth 10 occludes him).
                || curAnimKey.endsWith('_landing'));

          // Pre-compute "is the target in melee strike distance right now?"
          // Lifted ABOVE the y-tracker so y-track also pauses when a mob is
          // already in strike range — otherwise the body kept drifting
          // vertically while the legs were still (minotaur "sliding" bug).
          // Per user "should not slide never" — position must NEVER change
          // without an active move animation.
          const dxNow = target ? es.x - target.x : Infinity;
          const dyNow = target ? es.y - target.y : Infinity;
          const meleeInRangeNow = !!target && !isRangedEnemy
            && (dxNow * dxNow + dyNow * dyNow) < atkRange * atkRange;
          // Y tracking for melee enemies — runs EVERY frame regardless of
          // X-isMoving so a wolf that already X-aligned still closes the Y
          // gap. NOW also gated on !meleeInRangeNow so the body doesn't
          // drift vertically while the sprite is held on an attack frame
          // (the minotaur slide). Walk anim is played alongside the y-step
          // so the legs visibly run during the y-close.
          const walkKeyForY = `${texKey}_walk`;
          if (target && !isRangedEnemy && !isAttackAnim && !meleeInRangeNow) {
            const dy = target.y - es.y;
            if (Math.abs(dy) > 5) {
              es.y += Math.sign(dy) * speed * 0.3 * moveDt;
              if (this.anims.exists(walkKeyForY) && es.anims.currentAnim?.key !== walkKeyForY
                  && es.anims.currentAnim?.key !== `${texKey}_attack`) {
                es.play(walkKeyForY, true);
              }
            }
          }
          if (isMoving && !isAttackAnim && !isDescending && !meleeInRangeNow) {
            // Move toward targetX — frozen during attack animation to prevent sliding,
            // and frozen during Telurion's descent so the y-tween owns the sprite.
            // ALSO frozen when a melee mob is already in strike range so it
            // doesn't shuffle inside the kill box looking like it's pacing.
            const dir = es.x > es.targetX ? -1 : 1;
            es.x += dir * speed * 0.5 * moveDt;
            // Wolves get a dedicated run cycle while closing the gap so their advance
            // reads as a feral sprint, not a casual trot. White Claw (whitewolf) and
            // every black-werewolf-textured mob both get their own run sheet.
            const wolfRunKey = texKey === 'enemy_whitewolf' ? 'enemy_whitewolf_run'
              : texKey === 'enemy_wolf' ? 'enemy_wolf_run'
              : null;
            const moveKey = (wolfRunKey && this.anims.exists(wolfRunKey)) ? wolfRunKey : walkKey;
            if (this.anims.exists(moveKey)) {
              es.play(moveKey, true);
            }
          } else if (!isMoving && !isAttackAnim
              && (curAnimKey === walkKey || curAnimKey === 'enemy_whitewolf_run' || curAnimKey === 'enemy_wolf_run')) {
            // Arrived at position — switch to idle.
            // Wolves used to skip idle entirely (sliding-on-4-legs read wrong),
            // but parking on the run cycle while standing in melee made the
            // wolf "still running" between attack flashes — the user reported
            // he was never seeing the actual attack pose. Now: wolves DO drop
            // to idle (upright werewolf "ready stance") whenever they're in
            // melee strike range, which gives the attack anim a clean upright
            // pose to launch from instead of the 4-legged run-bite frame.
            // Out-of-range wolves still skip idle so the closing approach
            // reads as a non-stop sprint.
            const isWolfType = texKey === 'enemy_wolf' || texKey === 'enemy_whitewolf';
            const wolfShouldIdle = isWolfType && meleeInRangeNow;
            if (this.anims.exists(idleKey)
                && (wolfShouldIdle || (!isWolfType && !meleeInRangeNow))) {
              es.play(idleKey);
            }
          }
          // Failsafe: any melee mob inside strike range while still on a
          // moving anim drops to idle so the upcoming attack tick has a
          // clean pose to launch from. Covers walk + both wolf-run keys;
          // non-wolves with a walk-anim mid-approach also benefit.
          if (meleeInRangeNow && !isAttackAnim
              && (curAnimKey === walkKey
                  || curAnimKey === 'enemy_whitewolf_run'
                  || curAnimKey === 'enemy_wolf_run')
              && this.anims.exists(idleKey)) {
            es.play(idleKey);
          }
        }

        // ── Wolf state-machine override (runs LAST in the move-anim block,
        // so its play() calls win over y-tracker / move-anim / arrived /
        // failsafe above). The user repeatedly hit "wolf still running, no
        // attack pose" because the older logic was a patchwork; this is a
        // single deterministic resolver that maps the wolf's relationship
        // to its current target → exactly one anim, every frame.
        //   - mid-attack (anim still playing & not stale): don't touch it
        //   - in melee strike range: force IDLE (upright werewolf "ready"
        //     stance between bites); attack-firing block below replaces
        //     this with the attack anim on each interval tick
        //   - out of range: force RUN (4-legged sprint while closing)
        // Stunned / feared / descending wolves are skipped so other systems
        // (stun pause, fear flee anim) still own the sprite. */
        {
          const isWolfTexture = texKey === 'enemy_wolf' || texKey === 'enemy_whitewolf';
          if (isWolfTexture && target && !hasFear && !isDescending) {
            const dxW = es.x - target.x;
            const dyW = es.y - target.y;
            const inRangeW = (dxW * dxW + dyW * dyW) < atkRange * atkRange;
            const curW = es.anims.currentAnim?.key ?? '';
            const atkAnimKeyW = `${texKey}_attack`;
            const idleKeyW    = `${texKey}_idle`;
            const runKeyW = texKey === 'enemy_wolf' ? 'enemy_wolf_run' : 'enemy_whitewolf_run';
            // Trust the atkAnimStartTime stamp set by the attack-firing
            // block as the source of truth — `es.anims.isPlaying` can
            // be false for one frame immediately after `play()` (Phaser
            // queues the anim but the ticker doesn't flip the flag
            // until the next tick), and that one-frame gap was enough
            // for this state machine to fire `play(idleKeyW)` and stomp
            // the attack anim that had just been queued by the
            // attack-firing block. Stamp + watchdog window is the same
            // 1.5 s the attack-firing block uses to detect a stale
            // animation, so the two stay in sync. Also explicitly
            // recognise a freshly-set startTime when curW hasn't yet
            // flipped to the attack key (queued-but-not-rendered).
            const atkStartW = (es.getData('atkAnimStartTime') as number) ?? 0;
            const recentAtkStamp = atkStartW > 0 && (time - atkStartW) <= 1500;
            const isAtkPlayingW = recentAtkStamp || curW === atkAnimKeyW;
            if (isAtkPlayingW) {
              // mid-swing: leave it alone
            } else if (inRangeW) {
              if (curW !== idleKeyW && this.anims.exists(idleKeyW)) {
                es.play(idleKeyW);
              }
            } else {
              if (this.anims.exists(runKeyW) && curW !== runKeyW) {
                es.play(runKeyW);
              }
            }
          }
        }

        // Only attack if not stunned/feared, and not Telurion mid-descent.
        // Malakhar's Phase-2 Shadow Veil also disables his auto-attack — he's
        // entirely focused on tearing rifts to summon Shadow Realm bosses,
        // so any standard swing during the veil would break the read.
        // Standard auto-attack is suppressed for Malakhar's caster phases —
        // his auto-attack is a custom traveling shadow orb (handled in his
        // dedicated phase block below) that fires from caster range with
        // damage chained to the orb's arrival. In PHASE 3 (melee mode) the
        // standard auto-attack is re-enabled so he swings at heroes in the
        // formation like a normal melee boss.
        const isShadowlordVeiled = isShadowlord && !!(es.enemyRef as any)._invuln;
        const suppressShadowlordStandardAtk = isShadowlord && !isShadowlordMelee;
        if (!hasFear && !isDescending && !isShadowlordVeiled && !suppressShadowlordStandardAtk) {
          // Use actual target.x — enemies must be able to hit the warrior wherever he physically is.
          // Melee enemies must be in range on BOTH axes — without the Y check, a wolf in the top
          // lane (y=262) could bite a hero in the bottom lane (y=430) the second they crossed the
          // same x, even though they were 168 px apart vertically. Ranged enemies keep the X-only
          // check since they shoot from any lane.
          const dxAtk = target ? es.x - target.x : Infinity;
          const dyAtk = target ? es.y - target.y : Infinity;
          const inAtkRange = isRangedEnemy
            ? Math.abs(dxAtk) < atkRange
            : (dxAtk * dxAtk + dyAtk * dyAtk) < atkRange * atkRange;
          if (target && inAtkRange) {
            const data = es.getData('lastAtk') as number || 0;
            const baseInterval = getAttackInterval(es.enemyRef.speed);
            // Rank-7 Earthbind extra: slowed enemies attack 25 % slower (flag refreshed
            // by the earthbind tick each pulse; any stale value just expires on its own).
            const atkSlowUntil = (es.enemyRef as any)._atkSpdSlowUntil ?? 0;
            const atkSlowMult = time < atkSlowUntil ? 0.75 : 1;
            // Lieutenant buff: nearby allies attack 25 % faster while in the aura.
            const lieutAtkSpdUntil = (es.enemyRef as any)._lieutenantAtkSpdUntil ?? 0;
            const lieutAtkSpdMult = time < lieutAtkSpdUntil ? 1.25 : 1;
            const interval = Math.round(baseInterval / ((es.enemyRef.attackSpeedMult || 1) * atkSlowMult * lieutAtkSpdMult));
            // Don't queue a new attack while the previous swing anim is still playing — keeps
            // each hit feeling deliberate instead of stuttering mid-animation. Applies to all
            // enemy types so minions, elites, bosses and summons behave consistently.
            // Watchdog — if an enemy's attack anim has been "playing" for > 1.5 s (way past
            // any natural swing duration), treat the gate as cleared. This catches the
            // "hero got swapped mid-swing, animationcomplete listener was consumed, enemy
            // is now stuck in attack state forever" bug the user reported — where mobs in
            // melee range visibly walk/run but never swing again until a page reload.
            const atkAnimStartedAt = (es.getData('atkAnimStartTime') as number) ?? 0;
            const atkAnimStale = atkAnimStartedAt > 0 && (time - atkAnimStartedAt) > 1500;
            const pendingAtkAnim = es.anims.currentAnim?.key === `${texKey}_attack`
              && es.anims.isPlaying && !atkAnimStale;
            // Extra belt-and-suspenders: if the enemy has been "in atkRange" without
            // landing a swing for > 3× the nominal interval (stale `lastAtk`), force a
            // walk-anim reset so the rest of the state machine can re-latch cleanly.
            const attackOverdue = data > 0 && (time - data) > interval * 3;
            if (attackOverdue && es.anims.currentAnim?.key === `${texKey}_attack`) {
              es.off('animationcomplete');
              if (this.anims.exists(walkKey)) es.play(walkKey);
              es.setData('atkAnimStartTime', 0);
            }
            if (!pendingAtkAnim && time - data >= interval) {
              es.setData('lastAtk', time);
              es.setData('atkAnimStartTime', time);

              // Each enemy uses its own spritesheet attack frames.
              // Previously goblin/ghoul used a shared Elf_3 sheet (enemy_ranged_shoot) which made them
              // briefly look like Sylvari — now they use their own orc/skeleton attack animation.
              let atkKey: string;
              if (es.enemyRef.defId === 'boss_gorgon') {
                const toggle = es.getData('gorgonAtkToggle') as boolean || false;
                atkKey = toggle ? 'boss_gorgon_attack2' : 'boss_gorgon_attack1';
                es.setData('gorgonAtkToggle', !toggle);
              } else {
                atkKey = `${texKey}_attack`;
              }
              // Wolves return to their run cycle (not the shared walk) after each attack swing.
              const wolfReturnRunKey = texKey === 'enemy_whitewolf' && this.anims.exists('enemy_whitewolf_run')
                ? 'enemy_whitewolf_run'
                : texKey === 'enemy_wolf' && this.anims.exists('enemy_wolf_run')
                  ? 'enemy_wolf_run'
                  : null;
              // When a wolf finishes its swing while still in melee strike
              // range, return to IDLE (upright "ready stance") instead of the
              // 4-legged run cycle — otherwise the post-swing run frames
              // dominated the screen and the user perceived the wolf as
              // "still running, not attacking". Out-of-range wolves keep the
              // run return so a wolf knocked back / pushed off resumes its
              // sprint toward the new target.
              const isWolfTex = texKey === 'enemy_wolf' || texKey === 'enemy_whitewolf';
              const wolfIdleReturn = isWolfTex && inAtkRange && this.anims.exists(idleKey);
              // Archivist used to skip idle here because his idle was seated;
              // since the idle is now sourced from a standing-pose sheet, he
              // can return to idle like every other ranged enemy.
              const returnKey = (isRangedEnemy && this.anims.exists(idleKey))
                ? idleKey
                : wolfIdleReturn
                  ? idleKey
                  : (wolfReturnRunKey ?? walkKey);
              if (this.anims.exists(atkKey) && es.anims.currentAnim?.key !== atkKey) {
                // Clear any pending listener so rapid cycle doesn't stack callbacks;
                // then play attack fresh from frame 0 and wire a single completion handler
                // that returns to walk/run/idle.
                es.off('animationcomplete');
                es.play(atkKey);
                es.once('animationcomplete', () => {
                  // Clear the watchdog stamp so the next attack-gate check sees a fresh
                  // state (not a stale "anim has been playing for 1.5 s" trip).
                  es.setData('atkAnimStartTime', 0);
                  if (es.active && this.anims.exists(returnKey)) es.play(returnKey);
                });
              }
              // Auto-attack projectile VFX for ranged enemies. Spawn point sits on whichever
              // side the enemy is facing (es.flipX=true → facing left → arrow leaves from left
              // of body, else right) so the arrow never appears to fire from the wrong hand.
              if (target && es.enemyRef.attackRange === 'ranged') {
                const facingLeft = es.flipX;
                const armOffsetX = facingLeft ? -20 : 20;
                const spawnX = es.x + armOffsetX;
                const spawnY = es.y - 50;
                const tgtY = target.y - 40;
                const id = es.enemyRef.defId;
                if (id === 'boss_demon2') {
                  // Demon Overlord: hellfire ball
                  this.vfx.playHellfireBall(es.x + (facingLeft ? -30 : 30), es.y - 60, target.x, tgtY);
                } else if (['goblin', 'orc_shaman'].includes(id)) {
                  // Orc family — green poison arrow
                  this.vfx.playPoisonArrow(spawnX, spawnY, target.x, tgtY);
                } else if (['wraith', 'lich', 'spectre'].includes(id)) {
                  // Undead archers (skeleton archer, wraith elder, spectre) — plain arrow
                  this.vfx.playArrow(spawnX, spawnY, target.x, tgtY);
                } else if (id === 'boss_skeleton_mage') {
                  // The Archivist lich — frost-school caster: icy shard projectile.
                  this.vfx.playFrostbolt(spawnX, spawnY, target.x, tgtY);
                } else if (['imp', 'succubus', 'demoniac_ghost', 'dragon', 'whelp'].includes(id)) {
                  // Fire-breathers (demons + dragons) — hellfire ball
                  this.vfx.playHellfireBall(spawnX, spawnY, target.x, tgtY);
                } else if (['void_worm', 'boss_lava_golem'].includes(id)) {
                  // Void / lava — void lance projectile
                  this.vfx.playVoidLance(spawnX, spawnY, target.x, tgtY);
                } else {
                  // Fallback for any other ranged enemy — plain arrow
                  this.vfx.playArrow(spawnX, spawnY, target.x, tgtY);
                }
              }

              // ── Summon target shortcut ─────────────────────────────────────
              // If the enemy's pick was a SummonSprite (no `heroRef`), route
              // through the thin summon damage pipeline — defense-mitigated
              // single hit, no shields/dodge/resists/holy-shield/armory.
              // The `'heroRef' in target` predicate narrows TypeScript so the
              // remaining else-if branches all see target as HeroSprite.
              if (!('heroRef' in target)) {
                if (!this.gsm.godMode) {
                  this.applyDamageToSummon(target, es.enemyRef.attack);
                }
              } else if (this.gsm.godMode) {
                // God mode active — heroes take no damage
              } else if (this.rollGeneralDodge(target, /* isMelee */ true)) {
                // rollGeneralDodge surfaces the DODGE popup itself
              } else {
                // Apply defensive tome + consumable bonuses before taking hit
                const armorTomeBonus = Math.floor(getTomeBonus(this.gsm.player.tomes, 'armor'));
                // Elixir of Fortitude and Tome of Vitality are applied directly to hero.stats.maxHp
                if (armorTomeBonus > 0) target.heroRef.stats.defense += armorTomeBonus;
                const result = enemyAttackHero(es.enemyRef, target.heroRef, time);
                if (armorTomeBonus > 0) target.heroRef.stats.defense -= armorTomeBonus;
                // Zyphara Magma Shield — retaliatory stun on melee attacker. Melee damage
                // routes through enemyAttackHero (not applyDamageToHero) so the proc needs
                // its own hook here. DoT/special paths are covered by the hook inside
                // applyDamageToHero so bosses stabbing her with a frost nova tick stun too.
                if ((target.heroRef as any)._magmaShieldActive && result.damage > 0 && es.active) {
                  const stunMs = ((target.heroRef as any)._magmaShieldStunMs as number) || 500;
                  applyDebuff(es.enemyRef, { type: 'stun', value: 1, duration: stunMs, remaining: stunMs });
                  this.showDamageNumber(es.x, es.y - 20, 'STUNNED', '#f97316', '11px');
                  this.flashSprite(es);
                }
                // Holy Shield absorption: restore HP absorbed by the shield pool
                { const shHp = (target.heroRef as any)._shieldHp ?? 0;
                  if (shHp > 0 && result.damage > 0) {
                    const absorbed = Math.min(shHp, result.damage);
                    const newShield = shHp - absorbed;
                    (target.heroRef as any)._shieldHp = newShield;
                    if (newShield <= 0) (target.heroRef as any)._shieldSources = new Set();
                    target.heroRef.stats.hp += absorbed;
                    if (target.heroRef.isDead && target.heroRef.stats.hp > 0) {
                      target.heroRef.isDead = false;
                      const di = result.events.findIndex((e) => e.type === 'hero_death');
                      if (di >= 0) result.events.splice(di, 1);
                    }
                  }
                }
                // Shaman Spirit Shield — 3-stack flat absorb. Each hit consumes one stack;
                // mana shield returns mana, lightning shield reflects 5% damage back.
                { const ss = (target.heroRef as any)._shamanShield as { kind: 'mana' | 'lightning'; stacks: number; perStack: number; expiry: number } | undefined;
                  if (ss && time > ss.expiry) (target.heroRef as any)._shamanShield = undefined;
                  else if (ss && ss.stacks > 0 && result.damage > 0) {
                    const absorb = Math.min(ss.perStack, result.damage);
                    result.damage -= absorb;
                    ss.stacks -= 1;
                    if (ss.kind === 'mana') {
                      const maxMp = this.effStats(target.heroRef).maxMp;
                      target.heroRef.stats.mp = Math.min(maxMp, target.heroRef.stats.mp + Math.floor(absorb * 0.6));
                      this.vfx.playManaReturn(target.x, target.y);
                    } else {
                      const reflect = Math.max(1, Math.floor(result.damage * 0.05 + absorb * 0.05));
                      es.enemyRef.currentHp -= reflect;
                      this.vfx.playShieldReflect(target.x, target.y - 40, es.x, es.y - 40);
                      this.showDamageNumber(es.x, es.y - 20, `⚡${reflect}`, '#fde68a', '11px');
                      if (es.enemyRef.currentHp <= 0) {
                        const killer = this.heroSprites.find((h) => h.heroRef.id === target.heroRef.id) ?? this.heroSprites[0];
                        if (killer) this.killEnemy(es, killer);
                      }
                    }
                    // No text indicator per feedback — the stack pips next to the HP bar
                    // already communicate the shield state.
                    if (ss.stacks <= 0) {
                      // Shield broken — just zero the state. Orbit fades itself out on the
                      // next updateShamanShieldOrbit tick since kind becomes null.
                      (target.heroRef as any)._shamanShield = undefined;
                    }
                  }
                }
                let dmg = result.damage;
                // Trophy: Ironhide Pelt — 20% less melee damage for all heroes
                if (es.enemyRef.attackRange !== 'ranged' && this.hasTrophy('melee attacks')) {
                  dmg = Math.max(1, Math.floor(dmg * 0.8));
                }
                // Gorgon Queen: nature damage — reduced by natureResist
                if (es.enemyRef.defId === 'boss_gorgon') {
                  const nRes = Math.min(this.getHeroResist(target.heroRef, 'nature'), 60);
                  if (nRes > 0) dmg = Math.max(1, Math.floor(dmg * (1 - nRes / 100)));
                }
                // Skeleton Mage (Archivist): frost damage — reduced by frostResist
                if (es.enemyRef.defId === 'boss_skeleton_mage') {
                  const fRes = Math.min(this.getHeroResist(target.heroRef, 'frost'), 75);
                  if (fRes > 0) dmg = Math.max(1, Math.floor(dmg * (1 - fRes / 100)));
                }
                // Relic: Aegis of Stoneskin — reduce incoming damage %
                const rbDef = this.getRelicBonuses(target.heroRef);
                if (rbDef.dmgReduction > 0) dmg = Math.max(1, Math.floor(dmg * (1 - Math.min(rbDef.dmgReduction, 0.5))));
                // Relic: Bulwark Emblem — block 15% when above 50% HP
                if (rbDef.bulwarkPct > 0) {
                  const hpPctCheck = target.heroRef.stats.hp / this.effStats(target.heroRef).maxHp;
                  if (hpPctCheck > 0.5) dmg = Math.max(1, Math.floor(dmg * (1 - rbDef.bulwarkPct)));
                }
                // Shield Fortify: block X% of all incoming damage
                { const shieldW = target.heroRef.equipment.weapon2;
                  const fortifyMatch = shieldW?.specialEffect?.match(/Fortify.*Block (\d+)%/);
                  if (fortifyMatch) dmg = Math.max(1, Math.floor(dmg * (1 - parseInt(fortifyMatch[1]) / 100)));
                }
                // Relic: Timestop Pendant — prevent lethal damage once per 90s, freeze all enemies 4s
                if (rbDef.hasTimestopPendant && target.heroRef.stats.hp - dmg <= 0
                    && !(target.heroRef as any)._timestopOnCd) {
                  (target.heroRef as any)._timestopOnCd = true;
                  this.time.delayedCall(90000, () => { (target.heroRef as any)._timestopOnCd = false; });
                  dmg = target.heroRef.stats.hp - 1; // leave at 1 HP
                  this.showDamageNumber(target.x, target.y - 50, 'TIMESTOP!', '#22d3ee');
                  // Freeze all enemies for 4s + show hourglass icon above each
                  for (const fe of this.enemySprites) {
                    if (fe.active && fe.enemyRef.currentHp > 0) {
                      fe.enemyRef.debuffs.push({ type: 'stun', value: 1, duration: 4000, remaining: 4000 });
                      const hg = this.add.text(fe.x, fe.y - 44, '⏳', { fontSize: '16px' })
                        .setOrigin(0.5).setDepth(15);
                      this.time.delayedCall(4000, () => hg.destroy());
                    }
                  }
                }
                // Paladin Divine Reflect: reflect back (talent upgradeable %)
                if ((target.heroRef as any)._reflectActive) {
                  const reflectDmg = Math.floor(dmg * ((target.heroRef as any)._reflectPct || 0.4));
                  es.enemyRef.currentHp -= reflectDmg;
                  this.showDamageNumber(es.x, es.y - 20, `${reflectDmg}`, '#5090c0');
                  if (es.enemyRef.currentHp <= 0 && es.active) { this.killEnemy(es, target); continue; }
                }
                // Thorn of Reckoning sigil: 25% reflect on every incoming hit (stacks with paladin reflect).
                // Main-hero-only combat behaviour.
                if (this.getSigilEffects().specials.has('thorn_of_reckoning') && this.isMainHero(target)) {
                  const reflectAmt = Math.max(1, Math.floor(dmg * 0.25));
                  es.enemyRef.currentHp -= reflectAmt;
                  this.showDamageNumber(es.x, es.y - 30, `↩${reflectAmt}`, '#be123c');
                  if (es.enemyRef.currentHp <= 0 && es.active) { this.killEnemy(es, target); continue; }
                }
                // Thorns Tome: reflect % of incoming damage back
                const thornsPct = getTomeBonus(this.gsm.player.tomes, 'thorns');
                const thornsDmg = thornsPct > 0 ? Math.max(1, Math.floor(dmg * thornsPct)) : 0;
                if (thornsDmg > 0) {
                  es.enemyRef.currentHp -= thornsDmg;
                  this.showDamageNumber(es.x, es.y - 30, `↩${thornsDmg}`, '#be123c');
                  if (es.enemyRef.currentHp <= 0 && es.active) { this.killEnemy(es, target); continue; }
                }
                this.showDamageNumber(target.x + (Math.random() - 0.5) * 15, target.y - 20, `${dmg}`, '#ef4444');
                this.flashSprite(target);
                // Void Worm: poison DOT on hit (5 ticks) — nature damage, reduced by natureResist
                if (es.enemyRef.defId === 'void_worm') {
                  const halfRange = 290;
                  const isClose = Math.abs(es.x - target.x) < halfRange;
                  const nRes = Math.min(this.getHeroResist(target.heroRef, 'nature'), 60);
                  const rawTick = isClose ? Math.round(es.enemyRef.attack * 0.12) : Math.round(es.enemyRef.attack * 0.07);
                  const tickDmg = nRes > 0 ? Math.max(1, Math.floor(rawTick * (1 - nRes / 100))) : rawTick;
                  const ticks = 5;
                  this.showDamageNumber(target.x, target.y - 35, '☠ POISON', '#4ade80');
                  this.vfx.playPoisonDrip(target.x, target.y);
                  for (let t = 1; t <= ticks; t++) {
                    this.time.delayedCall(t * 800, () => {
                      if (!target.heroRef.isDead && !this.gsm.godMode) {
                        this.applyDamageToHero(target, tickDmg);
                        this.showDamageNumber(target.x + (Math.random()-0.5)*12, target.y - 20, `${tickDmg}`, '#4ade80');
                        if (target.heroRef.stats.hp <= 0) {
                          this.lastDeathSource = `Void Worm Poison`;
                          this.handleHeroDeath(target);
                        }
                      }
                    });
                  }
                }
                // Orc ranged enemies (Scout + Elder): poison arrow DoT — nature damage, 4 ticks
                if (['goblin', 'orc_shaman'].includes(es.enemyRef.defId)) {
                  const nRes = Math.min(this.getHeroResist(target.heroRef, 'nature'), 60);
                  const rawPoison = Math.round(es.enemyRef.attack * 0.05);
                  const poisonTick = nRes > 0 ? Math.max(1, Math.floor(rawPoison * (1 - nRes / 100))) : rawPoison;
                  this.showDamageNumber(target.x, target.y - 38, '☠ POISONED', '#4ade80');
                  this.vfx.playPoisonDrip(target.x, target.y);
                  for (let t = 1; t <= 4; t++) {
                    this.time.delayedCall(t * 1200, () => {
                      if (!target.heroRef.isDead && !this.gsm.godMode) {
                        this.applyDamageToHero(target, poisonTick);
                        this.showDamageNumber(target.x + (Math.random() - 0.5) * 14, target.y - 20, `${poisonTick}`, '#4ade80');
                        if (target.heroRef.stats.hp <= 0) {
                          this.lastDeathSource = `Poison Arrow (${es.enemyRef.name})`;
                          this.handleHeroDeath(target);
                        }
                      }
                    });
                  }
                }
                // Relic: Ward of the Guardian — chance to shield % of max HP on hit.
                // Scales 8% → 30% of max HP (lv 1 → lv 50). Internal cooldown
                // of 4 s per hero so a high-attack-speed encounter can't proc
                // it on every swing and turn the hero into a permanent shield
                // tower (user-reported: was reading as "basically immortal" at
                // 15% × s chance with sub-second hit cadence).
                if (rbDef.guardianChance > 0) {
                  const heroRef = target.heroRef as any;
                  const cdExpiry = (heroRef._wardGuardianCdExpiry as number) ?? 0;
                  const now = Date.now();
                  if (now >= cdExpiry && Math.random() < rbDef.guardianChance) {
                    const eq = target.heroRef.equipment;
                    const guardianRelics = [eq.relic1, eq.relic2, eq.relic3, eq.relic4]
                      .filter((r) => r?.name === 'Ward of the Guardian');
                    const bestLevel = guardianRelics.reduce((max, r) => Math.max(max, r!.level), 1);
                    const shieldPct = Math.min(0.30, 0.08 + (bestLevel - 1) * 0.005);
                    const targetMaxHp = this.effStats(target.heroRef).maxHp ?? target.heroRef.stats.maxHp;
                    const shieldHp = Math.max(1, Math.round(targetMaxHp * shieldPct));
                    if (this.applyShield(target.heroRef, shieldHp, 'ward_guardian_' + Date.now())) {
                      heroRef._wardGuardianCdExpiry = now + 4000;
                      this.showDamageNumber(target.x, target.y - 35, `🛡${shieldHp}`, '#dde6f0');
                    }
                  }
                }
                if (target.heroRef.isDead) {
                  this.lastDeathSource = `${es.enemyRef.attackRange === 'ranged' ? 'Ranged attack' : 'Melee attack'} from ${es.enemyRef.name}`;
                  this.handleHeroDeath(target);
                }
              }
            }
          }
          // Enemy attacks nearest minion in range (if no hero in attack range, or as secondary)
          if (this.minionSprites.length > 0) {
            const minionTarget = this.minionSprites
              .filter((m) => m.hp > 0 && Math.abs(es.x - m.sprite.x) < atkRange)
              .sort((a, b) => Math.abs(es.x - a.sprite.x) - Math.abs(es.x - b.sprite.x))[0];
            if (minionTarget) {
              const mLastAtk = es.getData('lastAtkMinion') as number || 0;
              const mInterval = Math.round(getAttackInterval(es.enemyRef.speed) / (es.enemyRef.attackSpeedMult || 1));
              if (time - mLastAtk >= mInterval) {
                es.setData('lastAtkMinion', time);
                minionTarget.hp -= es.enemyRef.attack;
                this.showDamageNumber(minionTarget.sprite.x, minionTarget.sprite.y - 20, `${es.enemyRef.attack}`, '#f87171', '11px');
                this.flashSprite(minionTarget.sprite as any);
              }
            }
          }
        }
      }

      // ── Abyssal Demon (boss_demon1): Infernal Laser Beam — 8 s cooldown, 2 s channel ──
      if (es.enemyRef.defId === 'boss_demon1' && !es.enemyRef.debuffs.some((d) => d.type === 'stun')) {
        const BEAM_CD      = 8000;   // ms between casts
        const WINDUP_MS    =   500;  // charge-up before beam fires
        const BEAM_MS      =  2000;  // beam active duration
        const TICK_INTERVAL =  400;  // damage every N ms during beam
        const lastBeam     = (es.getData('lastBeam')  as number)  || 0;
        const beaming      = (es.getData('beaming')   as boolean) || false;

        // Beam only fires at heroes that are far enough away
        const MIN_BEAM_RANGE = 260;
        const beamCandidates = this.heroSprites.filter((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) >= MIN_BEAM_RANGE);
        if (!beaming && time - lastBeam >= BEAM_CD) {
          const targets = beamCandidates.length > 0 ? beamCandidates : [];
          if (targets.length > 0) {
            es.setData('lastBeam', time);
            es.setData('beaming', true);

            // Start from eye-level of boss sprite (faces left, beam exits from upper-left = eyes)
            const castX  = es.x - 90;
            const castY  = es.y - 90;
            // Beam visually reaches the farthest (leftmost) hero
            const endX   = Math.min(...targets.map((t) => t.x));
            // Primary target = hero closest to boss (highest X) — takes full damage
            const primaryTarget = targets.reduce((best, t) => t.x > best.x ? t : best, targets[0]);

            // Announce cast
            this.showDamageNumber(es.x, es.y - 130, '☄ INFERNAL BEAM', '#5090c0');

            // Fire the VFX (charge → beam, returns cleanup fn)
            const stopVfx = this.vfx.playDemonLaserBeam(castX, castY, endX, castY);

            // Damage ticks begin after windup
            const tickCount = Math.floor(BEAM_MS / TICK_INTERVAL);
            for (let tick = 0; tick < tickCount; tick++) {
              this.time.delayedCall(WINDUP_MS + tick * TICK_INTERVAL, () => {
                for (const target of targets) {
                  if (target.heroRef.isDead || this.gsm.godMode) continue;
                  // Per-tick dmg: 40% attack - 30% def. Primary takes full; AoE targets take 50%
                  // Shadow damage — reduced by shadowResist
                  const shadowResist = Math.min(this.getHeroResist(target.heroRef, 'shadow'), 75);
                  const rawFull = Math.max(1, Math.floor(es.enemyRef.attack * 0.40 * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
                  const fullDmg = shadowResist > 0 ? Math.max(1, Math.floor(rawFull * (1 - shadowResist / 100))) : rawFull;
                  const dmg = target === primaryTarget ? fullDmg : Math.max(1, Math.floor(fullDmg * 0.5));
                  this.applyDamageToHero(target, dmg, undefined, true /* AOE */);
                  this.showDamageNumber(target.x, target.y - 25 - tick * 6, `${dmg}`, '#5090c0');
                  this.applySpellReflect(target, es, dmg);
                  if (target.heroRef.stats.hp <= 0) {
                    this.lastDeathSource = `Infernal Laser Beam (${es.enemyRef.name})`;
                    this.handleHeroDeath(target);
                  }
                }
                // Beam also burns summons that lie past MIN_BEAM_RANGE on
                // the boss's left (matching the hero range filter).
                for (const ss of this.summonSprites) {
                  if (ss.isDead) continue;
                  if (Math.abs(es.x - ss.x) < MIN_BEAM_RANGE) continue;
                  this.applyDamageToSummon(ss, Math.floor(es.enemyRef.attack * 0.40 * 0.5));
                }
              });
            }

            // Clean up after full duration
            this.time.delayedCall(WINDUP_MS + BEAM_MS + 100, () => {
              stopVfx();
              es.setData('beaming', false);
            });
          }
        }
      }

      // ── Dark Elf Sovereign: shadow bolt 7s CD, 650px reach, 1.9× attack damage ─────
      if (es.enemyRef.defId === 'boss_darkelf' && !es.enemyRef.debuffs.some((d) => d.type === 'fear')) {
        const lastBolt = (es.getData('lastBolt') as number) || 0;
        const BOLT_CD = 7000;
        if (time - lastBolt >= BOLT_CD) {
          const boltTarget = this.heroSprites.filter((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < 650)[0];
          if (boltTarget) {
            es.setData('lastBolt', time);
            this.playSpellCastAnim(es);
            // Visual: dark bolt flies to target
            this.vfx.playMagicBolt(es.x, es.y - 80, boltTarget.x, boltTarget.y - 60, 0x1a4878);
            this.showDamageNumber(es.x, es.y - 100, '✦ SHADOW BOLT', '#2060a0');
            // Apply after travel delay (~200ms bolt flight) — shadow damage, reduced by shadowResist + DEF.
            // Damage multiplier halves on early-stage encounters per user
            // feedback: the Sovereign can spawn as an Elite-Pack pick at
            // stage 7-ish (well before its natural Demon-Rift window) and
            // 1.9× of his already-elite ATK was one-shotting low-level
            // heroes through Shadow resist. Stages ≤ 10 → 0.95×; from
            // stage 11 onward the full 1.9× is back, since heroes by then
            // have the gear / talents to weather it.
            const earlyEncounter = this.battleLevel <= 10;
            const boltMult = earlyEncounter ? 0.95 : 1.9;
            this.time.delayedCall(210, () => {
              if (!boltTarget.heroRef.isDead && !this.gsm.godMode) {
                const rawBoltDmg = Math.max(1, Math.floor(es.enemyRef.attack * boltMult * (1 - spellMitigation(this.effStats(boltTarget.heroRef).defense))));
                const sRes = Math.min(this.getHeroResist(boltTarget.heroRef, 'shadow'), 75);
                const boltDmg = sRes > 0 ? Math.max(1, Math.floor(rawBoltDmg * (1 - sRes / 100))) : rawBoltDmg;
                this.applyDamageToHero(boltTarget, boltDmg);
                this.showDamageNumber(boltTarget.x, boltTarget.y - 30, `${boltDmg}`, '#2060a0');
                this.vfx.playBlackHole(boltTarget.x, boltTarget.y - 40);
                this.applySpellReflect(boltTarget, es, boltDmg);
                if (boltTarget.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Shadow Bolt (${es.enemyRef.name})`;
                  this.handleHeroDeath(boltTarget);
                }
              }
            });
          }
        }
      }

      // ── White Claw (boss_wolf_lord): Howl of Silence — locks one hero's spellcasting
      // for 1.5 s on a 12 s cooldown. Unlocks at stage 10+ so early Dark Forest clears
      // don't get ambushed by a silence mechanic they can't counter yet.
      if (es.enemyRef.defId === 'boss_wolf_lord' && this.battleLevel >= 10
          && !es.enemyRef.debuffs.some((d) => d.type === 'fear')) {
        const lastHowl = (es.getData('lastHowl') as number) || 0;
        const HOWL_CD = 12000;
        if (time - lastHowl >= HOWL_CD) {
          const aliveHeroes = this.heroSprites.filter((h) => !h.heroRef.isDead);
          if (aliveHeroes.length > 0) {
            es.setData('lastHowl', time);
            // Pick the caster with the most remaining mana as silence target — locks
            // the highest-impact rotation. Falls back to closest if nobody has mana.
            const casters = aliveHeroes.filter((h) => (this.effStats(h.heroRef).maxMp ?? 0) > 0);
            let target: HeroSprite;
            if (casters.length > 0) {
              target = casters.reduce((best, h) => h.heroRef.stats.mp > best.heroRef.stats.mp ? h : best, casters[0]);
            } else {
              target = aliveHeroes.reduce((best, h) =>
                Math.abs(es.x - h.x) < Math.abs(es.x - best.x) ? h : best, aliveHeroes[0]);
            }
            (target.heroRef as any)._silencedUntil = this.gameTime + 1500;
            this.showDamageNumber(es.x, es.y - 130, 'HOWL OF SILENCE', '#a8a8ff');
            this.showDamageNumber(target.x, target.y - 40, 'SILENCED!', '#a8a8ff');
            this.vfx.playFearWave(target.x, target.y);
          }
        }
      }

      // ── Demon Overlord (boss_demon2): Hellfire Blast — AoE all heroes, 10s CD ── fire damage
      if (es.enemyRef.defId === 'boss_demon2') {
        const HELLFIRE_CD = 10000;
        const lastHellfire = (es.getData('lastHellfire') as number) || 0;
        if (time - lastHellfire >= HELLFIRE_CD) {
          const aliveHeroes = this.heroSprites.filter((h) => !h.heroRef.isDead);
          if (aliveHeroes.length > 0) {
            es.setData('lastHellfire', time);
            this.playSpellCastAnim(es);
            this.showDamageNumber(es.x, es.y - 110, '🔥 HELLFIRE BLAST', '#f97316');
            aliveHeroes.forEach((target) => {
              this.vfx.playHellfireBall(es.x, es.y - 70, target.x, target.y - 40);
              this.time.delayedCall(280, () => {
                if (!target.heroRef.isDead && !this.gsm.godMode) {
                  const fRes = Math.min(this.getHeroResist(target.heroRef, 'fire'), 75);
                  const rawDmg = Math.max(1, Math.floor(es.enemyRef.attack * 0.85 * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
                  const dmg = fRes > 0 ? Math.max(1, Math.floor(rawDmg * (1 - fRes / 100))) : rawDmg;
                  this.applyDamageToHero(target, dmg, undefined, true /* AOE: Hellfire Blast */);
                  this.showDamageNumber(target.x, target.y - 30, `${dmg}`, '#f97316');
                  this.applySpellReflect(target, es, dmg);
                  if (target.heroRef.stats.hp <= 0) {
                    this.lastDeathSource = `Hellfire Blast (${es.enemyRef.name})`;
                    this.handleHeroDeath(target);
                  }
                }
              });
            });
            // Hellfire Blast also rains on summons. Same delay so they take
            // damage at the same beat as heroes; raw damage (no fire resist).
            this.time.delayedCall(280, () => {
              for (const ss of this.summonSprites) {
                if (!ss.isDead) this.applyDamageToSummon(ss, Math.floor(es.enemyRef.attack * 0.85));
              }
            });
          }
        }
      }

      // ── Skeleton Mage / The Archivist (boss_skeleton_mage): Frost Nova — radial AoE
      //    around the boss every 12 s. Damages every hero within 500 px (frost school,
      //    reduced by frostResist) and roots them for 1.5 s — Morveth-style mana-cooldown
      //    pressure since rooted heroes can't auto-attack. ──
      if (es.enemyRef.defId === 'boss_skeleton_mage') {
        const NOVA_CD = 12000;
        const NOVA_RANGE = 500;
        const NOVA_ROOT_MS = 1500;
        const lastNova = (es.getData('lastNova') as number) || 0;
        // Hold the first nova for ~3 s after spawn so the player has time to read the boss.
        const firstNovaDelay = lastNova === 0 ? 3000 : 0;
        if (time - lastNova >= NOVA_CD + firstNovaDelay) {
          const inRange = this.heroSprites.filter((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < NOVA_RANGE);
          if (inRange.length > 0) {
            es.setData('lastNova', time);
            // Archivist uses the Wanderer Magican area_attack sheet for the Frost Nova cast pose.
            if (this.anims.exists('boss_skeleton_mage_area_attack')) {
              es.play('boss_skeleton_mage_area_attack', true);
            } else {
              this.playSpellCastAnim(es);
            }
            this.vfx.playFrostNova(es.x, es.y - 50);
            this.showDamageNumber(es.x, es.y - 110, '❄ FROST NOVA', '#8ad4ff');
            // Frost Nova also catches summons in the x-range. Summons have
            // no frost resist so they take the unmitigated raw damage.
            this.time.delayedCall(180, () => {
              for (const ss of this.summonSprites) {
                if (!ss.isDead && Math.abs(es.x - ss.x) < NOVA_RANGE) {
                  this.applyDamageToSummon(ss, Math.floor(es.enemyRef.attack * 1.1));
                }
              }
            });
            inRange.forEach((target) => {
              this.time.delayedCall(180, () => {
                if (target.heroRef.isDead || this.gsm.godMode) return;
                const fRes = Math.min(this.getHeroResist(target.heroRef, 'frost'), 75);
                const rawDmg = Math.max(1, Math.floor(es.enemyRef.attack * 1.1 * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
                const dmg = fRes > 0 ? Math.max(1, Math.floor(rawDmg * (1 - fRes / 100))) : rawDmg;
                this.applyDamageToHero(target, dmg, undefined, true /* AOE: Frost Nova */);
                this.showDamageNumber(target.x, target.y - 30, `${dmg}`, '#8ad4ff');
                this.applySpellReflect(target, es, dmg);
                if (target.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Frost Nova (${es.enemyRef.name})`;
                  this.handleHeroDeath(target);
                  return;
                }
                // Apply root — extends if already rooted (max wins).
                // Stun-resist sigil affix gates frost root (root is a movement-stun CC).
                if (this.isMainHero(target) && this.rollCcResist('root')) {
                  this.showDamageNumber(target.x, target.y - 50, 'RESIST ❄', '#fbbf24', '11px');
                } else {
                  const cur = (target.heroRef as any)._frostRootedTimer || 0;
                  (target.heroRef as any)._frostRooted = true;
                  (target.heroRef as any)._frostRootedTimer = Math.max(cur, NOVA_ROOT_MS);
                  this.showDamageNumber(target.x, target.y - 50, '❄ ROOTED', '#8ad4ff', '11px');
                }
              });
            });
          }
        }
      }

      // ── Void Elf Warlord (boss_voidelf): Void Lance — ranged pierce strike, 2× atk to farthest hero within 700px, 8s CD ──
      // Also: Void Shield — self buff every 15s, absorbs next hit entirely
      if (es.enemyRef.defId === 'boss_voidelf') {
        // Void Lance: targets the farthest hero within range for high damage.
        // First-cast warm-up: held for ~4 s after spawn so the player has time
        // to react before the first chunk-damage hits — otherwise the lance
        // fires on engagement (lastVLance=0 → time−0 ≥ 8000 the moment the
        // boss is past stage time 8 s).
        const VLANCE_CD = 8000;
        const VLANCE_FIRST_DELAY = 4000;
        const lastLance = (es.getData('lastVLance') as number) || 0;
        const cdRequired = lastLance === 0 ? VLANCE_FIRST_DELAY : VLANCE_CD;
        if (time - lastLance >= cdRequired) {
          const lancePool = this.heroSprites.filter((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < 700);
          if (lancePool.length > 0) {
            // Farthest hero
            const lanceTarget = lancePool.reduce((far, h) =>
              Math.abs(es.x - h.x) > Math.abs(es.x - far.x) ? h : far,
            );
            es.setData('lastVLance', time);
            this.playSpellCastAnim(es);
            this.showDamageNumber(es.x, es.y - 110, '⚡ VOID LANCE', '#1a4878');
            this.vfx.playVoidLance(es.x, es.y - 70, lanceTarget.x, lanceTarget.y - 50);
            // Shadow damage — reduced by shadowResist. Damage scaled to ×1.4
            // (was 2.0) — at first-encounter stages a 2× hit on the back-row
            // wizard reads as instakill before the player has any tools.
            this.time.delayedCall(360, () => {
              if (!lanceTarget.heroRef.isDead && !this.gsm.godMode) {
                const sRes = Math.min(this.getHeroResist(lanceTarget.heroRef, 'shadow'), 75);
                const rawDmg = Math.max(1, Math.floor(es.enemyRef.attack * 1.4 * (1 - spellMitigation(this.effStats(lanceTarget.heroRef).defense))));
                const dmg = sRes > 0 ? Math.max(1, Math.floor(rawDmg * (1 - sRes / 100))) : rawDmg;
                this.applyDamageToHero(lanceTarget, dmg);
                this.showDamageNumber(lanceTarget.x, lanceTarget.y - 30, `${dmg}`, '#1a4878');
                this.vfx.playLightning(lanceTarget.x, lanceTarget.y - 40); // void impact — distinct from dark elf's black hole
                this.applySpellReflect(lanceTarget, es, dmg);
                if (lanceTarget.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Void Lance (${es.enemyRef.name})`;
                  this.handleHeroDeath(lanceTarget);
                }
              }
            });
          }
        }
        // Void Shield: absorbs next incoming hit, 15s CD
        const VSHIELD_CD = 15000;
        const lastShield = (es.getData('lastVShield') as number) || 0;
        const hasShield = (es.getData('voidShield') as boolean) || false;
        if (!hasShield && time - lastShield >= VSHIELD_CD) {
          es.setData('voidShield', true);
          es.setData('lastVShield', time);
          this.showDamageNumber(es.x, es.y - 90, '🛡 VOID SHIELD', '#5090c0');
          es.setTint(0x4090c0);
          // Auto-clear tint on next hit handled in takeDamage; fallback clear after 15s
          this.time.delayedCall(15000, () => {
            if (es.active && es.getData('voidShield')) {
              es.setData('voidShield', false);
              es.clearTint();
            }
          });
        }
      }

      // ── Bone Warlord (boss_spearman): Cursed Javelin — 0.85× atk to nearest hero within 420px, 10s CD ──
      if (es.enemyRef.defId === 'boss_spearman') {
        const JAVELIN_CD = 10000;
        const lastJavelin = (es.getData('lastJavelin') as number) || 0;
        if (time - lastJavelin >= JAVELIN_CD) {
          const javelinTarget = this.heroSprites
            .filter((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < 420)
            .sort((a, b) => Math.abs(es.x - a.x) - Math.abs(es.x - b.x))[0];
          if (javelinTarget) {
            es.setData('lastJavelin', time);
            this.playSpellCastAnim(es);
            this.showDamageNumber(es.x, es.y - 100, '☠ CURSED JAVELIN', '#78350f');
            this.vfx.playMagicBolt(es.x, es.y - 60, javelinTarget.x, javelinTarget.y - 40, 0x5a2800);
            this.time.delayedCall(240, () => {
              if (!javelinTarget.heroRef.isDead && !this.gsm.godMode) {
                const dmg = Math.max(1, Math.floor(es.enemyRef.attack * 0.85 * (1 - calculateMitigation(this.effStats(javelinTarget.heroRef).defense))));
                this.applyDamageToHero(javelinTarget, dmg);
                this.showDamageNumber(javelinTarget.x, javelinTarget.y - 30, `${dmg}`, '#d97706');
                this.applySpellReflect(javelinTarget, es, dmg);
                if (javelinTarget.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Cursed Javelin (${es.enemyRef.name})`;
                  this.handleHeroDeath(javelinTarget);
                }
              }
            });
          }
        }
      }

      // ── Gorgon Queen (boss_gorgon): Stone Gaze — petrify closest hero, 3.5s, 7.5s CD, 700px range ──
      // After casting: 2× move speed for 3.5s (fury rush). Immune to stun, slowable.
      if (es.enemyRef.defId === 'boss_gorgon') {
        const GAZE_CD = 7500;
        const GAZE_RANGE = 700;
        const PETRIFY_MS = 3500;
        const RUSH_MS    = 3500;
        const lastGaze = (es.getData('lastGaze') as number) || 0;
        if (time - lastGaze >= GAZE_CD) {
          const gazePool = this.heroSprites.filter(
            (h) => !h.heroRef.isDead && !(h.heroRef as any)._petrified && Math.abs(es.x - h.x) < GAZE_RANGE,
          );
          if (gazePool.length > 0) {
            // Target the closest hero (not random)
            const gazeTarget = gazePool.reduce((closest, h) =>
              Math.abs(es.x - h.x) < Math.abs(es.x - closest.x) ? h : closest,
            );
            es.setData('lastGaze', time);
            // Use attack3 animation for Stone Gaze spell
            const gazeAtkKey = this.anims.exists('boss_gorgon_attack3') ? 'boss_gorgon_attack3' : 'boss_gorgon_attack';
            const gazeReturnKey = this.anims.exists('boss_gorgon_walk') ? 'boss_gorgon_walk' : 'boss_gorgon_idle';
            if (this.anims.exists(gazeAtkKey) && es.anims.currentAnim?.key !== gazeAtkKey) {
              es.off('animationcomplete');
              es.play(gazeAtkKey);
              es.once('animationcomplete', () => {
                if (es.active && this.anims.exists(gazeReturnKey)) es.play(gazeReturnKey);
              });
            }
            this.showDamageNumber(es.x, es.y - 110, '🪨 STONE GAZE', '#6b7280');
            // Petrify resist — sigil affix can block the gaze on the main hero.
            if (this.isMainHero(gazeTarget) && this.rollCcResist('petrify')) {
              this.showDamageNumber(gazeTarget.x, gazeTarget.y - 45, 'RESIST 🪨', '#fbbf24');
            } else {
              this.showDamageNumber(gazeTarget.x, gazeTarget.y - 45, '🪨 PETRIFIED!', '#9ca3af');
              (gazeTarget.heroRef as any)._petrified = true;
              (gazeTarget.heroRef as any)._petrifiedTimer = PETRIFY_MS;
            }
            // Fury Rush: double move speed for RUSH_MS after casting
            (es.enemyRef as any)._gorgonRush = RUSH_MS;
            this.showDamageNumber(es.x, es.y - 75, '⚡ FURY RUSH', '#f59e0b');
          }
        }
        // Tick down fury rush timer
        const rush = (es.enemyRef as any)._gorgonRush || 0;
        if (rush > 0) (es.enemyRef as any)._gorgonRush = Math.max(0, rush - delta);
      }

      // ── Gorgon Echo (boss_gorgon_echo): Echo Wail — DoT echo on closest hero, 6s CD, 700px range ──
      // Applies a shadow-damage DoT for 5s that pulses every 1s. Uses the Special animation.
      if (es.enemyRef.defId === 'boss_gorgon_echo' && !es.enemyRef.debuffs.some((d) => d.type === 'stun')) {
        const ECHO_CD = 6000;
        const ECHO_RANGE = 700;
        const ECHO_DUR = 5000;
        const lastEcho = (es.getData('lastEcho') as number) || 0;
        if (time - lastEcho >= ECHO_CD) {
          const pool = this.heroSprites.filter(
            (h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < ECHO_RANGE,
          );
          if (pool.length > 0) {
            const target = pool.reduce((closest, h) =>
              Math.abs(es.x - h.x) < Math.abs(es.x - closest.x) ? h : closest,
            );
            es.setData('lastEcho', time);
            const echoAtkKey = this.anims.exists('boss_gorgon2_special') ? 'boss_gorgon2_special' : 'boss_gorgon2_attack';
            const echoReturnKey = this.anims.exists('boss_gorgon2_walk') ? 'boss_gorgon2_walk' : 'boss_gorgon2_idle';
            if (this.anims.exists(echoAtkKey) && es.anims.currentAnim?.key !== echoAtkKey) {
              es.off('animationcomplete');
              es.play(echoAtkKey);
              es.once('animationcomplete', () => {
                if (es.active && this.anims.exists(echoReturnKey)) es.play(echoReturnKey);
              });
            }
            this.showDamageNumber(es.x, es.y - 110, '🌀 ECHO WAIL', '#a855f7');
            // Echo VFX — pulsing violet ring at target using Phaser graphics
            const pulseRing = (radius: number, color: number, dur: number, delay = 0) => {
              this.time.delayedCall(delay, () => {
                if (!target.active) return;
                const ring = this.add.circle(target.x, target.y - 30, 6, 0, 0).setStrokeStyle(3, color, 0.85).setDepth(20).setBlendMode(Phaser.BlendModes.ADD);
                this.tweens.add({
                  targets: ring, radius, alpha: 0, duration: dur, ease: 'Cubic.easeOut',
                  onComplete: () => ring.destroy(),
                });
              });
            };
            pulseRing(50, 0xa855f7, 320);
            pulseRing(70, 0x6b21a8, 360, 150);
            // Apply an echo DoT: 3 ticks, ~10% of boss attack per tick (shadow damage).
            // Reduced from 5 ticks × 21% (≈1.05× attack total) → 3 ticks × 10% (≈0.30× attack total) per cast.
            const perTickRaw = Math.max(1, Math.floor(es.enemyRef.attack * 0.10));
            for (let tick = 1; tick <= 3; tick++) {
              this.time.delayedCall(tick * 1000, () => {
                if (!target.active || target.heroRef.isDead || this.gsm.godMode) return;
                const resist = Math.min(this.getHeroResist(target.heroRef, 'shadow'), 75);
                const mitigated = Math.max(1, Math.floor(perTickRaw * (1 - resist / 100)));
                this.applyDamageToHero(target, mitigated);
                this.showDamageNumber(target.x + (Math.random() - 0.5) * 16, target.y - 25 - tick * 4, `${mitigated} 🌀`, '#a855f7');
                if (target.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Echo Wail (${es.enemyRef.name})`;
                  this.handleHeroDeath(target);
                }
              });
            }
            void ECHO_DUR; // duration is expressed via the 5 ticks above
          }
        }
      }

      // ── White Claw (boss_wolf_lord): Rally Howl — buffs nearby beast allies' atk & atk speed, 14s CD ──
      if (es.enemyRef.defId === 'boss_wolf_lord' && !es.enemyRef.debuffs.some((d) => d.type === 'stun')) {
        const HOWL_CD = 14000;         // was 10s — give the party breathing room
        const HOWL_RANGE = 200;        // was 260 — fewer allies catch the buff
        const HOWL_BUFF_DURATION = 4000;
        const HOWL_ATK_PCT = 0.15;     // was 0.30 — halved damage uplift
        const HOWL_SPD_MULT = 1.15;    // was 1.30 — gentler attack-speed boost
        const lastHowl = (es.getData('lastHowl') as number) || 0;
        if (time - lastHowl >= HOWL_CD) {
          es.setData('lastHowl', time);
          // Howl VFX on the boss
          this.vfx.playWolfHowl(es.x, es.y - 30);
          this.showDamageNumber(es.x, es.y - 110, '🐺 RALLY HOWL', '#ef4444');
          this.cameras.main.shake(180, 0.003);
          // Find nearby beast minions/elites and buff them
          for (const ally of this.enemySprites) {
            if (ally === es || !ally.active || ally.enemyRef.currentHp <= 0) continue;
            if (ally.enemyRef.faction !== 'beast') continue;
            if (Phaser.Math.Distance.Between(es.x, es.y, ally.x, ally.y) > HOWL_RANGE) continue;
            const atkBonus = Math.floor(ally.enemyRef.attack * HOWL_ATK_PCT);
            ally.enemyRef.attack += atkBonus;
            const prevSpdMult = ally.enemyRef.attackSpeedMult ?? 1;
            ally.enemyRef.attackSpeedMult = prevSpdMult * HOWL_SPD_MULT;
            this.vfx.playRallyBuff(ally.x, ally.y);
            this.time.delayedCall(HOWL_BUFF_DURATION, () => {
              if (!ally.enemyRef) return;
              ally.enemyRef.attack = Math.max(1, ally.enemyRef.attack - atkBonus);
              ally.enemyRef.attackSpeedMult = prevSpdMult;
            });
          }
        }
      }

      // ── Lava Golem (boss_lava_golem): 3 spells — Magma Hurl, Inferno Wave, Molten Shell ──
      if (es.enemyRef.defId === 'boss_lava_golem') {
        // Helper: apply fire resistance reduction (fireResist 0–100 → % damage reduction)
        const applyFireResist = (baseDmg: number, target: typeof this.heroSprites[0]): number => {
          const resist = this.getHeroResist(target.heroRef, 'fire');
          if (resist <= 0) return baseDmg;
          return Math.max(1, Math.floor(baseDmg * (1 - Math.min(resist, 75) / 100)));
        };
        // Late-stage spell-coefficient softener — Magmarath's atk scales at
        // 1.12^lvl and the spells multiply that by 2.2× / 1.0× / 2.2×, so
        // raw burst grows exponentially past stage 50 (89 k Magma Hurl at
        // st-50, 250 k+ at st-60). One-shotting casters from full HP isn't
        // the design goal — bosses should be scary, not unplayable. From
        // st-50 onward we taper the spell coefficients (auto-attacks keep
        // their full atk so a melee front-line still feels his weight).
        const stage = this.gsm.player.currentLevel ?? 1;
        const spellStageScale = stage < 50
          ? 1
          : Math.max(0.35, 0.65 * Math.pow(0.97, Math.max(0, stage - 50)));

        // Spell 1: Magma Hurl — ranged lava drop at farthest hero within 700px, 2.2× atk, burn DoT, 9s CD
        const MAGMA_CD = 9000;
        const lastMagma = (es.getData('lastMagma') as number) || 0;
        if (time - lastMagma >= MAGMA_CD) {
          const magmaPool = this.heroSprites.filter((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < 700);
          if (magmaPool.length > 0) {
            const magmaTarget = magmaPool.reduce((far, h) =>
              Math.abs(es.x - h.x) > Math.abs(es.x - far.x) ? h : far,
            );
            es.setData('lastMagma', time);
            this.playSpellCastAnim(es);
            this.showDamageNumber(es.x, es.y - 110, '🔥 MAGMA HURL', '#f97316');
            this.vfx.playLavaDrop(es.x, es.y - 70, magmaTarget.x, magmaTarget.y - 40);
            this.time.delayedCall(320, () => {
              if (!magmaTarget.heroRef.isDead && !this.gsm.godMode) {
                const rawDmg = Math.max(1, Math.floor(es.enemyRef.attack * 2.2 * spellStageScale * (1 - spellMitigation(this.effStats(magmaTarget.heroRef).defense))));
                const dmg = applyFireResist(rawDmg, magmaTarget);
                this.applyDamageToHero(magmaTarget, dmg);
                this.showDamageNumber(magmaTarget.x, magmaTarget.y - 30, `${dmg}`, '#f97316');
                this.applySpellReflect(magmaTarget, es, dmg);
                // Apply burn DoT: deal additional fire damage over 4 ticks (every 2s)
                const rawBurn = Math.max(1, Math.floor(es.enemyRef.attack * 0.10 * spellStageScale * (1 - spellMitigation(this.effStats(magmaTarget.heroRef).defense))));
                const burnTick = applyFireResist(rawBurn, magmaTarget);
                for (let t = 1; t <= 4; t++) {
                  this.time.delayedCall(t * 2000, () => {
                    if (!magmaTarget.heroRef.isDead && !this.gsm.godMode) {
                      this.applyDamageToHero(magmaTarget, burnTick);
                      this.showDamageNumber(magmaTarget.x, magmaTarget.y - 20, `${burnTick}`, '#f97316');
                      if (magmaTarget.heroRef.stats.hp <= 0) {
                        this.lastDeathSource = `Magma burn (${es.enemyRef.name})`;
                        this.handleHeroDeath(magmaTarget);
                      }
                    }
                  });
                }
                if (magmaTarget.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Magma Hurl (${es.enemyRef.name})`;
                  this.handleHeroDeath(magmaTarget);
                }
              }
            });
          }
        }

        // Spell 2: Inferno Wave — AoE fire hurricane to ALL heroes, 14s CD
        const INFERNO_CD = 14000;
        const lastInferno = (es.getData('lastInferno') as number) || 0;
        if (time - lastInferno >= INFERNO_CD) {
          const aliveHeroes = this.heroSprites.filter((h) => !h.heroRef.isDead);
          if (aliveHeroes.length > 0) {
            es.setData('lastInferno', time);
            this.playSpellCastAnim(es);
            this.showDamageNumber(es.x, es.y - 120, '🌋 INFERNO WAVE', '#dc2626');
            aliveHeroes.forEach((target) => {
              this.vfx.playFireHurricane(target.x, target.y - 20);
              this.time.delayedCall(250, () => {
                if (!target.heroRef.isDead && !this.gsm.godMode) {
                  // Inferno Wave — 1.0× was 1.4×. Hits ALL heroes simultaneously,
                  // so a 5-hero party eats 5× the throughput; lighter per-hit
                  // keeps the all-target threat real without the casters
                  // dropping in one volley.
                  const rawDmg = Math.max(1, Math.floor(es.enemyRef.attack * 1.0 * spellStageScale * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
                  const dmg = applyFireResist(rawDmg, target);
                  this.applyDamageToHero(target, dmg, undefined, true /* AOE: Inferno Wave */);
                  this.showDamageNumber(target.x, target.y - 30, `${dmg}`, '#dc2626');
                  this.applySpellReflect(target, es, dmg);
                  if (target.heroRef.stats.hp <= 0) {
                    this.lastDeathSource = `Inferno Wave (${es.enemyRef.name})`;
                    this.handleHeroDeath(target);
                  }
                }
              });
            });
            // Inferno Wave hits the whole field — summons too.
            this.time.delayedCall(250, () => {
              for (const ss of this.summonSprites) {
                if (!ss.isDead) this.applyDamageToSummon(ss, Math.floor(es.enemyRef.attack * 1.0 * spellStageScale));
              }
            });
          }
        }

        // Spell 3: Molten Shell — absorbs next hit + retaliates with fire damage, 20s CD
        const MSHELL_CD = 20000;
        const lastShell = (es.getData('lastMoltenShell') as number) || 0;
        const hasShell  = (es.getData('moltenShell')     as boolean) || false;
        if (!hasShell && time - lastShell >= MSHELL_CD) {
          es.setData('moltenShell', true);
          es.setData('lastMoltenShell', time);
          this.showDamageNumber(es.x, es.y - 90, '🛡 MOLTEN SHELL', '#f97316');
          es.setTint(0xff4500);
          this.vfx.playMoltenShellActivate(es.x, es.y);
          this.time.delayedCall(20000, () => {
            if (es.active && es.getData('moltenShell')) {
              es.setData('moltenShell', false);
              es.clearTint();
            }
          });
        }

        // Tectonic Ground Slam: if any hero steps within 120px, charge at them — run+attack anim, 12s CD
        const SLAM_CD = 12000;
        const lastSlam = (es.getData('lastSlam') as number) || 0;
        if (time - lastSlam >= SLAM_CD) {
          const closeHero = this.heroSprites.find((h) => !h.heroRef.isDead && Math.abs(es.x - h.x) < 120);
          if (closeHero) {
            es.setData('lastSlam', time);
            this.showDamageNumber(es.x, es.y - 100, '💥 GROUND SLAM', '#f97316');
            if (this.anims.exists('boss_lava_golem_charge')) {
              es.play('boss_lava_golem_charge');
              es.once('animationcomplete', () => {
                if (es.active) {
                  const walkKey = 'boss_lava_golem_walk';
                  if (this.anims.exists(walkKey)) es.play(walkKey);
                }
              });
            }
            if (!closeHero.heroRef.isDead && !this.gsm.godMode) {
              // Ground Slam — 2.2× was 3.0×. Pulled in line with Magma Hurl
              // so Magmarath's burst tops out around 23 k raw at stage 39
              // (matching Telurion). Caster classes were one-shotting from
              // full HP at 3.0× even with 50 % fire resist + def mitigation.
              const rawDmg = Math.max(1, Math.floor(es.enemyRef.attack * 2.2 * spellStageScale * (1 - spellMitigation(this.effStats(closeHero.heroRef).defense))));
              const slamDmg = applyFireResist(rawDmg, closeHero);
              this.applyDamageToHero(closeHero, slamDmg, undefined, true /* AOE: Ground Slam */);
              this.showDamageNumber(closeHero.x, closeHero.y - 40, `${slamDmg}`, '#f97316');
              this.vfx.playGroundSlamStrike(closeHero.x, closeHero.y);
              this.applySpellReflect(closeHero, es, slamDmg);
              if (closeHero.heroRef.stats.hp <= 0) {
                this.lastDeathSource = `Ground Slam (${es.enemyRef.name})`;
                this.handleHeroDeath(closeHero);
              }
            }
          }
        }
      }

      // ── Shadowlord (boss_shadowlord) — stage-50 endboss + endless gate ──
      // Phase gates trigger on HP-percent thresholds. Each phase fires its
      // own ability on its own cooldown; the standard autoattack still runs
      // alongside the phase abilities (he doesn't pause to cast).
      //   Phase 1 (100 → 75 %): Fire trap on a random formation slot — 6 s CD
      //   Phase 2 (75 → 50 %):  Become invuln + summon 4 ghosts — one-shot
      //                         (when crossing the 75 % gate)
      //   Phase 3 ( < 50 %):    Kill-Zone — cast every 8 s, lethal-band
      //                         radial AoE around the boss
      if (es.enemyRef.defId === 'boss_shadowlord') {
        // Invuln pin — Phase 2 freezes HP at the 75 % threshold so the boss
        // cannot be killed while the Shadow Veil is up. Clamping in the
        // per-frame block dodges having to wrap every damage-application
        // site (auto-attack, AoE, DoT tick, Divine Wrath, etc.) with an
        // _invuln check.
        if ((es.enemyRef as any)._invuln) {
          const floor = Math.floor(es.enemyRef.maxHp * 0.75);
          if (es.enemyRef.currentHp < floor) es.enemyRef.currentHp = floor;
        }
        // Shadowlord deals 'shadow' school damage — re-uses the existing
        // shadow resist pipeline (Shadewarden trophy, sigil affix, etc.)
        // instead of forcing a brand-new resist key into the codebase.
        const applyShadowResist = (baseDmg: number, target: typeof this.heroSprites[0]): number => {
          const resist = this.getHeroResist(target.heroRef, 'shadow');
          if (resist <= 0) return baseDmg;
          return Math.max(1, Math.floor(baseDmg * (1 - Math.min(resist, 75) / 100)));
        };
        const hpPctNow = es.enemyRef.currentHp / es.enemyRef.maxHp;

        // ── Phase 2 ONE-SHOT trigger (cross 75 % HP). Becomes invulnerable
        //    AND invisible for as long as the summoned bosses are alive.
        //    The boss locks into a looped Hurt anim while invisible (read:
        //    "phased into the veil"). Auto-attack is gated on the same
        //    `_invuln` flag (see attack block above) so he stops swinging
        //    entirely while veiled.
        //    The phase ends when ALL summoned demons are dead — not on a
        //    fixed timer (per user feedback: "he should be in no attack/
        //    invisible phase while the two boss are alive").
        if (hpPctNow <= 0.75 && !es.getData('phase2Triggered')) {
          es.setData('phase2Triggered', true);
          (es.enemyRef as any)._invuln = true;
          // Looped Hurt — auto-registry creates the standard hurt anim with
          // repeat=0; we need a looping variant for the indefinite veil
          // window. Frames 24-25 at 5 fps reads as a slow agonised heave.
          const HURT_LOOP_KEY = 'enemy_shadowlord_hurt_loop';
          if (!this.anims.exists(HURT_LOOP_KEY) && this.textures.exists('enemy_shadowlord')) {
            this.anims.create({
              key: HURT_LOOP_KEY,
              frames: this.anims.generateFrameNumbers('enemy_shadowlord', { start: 24, end: 25 }),
              frameRate: 5,
              repeat: -1,
            });
          }
          if (this.anims.exists(HURT_LOOP_KEY)) {
            es.off('animationcomplete');
            es.play(HURT_LOOP_KEY);
          }
          this.showDamageNumber(es.x, es.y - 140, '✦ SHADOW VEIL', '#b026ff');
          // Violet shimmer reads invuln if briefly visible during fade.
          es.setTint(0x9d4dff);
          // INVISIBILITY — fade Malakhar (sprite + shadow + HP bar + name)
          // out so he physically removes himself from the scene while the
          // summons are loose. The HP bar fades so the player isn't
          // tracking a boss they can't damage.
          const veilTargets: Phaser.GameObjects.GameObject[] = [es];
          if (es.shadow)     veilTargets.push(es.shadow);
          if (es.hpBar)      veilTargets.push(es.hpBar);
          if (es.hpBarBg)    veilTargets.push(es.hpBarBg);
          if (es.nameText)   veilTargets.push(es.nameText);
          const bossSkull = es.getData('bossSkull') as Phaser.GameObjects.Text | undefined;
          if (bossSkull)     veilTargets.push(bossSkull);
          this.tweens.add({ targets: veilTargets, alpha: 0, duration: 700, ease: 'Quad.easeIn' });

          // Track summoned demon refs so we can detect "all dead" → end veil.
          const summonRefs: any[] = [];
          (es.enemyRef as any)._phase2Summons = summonRefs;
          // Phase-2 summons — TWO weakened Shadow Realm bosses spawn from
          // staggered rifts. Each summon gets a portal VFX 600 ms BEFORE
          // it appears so the player has a beat to read the threat.
          const SUMMON_IDS = ['boss_demon1', 'boss_demon2'];
          for (let i = 0; i < SUMMON_IDS.length; i++) {
            const portalX = GAME_W - 100 - i * 60;
            const portalY = LANE_Y[1 + i];
            this.time.delayedCall(i * 1200, () => {
              if (!es.active) return;
              this.vfx.playSummonPortal(portalX, portalY);
              this.time.delayedCall(600, () => {
                if (!BattleScene.shadowrealmPending || this.gameOver) return;
                const base = BASE_ENEMIES.find((e) => e.id === SUMMON_IDS[i]);
                if (!base) return;
                const inst = scaleEnemy(base, this.battleLevel, true);
                inst.maxHp     = Math.max(1, Math.floor(inst.maxHp     * 0.50));
                inst.currentHp = inst.maxHp;
                inst.attack    = Math.max(1, Math.floor(inst.attack    * 0.60));
                this.spawnEnemy(inst);
                summonRefs.push(inst);
              });
            });
          }
          // Safety cap — even if the summons don't die (game state bug,
          // user pause-cheese, etc.) the veil drops after 30 s so the
          // fight can't hard-stall. Normal flow ends earlier via the
          // per-frame summon-alive check below.
          es.setData('phase2VeilExpiry', time + 30000);
        }

        // ── Phase 2 END check — runs every frame while veiled. When all
        //    summon refs are dead (or the safety cap expires), fade the
        //    boss back in and clear the invuln flag.
        if ((es.enemyRef as any)._invuln && es.getData('phase2Triggered') && !es.getData('phase2Ended')) {
          const summons = ((es.enemyRef as any)._phase2Summons as any[]) ?? [];
          const allSpawnedAndDead = summons.length >= 2 && summons.every((s) => (s.currentHp ?? 0) <= 0);
          const veilExpired = time > ((es.getData('phase2VeilExpiry') as number) ?? 0);
          if (allSpawnedAndDead || veilExpired) {
            es.setData('phase2Ended', true);
            (es.enemyRef as any)._invuln = false;
            this.restoreEnemyTint(es);
            es.off('animationcomplete');
            if (this.anims.exists('enemy_shadowlord_idle')) es.play('enemy_shadowlord_idle');
            // Fade everything back in — sprite + shadow + HP UI.
            const veilTargets: Phaser.GameObjects.GameObject[] = [es];
            if (es.shadow)     veilTargets.push(es.shadow);
            if (es.hpBar)      veilTargets.push(es.hpBar);
            if (es.hpBarBg)    veilTargets.push(es.hpBarBg);
            if (es.nameText)   veilTargets.push(es.nameText);
            const bossSkull = es.getData('bossSkull') as Phaser.GameObjects.Text | undefined;
            if (bossSkull)     veilTargets.push(bossSkull);
            // Different alpha targets per type so we don't accidentally
            // overshoot. Sprite + shadow back to 1; HP UI back to 1 too.
            this.tweens.add({ targets: veilTargets, alpha: 1, duration: 600, ease: 'Quad.easeOut' });
            this.vfx.playSummonPortal(es.x, es.y - 80);
            this.showDamageNumber(es.x, es.y - 140, 'VEIL FADES', '#c8973a');
          }
        }

        // ── Phase 3 ONE-SHOT trigger (cross 50 % HP). Switches Malakhar
        //    to MELEE mode: caster-band movement disabled, kill-zone unlocks,
        //    Void Hex disabled, the previously-destroyed hex slots all
        //    RESTORE so the player has the full grid back for the final
        //    push (per user feedback: "After 50% we could do something
        //    else, make the boss this time move in melee, and make the
        //    previously banned grid the good one").
        if (hpPctNow <= 0.50 && !((es.enemyRef as any)._invuln) && !es.getData('phase3Triggered')) {
          es.setData('phase3Triggered', true);
          (es.enemyRef as any)._phase3Melee = true;
          this.showDamageNumber(es.x, es.y - 140, '⚔ HOLLOW WRATH', '#ff2080');
          // Restore every void-hexed slot — animate a violet → cyan ring
          // pulse at each restored hex so the recovery reads as a beat.
          // Iterate over a snapshot so we can safely clear the set as we
          // go without mutating during iteration.
          const restored = Array.from(this.voidHexedSlots);
          this.voidHexedSlots.clear();
          for (const slotIdx of restored) {
            const slot = this.formationSlots[slotIdx] ?? FORMATION_SLOTS[slotIdx];
            const overlay = this.voidHexOverlays.get(slotIdx);
            if (overlay) {
              const rune = overlay.getData('rune') as Phaser.GameObjects.Text | undefined;
              this.tweens.add({
                targets: overlay, alpha: 0, duration: 500, ease: 'Quad.easeIn',
                onComplete: () => overlay.destroy(),
              });
              if (rune) {
                this.tweens.add({
                  targets: rune, alpha: 0, scale: 1.6, duration: 500, ease: 'Quad.easeIn',
                  onComplete: () => rune.destroy(),
                });
              }
              this.voidHexOverlays.delete(slotIdx);
            }
            // Restoration pulse — distinct cyan/white expansion (NOT the
            // violet portal palette) so the player reads "tile recovered"
            // not "another rift opened".
            if (slot) {
              this.vfx.playGhostRift(slot.x, slot.y);
            }
          }
        }

        // ── Phase 0 — Auto-attack: traveling shadow orb. Caster-band damage
        //    output that fires from his caster band (320-520 px) and arcs
        //    to a random alive hero. Damage is held until the orb actually
        //    lands (returned travel duration from VFX) so the spell reads
        //    as something Malakhar HURLED, not a melee swing.
        //    Skipped while veiled (Phase 2) AND skipped in Phase 3 (melee
        //    mode owns his offence — standard auto-attack handles swings).
        if (!((es.enemyRef as any)._invuln) && !((es.enemyRef as any)._phase3Melee)) {
          const ORB_CD = 2400;
          const lastOrb = (es.getData('lastShadowOrb') as number) || 0;
          if (time - lastOrb >= ORB_CD) {
            const aliveTargets = this.heroSprites.filter((h) => !h.heroRef.isDead);
            if (aliveTargets.length > 0) {
              es.setData('lastShadowOrb', time);
              const orbTarget = aliveTargets[Math.floor(Math.random() * aliveTargets.length)];
              // Brief attack-anim play so his sprite reads "casting".
              if (this.anims.exists('enemy_shadowlord_attack')) {
                es.off('animationcomplete');
                es.play('enemy_shadowlord_attack');
                es.once('animationcomplete', () => {
                  if (es.active && this.anims.exists('enemy_shadowlord_idle')) es.play('enemy_shadowlord_idle');
                });
              }
              const fromY = es.y - 90; // boss chest height for a 297 px sprite
              const travelMs = this.vfx.playShadowlordOrb(es.x, fromY, orbTarget.x, orbTarget.y - 30);
              const orbBase = Math.max(1, Math.floor(es.enemyRef.attack * 0.85));
              this.time.delayedCall(travelMs + 60, () => {
                if (orbTarget.heroRef.isDead || this.gsm.godMode) return;
                const mit = Math.max(1, Math.floor(orbBase * (1 - spellMitigation(this.effStats(orbTarget.heroRef).defense))));
                const dmg = applyShadowResist(mit, orbTarget);
                this.applyDamageToHero(orbTarget, dmg, undefined, false);
                this.showDamageNumber(orbTarget.x, orbTarget.y - 30, `${dmg}`, '#c080ff');
                if (orbTarget.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Shadow Orb (${es.enemyRef.name})`;
                  this.handleHeroDeath(orbTarget);
                }
              });
            }
          }
        }

        // ── Phase 1 — Void Hex. Skip while invuln (Phase 2) AND skip
        //    after Phase 3 (melee transition at <50% HP). Renamed from
        //    "Fire Trap" — the spell permanently destroys the hex slot
        //    it lands on. Capped at 4 destructions per fight so the grid
        //    never collapses to "no usable slots" (per user feedback:
        //    "hex zone are too wild and are taken too much of the grid").
        const phase3Active = !!(es.enemyRef as any)._phase3Melee;
        if (!phase3Active && this.voidHexedSlots.size < 4
            && (hpPctNow > 0.75 || (hpPctNow > 0.50 && !((es.enemyRef as any)._invuln)))) {
          const TRAP_CD = 6000;
          const lastTrap = (es.getData('lastFireTrap') as number) || 0;
          if (time - lastTrap >= TRAP_CD) {
            es.setData('lastFireTrap', time);
            // Pick a slot that ISN'T already void-hexed — otherwise late-game
            // the boss could keep "hexing" already-dead tiles and wasting his
            // damage budget. If every slot is hexed, fall through to a random
            // pick (the player is already in serious trouble at that point).
            const liveSlots: number[] = [];
            for (let i = 0; i < FORMATION_SLOTS.length; i++) {
              if (!this.voidHexedSlots.has(i)) liveSlots.push(i);
            }
            const slotIdx = liveSlots.length > 0
              ? liveSlots[Math.floor(Math.random() * liveSlots.length)]
              : Math.floor(Math.random() * FORMATION_SLOTS.length);
            const slot = FORMATION_SLOTS[slotIdx];
            this.showDamageNumber(es.x, es.y - 140, '✦ VOID HEX', '#a040ff');
            const trapBase = Math.max(1, Math.floor(es.enemyRef.attack * 0.45));
            // Replace the legacy add.circle telegraph with the spike-eruption
            // VFX. Plays once at trap-detonation, then the slot is marked as
            // permanently destroyed (`voidHexedSlots`) and the dark overlay
            // takes over the visual identity for the rest of the run.
            // Smaller scale (110) than initial 150 — user reported the
            // 150-px burst was visually overwhelming the formation.
            this.vfx.playVoidHex(slot.x, slot.y, 110);
            // Mark the slot as destroyed + redraw grid + evict any hero in it.
            this.markSlotVoidHexed(slotIdx);
            const tickEvent = this.time.addEvent({
              delay: 1000, repeat: 3, callback: () => {
                for (const h of this.heroSprites) {
                  if (h.heroRef.isDead) continue;
                  if (Phaser.Math.Distance.Between(slot.x, slot.y, h.x, h.y) > 38) continue;
                  const mit = Math.max(1, Math.floor(trapBase * (1 - spellMitigation(this.effStats(h.heroRef).defense))));
                  const dmg = applyShadowResist(mit, h);
                  this.applyDamageToHero(h, dmg, undefined, true);
                  this.showDamageNumber(h.x, h.y - 30, `${dmg}`, '#a040ff');
                  if (h.heroRef.stats.hp <= 0) {
                    this.lastDeathSource = `Void Hex (${es.enemyRef.name})`;
                    this.handleHeroDeath(h);
                  }
                }
              },
            });
            this.time.delayedCall(4200, () => tickEvent.remove());
          }
        }

        // ── Phase 3 — Kill-Zone. ONLY fires once Malakhar is in melee mode
        //    (`_phase3Melee` set by the 50% transition). The earlier behaviour
        //    fired the kill-zone from caster range, where most heroes lived
        //    in the lethal 60-240 band by default and got annihilated with
        //    no escape (per user feedback: "while the boss is not reachable
        //    for most hero, he will just kill them all"). Now the kill-zone
        //    only ever activates when he's pushed into the formation, so
        //    the safe-zone gameplay (melee tuck-in vs ranged kite-out) is
        //    actually achievable.
        //    Tightened safe bands too: ≤80 (was 60) for melee tuck, ≥220
        //    (was 240) for ranged kite, lethal 80-220 — slightly larger
        //    melee window for the warrior pile + a pinch wider safe ring
        //    on the outside.
        if ((es.enemyRef as any)._phase3Melee && !((es.enemyRef as any)._invuln)) {
          const KILLZONE_CD = 8000;
          const lastKZ = (es.getData('lastKillzone') as number) || 0;
          if (time - lastKZ >= KILLZONE_CD) {
            es.setData('lastKillzone', time);
            // Telegraph: outer + inner rings communicate the safe zones
            // (≤80 melee tuck-in, ≥220 ranged kite-out, 80-220 lethal).
            // The rings are gameplay-essential so they stay; on top of
            // them we play a violet conjure-sigil VFX
            // (`playShadowlordKillzoneTelegraph`, ree-pixel-magic 2.png)
            // so the warmup reads as "Malakhar is conjuring SOMETHING"
            // instead of "two placeholder rings appeared".
            const inner = this.add.circle(es.x, es.y, 80, 0x000000, 0).setStrokeStyle(2, 0x9d4dff, 0.95).setDepth(1);
            const outer = this.add.circle(es.x, es.y, 220, 0x000000, 0).setStrokeStyle(3, 0xff2080, 0.95).setDepth(1);
            this.tweens.add({ targets: [inner, outer], alpha: { from: 1, to: 0.4 }, duration: 700, yoyo: true, repeat: 0 });
            this.vfx.playShadowlordKillzoneTelegraph(es.x, es.y, 380);
            this.showDamageNumber(es.x, es.y - 140, '☠ KILL ZONE', '#ff2080');
            if (this.anims.exists('enemy_shadowlord_attack3')) {
              es.off('animationcomplete');
              es.play('enemy_shadowlord_attack3');
              es.once('animationcomplete', () => {
                if (es.active && this.anims.exists('enemy_shadowlord_idle')) es.play('enemy_shadowlord_idle');
              });
            }
            this.time.delayedCall(1400, () => {
              if (!es.active) return;
              this.vfx.playShadowlordRift(es.x, es.y - 20);
              const kzBase = Math.max(1, Math.floor(es.enemyRef.attack * 4.0));
              for (const h of this.heroSprites) {
                if (h.heroRef.isDead) continue;
                const d = Phaser.Math.Distance.Between(es.x, es.y, h.x, h.y);
                if (d <= 80 || d >= 220) continue; // safe zones (≤80 melee tuck, ≥220 ranged kite)
                const mit = Math.max(1, Math.floor(kzBase * (1 - spellMitigation(this.effStats(h.heroRef).defense))));
                const dmg = applyShadowResist(mit, h);
                this.applyDamageToHero(h, dmg, undefined, true);
                this.showDamageNumber(h.x, h.y - 30, `${dmg}`, '#ff2080');
                if (h.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Kill Zone (${es.enemyRef.name})`;
                  this.handleHeroDeath(h);
                }
              }
              this.time.delayedCall(220, () => { inner.destroy(); outer.destroy(); });
            });
          }
        }
      }

      // ── Telurion (boss_telurion) — Earth/Storm dragon, event boss ─────
      if (es.enemyRef.defId === 'boss_telurion') {
        const applyNatureResist = (baseDmg: number, target: typeof this.heroSprites[0]): number => {
          const resist = this.getHeroResist(target.heroRef, 'nature');
          if (resist <= 0) return baseDmg;
          return Math.max(1, Math.floor(baseDmg * (1 - Math.min(resist, 75) / 100)));
        };

        // Spell 1: Stormcall — chain lightning on the 2 highest-HP heroes for
        // 60% atk each. Splashes to nearby allies (≤ 80 px) if the heroes are
        // spread, so a tightly-packed party catches collateral damage. 8 s CD.
        const STORMCALL_CD = 8000;
        const lastStorm = (es.getData('lastStormcall') as number) || 0;
        if (time - lastStorm >= STORMCALL_CD) {
          const aliveSorted = this.heroSprites
            .filter((h) => !h.heroRef.isDead)
            .sort((a, b) => b.heroRef.stats.hp - a.heroRef.stats.hp);
          const primaries = aliveSorted.slice(0, 2);
          if (primaries.length > 0) {
            es.setData('lastStormcall', time);
            // Stormcall uses the snappy 4-frame Attack_1 (bolt cast).
            this.playSpellCastAnim(es, 'attack');
            this.showDamageNumber(es.x, es.y - 130, '⚡ STORMCALL', '#80c8ff');
            const stormBase = Math.max(1, Math.floor(es.enemyRef.attack * 0.60));
            for (const target of primaries) {
              // Stormcall hits each primary individually → cyan single-
              // target ring/burst (magic-spell-pack 8, fx_telurion_st).
              // The orb now ARCS from Telurion's mouth to the hero's chest
              // so the spell reads as something Telurion threw, not a free
              // bolt that spawned on the hero's head. Damage is held until
              // the orb actually lands (travel duration returned from VFX).
              const travelMs = this.vfx.playTelurionStrikeTravel(es.x, es.y - 30, target.x, target.y - 20);
              this.time.delayedCall(travelMs + 60, () => {
                if (target.heroRef.isDead || this.gsm.godMode) return;
                const mit = Math.max(1, Math.floor(stormBase * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
                const dmg = applyNatureResist(mit, target);
                this.applyDamageToHero(target, dmg, undefined, true /* AOE: Stormcall */);
                this.showDamageNumber(target.x, target.y - 30, `${dmg}`, '#80c8ff');
                this.applySpellReflect(target, es, dmg);
                if (target.heroRef.stats.hp <= 0) {
                  this.lastDeathSource = `Stormcall (${es.enemyRef.name})`;
                  this.handleHeroDeath(target);
                }
                // Splash: any other ally within 80 px takes 50 % damage.
                for (const other of this.heroSprites) {
                  if (other === target || other.heroRef.isDead) continue;
                  if (Phaser.Math.Distance.Between(target.x, target.y, other.x, other.y) > 80) continue;
                  const splash = Math.max(1, Math.floor(dmg * 0.5));
                  this.applyDamageToHero(other, splash, undefined, true /* AOE: Stormcall splash */);
                  this.showDamageNumber(other.x, other.y - 30, `${splash}`, '#a8d8ff', '11px');
                  if (other.heroRef.stats.hp <= 0) {
                    this.lastDeathSource = `Stormcall splash (${es.enemyRef.name})`;
                    this.handleHeroDeath(other);
                  }
                }
                // Splash also catches summons within 80 px.
                this.applyAoeDamageToSummons(target.x, target.y, 80, Math.floor(dmg * 0.5));
              });
            }
          }
        }

        // Spell 2: Verdant Spike Field — drops 4 spike telegraphs in front of
        // Telurion, 1.5 s warmup, then erupts: heroes inside take 150 % atk +
        // a 4 s nature DoT. 20 s CD.
        const SPIKE_CD = 20000;
        const lastSpike = (es.getData('lastSpike') as number) || 0;
        if (time - lastSpike >= SPIKE_CD) {
          const aliveHeroes = this.heroSprites.filter((h) => !h.heroRef.isDead);
          if (aliveHeroes.length > 0) {
            es.setData('lastSpike', time);
            // Verdant Spike uses the 10-frame Attack_2 (wide fire-breath cast).
            this.playSpellCastAnim(es, 'attack2');
            // Cast text + telegraphs / damage numbers retuned to bronze /
            // ember palette per user feedback — the previous green VFX
            // (`#7fdc6c` everywhere + `🌿` emoji) read as nature-druid and
            // didn't fit Telurion's earth-storm theme. Switched to a
            // muted-bronze ground telegraph and a dim-orange damage
            // number so the spike beats blend with the rest of the
            // boss's cast palette.
            this.showDamageNumber(es.x, es.y - 130, 'EARTH SPIKES', '#c8973a');
            const spikePositions: Array<{ x: number; y: number }> = [];
            for (let i = 0; i < 4; i++) {
              spikePositions.push({
                x: es.x - 120 - i * 80,
                y: 220 + (i % 2) * 100,
              });
            }
            for (const pos of spikePositions) {
              const telegraph = this.add.circle(pos.x, pos.y, 14, 0x7a4a18, 0.55)
                .setDepth(2).setStrokeStyle(2, 0xc8973a, 0.85);
              this.tweens.add({
                targets: telegraph, alpha: { from: 0.55, to: 0.95 }, duration: 750, yoyo: true, repeat: 0,
                onComplete: () => telegraph.destroy(),
              });
            }
            this.time.delayedCall(1500, () => {
              const spikeBase = Math.max(1, Math.floor(es.enemyRef.attack * 2.2));
              for (const pos of spikePositions) {
                this.vfx.playTelurionAoe(pos.x, pos.y);
                for (const target of aliveHeroes) {
                  if (target.heroRef.isDead) continue;
                  if (Phaser.Math.Distance.Between(pos.x, pos.y, target.x, target.y) > 60) continue;
                  const mit = Math.max(1, Math.floor(spikeBase * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
                  const dmg = applyNatureResist(mit, target);
                  this.applyDamageToHero(target, dmg, undefined, true /* AOE: Verdant Spike */);
                  this.showDamageNumber(target.x, target.y - 30, `${dmg}`, '#d97706');
                  // Hero DoT removed — `applyDebuff` only knows how to
                  // apply to enemies (it reads `enemy.debuffs.find`).
                  // Calling it on `heroRef` was crashing every Verdant
                  // Spike hit:
                  //   "Cannot read properties of undefined (reading 'find')"
                  //   debuffs.js:51 ← BattleScene.ts:11904 callback
                  // Heroes use a different status pipeline; the up-front
                  // hit is plenty for this beat.
                  if (target.heroRef.stats.hp <= 0) {
                    this.lastDeathSource = `Earth Spike (${es.enemyRef.name})`;
                    this.handleHeroDeath(target);
                  }
                }
                this.applyAoeDamageToSummons(pos.x, pos.y, 60, spikeBase);
              }
            });
          }
        }

        // Spell 3: Quake Roar — all heroes within 400 px take ~40 % atk + are
        // ROOTED for 5 s (can't move/swap). 30 s CD.
        const QUAKE_CD = 30000;
        const lastQuake = (es.getData('lastQuake') as number) || 0;
        if (time - lastQuake >= QUAKE_CD) {
          const inRange = this.heroSprites.filter((h) =>
            !h.heroRef.isDead && Phaser.Math.Distance.Between(es.x, es.y, h.x, h.y) <= 400,
          );
          if (inRange.length > 0) {
            es.setData('lastQuake', time);
            // Quake Roar uses the 10-frame Attack_2 (wide-mouth cast).
            this.playSpellCastAnim(es, 'attack2');
            this.showDamageNumber(es.x, es.y - 140, '⛰ QUAKE ROAR', '#c89040');
            this.cameras.main.shake(420, 0.0080);
            // Quake Roar shockwave — large tornado AoE centered on the
            // dragon (magic-spell-pack 9, fx_telurion_aoe). Per-target
            // tornadoes also play below so every hit visibly registers
            // even if the central burst is far from a back-row hero.
            this.vfx.playTelurionAoe(es.x, es.y - 20, 280);
            const quakeBase = Math.max(1, Math.floor(es.enemyRef.attack * 0.40));
            for (const target of inRange) {
              const mit = Math.max(1, Math.floor(quakeBase * (1 - spellMitigation(this.effStats(target.heroRef).defense))));
              const dmg = applyNatureResist(mit, target);
              this.applyDamageToHero(target, dmg, undefined, true /* AOE: Quake Roar */);
              this.showDamageNumber(target.x, target.y - 30, `${dmg}`, '#c89040');
              // Per-target AoE tornado so every hit reads visually. Smaller
              // size than the central shockwave so back-row hits don't
              // crowd front-row tornadoes.
              this.vfx.playTelurionAoe(target.x, target.y - 20, 140);
              // Root: re-uses _frostRooted flag (gates auto-attacks AND blocks
              // the slot-swap path via isHeroBusy / handleHeroSwapClick).
              // CC resist sigil + Resolute talent gate the application.
              if (this.isMainHero(target) && this.rollCcResist('root')) {
                this.showDamageNumber(target.x, target.y - 50, 'RESIST ⛰', '#fbbf24', '11px');
              } else {
                (target.heroRef as any)._frostRooted = true;
                (target.heroRef as any)._frostRootedTimer = Math.max(((target.heroRef as any)._frostRootedTimer ?? 0), 5000);
                this.showDamageNumber(target.x, target.y - 50, '⛰ ROOTED 5s', '#c89040', '11px');
              }
              if (target.heroRef.stats.hp <= 0) {
                this.lastDeathSource = `Quake Roar (${es.enemyRef.name})`;
                this.handleHeroDeath(target);
              }
            }
            // Quake Roar also catches summons inside the 400 px radius.
            this.applyAoeDamageToSummons(es.x, es.y, 400, quakeBase);
          }
        }
      }

      const hpPct = es.enemyRef.currentHp / es.enemyRef.maxHp;
      // Boss HP-bar Y clamp — the top icon row (camera/music/log/social)
      // sits centred at y=18 inside a 36-px-tall bar (so spans 0-36). For
      // tall bosses (Malakhar at 297 px display + name label 9 px above
      // the bar) the natural barY = es.y - 297 + 18 = ~39, which lands the
      // name label at y=30 — directly underneath the icon row. Clamp the
      // boss tier to a minimum y of 75 so the name (66) sits clear of the
      // 36-px icon strip with breathing room. Non-boss tiers keep the
      // unclamped value since their sprites are short enough to clear
      // naturally.
      const rawBarY = es.y - es.displayHeight + 18;
      const esBarY = es.enemyRef.tier === 'boss' ? Math.max(75, rawBarY) : rawBarY;
      const esBarW = es.enemyRef.tier === 'boss' ? 90 : es.enemyRef.tier === 'elite' ? 65 : 42;
      es.hpBar.width = esBarW * Math.max(0, hpPct);
      es.hpBarBg.width = esBarW;
      es.hpBar.setPosition(es.x, esBarY);
      es.hpBarBg.setPosition(es.x, esBarY);
      const eliteStar = es.getData('eliteStar') as Phaser.GameObjects.Text | undefined;
      if (eliteStar) eliteStar.setPosition(es.x + esBarW / 2 + 7, esBarY);
      const bossSkull = es.getData('bossSkull') as Phaser.GameObjects.Text | undefined;
      if (bossSkull) bossSkull.setPosition(es.x + esBarW / 2 + 9, esBarY);
      const lieutBadge = es.getData('lieutenantBadge') as Phaser.GameObjects.Text | undefined;
      if (lieutBadge) lieutBadge.setPosition(es.x + esBarW / 2 + 9, esBarY);
      if (es.enemyRef.tier === 'boss' && es.nameText) es.nameText.setPosition(es.x, esBarY - 9);
      if ((es.enemyRef as any)._isLieutenant && es.nameText) es.nameText.setPosition(es.x, esBarY - 9);
      // Persistent fear skull/glow badge removed — one-shot fear VFX is now handled in playFearWave().
      // Clean up any legacy badges left by previously-cached scenes.
      {
        const leftoverIcon  = es.getData('fearIcon')  as Phaser.GameObjects.Text    | undefined;
        const leftoverGlow  = es.getData('fearGlow')  as Phaser.GameObjects.Ellipse | undefined;
        const leftoverGlow2 = es.getData('fearGlow2') as Phaser.GameObjects.Ellipse | undefined;
        if (leftoverIcon || leftoverGlow || leftoverGlow2) {
          if (leftoverGlow) this.tweens.killTweensOf(leftoverGlow);
          leftoverIcon?.destroy();
          leftoverGlow?.destroy();
          leftoverGlow2?.destroy();
          es.setData('fearIcon', undefined);
          es.setData('fearGlow', undefined);
          es.setData('fearGlow2', undefined);
        }
      }
      // Slightly widen shadow while walking (mid-stride stance ~15% wider)
      // Shadow scales with actual display width for per-sprite fidelity
      const ratio = this.getEnemyShadowRatio(es);
      const baseW = Math.max(28, Math.round(es.displayWidth * ratio));
      const isWalking = es.anims.currentAnim?.key.endsWith('_walk') ?? false;
      es.shadow.width = isWalking ? Math.round(baseW * 1.10) : baseW;
      es.shadow.setPosition(es.x, es.y);
    }
  }

  private spawnEnemy(enemy: EnemyInstance, forceSide?: 'left' | 'right'): void {
    // Assign to a random lane
    const lane = Math.floor(Math.random() * LANE_Y.length);
    // Spawn side — rear-flank roll per biome (Shadow Realm 45 %, Demon Rift 30 %,
    // others 0 %). Rear flankers spawn at the LEFT edge and walk RIGHT toward
    // the back-row heroes. Bosses never rear-spawn (they're the main threat, not a flank).
    // `forceSide` overrides the biome roll — the Horde of Goblins event uses this
    // to guarantee a clean 50/50 bidirectional stream regardless of current biome.
    const canRearFlank = this.biomeRearFlankChance > 0 && enemy.tier !== 'boss';
    const isRearFlank = forceSide === 'left'
      ? true
      : forceSide === 'right'
        ? false
        : canRearFlank && Math.random() < this.biomeRearFlankChance;
    // Tier-aware right-edge clamp — keeps the full sprite on-screen (GAME_W=960) with
    // a 12 px visual margin. Without this, bosses (230 px display @ origin 0.5) at x=940
    // hang ~95 px off the right edge on spawn. Values assume the displaySize block below.
    const previewKeyEarly = this.getEnemySpriteKey(enemy);
    const isTelurionEarly = previewKeyEarly === 'enemy_telurion';
    const isShadowlordEarly = previewKeyEarly === 'enemy_shadowlord';
    const isDragonEarly = previewKeyEarly === 'enemy_dragon' || isTelurionEarly;
    const isLavaGolemEarly = previewKeyEarly === 'boss_lava_golem';
    const isArchivistEarly = previewKeyEarly === 'boss_skeleton_mage';
    // Dragons render at 1.1× the standard 230 px boss size (≈ 253 px) so the
    // wing silhouette reads as the apex threat it's supposed to be. Telurion
    // gets bumped to 290 (≈ 1.26×) per user feedback — the +30 % stat bump
    // wants matching visual weight, and the bigger frame also lets the
    // descent + lane pin (see Telurion spawn block) keep him obviously
    // clear of the bottom HUD bar. Malakhar (Shadowlord) bumped to 297
    // (≈ 1.5× the standard 198) so the stage-50 endboss has clearly more
    // visual weight than a regular zone boss — apex of the run.
    const displayWForClamp = enemy.tier === 'boss'
      ? (isTelurionEarly ? 290 : isShadowlordEarly ? 297 : isDragonEarly ? 253 : isLavaGolemEarly ? 230 : isArchivistEarly ? 180 : 198)
      : enemy.tier === 'elite' ? 135 : 108;
    const maxRightX = GAME_W - displayWForClamp / 2 - 12;
    const minLeftX = displayWForClamp / 2 + 12;
    // Right-side spawn at GAME_W - 80…-20 (~74 px of sprite visible for a
    // 108-px minion). Rear-flank used to spawn at [-70, -30] which left
    // bigger sprites (elite/boss = 135-230 px wide) entirely off-screen
    // until they had walked in 30+ px — the user reported "left enemies
    // are still out of the map." Pulled the rear spawn band closer to the
    // edge so even a 230 px boss has ~85 px peeking at spawn.
    const rawX = isRearFlank ? -5 - Math.random() * 20 : ENEMY_SPAWN_X + Math.random() * 60;
    // Right-spawners clamp to maxRightX so they don't spawn fully off the
    // right edge. Rear flankers don't need a similar clamp now that the
    // spawn band sits next to the visible map.
    const x = isRearFlank ? rawX : Math.min(rawX, maxRightX);
    const y = LANE_Y[lane] + (Math.random() - 0.5) * 20;

    // Ground shadow — sized from sprite display width with per-sprite ratio
    // Compute the display size the same way we set it below so shadow matches
    const previewSpriteKey = this.getEnemySpriteKey(enemy);
    const prevIsTelurion = previewSpriteKey === 'enemy_telurion';
    const prevIsShadowlord = previewSpriteKey === 'enemy_shadowlord';
    const prevIsDragon = previewSpriteKey === 'enemy_dragon' || prevIsTelurion;
    const prevIsLavaGolem = previewSpriteKey === 'boss_lava_golem';
    // Dragons use a slightly larger shadow footprint to match the 1.1× sprite bump.
    // Telurion gets a wider shadow (~320 vs 281) to match his bumped 290 px display.
    // Malakhar gets ~330 to match his 297 px sprite.
    const displayW = enemy.tier === 'boss' ? (prevIsTelurion ? 320 : prevIsShadowlord ? 330 : prevIsDragon ? 281 : prevIsLavaGolem ? 256 : 220) : enemy.tier === 'elite' ? 150 : 120;
    const shadowRatio = this.shadowRatioForSpriteKey(previewSpriteKey, enemy.tier);
    const shadowW = Math.max(28, Math.round(displayW * shadowRatio));
    const shadowH = enemy.tier === 'boss' ? 7 : enemy.tier === 'elite' ? 6 : 5;
    const shadow = this.add.ellipse(x, y, shadowW, shadowH, 0x000000, 0.82).setDepth(1.5);

    // Cursed Greed — +20 % boss HP applied ONCE at spawn. Bosses only (tier
    // check gates this); the mutation is written back to enemy.maxHp/currentHp
    // so every HP read after this point sees the buffed value. Since this is
    // the single spawn call per enemy, there is no double-apply path.
    {
      const bEff = this.getBoonEffects();
      if (enemy.tier === 'boss' && bEff.bossHpMult > 1) {
        const mult = bEff.bossHpMult;
        enemy.maxHp     = Math.max(1, Math.floor(enemy.maxHp     * mult));
        enemy.currentHp = enemy.maxHp;
      }
    }
    // Inferno save — bosses get a +20 % HP/ATK bump on top of every other
    // multiplier so the endgame mode actually reads as a step up. Applied
    // once per spawn (mutated into enemy.maxHp/currentHp/attack so all
    // downstream reads see the bumped values). Gated on the per-save
    // `infernoSave` flag — the legacy mid-run "Inferno Descent" used
    // `this.infernoMode` and that path stays untouched for backwards-
    // compat with old saves until the next pass retires it entirely.
    if (this.gsm.player.infernoSave && enemy.tier === 'boss') {
      enemy.maxHp     = Math.max(1, Math.floor(enemy.maxHp     * 1.20));
      enemy.currentHp = enemy.maxHp;
      enemy.attack    = Math.max(1, Math.floor(enemy.attack    * 1.20));
    }

    const sprite = this.add.sprite(x, y, this.getEnemySpriteKey(enemy)) as EnemySprite;
    sprite.enemyRef = enemy;
    sprite.lane = lane;
    sprite.shadow = shadow;
    sprite.maxRightX = maxRightX;
    sprite.minLeftX = minLeftX;
    // Rear flankers target the back row directly; right-spawners target the front attack band.
    sprite.targetX = isRearFlank
      ? HERO_ARC_CENTER_X - HERO_ARC_RADIUS + 10
      : HERO_ARC_CENTER_X + HERO_ARC_RADIUS + 30 + Math.random() * 40;
    // Stamp spawn side so the fear-flee branch knows which edge to flee toward.
    sprite.setData('spawnSide', isRearFlank ? 'rear' : 'right');

    // Sprites face RIGHT natively — right-spawners flip to face LEFT (toward heroes);
    // rear-flankers stay unflipped so they face RIGHT (also toward heroes, from the other side).
    sprite.setFlipX(!isRearFlank);
    // Scale by tier; dragon (256px source) needs a larger display size.
    // Archivist (boss_skeleton_mage / Wanderer Magican): 1.5× hero display size
    // (heroes render ~119 px tall, so 180 gives the robed wizard a boss silhouette
    // without crowding the lane).
    const spriteKey = this.getEnemySpriteKey(enemy);
    const isTelurion = spriteKey === 'enemy_telurion';
    const isDragon = spriteKey === 'enemy_dragon' || isTelurion;
    const isLavaGolem = spriteKey === 'boss_lava_golem';
    const isArchivist = spriteKey === 'boss_skeleton_mage';
    if (enemy.tier === 'boss') {
      // Dragons render at 1.1× the standard 230 boss size so the wing
      // silhouette earns its "apex threat" read. Telurion bumped to 290
      // for the +30 % stat-tier event boss footprint (matches the early
      // displayWForClamp bump above). Telurion's spritesheet is vertically
      // trimmed (256×96 frames, see buildTelurionSheet.cjs) so we preserve
      // its 8:3 aspect rather than forcing square — 290×109. Without this,
      // setDisplaySize(290, 290) would 3× stretch the dragon vertically.
      if (isTelurion) {
        sprite.setDisplaySize(290, 109);
      } else if (spriteKey === 'enemy_shadowlord') {
        // Stage-50 endboss footprint — 1.5× the standard 198 px boss size
        // so Malakhar reads as visibly more dangerous than a regular zone
        // boss. Sheet is square (256×256) so a square display is correct.
        sprite.setDisplaySize(297, 297);
      } else {
        const bossSize = isDragon ? 253 : isLavaGolem ? 230 : isArchivist ? 180 : 198;
        sprite.setDisplaySize(bossSize, bossSize);
      }
    }
    else if (enemy.tier === 'elite') sprite.setDisplaySize(135, 135);
    else sprite.setDisplaySize(108, 108);
    sprite.setOrigin(0.5, 1.0).setDepth(2 + sprite.y * 0.001); // feet exactly at spawn y (lane Y)
    // Gorgon Echo — spectral variant, semi-transparent for ghostly feel
    if (enemy.defId === 'boss_gorgon_echo') {
      sprite.setAlpha(0.70);
    }
    // Endless-mode ghost — pale icy-white tint + pulsing alpha so the
    // demon source sprite reads as a translucent revenant instead of a
    // solid imp. Pulse runs on a slow yoyo so the swarm doesn't strobe
    // when 5 ghosts are on screen — the staggered start (random delay)
    // also avoids in-phase pulsing across the wave.
    if (enemy.defId === 'endless_ghost') {
      sprite.setTint(0xc8e0ff);
      sprite.setAlpha(0.55);
      this.tweens.add({
        targets: sprite,
        alpha: { from: 0.55, to: 0.30 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 800,
        ease: 'Sine.easeInOut',
      });
      // Spectral entry rift — pale cyan/white pulse so the ghost reads
      // as "fading IN through the veil" rather than just walking on.
      // Distinct palette from the violet Shadow Veil portal so the two
      // threat tiers stay visually separated.
      this.vfx.playGhostRift(x, y - 30);
    }
    // Sentinel of Telurion — gold-tinted skeleton elite spawned by the
    // Telurion's Lair event. Reuses the standard skeleton sprite with a
    // runtime gold tint so we don't bake a separate spritesheet.
    if (enemy.defId === 'skeleton_elite_gold') {
      sprite.setTint(0xc89040);
    }
    // Archivist (Haunted Crypts boss) — pale frost motes rising every 500ms signal
    // "arcane caster" and give the dark-robed Wanderer Magican sprite extra contrast
    // against the dim Crypts palette.
    if (enemy.defId === 'boss_skeleton_mage') {
      const archivistAura = this.time.addEvent({
        delay: 500, loop: true,
        callback: () => {
          if (!sprite.active || sprite.enemyRef.currentHp <= 0) { archivistAura.remove(); return; }
          this.vfx.playArchivistAuraTick(sprite.x, sprite.y);
        },
      });
      sprite.setData('archivistAura', archivistAura);
    }

    // White Claw (Dark Forest boss) — uses its own White_Werewolf sprite, no tint needed.
    // Persistent pale aura ember drift while alive — signals "this one buffs his pack".
    if (enemy.defId === 'boss_wolf_lord') {
      const howlAura = this.time.addEvent({
        delay: 600, loop: true,
        callback: () => {
          if (!sprite.active || sprite.enemyRef.currentHp <= 0) { howlAura.remove(); return; }
          this.vfx.playWolfBossAuraTick(sprite.x, sprite.y);
        },
      });
      sprite.setData('wolfBossAura', howlAura);
    }

    // Inferno recolor — crimson tint by tier (bosses darkest, normals mid-red).
    // Restored per user follow-up: the previous "remove tint" pass was a
    // misread of "keep enemies colors" — they meant the inferno BG should
    // not crush the sprites' colours, NOT remove the per-enemy crimson
    // tint that signals "this is the harsher mode". So the bg + vignette
    // stay toned-down (see drawBattlefield) but enemy sprites get their
    // tier-graded red wash back.
    if (this.infernoMode) {
      const infernoTint = enemy.tier === 'boss' ? 0x8b0000
        : enemy.tier === 'elite' ? 0xaa1a00
        : 0xcc2200;
      sprite.setTint(infernoTint);
      sprite.setData('infernoTint', infernoTint);
    }

    // Bar sits near the sprite head (18px below sprite top so it overlays the character)
    const barY = y - sprite.displayHeight + 18;
    const barW = enemy.tier === 'boss' ? 90 : enemy.tier === 'elite' ? 65 : 42;
    const barH = enemy.tier === 'boss' ? 6 : enemy.tier === 'elite' ? 5 : 3;
    sprite.hpBarBg = this.add.rectangle(x, barY, barW, barH, 0x333333).setDepth(3);
    sprite.hpBar = this.add.rectangle(x, barY, barW, barH, 0xef4444).setDepth(3);

    // Small star icon to the right of the HP bar for elite enemies. Skipped
    // for Lieutenants — they get their own (single) badge below; without
    // this guard the elite star and the lieutenant star drew on top of
    // each other and read as a smudged double-star.
    if (enemy.tier === 'elite' && !(enemy as any)._isLieutenant) {
      const star = this.add.text(x + barW / 2 + 7, barY, '★', {
        fontSize: '8px', fontFamily: 'Arial', color: '#fbbf24',
      }).setOrigin(0.5).setDepth(4);
      sprite.setData('eliteStar', star);
      // Spawn VFX — tells the player "an elite just joined the fight"
      this.vfx.playEliteSpawn(x, y - 20);
      // Persistent aura — ember drift tick every ~900ms while the elite lives
      const auraTimer = this.time.addEvent({
        delay: 900, loop: true,
        callback: () => {
          if (!sprite.active || sprite.enemyRef.currentHp <= 0) { auraTimer.remove(); return; }
          this.vfx.playEliteAuraTick(sprite.x, sprite.y);
        },
      });
      sprite.setData('eliteAuraTimer', auraTimer);
    }
    // Lieutenant — wave-3 mid-zone mini-boss. Tagged on the EnemyInstance during
    // wave generation. Gold tint + double-star badge + nameplate + persistent
    // buff aura that boosts nearby allies' move + attack speed.
    if ((enemy as any)._isLieutenant) {
      // Tint shifted from pure gold (0xffd060) to a red-leaning amber
      // (0xff8050) per user feedback — Lieutenants now read as "blooded
      // commander" rather than "shiny gold mob," distinguishing them
      // visually from the regular elite tier without losing the warm hue.
      sprite.setTint(0xff8050);
      sprite.setDisplaySize(sprite.displayWidth * 1.10, sprite.displayHeight * 1.10);
      // Single star (was ★★, which overlapped with the elite block's own
      // ★ from above and read as a smudged blob — kept just one).
      const badge = this.add.text(x + barW / 2 + 9, barY, '★', {
        fontSize: '10px', fontFamily: 'Arial', color: '#ff8050', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(4);
      sprite.setData('lieutenantBadge', badge);
      // Use the Lieutenant's proper name when set (per-faction pool — see
      // LIEUTENANT_NAMES). Falls back to the legacy "Lieutenant <name>"
      // format if the field somehow isn't stamped (e.g. promoted via a
      // path that hasn't been updated to the new naming convention).
      const lieutenantName = (enemy as any)._lieutenantName as string | undefined;
      const nameLabel = lieutenantName ? `Lt. ${lieutenantName}` : `Lieutenant ${enemy.name}`;
      sprite.nameText = this.add.text(x, barY - 9, nameLabel, {
        fontSize: '10px', fontFamily: 'Arial', color: '#ff8050', fontStyle: 'bold',
        stroke: '#3a1a08', strokeThickness: 3,
      }).setOrigin(0.5, 1).setDepth(4);
      this.vfx.playEliteSpawn(x, y - 20);
      // Buff aura — every 500 ms, stamp a +25 % atkSpd / +20 % move buff on every
      // faction-matched ally inside 200 px. The flags are time-stamped so the
      // buff naturally expires (600 ms grace) when out of range / Lieutenant dies.
      const auraTimer = this.time.addEvent({
        delay: 500, loop: true,
        callback: () => {
          if (!sprite.active || sprite.enemyRef.currentHp <= 0) { auraTimer.remove(); return; }
          // Drifty gold-ember tick to make the aura readable.
          this.vfx.playEliteAuraTick(sprite.x, sprite.y);
          const expiry = this.time.now + 600;
          for (const other of this.enemySprites) {
            if (other === sprite || !other.active) continue;
            if (other.enemyRef.currentHp <= 0) continue;
            if (other.enemyRef.faction !== sprite.enemyRef.faction) continue;
            if (Phaser.Math.Distance.Between(other.x, other.y, sprite.x, sprite.y) > 200) continue;
            (other.enemyRef as any)._lieutenantSpeedUntil = expiry;
            (other.enemyRef as any)._lieutenantAtkSpdUntil = expiry;
            // Per-ally buff VFX so the player can see WHO is being empowered,
            // not just the lieutenant. Small gold halo + 2-particle puff on
            // each affected mob's feet. Cheap (1 graphics ring + 2 particles
            // per ally) so the aura tick stays performant even with 10+
            // mobs in range.
            this.vfx.playLieutenantAllyBuffTick(other.x, other.y);
          }
        },
      });
      sprite.setData('lieutenantAuraTimer', auraTimer);
    }
    // Boss nameplate — name above the HP bar, skull icon to the right of it.
    if (enemy.tier === 'boss') {
      const skull = this.add.text(x + barW / 2 + 9, barY, '\u2620', {
        fontSize: '11px', fontFamily: 'Arial', color: '#e11d48',
      }).setOrigin(0.5).setDepth(4);
      sprite.setData('bossSkull', skull);
      sprite.nameText = this.add.text(x, barY - 9, enemy.name, {
        fontSize: '10px', fontFamily: 'Arial', color: '#fca5a5',
        stroke: '#000000', strokeThickness: 3,
      }).setOrigin(0.5, 1).setDepth(4);
    }

    this.enemySprites.push(sprite);

    // Focus-target click — extracted so event-mob spawn paths
    // (Goblino, RotBomber) can wire the same handler without
    // duplicating logic.
    this.wireFocusTargetClick(sprite);

    // Start walk animation; ranged enemies start at idle until they reach
    // firing range. Archivist no longer skips idle — his idle is now sourced
    // from a standing-pose sheet (was seated/crouched in the original frames).
    const texKey = this.getEnemySpriteKey(enemy);
    const startKey = enemy.attackRange === 'ranged' ? `${texKey}_idle` : `${texKey}_walk`;
    const fallbackKey = `${texKey}_walk`;
    if (this.anims.exists(startKey)) sprite.play(startKey);
    else if (this.anims.exists(fallbackKey)) sprite.play(fallbackKey);
  }

  /** Clear a status tint from an enemy sprite, restoring its inferno tint if applicable */
  private restoreEnemyTint(es: EnemySprite): void {
    // persistentTint (per-def recolor e.g. White Claw) beats inferno, then falls back to clear.
    const persistent = es.getData('persistentTint');
    const infernoTint = es.getData('infernoTint');
    if (persistent != null) es.setTint(persistent);
    else if (infernoTint != null) es.setTint(infernoTint);
    else es.clearTint();
  }

  /** Per-sprite ratio of shadow width to sprite display width.
   *  Thin/tall sprites get a narrower shadow, wide/bulky bosses get wider. */
  private shadowRatioForSpriteKey(spriteKey: string, tier: string): number {
    // Slim/tall silhouettes (humanoid skeletons, dark elf, etc.) — shadow narrower than display
    const slim = ['enemy_skeleton', 'enemy_dark_elf', 'enemy_zombie', 'enemy_bandit', 'enemy_archer', 'enemy_cultist'];
    // Wide/bulky — shadow wider
    const wide = ['enemy_troll', 'enemy_ogre', 'boss_lava_golem', 'enemy_demon'];
    // Dragons/4-legged — even wider on the ground
    const sprawling = ['enemy_dragon', 'enemy_wolf', 'enemy_spider'];
    if (sprawling.some((k) => spriteKey.includes(k))) return 0.55;
    if (wide.some((k) => spriteKey.includes(k))) return 0.50;
    if (slim.some((k) => spriteKey.includes(k))) return 0.30;
    // Default tier-based: boss big, elite medium, normal small
    return tier === 'boss' ? 0.42 : tier === 'elite' ? 0.40 : 0.38;
  }

  /** Runtime shadow ratio lookup from a spawned EnemySprite. */
  private getEnemyShadowRatio(es: EnemySprite): number {
    return this.shadowRatioForSpriteKey(es.texture.key, es.enemyRef.tier);
  }

  private getEnemySpriteKey(enemy: EnemyInstance): string {
    const mapping: Record<string, string> = {
      // Beast — Dark Forest
      wolf: 'enemy_wolf', spider: 'enemy_wolf', boar: 'enemy_wolf',
      bear: 'enemy_wolf', alpha_wolf: 'enemy_wolf',
      // Dark Forest boss uses the White_Werewolf sheet
      boss_wolf_lord: 'enemy_whitewolf',
      // Undead — Haunted Crypts
      skeleton: 'enemy_skeleton', ghoul: 'enemy_skeleton', zombie: 'enemy_skeleton',
      wraith: 'enemy_wraith', lich: 'enemy_wraith',
      // Orc — Orcish Wastes
      orc_grunt: 'enemy_orc', goblin: 'enemy_orc', troll: 'enemy_orc',
      orc_warlord: 'enemy_orc_elite', orc_shaman: 'enemy_orc_elite',
      // Demon — Demon Rift
      imp: 'enemy_imp', hellhound: 'enemy_imp',
      succubus: 'enemy_demon', demon_knight: 'enemy_demon',
      // Dragon
      dragon: 'enemy_dragon', whelp: 'enemy_dragon',
      // Endgame
      demoniac_ghost: 'enemy_wraith', void_walker: 'enemy_demon', spectre: 'enemy_wraith',
      // Zone Bosses
      boss_demon1: 'boss_demon1', boss_demon2: 'boss_demon2',
      boss_spearman: 'boss_spearman', boss_darkelf: 'boss_darkelf', boss_gorgon: 'boss_gorgon', boss_voidelf: 'boss_voidelf',
      // Archivist lich (Haunted Crypts, Archive Beneath quest) — identity map or the
      // fallback below would render him as enemy_wolf.
      boss_skeleton_mage: 'boss_skeleton_mage',
      // Satyr
      satyr: 'enemy_satyr',
      // Shadow Realm
      void_worm: 'enemy_void_worm',
      // Minotaur — Ashen Labyrinth
      minotaur_grunt: 'enemy_minotaur', minotaur_berserker: 'enemy_minotaur',
      minotaur_thrower: 'enemy_minotaur_ranged',
      minotaur_champion: 'enemy_minotaur_elite', minotaur_warden: 'enemy_minotaur_elite',
      boss_lava_golem: 'boss_lava_golem',
      // Goblin horde (event-only — never in normal wave pools)
      goblin_mook_a: 'enemy_goblin_a',
      goblin_mook_b: 'enemy_goblin_b',
      goblin_elite:  'enemy_goblin_elite',
      // Telurion's Lair — event-only mobs. Without these entries, defId
      // fell through to the 'enemy_wolf' default, so the dragon and his
      // Sentinels were rendering as werewolves (user-reported bug).
      // skeleton_elite_gold reuses the standard skeleton sheet; spawn-time
      // gold tint is applied separately in the spawn path.
      skeleton_elite_gold: 'enemy_skeleton',
      boss_telurion: 'enemy_telurion',
      // Shadowrealm — stage-50 endboss + endless-mode ghost mob
      boss_shadowlord: 'enemy_shadowlord',
      endless_ghost:   'enemy_ghost',
      // Orcish Wastes echo-boss variant — same body as Gorgon Queen, drawn
      // from the gorgon2 sheet (semi-transparent ghost is set alpha-side
      // in the spawn path). Without this entry the Echo dropped to the
      // 'enemy_wolf' default and rendered as a werewolf in the Wastes.
      boss_gorgon_echo: 'boss_gorgon2',
      // Rot Bomber — random event mob (stage 10+, 15 % per wave). Spawned
      // outside the wave pool via spawnRotBomber which already sets the
      // texture directly, but mapping it here keeps any downstream lookup
      // (animations, restoration, etc.) from falling through to wolf.
      rot_bomber: 'enemy_rotbomber',
    };
    // Faction-based fallback so ANY future unmapped defId at least lands
    // in its own faction's biome (was 'enemy_wolf' — meant any unknown
    // mob rendered as a werewolf in any biome). Beast keeps enemy_wolf
    // because that's still its sheet; everything else picks a sensible
    // per-faction stand-in.
    const fallbackByFaction: Record<string, string> = {
      beast:    'enemy_wolf',
      undead:   'enemy_skeleton',
      orc:      'enemy_orc',
      demon:    'enemy_imp',
      dragon:   'enemy_dragon',
      goblin:   'enemy_goblin_a',
      minotaur: 'enemy_minotaur',
      void:     'enemy_void_worm',
    };
    return mapping[enemy.defId] || fallbackByFaction[enemy.faction] || 'enemy_skeleton';
  }

  private killEnemy(es: EnemySprite, killer: HeroSprite): void {
    if (!es.active) return; // already killed — prevents double-loot from Spear Volley + auto-attack same tick

    // Shadowrealm endless ramp — every endless ghost kill bumps the ramp
    // counter that drives spawn density + per-spawn HP/ATK on subsequent
    // ghosts. Counter survives stage-50 entry (lives on the static
    // shadowrealmPending block) so it persists across the boss-fight ↔
    // endless transition.
    if (BattleScene.shadowrealmPending && es.enemyRef.defId === 'endless_ghost') {
      BattleScene.shadowrealmPending.ghostKills += 1;
      // Update the all-time best when the player passes a milestone of 25
      // ghost kills (rough proxy for "wave reached" — endless has no
      // discrete waves). Persisted on the player so the title screen can
      // surface it across runs.
      const cur = this.gsm.player.shadowrealmEndlessBest ?? 0;
      if (BattleScene.shadowrealmPending.ghostKills > cur) {
        this.gsm.player.shadowrealmEndlessBest = BattleScene.shadowrealmPending.ghostKills;
      }
    }

    // Hero Armory hooks fired on this hero killing an enemy.
    {
      const k = killer.heroRef as any;
      // Goomar Crushing Momentum — set the next-attack armour-pen flag.
      if (this.hasArmoryPassive(killer.heroRef, 'passive_goomar_crushing_momentum')) {
        k._momentumCharge = true;
      }
      // Lootfi Gold Rush — +1 % gold per kill, capped at +15 % for the run
      // (tracked on the hero so the buff doesn't leak across runs).
      if (this.hasArmoryPassive(killer.heroRef, 'passive_lootfi_gold_rush')) {
        const cur = (k._goldRushBonus ?? 0) as number;
        if (cur < 0.15) k._goldRushBonus = Math.min(0.15, cur + 0.01);
      }
      // Kael Energy Surge — restore 15 energy on kill, internal cooldown 3 s
      // so chained kills can't refund the whole bar instantly.
      if (this.hasArmoryPassive(killer.heroRef, 'passive_kael_energy_surge')) {
        const now = Date.now();
        const lastSurge = (k._energySurgeCdExpiry as number) ?? 0;
        if (now >= lastSurge) {
          k._energySurgeCdExpiry = now + 3000;
          const stats = killer.heroRef.stats;
          const maxEnergy = stats.maxEnergy ?? 0;
          stats.energy = Math.min(maxEnergy, (stats.energy ?? 0) + 15);
          this.showDamageNumber(killer.x, killer.y - 30, 'ᛊ +15 NRG', '#5090c0', '11px');
        }
      }
      // Zyphara Cinder Chain — fire-spell kill explodes 20 % of last spell dmg
      // to enemies within 120 px. _lastFireSpellDmg is stamped at spell-resolve
      // time below so the splash uses the right magnitude.
      if (this.hasArmoryPassive(killer.heroRef, 'passive_zyphara_cinder_chain')
        && (k._lastFireSpellTime ?? 0) > Date.now() - 800) {
        const splashSrc = (k._lastFireSpellDmg ?? 0) as number;
        if (splashSrc > 0) {
          const splashDmg = Math.max(1, Math.floor(splashSrc * 0.20));
          for (const other of this.enemySprites) {
            if (other === es || !other.active || other.enemyRef.currentHp <= 0) continue;
            if (Math.abs(other.x - es.x) <= 120 && Math.abs(other.y - es.y) <= 90) {
              other.enemyRef.currentHp -= splashDmg;
              this.showDamageNumber(other.x, other.y - 24, `${splashDmg}`, '#f97316', '11px');
              if (other.enemyRef.currentHp <= 0) this.killEnemy(other, killer);
            }
          }
        }
      }
    }

    // Goblino kill: trigger item shop instead of normal loot
    if (es.isGoblino) {
      this.goblino = null;
      this.goblinoTimer = 0;
      es.hpBar.destroy();
      es.hpBarBg.destroy();
      es.nameText?.destroy();
      es.nameText = undefined;
      es.shadow?.destroy();
      (es.getData('eliteStar')  as Phaser.GameObjects.Text    | undefined)?.destroy();
      (es.getData('bossSkull')  as Phaser.GameObjects.Text    | undefined)?.destroy();
      (es.getData('lieutenantBadge') as Phaser.GameObjects.Text   | undefined)?.destroy();
      (es.getData('lieutenantAuraTimer') as Phaser.Time.TimerEvent | undefined)?.remove();
      (es.getData('fearIcon')   as Phaser.GameObjects.Text    | undefined)?.destroy();
      (es.getData('fearGlow')   as Phaser.GameObjects.Ellipse | undefined)?.destroy();
      (es.getData('fearGlow2')  as Phaser.GameObjects.Ellipse | undefined)?.destroy();
      es.setActive(false);
      this.vfx.playDeathPoof(es.x, es.y);
      this.showDamageNumber(es.x, es.y - 35, 'MIMIC CAUGHT!', '#fbbf24');
      const removeSprite = (): void => {
        const idx = this.enemySprites.indexOf(es);
        if (idx !== -1) this.enemySprites.splice(idx, 1);
        es.destroy();
      };
      this.tweens.add({ targets: es, alpha: 0, delay: 300, duration: 300, onComplete: removeSprite });
      this.showGoblinoShop();
      return;
    }

    // Rot Bomber kill: detonate AoE before running normal loot/cleanup.
    // A `_detonated` flag skips re-detonation when the bomber already blew
    // up from a hero-collision earlier (see updateRotBombers) — without
    // it, the collision blast + kill-by-cleanup double-detonates.
    if (es.isRotBomber) {
      (es.getData('rotAura') as Phaser.GameObjects.Graphics | undefined)?.destroy();
      es.setData('rotAura', undefined);
      if (!(es.enemyRef as any)._detonated) {
        this.detonateRotBomber(es.x, es.y);
      }
    }

    // ── Loot-chance multiplier: all sources are multiplicative on the base drop chance ──
    // +10% means 10% of base, not +10 percentage points (e.g. base 10% × 1.10 = 11%, not 20%)
    const luckBonus   = getTomeBonus(this.gsm.player.tomes, 'luck');  // luck tome: +5% per level
    const hasLuckyLoot = this.heroSprites.some(
      (h) => !h.heroRef.isDead && h.heroRef.abilities.some((a) => a.id === 'lucky_loot'),
    );
    const rk = this.getRelicBonuses(killer.heroRef);  // Scavenger +10%, Fortunekeeper +10%, Goldseer, etc.
    const goblinFoot = this.hasTrophy('item drop') ? 1.12 : 1;
    const infernoLootBoost = this.infernoMode ? 1.5 : 1.0;
    // Stay mode no longer boosts per-kill drop chance — the extra enemies from
    // the count multiplier already give more loot rolls per wave, so layering a
    // drop-chance bump on top was turning stays into a pure farm mode. Gold +
    // harder mobs remain the stay-2+ reward; runes/tomes stay reduced.
    // Crossroads Riches / Cursed Riches scale equipment drop chance too —
    // matches how the gold/rune lines below get the same multiplier.
    const crossroadsModForDrops = this.gsm.player.activeCrossroadsMod
      ? scaleCrossroadsModifier(
          CROSSROADS_MODIFIERS[this.gsm.player.activeCrossroadsMod],
          this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel,
        )
      : null;
    const crossroadsDropMult = crossroadsModForDrops?.lootMult ?? 1;
    const lootDropMult = (1 + luckBonus) * (hasLuckyLoot ? 1.10 : 1) * (1 + rk.lootPct) * goblinFoot * infernoLootBoost * crossroadsDropMult;

    // Loot tome: proportional rarity boost — +5% per level scales all non-common weights by 1.05×
    // (so rare 10% chance × 1.05 = 10.5%, not +5 weight points = ~14%)
    const lootTomeBonus = getTomeBonus(this.gsm.player.tomes, 'loot');
    const rarityUpMult = 1 + lootTomeBonus;
    const effectiveLootTier = es.enemyRef.lootTier; // enemy quality only; tome boost via rarityUpMult
    // Lucky Draw rune — chance to upgrade rare loot to epic
    const rareLootUpgrade = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).rareLootUpgrade;
    const loot = rollLoot(es.enemyRef, this.battleLevel, killer.heroRef.heroClass, killer.heroRef.heroKey, lootDropMult, rarityUpMult, rareLootUpgrade);

    // Shrine of Boons — loot post-processing (applied ONCE per kill's loot roll):
    //   • Cursed Greed (rarityBumpChance) — regenerate each rolled item at +1
    //     rarity tier. Uses exactRarity so the regen's name/stats/specialEffect
    //     match the bumped tier. Skips items already at legendary (max for
    //     non-relic) or unique. Relic rarity floor is enforced inside
    //     generateEquipment so rare+unique jumps stay consistent.
    //   • Merchant's Gamble (relicDropMult=0.5) — cull 50 % of rolled relics.
    //     Applied as a post-filter instead of reaching into rollLoot's RNG so
    //     non-relic items are untouched and the math is obvious.
    const boonLootEff = this.getBoonEffects();
    if (boonLootEff.rarityBumpChance > 0 && loot.items.length > 0) {
      const rarityOrder: Array<'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'> =
        ['common', 'uncommon', 'rare', 'epic', 'legendary'];
      loot.items = loot.items.map((item) => {
        if (Math.random() >= boonLootEff.rarityBumpChance) return item;
        const idx = rarityOrder.indexOf(item.rarity as typeof rarityOrder[number]);
        if (idx < 0 || idx >= rarityOrder.length - 1) return item;
        const bumped = rarityOrder[idx + 1];
        try {
          return generateEquipment(this.battleLevel, effectiveLootTier, es.enemyRef.tier, undefined, 1.0, 0, bumped);
        } catch {
          return item;
        }
      });
    }
    if (boonLootEff.relicDropMult < 1) {
      loot.items = loot.items.filter((item) => {
        if (!item.slot.startsWith('relic')) return true;
        return Math.random() < boonLootEff.relicDropMult;
      });
    }

    // ── Ashen Labyrinth boss: guaranteed 1 epic+ item, 33% chance for a 2nd and 3rd ──
    // Pre-pick the target rarity and pass it via exactRarity so name/stats/effect all
    // key off the chosen tier (fixes the "rare stats stamped as legendary" mismatch).
    if (es.enemyRef.defId === 'boss_lava_golem') {
      const EPIC_RARITIES: Array<'epic' | 'legendary' | 'unique'> = ['epic', 'epic', 'epic', 'legendary', 'legendary', 'unique'];
      const rollEpicPlus = () => {
        const picked = EPIC_RARITIES[Math.floor(Math.random() * EPIC_RARITIES.length)];
        return generateEquipment(this.battleLevel, effectiveLootTier, 'boss', undefined, 1.0, 0, picked);
      };
      loot.items = [rollEpicPlus()]; // guaranteed first drop
      if (Math.random() < 0.33) loot.items.push(rollEpicPlus());
      if (Math.random() < 0.33) loot.items.push(rollEpicPlus());
    }
    // Lucky Loot passive (Lootfi): +10% gold (drop chance already baked into lootDropMult above)
    const luckyLootGoldMult = hasLuckyLoot ? 1.10 : 1;
    loot.gold = Math.floor(loot.gold * luckyLootGoldMult);

    const goldBonus = this.gsm.player.base.upgrades.find((u) => u.id === 'gold_bonus');
    const xpBonus = this.gsm.player.base.upgrades.find((u) => u.id === 'xp_bonus');
    const goldMult = 1 + (goldBonus ? goldBonus.currentLevel * goldBonus.effectValue : 0);
    const xpMult = 1 + (xpBonus ? xpBonus.currentLevel * xpBonus.effectValue : 0);

    const speedPenalty = this.speedPenaltyActive
      ? (this.speedMultiplier >= 10 ? 0.6 : this.speedMultiplier >= 5 ? 0.7 : 0.9)
      : 1;
    const tomes = this.gsm.player.tomes;
    const xpTomeBonus = 1 + getTomeBonus(tomes, 'xp');       // +10% per level
    const luckTomeBonus = 1 + getTomeBonus(tomes, 'luck');    // +5% per level (extra gold/mats)
    const goldTomeBonus = 1 + getTomeBonus(tomes, 'gold');    // +8% per level
    const merchantBlessing = this.gsm.hasActiveConsumable('merchant_blessing') ? 2 : 1;
    // Trophy: Treasure Magnet +40% gold from all kills
    const treasureMagnetMult = this.hasTrophy('+40% gold') ? 1.4 : 1;
    // rk already computed above (getRelicBonuses) — Goldseer +20% gold, Enlightenment +15% XP
    // Bloodtally Knife: 5% chance on kill to gain +1% attack, tallied per equipped item.
    // Each equipped knife rolls its own 5% chance and keeps its own 0-100 stack counter.
    {
      let totalStacks = 0;
      let anyProc = false;
      for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        const it = killer.heroRef.equipment[slot];
        if (it?.name !== 'Bloodtally Knife') continue;
        const cur = it.runtimeStacks ?? 0;
        if (cur < 100 && Math.random() < 0.05) {
          it.runtimeStacks = cur + 1;
          anyProc = true;
        }
        totalStacks += it.runtimeStacks ?? 0;
      }
      if (anyProc) {
        this.showDamageNumber(killer.x, killer.y - 50, `🔪+${totalStacks}%`, '#ef4444');
        this.gsm.save();
      }
    }
    // Vitality Idol: +0.2% max HP per kill per equipped item (each item capped at 500 stacks).
    // The actual maxHp bump + heal-by-delta happens in updateVitalityIdolHp() on the next frame.
    {
      let gained = false;
      let totalStacks = 0;
      for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        const it = killer.heroRef.equipment[slot];
        if (it?.name !== 'Vitality Idol') continue;
        const cur = it.runtimeStacks ?? 0;
        if (cur < 500) {
          it.runtimeStacks = cur + 1;
          gained = true;
        }
        totalStacks += it.runtimeStacks ?? 0;
      }
      if (gained) {
        this.updateVitalityIdolHp(killer.heroRef);
        if (totalStacks % 25 === 0) {
          const pct = (totalStacks * 0.2).toFixed(1);
          this.showDamageNumber(killer.x, killer.y - 50, `♥+${pct}% HP`, '#f43f5e');
        }
      }
    }
    // Archon's Focus: +1% SP per kill per equipped copy. Each instance rolls its
    // OWN max-stack ceiling based on rarity (common 30 → legendary 99), set
    // at drop time. A fresh common caps at +30 % SP for that relic; a legendary
    // caps at +99 %. Multiple copies accrue stacks in parallel with NO aggregate
    // cap — a player who lucks into 2+ legendaries genuinely pulls ahead, which
    // is the intended reward for rare same-name stacking.
    {
      let gained = false;
      let totalStacks = 0;
      for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        const it = killer.heroRef.equipment[slot];
        if (it?.name !== "Archon's Focus") continue;
        const cur = it.runtimeStacks ?? 0;
        const cap = archonFocusMaxStacks(it.rarity, it.maxStacksRoll);
        if (cur < cap) {
          it.runtimeStacks = cur + 1;
          gained = true;
        }
        totalStacks += it.runtimeStacks ?? 0;
      }
      if (gained && totalStacks % 10 === 0) {
        this.showDamageNumber(killer.x, killer.y - 50, `✧+${totalStacks}% SP`, '#60a5fa');
      }
    }
    const relicGoldMult = 1 + rk.goldPct;
    const relicXpMult   = 1 + rk.xpPct;
    // Passive tree: gold %, loot %, and combined resource % bonuses
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    const passiveGoldMult = 1 + pb.goldPct + pb.resourcePct;
    // v2 talent: Runeforge folds runeDropPct into the passive loot multiplier
    // so it boosts every rune token granted from kills.
    const passiveLootMult = 1 + pb.lootPct + pb.resourcePct + pb.runeDropPct;
    const sigEff = this.getSigilEffects();
    const sigilGoldMult = 1 + sigEff.goldPct / 100;
    const sigilLootMult = 1 + sigEff.lootPct / 100;
    const sigilXpMult   = 1 + sigEff.xpPct / 100;
    // Stay 2+: gold-reward bonus from the stay table (stay 1 unchanged).
    const stayGoldMult = getStayBonuses(this.gsm.player.stayCount || 0).goldRewardMult;
    // Shrine boon multipliers — Merchant's Gamble / No Turning Back (×3 gold),
    // Lucky Break (+40% loot chance), Merchant's Gamble (−50% relic drops handled
    // in loot gen), Cursed Greed (+1 rarity handled in loot gen). Here we fold
    // the gold & loot multipliers into the final grant.
    const boonEff = this.getBoonEffects();
    const boonGoldMult = boonEff.goldMult;         // 1 when no boons; 3 / 9 when multiple ×3 active
    const boonLootMult = 1 + boonEff.lootChancePct; // +40% for Lucky Break
    // Crossroads modifier — Riches / Cursed Riches scale loot + rune drops.
    // XP modifier (Pilgrim's Path) folds into the XP grant below.
    const crossroadsMod = this.gsm.player.activeCrossroadsMod
      ? scaleCrossroadsModifier(
          CROSSROADS_MODIFIERS[this.gsm.player.activeCrossroadsMod],
          this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel,
        )
      : null;
    const crossroadsLootMult = crossroadsMod?.lootMult ?? 1;
    const crossroadsXpMult = crossroadsMod?.xpMult ?? 1;
    const gold = Math.floor(loot.gold * goldMult * speedPenalty * luckTomeBonus * goldTomeBonus * merchantBlessing * treasureMagnetMult * relicGoldMult * passiveGoldMult * sigilGoldMult * stayGoldMult * boonGoldMult * crossroadsLootMult);
    // v2 talent: Scholar's Eye + Inquisitor stack into pb.xpPct.
    const v2XpMult = 1 + pb.xpPct;
    const xp = Math.floor(loot.xp * xpMult * xpTomeBonus * relicXpMult * sigilXpMult * v2XpMult * crossroadsXpMult);
    const stayRuneMult = this.gsm.player.stayCount > 0 ? 0.5 : 1;
    // v2 talent: Tribute (CUNNING capstone) — every rune drop is doubled.
    const tributeMult = pb.tributeFlag ? 2 : 1;
    const tokens = Math.floor(loot.loot * speedPenalty * luckTomeBonus * passiveLootMult * stayRuneMult * sigilLootMult * boonLootMult * tributeMult * crossroadsLootMult);
    // Ancient Pact keystone: +1 gold per kill this run
    if (pb.keystoneAncientPact) this.gsm.player.resources.gold += 1;

    this.gsm.player.resources.gold += gold;
    this.gsm.player.resources.loot += tokens;
    this.gsm.player.totalKills++;
    this.gsm.trackGoldEarned(gold);
    killer.heroRef.totalKills++;
    // Hero-quest hook — bump per-hero lifetime kill (and boss-kill) counters
    // so the Heroes tab progress bars climb. Resolves to heroKey first since
    // unique characters (e.g. Lootfi → 'dwarf') need their own bucket apart
    // from generic warrior totals.
    {
      const heroKey = killer.heroRef.heroKey ?? killer.heroRef.heroClass;
      this.gsm.trackHeroKill(heroKey, es.enemyRef.tier === 'boss');
    }
    this.runGold += gold;
    this.runLoot += tokens;
    this.runXp += xp;
    // Persist the gold/loot/totalKills mutation to the server. Debounced 1s in SyncService,
    // so rapid kills coalesce into one PUT; but the data lives in saveCache immediately.
    // Without this, Ctrl+Shift+R mid-wave would revert gold to whatever was last saved
    // (often 0 at wave start) — every kill's gold was otherwise memory-only until the
    // next wave-end / relic / sigil trigger happened to call save().
    this.gsm.save();

    // Score: kills + gold + tier bonus (same level-gap penalty as XP when staying)
    const tierScore = es.enemyRef.tier === 'boss' ? 100 : es.enemyRef.tier === 'elite' ? 30 : 10;
    let scoreKillMult = 1;
    if ((this.gsm.player.stayCount || 0) > 0) {
      const aliveForScore = this.heroSprites.filter((h) => !h.heroRef.isDead);
      if (aliveForScore.length > 0) {
        const avgLvlS = aliveForScore.reduce((s, h) => s + h.heroRef.level, 0) / aliveForScore.length;
        const gapS = Math.max(0, avgLvlS - this.battleLevel);
        scoreKillMult = gapS >= 70 ? 0 : 1 - gapS / 70;
      }
    }
    const killScore = Math.floor((tierScore + gold) * scoreKillMult);
    this.runScore += killScore;
    if (this.infernoMode) this.infernoScore += killScore;

    // Track quest progress
    this.gsm.trackKill(es.enemyRef.faction);
    // Story-quest progress — kill_enemy for everything, kill_boss additionally for tier='boss'.
    const stage = this.gsm.player.currentLevel;
    const killResult = this.gsm.trackStoryQuestEvent({
      kind: 'kill_enemy', defId: es.enemyRef.defId, faction: es.enemyRef.faction, stage,
    });
    this.queueStoryLorePopups(killResult);
    if (es.enemyRef.tier === 'boss') {
      const bossResult = this.gsm.trackStoryQuestEvent({
        kind: 'kill_boss', defId: es.enemyRef.defId, stage,
      });
      this.queueStoryLorePopups(bossResult);
    }

    // XP sharing among all deployed heroes
    const aliveHeroes = this.heroSprites.filter((h) => !h.heroRef.isDead);
    const deployedCount = aliveHeroes.length;
    const shareMult = XP_SHARE_MULT[deployedCount] ?? 0.5;
    const sharedXp = Math.floor(xp * shareMult);
    // XP gap penalty: ramps linearly from 100% → 0% over a 60-level gap between
    // average hero level and current stage level. Always active (not just during stay).
    const stayXpMult = this.currentXpMult;
    // Elite Pack 2× XP bonus — stacks on top of stayXpMult so the pack still
    // respects the gap penalty, but feels meaningfully rewarding.
    const elitePackXpMult = BattleScene.elitePackPending?.xpMult ?? 1;
    const INFERNO_HERO_LEVEL_CAP = 60;
    for (const hs of aliveHeroes) {
      // Inferno: hero capped at level 60 — skip XP if already at cap
      if (this.infernoMode && hs.heroRef.level >= INFERNO_HERO_LEVEL_CAP) continue;
      const heroXp = Math.floor(sharedXp * stayXpMult * elitePackXpMult);
      if (heroXp <= 0) continue;
      const prevLevel = hs.heroRef.level;
      const result = addXp(hs.heroRef, heroXp);
      // Hero Armory: share a fraction of this kill's XP toward the account-
      // level hero progression (5 % for slot-0 main, 1 % for recruits). Resolved
      // per-hero inside grantArmoryXp so we don't re-compute the main-flag here.
      this.gsm.grantArmoryXp(hs.heroRef, heroXp);
      // Inferno: clamp hero level to cap
      if (this.infernoMode && hs.heroRef.level > INFERNO_HERO_LEVEL_CAP) {
        hs.heroRef.level = INFERNO_HERO_LEVEL_CAP;
      }
      if (result.levelsGained > 0) {
        this.showDamageNumber(hs.x, hs.y - 45, `LEVEL UP! ${hs.heroRef.level}`, '#c8973a');
        this.sfx.play('levelup', { volume: 0.7 });
        this.gsm.trackHeroLevel(hs.heroRef.level);

        // Check for talent points earned (every 5 levels)
        const heroIndex = this.gsm.player.heroes.indexOf(hs.heroRef);
        if (heroIndex >= 0) {
          const talentsBefore = Math.floor(prevLevel / 5);
          const talentsAfter = Math.floor(hs.heroRef.level / 5);
          if (talentsAfter > talentsBefore) {
            const pointsToGive = talentsAfter - talentsBefore;
            for (let tp = 0; tp < pointsToGive; tp++) {
              this.pendingTalentChoices.push({ heroIndex, hero: hs.heroRef });
            }
          }
        }
      }
    }

    this.vfx.playDeathPoof(es.x, es.y);
    // Elites get an extra crimson shockwave + ember burst on top of the base poof.
    if (es.enemyRef.tier === 'elite') {
      this.vfx.playEliteDeath(es.x, es.y);
      const auraTimer = es.getData('eliteAuraTimer') as Phaser.Time.TimerEvent | undefined;
      if (auraTimer) auraTimer.remove();
    }
    // Enemy death SFX — boss gets a deep low-freq explosion, rest get a soft thud.
    this.sfx.play(es.enemyRef.tier === 'boss' ? 'death_boss' : 'death_small', { volume: es.enemyRef.tier === 'boss' ? 0.9 : 0.4 });
    this.showDamageNumber(es.x, es.y - 35, `🪙${gold}`, '#fbbf24');

    // Sigil drops (rare): pushed the stage gate out to stage 20 per feedback —
    // they were landing too early, diluting rune/equip progression in the first
    // zones. Rates halved per playtest feedback — sigils were accruing too fast.
    //   pre-stage-20 → no sigil drops at all
    //   boss: 5.0% · elite: 1.0%
    {
      const tier = es.enemyRef.tier;
      const stageGated = this.gsm.player.currentLevel >= 20;
      // Stay mode does NOT boost sigil drop chance either — the extra enemies
      // per wave already give more roll attempts, so layering a per-kill bump
      // would compound into a farm incentive. Base drop chance only.
      // Inferno multiplies sigil drop ×1.5 to match the item-drop boost so the mode
      // rewards sigil-hunters too (was stuck at flat 5%/1% regardless of mode).
      const infernoSigilBoost = this.infernoMode ? 1.5 : 1;
      const dropPct = stageGated
        ? (tier === 'boss' ? 0.05 * infernoSigilBoost : tier === 'elite' ? 0.01 * infernoSigilBoost : 0)
        : 0;
      if (dropPct > 0 && Math.random() < dropPct) {
        const s = generateSigil((sp) => this.gsm.isSigilUnlocked(sp));
        if (!this.gsm.player.sigilBag) this.gsm.player.sigilBag = [];
        this.gsm.player.sigilBag.push(s);
        this.gsm.player.newSigilCount = (this.gsm.player.newSigilCount ?? 0) + 1;
        this.showDamageNumber(es.x + 20, es.y - 45, `${s.name}!`, '#f0c040');
        if (this.textures.exists(s.special.iconKey)) {
          this.showLootIcon(es.x, es.y, s.special.iconKey);
          // Slide the sigil icon into the Items button (nav bar, centered at 329, 556)
          // after the short floating-label pause so both effects are visible.
          this.time.delayedCall(240, () => this.vfx.playSigilCollect(es.x, es.y, 329, 556, s.special.iconKey));
        }
        this.vfx.playLootShimmer(es.x, es.y);
        this.gsm.save();
      }
    }

    // Handle item drops with loot badge.
    // Alchemist's Scale sigil: every 8th drop is re-rolled one rarity tier
    // higher. Was every 4th — bumped to 8 per balance pass since the 4-drop
    // cadence pushed too many rares into epic territory across a normal run.
    const hasAlchemistScale = this.getSigilEffects().specials.has('alchemists_scale');
    const alchemistRarityLadder = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;
    for (let lootIdx = 0; lootIdx < loot.items.length; lootIdx++) {
      let item = loot.items[lootIdx];
      if (hasAlchemistScale) {
        const p = this.gsm.player as any;
        p._alchemistCounter = (p._alchemistCounter || 0) + 1;
        if (p._alchemistCounter % 8 === 0) {
          const curIdx = alchemistRarityLadder.indexOf(item.rarity as typeof alchemistRarityLadder[number]);
          if (curIdx >= 0 && curIdx < alchemistRarityLadder.length - 1) {
            const upgraded = generateEquipment(
              this.battleLevel, es.enemyRef.lootTier, es.enemyRef.tier,
              alchemistRarityLadder[curIdx + 1],
            );
            loot.items[lootIdx] = upgraded;
            item = upgraded;
            this.showDamageNumber(es.x, es.y - 70, '⚗ UPGRADED!', '#a78bfa', '13px');
          }
        }
      }
      const autoSold =
        (item.rarity === 'common'   && this.gsm.player.autoSellCommon)   ||
        (item.rarity === 'uncommon' && this.gsm.player.autoSellUncommon) ||
        (item.rarity === 'rare'     && this.gsm.player.autoSellRare);
      if (autoSold) {
        this.gsm.player.resources.gold += item.sellValue;
        this.gsm.trackItemFound(item.rarity);
        this.runItems++;
        this.showDamageNumber(es.x + 20, es.y - 45, `🪙${item.sellValue}`, '#9ca3af');
      } else {
        this.gsm.player.inventory.push(item);
        this.gsm.player.newLootCount++;
        this.gsm.trackItemFound(item.rarity);
        this.runItems++;
        const color = RARITY_COLORS[item.rarity];
        this.showDamageNumber(es.x + 20, es.y - 45, `${item.name}!`, color);
        this.showLootIcon(es.x, es.y, item.spriteKey || `item_${item.rarity}`);
        this.vfx.playLootShimmer(es.x, es.y);
        // Bigger VFX + name banner for high-rarity drops so the player
        // notices something special fell. Per user "not that intrusive
        // but noticeable" — no modal popup, just an in-place flourish
        // and a 2.5 s floating banner that fades into the bag.
        if (item.rarity === 'legendary' || item.rarity === 'unique') {
          this.playRareLootFlourish(es.x, es.y, item);
        }
      }
    }
    // Auto-equip new loot into empty slots on deployed heroes
    if (loot.items.length > 0) {
      const autoEquipped = this.gsm.autoEquipInventory();
      for (const { heroIdx, item } of autoEquipped) {
        const hs = this.heroSprites.find((s) => this.gsm.player.heroes.indexOf(s.heroRef) === heroIdx);
        if (!hs) continue;
        const hero = this.gsm.player.heroes[heroIdx];
        const w1Type = hero?.equipment?.weapon1?.weaponType;
        if ((item.slot === 'weapon2' || item.weaponType === 'focus') &&
            w1Type && TWO_HANDED_WEAPON_TYPES.includes(w1Type)) continue;
        this.showAutoEquipPopup(hs, item);
      }
    }
    // Trophy: Soulreaper Charm — 15% chance to restore 10% max HP to the killer on kill
    if (this.hasTrophy('restore 10%') && Math.random() < 0.15) {
      const stats = this.effStats(killer.heroRef);
      const healAmt = Math.floor(stats.maxHp * 0.10);
      killer.heroRef.stats.hp = Math.min(stats.maxHp, killer.heroRef.stats.hp + healAmt);
      this.showDamageNumber(killer.x, killer.y - 40, `+${healAmt}`, '#86efac');
    }
    // Tome of Bloodthirst — permanently increase max HP on every kill
    // bonusPerLevel=0.1, so Lv10 = +1 maxHP/kill. Accumulate fractionally so
    // lower tome levels still grant whole HP (every N kills instead of every kill).
    // Cap: total lifetime gain per hero = tomeLevel × 100 (e.g. Lv10 = 1000 HP max).
    const killHpBonus = getTomeBonus(this.gsm.player.tomes, 'killHeal');
    if (killHpBonus > 0) {
      const btTomeLevel = getTomeLevel(this.gsm.player.tomes, 'killHeal');
      const BT_CAP = btTomeLevel * 100; // max permanent HP gain from bloodthirst
      for (const hs of this.heroSprites.filter((h) => !h.heroRef.isDead)) {
        const h = hs.heroRef as any;
        const totalGained = (h._bloodthirstTotalGain as number) || 0;
        if (totalGained >= BT_CAP) continue; // at cap — no more gain
        h._killHpAccum = Math.round(((h._killHpAccum || 0) + killHpBonus) * 100) / 100;
        if (h._killHpAccum >= 1) {
          const rawGain = Math.floor(h._killHpAccum);
          const gain = Math.min(rawGain, BT_CAP - totalGained); // clamp to remaining room
          h._killHpAccum -= rawGain; // consume full accum
          h._bloodthirstTotalGain = totalGained + gain;
          hs.heroRef.stats.maxHp += gain;
          hs.heroRef.stats.hp += gain;
        }
      }
    }
    // ── v2 talent tree (WRATH) on-kill effects ────────────────────────
    {
      const pbK = pb;
      // Bloodthirst — heal % maxHp on kill (Steady Heart amplifies)
      if (pbK.killHealPct > 0) {
        const maxHpK = this.effStats(killer.heroRef).maxHp;
        const healMult = 1 + pbK.healingReceivedPct;
        const heal = Math.max(1, Math.floor(maxHpK * pbK.killHealPct * healMult));
        killer.heroRef.stats.hp = Math.min(maxHpK, killer.heroRef.stats.hp + heal);
      }
      // Bloodfrenzy — kill grants stacking +X% atk speed for 5s (max 5 stacks)
      if (pbK.bloodfrenzyAtkSpd > 0) {
        const k = killer.heroRef as any;
        const now = Date.now();
        const stacks = (k._bloodfrenzyStacks as { exp: number }[] | undefined) ?? [];
        const live = stacks.filter((s) => s.exp > now);
        if (live.length < 5) live.push({ exp: now + 5000 });
        k._bloodfrenzyStacks = live;
        // The atk-speed read site multiplies by `1 - pct` (capped at 0.3) per stack.
        // We compute the active multiplier on every attack from _bloodfrenzyStacks.
      }
    }
    // Relic: Slayer Emblem — +25% attack speed for 5s after kill
    if (rk.hasSlayer) {
      (killer.heroRef as any)._slayerActive = true;
      this.time.delayedCall(5000, () => { (killer.heroRef as any)._slayerActive = false; });
    }
    // Relic: Totem of Bloodlust — +20% damage for 5s after kill
    if (rk.hasBloodlust) {
      (killer.heroRef as any)._bloodlustActive = true;
      this.time.delayedCall(5000, () => { (killer.heroRef as any)._bloodlustActive = false; });
    }

    // Boss kill: award 1 loot crystal (halved on stay)
    if (es.enemyRef.tier === 'boss') {
      // v2 Tribute: doubled (one extra rune per boss kill on top of the base bonus).
      const bossRune = (stayRuneMult < 1 ? (Math.random() < 0.5 ? 1 : 0) : 1) * tributeMult;
      this.gsm.player.resources.loot += bossRune;
      this.runLoot += bossRune;
    }

    // Elite kills: chance to drop a tome choice (checked BEFORE talents to prevent overlap).
    // Base rate is intentionally low (8%) — Cursed tome can ×10 the wave size and elite
    // chance scales to 70% at high stages, so even at 8% per kill a 30-elite Cursed
    // wave still averages ~2 tomes. Stay/Luck/Foresight multipliers stack on top so
    // tome-luck investment continues to scale linearly without a hard cap.
    const stayTomeMult = this.gsm.player.stayCount > 0 ? Math.pow(0.5, this.gsm.player.stayCount) : 1;
    const foresightMult = this.gsm.hasActiveConsumable('crystal_foresight') ? 2 : 1;
    const luckTomeMult = 1 + getTomeBonus(this.gsm.player.tomes, 'luck');
    if (es.enemyRef.tier === 'elite' && Math.random() < TOME_ELITE_DROP_CHANCE * stayTomeMult * foresightMult * luckTomeMult) {
      this.showTomeChoice();
    }

    // Process pending talent choices after XP distribution (deferred if tome choice is active)
    if (this.pendingTalentChoices.length > 0 && !this.talentChoiceActive && !this.tomeChoiceActive) {
      this.processNextTalentChoice();
    }

    this.lastKillerName = es.enemyRef.name;

    // Armory Journal — credit this kill (and boss kill if applicable) to the
    // finishing hero's heroKey. Lifetime totals, no mechanical effect.
    const journalKey = killer.heroRef.heroKey ?? killer.heroRef.heroClass;
    this.gsm.trackArmoryKill(journalKey, es.enemyRef.tier === 'boss');
    // Tick stack counters on run-long stacking boons (Inner Flame, Blood Price).
    if (this.gsm.player.activeBoons && this.gsm.player.activeBoons.length > 0) {
      onKillTickBoons(this.gsm.player.activeBoons);
      this.invalidateBoonCache();
    }

    // ── Necromancer: Soul Leech passive + last-corpse tracking ──────────────
    for (const hs of this.heroSprites.filter((h) => !h.heroRef.isDead && (h.heroRef.heroKey ?? h.heroRef.heroClass) === 'necromancer')) {
      // Soul count — used for Bone Shield scaling. Soul Harvest passive
      // doubles the gain to +2 when the finishing blow came from the
      // killer hero's own crit (flag stamped by performHeroAttack).
      const wasCrit = killer === hs && !!(hs.heroRef as any)._lastKillWasCrit;
      const harvestBonus = wasCrit && this.hasArmoryPassive(hs.heroRef, 'passive_morveth_soul_harvest') ? 2 : 1;
      (hs.heroRef as any)._soulCount = ((hs.heroRef as any)._soulCount ?? 0) + harvestBonus;


      // Store last corpse position + type for Raise Dead — bosses cannot be raised
      if (es.enemyRef.tier !== 'boss' && Phaser.Math.Distance.Between(hs.x, hs.y, es.x, es.y) < 600) {
        const corpseTexKey = this.getEnemySpriteKey(es.enemyRef);
        const corpseSize = es.enemyRef.tier === 'elite' ? 150 : 120;
        (hs.heroRef as any)._lastCorpse = { defId: es.enemyRef.defId, texKey: corpseTexKey, displaySize: corpseSize, x: es.x, y: es.y, atk: es.enemyRef.attack, hp: es.enemyRef.maxHp, atkSpeed: es.enemyRef.speed, atkSpeedMult: es.enemyRef.attackSpeedMult || 1 };
      }
    }

    es.hpBar.destroy();
    es.hpBarBg.destroy();
    es.nameText?.destroy();
    es.nameText = undefined;
    (es.getData('eliteStar')  as Phaser.GameObjects.Text    | undefined)?.destroy();
    (es.getData('bossSkull')  as Phaser.GameObjects.Text    | undefined)?.destroy();
    (es.getData('lieutenantBadge') as Phaser.GameObjects.Text   | undefined)?.destroy();
    (es.getData('lieutenantAuraTimer') as Phaser.Time.TimerEvent | undefined)?.remove();
    (es.getData('fearIcon')   as Phaser.GameObjects.Text    | undefined)?.destroy();
    (es.getData('fearGlow')   as Phaser.GameObjects.Ellipse | undefined)?.destroy();
    (es.getData('fearGlow2')  as Phaser.GameObjects.Ellipse | undefined)?.destroy();

    // Remove from game-logic array immediately so no more AI/combat processes this sprite
    const removeIdx = this.enemySprites.indexOf(es);
    if (removeIdx !== -1) this.enemySprites.splice(removeIdx, 1);
    es.setActive(false);

    // Death shadow: keep visible throughout the death animation, fade out alongside sprite
    const deathShadow = es.shadow;

    const removeEnemySprite = (): void => {
      if (es.scene) es.destroy();
    };

    const fadeShadowOut = () => {
      if (!deathShadow || !deathShadow.scene) return;
      this.tweens.add({
        targets: deathShadow,
        alpha: 0, duration: 180, ease: 'Quad.easeIn',
        onComplete: () => deathShadow.destroy(),
      });
    };

    // Play death animation then fade out immediately — keep active only for the anim tick.
    es.setActive(true);
    const deathKey = `${es.texture.key}_death`;
    if (this.anims.exists(deathKey)) {
      es.play(deathKey);
      es.once('animationcomplete', () => {
        this.tweens.add({ targets: es, alpha: 0, delay: 0, duration: 180, onComplete: removeEnemySprite });
        fadeShadowOut();
      });
      // Safety: force remove after 1.5s in case animationcomplete never fires
      this.time.delayedCall(1500, () => {
        if (es.scene) removeEnemySprite();
        if (deathShadow?.scene) deathShadow.destroy();
      });
    } else {
      // No death anim — fade sprite and shadow in parallel
      this.tweens.add({ targets: es, alpha: 0, delay: 0, duration: 200, onComplete: removeEnemySprite });
      fadeShadowOut();
    }
  }

  // ==================== WAVE MANAGEMENT ====================

  /** Cheat: set gsm.battle.waveNumber before calling this to jump to a specific wave. */
  public startNextWave(): void {
    this.gsm.battle.waveNumber++;
    this.gsm.trackWave(this.gsm.battle.waveNumber);
    // Pre-wave telegraph — larger banner above the standard announcement strip.
    // Boss waves get a red palette + glow so "the final wave is here" reads at a
    // glance. Skipped during event flows (Telurion / horde / elite pack) which
    // run their own bespoke announcement copy.
    {
      const wn = this.gsm.battle.waveNumber;
      const isBossWave = wn === WAVES_PER_ZONE;
      const telegraphText = isBossWave
        ? `WAVE ${wn} — BOSS APPROACHES`
        : `Wave ${wn} of ${WAVES_PER_ZONE}`;
      this.showWaveTelegraph(telegraphText, isBossWave);
    }
    // Reset each hero's per-wave swap budget. The cap is the same per-hero
    // (`getEffectiveMovesPerWave`) for every biome with a finite cap; biomes
    // with Infinity stay un-gated. Counter lives on heroRef so it survives
    // sprite recreation but resets here on every wave start.
    this.movesUsedThisWave = 0;
    for (const hs of this.heroSprites) {
      (hs.heroRef as any)._movesUsedThisWave = 0;
    }
    // Vorrak's per-wave move budget — fixed at 1, never extended by tomes /
    // talents / trophies. Reset here alongside the hero budget.
    this.summonMovesUsedThisWave = 0;
    // v2 talent: Hearthlight — heal X% maxHp on every hero at wave start.
    // Steady Heart amplifies the heal.
    {
      const pbW = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
      if (pbW.healPerWavePct > 0) {
        const healMult = 1 + pbW.healingReceivedPct;
        for (const hs of this.heroSprites) {
          if (hs.heroRef.isDead) continue;
          const maxHp = this.effStats(hs.heroRef).maxHp;
          const heal = Math.floor(maxHp * pbW.healPerWavePct * healMult);
          hs.heroRef.stats.hp = Math.min(maxHp, hs.heroRef.stats.hp + heal);
        }
      }
    }
    // Hero Armory: clear once-per-encounter passive flags at the start of each wave.
    this.resetPerEncounterArmoryFlags();
    // Death's Promise counter is now run-scoped (2 total per run) — no per-wave reset.
    // Bloody Ledger sigil: start the wave at 15% HP and prime the hero for 10 guaranteed
    // crits. Combat-behavior sigil — only the main (first-deployed) hero eats the cut.
    if (this.getSigilEffects().specials.has('bloody_ledger')) {
      const mainHs = this.getMainHeroSprite();
      if (mainHs) {
        const maxHp = this.effStats(mainHs.heroRef).maxHp;
        mainHs.heroRef.stats.hp = Math.max(1, Math.floor(maxHp * 0.15));
        (mainHs.heroRef as any)._bloodyLedgerPrimed = 10;
      }
    }
    // Hide placement indicators after wave 1 (deploy via Heroes menu instead)
    if (this.gsm.battle.waveNumber > 1) {
      for (const obj of this.emptySlotObjects) obj.destroy();
      this.emptySlotObjects = [];
    }
    // Tick consumable durations & refresh merchant — track expiries to revert stat changes
    const consumablesBefore = new Set(this.gsm.player.activeConsumables.map((c) => c.id));
    this.gsm.tickConsumableWaves();
    const consumablesAfter = new Set(this.gsm.player.activeConsumables.map((c) => c.id));
    for (const id of consumablesBefore) {
      if (!consumablesAfter.has(id)) this.removeConsumableStatBuff(id);
    }
    // Tick the merchant refresh counter FIRST (monotonic run-wave clock that
    // survives stays), THEN run the refresh pass so a full cycle actually fires.
    this.gsm.tickMerchantRefreshClocks();
    const { mainRefreshed } = this.gsm.refreshMerchantInventory();
    if (mainRefreshed) this.showMerchantRefreshBadge();
    // Score: wave bonus (same level-gap penalty as XP when staying).
    // Idempotency key gated on (level, wave, mode) — prevents double-count
    // if this path fires twice for the same wave (race condition during
    // stay rebuilds, F5 mid-wave, scene re-init). Mirrors the `_zone`
    // counterpart used for the zone-clear bonus.
    {
      const waveScoreKey = `${this.gsm.player.currentLevel}_${this.gsm.battle.waveNumber}_${this.infernoMode ? 'inf' : 'std'}`;
      if (this._waveScoreCountedKey !== waveScoreKey) {
        let scoreWaveMult = 1;
        if ((this.gsm.player.stayCount || 0) > 0) {
          const aliveForWaveScore = this.heroSprites.filter((h) => !h.heroRef.isDead);
          if (aliveForWaveScore.length > 0) {
            const avgLvlW = aliveForWaveScore.reduce((s, h) => s + h.heroRef.level, 0) / aliveForWaveScore.length;
            const gapW = Math.max(0, avgLvlW - this.battleLevel);
            scoreWaveMult = gapW >= 70 ? 0 : 1 - gapW / 70;
          }
        }
        const waveBonus = Math.floor(25 * scoreWaveMult);
        this.runScore += waveBonus;
        if (this.infernoMode) this.infernoScore += waveBonus;
        this._waveScoreCountedKey = waveScoreKey;
      }
    }
    // ── Shadowrealm path — stage-50 endboss + endless-mode handoff. ─────
    // Auto-init the pending state at stage 50 (no event card; the encounter
    // is forced as the floor's stage-50 wall). Once `bossDefeated` flips
    // we never re-enter this branch via wave gen — the endless ticker owns
    // spawning. Keeping the run inside the same Phaser scene lets the
    // background, hero state, gear, talents, and all other systems carry
    // through cleanly without a scene reset.
    //
    // Gate (TEST MODE): the Telurion-killed-first prereq is disabled while
    // the questline is still under construction so every stage-50 run
    // surfaces Malakhar for testing. Restore the gate (`telurionBossDefeated`
    // check on tl_2_telurion) before shipping the questline to players.
    if (this.gsm.player.currentLevel === 50
        && !this.gsm.player.shadowlordKilledThisRun
        && !BattleScene.shadowrealmPending) {
      BattleScene.shadowrealmPending = {
        bossSpawned: false,
        bossDefeated: false,
        endlessActive: false,
        ghostKills: 0,
        waveCount: 0,
        nextSpawnTime: 0,
      };
    }
    if (BattleScene.shadowrealmPending) {
      const sp = BattleScene.shadowrealmPending;
      // Wave 1 → spawn the Shadowlord, no other adds. Subsequent wave
      // increments while the boss is alive are no-ops (waveActive stays
      // true; player kills him to advance).
      if (!sp.bossSpawned) {
        const bossBase = BASE_ENEMIES.find((e) => e.id === 'boss_shadowlord');
        if (bossBase) {
          const bossInst = scaleEnemy(bossBase, this.battleLevel, true);
          this.spawnEnemy(bossInst);
          sp.bossSpawned = true;
          this.showAnnouncement('MALAKHAR AWAKENS');
        }
      }
      this.waveActive = true;
      this.waveText.setText('Shadowrealm  ·  Malakhar, the Hollow King');
      // Skip the rest of startNextWave — Shadowrealm is self-contained
      // (no stay/cursed scaling, no normal boss flow, no rune events).
      return;
    }

    // Elite Pack path — bypass normal wave gen entirely when the player picked the
    // Elite Pack card. Spawns only elite-tier enemies from the current zone's pool
    // at the rolled count (14-28 total across 2 waves). Skips cursed/stay scaling
    // since the pack is its own balance curve.
    if (BattleScene.elitePackPending) {
      const ep = BattleScene.elitePackPending;
      const isWave1 = this.gsm.battle.waveNumber === 1;
      // Reserve 1 slot per wave for the pack-leader boss, so total non-boss enemies
      // equal `count` and total entities = count + 2 bosses.
      const eliteN = Math.max(0, (isWave1 ? ep.waveOneSize : ep.count - ep.waveOneSize) - 1);
      const eliteEnemies = this.generateElitePackWaveEnemies(eliteN);
      // Spawn the pack-leader boss for this wave (boss1 on wave 1, boss2 on wave 2).
      // Bosses now also pick up the same stay-bonus / crossroads scaling the
      // elite mobs do — without this a stay-looped or Iron-Path-active player
      // walked into pack bosses that read as soft compared to the elites,
      // because BOSS_HP_MULT alone didn't keep pace with stay-stacked elites.
      const bossId = isWave1 ? ep.bossIds[0] : ep.bossIds[1];
      const bossBase = BASE_ENEMIES.find((e) => e.id === bossId);
      const bossInstance = bossBase ? scaleEnemy(bossBase, this.battleLevel, true) : null;
      if (bossInstance) {
        const stayB = getStayBonuses(this.gsm.player.stayCount || 0);
        const crMod = this.gsm.player.activeCrossroadsMod
          ? scaleCrossroadsModifier(
              CROSSROADS_MODIFIERS[this.gsm.player.activeCrossroadsMod],
              this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel,
            )
          : null;
        const crA = crMod?.enemyAtkMult ?? 1;
        const crS = crMod?.enemySpeedMult ?? 1;
        bossInstance.maxHp     = Math.max(1, Math.floor(bossInstance.maxHp     * stayB.enemyHpMult));
        bossInstance.currentHp = bossInstance.maxHp;
        bossInstance.attack    = Math.max(1, Math.floor(bossInstance.attack    * stayB.enemyAtkMult * crA));
        bossInstance.speed     = bossInstance.speed * stayB.enemySpeedMult * crS;
        bossInstance.attackSpeedMult = (bossInstance.attackSpeedMult ?? 1) * stayB.enemyAtkSpeedMult;
      }
      if (!bossInstance && eliteEnemies.length === 0) {
        console.warn('[elite-pack] zone has no eligible enemies and boss lookup failed; bailing');
        BattleScene.elitePackPending = null;
        this.grantElitePackReward(ep.count);
        this.gsm.battle.bossDefeated = true;
        this.time.delayedCall(600, () => this.showZoneClearedPopup());
        return;
      }
      for (const e of eliteEnemies) this.spawnEnemy(e);
      if (bossInstance) this.spawnEnemy(bossInstance);
      this.waveActive = true;
      const totalThisWave = eliteEnemies.length + (bossInstance ? 1 : 0);
      this.waveText.setText(`Elite Pack ${this.gsm.battle.waveNumber}/2  ·  ${totalThisWave} enemies  ·  ✦ ${ep.xpMult}× XP`);
      // Skip the rest of startNextWave's normal-wave logic (stay/cursed scaling, boss
      // flow, etc.) — pack waves are self-contained.
      return;
    }

    // Horde of Goblins path — one wave, bidirectional stagger, 2× mob speed,
    // 2.5× elite speed, +1× HP/ATK on top of the global elite mult for horde
    // elites (net ×6 HP / ×3 ATK vs a vanilla minion). Mooks + elites stream
    // in over ~8 s alternating sides so the fight is a pressure test instead
    // of an instant pile.
    if (BattleScene.hordePending) {
      this.startHordeWaveSpawn();
      this.waveActive = true;
      const hp = BattleScene.hordePending;
      this.waveText.setText(`Horde of Goblins  ·  ${hp.mobCount + hp.eliteCount} goblins  ·  ✦ ${hp.xpMult}× XP`);
      return;
    }

    // Telurion's Lair path — single wave: N gold-tinted Sentinel-of-Telurion
    // skeleton elites must ALL be killed before Telurion will descend. Elites
    // trickle in staggered like the horde; the boss-spawn watcher polls the
    // live-elite count and only fires when the wave is empty (no Sentinels
    // alive AND no spawn deliveries pending). User-driven: "Telurion need to
    // come only after elites are beaten."
    if (BattleScene.telurionPending) {
      const tp = BattleScene.telurionPending;
      // Spawn elites with a stagger
      for (let i = 0; i < tp.eliteCount; i++) {
        this.time.delayedCall(i * 350, () => {
          const base = BASE_ENEMIES.find((e) => e.id === 'skeleton_elite_gold');
          if (!base) return;
          const inst = scaleEnemy(base, this.battleLevel, false);
          this.spawnEnemy(inst);
          tp.spawnedElite++;
        });
      }
      // Boss-spawn watcher — polls every 400 ms. Telurion spawns only when:
      //   1) every Sentinel that should be in this wave has already spawned
      //      (spawnedElite === eliteCount — no in-flight delayedCalls left), AND
      //   2) zero live Sentinels remain on the field.
      // Adds a 600 ms grace pause after the last elite dies so the descent
      // reads as a beat, not a jump-cut.
      const watcher = this.time.addEvent({
        delay: 400, loop: true,
        callback: () => {
          // Outer try/catch is the final safety against any Phaser-internal
          // teardown race that can fire this timer tick after the scene has
          // started disposing — user reported repeating crashes on the
          // Telurion lair flow.
          try {
            // Defensive: scene may have torn down between ticks (death, retreat,
            // restart) — bail cleanly without crashing the timer loop.
            if (!this.scene || !this.scene.key) { watcher.remove(); return; }
            if (!BattleScene.telurionPending || this.gameOver) {
              watcher.remove();
              return;
            }
            if (BattleScene.telurionPending.bossSpawned) {
              watcher.remove();
              return;
            }
            if (BattleScene.telurionPending.spawnedElite < tp.eliteCount) return;
            if (!this.enemySprites) return;
            const liveSentinels = this.enemySprites.some((es) =>
              es && es.active && es.enemyRef && es.enemyRef.currentHp > 0 && es.enemyRef.defId === 'skeleton_elite_gold');
            if (liveSentinels) return;
            watcher.remove();
          } catch (err) {
            // Anything thrown — clear the watcher and forget it. Boss won't
            // spawn this run if we get here, but the rest of the scene keeps
            // running cleanly.
            console.error('[telurion-watcher] error, aborting watcher', err);
            try { watcher.remove(); } catch { /* watcher already gone */ }
            return;
          }
          this.time.delayedCall(600, () => {
            try {
              // Defensive: every reference here can be undefined if the scene
              // tore down (death, retreat, restart) between the watcher firing
              // and this 600 ms grace beat. Bail if anything's missing.
              if (!this.scene || !this.scene.key) return;
              if (!BattleScene.telurionPending || this.gameOver) return;
              if (!this.enemySprites) return;
              const bossBase = BASE_ENEMIES.find((e) => e.id === 'boss_telurion');
              if (!bossBase) return;
              const bossInst = scaleEnemy(bossBase, this.battleLevel, true);
              this.spawnEnemy(bossInst);
              if (BattleScene.telurionPending) BattleScene.telurionPending.bossSpawned = true;
              this.showAnnouncement('TELURION DESCENDS');
              // Find the freshly-spawned dragon sprite. Sprite already has
              // flipX=true from spawnEnemy (he came from the right side).
              const bossSprite = this.enemySprites.find((es) =>
                es && es.active && es.enemyRef && es.enemyRef.defId === 'boss_telurion');
              if (bossSprite) {
                // Telurion now spawns like every other boss — no scripted
                // landing tween, no Landing.png anim, no descent state
                // machine. Repeated under-map / clipping bugs survived every
                // pinning + watchdog attempt at the descent path; ripping it
                // out removes the failure surface entirely. He just walks in
                // from the right edge and uses the standard idle / walk /
                // attack anims like the rest of the boss roster.
                //
                // Pin to a centre lane (lane 1, y=318) so the 290 px sprite
                // sits in the middle band of the screen with clearance both
                // above (head visible) and below (feet well clear of the
                // y=526 HUD bar). Shadow follows.
                const SAFE_LANE = 1;
                bossSprite.lane = SAFE_LANE;
                bossSprite.y = LANE_Y[SAFE_LANE];
                bossSprite.setDepth(2 + bossSprite.y * 0.001);
                if (bossSprite.shadow) bossSprite.shadow.setPosition(bossSprite.x, bossSprite.y);
                bossSprite.setData('telurionDescending', false);
                // Telurion's spritesheet only carries idle + 2 attack anims —
                // no walk / hurt / death / landing (the previous full-anim
                // sheet was rendering as empty frames with horn-tips, see
                // scripts/buildTelurionSheet.cjs). Idle holds him in his
                // hover stance until the spell handler triggers an attack.
                if (this.anims.exists('enemy_telurion_idle')) {
                  try { bossSprite.play('enemy_telurion_idle'); } catch { /* sprite torn down */ }
                }
              }
            } catch (err) {
              console.error('[telurion-spawn] error during descent', err);
              return;
            }
          });
        },
      });
      this.waveActive = true;
      this.waveText.setText(`Telurion's Lair  ·  ${tp.eliteCount} sentinels + dragon  ·  ×10 gold`);
      return;
    }

    const enemies = this.generateWaveEnemiesForStory();

    // Stay scaling — softer curve from getStayBonuses. Stay 1 = 1.5× enemies with
    // no stat boost; stay 2+ add HP/ATK and reward bonuses on top.
    const stayCount = this.gsm.player.stayCount || 0;
    const stayBonus = getStayBonuses(stayCount);
    if (stayCount > 0 && stayBonus.enemyCountMult > 1) {
      const extraFromStay = Math.floor(enemies.length * (stayBonus.enemyCountMult - 1));
      for (let i = 0; i < extraFromStay; i++) {
        const extra = this.generateWaveEnemiesForStory();
        const nonBoss = extra.filter((e) => e.tier !== 'boss');
        const pool = nonBoss.length > 0 ? nonBoss : extra;
        if (pool.length > 0) enemies.push(pool[Math.floor(Math.random() * pool.length)]);
      }
    }

    // Cursed Tome: scales wave size — getTomeBonus returns rank×(9/99); rank 99 = ×9 extras = 10× total
    const cursedBonus = getTomeBonus(this.gsm.player.tomes, 'cursed');
    if (cursedBonus > 0) {
      const baseCount = enemies.length;
      const cursedExtras = Math.round(baseCount * cursedBonus);
      for (let i = 0; i < cursedExtras; i++) {
        const extra = this.generateWaveEnemiesForStory();
        const nonBoss = extra.filter((e) => e.tier !== 'boss');
        if (nonBoss.length > 0) enemies.push(nonBoss[Math.floor(Math.random() * nonBoss.length)]);
      }
    }

    // Crossroads modifier — Pilgrim's Path adds extra mobs per wave on top of
    // stay/cursed scaling. Other Crossroads picks (Riches/Iron Path) only
    // scale enemy stats, not population, and skip this block.
    const crossroadsModForSpawn = this.gsm.player.activeCrossroadsMod
      ? scaleCrossroadsModifier(
          CROSSROADS_MODIFIERS[this.gsm.player.activeCrossroadsMod],
          this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel,
        )
      : null;
    const crossroadsCountMult = crossroadsModForSpawn?.enemyCountMult ?? 1;
    if (crossroadsCountMult > 1) {
      const baseCount = enemies.length;
      const extras = Math.round(baseCount * (crossroadsCountMult - 1));
      for (let i = 0; i < extras; i++) {
        const extra = this.generateWaveEnemiesForStory();
        const nonBoss = extra.filter((e) => e.tier !== 'boss');
        if (nonBoss.length > 0) enemies.push(nonBoss[Math.floor(Math.random() * nonBoss.length)]);
      }
    }

    // Stay scaling: HP/ATK/speed/atk-speed all compound exponentially from stay 1+.
    // Every stay stacks on top of the last — no gate, no cap, matches the
    // advertised zone-cleared popup numbers so the player gets the fight they
    // were promised.
    // Crossroads enemy modifiers (atk / speed) fold in here so a stay-1 +
    // Riches run gets BOTH multipliers applied — they stack multiplicatively.
    const crossroadsAtkMult   = crossroadsModForSpawn?.enemyAtkMult   ?? 1;
    const crossroadsSpeedMult = crossroadsModForSpawn?.enemySpeedMult ?? 1;
    if (stayCount >= 1 || crossroadsAtkMult !== 1 || crossroadsSpeedMult !== 1) {
      for (const e of enemies) {
        e.attack            = Math.round(e.attack            * stayBonus.enemyAtkMult * crossroadsAtkMult);
        e.currentHp         = Math.round(e.currentHp         * stayBonus.enemyHpMult);
        e.maxHp             = Math.round(e.maxHp             * stayBonus.enemyHpMult);
        e.speed             = e.speed             * stayBonus.enemySpeedMult * crossroadsSpeedMult;
        e.attackSpeedMult   = (e.attackSpeedMult ?? 1) * stayBonus.enemyAtkSpeedMult;
      }
    }

    // ── Lieutenant mid-zone mini-boss (wave 3 only). Stage-scaled spawn chance:
    // 10 % at stage 5, scaling to 40 % at stage 30+. Picks one elite from the
    // wave (or upgrades a minion to elite if no elites rolled) and tags it as
    // a Lieutenant — bigger stats, gold tint, +1 rarity tier on the drop, and
    // a buff aura that boosts nearby faction-matched allies. Skipped during
    // event modes (Telurion lair / horde / elite pack — those have their own
    // pacing). ──
    if (this.gsm.battle.waveNumber === 3
        && !BattleScene.telurionPending && !BattleScene.hordePending && !BattleScene.elitePackPending) {
      const stage = this.gsm.player.currentLevel;
      const lieutenantChance = Math.max(0.10, Math.min(0.40, 0.10 + (stage - 5) * 0.012));
      if (Math.random() < lieutenantChance && enemies.length > 0) {
        // Prefer an existing elite; if none, upgrade a minion in place.
        // Rot Bomber is explicitly excluded — it's an event-spawned hazard
        // that runs on its own AI loop (one-shot suicide bomber), not a
        // biome elite that can wear the Lieutenant title. Same goes for
        // any other event-only defId we add later.
        const EVENT_ONLY = new Set<string>(['rot_bomber']);
        let target = enemies.find((e) => e.tier === 'elite' && !EVENT_ONLY.has(e.defId));
        if (!target) {
          const minion = enemies.find((e) => e.tier === 'minion' && !EVENT_ONLY.has(e.defId));
          if (minion) {
            // Promote to elite with the standard elite multipliers baked in.
            minion.tier = 'elite';
            minion.maxHp = Math.round(minion.maxHp * 3);
            minion.currentHp = minion.maxHp;
            minion.attack = Math.round(minion.attack * 1.5);
            target = minion;
          }
        }
        if (target) {
          (target as any)._isLieutenant = true;
          // Lieutenant stat bump: ×1.6 HP, ×1.35 ATK on top of elite scaling.
          target.maxHp = Math.round(target.maxHp * 1.6);
          target.currentHp = target.maxHp;
          target.attack = Math.round(target.attack * 1.35);
          target.lootChance = 1.0;
          target.lootTier = Math.min(5, (target.lootTier ?? 2) + 1);
          // Lieutenant proper-name per faction. Replaces the generic
          // "Lieutenant <enemy.name>" nameplate with a single character
          // name so each lieutenant reads as a distinct figure rather than
          // a labeled mob (per user playtest pass).
          (target as any)._lieutenantName = LIEUTENANT_NAMES[target.faction] ?? 'Lieutenant';
        }
      }
    }

    this.waveSpawnQueue = enemies;
    this.spawnTimer = 0;
    this.waveActive = true;
    this.dpsWaveDamage.clear();
    this.dpsWaveStart = this.gameTime;
    // Save at wave START so quitting mid-wave restores to this wave, not the previous one
    this.gsm.saveRunSession(this.gsm.battle.waveNumber, this.runScore, this.infernoMode, this.infernoZone, this.infernoScore, false, this.runGold, this.runLoot, this.runLootSpent, this.runXp, this.runItems);

    // Relic: Vanguard Ward — apply 12% max HP shield to each hero with the relic at wave start
    for (const hs of this.heroSprites) {
      if (!hs.heroRef.isDead) {
        const rbW = this.getRelicBonuses(hs.heroRef);
        if (rbW.vanguardShield > 0) {
          const maxHpW = this.effStats(hs.heroRef).maxHp;
          const shield = Math.round(rbW.vanguardShield * maxHpW);
          if (this.applyShield(hs.heroRef, shield, 'vanguard_shield_relic')) {
            this.showDamageNumber(hs.x, hs.y - 40, `🛡${shield}`, '#dde6f0');
          }
        }
      }
    }

    // Goblino event: 0.5% chance per wave to spawn a gold goblin
    if (!this.goblino && Math.random() < 0.005) {
      this.time.delayedCall(1000, () => this.spawnGoblino(enemies));
    }

    // Rot Bomber event: stage 10+, 3% per wave. Guarded by "no rot bomber already alive"
    // so staying/speed-running the same wave can't stack several bombers on top of the party.
    if (this.battleLevel >= 10
        && !this.enemySprites.some((es) => es.isRotBomber && es.active)
        && Math.random() < 0.03) {
      this.time.delayedCall(1500, () => this.spawnRotBomber(enemies));
    }
  }

  /** Check if any enemy has walked onto an active poison trap and trigger it */
  private updatePoisonTraps(): void {
    if (this.activePoisonTraps.length === 0) return;
    for (let i = this.activePoisonTraps.length - 1; i >= 0; i--) {
      const trap = this.activePoisonTraps[i];
      const triggered = this.enemySprites.find(
        (es) => es.active && es.enemyRef.currentHp > 0 && Math.abs(es.x - trap.x) < 100,
      );
      if (!triggered) continue;
      // Triggered — apply damage + DoT to all enemies within 140px, play hit VFX for 1s
      if (trap.sprite) { if (this.anims.exists('fx_trap_hit_start')) trap.sprite.play('fx_trap_hit_start'); this.time.delayedCall(1000, () => trap.sprite?.destroy()); }
      for (const es of this.enemySprites) {
        if (es.active && es.enemyRef.currentHp > 0 && Math.abs(es.x - trap.x) < 140) {
          es.enemyRef.currentHp -= trap.damage;
          applyDebuff(es.enemyRef, {
            type: 'dot', value: trap.damage * trap.dotFraction,
            duration: trap.dotDuration, remaining: trap.dotDuration,
          });
          this.showDamageNumber(es.x, es.y - 20, `${trap.damage} ☠`, '#22c55e');
          this.flashSprite(es);
          if (es.enemyRef.currentHp <= 0) this.killEnemy(es, trap.casterHs);
        }
      }
      this.activePoisonTraps.splice(i, 1);
    }
  }

  /** Destroy all active poison traps and reset their caster cooldown if unused */
  private clearPoisonTraps(): void {
    for (const trap of this.activePoisonTraps) {
      trap.sprite?.destroy();
      // Reset cooldown so the ability is ready next wave
      const casterAbility = trap.casterHs.heroRef.abilities.find((a) => a.effectType === 'trap');
      if (casterAbility) casterAbility.currentCooldown = 0;
    }
    this.activePoisonTraps = [];
  }

  private spawnQueuedEnemies(delta: number): void {
    if (this.waveSpawnQueue.length === 0) return;
    this.spawnTimer += delta;
    if (this.spawnTimer >= ENEMY_SPAWN_DELAY) {
      this.spawnTimer -= ENEMY_SPAWN_DELAY;
      this.spawnEnemy(this.waveSpawnQueue.shift()!);
    }
  }

  private checkWaveComplete(): void {
    if (!this.waveActive) return;
    if (this.waveSpawnQueue.length > 0) return;
    // Goblino and 0-HP zombies don't count for wave completion
    if (this.enemySprites.some((es) => es.active && !es.isGoblino && es.enemyRef.currentHp > 0)) return;

    // Horde-of-Goblins race guard — MUST run before waveActive=false + the
    // saveRunSession + Crescendo-stack side-effects below. Horde streams
    // spawns via delayedCall(i*gap) with gap starting at 0, so the very first
    // frame after startNextWave() has waveActive=true, empty waveSpawnQueue,
    // and zero live enemies (none of the delayedCalls have fired yet). Without
    // this early-bail the wave is marked complete on frame 1, saveRunSession
    // persists waveJustCompleted=true, Crescendo ticks a stack, and the clear
    // popup fires with zero goblins ever appearing.
    if (BattleScene.hordePending) {
      const hp = BattleScene.hordePending;
      const totalScheduled = hp.mobCount + hp.eliteCount;
      const totalSpawned = hp.spawnedMob + hp.spawnedElite;
      if (totalSpawned < totalScheduled) {
        // Spawns still in flight — leave waveActive = true and bail. No
        // side-effects. checkWaveComplete fires again next frame after more
        // delayedCalls have populated enemySprites.
        return;
      }
    }
    // Same race guard for Telurion's Lair — boss spawns on a delay after half
    // the elites trickle in, so wave-complete must wait until both spawn streams
    // have populated.
    if (BattleScene.telurionPending) {
      const tp = BattleScene.telurionPending;
      if (tp.spawnedElite < tp.eliteCount || !tp.bossSpawned) return;
    }
    // Shadowrealm gates wave-complete on the Shadowlord himself (only enemy
    // pre-portal). Once the boss falls, control passes into endless mode and
    // checkWaveComplete should NEVER fire during endless — the spawn-ticker
    // keeps fresh ghosts on the field constantly. If the field somehow goes
    // empty for a frame in endless, return so the standard "wave cleared"
    // side-effects (saveRunSession, Crescendo ticks, popup) don't trigger.
    if (BattleScene.shadowrealmPending && BattleScene.shadowrealmPending.endlessActive) {
      return;
    }

    this.waveActive = false;
    this.clearPoisonTraps();

    // Crescendo Idol: each equipped instance ticks its OWN per-item stack (cap 50).
    // Per-item counters are stored on the Equipment runtimeStacks field, so swapping
    // copies in/out of a slot preserves each item's independent progress.
    for (const hs of this.heroSprites) {
      if (hs.heroRef.isDead) continue;
      for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        const it = hs.heroRef.equipment[slot];
        if (it?.name === 'Crescendo Idol') {
          it.runtimeStacks = Math.min(50, (it.runtimeStacks ?? 0) + 1);
        }
      }
    }

    // Persist session at wave COMPLETION (includes all kill score from this wave).
    // Saving here — not at wave start — prevents F5 kill-farming: restoring from here
    // always redoes the next wave from scratch without carrying mid-wave kill score.
    this.gsm.saveRunSession(this.gsm.battle.waveNumber, this.runScore, this.infernoMode, this.infernoZone, this.infernoScore, true, this.runGold, this.runLoot, this.runLootSpent, this.runXp, this.runItems);

    // Elite Pack completion — after the 2nd elite wave clears, grant the sigil
    // reward + gold bonus and show the zone-clear popup (the pack is the zone).
    // Short-circuits the normal boss-wave flow since there is no boss.
    if (BattleScene.elitePackPending && this.gsm.battle.waveNumber >= 2) {
      const ep = BattleScene.elitePackPending;
      BattleScene.elitePackPending = null;
      this.grantElitePackReward(ep.count);
      this.gsm.battle.bossDefeated = true;
      this.gsm.battle.lastClearType = 'elite';
      this.gsm.save(true);
      // Zone-cleared popup chains to the elite-pack summary's onClose.
      return;
    }

    // Horde of Goblins completion — one wave only (all mobs + elites streamed in
    // from both sides over ~8 s). On clear, lock the event out for the rest of
    // the run via `hordeClearedThisRun`, grant the reward, and show the popup.
    // The race guard at the top of checkWaveComplete already gated the early-
    // clear case — by the time we reach this branch, all goblins have spawned
    // AND been killed. Straight to the reward. Zone-cleared popup is chained to
    // the event-summary onClose (otherwise it renders on top and hides the event
    // summary entirely — user-reported "reward granted but popup never showed").
    if (BattleScene.hordePending && this.gsm.battle.waveNumber >= 1) {
      const hp = BattleScene.hordePending;
      BattleScene.hordePending = null;
      this.gsm.player.hordeClearedThisRun = true;
      this.grantHordeReward(hp.mobCount + hp.eliteCount);
      this.gsm.battle.bossDefeated = true;
      this.gsm.battle.lastClearType = 'horde';
      this.gsm.save(true);
      return;
    }

    // ── Shadowrealm completion — Shadowlord defeated. ─────────────────
    // Flip into endless mode instead of granting a normal zone-clear: the
    // boss is the GATE, the endless ghost stream is the REWARD (and the
    // measuring stick for builds). The standard zone-clear popup never
    // fires here — the player either survives endless until they wipe,
    // or they die and the run ends. A first-kill sigil drop (Shadowrealm
    // Sigil) and quest-completion ping fire once before the endless
    // ticker takes over.
    if (BattleScene.shadowrealmPending
        && BattleScene.shadowrealmPending.bossSpawned
        && !BattleScene.shadowrealmPending.bossDefeated) {
      const sp = BattleScene.shadowrealmPending;
      const bossStillAlive = this.enemySprites.some(
        (es) => es && es.active && es.enemyRef && es.enemyRef.defId === 'boss_shadowlord' && es.enemyRef.currentHp > 0,
      );
      if (!bossStillAlive) {
        sp.bossDefeated = true;
        sp.endlessActive = true;
        // Bumped from 2.5 s → 4.5 s so the death cinematic (overlay + lore
        // card + portal stinger) gets clean breathing room before the
        // endless ticker starts heaving ghosts back at the player.
        sp.nextSpawnTime = this.time.now + 4500;
        this.gsm.player.shadowlordKilledThisRun = true;
        this.playMalakharDeathCinematic();
        this.grantShadowlordReward();
        this.showAnnouncement('THE PORTAL OPENS');
        // Swap BGM from boss track to endless track. Stop+restart so
        // startBgm picks up the new key based on `sp.endlessActive`. If
        // neither track is shipped yet, the standard rotation continues.
        BattleScene.stopBattleMusic();
        this.startBgm();
        // No zone-clear popup, no advance/stay flow. Endless owns the run from here.
        this.gsm.save(true);
        return;
      }
    }

    // Telurion's Lair completion — single wave (N elites + 1 boss). On clear,
    // grant 10× gold reward + first-kill Stormcoil Charm sigil drop.
    if (BattleScene.telurionPending && this.gsm.battle.waveNumber >= 1) {
      const tp = BattleScene.telurionPending;
      BattleScene.telurionPending = null;
      this.grantTelurionReward(tp.eliteCount);
      this.gsm.battle.bossDefeated = true;
      this.gsm.battle.lastClearType = 'telurion' as any;
      this.gsm.save(true);
      // Zone-cleared popup chains to the Telurion summary's onClose.
      return;
    }

    if (this.gsm.battle.waveNumber >= WAVES_PER_ZONE) {
      this.gsm.battle.bossDefeated = true;
      this.gsm.battle.lastClearType = 'normal';
      this.gsm.trackBossKill();
      this.gsm.checkAchievements();
      // Save immediately so a page refresh restores the zone-cleared state
      // and forces the player back to the advance/stay popup rather than replaying the wave
      this.gsm.save(true);

      // Boss drops a tome choice (stay penalty: 50% reduced chance per stay loop; luck tome boosts it)
      const bossTomeMult = this.gsm.player.stayCount > 0 ? Math.pow(0.5, this.gsm.player.stayCount) : 1;
      const bossLuckMult = 1 + getTomeBonus(this.gsm.player.tomes, 'luck');
      if (bossTomeMult * bossLuckMult >= 1 || Math.random() < bossTomeMult * bossLuckMult) {
        this.showTomeChoice();
      }
      // Scholar rune — every 5 stages, grant extra book choice(s) equal to bookChoiceBonus
      const pbBook = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).bookChoiceBonus;
      if (pbBook > 0 && this.gsm.player.currentLevel > 0 && this.gsm.player.currentLevel % 5 === 0) {
        for (let i = 0; i < pbBook; i++) this.pendingTomeChoices++;
      }

      // Auto-Run no longer auto-advances zones — only auto-picks books / talents
      // (handled in showTomeChoice + processNextTalentChoice). The zone-cleared
      // popup always shows so the player picks Battle / Elite Pack / Horde /
      // Shrine / Altar / Stay manually. If choices are still pending, defer
      // the popup until they resolve via drainPendingAfterTome →
      // pendingZoneCleared.
      this.zonePaused = true;
      this.showZoneClearedPopup();
      // Safety fallback: if pendingZoneCleared is still orphaned after 8s
      // (e.g. tome/talent popup closed without resolving it), force-show the popup.
      this.time.delayedCall(8000, () => {
        if (this.pendingZoneCleared && !this.tomeChoiceActive && !this.talentChoiceActive && !this.zoneClearedActive) {
          this.showZoneClearedPopup();
        }
      });
    } else {
      this.time.delayedCall(1500, () => this.startNextWave());
    }
  }

  // ==================== MIMIC EVENT ====================

  private spawnGoblino(waveEnemies: EnemyInstance[]): void {
    if (this.goblino || this.gameOver) return;

    // HP = 1/3 of estimated boss HP (boss = strongest wave enemy × BOSS_HP_MULT(8))
    const maxEnemyHp = waveEnemies.length > 0
      ? Math.max(...waveEnemies.map((e) => e.maxHp))
      : 300;
    // Boss HP would be ~maxEnemyHp * 8, goblino gets 1/5 of that (beatable)
    const goblinoHp = Math.max(200, Math.floor((maxEnemyHp * 8) / 5));

    // Scale goblino stats by player level so it's catchable at low levels
    const playerLevel = this.gsm.player.currentLevel;
    // Speed: slightly faster than the fastest wave enemy so it's always a chase
    const waveMaxSpeed = waveEnemies.length > 0 ? Math.max(...waveEnemies.map((e) => e.speed)) : 1.0;
    const goblinoSpeed = Math.min(2.5, waveMaxSpeed * 1.2);
    // HP: much lower at early levels (cap at 60% of normal formula for levels 1-3)
    const hpScale = playerLevel <= 3 ? 0.4 + playerLevel * 0.1 : 1;
    const scaledHp = Math.max(100, Math.floor(goblinoHp * hpScale));

    // Create a special EnemyInstance for the Goblino
    const goblinoRef: EnemyInstance = {
      instanceId: `goblino_${Date.now()}`,
      defId: 'goblino',
      name: 'Goblino',
      faction: 'orc',
      tier: 'elite',
      currentHp: scaledHp,
      maxHp: scaledHp,
      attack: 0,
      defense: 5,
      speed: goblinoSpeed,
      attackSpeedMult: 0,
      attackRange: 'melee',
      position: { x: 0, y: 0 },
      xpReward: 0,
      goldReward: 0,
      lootChance: 0,
      lootTier: 0,
      debuffs: [],
    };

    const lane = Math.floor(Math.random() * LANE_Y.length);
    const x = ENEMY_SPAWN_X;
    const y = LANE_Y[lane];

    // Use mimic sprite; fallback to imp (goblin) with gold tint
    const spriteKey = this.textures.exists('npc_mimic') ? 'npc_mimic'
      : this.textures.exists('enemy_imp') ? 'enemy_imp' : 'enemy_wolf';
    const mimicShadow = this.add.ellipse(x, y, 40, 5, 0x000000, 0.82).setDepth(1.5);
    const sprite = this.add.sprite(x, y, spriteKey) as EnemySprite;
    sprite.enemyRef = goblinoRef;
    sprite.lane = lane;
    sprite.shadow = mimicShadow;
    sprite.targetX = BASE_X + 100;
    sprite.maxRightX = GAME_W - 99 / 2 - 12; // goblino display size is 99
    sprite.minLeftX = 99 / 2 + 12;
    sprite.isGoblino = true;
    sprite.setDisplaySize(99, 99).setOrigin(0.5, 1.0).setDepth(2 + y * 0.001);
    if (spriteKey !== 'npc_mimic') sprite.setTint(0xffd700);
    // origin 1.0: feet at y, top at y-110, bar 2px above top at y-112
    sprite.nameText = this.add.text(x, y - 118, '🪙 MIMIC', {
      fontSize: '10px', fontFamily: 'monospace', color: '#fbbf24', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(50);
    sprite.hpBarBg = this.add.rectangle(x, y - 112, 52, 4, 0x333333);
    sprite.hpBar = this.add.rectangle(x, y - 112, 52, 4, 0xfbbf24);

    const walkKey = `${spriteKey}_walk`;
    if (this.anims.exists(walkKey)) sprite.play(walkKey);

    this.enemySprites.push(sprite);
    this.wireFocusTargetClick(sprite);
    this.goblino = sprite;
    // Lifetime: longer at low levels (12s at lv1, 7s at lv5+)
    this.goblinoTimer = playerLevel <= 2 ? 12000 : playerLevel <= 4 ? 10000 : 7000;

    this.showAnnouncement('🪙 MIMIC APPEARED! Kill it for rare loot!');
  }

  private updateGoblino(delta: number): void {
    if (!this.goblino || !this.goblino.active) return;

    // Countdown timer
    this.goblinoTimer -= delta;
    if (this.goblinoTimer <= 0) {
      // Mimic escaped — clean up every attachment (shadow, HP bars, name).
      // Previously the shadow ellipse was leaked on this path: the sprite
      // got destroyed but the shadow stayed glued to the ground.
      this.showAnnouncement('🎁 The Mimic escaped!');
      this.goblino.hpBar?.destroy();
      this.goblino.hpBarBg?.destroy();
      this.goblino.nameText?.destroy();
      this.goblino.shadow?.destroy();
      const idx = this.enemySprites.indexOf(this.goblino);
      if (idx !== -1) this.enemySprites.splice(idx, 1);
      this.goblino.destroy();
      this.goblino = null;
      return;
    }

    const es = this.goblino;

    // Check if killed by any hero attack (hp <= 0)
    if (es.enemyRef.currentHp <= 0) {
      // Find closest hero as the "killer" for killEnemy
      const killer = this.heroSprites.find((h) => !h.heroRef.isDead);
      if (killer) {
        this.killEnemy(es, killer);
      }
      return;
    }

    // Movement: bouncing speed scales with goblino's base speed × the
    // global 1.5× monster-speed multiplier (mirrors main movement loop).
    const moveDt = delta / 16.67;
    const speed = es.enemyRef.speed * 0.8 * 1.5; // already scaled by level in spawnGoblino
    if (es.x <= BASE_X + 120) {
      es.setData('gobDir', 1); // move right
    } else if (es.x >= ENEMY_SPAWN_X - 60) {
      es.setData('gobDir', -1); // move left
    }
    const dir = (es.getData('gobDir') as number) || -1;
    es.x += dir * speed * moveDt;
    es.setFlipX(dir < 0);

    // Random lane switching
    if (Math.random() < 0.005) {
      const newLane = Math.floor(Math.random() * LANE_Y.length);
      es.lane = newLane;
    }
    // Lerp toward target lane Y
    const targetY = LANE_Y[es.lane] + (Math.random() - 0.5) * 10;
    es.y += (targetY - es.y) * 0.03;

    // Update HP bar, name & shadow position
    const hpPct = es.enemyRef.currentHp / es.enemyRef.maxHp;
    es.hpBar.width = 52 * Math.max(0, hpPct);
    es.hpBar.setPosition(es.x, es.y - 112);
    es.hpBarBg.setPosition(es.x, es.y - 112);
    const timeLeft = Math.ceil(this.goblinoTimer / 1000);
    es.nameText?.setText(`🪙 MIMIC (${timeLeft}s)`);
    es.nameText?.setPosition(es.x, es.y - 118);
    es.shadow?.setPosition(es.x, es.y);
  }

  // ── Rot Bomber: stage 10+ event walker, no attack, explodes on death ───────
  // HP sits between elite (×2) and boss (×8); we use ×2.5 of the strongest wave
  // enemy's HP so it's a hard-but-killable DPS check. Speed = fastest elite ×1.2.
  // At ≤10% HP it stops moving and plays the Special anim (reveal → ready to pop).
  // On death it detonates a 200 px AoE for ≈85% of the *average living hero's*
  // max HP to EVERY unit in radius (friendly fire — damages its own enemies too).
  private spawnRotBomber(waveEnemies: EnemyInstance[]): void {
    if (this.gameOver) return;
    if (this.enemySprites.some((es) => es.isRotBomber && es.active)) return;

    const maxEnemyHp = waveEnemies.length > 0
      ? Math.max(...waveEnemies.map((e) => e.maxHp))
      : Math.max(200, 80 * Math.pow(1.15, this.battleLevel - 1));
    // Between elite (×2) and boss (×8). ×4.2 (was ×3.5, +20 % per user
    // balance pass) so the DPS check bites harder; low-damage parties no
    // longer chip it down before it walks into the line.
    const bomberHp = Math.max(720, Math.floor(maxEnemyHp * 4.2));

    // Speed = 1.2× the fastest live wave enemy (fall back to 1.4 if wave is empty).
    const maxWaveSpeed = waveEnemies.length > 0 ? Math.max(...waveEnemies.map((e) => e.speed)) : 1.2;
    const bomberSpeed = Math.min(2.8, maxWaveSpeed * 1.2);

    const bomberRef: EnemyInstance = {
      instanceId: `rotbomber_${Date.now()}`,
      defId: 'rot_bomber',
      name: 'Rot Bomber',
      faction: 'demon',
      tier: 'elite',
      currentHp: bomberHp,
      maxHp: bomberHp,
      attack: 0,
      defense: 6,
      speed: bomberSpeed,
      attackSpeedMult: 0,
      attackRange: 'melee',
      position: { x: 0, y: 0 },
      xpReward: Math.max(60, Math.floor(80 * Math.pow(1.10, this.battleLevel - 1))),
      goldReward: Math.max(30, Math.floor(40 * Math.pow(1.08, this.battleLevel - 1))),
      lootChance: 0.25,
      lootTier: 3,
      debuffs: [],
    };

    const lane = Math.floor(Math.random() * LANE_Y.length);
    const displayW = 140;
    const x = Math.min(ENEMY_SPAWN_X + Math.random() * 60, GAME_W - displayW / 2 - 12);
    const y = LANE_Y[lane];

    const shadow = this.add.ellipse(x, y, 48, 6, 0x000000, 0.82).setDepth(1.5);
    const sprite = this.add.sprite(x, y, 'enemy_rotbomber') as EnemySprite;
    sprite.enemyRef = bomberRef;
    sprite.lane = lane;
    sprite.shadow = shadow;
    sprite.targetX = HERO_ARC_CENTER_X + 20;
    sprite.maxRightX = GAME_W - displayW / 2 - 12;
    sprite.minLeftX  = displayW / 2 + 12;
    sprite.isRotBomber = true;
    sprite.setDisplaySize(displayW, displayW).setOrigin(0.5, 1.0).setDepth(2 + y * 0.001);
    sprite.setFlipX(true); // face LEFT toward the party

    // Pick a target hero on spawn — bomber LOCKS ON and walks toward this
    // specific hero for the rest of its life. The hero is stored by heroRef id
    // so we survive slot swaps; if the locked hero dies mid-chase, updateRot-
    // Bombers re-picks a new target.
    const livingHeroes = this.heroSprites.filter((h) => !h.heroRef.isDead);
    const targetHero = livingHeroes.length > 0
      ? livingHeroes[Math.floor(Math.random() * livingHeroes.length)]
      : null;
    if (targetHero) sprite.setData('rotTargetHeroId', targetHero.heroRef.id);

    // Name label — bright red, above HP bar (danger banner, boss-style).
    sprite.nameText = this.add.text(x, y - 154, 'Rot Bomber', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#ef4444', fontStyle: 'bold',
      stroke: '#0d0a0e', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(50);

    // HP bar — red. Signals "this thing is a threat, not a friendly minion"
    // (previous toxic green fill read as a healer buff to some players).
    const barW = 72;
    sprite.hpBarBg = this.add.rectangle(x, y - 142, barW, 5, 0x2a0808).setDepth(49);
    sprite.hpBar   = this.add.rectangle(x, y - 142, barW, 5, 0xdc2626).setDepth(49);

    // Toxic aura — bespoke Graphics rings instead of effect_aura_bless (which
    // is Lumiel's HoT buff — players were reading the bomber as a heal source).
    // Drawn/redrawn every frame in updateRotBombers so the rings pulse + tint
    // shift to red when primed at low HP.
    const aura = this.add.graphics().setDepth(sprite.depth - 0.1);
    sprite.setData('rotAura', aura);
    sprite.setData('rotAuraT', 0);

    const walkKey = 'enemy_rotbomber_walk';
    if (this.anims.exists(walkKey)) sprite.play(walkKey);

    this.enemySprites.push(sprite);
    this.wireFocusTargetClick(sprite);
    this.showAnnouncement('☠ ROT BOMBER APPEARED — KILL FAST OR CLEAR THE BLAST!');
  }

  /**
   * Per-frame update for all active Rot Bombers. Each bomber:
   *  - Walks left toward the hero formation at its base speed (no bouncing).
   *  - Transitions to "primed" state at ≤10% HP: stops, plays the Special
   *    animation once, and its aura intensifies (red-ish tint + pulse) to
   *    telegraph the imminent blast.
   *  - Aura sprite + name + HP bar track the body each frame.
   */
  private updateRotBombers(delta: number): void {
    const moveDt = delta / 16.67;
    for (const es of this.enemySprites) {
      if (!es.isRotBomber || !es.active) continue;
      if (es.enemyRef.currentHp <= 0) continue; // killEnemy handles detonation

      const hpPct = es.enemyRef.currentHp / es.enemyRef.maxHp;
      const primed = hpPct <= 0.10;

      // Primed: stop moving + switch to the HURT anim so the bomber visibly
      // staggers as a pre-blast telegraph. (Was using `_special` before —
      // special reveal doubled as the "idle-equivalent" pose which felt
      // inert; hurt loops reads as "wounded, about to pop".)
      if (primed) {
        if (!es.getData('rotPrimed')) {
          es.setData('rotPrimed', true);
          if (this.anims.exists('enemy_rotbomber_hurt')) {
            es.play('enemy_rotbomber_hurt');
          }
        }
      } else {
        // Keep the walk anim playing whenever the bomber is in motion — we
        // never use idle, even if the hero-tracking chase briefly holds the
        // bomber at a standstill (e.g. targeted hero at same (x, y)).
        const walkKey = 'enemy_rotbomber_walk';
        if (this.anims.exists(walkKey) && es.anims.currentAnim?.key !== walkKey) {
          es.play(walkKey);
        }

        // Hero-tracking movement. Look up the locked target by id (survives
        // slot swaps). If the locked hero is dead, re-roll against any living
        // hero so the bomber never idles. Moves diagonally toward the hero
        // each frame with a per-sprite left-edge clamp so it can't wander off
        // the map into the base.
        let targetHero: HeroSprite | undefined;
        const targetId = es.getData('rotTargetHeroId') as string | undefined;
        if (targetId) {
          const found = this.heroSprites.find((h) => h.heroRef.id === targetId && !h.heroRef.isDead);
          if (found) targetHero = found;
        }
        if (!targetHero) {
          const living = this.heroSprites.filter((h) => !h.heroRef.isDead);
          targetHero = living.length > 0 ? living[Math.floor(Math.random() * living.length)] : undefined;
          if (targetHero) es.setData('rotTargetHeroId', targetHero.heroRef.id);
        }
        if (targetHero) {
          const dx = targetHero.x - es.x;
          const dy = targetHero.y - es.y;
          const dist = Math.hypot(dx, dy) || 1;
          // Rot Bomber chase — 1.5× global monster-speed multiplier.
          const step = es.enemyRef.speed * 1.5 * moveDt;
          const nx = Math.max(es.minLeftX, es.x + (dx / dist) * step);
          es.x = nx;
          es.y = es.y + (dy / dist) * step;
        } else {
          // No living heroes — fall back to the old left-ward march.
          es.x = Math.max(es.minLeftX, es.x - es.enemyRef.speed * 1.5 * moveDt);
        }
      }

      // Hero contact check — if the bomber body touches any living hero, it
      // detonates immediately at +10 % damage (the momentum adds to the blast)
      // and the bomber dies from its own explosion. 52 px collision radius
      // matches the sprite footprint so players see the bump visibly land.
      if (!(es.enemyRef as any)._detonated) {
        for (const hs of this.heroSprites) {
          if (hs.heroRef.isDead) continue;
          if (Phaser.Math.Distance.Between(hs.x, hs.y, es.x, es.y) > 52) continue;
          (es.enemyRef as any)._detonated = true;
          this.detonateRotBomber(es.x, es.y, 1.10);
          es.enemyRef.currentHp = 0;
          break;
        }
      }

      // HP bar + name + shadow follow
      es.hpBar.width = 72 * Math.max(0, hpPct);
      es.hpBar.setPosition(es.x, es.y - 142);
      es.hpBarBg.setPosition(es.x, es.y - 142);
      es.nameText?.setPosition(es.x, es.y - 154);
      es.shadow?.setPosition(es.x, es.y);

      // Custom aura: three concentric pulsing rings redrawn from scratch each
      // frame. Green while walking, shifts to red when primed at low HP. Not
      // reusing any hero-buff texture so players can't confuse it with a heal.
      const aura = es.getData('rotAura') as Phaser.GameObjects.Graphics | undefined;
      if (aura && aura.active) {
        const t = ((es.getData('rotAuraT') as number) ?? 0) + delta / 1000;
        es.setData('rotAuraT', t);
        aura.clear();
        const baseColor = primed ? 0xcc3322 : 0x4a8a2a;
        const cx = es.x;
        const cy = es.y - 35;
        const intensity = primed ? 1.0 : 0.65;
        for (let i = 0; i < 3; i++) {
          const phase = t * 2.2 + i * 1.1;
          const r = 34 + i * 14 + Math.sin(phase) * 4;
          const ringAlpha = intensity * (1 - i * 0.22) * (0.65 + 0.35 * Math.sin(phase + i));
          aura.fillStyle(baseColor, Math.max(0.08, ringAlpha));
          aura.fillCircle(cx, cy, r);
        }
        aura.setDepth(es.depth - 0.1);
      }
    }
  }

  /**
   * Detonate a Rot Bomber at (x, y). Deals a lethal blow to every hero within
   * 200 px — damage equals 125% of each hero's own max HP (per hero, not an
   * average), routed through applyDamageToHero so shields + hard DR windows
   * still protect. Unshielded heroes die; shielded/bear-form heroes may
   * survive as a sliver. Bosses take ZERO damage. Non-boss enemies eat the
   * same 125%-of-hero-max-HP blast as collateral.
   */
  /** `dmgMult` scales the blast — defaults to 1.0 for kill-triggered blasts,
   *  1.10 when the bomber detonates from touching a hero (momentum add). */
  private detonateRotBomber(x: number, y: number, dmgMult = 1.0): void {
    const RADIUS = 200;

    // Blast VFX: orange explosion core + lingering green poison cloud. Both scaled
    // up so the visual radius roughly matches the 200 px damage radius.
    if (this.textures.exists('effect_explosion') && this.anims.exists('fx_explosion')) {
      const boom = this.add.sprite(x, y, 'effect_explosion').setDepth(25).setScale(3.5);
      boom.play('fx_explosion');
      boom.once('animationcomplete', () => boom.destroy());
    }
    if (this.textures.exists('effect_poison_cloud') && this.anims.exists('fx_poison_cloud')) {
      const cloud = this.add.sprite(x, y, 'effect_poison_cloud').setDepth(24).setScale(3.8).setAlpha(0.85).setTint(0x7acc60);
      cloud.play('fx_poison_cloud');
      cloud.once('animationcomplete', () => cloud.destroy());
    }
    this.cameras.main.shake(350, 0.012);

    const living = this.heroSprites.filter((h) => !h.heroRef.isDead);

    // Hero damage — was 75 % of max HP, halved to 37.5 % per earlier user
    // feedback (squishy one-shot fix), then bumped to 45 % (+20 %) per the
    // "rot bomber damage +20 %" balance pass so the explosion is a real
    // threat without going back to one-shot territory. Collision blasts
    // still deal 10 % more from `dmgMult`; shields + DR still protect.
    for (const hs of living) {
      if (Phaser.Math.Distance.Between(hs.x, hs.y, x, y) > RADIUS) continue;
      const maxHp = this.effStats(hs.heroRef).maxHp ?? hs.heroRef.stats.maxHp ?? 1;
      const heroDmg = Math.max(72, Math.floor(maxHp * 0.45 * dmgMult));
      this.applyDamageToHero(hs, heroDmg, undefined, true /* AOE: Rot Bomber explosion */);
      this.showDamageNumber(hs.x, hs.y - 30, `💥 ${heroDmg}`, '#f97316', '14px');
    }
    // Rot Bomber also catches summons. Use a flat % of summon max HP so the
    // golem isn't one-shot by his own positioning — 35 % feels like a real
    // hit but leaves room to keep fighting if you're not standing on top.
    for (const ss of this.summonSprites) {
      if (ss.isDead) continue;
      if (Phaser.Math.Distance.Between(ss.x, ss.y, x, y) > RADIUS) continue;
      const summonDmg = Math.max(50, Math.floor(ss.maxHp * 0.35 * dmgMult));
      this.applyDamageToSummon(ss, summonDmg);
    }

    // Baseline for enemy collateral — average hero max HP × 0.75 (matches the
    // per-hero number) so minion / elite damage stays in the same ballpark.
    const avgMaxHp = living.length > 0
      ? living.reduce((s, h) => s + (this.effStats(h.heroRef).maxHp ?? h.heroRef.stats.maxHp ?? 1), 0) / living.length
      : 400;
    const heroDmg = Math.max(120, Math.floor(avgMaxHp * 0.75 * dmgMult));

    // Enemy friendly fire — minions + elites eat the full blast. Bosses are
    // IMMUNE: a bomber spawning next to the boss wave would otherwise erase
    // most of the boss's HP in a single blast (~85 % of avg-hero-max-HP ≈
    // enough to one-shot most mid-run bosses), trivialising the encounter.
    for (const other of this.enemySprites) {
      if (!other.active || other.isRotBomber) continue;
      if (other.enemyRef.currentHp <= 0) continue;
      if (other.enemyRef.tier === 'boss') continue; // bosses take zero bomber damage
      if (Phaser.Math.Distance.Between(other.x, other.y, x, y) > RADIUS) continue;
      other.enemyRef.currentHp -= heroDmg;
      this.showDamageNumber(other.x, other.y - 24, `💥 ${heroDmg}`, '#f97316', '12px');
      if (other.enemyRef.currentHp <= 0) {
        const killer = this.heroSprites.find((h) => !h.heroRef.isDead);
        if (killer) this.killEnemy(other, killer);
      }
    }
  }

  private showGoblinoShop(): void {
    const overlayOpen = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene']
      .some((k) => this.scene.isActive(k));
    if (overlayOpen) return;
    // Defer if a tome / talent / zone-cleared / shrine popup is active. Showing
    // Mimic's Chest on top of an active tome popup left both visible at once
    // (the user reported the "MIMIC'S CHEST + CHOOSE A BOOK" overlap). Retry
    // after a short tick so the chest still appears once the blocker clears.
    if (this.tomeChoiceActive || this.talentChoiceActive || this.zoneClearedActive
        || this.merchantShopActive || this.goblinoShopActive) {
      this.time.delayedCall(400, () => this.showGoblinoShop());
      return;
    }
    this.goblinoShopActive = true;

    const width = GAME_W, height = GAME_H;
    const elements: Phaser.GameObjects.GameObject[] = [];

    // Overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x050402, 0.88)
      .setDepth(70).setInteractive();
    elements.push(overlay);

    // (Mimic sprite icon removed — title is self-explanatory)

    // Title
    const title = this.add.text(width / 2, height / 2 - 150, "✦  MIMIC'S CHEST  ✦", {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(71);
    elements.push(title);

    const subtitle = this.add.text(width / 2, height / 2 - 128, 'Choose one trophy for your hero (Trophy slot)', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(71);
    elements.push(subtitle);

    // Generate 3 unique trophy items
    const level = this.gsm.player.currentLevel;
    const usedNames = new Set<string>();
    const items: import('@roe/shared').Equipment[] = [];
    for (let i = 0; i < 3; i++) {
      let trophy: import('@roe/shared').Equipment;
      let attempts = 0;
      do {
        trophy = generateTrophy(level);
        attempts++;
      } while (usedNames.has(trophy.name) && attempts < 20);
      usedNames.add(trophy.name);
      items.push(trophy);
    }

    // 3 trophy cards
    const cardW = 200;
    const gap = 16;
    const totalW = 3 * cardW + 2 * gap;
    const startX = (width - totalW) / 2 + cardW / 2;

    items.forEach((item, i) => {
      const cx = startX + i * (cardW + gap);
      const cy = height / 2 + 10;
      const rarityColor = RARITY_COLORS[item.rarity] || '#cc3322';

      // Card bg
      const rarColorHex = Phaser.Display.Color.HexStringToColor(rarityColor).color;
      const card = this.add.rectangle(cx, cy, cardW, 180, 0x0d0a08, 0.97)
        .setStrokeStyle(2, rarColorHex)
        .setDepth(71).setInteractive({ useHandCursor: true });
      elements.push(card);

      // Trophy icon
      if (item.spriteKey && this.textures.exists(item.spriteKey)) {
        elements.push(this.add.image(cx, cy - 65, item.spriteKey)
          .setDisplaySize(32, 32).setDepth(72));
      }

      // Trophy name
      elements.push(this.add.text(cx, cy - 40, item.name, {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: rarityColor, fontStyle: 'bold',
        wordWrap: { width: cardW - 16 }, align: 'center',
      }).setOrigin(0.5).setDepth(72));

      // Rarity + slot label
      elements.push(this.add.text(cx, cy - 24, `[Trophy] ${item.rarity}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
      }).setOrigin(0.5).setDepth(72));

      // Effect (main draw — big and prominent)
      if (item.specialEffect) {
        elements.push(this.add.text(cx, cy + 10, item.specialEffect, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a',
          wordWrap: { width: cardW - 20 }, align: 'center', lineSpacing: 2,
        }).setOrigin(0.5).setDepth(72));
      }

      // Hover
      card.on('pointerover', () => { card.setFillStyle(0x1a1205, 1); card.setScale(1.03); });
      card.on('pointerout', () => { card.setFillStyle(0x0d0a08, 0.97); card.setScale(1); });

      // Choose this trophy (free - it's a reward)
      card.on('pointerdown', () => {
        // Find first alive deployed hero without a trophy
        const heroIdx = this.gsm.player.deployedHeroes.find(idx => {
          const h = this.gsm.player.heroes[idx];
          const hs = this.heroSprites.find((s) => s.heroRef === h);
          return h && !h.equipment.reserved && hs && !hs.heroRef.isDead;
        });

        if (heroIdx !== undefined) {
          // Assign to first hero without a trophy
          const hero = this.gsm.player.heroes[heroIdx];
          if (hero) hero.equipment.reserved = item;
        } else {
          // All heroes already have trophies — send to inventory
          this.gsm.player.inventory.push(item);
          this.gsm.player.newLootCount++;
        }
        this.gsm.save(true); // immediate: prevent F5 refund exploit

        for (const el of elements) el.destroy();
        this.goblinoShopActive = false;
        const dest = heroIdx !== undefined
          ? `Trophy equipped: ${item.name}!`
          : `${item.name} added to inventory (all heroes have trophies)`;
        this.showAnnouncement(dest);
        this.refreshTrophyDisplay();
        // Trophy chest closed — drain any tome choices that queued themselves while the
        // Mimic's Chest was blocking, so the book popup surfaces after the trophy pick.
        this.drainPendingAfterTome();
      });
    });

    // Skip button — both bg rect and text label interactive so the click lands
    // anywhere on the visible button area, not just the text glyphs.
    const skipBtnBg = this.add.rectangle(width / 2, height / 2 + 120, 100, 34, 0x0d0a08, 1)
      .setStrokeStyle(2, 0x5a3600).setDepth(71);
    elements.push(skipBtnBg);
    const skipBtn = this.add.text(width / 2, height / 2 + 120, 'SKIP', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#6b4020',
    }).setOrigin(0.5).setDepth(72);
    elements.push(skipBtn);
    wireButton({
      bg: skipBtnBg, text: skipBtn,
      onHover: () => { skipBtn.setColor('#a07840'); skipBtnBg.setStrokeStyle(2, 0x9a6020); },
      onOut:   () => { skipBtn.setColor('#6b4020'); skipBtnBg.setStrokeStyle(2, 0x5a3600); },
      onDown:  () => {
        for (const el of elements) el.destroy();
        this.goblinoShopActive = false;
        // Drain any queued tome choices that were blocked while the trophy chest was open.
        this.drainPendingAfterTome();
      },
    });
  }

  // ==================== MERCHANT SHOP ====================

  /** Safely play an animation on baseSprite — guards against missing frames / broken textures. */
  private playMerchantAnim(key: string): boolean {
    if (!this.baseSprite?.active) return false;
    const anim = this.anims.get(key);
    if (!anim || anim.frames.length === 0 || anim.frames[0].duration === undefined) return false;
    this.baseSprite.play(key);
    return true;
  }

  /** Show the ! badge above the vendor and play the approval animation when stock refreshes. */
  private showMerchantRefreshBadge(): void {
    // Play approval animation then return to idle
    if (this.playMerchantAnim('npc_merchant_approval')) {
      this.baseSprite.once('animationcomplete', () => {
        this.playMerchantAnim('npc_merchant_idle');
      });
    }
    // Fade-in the ! badge and start a bobbing loop
    if (!this.merchantExclamBadge) return;
    const badge = this.merchantExclamBadge;
    this.tweens.killTweensOf(badge);
    badge.setAlpha(0);
    // Fade in
    this.tweens.add({
      targets: badge,
      alpha: 1,
      duration: 200,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Continuous bob up/down ±4px
        this.tweens.add({
          targets: badge,
          y: badge.y - 5,
          duration: 550,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
      },
    });
  }

  /** In-battle Whiskers popup — offers to deposit all unequipped bag sigils into the
   *  account-wide collection at SIGIL_DEPOSIT_PRICE runes per sigil. */
  private showWhiskersBattlePopup(): void {
    const overlayOpen = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene']
      .some((k) => this.scene.isActive(k));
    if (this.gameOver || overlayOpen) return;
    const count = this.gsm.countUnequippedBagSigils();
    const price = GameStateManager.SIGIL_DEPOSIT_PRICE;
    const total = count * price;
    const runes = this.gsm.player.resources.loot ?? 0;

    // Compact speech-bubble popup anchored above Whiskers. Colors match the shared
    // tooltip palette (#0d0a0e background + #c8973a gold border/text) instead of the
    // previous dim-overlay modal style.
    const PANEL_W = 240, PANEL_H = 104;
    // Whiskers' patrol lives between x≈680..780 with feet at y=525. Anchor above her feet,
    // clamped so the popup never leaves the screen if she's wandering near an edge.
    const anchorX = 730;
    const anchorY = 525 - 80; // bottom of the popup sits just above her head
    const panelCx = Math.max(PANEL_W / 2 + 8, Math.min(GAME_W - PANEL_W / 2 - 8, anchorX));
    const panelCy = anchorY - PANEL_H / 2;

    const overlay = this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0)
      .setDepth(90).setInteractive(); // invisible click-catcher so tapping outside dismisses
    // Match the Zone Cleared popup palette: dark-brown fill, gold outer border, dim-gold inner.
    const panel = this.add.rectangle(panelCx, panelCy, PANEL_W, PANEL_H, 0x0d0a08, 0.97)
      .setDepth(91).setStrokeStyle(2, 0xc8973a);
    const panelInner = this.add.rectangle(panelCx, panelCy, PANEL_W - 6, PANEL_H - 6, 0x0d0a08, 0)
      .setDepth(91).setStrokeStyle(1, 0x5a3600, 0.5);
    const title = this.add.text(panelCx, panelCy - PANEL_H / 2 + 10, 'Rudy', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(92);

    let bodyText: string;
    if (count === 0) {
      bodyText = 'No unequipped sigils to deposit.\nBring me some next run.';
    } else if (runes < price) {
      bodyText = `Deposit ${count} sigil${count === 1 ? '' : 's'}\nfor ${total}◆?  (not enough runes)`;
    } else {
      const affordable = Math.min(count, Math.floor(runes / price));
      bodyText = affordable === count
        ? `Deposit ${count} sigil${count === 1 ? '' : 's'}\nto your collection for ${total}◆?`
        : `Can only afford ${affordable} of ${count}\nsigils (${affordable * price}◆).`;
    }
    const body = this.add.text(panelCx, panelCy - 4, bodyText, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#b38a5a',
      align: 'center', lineSpacing: 3,
    }).setOrigin(0.5).setDepth(92);

    const canConfirm = count > 0 && runes >= price;
    const btnY = panelCy + PANEL_H / 2 - 16;
    const confirmBtn = this.add.text(panelCx - 40, btnY, 'DEPOSIT', {
      fontSize: '10px', fontFamily: 'Georgia, serif',
      color: canConfirm ? '#c8973a' : '#4a3010', fontStyle: 'bold',
      backgroundColor: '#0d0a0e', padding: { x: 6, y: 2 },
    }).setOrigin(0.5).setDepth(92);
    if (canConfirm) confirmBtn.setInteractive({ useHandCursor: true });
    const cancelBtn = this.add.text(panelCx + 40, btnY, 'CANCEL', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#b38a5a', fontStyle: 'bold',
      backgroundColor: '#0d0a0e', padding: { x: 6, y: 2 },
    }).setOrigin(0.5).setDepth(92).setInteractive({ useHandCursor: true });

    const close = (): void => {
      overlay.destroy(); panel.destroy(); panelInner.destroy(); title.destroy(); body.destroy();
      confirmBtn.destroy(); cancelBtn.destroy();
    };
    cancelBtn.on('pointerdown', close);
    overlay.on('pointerdown', close);
    if (canConfirm) {
      confirmBtn.on('pointerdown', () => {
        const { moved, spent } = this.gsm.depositUnequippedSigils();
        this.showDamageNumber(panelCx, panelCy - PANEL_H / 2 - 6, `${moved} → Collection  (−${spent}◆)`, '#c8973a', '12px');
        this.invalidateSigilCache();
        this.refreshConsumableIcons();
        close();
      });
    }
  }

  /**
   * Blacksmith upgrade modal — two upgrade tracks per equipped item:
   *  - **+1 LV** (cheap, repeatable): bumps `item.level` and scales integer
   *    statBonuses by ~10 %. `Math.max(50, level × 60)` runes.
   *  - **+1 RARITY** (expensive, max once): bumps the rarity tier label
   *    (common→uncommon→…→legendary) and applies a flat +30 % boost to every
   *    integer statBonus. `level × 200` runes for weapons/armor, `level × 300`
   *    runes for relics. Locked once used (per-item flag).
   *
   * Lists every equipped weapon + armor + relic across the player's heroes.
   */
  private showBlacksmithPopup(smith: Phaser.GameObjects.Sprite): void {
    const overlayOpen = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene']
      .some((k) => this.scene.isActive(k));
    if (this.gameOver || overlayOpen) return;

    const PANEL_W = 480;
    const PANEL_H = 360;
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;
    const elements: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(cx, cy, GAME_W, GAME_H, 0x000000, 0.55).setDepth(90).setInteractive();
    elements.push(overlay);
    const panel = this.add.rectangle(cx, cy, PANEL_W, PANEL_H, 0x0d0a08, 0.97).setDepth(91).setStrokeStyle(2, 0xc8973a);
    elements.push(panel);
    const inner = this.add.rectangle(cx, cy, PANEL_W - 8, PANEL_H - 8, 0x0d0a08, 0).setDepth(91).setStrokeStyle(1, 0x5a3600, 0.5);
    elements.push(inner);

    const title = this.add.text(cx, cy - PANEL_H / 2 + 18, 'Blacksmith', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(92);
    elements.push(title);
    const subtitle = this.add.text(cx, cy - PANEL_H / 2 + 38, 'Upgrade gear — costs runes, scales stats', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(92);
    elements.push(subtitle);

    const closeBtn = this.add.text(cx + PANEL_W / 2 - 14, cy - PANEL_H / 2 + 12, '✕', {
      fontSize: '14px', fontFamily: 'Arial', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(92).setInteractive({ useHandCursor: true });
    elements.push(closeBtn);
    const close = (): void => {
      elements.forEach((o) => o.destroy());
      // Smith resumes hammering once the panel closes.
      if (smith.active && this.anims.exists('npc_blacksmith_working')) {
        smith.play('npc_blacksmith_working');
      }
    };
    closeBtn.on('pointerdown', close);
    overlay.on('pointerdown', close);

    // Rune balance line — kept up to date after every successful upgrade.
    const runeText = this.add.text(cx, cy - PANEL_H / 2 + 56, '', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#fbbf24',
    }).setOrigin(0.5).setDepth(92);
    elements.push(runeText);
    const refreshRunes = (): void => {
      runeText.setText(`◆ ${this.gsm.player.resources.loot ?? 0} runes`);
    };
    refreshRunes();

    // Build a flat list of {heroIdx, slot, item} for every equipped weapon + armor + relic.
    type SlotKey = 'weapon1' | 'weapon2' | 'armor' | 'relic1' | 'relic2' | 'relic3' | 'relic4';
    type Row = { heroIdx: number; slot: SlotKey; itemName: string; itemLevel: number; itemRarity: string; rarityUpgraded: boolean; isRelic: boolean };
    const RARITY_COLORS: Record<string, string> = {
      common: '#9ca3af', uncommon: '#60d394', rare: '#60a5fa', epic: '#a78bfa', legendary: '#f59e0b', unique: '#f97316',
    };
    const collectRows = (): Row[] => {
      const rows: Row[] = [];
      const slots: SlotKey[] = ['weapon1', 'weapon2', 'armor', 'relic1', 'relic2', 'relic3', 'relic4'];
      this.gsm.player.heroes.forEach((h, hi) => {
        for (const slot of slots) {
          const it = h.equipment[slot];
          if (it) rows.push({
            heroIdx: hi, slot, itemName: it.name, itemLevel: it.level,
            itemRarity: it.rarity, rarityUpgraded: !!it.rarityUpgraded, isRelic: slot.startsWith('relic'),
          });
        }
      });
      return rows;
    };

    const rowObjects: Phaser.GameObjects.GameObject[] = [];
    // Vertical scroll offset for the row list — wheel scrolls when there are
    // more rows than fit. Persists across renderRows() calls so a re-render
    // (after an upgrade) doesn't reset the player's position.
    let scrollOffset = 0;
    const ROW_H = 30;
    const renderRows = (): void => {
      rowObjects.forEach((o) => o.destroy());
      rowObjects.length = 0;
      const rows = collectRows();
      const listTop = cy - PANEL_H / 2 + 84;
      const listBottom = cy + PANEL_H / 2 - 24;
      const maxRows = Math.floor((listBottom - listTop) / ROW_H);
      const totalRows = rows.length;
      scrollOffset = Math.max(0, Math.min(scrollOffset, Math.max(0, totalRows - maxRows)));
      const visible = rows.slice(scrollOffset, scrollOffset + maxRows);
      if (totalRows === 0) {
        const empty = this.add.text(cx, cy, 'Nothing equipped to upgrade.', {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: '#7a5a30', fontStyle: 'italic',
        }).setOrigin(0.5).setDepth(92);
        rowObjects.push(empty);
        elements.push(empty);
        return;
      }
      visible.forEach((row, i) => {
        const ry = listTop + i * ROW_H + ROW_H / 2;
        const heroName = this.gsm.player.heroes[row.heroIdx]?.name ?? `H${row.heroIdx + 1}`;
        const slotLabel = row.slot === 'armor' ? 'Armor'
          : row.slot === 'weapon1' ? 'Wpn1'
          : row.slot === 'weapon2' ? 'Wpn2'
          : `R${row.slot.slice(-1)}`;
        const labelTxt = this.add.text(cx - PANEL_W / 2 + 14, ry, `${heroName.slice(0, 7)} · ${slotLabel}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
        }).setOrigin(0, 0.5).setDepth(92);
        rowObjects.push(labelTxt); elements.push(labelTxt);

        const rarityColor = RARITY_COLORS[row.itemRarity] ?? '#c8973a';
        const nameTxt = this.add.text(cx - PANEL_W / 2 + 96, ry, `${row.itemName} Lv${row.itemLevel}`, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: rarityColor,
        }).setOrigin(0, 0.5).setDepth(92);
        rowObjects.push(nameTxt); elements.push(nameTxt);

        const runes = this.gsm.player.resources.loot ?? 0;

        // +1 LV button (cheap, repeatable)
        const lvCost = GameStateManager.blacksmithUpgradeCost(row.itemLevel);
        const lvAffordable = runes >= lvCost;
        const lvBtn = this.add.text(cx + PANEL_W / 2 - 96, ry, `+LV ◆${lvCost}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif',
          color: lvAffordable ? '#c8973a' : '#4a3010', fontStyle: 'bold',
          backgroundColor: '#1a0e04', padding: { x: 6, y: 3 },
        }).setOrigin(1, 0.5).setDepth(92);
        if (lvAffordable) lvBtn.setInteractive({ useHandCursor: true });
        rowObjects.push(lvBtn); elements.push(lvBtn);

        // +1 RARITY button (expensive, once per item)
        const isLegendary = row.itemRarity === 'legendary' || row.itemRarity === 'unique';
        const rarityCost = GameStateManager.blacksmithRarityCost(row.itemLevel, row.isRelic);
        const rarityLocked = row.rarityUpgraded || isLegendary;
        const rarityAffordable = !rarityLocked && runes >= rarityCost;
        const rarityLabel = rarityLocked ? '+RAR ✓' : `+RAR ◆${rarityCost}`;
        const rarityBtn = this.add.text(cx + PANEL_W / 2 - 14, ry, rarityLabel, {
          fontSize: '9px', fontFamily: 'Georgia, serif',
          color: rarityAffordable ? '#a78bfa' : (rarityLocked ? '#4a3010' : '#5a3a14'),
          fontStyle: 'bold',
          backgroundColor: '#1a0e04', padding: { x: 6, y: 3 },
        }).setOrigin(1, 0.5).setDepth(92);
        if (rarityAffordable) rarityBtn.setInteractive({ useHandCursor: true });
        rowObjects.push(rarityBtn); elements.push(rarityBtn);

        if (lvAffordable) {
          lvBtn.on('pointerdown', () => {
            const result = this.gsm.upgradeEquipmentLevel(row.heroIdx, row.slot);
            if (result.ok) {
              this.showDamageNumber(cx, cy - PANEL_H / 2 - 6, `+1 LV  ${row.itemName}  −${result.cost}◆`, '#c8973a', '12px');
              this.tweens.add({ targets: smith, scaleX: smith.scaleX * 1.06, scaleY: smith.scaleY * 1.06, duration: 90, yoyo: true });
              refreshRunes();
              renderRows();
            }
          });
        }
        if (rarityAffordable) {
          rarityBtn.on('pointerdown', () => {
            const result = this.gsm.upgradeEquipmentRarity(row.heroIdx, row.slot);
            if (result.ok) {
              this.showDamageNumber(cx, cy - PANEL_H / 2 - 6, `+1 RARITY  ${row.itemName} → ${result.newRarity}  −${result.cost}◆`, '#a78bfa', '13px');
              this.tweens.add({ targets: smith, scaleX: smith.scaleX * 1.10, scaleY: smith.scaleY * 1.10, duration: 140, yoyo: true });
              refreshRunes();
              renderRows();
            }
          });
        }
      });
    };
    renderRows();

    // Wheel-scroll for long lists (rare with 3 heroes × 7 slots = 21 max but worth supporting).
    const wheelHandler = (_p: unknown, _go: unknown, _dx: number, dy: number): void => {
      scrollOffset += dy > 0 ? 1 : -1;
      renderRows();
    };
    this.input.on('wheel', wheelHandler);
    overlay.once('destroy', () => this.input.off('wheel', wheelHandler));
  }

  /**
   * Rune Sage modal — services panel. First service: **Forget a Book** (20 runes
   * per use). Lists every tome the player has invested in (level > 0); click
   * FORGET on a row to drop it back to level 0, freeing a slot in the 6-of-N
   * pick limit so the player can pivot mid-run. More services (cleanse boon /
   * forge Epic / sigil reroll) land in future passes.
   */
  private showSagePopup(sage: Phaser.GameObjects.Sprite): void {
    const overlayOpen = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene']
      .some((k) => this.scene.isActive(k));
    if (this.gameOver || overlayOpen) return;

    // Tightened from 460×340 → 380×260 per user feedback ("too much empty
    // space"). Header / runes line / row pitch all reflowed below.
    const PANEL_W = 380;
    const PANEL_H = 260;
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;
    const elements: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(cx, cy, GAME_W, GAME_H, 0x000000, 0.55).setDepth(90).setInteractive();
    elements.push(overlay);
    const panel = this.add.rectangle(cx, cy, PANEL_W, PANEL_H, 0x0d0a08, 0.97).setDepth(91).setStrokeStyle(2, 0xa78bfa);
    elements.push(panel);
    const inner = this.add.rectangle(cx, cy, PANEL_W - 8, PANEL_H - 8, 0x0d0a08, 0).setDepth(91).setStrokeStyle(1, 0x4a3a6a, 0.6);
    elements.push(inner);

    const title = this.add.text(cx, cy - PANEL_H / 2 + 14, 'Rune Sage', {
      fontSize: '15px', fontFamily: 'Georgia, serif', color: '#a78bfa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(92);
    elements.push(title);
    const subtitle = this.add.text(cx, cy - PANEL_H / 2 + 30, 'Forget a Book — drops level to 0, frees a slot', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a6a8a', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(92);
    elements.push(subtitle);

    const closeBtn = this.add.text(cx + PANEL_W / 2 - 12, cy - PANEL_H / 2 + 10, '✕', {
      fontSize: '13px', fontFamily: 'Arial', color: '#a78bfa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(92).setInteractive({ useHandCursor: true });
    elements.push(closeBtn);
    const close = (): void => elements.forEach((o) => o.destroy());
    closeBtn.on('pointerdown', close);
    overlay.on('pointerdown', close);

    const runeText = this.add.text(cx, cy - PANEL_H / 2 + 46, '', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#fbbf24',
    }).setOrigin(0.5).setDepth(92);
    elements.push(runeText);
    const refreshRunes = (): void => {
      runeText.setText(`◆ ${this.gsm.player.resources.loot ?? 0} runes`);
    };
    refreshRunes();

    const rowObjects: Phaser.GameObjects.GameObject[] = [];
    const ROW_H = 26;
    const renderRows = (): void => {
      rowObjects.forEach((o) => o.destroy());
      rowObjects.length = 0;
      const picked = (this.gsm.player.tomes ?? []).filter((t) => t.level > 0);
      const listTop = cy - PANEL_H / 2 + 64;
      if (picked.length === 0) {
        const empty = this.add.text(cx, cy, 'No tomes to forget.\nCome back when you have invested.', {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: '#5a4a6a', fontStyle: 'italic',
          align: 'center',
        }).setOrigin(0.5).setDepth(92);
        rowObjects.push(empty);
        elements.push(empty);
        return;
      }
      picked.forEach((tome, i) => {
        const def = TOME_DEFINITIONS[tome.id];
        if (!def) return;
        const ry = listTop + i * ROW_H + ROW_H / 2;
        const nameTxt = this.add.text(cx - PANEL_W / 2 + 18, ry, `${def.icon} ${def.name}  Lv${tome.level}`, {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: def.color ?? '#c8973a',
        }).setOrigin(0, 0.5).setDepth(92);
        rowObjects.push(nameTxt); elements.push(nameTxt);

        const cost = GameStateManager.SAGE_FORGET_BOOK_COST;
        const runes = this.gsm.player.resources.loot ?? 0;
        const affordable = runes >= cost;
        const btn = this.add.text(cx + PANEL_W / 2 - 14, ry, `FORGET ◆${cost}`, {
          fontSize: '10px', fontFamily: 'Georgia, serif',
          color: affordable ? '#a78bfa' : '#4a3010', fontStyle: 'bold',
          backgroundColor: '#1a0e1e', padding: { x: 8, y: 3 },
        }).setOrigin(1, 0.5).setDepth(92);
        if (affordable) btn.setInteractive({ useHandCursor: true });
        rowObjects.push(btn); elements.push(btn);

        if (affordable) {
          btn.on('pointerdown', () => {
            const ok = this.gsm.forgetTomePick(tome.id);
            if (ok) {
              this.showDamageNumber(cx, cy - PANEL_H / 2 - 6, `Forgot ${def.name}  −${cost}◆`, '#a78bfa', '12px');
              // Channel pulse on the sage to confirm.
              if (sage.active && this.anims.exists('npc_sage_special')) {
                sage.play('npc_sage_special');
                sage.once('animationcomplete', () => {
                  if (sage.active && this.anims.exists('npc_sage_idle')) sage.play('npc_sage_idle');
                });
              }
              refreshRunes();
              renderRows();
            }
          });
        }
      });
    };
    renderRows();
  }

  private showMerchantShop(): void {
    // Merchant panel sits on the LEFT half of the canvas; HeroScene and
    // InventoryScene are right-half-clipped via `setupCamera('overlay')`,
    // so those two are explicitly allowed to be open at the same time.
    // Full-canvas overlays (Achievements / Tomes / Shop / Runes) still
    // block — they'd visually crash into the merchant.
    const blockingOverlay = ['AchievementScene', 'TomesScene', 'ShopScene', 'RuneShopScene']
      .some((k) => this.scene.isActive(k));
    if (this.merchantShopActive || this.gameOver || blockingOverlay) return;
    this.merchantShopActive = true;
    // Hide the refresh exclamation badge
    if (this.merchantExclamBadge) this.merchantExclamBadge.setAlpha(0);

    // Ensure merchant inventory is generated (full wipe when interval elapsed)
    this.gsm.refreshMerchantInventory();
    const merchant = this.gsm.player.merchant;

    const width = GAME_W, height = GAME_H;
    const elements: Phaser.GameObjects.GameObject[] = [];
    let activeTooltip: Phaser.GameObjects.Text | null = null;
    const clearTooltip = () => { activeTooltip?.destroy(); activeTooltip = null; };
    let panelOpen = true;

    // Invisible click-catcher covering the LEFT HALF of the canvas. Used
    // to close the merchant on click-outside-panel without dimming the
    // battlefield (the previous 50 % black overlay obscured the live wave
    // playing behind the panel — user wanted to keep eyes on the fight
    // while shopping). Right half is left clean so the HeroScene /
    // InventoryScene overlays own that side.
    const overlay = this.add.rectangle(width / 4, height / 2, width / 2, height, 0x000000, 0)
      .setDepth(70).setInteractive().setName('merchant_overlay');
    const forceCloseMerchant = () => {
      clearTooltip();
      panelOpen = false;
      (this as any)._mysteryBuying = false;
      for (const el of elements) { try { el.destroy(); } catch { /* ignore */ } }
      // Safety sweep: kill any named leftovers that might have been spawned by transient
      // actions (rebuildCards, hover tooltips) but missed the elements[] push.
      ['merchant_overlay', 'merchant_popup_el'].forEach((nm) => {
        this.children.list.filter((c: any) => c.name === nm).forEach((c) => c.destroy());
      });
      this.merchantShopActive = false;
    };
    overlay.on('pointerdown', (_ptr: Phaser.Input.Pointer) => forceCloseMerchant());
    elements.push(overlay);

    // Left-side panel — width reduced, height computed from actual content
    const panelW = 240;
    const panelX = 10 + panelW / 2; // left-anchored, centre at x=130

    const preEqItems = merchant.equipmentItems || [];
    const preMysteryItems = merchant.mysteryItems ?? [];
    const hasEqSection = preEqItems.length > 0;
    const hasMysterySection = preMysteryItems.length > 0;
    // Compute panel height from content sections (top-down cursor)
    const VPAN = 12; // vertical padding inside panel top/bottom
    const headerH = 28 + 15 + 13; // title block (incl. hairline clearance) + res + countdown
    const consSectionH = 12 + 58; // gap before cards + cards
    const dividerAreaH = hasEqSection ? 14 : 0;
    const eqSectionH = hasEqSection ? 58 + 10 : 0;
    const mysteryDividerH = hasMysterySection ? 14 : 0;
    const mysterySectionH = hasMysterySection ? 4 + 14 + 8 + 58 + 10 : 0; // gap + label + gap + cards + bottom padding
    const panelH = VPAN + headerH + consSectionH + dividerAreaH + eqSectionH + mysteryDividerH + mysterySectionH + VPAN;

    // Fix panel top ~same as before so it doesn't jump around
    const panelTop = Math.round(height / 2 - 150);
    const panelCY = panelTop + panelH / 2;

    // Build absolute Y positions via cursor
    let yc = panelTop + VPAN;
    const TITLE_Y    = yc + 10; yc += 28;  // advance past header bar + gold hairline (was 20 — caused overlap)
    const RES_Y      = yc + 5;  yc += 15;
    const COUNTDOWN_Y = yc + 4; yc += 13;
    yc += 12; // gap before cons cards
    const CONS_CY    = yc + 29; yc += 58 + 10; // cons card centre + advance past cards+gap
    const DIVIDER_Y  = hasEqSection ? yc + 2 : -1; yc += dividerAreaH;
    const EQ_CY      = hasEqSection ? yc + 29 : -1; yc += eqSectionH;
    const MYSTERY_DIVIDER_Y = hasMysterySection ? yc + 2 : -1; yc += mysteryDividerH;
    yc += 4; // small gap before mystery label
    const MYSTERY_LABEL_Y = hasMysterySection ? yc : -1; yc += 14;
    yc += 8; // gap between label and cards
    const MYSTERY_CY = hasMysterySection ? yc + 29 : -1; yc += 58 + 10;

    // Soft shadow behind panel
    elements.push(this.add.rectangle(panelX + 3, panelCY + 3, panelW + 4, panelH + 4, 0x000000, 0.3)
      .setDepth(69).setName('merchant_popup_el'));
    // Main panel — darker & subtly translucent, single thin gold border
    const panelBg = this.add.rectangle(panelX, panelCY, panelW, panelH, 0x0a0604, 0.88)
      .setStrokeStyle(1, 0x8a6420).setDepth(70).setName('merchant_popup_el');
    elements.push(panelBg);
    // Header bar — subtle, integrated with panel
    elements.push(this.add.rectangle(panelX, panelTop + 20, panelW - 10, 26, 0x1a120a, 0.6)
      .setDepth(70));
    // Thin gold hairline under header (separates title from content)
    elements.push(this.add.rectangle(panelX, panelTop + 34, panelW - 24, 1, 0x8a6420, 0.55).setDepth(71));
    // Corner pips — smaller and more subtle
    for (const [cx, cy] of [[panelX - panelW / 2 + 6, panelTop + 6], [panelX + panelW / 2 - 6, panelTop + 6], [panelX - panelW / 2 + 6, panelTop + panelH - 6], [panelX + panelW / 2 - 6, panelTop + panelH - 6]]) {
      elements.push(this.add.circle(cx, cy, 2, 0xd4a84a, 0.55).setDepth(71));
    }

    // Title — "Whisper" the merchant (NPC name)
    elements.push(this.add.text(panelX - 8, TITLE_Y, '✦  WHISPER  ✦', {
      fontSize: '14px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif", color: '#f0c040', fontStyle: 'bold',
      stroke: '#2a1600', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(72));

    // (Close button is created further below next to the header row — single instance)

    // Resources (live-updated ref)
    const wavesUntilRefresh = this.gsm.getMerchantWavesUntilRefresh();
    const resText = this.add.text(panelX, RES_Y, '', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(71);
    elements.push(resText);

    // Wave countdown
    elements.push(this.add.text(panelX, COUNTDOWN_Y, `⏱ Refreshes in ${wavesUntilRefresh} wave${wavesUntilRefresh !== 1 ? 's' : ''}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(71));

    // ✕ close button — subtle gold runic style, no red box
    const closeXx = panelX + panelW / 2 - 14;
    const closeXy = panelTop + 18;
    const closeX = this.add.text(closeXx, closeXy, '✕', {
      fontSize: '13px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: '#8a6420', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(74).setInteractive({ useHandCursor: true });
    elements.push(closeX);
    const closeOver = () => { closeX.setColor('#f0c040').setScale(1.15); };
    const closeOut  = () => { closeX.setColor('#8a6420').setScale(1); };
    const doPopupClose = () => forceCloseMerchant();
    closeX.on('pointerover', closeOver).on('pointerout', closeOut).on('pointerdown', doPopupClose);

    // Icon card dimensions (smaller / condensed)
    const iconCardW = 46;
    const iconCardH = 58;
    const gapX = 8;

    // Rarity background fills — border stays uniform gold for all cards
    const rarityBgColors: Record<string, number> = {
      common:    0x131517,  // neutral dark
      uncommon:  0x0d1f0a,  // dark green tint
      rare:      0x0a1228,  // dark blue tint
      epic:      0x1a0a28,  // dark purple tint
      legendary: 0x281a05,  // dark amber tint
      unique:    0x280a0a,  // dark red tint
    };
    const CARD_BORDER_NORMAL = 0x8b6020;  // uniform gold border (matches game button style)
    const CARD_BORDER_DIM    = 0x2e2008;  // dim border for unaffordable / purchased

    const baseKeepCount = elements.length;

    const rebuildCards = () => {
      if (!panelOpen) return;
      while (elements.length > baseKeepCount) {
        elements.pop()?.destroy();
      }
      clearTooltip();
      resText.setText(`🪙 ${this.gsm.player.resources.gold}  ◆ ${this.gsm.player.resources.loot}`);

      // ── Consumable icon cards (token-priced) ──
      const allItems = merchant.items.filter((it) => it.consumable.id !== 'mimic_chest');
      const totalConsW = allItems.length * iconCardW + (allItems.length - 1) * gapX;
      const consStartX = panelX - totalConsW / 2 + iconCardW / 2;

      allItems.forEach((item, i) => {
        const cx = consStartX + i * (iconCardW + gapX);
        const cy = CONS_CY;
        const def = item.consumable;
        const isPurchased = item.purchased;
        if (isPurchased) return; // hide purchased items instantly
        const effectivePrice = item.priceOverride ?? def.price;
        const canAfford = !isPurchased && (def.currency === 'gold'
          ? this.gsm.player.resources.gold >= effectivePrice
          : this.gsm.player.resources.loot >= effectivePrice);

        // Background = rarity tint; border = uniform gold (dim if unavailable)
        const rarBg = rarityBgColors[def.rarity] ?? 0x131517;
        const cardFill = isPurchased ? 0x0a0806 : (canAfford ? rarBg : 0x0a0806);
        const borderColor = isPurchased ? CARD_BORDER_DIM : (canAfford ? CARD_BORDER_NORMAL : CARD_BORDER_DIM);
        const borderWidth = isPurchased ? 1 : (canAfford ? 2 : 1);

        const card = this.add.rectangle(cx, cy, iconCardW, iconCardH, cardFill, 0.55)
          .setStrokeStyle(borderWidth, borderColor, 0.8).setDepth(71)
          .setInteractive({ useHandCursor: true });
        elements.push(card);

        const iconAlpha = isPurchased ? 0.3 : (canAfford ? 1 : 0.4);
        if (def.spriteKey && this.textures.exists(def.spriteKey)) {
          elements.push(this.add.image(cx, cy - 12, def.spriteKey)
            .setDisplaySize(28, 28).setOrigin(0.5).setDepth(72).setAlpha(iconAlpha));
        } else {
          elements.push(this.add.text(cx, cy - 12, def.icon, { fontSize: '22px', padding: { x: 4, y: 4 } })
            .setOrigin(0.5).setDepth(72).setAlpha(iconAlpha));
        }

        if (isPurchased) {
          elements.push(this.add.text(cx, cy + 20, 'SOLD', {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#3a2800',
          }).setOrigin(0.5).setDepth(72));
        } else {
          const currIcon = def.currency === 'gold' ? '🪙' : '◆';
          elements.push(this.add.text(cx, cy + 15, `${effectivePrice}${currIcon}`, {
            fontSize: '9px', fontFamily: 'Georgia, serif',
            color: canAfford ? '#c8973a' : '#4a3010',
            padding: { x: 2, y: 2 },
          }).setOrigin(0.5).setDepth(72));

          card.on('pointerover', () => {
            card.setFillStyle(canAfford ? 0x1f1508 : 0x120d06, 1);
            clearTooltip();
            const lines = [
              `${def.name}`,
              def.description,
              `Cost: ${effectivePrice} ${def.currency === 'gold' ? 'Gold' : 'Runes'}`,
              ...(!canAfford ? ['(insufficient funds)'] : []),
            ];
            // Clamp X so tooltip stays on screen (tooltip ~190px wide, origin 0.5)
            const ttx = Math.max(cx, 100);
            activeTooltip = this.add.text(ttx, cy - iconCardH / 2 - 3, lines.join('\n'), {
              fontSize: '11px', fontFamily: 'Georgia, serif', color: canAfford ? '#c8973a' : '#6b4020',
              backgroundColor: '#0d0a0e', padding: { x: 8, y: 5 }, lineSpacing: 4,
              wordWrap: { width: 190 }, align: 'center',
            }).setOrigin(0.5, 1).setDepth(80);
          });
          card.on('pointerout', () => { card.setFillStyle(cardFill, 0.97); clearTooltip(); });
          card.on('pointerdown', () => {
            if (!canAfford) return;
            // Check potion bar capacity before purchase (non-mimic items go to bar)
            if (def.id !== 'mimic_chest') {
              const bar = this.gsm.player.potionBar ?? [];
              const hasExisting = bar.some((s) => s.id === def.id && s.count > 0);
              if (!hasExisting && bar.filter((s) => s.count > 0).length >= this.gsm.getPotionBarMaxSlots()) {
                this.showAnnouncement('Potion Bar Full! Use a potion first.');
                return;
              }
            }
            // Resolve index by reference into the live array — `i` is the
            // index in the FILTERED `allItems`, not in `merchant.items` that
            // `purchaseMerchantItem` indexes into. Without this lookup any
            // filtered-out entry (e.g. mimic_chest leftovers from old saves)
            // would shift indices and the player would buy the wrong potion.
            const realIdx = this.gsm.player.merchant.items.indexOf(item);
            if (realIdx < 0 || !this.gsm.purchaseMerchantItem(realIdx)) return;
            card.setVisible(false); // hide immediately, don't wait for rebuildCards frame
            this.gsm.save(true); // immediate: prevent F5 refund exploit
            this.showAnnouncement(`Bought ${def.name}!`);
            if (def.id === 'mimic_chest') {
              clearTooltip();
              for (const el of elements) el.destroy();
              this.merchantShopActive = false;
              this.gsm.player.activeConsumables = this.gsm.player.activeConsumables.filter((c) => c.id !== 'mimic_chest');
              this.time.delayedCall(200, () => this.showGoblinoShop());
              return;
            }
            this.refreshPotionBar();
            rebuildCards();
          });
        }
      });

      // Thin divider between consumables and equipment (only if something follows)
      if (DIVIDER_Y > 0) {
        elements.push(this.add.rectangle(panelX, DIVIDER_Y, panelW - 24, 1, 0x5a3600, 0.6).setDepth(71));
      }

      // ── Equipment icon cards ──
      const eqItems = merchant.equipmentItems || [];
      if (eqItems.length > 0 && EQ_CY > 0) {
        const totalEqW = eqItems.length * iconCardW + (eqItems.length - 1) * gapX;
        const eqStartX = panelX - totalEqW / 2 + iconCardW / 2;

        eqItems.forEach((eqItem, idx) => {
          const cx = eqStartX + idx * (iconCardW + gapX);
          const cy = EQ_CY;
          const eq = eqItem.equipment;
          const isPurchased = eqItem.purchased;
          if (isPurchased) return; // hide purchased items instantly
          // Merchant's Coin sigil: 15% markup on all merchant prices (paid via getMerchantEquipmentCost).
          const displayedPrice = this.gsm.getMerchantEquipmentCost(idx);
          const canAfford = !isPurchased && this.gsm.player.resources.gold >= displayedPrice;
          const eqRarBg = rarityBgColors[eq.rarity] ?? 0x131517;
          const eqCardFill = isPurchased ? 0x0a0806 : eqRarBg;
          const eqBorderCol = isPurchased ? CARD_BORDER_DIM : (canAfford ? CARD_BORDER_NORMAL : CARD_BORDER_DIM);

          const card = this.add.rectangle(cx, cy, iconCardW, iconCardH, eqCardFill, 0.55)
            .setStrokeStyle(canAfford ? 2 : 1, eqBorderCol, 0.8).setDepth(71)
            .setInteractive({ useHandCursor: true });
          elements.push(card);

          const spriteAlpha = isPurchased ? 0.3 : (canAfford ? 1 : 0.4);
          const hasSprite = eq.spriteKey && this.textures.exists(eq.spriteKey);
          if (hasSprite) {
            elements.push(this.add.image(cx, cy - 12, eq.spriteKey!)
              .setDisplaySize(28, 28).setDepth(72).setAlpha(spriteAlpha));
          } else {
            elements.push(this.add.text(cx, cy - 12, (eq.slot || '?').toUpperCase().slice(0, 3), {
              fontSize: '10px', fontFamily: 'Georgia, serif',
              color: isPurchased ? '#3a2800' : (canAfford ? '#c8973a' : '#4a3010'),
            }).setOrigin(0.5).setDepth(72));
          }

          if (isPurchased) {
            elements.push(this.add.text(cx, cy + 20, 'SOLD', {
              fontSize: '8px', fontFamily: 'Georgia, serif', color: '#3a2800',
            }).setOrigin(0.5).setDepth(72));
          } else {
            const priceLabel = displayedPrice >= 10000
              ? `${Math.floor(displayedPrice / 1000)}k🪙`
              : `${displayedPrice}🪙`;
            elements.push(this.add.text(cx, cy + 15, priceLabel, {
              fontSize: '9px', fontFamily: 'Georgia, serif',
              color: canAfford ? '#c8973a' : '#4a3010',
            }).setOrigin(0.5).setDepth(72));

            card.on('pointerover', () => {
              card.setFillStyle(canAfford ? 0x1f1508 : 0x120d06, 1);
              clearTooltip();
              // Shared createItemTooltip — one stat per line, rarity-coloured
              // name, divider lines, neutral bronze stats, teal class-equip
              // accent at the bottom. Matches HeroScene + InventoryScene.
              const statLines = formatEquipmentTooltipLines(eq);
              const isRelicEq = (eq.slot || '').startsWith('relic');
              const eqEffectLine = isRelicEq
                ? (getRelicScaledEffect(eq.name, eq.level, eq.maxStacksRoll) || eq.specialEffect || '')
                : (eq.specialEffect || '');
              const allStatLines = eqEffectLine ? [...statLines, `★ ${eqEffectLine}`] : statLines;
              const archLabel = (eq.armorArchetype || eq.armorSubtype)
                ? equipArmorLabel(eq)
                : (eq.weaponType ? equipWeaponLabel(eq) : '');
              const classLine = isRelicEq
                ? { text: 'Relic — fits any class', color: '#a07840' }
                : buildEquipStatusAccent(eq, this.gsm.player.heroes);
              const levelLabel = eq.rarity === 'unique' ? '' : ` · Lv${eq.level}`;
              const bottom: string[] = [
                `Cost: ${displayedPrice} Gold`,
                ...(!canAfford ? ['(insufficient gold)'] : []),
              ];
              const eqTtx = Math.max(cx, 125);
              activeTooltip = createItemTooltip(this, eqTtx, cy - iconCardH / 2 - 3, {
                name: `${eq.name}${levelLabel}`,
                nameColor: RARITY_COLORS[eq.rarity] ?? '#c8973a',
                statLines: allStatLines,
                bottomLines: bottom,
                classLine,
                typeLabel: archLabel || undefined,
                fontSize: '11px', wrap: 220, depth: 80,
              }, 0.5, 1) as unknown as Phaser.GameObjects.Text;
            });
            card.on('pointerout', () => { card.setFillStyle(eqCardFill, 0.97); clearTooltip(); });
            card.on('pointerdown', () => {
              if (!canAfford) return;
              // Resolve by reference at click time so a wave-tick refresh
              // (which rebuilds equipmentItems) between render and click
              // can't slip a freshly-rolled item into the slot the player
              // thought they were buying.
              const realIdx = this.gsm.player.merchant.equipmentItems?.indexOf(eqItem) ?? -1;
              if (realIdx < 0 || !this.gsm.purchaseMerchantEquipment(realIdx)) return;
              card.setVisible(false); // hide immediately, don't wait for rebuildCards frame
              if (merchant.lockedEquipmentSlots) {
                merchant.lockedEquipmentSlots = merchant.lockedEquipmentSlots.filter((s) => s !== realIdx);
              }
              // Auto-equip into a free slot if one's available — autoEquipInventory
              // already respects 2H weapon constraints (skips weapon2 when weapon1
              // holds a 2H), per-class weapon/armor restrictions, and locked relic
              // slots. If no free slot fits, the item stays in the inventory.
              const autoEquipped = this.gsm.autoEquipInventory();
              const myEquipped = autoEquipped.find((e) => e.item.id === eq.id);
              this.gsm.save(true); // immediate: prevent F5 refund exploit
              this.showAnnouncement(myEquipped ? `Bought & equipped ${eq.name}!` : `Bought ${eq.name}!`);
              rebuildCards();
            });

            // Lock button — only shown if player has at least 1 vendor lock from for_mastery passive
            const vendorLocks = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).vendorLocks;
            const lockedSlots = merchant.lockedEquipmentSlots ?? [];
            const isLocked = lockedSlots.includes(idx);
            const usedLocks = lockedSlots.length;
            const canLock = isLocked || usedLocks < vendorLocks;
            if (vendorLocks > 0) {
              // Rune-style lock glyph: filled gold shield when locked, outline when unlocked
              const lockIcon = isLocked ? '⚿' : '⚷';
              const lockBtn = this.add.text(cx + iconCardW / 2 - 2, cy - iconCardH / 2 + 2, lockIcon, {
                fontSize: '13px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
                color: isLocked ? '#f0c040' : '#8a6420', fontStyle: 'bold',
                stroke: '#1a0f00', strokeThickness: 2,
              }).setResolution(2).setOrigin(1, 0).setDepth(74).setAlpha(canLock ? 1 : 0.4)
                .setInteractive({ useHandCursor: canLock });
              elements.push(lockBtn);
              if (canLock) {
                lockBtn.on('pointerdown', () => {
                  if (!merchant.lockedEquipmentSlots) merchant.lockedEquipmentSlots = [];
                  if (isLocked) {
                    merchant.lockedEquipmentSlots = merchant.lockedEquipmentSlots.filter((s) => s !== idx);
                  } else {
                    merchant.lockedEquipmentSlots.push(idx);
                  }
                  this.gsm.save(true); // immediate: prevent F5 refund exploit
                  rebuildCards();
                });
              }
            }
          }
        });
      }

      // ── Mystery Items section ──
      const mysteryItems = merchant.mysteryItems ?? [];
      if (mysteryItems.length > 0 && MYSTERY_CY > 0) {
        // Divider
        if (MYSTERY_DIVIDER_Y > 0) {
          elements.push(this.add.rectangle(panelX, MYSTERY_DIVIDER_Y, panelW - 24, 1, 0x7a5c2a, 0.5).setDepth(71));
        }
        const mysteryBasePrice = this.gsm.getMysteryItemPrice();
        const getMysteryItemPrice = (itemType: string) => itemType === 'relic' ? mysteryBasePrice * 3 : mysteryBasePrice;
        const unpurchasedItems = mysteryItems.filter(m => !m.purchased);

        // Section label — shows cooldown inline when all slots are purchased
        if (MYSTERY_LABEL_Y > 0) {
          if (unpurchasedItems.length === 0) {
            // Read the monotonic counter — stays no longer stall the clock.
            const wavesSince = this.gsm.player.merchant.wavesSinceMysteryRefresh ?? 0;
            const wavesLeft = Math.max(0, 5 - wavesSince);
            const label = wavesLeft > 0 ? `✦ MYSTERY — ${wavesLeft}w ✦` : '✦ MYSTERY — soon ✦';
            elements.push(this.add.text(panelX, MYSTERY_LABEL_Y, label, {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a5c2a',
            }).setOrigin(0.5).setDepth(71));
          } else {
            elements.push(this.add.text(panelX, MYSTERY_LABEL_Y, '✦ MYSTERY ✦', {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a5c2a',
            }).setOrigin(0.5).setDepth(71));
          }
        }

        const totalMysW = unpurchasedItems.length * iconCardW + (unpurchasedItems.length - 1) * gapX;
        const mysStartX = panelX - totalMysW / 2 + iconCardW / 2;

        const MYSTERY_ICON_KEYS: Record<string, string> = {
          weapon: 'icon_sword_c',
          armor: 'icon_armor_mystery',
          relic: 'icon_mystery_relic',
        };
        const MYSTERY_TYPE_LABELS: Record<string, string> = {
          weapon: 'WEAPON',
          armor: 'ARMOR',
          relic: 'RELIC',
        };

        unpurchasedItems.forEach((mItem, midx) => {
          const originalIdx = mysteryItems.indexOf(mItem);
          const cx = mysStartX + midx * (iconCardW + gapX);
          const cy = MYSTERY_CY;
          const itemPrice = getMysteryItemPrice(mItem.itemType);
          const canAfford = this.gsm.player.resources.gold >= itemPrice;

          const mystCardFill = canAfford ? 0x1a1408 : 0x0d0b08;
          const mystBorderCol = canAfford ? 0x8b6c30 : CARD_BORDER_DIM;

          const card = this.add.rectangle(cx, cy, iconCardW, iconCardH, mystCardFill, 0.55)
            .setStrokeStyle(canAfford ? 2 : 1, mystBorderCol, 0.8).setDepth(71)
            .setInteractive({ useHandCursor: true });
          elements.push(card);

          const iconKey = MYSTERY_ICON_KEYS[mItem.itemType] ?? 'icon_mystery_relic';
          if (this.textures.exists(iconKey)) {
            elements.push(this.add.image(cx, cy - 14, iconKey)
              .setDisplaySize(24, 24).setOrigin(0.5).setDepth(72).setAlpha(canAfford ? 0.7 : 0.3));
          }
          // Type label
          elements.push(this.add.text(cx, cy + 5, MYSTERY_TYPE_LABELS[mItem.itemType] ?? '?', {
            fontSize: '7px', fontFamily: 'Georgia, serif', color: canAfford ? '#a07840' : '#4a3010',
          }).setOrigin(0.5).setDepth(72));

          {
            const priceLabel = itemPrice >= 10000 ? `${Math.floor(itemPrice / 1000)}k🪙` : `${itemPrice}🪙`;
            elements.push(this.add.text(cx, cy + 18, priceLabel, {
              fontSize: '8px', fontFamily: 'Georgia, serif',
              color: canAfford ? '#c8973a' : '#4a3010',
            }).setOrigin(0.5).setDepth(72));

            card.on('pointerover', () => {
              card.setFillStyle(canAfford ? 0x221a0c : 0x140f08, 1);
              clearTooltip();
              const typeStr = MYSTERY_TYPE_LABELS[mItem.itemType] ?? '?';
              const MYSTERY_FLAVORS: Record<string, string> = {
                weapon: `"A blade wrapped in cloth.\nSmells of old blood and fortune.\nWhat lies beneath is anyone's guess."`,
                armor: `"Bundled in burlap, this armor\nhas seen better days — or perhaps\nits best days are yet to come."`,
                relic: `"A relic of unknown origin.\nThe merchant won't say where it's from.\nSome things are better left unasked."`,
              };
              const flavor = MYSTERY_FLAVORS[mItem.itemType] ?? `"Unknown contents."`;
              const lines = [
                `Mystery ${typeStr}`,
                flavor,
                `Cost: ${itemPrice} Gold`,
                ...(!canAfford ? ['(insufficient gold)'] : []),
              ];
              const ttx = Math.max(cx, 110);
              activeTooltip = this.add.text(ttx, cy - iconCardH / 2 - 3, lines.join('\n'), {
                fontSize: '10px', fontFamily: 'Georgia, serif', color: canAfford ? '#a07840' : '#6b4020',
                backgroundColor: '#0d0a0e', padding: { x: 8, y: 5 }, lineSpacing: 3,
                wordWrap: { width: 200 }, align: 'center',
              }).setOrigin(0.5, 1).setDepth(80);
            });
            card.on('pointerout', () => { card.setFillStyle(mystCardFill, 0.97); clearTooltip(); });
            card.on('pointerdown', () => {
              if (!canAfford || (this as any)._mysteryBuying) return;
              (this as any)._mysteryBuying = true;
              clearTooltip();
              // Re-resolve by reference at click time — the mysteryItems
              // array can be swapped out by `generateMysteryItems` on a
              // wave-tick refresh between render and click, leaving the
              // captured `originalIdx` pointing at the wrong type. Without
              // this lookup a click on a WEAPON card could index into a
              // freshly-rolled RELIC slot.
              const liveArr = this.gsm.player.merchant.mysteryItems ?? [];
              const liveIdx = liveArr.indexOf(mItem);
              if (liveIdx < 0) { (this as any)._mysteryBuying = false; rebuildCards(); return; }
              const revealedItem = this.gsm.purchaseMysteryItem(liveIdx);
              if (!revealedItem) { (this as any)._mysteryBuying = false; return; }
              this.gsm.save(true); // immediate: prevent F5 refund exploit
              // Rebuild panel immediately so the purchased slot disappears right away
              if (panelOpen) rebuildCards();
              // Show loot reveal popup; on close just clear the buying flag
              this.showMysteryRevealPopup(revealedItem, cx, cy, elements, () => {
                (this as any)._mysteryBuying = false;
              });
            });
          }
        });
      }
    };

    rebuildCards();
  }

  /** Show a loot-style popup revealing the mystery item that was just purchased. Auto-closes after 2.5s. */
  private showMysteryRevealPopup(item: import('@roe/shared').Equipment, anchorX: number, anchorY: number, parentElements: Phaser.GameObjects.GameObject[], onClose: () => void): void {
    const RARITY_COLORS: Record<string, string> = {
      common: '#9ca3af', uncommon: '#22c55e', rare: '#3b82f6',
      epic: '#a855f7', legendary: '#f59e0b', unique: '#ef4444',
    };
    const color = RARITY_COLORS[item.rarity] ?? '#c8973a';
    const els: Phaser.GameObjects.GameObject[] = [];

    const popW = 180;
    const popH = 82;
    const popX = Math.min(Math.max(anchorX, popW / 2 + 10), 960 - popW / 2 - 10);
    const popY = anchorY - 100;

    const bg = this.add.rectangle(popX, popY, popW, popH, 0x0a0608, 0.97)
      .setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(color).color).setDepth(90);
    els.push(bg);

    // Rarity label — top inside bg
    const rarLabel = item.rarity.toUpperCase();
    els.push(this.add.text(popX, popY - 26, `✦ ${rarLabel} ✦`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color,
    }).setOrigin(0.5).setDepth(91));

    // Icon on the left
    if (item.spriteKey && this.textures.exists(item.spriteKey)) {
      els.push(this.add.image(popX - 64, popY + 4, item.spriteKey)
        .setDisplaySize(34, 34).setOrigin(0.5).setDepth(91));
    }

    // Text on the right: name, slot, added
    const slotLabel = item.slot.startsWith('relic') ? 'Relic' : item.slot === 'armor' ? 'Armor' : 'Weapon';
    els.push(this.add.text(popX - 40, popY - 14, item.name, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
      wordWrap: { width: 112 },
    }).setOrigin(0, 0).setDepth(91));

    els.push(this.add.text(popX - 40, popY + 10, slotLabel, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0, 0).setDepth(91));

    els.push(this.add.text(popX - 40, popY + 24, 'Added to inventory!', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b8050',
    }).setOrigin(0, 0).setDepth(91));

    // Register with parent so closing the merchant destroys the popup too
    for (const e of els) parentElements.push(e);

    // Guard: prevent cleanup from running twice (early click + timer both firing)
    let popupClosed = false;
    const cleanup = () => {
      if (popupClosed) return;
      popupClosed = true;
      // Remove popup els from parentElements before destroying to prevent double-destroy in rebuildCards
      for (let i = parentElements.length - 1; i >= 0; i--) {
        if ((els as Phaser.GameObjects.GameObject[]).includes(parentElements[i])) parentElements.splice(i, 1);
      }
      for (const e of els) { if (e.active) e.destroy(); }
      onClose();
    };

    // Fade out quickly so the buy lockout releases ASAP — click on the popup still dismisses instantly.
    const timer = this.time.delayedCall(500, () => {
      this.tweens.add({
        targets: els.filter(e => e.active), alpha: 0, duration: 200,
        onComplete: cleanup,
      });
    });

    // Click to dismiss early — cancel timer so it doesn't fire again later
    bg.setInteractive().on('pointerdown', () => {
      timer.remove(false);
      this.tweens.killTweensOf(els);
      cleanup();
    });
  }

  // ==================== HERO DEATH / RESPAWN ====================

  private handleHeroDeath(hs: HeroSprite): void {
    // Guard: don't re-process if already permanently dead (avoids duplicate revive buttons)
    if (hs.heroRef.permanentlyDead) return;

    // Hero Armory — Lumiel: Divine Intervention saves an ally from death once
    // per encounter. Cooldown (180 s) enforced via wall-clock so repeated deaths
    // on the same hero don't chain-save. Heals 30 % max HP over 2 s after save.
    if (!hs.heroRef.isDead) {
      const saver = this.heroSprites.find((o) =>
        o !== hs && !o.heroRef.isDead
        && this.hasArmoryPassive(o.heroRef, 'passive_lumiel_divine_intervention')
        && !(o.heroRef as any)._divineInterventionUsed,
      );
      if (saver) {
        const sRef = saver.heroRef as any;
        const cdExpiry = sRef._divineInterventionCdExpiry ?? 0;
        if (Date.now() >= cdExpiry) {
          sRef._divineInterventionUsed = true;
          sRef._divineInterventionCdExpiry = Date.now() + 180_000;
          hs.heroRef.stats.hp = 1;
          const maxHp = hs.heroRef.stats.maxHp ?? 1;
          const healTotal = Math.floor(maxHp * 0.30);
          const ticks = 10;
          const perTick = Math.max(1, Math.floor(healTotal / ticks));
          for (let i = 0; i < ticks; i++) {
            this.time.delayedCall(200 * (i + 1), () => {
              if (!hs.active || hs.heroRef.isDead) return;
              const curMax = hs.heroRef.stats.maxHp ?? 1;
              hs.heroRef.stats.hp = Math.min(curMax, hs.heroRef.stats.hp + perTick);
            });
          }
          this.showDamageNumber(hs.x, hs.y - 50, 'ᛉ DIVINE INTERVENTION', '#ffe060', '13px');
          return; // abort the death flow entirely
        }
      }
    }

    // ── v2 talents: Last Breath + Revenant — both trigger before the death flow.
    if (!hs.heroRef.isDead) {
      const pbDeath = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
      const hRefAny = hs.heroRef as any;
      // Revenant — first death per run, resurrect at full HP.
      if (pbDeath.revenantFlag && !hRefAny._revenantUsed) {
        hRefAny._revenantUsed = true;
        hs.heroRef.stats.hp = hs.heroRef.stats.maxHp ?? 1;
        this.showDamageNumber(hs.x, hs.y - 50, 'ᛞ REVENANT', '#a060ff', '13px');
        return;
      }
      // Last Breath — once per encounter, snap to 30% maxHp instead of dying.
      if (pbDeath.lastBreathFlag && !hRefAny._lastBreathUsed) {
        hRefAny._lastBreathUsed = true;
        const maxHp = hs.heroRef.stats.maxHp ?? 1;
        hs.heroRef.stats.hp = Math.max(1, Math.floor(maxHp * 0.30));
        this.showDamageNumber(hs.x, hs.y - 50, '✚ LAST BREATH', '#fbbf24', '13px');
        return;
      }
    }

    // Morveth Death Pact — if the dying hero is a necromancer with the
    // Death Pact passive, ≥ 10 souls, and hasn't burned the charge yet,
    // consume all souls and revive with (souls × 5 % max HP). Once per run.
    if (!hs.heroRef.isDead
      && (hs.heroRef.heroKey ?? hs.heroRef.heroClass) === 'necromancer'
      && this.hasArmoryPassive(hs.heroRef, 'passive_morveth_death_pact')
      && !((hs.heroRef as any)._deathPactUsed)) {
      const souls = (hs.heroRef as any)._soulCount ?? 0;
      if (souls >= 10) {
        (hs.heroRef as any)._deathPactUsed = true;
        (hs.heroRef as any)._soulCount = 0;
        const maxHp = hs.heroRef.stats.maxHp ?? 1;
        const reviveHp = Math.max(1, Math.floor(maxHp * 0.05 * souls));
        hs.heroRef.stats.hp = Math.min(maxHp, reviveHp);
        this.showDamageNumber(hs.x, hs.y - 50, `ᛞ DEATH PACT · ${souls} souls`, '#a855f7', '13px');
        return; // abort the death flow
      }
    }

    // Close spell priority popup if it was open for this hero
    if (this.spellPriorityPopups.has(hs)) this.closeSpellPriorityPopup(hs);
    // Cancel swap selection if this hero was selected for a swap
    if (this.swapSelectedSprite === hs) this.clearSwapSelection();
    // Cancel any in-progress charge so bars and position snap back cleanly.
    // resetChargeToBase only handles Goomar/Lootfi auto-charge state; clear the
    // Valka Shield Bash transients explicitly so the flag never sticks across
    // a death (was leaving Valka frozen in idle even after the revive helper
    // ran, since the cancelled tween's onComplete never set the flag false).
    if (hs.heroRef.heroClass === 'warrior') this.resetChargeToBase(hs);
    (hs.heroRef as any)._shieldBashCharging = false;
    {
      const stayCall = (hs.heroRef as any)._sbStayCall as Phaser.Time.TimerEvent | undefined;
      if (stayCall && typeof stayCall.remove === 'function') stayCall.remove();
      (hs.heroRef as any)._sbStayCall = null;
    }
    hs.heroRef.isDead = true;
    // Reset stackable relic progress on death — zero runtimeStacks on every equipped copy.
    for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
      const it = hs.heroRef.equipment[slot];
      if (!it) continue;
      if (it.name === 'Crescendo Idol' || it.name === 'Bloodtally Knife' || it.name === 'Vitality Idol' || it.name === "Archon's Focus") {
        it.runtimeStacks = 0;
      }
    }
    // Immediately strip the persistent Vitality Idol maxHp bonus so the pool reverts pre-respawn.
    this.updateVitalityIdolHp(hs.heroRef);
    // Clear all active unique relic buff flags so they don't persist after death
    (hs.heroRef as any)._radiantSurge    = false;
    (hs.heroRef as any)._bloodlustActive = false;
    (hs.heroRef as any)._slayerActive    = false;
    (hs.heroRef as any)._manaFluxReady   = false;
    (hs.heroRef as any)._fifthAtkCount   = 0;
    // Cancel any in-progress Leap Strike so the flag can't get permanently stuck
    (hs as any)._leapStriking = false;
    // Cancel Magma Shield so the defense bonus doesn't persist through death/revive
    if ((hs.heroRef as any)._magmaShieldActive) {
      (hs.heroRef as any)._magmaShieldTimer?.remove(false);
      hs.heroRef.stats.defense = Math.round(hs.heroRef.stats.defense / 2);
      (hs.heroRef as any)._magmaShieldActive = false;
      (hs.heroRef as any)._magmaShieldTimer = null;
    }
    // Clear Eagle Eye crit flag so it doesn't persist across death/revive
    (hs.heroRef as any)._eagleEyeCrit = 0;
    // Clear visual buff states so auras/shield bar don't linger after death
    (hs.heroRef as any)._shieldHp            = 0;
    (hs.heroRef as any)._shieldSources       = new Set();
    (hs.heroRef as any)._blessActive         = false;
    (hs.heroRef as any)._hotActive_bless     = false;
    (hs.heroRef as any)._hotActive_regrowth  = false;
    (hs.heroRef as any)._berserkActive       = false;
    (hs.heroRef as any)._arcaneMightResistBuff = 0;
    (hs.heroRef as any)._resurrectAura       = false;
    hs.shieldGraphics.clear();
    // Destroy any lingering aura sprites immediately
    if (hs.auraSprites) {
      for (const spr of Object.values(hs.auraSprites)) spr?.destroy();
      hs.auraSprites = {};
    }
    // Destroy any lingering bone shield orbit
    { const orbit = hs.getData('boneShieldOrbit') as Phaser.GameObjects.Text[] | null;
      if (orbit) { for (const b of orbit) b?.destroy(); hs.setData('boneShieldOrbit', null); } }
    // Destroy petrify overlay if active
    hs.petrifyOverlay?.destroy();
    hs.petrifyOverlay = undefined;
    // Hide soul text while dead
    hs.soulText?.setVisible(false);
    (hs.heroRef as any)._petrified = false;
    (hs.heroRef as any)._petrifiedTimer = 0;
    (hs.heroRef as any)._frostRooted = false;
    (hs.heroRef as any)._frostRootedTimer = 0;
    // Clear any active taunt targeting this hero so enemies re-target freely
    for (const es of this.enemySprites) {
      if ((es.enemyRef as any)._tauntedTarget === hs) {
        (es.enemyRef as any)._tauntedTarget = undefined;
        (es.enemyRef as any)._tauntTimer    = 0;
      }
    }
    if (hs.heroRef.respawnUsesLeft > 0) {
      hs.heroRef.respawnTimer = getRespawnTime(hs.heroRef.level);
    } else if (this.triggerUndyingFury(hs)) {
      // Undying Fury passive — handled inside triggerUndyingFury (warrior only, but checked generically)
      return;
    } else if (this.gsm.hasActiveConsumable('potion_resurrect') && !this.getBoonEffects().cannotResurrect) {
      // Auto-revive from resurrection potion buff — blocked by Reaper's Pact.
      // Consumable stays on the player (not consumed) if the pact blocks the
      // revive, so they can still use it after the boon is replaced.
      this.gsm.removeActiveConsumable('potion_resurrect');
      hs.heroRef.isDead = false;
      hs.heroRef.permanentlyDead = false;
      const maxHp = this.effStats(hs.heroRef).maxHp;
      hs.heroRef.stats.hp = Math.floor(maxHp * 0.5);
      hs.heroRef.respawnTimer = 0;
      hs.reviveBtn?.destroy();
      hs.reviveBtn = undefined;
      this.destroyHeroGrave(hs);
      hs.setAlpha(1);
      hs.hpBar.setAlpha(1); hs.hpBarBg.setAlpha(1); hs.mpBar.setAlpha(1); hs.xpBar.setAlpha(1); hs.xpBarBg.setAlpha(1); // buffIconText alpha controlled by update loop
      // Snap back to slot + clear charge/jump/shield-bash flags so a hero who
      // died mid-action wakes up able to attack immediately.
      this.resetHeroToSlotOnRevive(hs);
      const idleKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_idle`;
      if (this.anims.exists(idleKey)) hs.play(idleKey);
      this.vfx.playResurrectPillar(hs.x, hs.y);
      this.showResurrectAura(hs);
      this.showDamageNumber(hs.x, hs.y - 30, 'AUTO-REVIVE!', '#5090c0');
      return;
    } else {
      hs.heroRef.respawnTimer = 0; // stays dead — no respawns left
      hs.heroRef.permanentlyDead = true;
      // Mastery — bump the per-run permanent-death counter so the
      // Untouchable / Iron Will challenges check correctly on next stage
      // advance. (Auto-revives don't count.)
      this.gsm.player.runDeaths = (this.gsm.player.runDeaths ?? 0) + 1;
      // Revive via the portrait frame on the left HUD
    }
    // Play death animation; updateHeroes will hide the sprite after it finishes
    const deathKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_death`;
    if (this.anims.exists(deathKey)) {
      hs.off('animationcomplete');
      hs.play(deathKey);
    } else {
      hs.setAlpha(0.3);
    }
    const deadMsg = hs.heroRef.respawnUsesLeft > 0 ? 'DEAD' : 'FALLEN';
    this.showDamageNumber(hs.x, hs.y - 40, deadMsg, '#ef4444');
    this.spawnHeroGrave(hs);

    const allDead = this.heroSprites.every((h) => h.heroRef.isDead);
    if (allDead && !this.gameOver) {
      if (this.gsm.player.autoRun) {
        // Route through handleDefeat so leaderboard, highScore, achievements are all recorded
        this.handleDefeat();
      } else {
        this.showAnnouncement('All heroes down! Base taking damage!');
      }
    }
  }

  private spawnHeroGrave(hs: HeroSprite): void {
    this.destroyHeroGrave(hs);
    const grave = this.add.image(hs.baseX, hs.baseY, 'obj_grave')
      .setDisplaySize(52, 52).setDepth(2).setAlpha(0);
    this.tweens.add({ targets: grave, alpha: 1, duration: 500, ease: 'Sine.easeOut' });
    (hs as any)._graveSprite = grave;
  }

  private destroyHeroGrave(hs: HeroSprite): void {
    const g = (hs as any)._graveSprite as Phaser.GameObjects.Image | undefined;
    if (g) { g.destroy(); (hs as any)._graveSprite = null; }
  }

  private updateRespawns(delta: number): void {
    if (this.heroSprites.length === 0) return; // No heroes spawned yet
    let allDead = true;
    for (const hs of this.heroSprites) {
      if (hs.heroRef.isDead) {
        // ── Gold revive countdown ──
        if ((hs.heroRef.goldReviveTimer ?? 0) > 0) {
          hs.heroRef.goldReviveTimer! -= delta;
          const secLeft = Math.max(0, hs.heroRef.goldReviveTimer!);
          // Create or update the floating timer text
          if (!hs.reviveTimerText) {
            hs.reviveTimerText = this.add.text(hs.x, hs.baseY - 60, '', {
              fontSize: '11px', fontFamily: 'Georgia, serif', color: '#fbbf24',
              stroke: '#3b1a00', strokeThickness: 3,
            }).setOrigin(0.5).setDepth(62);
          }
          hs.reviveTimerText.setText(`⟳ ${(secLeft / 1000).toFixed(1)}s`);
          hs.reviveTimerText.setPosition(hs.x, hs.baseY - 60);

          if (hs.heroRef.goldReviveTimer! <= 0) {
            hs.heroRef.goldReviveTimer = 0;
            hs.heroRef.isDead = false;
            // Gold revive starts only after the hero is permanentlyDead — the timer
            // doesn't clear that flag on its own. Without this, the hero comes back
            // alive but the dead-hero count (Reaper's Pact, swap UI, hotkeys) still
            // treats them as fallen, and they read as "stuck" because the swap and
            // some attack paths gate on permanentlyDead.
            hs.heroRef.permanentlyDead = false;
            hs.reviveTimerText.destroy();
            hs.reviveTimerText = undefined;
            this.destroyHeroGrave(hs);
            // Snap back to slot and clear all charge/jump/shield-bash flags so a hero
            // who died mid-action can attack again immediately on revive.
            this.resetHeroToSlotOnRevive(hs);
            const stats = this.effStats(hs.heroRef);
            hs.heroRef.stats.hp = Math.floor(stats.maxHp * 0.5);
            hs.heroRef.stats.mp = Math.floor(stats.maxMp * 0.3);
            hs.setAlpha(1);
            hs.hpBar.setAlpha(1); hs.hpBarBg.setAlpha(1); hs.mpBar.setAlpha(1); hs.xpBar.setAlpha(1); hs.xpBarBg.setAlpha(1);
            const reviveIdleKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_idle`;
            if (this.anims.exists(reviveIdleKey)) hs.play(reviveIdleKey);
            this.vfx.playResurrectPillar(hs.x, hs.y);
            this.showResurrectAura(hs);
            this.showDamageNumber(hs.x, hs.y - 40, 'REVIVED!', '#fbbf24');
          }
        } else if (hs.heroRef.respawnTimer > 0) {
          // ── Free respawn countdown ──
          hs.heroRef.respawnTimer -= delta;
          if (hs.heroRef.respawnTimer <= 0) {
            hs.heroRef.isDead = false;
            hs.heroRef.respawnTimer = 0;
            hs.heroRef.respawnUsesLeft = Math.max(0, hs.heroRef.respawnUsesLeft - 1);
            (hs.heroRef as any)._shieldHp = 0;
            (hs.heroRef as any)._shieldSources = new Set();
            hs.reviveBtn?.destroy();
            hs.reviveBtn = undefined;
            this.destroyHeroGrave(hs);
            // Snap back to slot and clear charge/jump/shield-bash transients +
            // cancel ghost stay-phase timers so the hero can attack immediately
            // (was only resetting warrior chargeState data, leaving Valka and
            // amazon mid-action flags stale).
            this.resetHeroToSlotOnRevive(hs);
            const stats = this.effStats(hs.heroRef);
            hs.heroRef.stats.hp = Math.floor(stats.maxHp * 0.5);
            hs.heroRef.stats.mp = Math.floor(stats.maxMp * 0.3);
            hs.setAlpha(1);
            hs.hpBar.setAlpha(1); hs.hpBarBg.setAlpha(1); hs.mpBar.setAlpha(1); hs.xpBar.setAlpha(1); hs.xpBarBg.setAlpha(1); // buffIconText alpha controlled by update loop
            const respawnIdleKey = `hero_${hs.heroRef.heroKey ?? hs.heroRef.heroClass}_idle`;
            if (this.anims.exists(respawnIdleKey)) hs.play(respawnIdleKey);
            this.showResurrectAura(hs);
            this.showDamageNumber(hs.x, hs.y - 40, 'RESPAWNED', '#22c55e');
          }
        }
        // If both timers === 0 and hero is still dead: no respawns left, stays dead
      }
      if (!hs.heroRef.isDead) allDead = false;
    }

    if (allDead && !this.gameOver) {
      // Defeat: all heroes permanently dead and no gold revival pending
      const allPermanentlyDead = this.heroSprites.every(
        (h) => h.heroRef.isDead && h.heroRef.respawnTimer <= 0 && h.heroRef.respawnUsesLeft <= 0
          && !(h.heroRef.goldReviveTimer && h.heroRef.goldReviveTimer > 0),
      );
      if (allPermanentlyDead) {
        this.handleDefeat();
      }
      // Heroes still respawning or gold-reviving — wait for them
    }
  }

  private handleDefeat(): void {
    if (this.gameOver) return;
    this.gameOver = true;

    if (this.infernoMode) {
      this.handleInfernoDefeat();
      return;
    }

    BattleScene.persistedRunScore = 0;
    BattleScene.persistedRunLootStart = 0;
    this.gsm.clearRunSession();
    localStorage.removeItem(this.pendingTomesKey());

    // Capture all display stats BEFORE resetting — gsm data will be wiped
    const finalScore   = this.runScore;
    const finalLevel   = this.gsm.player.currentLevel;
    const finalWave    = this.gsm.battle.waveNumber;
    const finalGold    = this.runGold;
    const finalKills   = this.gsm.player.totalKills;
    // Runes earned this run — directly from accumulated counter (not loot delta math)
    const finalTokens  = this.runLoot;
    BattleScene.persistedRunLoot = 0; // reset for next run
    BattleScene.elitePackPending = null; // clear any mid-pack state on death
    BattleScene.hordePending = null;     // same treatment for the horde event
    BattleScene.telurionPending = null;  // and for Telurion's Lair
    BattleScene.shadowrealmPending = null; // and the Shadowrealm endless run
    const heroName     = AuthService.getInstance().getUser()?.username || this.gsm.player.heroes[0]?.name || 'Hero';
    const isNewBest    = finalScore > this.gsm.player.highScore;

    this.gsm.trackDeath();
    this.gsm.trackRunScore(finalScore);
    this.gsm.checkAchievements();
    if (isNewBest) this.gsm.player.highScore = finalScore;

    postLeaderboardToServer(
      {
        mode: 'standard', name: heroName, score: finalScore,
        level: finalLevel, wave: finalWave,
        // Main-hero key for the Champions Board column (heroKey override
        // takes priority over heroClass so unique characters like Cyrith
        // / Morveth render under their own portrait, not the generic class).
        heroKey: this.gsm.player.heroes[0]?.heroKey ?? this.gsm.player.heroes[0]?.heroClass ?? null,
        // Books picked during the run — TomeId list ordered by level desc
        // (highest investment first) so the Champions Board can display the
        // top picks compactly without re-sorting client-side per render.
        tomeIds: this.gsm.player.tomes
          .filter((t) => t.level > 0)
          .sort((a, b) => b.level - a.level)
          .map((t) => t.id)
          .join(',') || null,
        date: new Date().toLocaleDateString(),
      },
      AuthService.getInstance().authHeaders(),
    );

    // If a rune checkpoint is active (deep takes priority), offer return instead of hard reset
    if (this.gsm.player.runeDeepCheckpoint || this.gsm.player.runeCheckpoint) {
      this.gsm.player.checkpointDeathPending = true; // F5 guard — detected on reload
      this.gsm.save(true); // persist high score + leaderboard + pending flag before pausing
      this.showCheckpointDeathOverlay(finalScore, finalLevel, finalWave, finalGold, finalKills, finalTokens, isNewBest);
      return;
    }

    // Reset run IMMEDIATELY — any navigation away (menu, close tab) already lands on a fresh run
    this.gsm.resetRun();
    this.gsm.save(true);

    this.showDeathOverlay(finalScore, finalLevel, finalWave, finalGold, finalKills, finalTokens, isNewBest);
  }

  private handleInfernoDefeat(): void {
    const finalScore  = this.infernoScore;
    const heroName    = AuthService.getInstance().getUser()?.username || this.gsm.player.heroes[0]?.name || 'Hero';
    const isNewBest   = finalScore > (this.gsm.player.infernoHighScore ?? 0);

    if (isNewBest) this.gsm.player.infernoHighScore = finalScore;
    this.gsm.clearRunSession();
    localStorage.removeItem(this.pendingTomesKey());

    postLeaderboardToServer(
      {
        mode: 'inferno', name: heroName, score: finalScore,
        infernoZone: this.infernoZone, wave: this.gsm.battle.waveNumber,
        heroKey: this.gsm.player.heroes[0]?.heroKey ?? this.gsm.player.heroes[0]?.heroClass ?? null,
        tomeIds: this.gsm.player.tomes
          .filter((t) => t.level > 0)
          .sort((a, b) => b.level - a.level)
          .map((t) => t.id)
          .join(',') || null,
        date: new Date().toLocaleDateString(),
      },
      AuthService.getInstance().authHeaders(),
    );

    if (this.gsm.player.infernoOnly) {
      // Permanent inferno death — reset heroes to level 1, save display data
      this.gsm.permanentInfernoDeath(this.infernoZone, this.gsm.battle.waveNumber);
      this.gsm.save(true);
      this.showPermanentInfernoDeathOverlay(finalScore, this.infernoZone, isNewBest);
    } else {
      // Standard inferno death — hero NOT wiped, gear carries over
      this.gsm.endInfernoRun();
      this.gsm.save(true);
      this.showInfernoDeathOverlay(finalScore, this.infernoZone, isNewBest);
    }
  }

  private showInfernoDeathOverlay(finalScore: number, infernoZone: number, isNewBest: boolean): void {
    const width = GAME_W, height = GAME_H;
    const cx = width / 2;
    const cy = height / 2;

    this.refreshUI();

    // Full-screen overlay
    this.add.rectangle(cx, cy, width, height, 0x050100, 0.90).setDepth(60).setInteractive();
    // Panel
    this.add.rectangle(cx, cy, 380, 300, 0x120608, 0.98).setStrokeStyle(2, 0xcc2200).setDepth(60);
    this.add.rectangle(cx, cy, 374, 294, 0x120608, 0).setStrokeStyle(1, 0xff4020, 0.3).setDepth(61);

    // Flame flanks — drawn triangles, no emoji
    const dfg = this.add.graphics().setDepth(62);
    for (const [ox, flip] of [[-155, 1], [155, -1]] as [number, number][]) {
      dfg.fillStyle(0x5a0e00, 0.9); dfg.fillTriangle(cx + ox, cy - 128, cx + ox - 8 * flip, cy - 112, cx + ox + 8 * flip, cy - 112);
      dfg.fillStyle(0x8b1800, 0.75); dfg.fillTriangle(cx + ox, cy - 125, cx + ox - 5 * flip, cy - 113, cx + ox + 5 * flip, cy - 113);
    }
    this.add.text(cx, cy - 118, 'INFERNO DEFEAT', {
      fontSize: '24px', fontFamily: 'Georgia, serif', color: '#9b1a00', fontStyle: 'bold',
      stroke: '#1a0000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy - 90, '— ✦ —', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8b2000',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy - 72, 'Your hero falls — but is not lost.', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a06030',
    }).setOrigin(0.5).setDepth(62);

    if (this.lastDeathSource) {
      this.add.text(cx, cy - 54, `Slain by: ${this.lastDeathSource}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#cc3010',
      }).setOrigin(0.5).setDepth(62);
    }

    const lines = [
      `Inferno Score: ${finalScore}${isNewBest ? '  🔥 NEW BEST!' : ''}`,
      `Deepest Zone: Inferno ${infernoZone}`,
    ];
    lines.forEach((line, i) => {
      const isScore = i === 0 && isNewBest;
      this.add.text(cx, cy - 30 + i * 22, line, {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: isScore ? '#ff8040' : '#9a6030',
      }).setOrigin(0.5).setDepth(62);
    });

    // RETRY button — restart inferno from zone 1
    const retryBg = this.add.rectangle(cx - 90, cy + 60, 140, 36, 0x2a0808, 0.98)
      .setStrokeStyle(2, 0xcc2200).setDepth(62).setInteractive({ useHandCursor: true });
    const retryTxt = this.add.text(cx - 90, cy + 60, '🔥  RETRY INFERNO', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#ff6030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(63).setInteractive({ useHandCursor: true });
    const onRetryOver = () => { retryBg.setFillStyle(0x3a1010); retryTxt.setColor('#ff9060'); };
    const onRetryOut  = () => { retryBg.setFillStyle(0x2a0808); retryTxt.setColor('#ff6030'); };
    retryBg.on('pointerover', onRetryOver).on('pointerout', onRetryOut);
    retryTxt.on('pointerover', onRetryOver).on('pointerout', onRetryOut);
    const doRetry = () => {
      this.cleanupBattle();
      // Revive hero fully before retry
      const hero = this.gsm.player.heroes[0];
      if (hero) {
        hero.isDead = false;
        hero.permanentlyDead = false;
        hero.respawnTimer = 0;
        hero.respawnUsesLeft = 0;
        const es = hero.stats;
        es.hp = es.maxHp;
        es.mp = es.maxMp;
      }
      this.gsm.save();
      this.scene.restart({ inferno: true, infernoZone: 1, infernoScore: 0 });
    };
    retryBg.on('pointerdown', doRetry);
    retryTxt.on('pointerdown', doRetry);

    // MENU button — return to main menu; hero is alive in base game
    const menuBg = this.add.rectangle(cx + 90, cy + 60, 120, 36, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(62).setInteractive({ useHandCursor: true });
    const menuTxt = this.add.text(cx + 90, cy + 60, 'RETURN TO REALM', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(63).setInteractive({ useHandCursor: true });
    const onMenuOver = () => { menuBg.setFillStyle(0x1a1208); menuTxt.setColor('#c8973a'); };
    const onMenuOut  = () => { menuBg.setFillStyle(0x0d0a08); menuTxt.setColor('#a07840'); };
    menuBg.on('pointerover', onMenuOver).on('pointerout', onMenuOut);
    menuTxt.on('pointerover', onMenuOver).on('pointerout', onMenuOut);
    const doMenu = () => {
      BattleScene.stopBattleMusic();
      this.scene.stop('BattleScene');
      this.scene.start('MenuScene', {});
    };
    menuBg.on('pointerdown', doMenu);
    menuTxt.on('pointerdown', doMenu);
  }

  private showPermanentInfernoDeathOverlay(finalScore: number, infernoZone: number, isNewBest: boolean): void {
    const width = GAME_W, height = GAME_H;
    const cx = width / 2; const cy = height / 2;

    this.refreshUI();

    // Darkened full-screen overlay
    this.add.rectangle(cx, cy, width, height, 0x020000, 0.93).setDepth(60).setInteractive();

    // Panel with heavy flame border
    this.add.rectangle(cx, cy, 400, 310, 0x0e0000, 0.99).setStrokeStyle(3, 0xcc2200).setDepth(61);
    this.add.rectangle(cx, cy, 394, 304, 0, 0).setStrokeStyle(1, 0xff6020, 0.4).setDepth(61);
    this.add.rectangle(cx, cy, 388, 298, 0, 0).setStrokeStyle(1, 0x8b1000, 0.2).setDepth(61);

    // Flame border — drawn flames at all four corners
    const fg = this.add.graphics().setDepth(62);
    const flames: [number, number, number][] = [
      [cx - 190, cy - 148, 1], [cx + 190, cy - 148, -1],
      [cx - 190, cy + 148, 1], [cx + 190, cy + 148, -1],
    ];
    for (const [fx, fy, flip] of flames) {
      fg.fillStyle(0x5a0e00, 0.9); fg.fillTriangle(fx, fy - 14*Math.sign(cy-fy), fx-7*flip, fy, fx+7*flip, fy);
      fg.fillStyle(0x8b1800, 0.8); fg.fillTriangle(fx, fy - 10*Math.sign(cy-fy), fx-4*flip, fy, fx+4*flip, fy);
      fg.fillStyle(0xcc2200, 0.55); fg.fillTriangle(fx, fy - 5*Math.sign(cy-fy), fx-2*flip, fy, fx+2*flip, fy);
    }
    // Flame row along top edge
    for (let i = -3; i <= 3; i++) {
      const fx2 = cx + i * 40; const fy2 = cy - 148;
      fg.fillStyle(0x5a0e00, 0.7); fg.fillTriangle(fx2, fy2 - 12, fx2 - 8, fy2 + 2, fx2 + 8, fy2 + 2);
      fg.fillStyle(0x8b1800, 0.6); fg.fillTriangle(fx2, fy2 - 8, fx2 - 5, fy2 + 1, fx2 + 5, fy2 + 1);
    }

    this.add.text(cx, cy - 126, 'INFERNAL FALL', {
      fontSize: '26px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
      stroke: '#1a0000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy - 96, '— ✦ YOUR SOUL IS CLAIMED ✦ —', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b1a00', letterSpacing: 1,
    }).setOrigin(0.5).setDepth(62);

    if (this.lastDeathSource) {
      this.add.text(cx, cy - 74, `Consumed by: ${this.lastDeathSource}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#9b2a00',
      }).setOrigin(0.5).setDepth(62);
    }

    const scoreColor = isNewBest ? '#ff6020' : '#7a3010';
    this.add.text(cx, cy - 50, `Inferno Score: ${finalScore}${isNewBest ? '  ✦ BEST!' : ''}`, {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: scoreColor, fontStyle: isNewBest ? 'bold' : 'normal',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy - 28, `Deepest Zone: Inferno ${infernoZone}  ·  Wave ${this.gsm.battle.waveNumber}`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#6b2a00',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy - 4, 'Your heroes are forever bound to the Inferno.', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#4a1800', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy + 14, 'They return — broken, diminished, level 1.', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#4a1800', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(62);

    // Single button — back to main menu
    const btnBg = this.add.rectangle(cx, cy + 64, 200, 36, 0x1a0000, 0.98)
      .setStrokeStyle(2, 0xcc2200).setDepth(62).setInteractive({ useHandCursor: true });
    const btnTxt = this.add.text(cx, cy + 64, '— Nice try, mortal. —', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#aa2000', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(63).setInteractive({ useHandCursor: true });
    const onOver = () => { btnBg.setFillStyle(0x2a0808); btnTxt.setColor('#ff4020'); };
    const onOut  = () => { btnBg.setFillStyle(0x1a0000); btnTxt.setColor('#aa2000'); };
    btnBg.on('pointerover', onOver).on('pointerout', onOut);
    btnTxt.on('pointerover', onOver).on('pointerout', onOut);
    const doMenu = () => { BattleScene.stopBattleMusic(); this.scene.stop('BattleScene'); this.scene.start('MenuScene', {}); };
    btnBg.on('pointerdown', doMenu);
    btnTxt.on('pointerdown', doMenu);
  }

  private showDeathOverlay(
    finalScore: number, finalLevel: number, finalWave: number,
    finalGold: number, finalKills: number, finalTokens: number,
    isNewBest: boolean,
  ): void {
    const width = GAME_W, height = GAME_H;

    this.refreshUI();

    // Full-screen overlay — blocks clicks on battle elements beneath
    this.add.rectangle(width / 2, height / 2, width, height, 0x050402, 0.88)
      .setDepth(60).setInteractive();
    // Panel
    this.add.rectangle(width / 2, height / 2, 380, 320, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(60);
    // Inner border highlight
    this.add.rectangle(width / 2, height / 2, 374, 314, 0x0d0a08, 0)
      .setStrokeStyle(1, 0xc8973a, 0.3).setDepth(61);

    this.add.text(width / 2, height / 2 - 128, '⚔  DEFEAT  ⚔', {
      fontSize: '28px', fontFamily: 'Georgia, serif', color: '#c8150f', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(62);

    // Play hero death sprite on the panel — uses the main hero if death animation exists
    const mainHero = this.gsm.player.heroes[0];
    if (mainHero) {
      const heroKey = `hero_${mainHero.heroKey ?? mainHero.heroClass}`;
      const deathAnimKey = `${heroKey}_death`;
      if (this.textures.exists(heroKey)) {
        const deathSprite = this.add.sprite(width / 2 - 130, height / 2 - 60, heroKey)
          .setDisplaySize(80, 80).setOrigin(0.5, 0.5).setDepth(62);
        if (this.anims.exists(deathAnimKey)) {
          deathSprite.play(deathAnimKey);
          // Freeze on last frame when animation completes
          deathSprite.on('animationcomplete', () => {
            const anim = this.anims.get(deathAnimKey);
            if (anim?.frames?.length) {
              deathSprite.setFrame(anim.frames[anim.frames.length - 1].frame.name);
            }
          });
        } else {
          // No death anim — show hurt/idle tinted dark as fallback
          deathSprite.setTint(0x5a1010);
          const hurtKey = `${heroKey}_hurt`;
          if (this.anims.exists(hurtKey)) deathSprite.play(hurtKey);
          else if (this.anims.exists(`${heroKey}_idle`)) deathSprite.play(`${heroKey}_idle`);
        }
      }
    }

    // Ornamental divider
    this.add.text(width / 2, height / 2 - 102, '— ✦ —', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(width / 2, height / 2 - 82, 'All heroes have fallen.', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(62);

    if (this.lastDeathSource) {
      this.add.text(width / 2, height / 2 - 64, `Slain by: ${this.lastDeathSource}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8150f',
      }).setOrigin(0.5).setDepth(62);
    }

    const lines = [
      `Score: ${finalScore}${isNewBest ? '  ✦ NEW BEST!' : ''}`,
      `Level Reached: ${finalLevel}`,
      `Waves Cleared: ${finalWave}`,
      `🪙 Gold Earned: ${finalGold}`,
      `Kills: ${finalKills}`,
      `◆ Runes Earned: ${finalTokens}`,
    ];
    lines.forEach((line, i) => {
      const isScore = i === 0 && isNewBest;
      this.add.text(width / 2, height / 2 - 44 + i * 20, line, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: isScore ? '#c8973a' : '#8a6030',
      }).setOrigin(0.5).setDepth(62);
    });

    this.add.text(width / 2, height / 2 + 80, 'Only ◆ Runes & high score persist.', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(62);

    // Run is already reset — both buttons just decide where to go next
    const tryAgainBtnBg = this.add.rectangle(width / 2 - 72, height / 2 + 112, 120, 34, 0x1a0608, 1)
      .setStrokeStyle(2, 0x8b2000).setDepth(62);
    const tryAgainBtn = this.add.text(width / 2 - 72, height / 2 + 112, 'TRY AGAIN', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(63);
    const menuBtnBg = this.add.rectangle(width / 2 + 72, height / 2 + 112, 100, 34, 0x0d0a08, 1)
      .setStrokeStyle(2, 0x5a3600).setDepth(62);
    const menuBtn = this.add.text(width / 2 + 72, height / 2 + 112, 'MENU', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(63);
    wireButton({
      bg: tryAgainBtnBg, text: tryAgainBtn,
      onHover: () => { tryAgainBtn.setColor('#ffe8a0'); tryAgainBtnBg.setStrokeStyle(2, 0xc84000); },
      onOut:   () => { tryAgainBtn.setColor('#e8a030'); tryAgainBtnBg.setStrokeStyle(2, 0x8b2000); },
      onDown:  () => {
        // Belt-and-suspenders: resetRun already ran before the overlay, but re-run it here so any
        // late HUD updates (gold coin counter etc.) aren't showing stale pre-defeat values.
        this.gsm.resetRun();
        this.gsm.save(true);
        this.cleanupBattle();
        BattleScene.stopBattleMusic();
        // Route through the sigil loadout modal so the player re-commits their
        // bring-sigils + insurance decision for the new run (collection picks
        // from the previous run were already spent on that run's outcome).
        this.scene.stop('BattleScene');
        this.scene.start('SigilLoadoutScene');
      },
    });
    wireButton({
      bg: menuBtnBg, text: menuBtn,
      onHover: () => { menuBtn.setColor('#c8973a'); menuBtnBg.setStrokeStyle(2, 0x9a6020); },
      onOut:   () => { menuBtn.setColor('#a07840'); menuBtnBg.setStrokeStyle(2, 0x5a3600); },
      onDown:  () => {
        this.gsm.save();
        BattleScene.stopBattleMusic();
        this.cleanupBattle();
        this.scene.stop('BattleScene');
        this.scene.start('MenuScene', {});
      },
    });
  }

  // ==================== AUTO-RUN SUMMARY ====================

  // ==================== LEVEL NAVIGATION ====================

  /** Close any open overlay scenes so battle popups can be clicked */
  private closeOverlayScenes(): void {
    // Never close menus during auto-run — popups resolve silently, so the player's open scene stays untouched
    if (this.gsm.player.autoRun) return;
    const overlays = ['HeroScene', 'InventoryScene', 'BaseScene', 'AchievementScene', 'ShopScene', 'GemShopScene', 'TomesScene'];
    for (const s of overlays) {
      if (this.scene.isActive(s) || this.scene.isSleeping(s)) this.scene.stop(s);
    }
  }

  /** Shared event-summary popup — used by Horde of Goblins + Elite Pack + future
   *  side-event clears. Compact panel with corner-bracket ornaments, a stat
   *  list, and a horizontal reward card (icon on left, name + subtitle on
   *  right). Player clicks "Continue" to dismiss. */
  private showEventSummary(opts: {
    title: string;
    accentColor: number;
    stats: { label: string; value: string; color?: string }[];
    rewards: { iconKey: string; name: string; rarity?: 'epic' | 'legendary' | 'unique' | string; subtitle?: string }[];
    onClose?: () => void;
  }): void {
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;
    const elements: Phaser.GameObjects.GameObject[] = [];

    // Dim the whole battlefield so the popup reads as a modal.
    const dim = this.add.rectangle(cx, cy, GAME_W, GAME_H, 0x000000, 0.6)
      .setDepth(95).setInteractive();
    elements.push(dim);

    // Panel — dynamically sized, tighter than the previous version. Stats are
    // 14 px rows (was 18), single reward card is 240 × 54, no separate rewards
    // label (the card itself is clearly a reward).
    const panelW = 280;
    const statRowH = 14;
    const rewardCardH = 54;          // single-card mode (1 reward)
    const rewardIconRowH = 56;       // icon-row mode (2+ rewards)
    const hasRewards = opts.rewards.length > 0;
    const useIconRow = opts.rewards.length >= 2;
    const rewardSectionH = !hasRewards ? 0
      : useIconRow ? 10 + rewardIconRowH
      : 10 + rewardCardH;
    const panelH = 18 /* top */
      + 20 /* title */
      + 10 /* subtitle */
      + 8  /* gap */
      + opts.stats.length * statRowH
      + rewardSectionH
      + 10 + 26 /* btn */ + 12 /* bottom */;

    // Outer panel + stroke
    const panel = this.add.rectangle(cx, cy, panelW, panelH, 0x0a0604, 0.98)
      .setStrokeStyle(2, opts.accentColor).setDepth(96);
    elements.push(panel);
    // Inset inner stroke — double-border ornament.
    const innerPanel = this.add.rectangle(cx, cy, panelW - 10, panelH - 10, 0, 0)
      .setStrokeStyle(1, 0x7a5518, 0.6).setDepth(96.5);
    elements.push(innerPanel);
    // Corner brackets — bright gold L-shape in each corner, matches hero portrait.
    const bracketLen = 12;
    const bL = cx - panelW / 2 + 6;
    const bT = cy - panelH / 2 + 6;
    const bR = cx + panelW / 2 - 6;
    const bB = cy + panelH / 2 - 6;
    const brackets = this.add.graphics().setDepth(96.7);
    brackets.lineStyle(2, 0xc08030, 1);
    brackets.beginPath();
    brackets.moveTo(bL, bT + bracketLen); brackets.lineTo(bL, bT); brackets.lineTo(bL + bracketLen, bT); // TL
    brackets.moveTo(bR - bracketLen, bT); brackets.lineTo(bR, bT); brackets.lineTo(bR, bT + bracketLen); // TR
    brackets.moveTo(bL, bB - bracketLen); brackets.lineTo(bL, bB); brackets.lineTo(bL + bracketLen, bB); // BL
    brackets.moveTo(bR - bracketLen, bB); brackets.lineTo(bR, bB); brackets.lineTo(bR, bB - bracketLen); // BR
    brackets.strokePath();
    elements.push(brackets);

    // Headline — colour-matched to the event accent. Smaller than before
    // (16 px) so the panel can shrink without the title overflowing.
    const titleColor = `#${opts.accentColor.toString(16).padStart(6, '0')}`;
    let cursorY = cy - panelH / 2 + 20;
    const title = this.add.text(cx, cursorY, opts.title, {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: titleColor,
      fontStyle: 'bold', stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5, 0.5).setDepth(97);
    elements.push(title);
    cursorY += 18;

    const subtitle = this.add.text(cx, cursorY, 'Event complete', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'italic',
    }).setOrigin(0.5, 0.5).setDepth(97);
    elements.push(subtitle);
    cursorY += 14;

    // Horizontal divider above stats.
    const divTop = this.add.rectangle(cx, cursorY, panelW - 50, 1, 0x4a3a20, 0.8).setDepth(97);
    elements.push(divTop);
    cursorY += 8;

    // Stats list — left-aligned label, right-aligned value.
    for (const stat of opts.stats) {
      const color = stat.color ?? '#fbbf24';
      const labelText = this.add.text(cx - panelW / 2 + 22, cursorY, stat.label, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#8a6420',
      }).setOrigin(0, 0.5).setDepth(97);
      const valueText = this.add.text(cx + panelW / 2 - 22, cursorY, stat.value, {
        fontSize: '11px', fontFamily: "'Consolas', 'Courier New', monospace",
        color, fontStyle: 'bold',
      }).setOrigin(1, 0.5).setDepth(97);
      elements.push(labelText, valueText);
      cursorY += statRowH;
    }

    // Reward render — two layouts:
    //   • Single reward → one wide card (icon + name + subtitle stacked).
    //   • Multiple rewards → horizontal icon row, hover-tooltip on each
    //     so the panel doesn't grow vertically (and long names like
    //     "Hourglass of Stasis" don't overflow into adjacent cards).
    if (hasRewards) {
      cursorY += 8;
      const rarityHexFor = (r: { rarity?: string }) => r.rarity === 'legendary' ? 0xf97316
        : r.rarity === 'epic' ? 0xa855f7
        : r.rarity === 'unique' ? 0xcc3322
        : 0xc8973a;
      const rarityColorStrFor = (r: { rarity?: string }) => r.rarity === 'legendary' ? '#f97316'
        : r.rarity === 'epic' ? '#a855f7'
        : r.rarity === 'unique' ? '#cc3322'
        : '#c8973a';

      if (!useIconRow) {
        // Single-reward card layout (unchanged from before).
        const cardW = Math.min(240, panelW - 30);
        const cardH = rewardCardH;
        const r = opts.rewards[0];
        const ry = cursorY + cardH / 2;
        const rarityHex = rarityHexFor(r);
        const rarityColorStr = rarityColorStrFor(r);
        const card = this.add.rectangle(cx, ry, cardW, cardH, 0x14100a, 1)
          .setStrokeStyle(2, rarityHex).setDepth(97);
        elements.push(card);
        const cardInner = this.add.rectangle(cx, ry, cardW - 6, cardH - 6, 0, 0)
          .setStrokeStyle(1, 0x7a5518, 0.5).setDepth(97.3);
        elements.push(cardInner);
        const iconX = cx - cardW / 2 + 24;
        if (r.iconKey && this.textures.exists(r.iconKey)) {
          const icon = this.add.image(iconX, ry, r.iconKey)
            .setDisplaySize(36, 36).setDepth(97.5);
          elements.push(icon);
        }
        const textLeft = iconX + 24;
        const textRight = cx + cardW / 2 - 10;
        const textWidth = textRight - textLeft;
        const name = this.add.text(textLeft, ry - 8, r.name, {
          fontSize: '12px', fontFamily: 'Georgia, serif', color: rarityColorStr,
          fontStyle: 'bold',
        }).setOrigin(0, 0.5).setDepth(97.5);
        elements.push(name);
        if (r.subtitle) {
          const sub = this.add.text(textLeft, ry + 8, r.subtitle, {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#a07840',
            wordWrap: { width: textWidth },
          }).setOrigin(0, 0.5).setDepth(97.5);
          elements.push(sub);
        }
        cursorY += cardH;
      } else {
        // Multi-reward icon row — hover for tooltip. Avoids overlapping
        // text on long names by collapsing each reward to a 44px icon
        // tile with a rarity stroke.
        const tileSize = 44;
        const tileGap = 14;
        const totalW = opts.rewards.length * tileSize + (opts.rewards.length - 1) * tileGap;
        const startX = cx - totalW / 2 + tileSize / 2;
        const rowY = cursorY + rewardIconRowH / 2;
        let activeTip: Phaser.GameObjects.GameObject[] | null = null;
        const clearTip = () => {
          if (activeTip) { for (const o of activeTip) o.destroy(); activeTip = null; }
        };
        opts.rewards.forEach((r, i) => {
          const tx = startX + i * (tileSize + tileGap);
          const rarityHex = rarityHexFor(r);
          const rarityColorStr = rarityColorStrFor(r);
          // Tile bg + rarity stroke.
          const tile = this.add.rectangle(tx, rowY, tileSize, tileSize, 0x14100a, 1)
            .setStrokeStyle(2, rarityHex).setDepth(97).setInteractive({ useHandCursor: true });
          elements.push(tile);
          // Icon centered.
          if (r.iconKey && this.textures.exists(r.iconKey)) {
            const icon = this.add.image(tx, rowY, r.iconKey)
              .setDisplaySize(32, 32).setDepth(97.5);
            elements.push(icon);
          }
          // Hover → name + subtitle tooltip above the row. Box auto-sizes to
          // fit a multi-line subtitle (sigil descriptions can run 80+ chars).
          tile.on('pointerover', () => {
            clearTip();
            const tipW = 240;
            const tipPad = 6;
            const nameTxt = this.add.text(0, 0, r.name, {
              fontSize: '11px', fontFamily: 'Georgia, serif', color: rarityColorStr, fontStyle: 'bold',
            }).setOrigin(0.5, 0).setDepth(99.5);
            let subTxt: Phaser.GameObjects.Text | null = null;
            if (r.subtitle) {
              subTxt = this.add.text(0, 0, r.subtitle, {
                fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
                wordWrap: { width: tipW - tipPad * 2 }, align: 'center',
              }).setOrigin(0.5, 0).setDepth(99.5);
            }
            const nameH = nameTxt.height;
            const subH = subTxt ? subTxt.height + 3 : 0;
            const tipH = tipPad * 2 + nameH + subH;
            const tipCenterY = rowY - tileSize / 2 - 8 - tipH / 2;
            const tipBg = this.add.rectangle(tx, tipCenterY, tipW, tipH, 0x0a0604, 0.97)
              .setStrokeStyle(1, rarityHex, 0.85).setDepth(99);
            nameTxt.setPosition(tx, tipCenterY - tipH / 2 + tipPad);
            if (subTxt) subTxt.setPosition(tx, tipCenterY - tipH / 2 + tipPad + nameH + 3);
            const tipObjs: Phaser.GameObjects.GameObject[] = [tipBg, nameTxt];
            if (subTxt) tipObjs.push(subTxt);
            activeTip = tipObjs;
            elements.push(...tipObjs);
          });
          tile.on('pointerout', clearTip);
        });
        cursorY += rewardIconRowH;
      }
    }

    // Continue button — accent-bordered, closes popup + fires onClose.
    const btnY = cy + panelH / 2 - 22;
    const btnBg = this.add.rectangle(cx, btnY, 140, 24, 0x1a1108, 1)
      .setStrokeStyle(1, opts.accentColor).setDepth(97).setInteractive({ useHandCursor: true });
    const btnText = this.add.text(cx, btnY, 'Continue', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: titleColor, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(98);
    elements.push(btnBg, btnText);
    btnBg.on('pointerover', () => { btnBg.setFillStyle(0x2a1810, 1); btnText.setColor('#ffe8a0'); });
    btnBg.on('pointerout',  () => { btnBg.setFillStyle(0x1a1108, 1); btnText.setColor(titleColor); });
    const close = (): void => {
      for (const e of elements) e.destroy();
      if (opts.onClose) opts.onClose();
    };
    btnBg.on('pointerdown', close);
    // Swallow clicks on the dim layer so background UI can't receive input while open.
    dim.on('pointerdown', () => { /* no-op */ });

    // Pop-in animation — scale from 0.7 → 1 for the panel group.
    for (const e of elements) {
      if ((e as any).scale !== undefined) (e as any).setScale(0.7);
    }
    this.tweens.add({
      targets: elements,
      scale: 1,
      duration: 220, ease: 'Back.easeOut',
    });
  }

  private showZoneClearedPopup(): void {
    // If a tome or talent choice is currently open, defer until it closes
    if (this.tomeChoiceActive || this.talentChoiceActive) {
      this.pendingZoneCleared = true;
      return;
    }
    // Close any overlay scenes so the popup is fully clickable
    this.closeOverlayScenes();
    this.pendingZoneCleared = false;
    this.zoneClearedActive = true;

    // Destroy any previous popup
    for (const obj of this.zoneClearedPopup) obj.destroy();
    this.zoneClearedPopup = [];

    // Score bonus for clearing a zone — gated by `_zoneScoreCountedKey` so
    // popup rebuilds (path-card click flow) don't re-add the bonus on every
    // re-render. The key combines stage + wave so a fresh zone clear at a
    // new (level, wave) tuple counts; same tuple is a no-op.
    const scoreKey = `${this.gsm.player.currentLevel}_${this.gsm.battle.waveNumber}_${this.infernoMode ? 'inf' : 'std'}`;
    const alreadyCounted = this._zoneScoreCountedKey === scoreKey;
    if (!alreadyCounted) {
      const zoneScore = this.battleLevel * 100 + this.gsm.battle.waveNumber * 25;
      this.runScore += zoneScore;
      if (this.infernoMode) {
        this.infernoScore += zoneScore;
      }
      // Both `player.highScore` (standard) and `player.infernoHighScore`
      // (inferno) are written ONLY at run END — standard in the death
      // handler (~line 15898), inferno in handleInfernoDefeat (~15941).
      // A mid-run write would let an abandoned Inferno run keep its peak
      // score even though `abandonInfernoRun()` rolls back hero/inventory
      // snapshots and the abandon popup promises "score will not be saved".
      this._zoneScoreCountedKey = scoreKey;
      // ── Mastery evaluation on zone clear ───────────────────────
      // Speed-Run challenges: how fast did this zone clear?
      // Boss-in-run challenges: every cleared zone counts toward the
      //   "5 bosses / 10 bosses in a single run" gates (the boss kill
      //   itself was already counted by trackBossKill; here we just
      //   trigger the evaluator so the unlock fires immediately).
      // Inferno-zone challenges: trigger when an inferno zone clears.
      const zoneSeconds = this.zoneStartedAtMs > 0
        ? Math.floor((Date.now() - this.zoneStartedAtMs) / 1000)
        : Number.POSITIVE_INFINITY;
      const claimedAll: import('@roe/shared').MasteryChallenge[] = [];
      claimedAll.push(...this.gsm.evaluateFastZoneClear(zoneSeconds));
      claimedAll.push(...this.gsm.evaluateBossesInRun());
      if (this.infernoMode) {
        claimedAll.push(...this.gsm.evaluateInfernoZone(this.infernoZone));
      }
      for (const c of claimedAll) {
        this.showAnnouncement(`✦ MASTERY: ${c.name} — +${c.runes} ◆`);
      }
    }

    const width = GAME_W, height = GAME_H;
    const cx = width / 2;

    const isInferno = this.infernoMode;
    // Inferno-save runs are now full-featured: paths, altars, events all
    // unlock as if it were a normal save. Only the LEGACY mid-run inferno
    // descent (data?.inferno from the ESC pathway) keeps the stripped-
    // down popup. Per user "add the path loop in inferno as well with
    // altar etc..." — `legacyInferno` is the gate the post-clear popup
    // checks for "no fancy menu", `isInferno` continues to drive cosmetics
    // (red border / score wording).
    const isInfernoSave = this.gsm.player.infernoSave === true;
    const legacyInferno = isInferno && !isInfernoSave;
    const borderColor = isInferno ? 0xcc2200 : 0xc8973a;
    const titleColor  = isInferno ? '#ff6030' : '#c8973a';
    const titleStr    = isInferno ? '🔥  INFERNO CLEARED  🔥' : '✦  ZONE CLEARED  ✦';
    const scoreStr    = isInferno ? this.infernoScore : this.runScore;
    const zoneLabel   = isInferno ? `Inferno Zone ${this.infernoZone}` : `Stage ${this.gsm.player.currentLevel}`;

    // Full-screen "book spread" layout — no contained panel, the whole canvas
    // becomes the page. Continue sits as a small pill on the right edge (turn
    // page forward), Stay as a small pill on the left edge (previous page).
    // Per user feedback the gold canvas-edge frame was removed — popup now
    // sits as just a dark overlay + title text + edge pills + cards. Cleaner
    // read against the live battlefield behind.
    const overlay = this.add.rectangle(cx, height / 2, width, height, 0x05030a, 0.85)
      .setDepth(50).setInteractive(); // blocks clicks under the popup
    this.zoneClearedPopup.push(overlay);

    // Title — moved down from y=44 to y=92 so it doesn't overlap the wave-
    // counter / zone-name HUD text that lives at y≈30. Subtle drop-shadow
    // (stroke) so it stays legible against the live background.
    const title = this.add.text(cx, 92, titleStr, {
      fontSize: '22px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: titleColor, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(52);
    this.zoneClearedPopup.push(title);
    const cy = height / 2;
    void borderColor; // border colour is no longer used directly — kept above for future re-skinning

    // Full 4-card choice fires AFTER clearing each multiple-of-5 stage (5, 10,
    // 15, …, 30, 35, 40, …). The popup title reads "Stage N cleared" where N
    // is `currentLevel`, so gating off currentLevel makes the menu line up
    // with the stage number the player just beat — matches the natural mental
    // model "every 5 stages I get the choice menu."
    //
    // Stages that ALSO offer the Inferno Descent button (40, 50, 60, …) keep
    // the 4-card menu intact — Inferno is rendered as an extra option below
    // the panel, not as a replacement.
    const curStage = this.gsm.player.currentLevel;
    // Path-pick cadence: every 10 stages (10, 20, 30, 40, 50). Path effect
    // still lasts 5 zones, so picks 11-15 ride the path and 16-19 are a
    // breather before the next pick at 20. Per-altar stage (9 / 19 / 29 …)
    // sits one stage before each path pick; they never overlap.
    const fullCardChoice = !legacyInferno && curStage % 10 === 0 && curStage >= 10;

    const nextMilestone = Math.ceil((curStage + 1) / 10) * 10;
    const stagesToCrossroads = Math.max(1, nextMilestone - curStage);
    const headerHint = fullCardChoice
      ? 'Choose your road'
      : (stagesToCrossroads === 1 ? 'A crossroads waits beyond' : `A crossroads in ${stagesToCrossroads} zones`);
    const info = this.add.text(cx, 124, `${zoneLabel}  ·  Score: ${scoreStr}  ·  ${headerHint}`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(52);
    this.zoneClearedPopup.push(info);

    // ── Path choice — asymmetric layout ──────────────────────────────────
    // RIGHT side: BIG Battle card (the default forward path).
    // LEFT side at milestones (stage 10+, every 5 stages): Crossroads modifier
    //   picks (2 stage 10-29, 3 stage 30+) + event + altar side options.
    // The Shrine card was retired here (curse/blessing rework deferred); altar
    // stays. Non-milestone stages just show the Battle card centered on the
    // right with empty space on the left.
    const showCrossroads = fullCardChoice;

    type CardPickHandler = () => void;
    interface CardOpts {
      /** Optional Unicode glyph rendered at the top of the card (large). Use for
       *  Crossroads modifier cards so each pick has a distinct icon — `⦿` riches,
       *  `⛨` iron, `☘` pilgrim, `☠` cursed-riches, etc. */
      iconChar?: string;
      /** Sub-line shown in italic at the bottom of the card — used to surface
       *  duration ("Active 1 zone — stays preserve") or other contextual info. */
      footer?: string;
      /** Multi-line tooltip surfaced on hover, anchored above the card. Lets the
       *  card stay terse while the full effect/duration spec lives on hover. */
      tooltip?: string;
      /** Renders the card with a brighter green stroke + green-tinted footer
       *  to mark "currently active" state — used for the live Crossroads path
       *  so players can see which one is bound at a glance. */
      highlightActive?: boolean;
      /** Optional Phaser texture key (e.g. `zone_background_forest`) rendered
       *  inside the card area + a dark scrim on top so the card content stays
       *  legible. Used for path cards to preview the biome of the upcoming
       *  zone (mirrors the main-menu save-slot treatment). */
      bgImageKey?: string;
    }
    const buildSmallCard = (
      ccx: number, ccy: number, w: number, h: number,
      title: string, desc: string, accent: number,
      drawGlyph: ((g: Phaser.GameObjects.Graphics, gx: number, gy: number) => void) | null,
      onPick: CardPickHandler,
      opts: CardOpts = {},
    ): void => {
      // Active highlight: brighter green stroke + green inner outline. Used
      // for the currently-bound Crossroads path so the player can see which
      // card is "live" at a glance.
      const baseStroke = opts.highlightActive ? 0x7acc60 : accent;
      const baseInner  = opts.highlightActive ? 0x7acc60 : accent;
      const baseFill   = opts.highlightActive ? 0x162410 : 0x1a1108;
      // When a biome bg image is supplied, the cardBg fill is fully
      // transparent so the image shows through; the dark scrim above
      // handles readability. Without an image, the regular dark fill is used.
      const hasBg = !!opts.bgImageKey && this.textures.exists(opts.bgImageKey);
      const fillAlpha = hasBg ? 0 : 1;
      const cardBg = this.add.rectangle(ccx, ccy, w, h, baseFill, fillAlpha)
        .setStrokeStyle(opts.highlightActive ? 3 : 2, baseStroke).setDepth(51).setInteractive({ useHandCursor: true });
      // Biome background image — clipped to card area via setDisplaySize, sits
      // BEHIND the card frame stroke (depth 50.5) and gets a dark scrim on top
      // (depth 50.6) so the title / desc / footer stay legible.
      let bgImg: Phaser.GameObjects.Image | undefined;
      let bgScrim: Phaser.GameObjects.Rectangle | undefined;
      if (hasBg && opts.bgImageKey) {
        bgImg = this.add.image(ccx, ccy, opts.bgImageKey)
          .setDisplaySize(w - 4, h - 4).setDepth(50.5);
        bgScrim = this.add.rectangle(ccx, ccy, w - 4, h - 4, 0x05030a, 0.62).setDepth(50.6);
      }
      // Inner accent outline — subtle gold trim that lifts the card from a
      // plain rectangle to "ornate slot".
      const innerOutline = this.add.rectangle(ccx, ccy, w - 8, h - 8, 0, 0)
        .setStrokeStyle(1, baseInner, opts.highlightActive ? 0.7 : 0.35).setDepth(51);

      // Card layout — icon tucked into the top-right corner so it doesn't
      // overlap the title. Title / desc / footer use the same vertical
      // positions as the no-icon variant.
      let glyphGfx: Phaser.GameObjects.Graphics | undefined;
      let iconText: Phaser.GameObjects.Text | undefined;
      const titleY = ccy - h * 0.30;
      const descY  = ccy - h * 0.05;
      const footerY = ccy + h * 0.36;
      const iconCx = ccx + w / 2 - 18;
      const iconCy = ccy - h / 2 + 18;
      if (drawGlyph) {
        glyphGfx = this.add.graphics().setDepth(52);
        drawGlyph(glyphGfx, iconCx, iconCy);
      } else if (opts.iconChar) {
        iconText = this.add.text(iconCx, iconCy, opts.iconChar, {
          fontSize: '20px', fontFamily: 'Georgia, serif', color: '#b89858', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(52);
      }
      // Title + desc colours desaturated from bright gold to muted bronze
      // per user feedback ("less yellow, more grey/brown — Diablo II tones").
      const titleT = this.add.text(ccx, titleY, title, {
        fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#b89858', fontStyle: 'bold',
        wordWrap: { width: w - 12 }, align: 'center',
      }).setOrigin(0.5).setDepth(52);
      const descT = this.add.text(ccx, descY, desc, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a08866',
        wordWrap: { width: w - 16 }, align: 'center', lineSpacing: 2,
      }).setOrigin(0.5).setDepth(52);
      let footerT: Phaser.GameObjects.Text | undefined;
      if (opts.footer) {
        footerT = this.add.text(ccx, footerY, opts.footer, {
          fontSize: '8px', fontFamily: 'Georgia, serif',
          color: opts.highlightActive ? '#7acc60' : '#7a5a30',
          fontStyle: opts.highlightActive ? 'bold' : 'italic',
          wordWrap: { width: w - 16 }, align: 'center',
        }).setOrigin(0.5).setDepth(52);
      }

      this.zoneClearedPopup.push(cardBg, innerOutline, titleT, descT);
      if (bgImg) this.zoneClearedPopup.push(bgImg);
      if (bgScrim) this.zoneClearedPopup.push(bgScrim);
      if (glyphGfx) this.zoneClearedPopup.push(glyphGfx);
      if (iconText) this.zoneClearedPopup.push(iconText);
      if (footerT) this.zoneClearedPopup.push(footerT);

      // Hover tooltip — bronze panel anchored above the card, surfaces the
      // detailed effect + duration spec. Skipped when no tooltip is provided.
      let tipObj: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
      const onOver = (): void => {
        if (hasBg) {
          cardBg.setFillStyle(baseFill, 0);
          if (bgScrim) bgScrim.setFillStyle(0x05030a, 0.35);
        } else {
          cardBg.setFillStyle(0x2a1810, 1);
        }
        // Desaturated hover stroke (was 0xffd060 / 0xffe8a0 — too neon).
        cardBg.setStrokeStyle(opts.highlightActive ? 3 : 2, 0xc0a060);
        innerOutline.setStrokeStyle(1, 0xc8b080, 0.6);
        titleT.setColor('#d8c090');
        if (opts.tooltip && !tipObj) {
          tipObj = this.createHudTooltip(ccx, ccy - h / 2 - 8, opts.tooltip,
            0.5, 1, { fontSize: '10px', color: '#c8973a', depth: 60 });
          this.zoneClearedPopup.push(tipObj as Phaser.GameObjects.GameObject);
        }
      };
      const onOut = (): void => {
        if (hasBg) {
          cardBg.setFillStyle(baseFill, 0);
          if (bgScrim) bgScrim.setFillStyle(0x05030a, 0.62);
        } else {
          cardBg.setFillStyle(baseFill, 1);
        }
        cardBg.setStrokeStyle(opts.highlightActive ? 3 : 2, baseStroke);
        innerOutline.setStrokeStyle(1, baseInner, opts.highlightActive ? 0.7 : 0.35);
        titleT.setColor('#b89858');
        if (tipObj) {
          tipObj.destroy();
          const idx = this.zoneClearedPopup.indexOf(tipObj as Phaser.GameObjects.GameObject);
          if (idx >= 0) this.zoneClearedPopup.splice(idx, 1);
          tipObj = null;
        }
      };
      cardBg.on('pointerover', onOver).on('pointerout', onOut).on('pointerdown', onPick);
    };

    // ── CONTINUE pill — small button on the RIGHT side of the screen ─────
    // When path cards are visible the pill becomes a fully-locked stub:
    // greyed bronze frame, "PICK A PATH" copy, hover surfaces a "Pick a
    // path or Stay" tooltip, and clicking only blinks the path-card row
    // instead of advancing. The user explicitly removed the "free skip"
    // option — Advance is no longer a no-curse easy path. Stay still
    // works (left pill) so the player can re-roll the picker.
    {
      const showsPaths = !!(typeof curStage === 'number' && !legacyInferno && curStage % 10 === 0 && curStage >= 10);
      const pillW = 96, pillH = 160;
      const pillX = width - 130;
      const pillY = cy;
      const accent = showsPaths ? 0x4a3618 : 0xc8973a;
      const pillBg = this.add.rectangle(pillX, pillY, pillW, pillH, 0x1a1108, showsPaths ? 0.55 : 0.97)
        .setStrokeStyle(2, accent).setDepth(53).setInteractive({ useHandCursor: !showsPaths });
      const innerOutline = this.add.rectangle(pillX, pillY, pillW - 8, pillH - 8, 0, 0)
        .setStrokeStyle(1, 0xffe8a0, showsPaths ? 0.10 : 0.45).setDepth(53);
      // Gold corner ornaments — small filled triangles in each pill corner so
      // the button reads as "ornate medieval button" rather than a plain box.
      const cornerGfx = this.add.graphics().setDepth(54);
      const drawCorner = (cx_: number, cy_: number, dx: number, dy: number): void => {
        cornerGfx.fillStyle(accent, 1);
        cornerGfx.fillTriangle(cx_, cy_, cx_ + 8 * dx, cy_, cx_, cy_ + 8 * dy);
      };
      const x0 = pillX - pillW / 2 + 2;
      const x1 = pillX + pillW / 2 - 2;
      const y0 = pillY - pillH / 2 + 2;
      const y1 = pillY + pillH / 2 - 2;
      drawCorner(x0, y0, +1, +1);
      drawCorner(x1, y0, -1, +1);
      drawCorner(x0, y1, +1, -1);
      drawCorner(x1, y1, -1, -1);
      const glyphGfx = this.add.graphics().setDepth(54);
      drawBattleCardGlyph(glyphGfx, pillX, pillY - 38, accent);
      glyphGfx.setAlpha(showsPaths ? 0.45 : 1);
      const divider = this.add.rectangle(pillX, pillY + 4, pillW - 20, 1, 0xffe8a0, showsPaths ? 0.15 : 0.55).setDepth(54);
      const restColor = showsPaths ? '#5a3f20' : '#f0c040';
      const titleT = this.add.text(pillX, pillY + 22, showsPaths ? 'PICK\nA PATH' : 'ADVANCE', {
        fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: restColor, fontStyle: 'bold', align: 'center',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(54);
      // Disabled state used to render a colour-emoji ⛔ which contrast-clashed
      // with the bronze HUD palette (per user feedback). When paths are
      // showing, the arrow is hidden entirely and the "PICK A PATH" label
      // carries the message on its own.
      const arrowT = this.add.text(pillX, pillY + 50, showsPaths ? '' : '→', {
        fontSize: '20px', fontFamily: 'Georgia, serif', color: restColor, fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(54).setVisible(!showsPaths);
      this.zoneClearedPopup.push(pillBg, innerOutline, cornerGfx, glyphGfx, divider, titleT, arrowT);
      const onOver = (): void => {
        if (showsPaths) {
          // Disabled tooltip — explain WHY clicking does nothing.
          const tip = this.add.text(pillX, pillY - pillH / 2 - 8, 'Choose a road or stay your hand.\nThere is no straight way here.', {
            fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', align: 'center',
            backgroundColor: '#0d0a0e', padding: { x: 8, y: 5 }, lineSpacing: 3,
          }).setOrigin(0.5, 1).setDepth(60).setName('advance_disabled_tip');
          this.zoneClearedPopup.push(tip);
          return;
        }
        pillBg.setFillStyle(0x2a1810, 1); pillBg.setStrokeStyle(2, 0xffd060);
        innerOutline.setStrokeStyle(1, 0xffe8a0, 0.85);
        titleT.setColor('#ffe8a0'); arrowT.setColor('#ffe8a0');
      };
      const onOut = (): void => {
        if (showsPaths) {
          const tip = this.zoneClearedPopup.find((o: any) => o.name === 'advance_disabled_tip');
          if (tip) { tip.destroy(); this.zoneClearedPopup.splice(this.zoneClearedPopup.indexOf(tip), 1); }
          return;
        }
        pillBg.setFillStyle(0x1a1108, 0.97); pillBg.setStrokeStyle(2, accent);
        innerOutline.setStrokeStyle(1, 0xffe8a0, 0.45);
        titleT.setColor(restColor); arrowT.setColor(restColor);
      };
      pillBg.on('pointerover', onOver).on('pointerout', onOut).on('pointerdown', () => {
        if (showsPaths) return; // hard-blocked — the path picker (or Stay) is the only commit.
        this.handleAdvance();
      });

      // ── Full Auto — auto-press Advance after a brief delay so the
      // player still sees the cleared screen flash by. Skipped when the
      // path picker is up (showsPaths) since path selection must be
      // manual; the user gets the popup as normal in that case.
      if (this.gsm.getAutoMode() === 'full' && !showsPaths) {
        this.time.delayedCall(1100, () => {
          if (this.zoneClearedActive && !this.tomeChoiceActive && !this.talentChoiceActive) {
            this.handleAdvance();
          }
        });
      }
    }

    // ── CENTER: Crossroads paths (top row) + event/altar (bottom row) ──
    // Per user feedback: cards are now smaller, packed closer together, and
    // each card has a clear icon glyph. Section labels make the path-vs-event
    // distinction explicit:
    //   PATHS  — buff/penalty for the next zone (mutate the run)
    //   EVENTS — one-time alternative encounter (replaces normal waves)
    if (showCrossroads) {
      // ── Path section header (top) ──
      // Paths cover 5 zones from the pick stage (e.g. picked at 10 → active
      // 11-15, breather 16-19, next pick at 20). Picking a card commits +
      // advances. The Advance pill is hard-disabled at this popup — Stay
      // is the only no-pick exit, and Stay re-opens the picker on the next
      // zone clear so the player can't dodge the choice forever.
      const activeMod = this.gsm.player.activeCrossroadsMod;
      const appliedAt = this.gsm.player.crossroadsModAppliedAtStage ?? (curStage + 1);
      const expiresAt = appliedAt + 5;
      const pathHeader = this.add.text(cx, cy - 144, 'CROSSROADS PATHS', {
        fontSize: '13px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#a78bfa', fontStyle: 'bold', stroke: '#000000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(52);
      const subCopy = activeMod
        ? `Walking the ${CROSSROADS_MODIFIERS[activeMod].name} — choose a new road or stay your hand`
        : 'Choose a road. Each leads far — five zones together — and there is no shortcut here.';
      const pathSub = this.add.text(cx, cy - 128, subCopy, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7c6e9e', fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(52);
      this.zoneClearedPopup.push(pathHeader, pathSub);

      // Path-card icon glyphs — picked to render as monochrome characters on
      // Windows (no colour-emoji fallback). One per modifier id.
      const PATH_ICONS: Record<CrossroadsModifierId, string> = {
        riches:        '⦿', // gold disc — wealth
        iron_path:     '▲', // mountain / endurance
        pilgrims_path: '✦', // star — XP / journey
        cursed_riches: '☠', // skull — high risk
        hermits_vow:   '☽', // crescent — solitude / scholarship
        glacial_path:  '❄', // snowflake — slow / cold tempo
      };

      // Modifier cards row — 2 picks stage 10-29, 3 picks stage 30+.
      // Smaller (160 / 200 wide × 100 tall) and tighter (8 px gap) per user
      // feedback. Cards now sit closer together so the row reads as one
      // grouped choice instead of three floating boxes.
      const allModIds: CrossroadsModifierId[] = [
        'riches', 'iron_path', 'pilgrims_path', 'cursed_riches',
        'hermits_vow', 'glacial_path',
      ];
      const pickCount = curStage >= 30 ? 3 : 2;
      const shuffled = [...allModIds].sort(() => Math.random() - 0.5);
      const modPicks = shuffled.slice(0, pickCount);
      const modCardW = pickCount === 3 ? 160 : 200;
      const modCardH = 110;
      const modGap = 8;
      const modGroupW = pickCount * modCardW + (pickCount - 1) * modGap;
      const modCy = cy - 50;
      // Per-path biome assignment — each path card now leads to a DIFFERENT
      // biome than the natural advance and different from the other paths.
      // Pulls from the eligible zone pool (excluding the natural-advance zone)
      // and shuffles, so picking a path actually re-routes the run instead
      // of just slapping a stat modifier on the same zone. The chosen zone
      // id is stored alongside the path mod and `getZoneForLevel`'s result
      // is overridden during scene init while the path is active.
      const naturalZone = getZoneForLevel(curStage + 1);
      const ELIGIBLE_PATH_ZONES = ZONES.filter((z) => (curStage + 1) >= (z.minLevel ?? 1));
      const otherZones = ELIGIBLE_PATH_ZONES
        .filter((z) => z.id !== naturalZone?.id)
        .sort(() => Math.random() - 0.5);
      // Map each pickIdx → a zone (loops if there are fewer eligible zones than picks).
      const zoneForPickIdx = (idx: number): typeof naturalZone | null => {
        if (otherZones.length === 0) return null;
        return otherZones[idx % otherZones.length];
      };
      const bgKeyForZone = (zone: typeof naturalZone | null): string | null => {
        if (!zone?.themeKey) return null;
        const k = zone.themeKey.replace(/^zone_/, 'zone_background_');
        return this.textures.exists(k) ? k : null;
      };

      // One-click commit per user feedback — the previous "select then click
      // again to travel" flow confused players into spam-clicking different
      // cards expecting forward motion. Clicking a path now writes the path
      // state and immediately advances via `handleAdvance`. Idempotency: a
      // local `pathLocked` flag swallows duplicate clicks during the brief
      // window between selection + scene restart.
      // Pick stage = the stage the path will activate on (current + 1).
      // Scaled values surface the actual deltas the player is committing to —
      // the same Riches pick reads "+15% gold · enemies hit 45% harder" at
      // stage 5 but "+105% gold · enemies hit 315% harder" at stage 50, so
      // the offer reads as genuinely scary in late game instead of identical
      // text everywhere.
      const pickStage = this.gsm.player.currentLevel + 1;
      const pickScale = getCrossroadsScale(pickStage);
      // Visual escalation: shift card accent toward an ominous crimson the
      // higher the scale climbs. At ×1.0 the original accent shows; at ×2.5+
      // the path's frame leans deep red so the row of choices reads as a
      // gauntlet, not a menu of small bonuses.
      const accentShift = (baseAccent: number, scale: number): number => {
        if (scale <= 1.05) return baseAccent;
        const t = Math.min(1, (scale - 1) / 2.5); // 0..1 over scale 1..3.5
        const target = 0xb02020;                  // crimson danger tone
        const br = (baseAccent >> 16) & 0xff, bg = (baseAccent >> 8) & 0xff, bb = baseAccent & 0xff;
        const tr = (target >> 16) & 0xff, tg = (target >> 8) & 0xff, tb = target & 0xff;
        const r = Math.round(br + (tr - br) * t * 0.65);
        const g = Math.round(bg + (tg - bg) * t * 0.65);
        const b = Math.round(bb + (tb - bb) * t * 0.65);
        return (r << 16) | (g << 8) | b;
      };
      let pathLocked = false;
      modPicks.forEach((modId, i) => {
        const baseDef = CROSSROADS_MODIFIERS[modId];
        const def = scaleCrossroadsModifier(baseDef, pickStage);
        const cardAccent = accentShift(def.accent, pickScale);
        const mcx = cx - modGroupW / 2 + modCardW / 2 + i * (modCardW + modGap);
        const cardZone = zoneForPickIdx(i);
        const cardBgKey = bgKeyForZone(cardZone);
        const cardBiomeName = cardZone?.name ?? 'Unknown';
        const isActiveCard = activeMod === modId;
        // Append a danger label to the footer when the scale is meaningfully
        // elevated — gives the player a one-glance read on the threat tier.
        const dangerTag =
          pickScale >= 2.5 ? '  ·  ⚠ DEADLY' :
          pickScale >= 1.8 ? '  ·  ⚠ HARSH'  :
          pickScale >= 1.4 ? '  ·  ⚠ TESTING' :
          '';
        const footer = `→ ${cardBiomeName}${dangerTag}`;
        const tooltipScaleLine = pickScale > 1.05
          ? `\nScale: ×${pickScale.toFixed(2)} (stage ${pickStage})`
          : '';
        buildSmallCard(mcx, modCy, modCardW, modCardH, def.name, def.description, cardAccent, null, () => {
          if (pathLocked) return;
          pathLocked = true;
          this.gsm.player.activeCrossroadsMod = modId;
          this.gsm.player.crossroadsModAppliedAtStage = this.gsm.player.currentLevel + 1;
          this.gsm.player.crossroadsPathZoneId = cardZone?.id;
          this.gsm.save(true);
          this.handleAdvance();
        }, {
          iconChar: PATH_ICONS[modId],
          footer,
          tooltip: `${def.name}\n${def.description}${tooltipScaleLine}\n\nLeads to: ${cardBiomeName}\nClick once to travel this path —\nactive for 5 zones.`,
          highlightActive: isActiveCard,
          bgImageKey: cardBgKey ?? undefined,
        });
      });

      // EVENTS section retired from the per-5-stage popup per user feedback.
      // Random events (Elite Pack / Horde / Telurion) now spawn as a small
      // optional card next to the ADVANCE pill at every zone clear with a
      // 10% chance — see the block below. Gilded Altar only appears at
      // stages 9 / 19 / 29 / 39 / 49 (also handled below). The Crossroads
      // path picker stays as the centrepiece of the every-5-stage popup.
    }

    // ── Gilded Altar will show? Computed here so the event card below can
    // re-anchor itself to sit underneath the altar when both are present.
    // Suppressed once consumed for this stage (one-shot per stage so a Stay
    // loop at a stage 9 / 19 / 29 / … milestone doesn't keep re-offering
    // the altar over and over).
    const altarStageConsumed = (this.gsm.player.altarConsumedAtStages ?? []).includes(curStage);
    const altarWillShow = !legacyInferno && curStage % 10 === 9 && !altarStageConsumed;

    // ── Random one-time event card (10% chance per zone clear) ───────────
    // Spawns to the LEFT of the ADVANCE pill so the player can choose to
    // engage or skip with a normal advance. Respects per-run lockouts:
    // Horde / Telurion vanish from the pool once cleared; Elite Pack is
    // always eligible. No event surfaces if the pool ends up empty.
    if (!legacyInferno && curStage >= 5) {
      const hordeAvailable    = !this.gsm.player.hordeClearedThisRun;
      const telurionAvailable = curStage >= 30 && !this.gsm.player.telurionClearedThisRun;
      // Elite Pack cooldown — once cleared, the event re-arms only after
      // ELITE_PACK_COOLDOWN_STAGES zones have advanced. Without this, a
      // 10 % roll on a pool of just-Elite-Pack (Horde + Telurion already
      // cleared / unavailable) ended up letting the player chain into
      // back-to-back packs, which the user reported as a bug. Cooldown
      // is per-run, cleared on resetRun.
      const ELITE_PACK_COOLDOWN_STAGES = 5;
      const lastElitePackStage = this.gsm.player.lastElitePackClearedAtStage ?? -999;
      const elitePackAvailable = curStage - lastElitePackStage >= ELITE_PACK_COOLDOWN_STAGES;
      const eventDefs: Array<{ title: string; desc: string; accent: number; onPick: CardPickHandler; iconChar: string; tooltip: string; bgImageKey?: string }> = [];
      if (elitePackAvailable) eventDefs.push({
        title: 'Elite Pack', desc: 'Hardened killers · richer spoils', accent: 0x7a1a00,
        onPick: () => this.startElitePack(),
        iconChar: '☠', tooltip: 'Elite Pack\nA gathering of hardened killers,\ntwo champions among them.\n\nThey carry richer spoils than\nwhat the road usually offers.',
        // Shadow-realm backdrop reads as the most threatening of the loaded
        // biome bgs — fits the "two cross-zone champions waiting for you".
        bgImageKey: 'zone_background_shadow',
      });
      if (hordeAvailable) eventDefs.push({
        title: 'Horde of Goblins', desc: 'Goblins from every side · richer spoils',
        accent: 0x3f6212, onPick: () => this.startHorde(),
        iconChar: '⚔', tooltip: 'Horde of Goblins\nThe greenskins close in from\nboth sides at once.\n\nThey leave more behind than\nthey took from the last village.',
        // Treasure-room dungeon — same backdrop the horde wave swaps in for
        // the actual encounter, so the card is a real preview of the fight.
        bgImageKey: 'zone_background_horde_treasure',
      });
      if (telurionAvailable) eventDefs.push({
        title: "Telurion's Lair", desc: 'Earth-dragon · a dragon\'s hoard',
        accent: 0x4a8030, onPick: () => this.startTelurionsLair(),
        iconChar: '⛰', tooltip: "Telurion's Lair\nThe earth-dragon stirs in his cave.\nHis sentinels stand the threshold.\n\nDragons hoard more than\nfair shares of gold.",
        // Earth-cavern backdrop — matches the actual lair encounter background.
        bgImageKey: 'zone_background_telurion',
      });

      // Telurion pity — first time the player hits stage 30+ with the quest
      // unclaimed AND the boss not yet beaten, force-pick Telurion (instead
      // of rolling 10%). Subsequent zones revert to the standard 10% roll.
      const telurionQuestClaimed = (this.gsm.player.storyQuests ?? [])
        .some((q) => q.id === 'telurions_lair' && q.claimed);

      // Sticky roll: only roll once per (stage, wave, mode) tuple. Re-shows
      // of the zone-clear popup (path-card cancel, event-clear chain, F5,
      // etc.) all fall through to the cached decision so an event card the
      // player saw a moment earlier doesn't vanish on a popup rebuild.
      let chosenIdx: number | null;
      let pityFire: boolean;
      if (this._eventRollCache?.key === scoreKey) {
        chosenIdx = this._eventRollCache.chosenIdx;
        pityFire = this._eventRollCache.pityFired;
      } else {
        pityFire = telurionAvailable && !telurionQuestClaimed && !this.gsm.player.telurionPityFiredThisRun;
        const fired = pityFire || Math.random() < 0.10;
        if (fired && eventDefs.length > 0) {
          chosenIdx = Math.floor(Math.random() * eventDefs.length);
          if (pityFire) {
            const telIdx = eventDefs.findIndex((e) => e.title === "Telurion's Lair");
            if (telIdx >= 0) chosenIdx = telIdx;
          }
        } else {
          chosenIdx = null;
        }
        this._eventRollCache = { key: scoreKey, chosenIdx, pityFired: pityFire };
      }

      if (chosenIdx !== null && eventDefs.length > 0) {
        let chosenEvent = eventDefs[Math.min(chosenIdx, eventDefs.length - 1)];
        if (pityFire && !this.gsm.player.telurionPityFiredThisRun) {
          chosenEvent = eventDefs.find((e) => e.title === "Telurion's Lair") ?? chosenEvent;
          this.gsm.player.telurionPityFiredThisRun = true;
          this.gsm.save(true);
        }
        // Layout: event card now ALWAYS centers, never anchors to the
        // right edge. Vertical position depends on what else is on the
        // popup so the cards stack as a clean column:
        //   - Path picker present → event sits below path-card row
        //   - Altar present       → event sits below altar
        //   - Plain popup         → event centers in the middle
        const eventCardW = (altarWillShow || showCrossroads) ? 200 : 220;
        const eventCardH = (altarWillShow || showCrossroads) ? 90 : 110;
        const eventCardX = cx;
        const eventCardY = altarWillShow ? cy + 80
                          : showCrossroads ? cy + 70
                          : cy;
        buildSmallCard(
          eventCardX, eventCardY, eventCardW, eventCardH,
          chosenEvent.title, chosenEvent.desc, chosenEvent.accent, null,
          chosenEvent.onPick,
          {
            iconChar: chosenEvent.iconChar,
            footer: 'A side road',
            tooltip: chosenEvent.tooltip,
            bgImageKey: chosenEvent.bgImageKey,
          },
        );
      }
    }

    // ── Gilded Altar — only at stages 9 / 19 / 29 / 39 / 49 ─────────────
    // Per user feedback: the altar is a "big thing" — promoted from a
    // small left-anchored card to a wide centered card occupying the
    // popup's main reading column. Random events at the same stage stack
    // beneath it (see eventCardY above).
    if (altarWillShow) {
      const altarCardW = 280;
      const altarCardH = 130;
      const altarCardX = cx;
      const altarCardY = cy - 30;
      // Phaser-drawn altar glyph — stepped slab + chalice silhouette + gold
      // halo behind. Drawn at 1.5× scale to match the bigger card footprint
      // so the icon doesn't look lonely in a 280-wide frame.
      const drawAltar = (g: Phaser.GameObjects.Graphics, gx: number, gy: number): void => {
        const s = 1.5;
        // Halo
        g.fillStyle(0xc8973a, 0.18);
        g.fillCircle(gx, gy + 1 * s, 13 * s);
        g.fillStyle(0xc8973a, 0.32);
        g.fillCircle(gx, gy + 1 * s, 8 * s);
        // Top step (slab)
        g.fillStyle(0x4a3818, 1);
        g.fillRect(gx - 11 * s, gy + 4 * s, 22 * s, 4 * s);
        g.fillStyle(0x6b5028, 1);
        g.fillRect(gx - 11 * s, gy + 4 * s, 22 * s, 1 * s);
        // Bottom step
        g.fillStyle(0x3a2814, 1);
        g.fillRect(gx - 13 * s, gy + 8 * s, 26 * s, 3 * s);
        // Chalice bowl
        g.fillStyle(0x8a6a20, 1);
        g.fillEllipse(gx, gy - 1 * s, 11 * s, 6 * s);
        g.fillStyle(0x6b5018, 1);
        g.fillRect(gx - 1.5 * s, gy + 1 * s, 3 * s, 4 * s);
        // Chalice base
        g.fillRect(gx - 4 * s, gy + 4 * s, 8 * s, 1.5 * s);
        // Inner gold rim
        g.lineStyle(1, 0xd4a040, 0.9);
        g.strokeEllipse(gx, gy - 2 * s, 11 * s, 5 * s);
        // Tiny gold flame above the chalice
        g.fillStyle(0xd4a040, 1);
        g.fillTriangle(gx, gy - 9 * s, gx - 2 * s, gy - 4 * s, gx + 2 * s, gy - 4 * s);
      };
      buildSmallCard(
        altarCardX, altarCardY, altarCardW, altarCardH,
        'Gilded Altar', 'Spill gold · the altar answers',
        0x8a6a20, drawAltar,
        () => this.openGildedAltar(),
        {
          footer: 'A rare audience',
          tooltip: 'Gilded Altar\nLay coin upon the slab — the unseen\naccept tribute and return a boon.\n\nThe stone does not move with you;\nyou return to where you stood.',
          // Treasure-room backdrop — gold piles + dim sconce light read as
          // "tribute slab in a hidden vault", which is what the altar is.
          bgImageKey: 'zone_background_horde_treasure',
        },
      );
    }

    // ── STAY pill — small button on the LEFT edge of the screen ─────────
    // Mirrors the Continue pill on the right. Vertical layout (label stacked
    // over the multiplier hints) so the pill stays narrow. Greyed when Stay
    // is unavailable (Stage 1, No Turning Back curse, or after an event
    // clear) — same disabled logic as the previous bottom-bar Stay button.
    const boonsForZoneClear = this.getBoonEffects();
    // Events (Elite Pack / Horde / Telurion) are now side encounters that
    // run at the SAME stage instead of consuming the advance. After they
    // clear, the popup re-opens with full Stay/Advance/Path options.
    const canStay = this.gsm.player.currentLevel > 1 && !boonsForZoneClear.cannotStay;
    if (!legacyInferno) {
      const stayCount = this.gsm.player.stayCount || 0;
      // Mirror the +20 % flat scaler from getStayBonuses so the pill copy
      // ("+X% mobs / +Y% HP / +Z% ATK") matches what combat actually applies.
      const STAY_DIFFICULTY = 1.20;
      const nextEnemyPct = Math.round(Math.pow(1.50, stayCount + 1) * STAY_DIFFICULTY * 100) - 100;
      const nextHpPct    = Math.round(Math.pow(1.50, stayCount + 1) * STAY_DIFFICULTY * 100) - 100;
      const nextAtkPct   = Math.round(Math.pow(1.30, stayCount + 1) * STAY_DIFFICULTY * 100) - 100;
      // Mirror of the Advance pill — pulled further inward (x=130) and
      // widened (76 → 96) so the label has breathing room and both pills
      // sit "more centered" against the live battlefield.
      const pillW = 96, pillH = 160;
      const pillX = 130;
      const pillY = cy;
      const accentBase = canStay ? 0xb07820 : 0x3a2810;
      const fillBase = canStay ? 0x141008 : 0x080806;
      const labelColorBase = canStay ? '#c8973a' : '#3a2810';
      const labelHexBase = canStay ? 0xffe8a0 : 0x3a2810;
      const stayBg = this.add.rectangle(pillX, pillY, pillW, pillH, fillBase, 0.97)
        .setStrokeStyle(2, accentBase).setDepth(53).setInteractive({ useHandCursor: canStay });
      const innerOutline = this.add.rectangle(pillX, pillY, pillW - 8, pillH - 8, 0, 0)
        .setStrokeStyle(1, labelHexBase, canStay ? 0.45 : 0.15).setDepth(53);
      // Gold corner ornaments — match the Continue pill's treatment so both
      // edge buttons share the "ornate medieval button" silhouette.
      const cornerGfx = this.add.graphics().setDepth(54);
      const drawCorner = (cx_: number, cy_: number, dx: number, dy: number): void => {
        cornerGfx.fillStyle(accentBase, 1);
        cornerGfx.fillTriangle(cx_, cy_, cx_ + 8 * dx, cy_, cx_, cy_ + 8 * dy);
      };
      const x0 = pillX - pillW / 2 + 2;
      const x1 = pillX + pillW / 2 - 2;
      const y0 = pillY - pillH / 2 + 2;
      const y1 = pillY + pillH / 2 - 2;
      drawCorner(x0, y0, +1, +1);
      drawCorner(x1, y0, -1, +1);
      drawCorner(x0, y1, +1, -1);
      drawCorner(x1, y1, -1, -1);
      // ── Stay glyph — a circular arrow ("repeat") drawn with Phaser
      // graphics so it mirrors the Continue button's drawn-glyph treatment
      // (no emoji, no system-font glyph fallback to dodge). Sits at the top
      // of the pill above the label.
      const glyphGfx = this.add.graphics().setDepth(54);
      const gx = pillX, gy = pillY - 38;
      const radius = 14;
      // Outer arc — three-quarter ring, leaving the bottom-right open for
      // the arrow head.
      glyphGfx.lineStyle(3, accentBase, 1);
      glyphGfx.beginPath();
      glyphGfx.arc(gx, gy, radius, Phaser.Math.DegToRad(45), Phaser.Math.DegToRad(360 + 0), false);
      glyphGfx.strokePath();
      // Arrow head pointing back into the arc opening — three small
      // triangles so it reads as "loop back".
      const headX = gx + radius * Math.cos(Phaser.Math.DegToRad(45));
      const headY = gy + radius * Math.sin(Phaser.Math.DegToRad(45));
      glyphGfx.fillStyle(accentBase, 1);
      glyphGfx.fillTriangle(
        headX - 4, headY - 8,
        headX + 8, headY,
        headX - 4, headY + 8,
      );
      // Inner highlight stripe so the arrow reads against the dark fill.
      glyphGfx.lineStyle(1, 0xffe8a0, 0.7);
      glyphGfx.beginPath();
      glyphGfx.arc(gx, gy, radius - 2, Phaser.Math.DegToRad(45), Phaser.Math.DegToRad(360), false);
      glyphGfx.strokePath();

      // Decorative divider between glyph and label.
      const divider = this.add.rectangle(pillX, pillY + 4, pillW - 20, 1, 0xffe8a0, canStay ? 0.55 : 0.15).setDepth(54);
      const titleT = this.add.text(pillX, pillY + 22, 'STAY', {
        fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: labelColorBase, fontStyle: 'bold',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(54);
      // Sub-text — multiplier preview when active, disabled reason otherwise.
      const subText = canStay
        ? `+${nextEnemyPct}% mobs\n+${nextHpPct}% HP\n+${nextAtkPct}% ATK`
        : boonsForZoneClear.cannotStay
          ? 'No Turning\nBack active'
          : 'unavailable\nStage 1';
      const subT = this.add.text(pillX, pillY + 50, subText, {
        fontSize: '7px', fontFamily: 'Georgia, serif',
        color: canStay ? '#8a6420' : '#2a1808', align: 'center', lineSpacing: 1,
      }).setOrigin(0.5).setDepth(54);
      this.zoneClearedPopup.push(stayBg, innerOutline, cornerGfx, glyphGfx, divider, titleT, subT);
      if (canStay) {
        // Hover explainer — was unclear to new players (read as "retry"
        // / "go back"). Spell out what Stay actually does: same stage
        // again, harder mobs, more loot, but the stage doesn't advance.
        let stayTip: Phaser.GameObjects.Text | null = null;
        const onStayOver = (): void => {
          stayBg.setFillStyle(0x2a1810, 1); stayBg.setStrokeStyle(2, 0xffd060);
          innerOutline.setStrokeStyle(1, 0xffe8a0, 0.85);
          titleT.setColor('#ffe8a0');
          stayTip?.destroy();
          stayTip = this.add.text(pillX, pillY + pillH / 2 + 8,
            'Stay re-runs THIS stage, not the next.\nMobs scale up, loot scales up.\nStage number does NOT advance.', {
              fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
              backgroundColor: '#0d0a0e', padding: { x: 8, y: 5 }, lineSpacing: 3,
              align: 'center',
            }).setOrigin(0.5, 0).setDepth(60).setResolution(2);
          this.zoneClearedPopup.push(stayTip);
        };
        const onStayOut = (): void => {
          stayBg.setFillStyle(fillBase, 0.97); stayBg.setStrokeStyle(2, accentBase);
          innerOutline.setStrokeStyle(1, labelHexBase, 0.45);
          titleT.setColor(labelColorBase);
          if (stayTip) {
            const idx = this.zoneClearedPopup.indexOf(stayTip);
            if (idx >= 0) this.zoneClearedPopup.splice(idx, 1);
            stayTip.destroy();
            stayTip = null;
          }
        };
        stayBg.on('pointerover', onStayOver).on('pointerout', onStayOut).on('pointerdown', () => this.handleRetreat());
      }
    }

    // Contextual info anchored to the bottom of the canvas (full-screen layout).
    if (isInferno) {
      const advHint = this.add.text(cx, height - 36, `Inferno Zone ${this.infernoZone + 1} — hotter & deadlier`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#8a4020', align: 'center',
      }).setOrigin(0.5).setDepth(52);
      this.zoneClearedPopup.push(advHint);
    } else {
      // DESCEND INTO HELL — available every ~10 levels starting at 39 (Ashen zones), not already inferno-only.
      // Anchored near the bottom of the screen so it doesn't compete with the
      // crossroads cards in the centre.
      const curLevel = this.gsm.player.currentLevel;
      const isAshenEntry = !this.gsm.player.infernoOnly && curLevel >= 39
        && Math.floor((curLevel - 39) / 2) % 5 === 0;
      if (isAshenEntry) {
        const descY = height - 50;
        const descBtnBg = this.add.rectangle(cx, descY, 260, 30, 0x1a0000, 1)
          .setStrokeStyle(2, 0xcc2200).setDepth(53).setInteractive({ useHandCursor: true });
        const descBtn = this.add.text(cx, descY, '— DESCEND INTO HELL —', {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(54);
        const onDescOver = (): void => { descBtn.setColor('#ff4020'); descBtnBg.setStrokeStyle(2, 0xff4020); };
        const onDescOut  = (): void => { descBtn.setColor('#cc2200'); descBtnBg.setStrokeStyle(2, 0xcc2200); };
        descBtnBg.on('pointerover', onDescOver).on('pointerout', onDescOut)
          .on('pointerdown', () => this.showInfernoDescentConfirm());
        const descHint = this.add.text(cx, descY - 22, 'No return. All heroes enter. Loot +50%.', {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#6a1a00', align: 'center',
        }).setOrigin(0.5).setDepth(54);
        this.zoneClearedPopup.push(descBtnBg, descBtn, descHint);
      }
    }

  }

  private showInfernoDescentConfirm(): void {
    // Close zone cleared popup
    for (const obj of this.zoneClearedPopup) obj.destroy();
    this.zoneClearedPopup = [];

    const width = GAME_W, height = GAME_H;
    const cx = width / 2; const cy = height / 2;
    const panel: Phaser.GameObjects.GameObject[] = [];

    panel.push(this.add.rectangle(cx, cy, width, height, 0x050100, 0.85).setDepth(60).setInteractive());
    panel.push(this.add.rectangle(cx, cy, 400, 260, 0x120204, 0.98).setStrokeStyle(2, 0xcc2200).setDepth(61));
    panel.push(this.add.rectangle(cx, cy, 394, 254, 0, 0).setStrokeStyle(1, 0xff4020, 0.25).setDepth(61));

    // Drawn flame accents top corners
    const fg = this.add.graphics().setDepth(62);
    for (const [ox, flip] of [[-170, 1], [170, -1]] as [number, number][]) {
      fg.fillStyle(0x5a0e00, 0.9); fg.fillTriangle(cx+ox, cy-120, cx+ox-8*flip, cy-104, cx+ox+8*flip, cy-104);
      fg.fillStyle(0x8b1800, 0.8); fg.fillTriangle(cx+ox, cy-117, cx+ox-5*flip, cy-105, cx+ox+5*flip, cy-105);
    }
    panel.push(fg);

    panel.push(this.add.text(cx, cy - 108, 'THE GATES OF HELL AWAIT', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#8b1a00', fontStyle: 'bold',
      stroke: '#1a0000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(62));

    panel.push(this.add.text(cx, cy - 80, '— ✦ —', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#6b1800',
    }).setOrigin(0.5).setDepth(62));

    const flavorLines = [
      'The mortal realm grows pale against the fire below.',
      'Your heroes stand at the threshold — beyond these gates',
      'lies only ash, ruin, and the screams of the fallen.',
      '',
      'There is no return. Not in life. Not in death.',
      'Your souls are forfeit to the Inferno.',
    ];
    flavorLines.forEach((line, i) => {
      panel.push(this.add.text(cx, cy - 56 + i * 16, line, {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: line === '' ? '#6b1800' : '#7a3820', align: 'center',
      }).setOrigin(0.5).setDepth(62));
    });

    // YES — DESCEND
    const yesBg = this.add.rectangle(cx - 80, cy + 92, 140, 32, 0x2a0000, 0.98)
      .setStrokeStyle(2, 0xcc2200).setDepth(62).setInteractive({ useHandCursor: true });
    const yesTxt = this.add.text(cx - 80, cy + 92, 'CROSS THE THRESHOLD', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(63).setInteractive({ useHandCursor: true });
    const onYesOver = () => { yesBg.setFillStyle(0x3a0808); yesTxt.setColor('#ff4020'); };
    const onYesOut  = () => { yesBg.setFillStyle(0x2a0000); yesTxt.setColor('#cc2200'); };
    yesBg.on('pointerover', onYesOver).on('pointerout', onYesOut);
    yesTxt.on('pointerover', onYesOver).on('pointerout', onYesOut);
    const doDescend = () => {
      for (const obj of panel) obj.destroy();
      const entryLevel = this.gsm.player.currentLevel;
      this.gsm.enterPermanentInferno(entryLevel);
      this.cleanupBattle();
      this.scene.restart({ inferno: true, infernoZone: 1, infernoScore: 0, infernoEntryLevel: entryLevel });
    };
    yesBg.on('pointerdown', doDescend);
    yesTxt.on('pointerdown', doDescend);
    panel.push(yesBg, yesTxt);

    // NO — turn back
    const noBg = this.add.rectangle(cx + 80, cy + 92, 110, 32, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x3a2010).setDepth(62).setInteractive({ useHandCursor: true });
    const noTxt = this.add.text(cx + 80, cy + 92, 'Turn Back', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3820',
    }).setOrigin(0.5).setDepth(63).setInteractive({ useHandCursor: true });
    const onNoOver = () => { noBg.setFillStyle(0x1a1208); noTxt.setColor('#a07840'); };
    const onNoOut  = () => { noBg.setFillStyle(0x0d0a08); noTxt.setColor('#5a3820'); };
    noBg.on('pointerover', onNoOver).on('pointerout', onNoOut);
    noTxt.on('pointerover', onNoOver).on('pointerout', onNoOut);
    const doCancel = () => {
      for (const obj of panel) obj.destroy();
      // Re-show the zone cleared popup
      this.showZoneClearedPopup();
    };
    noBg.on('pointerdown', doCancel);
    noTxt.on('pointerdown', doCancel);
    panel.push(noBg, noTxt);
  }

  /** Zone-clear card → Shrine of Boons. Shows 3 random rolls from the boon
   *  pool as a modal overlay on top of the current zone-clear popup. Picking
   *  one adds it to player.activeBoons (max 2, oldest replaced) and then
   *  advances to the next stage like the regular Battle card. */
  private openShrine(): void {
    if (this.gameOver) return;
    // Tear down the zone-clear popup — the shrine replaces it in the UI stack.
    for (const obj of this.zoneClearedPopup) obj.destroy();
    this.zoneClearedPopup = [];

    const width = GAME_W, height = GAME_H;
    const cx = width / 2, cy = height / 2;
    const elements: Phaser.GameObjects.GameObject[] = [];

    // Backdrop (blocks clicks to the scene below)
    const overlay = this.add.rectangle(cx, cy, width, height, 0x050402, 0.85)
      .setDepth(70).setInteractive();
    elements.push(overlay);

    // Panel
    const panelW = 620, panelH = 340;
    const bg = this.add.rectangle(cx, cy, panelW, panelH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0xc8973a).setDepth(71);
    elements.push(bg);

    // Title + subtitle
    const title = this.add.text(cx, cy - panelH / 2 + 24, '✦  SHRINE OF BOONS  ✦', {
      fontSize: '18px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#f0c040', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(72);
    elements.push(title);
    const sub = this.add.text(cx, cy - panelH / 2 + 48, 'Choose one — trades last for the rest of the run (max 2 active)', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(72);
    elements.push(sub);

    // 3 offering cards
    const offering: BoonDef[] = rollShrineOffering(3);
    const cardW = 180, cardH = 230, cardGap = 16;
    const totalW = 3 * cardW + 2 * cardGap;
    const startX = cx - totalW / 2 + cardW / 2;
    const cardY = cy + 8;

    offering.forEach((boon, i) => {
      const ccx = startX + i * (cardW + cardGap);
      const isCurse = boon.tier === 'curse';
      const accent = isCurse ? 0x8b2000 : 0x5a3a80;
      const accentHover = isCurse ? 0xcc4020 : 0x8a6a00;

      const cardBg = this.add.rectangle(ccx, cardY, cardW, cardH, 0x1a1108, 1)
        .setStrokeStyle(2, accent).setDepth(72).setInteractive({ useHandCursor: true });
      elements.push(cardBg);

      // Tier tag along the top edge — BLESSING gold, CURSE red
      const tagColor = isCurse ? '#ef4444' : '#f0c040';
      const tierTag = this.add.text(ccx, cardY - cardH / 2 + 12, isCurse ? '💀 CURSE' : '✦ BLESSING', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: tagColor, fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(73);
      elements.push(tierTag);

      // Runic glyph — drawn via Phaser Graphics so the shrine card vibe is
      // "summoning circle" rather than "flat pixel gem." Picks an inner symbol
      // from the boon's dominant effect (sword / heart / star / …). Outer ring
      // is gold for blessings, red for curses.
      const runeGfx = this.add.graphics().setDepth(73);
      drawBoonRune(runeGfx, ccx, cardY - 54, boon, 40);
      elements.push(runeGfx);

      // Name
      const nameT = this.add.text(ccx, cardY - 10, boon.name, {
        fontSize: '13px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
        wordWrap: { width: cardW - 16 }, align: 'center',
      }).setOrigin(0.5).setDepth(73);
      elements.push(nameT);

      // Description
      const descT = this.add.text(ccx, cardY + 30, boon.description, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#c8973a',
        wordWrap: { width: cardW - 20 }, align: 'center', lineSpacing: 3,
      }).setOrigin(0.5, 0).setDepth(73);
      elements.push(descT);

      const onOver = () => { cardBg.setFillStyle(0x2a1810, 1); cardBg.setStrokeStyle(2, accentHover); };
      const onOut  = () => { cardBg.setFillStyle(0x1a1108, 1); cardBg.setStrokeStyle(2, accent); };
      cardBg.on('pointerover', onOver).on('pointerout', onOut);
      cardBg.on('pointerdown', () => {
        // Confirmation beat: card scale-pulse + sparkle burst + accent ring,
        // SFX, then the actual apply + advance fires after a short delay so
        // the player sees the pick land before the popup tears down.
        this.vfx.playTomePickFlash(cardBg, ccx, cardY, accent);
        this.sfx.play('boon_choice', { volume: 0.75 });
        // Apply the boon, clean up, advance.
        if (!this.gsm.player.activeBoons) this.gsm.player.activeBoons = [];
        // v2 talent: Boon Hoarder — +1 active boon slot.
        const boonCap = 2 + getPassiveBonuses(this.gsm.player.passiveTree ?? {}).extraBoonSlot;
        applyBoon(this.gsm.player.activeBoons, boon.id, boonCap);
        this.invalidateBoonCache();
        this.gsm.save();
        this.time.delayedCall(420, () => {
          for (const el of elements) el.destroy();
          this.showDamageNumber(GAME_W / 2, GAME_H / 2, `✦ ${boon.name} accepted`, isCurse ? '#ef4444' : '#f0c040', '16px');
          this.time.delayedCall(300, () => this.handleAdvance());
        });
      });
    });

    // Skip button — close the shrine without picking (back to a regular Battle advance)
    const skipY = cy + panelH / 2 - 24;
    const skipBtn = this.add.text(cx, skipY, '[ Turn from the shrine — take the road ]', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(73).setInteractive({ useHandCursor: true });
    skipBtn.on('pointerover', () => skipBtn.setColor('#c8973a'));
    skipBtn.on('pointerout',  () => skipBtn.setColor('#8a6420'));
    skipBtn.on('pointerdown', () => {
      for (const el of elements) el.destroy();
      this.handleAdvance();
    });
    elements.push(skipBtn);
  }

  /** Zone-clear card → Gilded Altar. Opens a modal with 3 tier buttons
   *  (Modest / Devout / Grand) priced at `mysteryPrice × 3/10/50`. Cost doubles
   *  per repeat within the same visit so gold → power stays bounded. Each
   *  offering rolls one reward from the tier's pool and stamps it onto the
   *  run as a pure-upside "boon" via the existing engine (same HUD, same
   *  stat wiring, no curse side). */
  private openGildedAltar(): void {
    if (this.gameOver) return;
    for (const obj of this.zoneClearedPopup) obj.destroy();
    this.zoneClearedPopup = [];

    const width = GAME_W, height = GAME_H;
    const cx = width / 2, cy = height / 2;
    const elements: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(cx, cy, width, height, 0x050402, 0.88)
      .setDepth(70).setInteractive();
    elements.push(overlay);

    // Tighter panel — was 540×380 with a lot of dead space at the bottom.
    // Layout 560×340: title/subtitle/gold readout up top, an "Active
    // blessings" strip below the gold readout (so the player can audit
    // which boons they've already collected before committing more gold),
    // larger tier cards with per-tier ornaments in the middle, and leave
    // button flush with the bottom edge. Inner gold border adds altar flavour.
    const panelW = 560, panelH = 340;
    const bg = this.add.rectangle(cx, cy, panelW, panelH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0xc8a050).setDepth(71);
    elements.push(bg);
    const innerBorder = this.add.rectangle(cx, cy, panelW - 10, panelH - 10, 0, 0)
      .setStrokeStyle(1, 0xffd060, 0.25).setDepth(71);
    elements.push(innerBorder);

    const title = this.add.text(cx, cy - panelH / 2 + 18, '⚜  THE GILDED ALTAR  ⚜', {
      fontSize: '17px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#ffd060', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(72);
    elements.push(title);

    const subText = this.add.text(cx, cy - panelH / 2 + 38,
      'Offer gold for a random blessing — each tier\'s cost doubles per repeat.', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(72);
    elements.push(subText);

    // Per-visit repeat counters — start at 0 and tick up when the player picks
    // the same tier again. Used by the cost formula `base × 2^counter`.
    const repeatCounts: Record<AltarTier, number> = { modest: 0, devout: 0, grand: 0 };

    const mysteryPrice = this.gsm.getMysteryItemPrice();
    type TierCardDef = { tier: AltarTier; label: string; accent: number; baseMult: number };
    const tierDefs: TierCardDef[] = [
      { tier: 'modest', label: 'Modest Offering', accent: 0x8a6a20, baseMult: ALTAR_TIER_MULT.modest },
      { tier: 'devout', label: 'Devout Offering', accent: 0xc89040, baseMult: ALTAR_TIER_MULT.devout },
      { tier: 'grand',  label: 'Grand Offering',  accent: 0xffd060, baseMult: ALTAR_TIER_MULT.grand  },
    ];

    // Gold readout — big gold number so it reads as the key budget line.
    const goldText = this.add.text(cx, cy - panelH / 2 + 60, `◉ ${this.gsm.player.resources.gold} gold`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#fbbf24', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(72);
    elements.push(goldText);

    // Active blessings strip — small icon+name pills for every boon already
    // applied to this run, hover surfaces the full description so the player
    // can audit what they've collected before committing more gold. Re-built
    // by `refreshActiveBlessings` after each commit so newly-applied boons
    // appear inline without closing the popup.
    const ACTIVE_STRIP_Y = cy - panelH / 2 + 92;
    const activeStripObjs: Phaser.GameObjects.GameObject[] = [];
    let activeBlessingsTip: Phaser.GameObjects.Text | null = null;
    const refreshActiveBlessings = (): void => {
      for (const obj of activeStripObjs) obj.destroy();
      activeStripObjs.length = 0;
      activeBlessingsTip?.destroy(); activeBlessingsTip = null;
      const active = this.gsm.player.activeBoons ?? [];
      const label = this.add.text(cx, ACTIVE_STRIP_Y - 11,
        active.length === 0 ? 'No active blessings yet — pick a tier below' : 'Active blessings · hover for details',
        {
          fontSize: '9px', fontFamily: 'Georgia, serif', fontStyle: 'italic',
          color: active.length === 0 ? '#6a5028' : '#a07840',
        },
      ).setOrigin(0.5).setDepth(73);
      activeStripObjs.push(label);
      elements.push(label);
      if (active.length === 0) return;
      // Render each active boon as a framed icon tile (chiselled bronze
      // niche), not a truncated-text pill. Identity comes from the icon
      // image; the full name + description live on hover. Matches the
      // "stone tablet" picker aesthetic so the altar popup reads as a
      // single visual family.
      const tileSize = 32;
      const tileGap = 8;
      const totalW = active.length * tileSize + (active.length - 1) * tileGap;
      const startX = cx - totalW / 2 + tileSize / 2;
      active.forEach((ab, i) => {
        const def = getBoonOrAltarDef(ab.id);
        if (!def) return;
        const px = startX + i * (tileSize + tileGap);
        const py = ACTIVE_STRIP_Y + 10;
        // Frame: chiselled stone niche with double bronze border.
        const frame = this.add.graphics().setDepth(73);
        frame.fillStyle(0x080507, 1);
        frame.fillRoundedRect(px - tileSize / 2, py - tileSize / 2, tileSize, tileSize, 3);
        frame.lineStyle(1, 0x8a6420, 0.95);
        frame.strokeRoundedRect(px - tileSize / 2, py - tileSize / 2, tileSize, tileSize, 3);
        frame.lineStyle(0.5, 0xb38a5a, 0.55);
        frame.strokeRoundedRect(px - tileSize / 2 + 2, py - tileSize / 2 + 2, tileSize - 4, tileSize - 4, 2);
        activeStripObjs.push(frame);
        elements.push(frame);
        // Icon image — fall back to a glyph if the texture is missing.
        let iconObj: Phaser.GameObjects.Image | Phaser.GameObjects.Text;
        if (def.iconKey && this.textures.exists(def.iconKey)) {
          iconObj = this.add.image(px, py, def.iconKey).setDisplaySize(tileSize - 8, tileSize - 8).setDepth(74);
        } else {
          iconObj = this.add.text(px, py, '✦', {
            fontSize: '18px', fontFamily: 'Georgia, serif', color: '#d4a84a', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(74);
        }
        activeStripObjs.push(iconObj);
        elements.push(iconObj);
        // Stack badge — small bronze pill in the bottom-right corner if
        // the boon has accumulated stacks (e.g. Inner Flame, Blood Price).
        if (ab.stacks && ab.stacks > 1) {
          const badgeBg = this.add.graphics().setDepth(75);
          const bw = 16, bh = 11;
          const bx = px + tileSize / 2 - bw / 2 + 1;
          const by = py + tileSize / 2 - bh / 2 + 1;
          badgeBg.fillStyle(0x2a1d10, 0.95);
          badgeBg.fillRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, 2);
          badgeBg.lineStyle(1, 0x8a6420, 1);
          badgeBg.strokeRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, 2);
          activeStripObjs.push(badgeBg);
          elements.push(badgeBg);
          const badgeT = this.add.text(bx, by, String(ab.stacks), {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#d4a84a', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(76).setResolution(2);
          activeStripObjs.push(badgeT);
          elements.push(badgeT);
        }
        // Invisible hit zone covers the whole tile so hover works whether
        // the texture loaded or not.
        const hit = this.add.rectangle(px, py, tileSize, tileSize, 0x000000, 0)
          .setDepth(77).setInteractive({ useHandCursor: false });
        activeStripObjs.push(hit);
        elements.push(hit);
        hit.on('pointerover', () => {
          activeBlessingsTip?.destroy();
          activeBlessingsTip = this.add.text(px, py - tileSize / 2 - 4, `${def.name}\n${def.description}`, {
            fontSize: '10px', fontFamily: 'Georgia, serif', color: '#ffd060',
            backgroundColor: '#0d0a08', padding: { x: 8, y: 5 }, lineSpacing: 3,
            align: 'center', wordWrap: { width: 220 },
          }).setOrigin(0.5, 1).setDepth(80);
          elements.push(activeBlessingsTip);
          // Subtle lift on the icon so the hovered tile reads as "active".
          (iconObj as any).setScale?.(1.1);
        });
        hit.on('pointerout', () => {
          activeBlessingsTip?.destroy();
          activeBlessingsTip = null;
          (iconObj as any).setScale?.(1.0);
        });
      });
    };

    // Roll-result banner — hidden until a tier is picked. Positioned between
    // the tier cards (y ≈ cy + 30) and the leave button at the bottom, so it
    // visibly confirms the pick without burying the tier row. Single line to
    // keep vertical footprint small.
    const resultText = this.add.text(cx, cy + panelH / 2 - 48, '', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#ffd060', fontStyle: 'bold',
      align: 'center', wordWrap: { width: panelW - 60 },
    }).setOrigin(0.5).setDepth(72);
    elements.push(resultText);

    const tierRefs: Array<{
      tier: AltarTier; bg: Phaser.GameObjects.Rectangle;
      labelT: Phaser.GameObjects.Text; priceT: Phaser.GameObjects.Text;
      baseMult: number; accent: number;
    }> = [];

    // Bigger tier cards (170×110 vs old 150×80) so per-tier ornaments have
    // room to breathe. Centred vertically between the header strip and the
    // result banner. Ornaments scale: Modest = baseline bronze, Devout = gold
    // corner pips + accent stripe, Grand = bright gold halo + 4 gem dots +
    // slow shimmer tween.
    const tierCardW = 170, tierCardH = 110, tierGap = 14;
    const totalTierW = 3 * tierCardW + 2 * tierGap;
    const tierStartX = cx - totalTierW / 2 + tierCardW / 2;
    const tierY = cy + 12;

    // v2 talent: Pilgrim's Tithe — discount on Gilded Altar tier costs.
    const altarDiscount = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).shrineDiscountPct;
    const altarMult = Math.max(0.1, 1 - altarDiscount);
    const refreshCosts = (): void => {
      goldText.setText(`◉ ${this.gsm.player.resources.gold} gold`);
      for (const ref of tierRefs) {
        const cost = Math.floor(mysteryPrice * ref.baseMult * Math.pow(2, repeatCounts[ref.tier]) * altarMult);
        const afford = (this.gsm.player.resources.gold ?? 0) >= cost;
        ref.priceT.setText(`${cost} gold`);
        ref.priceT.setColor(afford ? '#fbbf24' : '#6b4020');
        ref.bg.setAlpha(afford ? 1 : 0.5);
        ref.bg.setStrokeStyle(2, afford ? ref.accent : 0x3a2810);
      }
    };

    tierDefs.forEach((td, i) => {
      const tcx = tierStartX + i * (tierCardW + tierGap);
      const cost = Math.floor(mysteryPrice * td.baseMult);
      const cardBg = this.add.rectangle(tcx, tierY, tierCardW, tierCardH, 0x1a1108, 1)
        .setStrokeStyle(2, td.accent).setDepth(72).setInteractive({ useHandCursor: true });
      elements.push(cardBg);

      // ── Per-tier ornaments ─────────────────────────────────────────────
      // Each escalation adds details on top of the previous tier, so Grand
      // visibly reads as the premium offering vs Modest's bare card.
      const orn = this.add.graphics().setDepth(73);
      const L = tcx - tierCardW / 2, T = tierY - tierCardH / 2;
      const R = L + tierCardW, B = T + tierCardH;
      if (td.tier === 'devout' || td.tier === 'grand') {
        // Gold corner pips (devout + grand)
        orn.fillStyle(0xffd060, 0.95);
        orn.fillCircle(L + 4, T + 4, 2);
        orn.fillCircle(R - 4, T + 4, 2);
        orn.fillCircle(L + 4, B - 4, 2);
        orn.fillCircle(R - 4, B - 4, 2);
        // Horizontal accent line just under the tier label
        orn.lineStyle(1, 0xffd060, 0.55);
        orn.beginPath();
        orn.moveTo(tcx - 40, tierY - 28); orn.lineTo(tcx + 40, tierY - 28);
        orn.strokePath();
      }
      if (td.tier === 'grand') {
        // Ornate corner diamonds (replacing the simple pips)
        orn.fillStyle(0xff9020, 1);
        orn.fillTriangle(L + 6, T + 2, L + 2, T + 6, L + 10, T + 6);
        orn.fillTriangle(L + 6, T + 10, L + 2, T + 6, L + 10, T + 6);
        orn.fillTriangle(R - 6, T + 2, R - 2, T + 6, R - 10, T + 6);
        orn.fillTriangle(R - 6, T + 10, R - 2, T + 6, R - 10, T + 6);
        // Outer halo — larger stroke rect beyond the card edge with slow pulse.
        const halo = this.add.graphics().setDepth(71);
        halo.lineStyle(3, 0xffb020, 0.35);
        halo.strokeRect(L - 4, T - 4, tierCardW + 8, tierCardH + 8);
        halo.lineStyle(1.5, 0xffe8a0, 0.5);
        halo.strokeRect(L - 1, T - 1, tierCardW + 2, tierCardH + 2);
        const pulseTw = this.tweens.add({
          targets: halo, alpha: { from: 0.35, to: 0.85 },
          duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
        elements.push(halo);
        // Dispose of the tween when the halo is destroyed so it doesn't leak.
        halo.on('destroy', () => { pulseTw.stop(); pulseTw.remove(); });
      }
      elements.push(orn);

      const labelT = this.add.text(tcx, tierY - 40, td.label, {
        fontSize: '13px', fontFamily: 'Georgia, serif', color: '#ffd060', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(74);
      elements.push(labelT);
      const tierTag = this.add.text(tcx, tierY - 16, td.tier === 'grand' ? '✦ Divine ✦' : td.tier === 'devout' ? '✧ Devout ✧' : 'Modest', {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: td.tier === 'grand' ? '#ffe8a0' : td.tier === 'devout' ? '#e8c060' : '#a07840',
        fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(74);
      elements.push(tierTag);
      const priceT = this.add.text(tcx, tierY + 22, `${cost} gold`, {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: '#fbbf24', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(74);
      elements.push(priceT);
      const hintT = this.add.text(tcx, tierY + 42,
        td.tier === 'grand' ? 'Divine blessing' : td.tier === 'devout' ? 'Greater blessing' : 'Lesser blessing', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#8a7040', fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(74);
      elements.push(hintT);
      tierRefs.push({ tier: td.tier, bg: cardBg, labelT, priceT, baseMult: td.baseMult, accent: td.accent });

      cardBg.on('pointerover', () => cardBg.setFillStyle(0x2a1810, 1));
      cardBg.on('pointerout', () => cardBg.setFillStyle(0x1a1108, 1));
      cardBg.on('pointerdown', () => {
        // Apply Pilgrim's Tithe discount to the actual cost paid (mirrors refreshCosts).
        const currentCost = Math.floor(mysteryPrice * td.baseMult * Math.pow(2, repeatCounts[td.tier]) * altarMult);
        if ((this.gsm.player.resources.gold ?? 0) < currentCost) {
          resultText.setText(`Not enough gold (need ${currentCost}).`);
          resultText.setColor('#ef4444');
          return;
        }
        // Spend gold, roll reward, apply to activeBoons, update UI.
        this.gsm.player.resources.gold -= currentCost;
        repeatCounts[td.tier]++;
        const reward = rollAltarReward(td.tier);
        if (!this.gsm.player.activeBoons) this.gsm.player.activeBoons = [];
        // v2 talent: Boon Hoarder — +1 active boon slot.
        const altarBoonCap = 2 + getPassiveBonuses(this.gsm.player.passiveTree ?? {}).extraBoonSlot;
        applyAltarReward(this.gsm.player.activeBoons, reward.id, altarBoonCap);
        // Mark this stage as having consumed its altar so the card disappears
        // from this popup (and any subsequent Stay re-clears at the same
        // stage). User-reported bug: clicking altar used to auto-advance
        // AND made the altar re-pop on next Stay if the player turned back —
        // now the altar is one-shot per stage and the choice loop returns
        // the player to the zone-cleared popup with full Stay/Advance options.
        if (!this.gsm.player.altarConsumedAtStages) this.gsm.player.altarConsumedAtStages = [];
        const curStageNow = this.gsm.player.currentLevel;
        if (!this.gsm.player.altarConsumedAtStages.includes(curStageNow)) {
          this.gsm.player.altarConsumedAtStages.push(curStageNow);
        }
        this.invalidateBoonCache();
        this.gsm.save();
        // Confirmation pulse on the picked tier card + boon-pick SFX so the
        // tier the player chose visibly lights up.
        this.vfx.playTomePickFlash(cardBg, cardBg.x, cardBg.y, td.accent);
        this.sfx.play('boon_choice', { volume: 0.75 });
        // Per-hero blessing burst — every deployed hero gets a gold-and-bronze
        // ring + spark burst at their position so the boon "applying" feels
        // grounded in the party, not just a popup readout. Rendered at depth
        // 75 so it shows above the popup overlay (heroes are otherwise
        // hidden behind the dim 0.88 alpha scrim while the altar is open).
        for (const hs of this.heroSprites) {
          if (!hs.active || hs.heroRef.permanentlyDead) continue;
          this.vfx.playAltarBlessing(hs.x, hs.y - 40);
        }
        resultText.setText(`✦ ${reward.name}\n${reward.description}`);
        resultText.setColor('#ffd060');
        refreshCosts();
        // Refresh the active-blessings strip so the freshly-applied boon
        // appears inline (and any FIFO-evicted boon disappears).
        refreshActiveBlessings();
      });
    });

    // Leave button — closes the altar and advances to the next stage.
    const leaveY = cy + panelH / 2 - 24;
    const leaveBtn = this.add.text(cx, leaveY, '[ Step back to the crossroads ]', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(73).setInteractive({ useHandCursor: true });
    leaveBtn.on('pointerover', () => leaveBtn.setColor('#c8973a'));
    leaveBtn.on('pointerout', () => leaveBtn.setColor('#8a6420'));
    leaveBtn.on('pointerdown', () => {
      for (const el of elements) el.destroy();
      // Don't auto-advance any more — return to the zone-cleared popup so
      // the player still gets the Stay / Advance / Path / event choice
      // (per user feedback: altar shouldn't steal the post-clear menu).
      // The altar card is suppressed on the rebuilt popup because this stage
      // is now in altarConsumedAtStages.
      this.showZoneClearedPopup();
    });
    elements.push(leaveBtn);

    refreshCosts();
    refreshActiveBlessings();
  }

  /** Zone-clear card → Elite Pack. Rolls 14-28 elites split across 2 waves plus
   *  2 distinct cross-zone boss-tier "pack leaders" (one per wave). Also flags
   *  2× XP for kills during the pack. Pack state lives in a static so it
   *  survives `scene.restart()`. */
  private startElitePack(): void {
    if (this.gameOver) return;
    const count = 14 + Math.floor(Math.random() * 15); // 14..28 inclusive
    const waveOneSize = Math.floor(count / 2);
    // Roll 2 distinct boss ids from the CURRENT BIOME's pool, respecting
    // story-quest gating (computeStoryBossGating already filters locked
    // bosses out). Previously this pulled from the flattened cross-biome
    // pool, which let a Gorgon spawn in Dark Forest etc. — user-reported
    // bug. Pack now stays biome-faithful: Dark Forest only spawns wolves,
    // Crypts only spawns Crypts bosses, etc.
    //
    // Magmarath the Undying (boss_lava_golem) stays excluded — he's a
    // quest boss with a unique reward chain (Magmarath's Ember sigil) and
    // shouldn't appear as a generic Elite Pack pick where the kill doesn't
    // advance the quest or grant the sigil.
    const { locked } = this.computeStoryBossGating();
    const zone = this.resolveActiveZone();
    let zoneBosses = getZoneBossIds(zone.name).filter((id) => !locked.has(id) && id !== 'boss_lava_golem');
    // Fallback: if the current biome has no eligible bosses (e.g. all
    // story-locked), expand to the cross-biome pool so the encounter still
    // ships rather than crashing or showing nothing.
    if (zoneBosses.length === 0) {
      zoneBosses = getAllBossDefIds().filter((id) => !locked.has(id) && id !== 'boss_lava_golem');
    }
    let boss1 = zoneBosses[Math.floor(Math.random() * zoneBosses.length)];
    let boss2 = zoneBosses[Math.floor(Math.random() * zoneBosses.length)];
    if (zoneBosses.length >= 2) {
      // Ensure the two bosses are different — retry until distinct (bounded).
      let attempts = 0;
      while (boss2 === boss1 && attempts < 10) {
        boss2 = zoneBosses[Math.floor(Math.random() * zoneBosses.length)];
        attempts++;
      }
    }
    BattleScene.elitePackPending = {
      count, wavesRemaining: 2, waveOneSize,
      bossIds: [boss1 ?? 'boss_spearman', boss2 ?? 'boss_wolf_lord'],
      xpMult: 2,
    };
    this.runEventInPlace();
  }

  /** Zone-clear card → Horde of Goblins (one-shot special event). Rolls mob +
   *  elite counts from stage (50/10 at stage 5, 75/15 at 30, 100/20 at 55+),
   *  stamps `hordePending`, and scene.restart so the new wave spawns the horde. */
  private startHorde(): void {
    if (this.gameOver) return;
    const stage = this.gsm.player.currentLevel;
    const mobCount   = Math.min(100, 50 + Math.max(0, stage - 5));
    const eliteCount = Math.min(20,  10 + Math.floor(Math.max(0, stage - 5) / 5));
    BattleScene.hordePending = {
      mobCount, eliteCount, xpMult: 2, spawnedMob: 0, spawnedElite: 0,
    };
    this.runEventInPlace();
  }

  /** Zone-clear card → Telurion's Lair (stage 30+, every 5 stages). Spawns
   *  N gold-tinted Sentinel of Telurion elites + 1 Telurion boss. First kill
   *  drops the Stormcoil Charm sigil; reward modal shows 10× gold of stage gold.
   *  eliteCount: 5 + 5 per 5-stage tier past 30 (stage 30 = 5, 35 = 10, ...). */
  private startTelurionsLair(): void {
    if (this.gameOver) return;
    const stage = this.gsm.player.currentLevel;
    const tier = Math.max(0, Math.floor((stage - 30) / 5));   // 0 at 30, 1 at 35, ...
    const eliteCount = 5 + tier * 5;                           // 5, 10, 15, 20, ...
    BattleScene.telurionPending = {
      eliteCount,
      spawnedElite: 0,
      bossSpawned: false,
    };
    this.runEventInPlace();
  }

  /** Build a single Horde-of-Goblins enemy instance. Goblin mooks stay at
   *  vanilla minion stats but get 2× move speed (they're the swarm layer).
   *  Horde elites take +100 % HP/ATK on top of the global elite multiplier
   *  AND 2.5× move speed — they read as the fast, scary anchors of the wave. */
  private generateHordeEnemy(isElite: boolean): EnemyInstance {
    const baseId = isElite
      ? 'goblin_elite'
      : Math.random() < 0.5 ? 'goblin_mook_a' : 'goblin_mook_b';
    const base = BASE_ENEMIES.find((e) => e.id === baseId)!;
    const inst = scaleEnemy(base, this.battleLevel, false);
    if (isElite) {
      // Horde elite: ×2 HP + ATK on top of ELITE_HP_MULT/ELITE_ATK_MULT already baked in.
      inst.maxHp     = Math.max(1, Math.floor(inst.maxHp     * 2));
      inst.currentHp = inst.maxHp;
      inst.attack    = Math.max(1, Math.floor(inst.attack    * 2));
      inst.speed     = inst.speed * 2.5;
    } else {
      inst.speed     = inst.speed * 2;
    }
    return inst;
  }

  /** Schedule the Horde of Goblins spawn stream. Total (mobs + elites) are
   *  trickled in over ~8 s, alternating left/right sides so the wave reads as
   *  a two-flank push. Elites are interleaved at a roughly-even cadence rather
   *  than front-loaded so the fight builds pressure instead of frontloading. */
  private startHordeWaveSpawn(): void {
    const hp = BattleScene.hordePending;
    if (!hp) return;
    const total = hp.mobCount + hp.eliteCount;
    if (total <= 0) return;
    const spawnWindowMs = 8000;
    const gap = Math.max(40, Math.floor(spawnWindowMs / total));
    // Pre-compute a shuffled schedule of "slots" where elites land, so the
    // elite cadence is deterministic (no RNG burst of 5 elites in the first
    // second). Even-ish distribution via stride.
    const eliteSlots = new Set<number>();
    if (hp.eliteCount > 0) {
      const stride = total / hp.eliteCount;
      for (let i = 0; i < hp.eliteCount; i++) eliteSlots.add(Math.floor(i * stride + stride / 2));
    }
    for (let i = 0; i < total; i++) {
      const isElite = eliteSlots.has(i);
      const side: 'left' | 'right' = i % 2 === 0 ? 'right' : 'left';
      this.time.delayedCall(i * gap, () => {
        // Safety gate — hordePending may have been cleared by a death / abandon
        // between schedule and fire. Skip the spawn in that case.
        if (!BattleScene.hordePending || this.gameOver) return;
        const inst = this.generateHordeEnemy(isElite);
        this.spawnEnemy(inst, side);
        if (isElite) BattleScene.hordePending.spawnedElite++;
        else         BattleScene.hordePending.spawnedMob++;
      });
    }
  }

  /** Grant the Horde of Goblins completion reward — the Goblin Kingpin trophy
   *  (legendary, party-wide +10% crit damage and +2 swap moves per wave) plus
   *  bonus gold, presented in a proper event-summary popup with the trophy
   *  icon, stats, and a Continue button. Returns the popup's onClose hook so
   *  the caller can chain the zone-cleared popup after the player dismisses. */
  private grantHordeReward(totalKilled: number): void {
    // Drop the trophy into the inventory.
    const level = this.gsm.player.currentLevel;
    const trophy = generateGoblinKingpin(level);
    this.gsm.player.inventory.push(trophy);
    this.gsm.player.newLootCount = (this.gsm.player.newLootCount ?? 0) + 1;
    // Gold reward — user request: ×5 again (was 30/goblin → now 150/goblin).
    // Total scales with stage organically because totalKilled grows with stage:
    //   stage 5  → 50 goblins × 150 = 7,500 gold
    //   stage 10 → 66 goblins × 150 = 9,900 gold
    //   stage 30 → 90 goblins × 150 = 13,500 gold
    //   stage 60 → 120 goblins × 150 = 18,000 gold
    const bonusGold = totalKilled * 150;
    this.gsm.player.resources.gold += bonusGold;
    this.runGold += bonusGold;
    // Gold-shower VFX at screen center, then stream a flight of gold coins
    // into the top-left gold counter — each coin arrival pulses the text so
    // the deposit reads as "the treasure flies into your purse."
    this.vfx.playGoldShower(GAME_W / 2, GAME_H / 2 + 30);
    const goldX = this.goldText.x + 10;
    const goldY = this.goldText.y;
    const pulse = (): void => {
      this.tweens.add({
        targets: this.goldText, scale: { from: 1, to: 1.25 }, duration: 80, yoyo: true, ease: 'Quad.easeOut',
      });
    };
    this.vfx.playGoldFlightTo(GAME_W / 2, GAME_H / 2 + 30, goldX, goldY, {
      count: 18, stagger: 50, onArrive: pulse,
    });
    this.sfx.play('levelup', { volume: 0.8 });
    this.showEventSummary({
      title: 'HORDE CLEARED',
      accentColor: 0xfde047,
      stats: [
        { label: 'Goblins slain',    value: `${totalKilled}`,        color: '#6ab04a' },
        { label: 'Gold earned',      value: `+${bonusGold}`,         color: '#fbbf24' },
        { label: 'Event',            value: 'Horde of Goblins',      color: '#a07840' },
      ],
      rewards: [{
        iconKey: 'icon_goblin_kingpin',
        name: 'Goblin Kingpin',
        rarity: 'legendary',
        subtitle: '+10% crit dmg · +2 swaps/wave',
      }],
      // Chain into the zone-cleared popup so the player can read the horde
      // summary first; the zone-cleared popup would otherwise stack on top
      // and hide it entirely.
      onClose: () => this.showZoneClearedPopup(),
    });
  }

  /** Telurion's Lair clear reward — ×10 gold (relative to standard stage gold)
   *  and a guaranteed Stormcoil Charm sigil drop. Mirrors the horde-event flow:
   *  gold-shower VFX, flying-coin deposit, summary modal with the sigil card. */
  private grantTelurionReward(eliteCount: number): void {
    const stage = this.gsm.player.currentLevel;
    // Telurion is a one-shot per run — flag it so the random-event roller
    // never offers it again until resetRun clears the flag.
    this.gsm.player.telurionClearedThisRun = true;
    // 10× the base "stage gold" — use the same per-stage anchor as elite-pack so
    // values feel consistent. Roughly: stage 30 ≈ 12k gold, stage 50 ≈ 24k.
    const stageGold = 600 * stage;
    const bonusGold = stageGold * 10;
    this.gsm.player.resources.gold += bonusGold;
    this.runGold += bonusGold;
    // Drop the Stormcoil Charm sigil into the player's bag.
    const sigil = generateSpecificSigil('stormcoil_charm');
    if (sigil) {
      this.gsm.player.sigilBag = this.gsm.player.sigilBag ?? [];
      this.gsm.player.sigilBag.push(sigil);
      this.gsm.player.newLootCount = (this.gsm.player.newLootCount ?? 0) + 1;
    }
    // Gold shower VFX + flying-coin deposit.
    this.vfx.playGoldShower(GAME_W / 2, GAME_H / 2 + 30);
    const goldX = this.goldText.x + 10;
    const goldY = this.goldText.y;
    const pulse = (): void => {
      this.tweens.add({
        targets: this.goldText, scale: { from: 1, to: 1.25 }, duration: 80, yoyo: true, ease: 'Quad.easeOut',
      });
    };
    this.vfx.playGoldFlightTo(GAME_W / 2, GAME_H / 2 + 30, goldX, goldY, {
      count: 22, stagger: 50, onArrive: pulse,
    });
    this.sfx.play('levelup', { volume: 0.85 });
    this.showEventSummary({
      title: "TELURION'S LAIR CLEARED",
      accentColor: 0xc89040,
      stats: [
        { label: 'Sentinels slain', value: `${eliteCount}`,           color: '#d4a84a' },
        { label: 'Dragon felled',   value: 'Telurion',                 color: '#80c8ff' },
        { label: 'Gold earned',     value: `+${bonusGold}`,            color: '#fbbf24' },
      ],
      rewards: [{
        iconKey: sigil?.special.iconKey ?? 'sigil_3',
        name: 'Stormcoil Charm',
        rarity: 'legendary',
        subtitle: 'Crits chain lightning · +5% nature resist',
      }],
      onClose: () => this.showZoneClearedPopup(),
    });
  }

  /** Malakhar death cinematic — runs in parallel with the standard enemy
   *  death-anim handler and the grantShadowlordReward gold/sigil flow.
   *  Layers a darkening overlay, a 3-line lore card, and a portal-opens
   *  stinger over the death scene so the kill reads as a story beat
   *  rather than just another boss-slot freeing up. Total run-time is
   *  ~3.5 s, all autocleans before the endless ticker reaches its 4.5 s
   *  grace bump. */
  private playMalakharDeathCinematic(): void {
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;
    // Black overlay — fades to ~70 % so the death anim still reads through it.
    const overlay = this.add.rectangle(cx, cy, GAME_W, GAME_H, 0x000000, 0).setDepth(45);
    this.tweens.add({ targets: overlay, alpha: 0.70, duration: 600, ease: 'Quad.easeOut' });
    // Three-line lore stack — bronze title, italic narration below.
    const TITLE = 'THE HOLLOW KING FALLS';
    const LINES = [
      "His last breath shudders through the rift —",
      'and the veil splits open behind him.',
    ];
    const title = this.add.text(cx, cy - 32, TITLE, {
      fontSize: '32px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#c8973a', fontStyle: 'bold', align: 'center',
    }).setOrigin(0.5).setDepth(46).setAlpha(0).setScale(0.85);
    this.tweens.add({ targets: title, alpha: 1, scale: 1, duration: 700, delay: 250, ease: 'Quad.easeOut' });
    const subTexts: Phaser.GameObjects.Text[] = [];
    for (let i = 0; i < LINES.length; i++) {
      const t = this.add.text(cx, cy + 6 + i * 22, LINES[i], {
        fontSize: '15px', fontFamily: 'Georgia, serif',
        color: '#b38a5a', fontStyle: 'italic', align: 'center',
      }).setOrigin(0.5).setDepth(46).setAlpha(0);
      this.tweens.add({ targets: t, alpha: 0.92, duration: 600, delay: 700 + i * 250 });
      subTexts.push(t);
    }
    // Fade everything out so the endless ticker comes up on a clean canvas.
    this.time.delayedCall(3300, () => {
      this.tweens.add({
        targets: [overlay, title, ...subTexts],
        alpha: 0, duration: 500, ease: 'Quad.easeIn',
        onComplete: () => {
          overlay.destroy();
          title.destroy();
          subTexts.forEach((t) => t.destroy());
        },
      });
    });
  }

  /** Shadowlord defeat → endless-mode handoff. Grants the first-kill
   *  Shadowrealm Sigil + a fixed gold reward, then leaves the scene
   *  running so the endless ghost ticker can take over. No advance/stay
   *  popup, no zone-clear card — the player is committed to the endless
   *  encounter until they wipe. Death in endless ends the run normally
   *  (gameOver flow runs through the same checkPartyWipe path). */
  private grantShadowlordReward(): void {
    const stage = this.gsm.player.currentLevel;
    // 15× the base "stage gold" — heavier than Telurion (10×) since this
    // is the gated final-pre-endless boss and the run won't see another
    // gold drop event after this point.
    const stageGold = 600 * stage;
    const bonusGold = stageGold * 15;
    this.gsm.player.resources.gold += bonusGold;
    this.runGold += bonusGold;
    // Drop the Shadowrealm Sigil (one-time, ungated by quest at first-kill).
    const sigil = generateSpecificSigil('shadowrealm_sigil');
    if (sigil) {
      this.gsm.player.sigilBag = this.gsm.player.sigilBag ?? [];
      this.gsm.player.sigilBag.push(sigil);
      this.gsm.player.newLootCount = (this.gsm.player.newLootCount ?? 0) + 1;
    }
    // Gold shower + flying-coin deposit. Re-uses Telurion's polish — same
    // VFX, same audio cue.
    this.vfx.playGoldShower(GAME_W / 2, GAME_H / 2 + 30);
    const goldX = this.goldText.x + 10;
    const goldY = this.goldText.y;
    const pulse = (): void => {
      this.tweens.add({
        targets: this.goldText, scale: { from: 1, to: 1.25 }, duration: 80, yoyo: true, ease: 'Quad.easeOut',
      });
    };
    this.vfx.playGoldFlightTo(GAME_W / 2, GAME_H / 2 + 30, goldX, goldY, {
      count: 28, stagger: 50, onArrive: pulse,
    });
    this.sfx.play('levelup', { volume: 0.85 });
    // No event-summary popup — going straight into endless. Surface the
    // sigil drop in the corner toast (fits the loot pipeline) and post a
    // mid-screen banner so the transition reads as deliberate.
    this.showAnnouncement('SHADOWREALM SIGIL ACQUIRED');
    this.time.delayedCall(1800, () => {
      this.showAnnouncement('ENDLESS — survive as long as you can');
    });
  }

  /** Run a one-time event (Elite Pack / Horde / Telurion) at the CURRENT
   *  stage without consuming the player's advance. After the event clears,
   *  the zone-cleared popup re-opens at the same stage with the same
   *  path/advance/stay options — the event is a side encounter, not a
   *  rotation. The pending event flag (`elitePackPending` etc.) was already
   *  set by the caller so the scene-init wave-pick will spawn the event. */
  private runEventInPlace(): void {
    if (this.gameOver) return;
    this.zoneClearedActive = false;
    BattleScene.persistedRunScore = this.runScore;
    BattleScene.persistedRunLoot = this.runLoot;
    // Reset the just-cleared zone's wave/boss state so the next scene init
    // starts wave 1 fresh with the pending-event flag — without this, the
    // init's "already cleared" check (battle.bossDefeated) re-shows the
    // zone-cleared popup on restart and the player has to click Advance to
    // make the event actually fire (user-reported "event needs an Advance
    // click to go" bug). After the event clears, the wave-clear flow flips
    // bossDefeated back to true so the zone-cleared popup re-opens at the
    // SAME stage as before — picking the event no longer steals the chance
    // to Stay / pick a path / fight the normal zone boss again.
    this.gsm.battle.bossDefeated = false;
    this.gsm.battle.waveNumber = 0;
    this.gsm.save(true);
    this.gsm.saveRunSession(0, this.runScore, false, 0, 0, false, this.runGold, this.runLoot, this.runLootSpent, this.runXp, this.runItems);
    this.cleanupBattle();
    this.scene.restart();
  }

  private handleAdvance(): void {
    if (this.gameOver) return;
    this.zoneClearedActive = false;
    if (this.infernoMode) {
      // Inferno: advance inferno zone, carry score — save session so F5 restores new zone
      this.gsm.saveRunSession(0, this.runScore, true, this.infernoZone + 1, this.infernoScore, false, this.runGold, this.runLoot, this.runLootSpent, this.runXp, this.runItems);
      this.cleanupBattle();
      this.scene.restart({ inferno: true, infernoZone: this.infernoZone + 1, infernoScore: this.infernoScore, infernoEntryLevel: this.infernoEntryLevel });
      return;
    }
    // Persist score + runes earned across zone transition
    BattleScene.persistedRunScore = this.runScore;
    BattleScene.persistedRunLoot = this.runLoot;
    this.gsm.advanceLevel();

    // ── Mastery evaluation on stage advance ─────────────────────────
    // Update the run's max-heroes-deployed snapshot AND fire stage-gate
    // checks. Toast for any newly-claimed feat so the player gets the
    // dopamine + sees the rune reward fly. These run BEFORE the inferno
    // branch above? No — the inferno branch returns, so this never fires
    // on inferno transitions, which is fine: the inferno-zone evaluator
    // covers that path separately.
    {
      const deployedCount = this.gsm.player.deployedHeroes.length;
      if (deployedCount > (this.gsm.player.runMaxHeroesDeployed ?? 1)) {
        this.gsm.player.runMaxHeroesDeployed = deployedCount;
      }
      const claimed = this.gsm.evaluateStageGate(this.gsm.player.currentLevel);
      for (const c of claimed) {
        this.showAnnouncement(`✦ MASTERY: ${c.name} — +${c.runes} ◆`);
      }
    }
    // Restart the zone clear-time stopwatch for the next zone.
    this.zoneStartedAtMs = Date.now();
    // Story-quest hook — fire `advance_stage` so reach_stage triggers (e.g.
    // The Sage's Pilgrimage beat 1) can advance on level-up. `stayCount`
    // included so `reach_stage_no_stay` triggers (e.g. Haste-tome unlock)
    // can verify a clean run.
    this.gsm.trackStoryQuestEvent({
      kind: 'advance_stage',
      newStage: this.gsm.player.currentLevel,
      stage: this.gsm.player.currentLevel,
      stayCount: this.gsm.player.stayCount ?? 0,
    });
    this.gsm.save(true);
    // Save session for the new level so a page reload before the first wave completes
    // still restores the accumulated run score (level already incremented by advanceLevel).
    this.gsm.saveRunSession(0, this.runScore, false, 0, 0, false, this.runGold, this.runLoot, this.runLootSpent, this.runXp, this.runItems);
    this.cleanupBattle();
    this.scene.restart();
  }

  private handleRetreat(): void {
    if (this.gameOver) return;
    this.zoneClearedActive = false;
    if (this.infernoMode) {
      // Inferno: "Stay" repeats the current inferno zone — save session so F5 restores same zone
      this.gsm.saveRunSession(0, this.runScore, true, this.infernoZone, this.infernoScore, false, this.runGold, this.runLoot, this.runLootSpent, this.runXp, this.runItems);
      this.cleanupBattle();
      this.scene.restart({ inferno: true, infernoZone: this.infernoZone, infernoScore: this.infernoScore, infernoEntryLevel: this.infernoEntryLevel });
      return;
    }
    // Persist score + runes earned across zone transition
    BattleScene.persistedRunScore = this.runScore;
    BattleScene.persistedRunLoot = this.runLoot;
    this.gsm.stayAtLevel();
    this.gsm.save(true);
    // Clear the session — stayAtLevel resets waveNumber to 0, so the old
    // session with waveNumber=5 must not persist (it would re-trigger zone-cleared on restart).
    this.gsm.clearRunSession();
    localStorage.removeItem(this.pendingTomesKey());
    this.cleanupBattle();
    this.scene.restart();
  }

  /**
   * The effective enemy level used for wave generation.
   * In Inferno mode, enemies start at base-game lv40 and scale +1 per inferno zone.
   * In normal mode, uses the player's current level.
   */
  private get battleLevel(): number {
    return this.infernoMode ? this.infernoEntryLevel + this.infernoZone - 1 : this.gsm.player.currentLevel;
  }

  /** Zone override passed to generateWaveEnemies. In inferno mode, the
   *  override pins the biome by inferno zone. Outside inferno, an active
   *  Crossroads path may force a different biome — fall through to
   *  `resolveActiveZone` so wave generation pulls enemies from the forced
   *  faction pool. */
  private get infernoZoneOverride(): import('@roe/shared').LevelZone | undefined {
    if (this.infernoMode) {
      return getZoneForInferno(this.infernoZone, this.infernoEntryLevel);
    }
    if (this.gsm.player.activeCrossroadsMod && this.gsm.player.crossroadsPathZoneId) {
      return this.resolveActiveZone();
    }
    return undefined;
  }

  private cleanupBattle(): void {
    // Revert any active temporary stat buffs before scene restart/shutdown
    // so inflated stats are never baked into the saved hero objects
    for (const hs of this.heroSprites) {
      const h = hs.heroRef as any;

      // Reset Leap Strike flag so it never stays stuck across scene restarts
      (hs as any)._leapStriking = false;

      // Berserk: clear flags and reset scale (stats not mutated, so no revert needed)
      if (h._berserkActive) {
        h._berserkActive = false;
        h._berserkCritDmgBuff = 0;
        h._berserkSpdMult = 1;
        hs.setScale(1.0);
      }

      // Undying Fury: revert doubled attack if still active
      if (h._furyActive && h._furyOriginalAttack !== undefined) {
        hs.heroRef.stats.attack = h._furyOriginalAttack;
        h._furyActive = false;
        h._furyOriginalAttack = undefined;
      }

      // Stone Form: revert defense bonus if still active
      if (h._stoneFormActive && h._stoneFormDefBonus) {
        hs.heroRef.stats.defense = Math.max(0, hs.heroRef.stats.defense - h._stoneFormDefBonus);
        h._stoneFormActive = false;
        h._stoneFormDefBonus = 0;
        h._stoneFormTimer = 0;
      }

      // Arcane Might / Elixir of Resistance / Bless: zero flags (flag-based — no stat mutations to revert)
      h._arcaneMightResistBuff = 0;
      h._resistanceBuff = 0;
      h._blessActive = false;
      h._hotActive_bless = false;
      h._hotActive_regrowth = false;
      h._resurrectAura = false;
      h._challengeResistTimer = 0;
      h._challengeWalking = false;
      h._challengeWalkTarget = null;
      // Eagle Eye crit flag: zero out so it never leaks into next wave/run
      h._eagleEyeCrit = 0;
    }
    this.zonePaused = false;
    this.zoneClearedText?.destroy();
    this.zoneClearedText = null;
    for (const obj of this.zoneClearedPopup) obj.destroy();
    this.zoneClearedPopup = [];
    for (const es of this.enemySprites) {
      es.hpBar?.destroy();
      es.hpBarBg?.destroy();
      es.shadow?.destroy();
      // Rot Bomber extras — name banner + toxic aura sprite.
      if (es.isRotBomber) {
        es.nameText?.destroy();
        (es.getData('rotAura') as Phaser.GameObjects.Graphics | undefined)?.destroy();
      }
    }
    // Clean up Goblino
    if (this.goblino) {
      this.goblino.hpBar?.destroy();
      this.goblino.hpBarBg?.destroy();
      this.goblino.shadow?.destroy();
      this.goblino.destroy();
      this.goblino = null;
      this.goblinoTimer = 0;
    }
  }

  // ==================== RESUME (menus no longer pause, but keep for equipment refresh) ====================

  resumeBattle(): void {
    // Guard against calling after scene shutdown
    // Allow both active (running) and paused states — isPaused() returns true when paused by menu
    if (!this.scene) return;
    const isRunning = this.scene.isActive('BattleScene') || this.scene.isPaused('BattleScene');
    if (!isRunning) return;

    // Queue any recruit-owed talent picks that appeared while battle was paused (ShopScene, etc.).
    // Only push entries that aren't already in the queue — compare against what's already pending
    // for each hero so repeated resumes don't double-queue.
    let queuedNewFromRecruit = false;
    for (let i = 0; i < this.gsm.player.heroes.length; i++) {
      const hero = this.gsm.player.heroes[i];
      const owed = hero.pendingRecruitTalents ?? 0;
      const queued = this.pendingTalentChoices.filter((c) => c.hero === hero).length;
      const missing = owed - queued;
      for (let k = 0; k < missing; k++) {
        this.pendingTalentChoices.push({ heroIndex: i, hero });
        queuedNewFromRecruit = true;
      }
    }
    // Fire the first pending popup immediately so the recruit's talent picker shows the moment
    // the Shop closes (not only on the next enemy kill). Deferred one tick so Phaser can finish
    // the resume lifecycle before we spawn popup elements.
    if (queuedNewFromRecruit && !this.talentChoiceActive && !this.tomeChoiceActive && !this.zoneClearedActive) {
      this.time.delayedCall(150, () => this.processNextTalentChoice());
    }

    // Resume update loop if it was paused by the pause menu
    if (this.scene.isPaused('BattleScene')) {
      this.scene.resume('BattleScene');
    }

    // Recalculate HP caps for all current sprites
    for (const hs of this.heroSprites) {
      if (!hs.heroRef) continue;
      try {
        const stats = this.effStats(hs.heroRef);
        hs.heroRef.stats.hp = Math.min(hs.heroRef.stats.hp, stats.maxHp);
      } catch { /* hero may have been removed */ }
    }

    // Sync sprites with current deployedHeroes list — inferno uses the full roster now
    // (previously capped at 1, which caused recruited heroes to disappear when re-entering
    // battle from the menu and left solo runs too weak to progress past INF-3).
    const deployedHeroes = this.gsm.getDeployedHeroes();

    // Remove sprites for heroes that are no longer deployed
    const spriteMap = new Map(this.heroSprites.map((hs) => [hs.heroRef, hs]));
    for (const [hero, hs] of spriteMap) {
      if (!deployedHeroes.includes(hero)) {
        if (hs.hpBar) hs.hpBar.destroy();
        if (hs.hpBarBg) hs.hpBarBg.destroy();
        if (hs.shieldGraphics) hs.shieldGraphics.destroy();
        if (hs.mpBar) hs.mpBar.destroy();
        if (hs.nameText) hs.nameText.destroy();
        if (hs.shadow) hs.shadow.destroy();
        if (hs.auraSprites) { for (const spr of Object.values(hs.auraSprites)) spr?.destroy(); hs.auraSprites = {}; }
        hs.destroy();
      }
    }

    // Rebuild heroSprites in deployedHeroes order so repositionHeroes assigns the correct arc slot.
    // Previously, filtered sprites kept their old array order and new sprites were appended —
    // causing mage/healer to end up at the forward (melee) arc index when a new hero was deployed.
    this.heroSprites = [];
    for (const hero of deployedHeroes) {
      if (!hero) continue;
      const existing = spriteMap.get(hero);
      if (existing) {
        this.heroSprites.push(existing);
      } else {
        // createHeroSprite appends the new sprite to this.heroSprites
        this.createHeroSprite(hero, HERO_ARC_CENTER_X, HERO_ARC_CENTER_Y);
      }
    }

    // Reposition all heroes into the arc
    this.repositionHeroes();
    this.refreshTrophyDisplay();
    // Redraw empty slot indicators (keeps + buttons in sync after hero deploy/undeploy)
    this.drawEmptyHeroSlots();
    // Rebuild portraits to reflect new deploy state
    this.createHeroPortraits();
  }

  // ==================== UTILITIES ====================

  /**
   * Play a boss/enemy attack animation for a spell cast, then return to idle/walk.
   * Used so bosses show an attack motion even when casting at range. Pass
   * `attackVariant` to play `_attack2` / `_attack3` etc. — needed for bosses
   * (Telurion) that use multiple attack sheets to alternate across spells.
   */
  private playSpellCastAnim(es: EnemySprite, attackVariant?: string): void {
    const wantedKey = attackVariant
      ? `${es.texture.key}_${attackVariant}`
      : `${es.texture.key}_attack`;
    const fallbackAtk = `${es.texture.key}_attack`;
    const atkKey   = this.anims.exists(wantedKey) ? wantedKey : fallbackAtk;
    const walkKey  = `${es.texture.key}_walk`;
    const idleKey  = `${es.texture.key}_idle`;
    const returnKey = this.anims.exists(idleKey) ? idleKey
                    : this.anims.exists(walkKey) ? walkKey
                    : null;
    if (this.anims.exists(atkKey) && es.anims.currentAnim?.key !== atkKey) {
      es.off('animationcomplete');
      es.play(atkKey);
      es.once('animationcomplete', () => {
        if (es.active && returnKey && this.anims.exists(returnKey)) es.play(returnKey);
      });
    }
  }

  private findNearestEnemy(hs: HeroSprite): EnemySprite | null {
    // Focus target always wins if it's still alive — heroes have no range
    // gate on `performHeroAttack`, so a back-row hero focusing a far enemy
    // still hits it. The previous 350 px guard caused focus to silently
    // fall back to "nearest" for heroes too far from the marker, which
    // read to the player as "focus didn't switch" on some heroes.
    const focused = this.getFocusedEnemy();
    if (focused) return focused;
    let nearest: EnemySprite | null = null;
    let minDist = Infinity;
    for (const es of this.enemySprites) {
      if (!es.active || es.enemyRef.currentHp <= 0) continue;
      const dx = es.x - hs.x;
      const dy = es.y - hs.y;
      const d = dx * dx + dy * dy;
      if (d < minDist) { minDist = d; nearest = es; }
    }
    return nearest;
  }

  /** Cast-range helper — true if any live enemy is within `range` px of
   *  the hero's current position. Used by the spell cast gate to skip
   *  out-of-range cast attempts (so mana isn't burned + CD doesn't tick
   *  on a fired spell that hits nothing). */
  private hasEnemyInRange(hs: HeroSprite, range: number): boolean {
    const r2 = range * range;
    for (const e of this.enemySprites) {
      if (!e.active || e.enemyRef.currentHp <= 0) continue;
      const dx = e.x - hs.x;
      const dy = e.y - hs.y;
      if (dx * dx + dy * dy <= r2) return true;
    }
    return false;
  }

  // ── Focus Target API ────────────────────────────────────────────────
  /** Wire up the focus-target click handler on a freshly-spawned enemy
   *  sprite. Left-click toggles focus; right-click is reserved for the
   *  global cancel-swap handler so we skip it here. Called from the
   *  main spawnEnemy path AND from event-mob spawn paths (Goblino,
   *  RotBomber) so every enemy on screen is clickable. */
  private wireFocusTargetClick(sprite: EnemySprite): void {
    // Tight body-only hit rect in texture-local frame coords (Phaser scales it
    // with displaySize). Default full-sprite bounds let bosses (230-290 px)
    // swallow clicks meant for adjacent heroes. 40 % width × 65 % height,
    // vertically anchored to cover the torso and skip the empty headroom +
    // feet shadow area — same shape as hero hitboxes at line ~5312.
    const fw = sprite.frame?.width ?? 128;
    const fh = sprite.frame?.height ?? 128;
    const hitW = Math.round(fw * 0.40);
    const hitH = Math.round(fh * 0.65);
    const hitX = Math.round((fw - hitW) / 2);
    const hitY = Math.round(fh * 0.25);
    sprite.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(hitX, hitY, hitW, hitH),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) return;
      this.toggleFocusTarget(sprite);
    });
  }

  /** Returns the live focused EnemySprite, or null if none / dead. Also
   *  auto-clears the marker if the target died since the last frame. */
  private getFocusedEnemy(): EnemySprite | null {
    if (!this.focusedEnemyId) return null;
    const es = this.enemySprites.find((e) => e.enemyRef.instanceId === this.focusedEnemyId) ?? null;
    if (!es || !es.active || es.enemyRef.currentHp <= 0) {
      // Target died / despawned — clean up the marker. Next frame's
      // findNearestEnemy reverts to plain "nearest" behavior.
      this.clearFocusTarget();
      return null;
    }
    return es;
  }

  /** Click handler entry point — sets the focus to this enemy, OR clears
   *  it if this enemy was already the focus (toggle behavior so the
   *  player can click-to-clear on the same target). */
  private toggleFocusTarget(es: EnemySprite): void {
    if (this.focusedEnemyId === es.enemyRef.instanceId) {
      this.clearFocusTarget();
    } else {
      this.setFocusTarget(es);
    }
  }

  private setFocusTarget(es: EnemySprite): void {
    this.focusedEnemyId = es.enemyRef.instanceId;
    this.ensureFocusMarker();
    this.updateFocusMarkerPosition();
  }

  private clearFocusTarget(): void {
    this.focusedEnemyId = null;
    if (this.focusMarker) {
      this.focusMarker.ring.destroy();
      this.focusMarker.label.destroy();
      this.focusMarker = null;
    }
  }

  /** Build the rotating gold ring + label if not already present. */
  private ensureFocusMarker(): void {
    if (this.focusMarker) return;
    const ring = this.add.graphics().setDepth(11);
    // Two concentric arcs (gold + ember) drawn in the same Graphics object;
    // the ring's `angle` is animated each frame in `updateFocusMarker` so
    // it reads as a rotating reticle.
    ring.lineStyle(2, 0xffd060, 1);
    ring.strokeCircle(0, 0, 22);
    ring.lineStyle(1, 0xff8030, 0.7);
    ring.strokeCircle(0, 0, 17);
    // Three short arc dashes around the perimeter so the rotation reads
    // visibly even on a uniform-color sprite.
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      ring.lineStyle(3, 0xffe080, 1);
      ring.beginPath();
      ring.arc(0, 0, 22, a - 0.18, a + 0.18, false);
      ring.strokePath();
    }
    const label = this.add.text(0, 0, '▼ FOCUS', {
      fontSize: '9px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#ffe080', fontStyle: 'bold',
      stroke: '#1a0a04', strokeThickness: 3,
    }).setOrigin(0.5, 1).setDepth(11.5).setResolution(2);
    this.focusMarker = { ring, label };
  }

  /** Per-frame: track the marker to the focused enemy + spin the ring. */
  private updateFocusMarkerPosition(): void {
    if (!this.focusMarker || !this.focusedEnemyId) return;
    const es = this.enemySprites.find((e) => e.enemyRef.instanceId === this.focusedEnemyId);
    if (!es || !es.active || es.enemyRef.currentHp <= 0) {
      this.clearFocusTarget();
      return;
    }
    this.focusMarker.ring.setPosition(es.x, es.y - 30);
    this.focusMarker.label.setPosition(es.x, es.y - 50);
  }

  /** Returns the live SummonSprite belonging to `ownerHeroId`, or null if the
   *  owner has no summon out (or it's already dead). Used by ability handlers
   *  that need to know "where is my golem" — e.g. Sorcerer's Static Field
   *  anchors on the golem; falls back to caster position if null. */
  private getSummonOf(ownerHeroId: string): SummonSprite | null {
    for (const ss of this.summonSprites) {
      if (!ss.isDead && ss.ownerHeroId === ownerHeroId) return ss;
    }
    return null;
  }

  /** Single-source-of-truth: who's standing on this slot? Heroes win the
   *  lookup over summons — a slot occupied by a hero is "taken" before
   *  considering whether any summon flag claims it. */
  private getOccupant(slotIdx: number): HeroSprite | SummonSprite | null {
    const h = this.heroSprites.find((hs) => hs.getData('slotIdx') === slotIdx);
    if (h) return h;
    const s = this.summonSprites.find((ss) => ss.slotIdx === slotIdx);
    return s ?? null;
  }

  /** Place a Copper Golem at the nearest empty front-row-ish slot to the
   *  owner. Visual is a placeholder copper-tinted graphics body until the
   *  real spritesheet is wired. Returns the new SummonSprite, or null if no
   *  slot was available (ALL slots taken by heroes/other summons). */
  private summonCopperGolem(owner: HeroSprite): SummonSprite | null {
    // Find empty slot — prefer ones in front of the owner (higher x baseX),
    // fall back to any empty slot. Owner's slot itself is excluded.
    const ownerSlot = owner.getData('slotIdx') as number | undefined;
    const candidates: { idx: number; score: number }[] = [];
    for (let i = 0; i < this.formationSlots.length; i++) {
      if (i === ownerSlot) continue;
      if (this.getOccupant(i)) continue;
      const slot = this.formationSlots[i];
      // Score = prefer slots in front of owner (positive dx) and close in y.
      const dx = slot.x - owner.x;
      const dy = Math.abs(slot.y - owner.y);
      const score = (dx >= 0 ? 0 : 10000) + dy * 2 - dx;
      candidates.push({ idx: i, score });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.score - b.score);
    const slotIdx = candidates[0].idx;
    const slot = this.formationSlots[slotIdx];

    // Real Copper Golem spritesheet — 128×128, faces RIGHT by default.
    // Owner is summoning toward the enemy side (right of screen) so flipX is
    // false. If somehow placed left of the owner, we'll mirror.
    const spriteKey = this.textures.exists('summon_copper_golem_idle')
      ? 'summon_copper_golem_idle'
      : 'summon_copper_golem_idle';                  // fallback handled by BootScene if texture missing
    const sprite = this.add.sprite(slot.x, slot.y, spriteKey)
      .setDisplaySize(96, 96)
      .setOrigin(0.5, 1)                              // anchor at bottom-center so feet touch slot.y
      .setDepth(2 + slot.y * 0.001);
    if (this.anims.exists('summon_copper_golem_idle_anim')) {
      sprite.play('summon_copper_golem_idle_anim');
    }
    // Make Vorrak movable like a hero — clicking selects him for swap.
    // Hit area covers most of the body so the click target isn't tiny.
    // Click handler is wired AFTER `ss` is constructed below to satisfy TDZ.
    sprite.setInteractive({ useHandCursor: true });

    const shadow = this.add.ellipse(slot.x, slot.y, 48, 14, 0x000000, 0.45).setDepth(0.6);
    const hpBarBg = this.add.rectangle(slot.x, slot.y - 96, 50, 5, 0x000000, 0.7).setDepth(50);
    const hpBar   = this.add.rectangle(slot.x - 24, slot.y - 96, 50, 5, 0x90ee70, 1).setOrigin(0, 0.5).setDepth(50);
    // Single nameplate "Vorrak" above the HP bar — the previous two-line
    // (Stormbound Forge / Vorrak) was visual noise per user feedback.
    const nameText = this.add.text(slot.x, slot.y - 106, 'Vorrak', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#d4a060', fontStyle: 'bold',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setDepth(50).setResolution(2);

    // Stats — base values, then layered with hybrid ATK+SP scaling from the
    // owning Sorcerer (per design: she's an ATK/SP combo hero so the golem
    // should reflect both stats). Recomputed each swing in updateSummons so
    // mid-run gear/buff changes propagate without a re-summon.
    // HP bumped 220 → 360 — golem is meant to be a frontline anvil, the old
    // value let him pop within a few stage-30 swings.
    //
    // Talent: Summon Copper Golem maxHpMult (linear, +20 % per rank up to 4).
    // Read off the owner's talent bonuses at spawn time so respec only
    // applies on the next summon (matches the talent-respec UX pattern).
    const golemHpTalent = getTalentBonusForStat(owner.heroRef.talentBonuses ?? {}, 'summon_copper_golem', 'maxHpMult');
    const maxHp = Math.round(360 * (1 + golemHpTalent));
    const ss: SummonSprite = {
      defId: 'copper_golem',
      ownerHeroId: owner.heroRef.id,
      sprite, hpBar, hpBarBg, nameText, shadow,
      x: slot.x, y: slot.y, baseX: slot.x, baseY: slot.y,
      slotIdx, hp: maxHp, maxHp,
      atk: 28, defense: 12,
      atkTimer: 0, atkInterval: 1900,
      isDead: false,
      animState: 'idle',
      animLockUntil: 0,
      lightSwingCount: 0,
    };
    this.summonSprites.push(ss);
    // Wire the swap-click handler now that `ss` is fully constructed.
    sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Right-click is "cancel swap" globally — don't toggle a summon
      // selection on a right-click stroke.
      if (pointer.rightButtonDown()) return;
      this.handleSummonClick(ss);
    });
    // Persist on owner so save/load can restore (transient flag stripped by
    // the save replacer's allow-list — added below).
    (owner.heroRef as any)._summon = { defId: 'copper_golem', hp: maxHp, slotIdx };
    this.gsm.save();
    return ss;
  }

  /** Handle a click on a SummonSprite — enters swap mode with the summon
   *  as the source. If a hero is already selected for swap, swap them
   *  with this summon. Otherwise just select. Mirrors the hero swap UX. */
  private handleSummonClick(ss: SummonSprite): void {
    if (ss.isDead) return;
    // Already selected — clicking again deselects.
    if (this.swapSelectedSummon === ss) {
      this.clearSwapSelection();
      return;
    }
    // If a hero is selected for swap, treat this click as a destination —
    // route through handleSlotClick(slotIdx) which knows about summons.
    if (this.swapSelectedSprite) {
      this.handleSlotClick(ss.slotIdx);
      return;
    }
    // If another summon was selected, swap targets — clear and select fresh.
    if (this.swapSelectedSummon) this.clearSwapSelection();
    this.swapSelectedSummon = ss;
    // Highlight the golem with a pulse so the player sees the selection.
    ss.sprite.setTint(0xa8e0ff);
    this.swapHighlight = this.tweens.add({
      targets: ss.sprite, alpha: { from: 1, to: 0.55 },
      duration: 380, yoyo: true, repeat: -1,
    });
    // Bobbing ▼ arrow above Vorrak — same visual as the hero swap arrow
    // so the player reads "this is the unit being moved" identically. Red
    // if he's already used his per-wave move, white otherwise.
    const outOfMoves = this.summonMovesUsedThisWave >= 1;
    const arrowColor = outOfMoves ? '#ef4444' : '#ffffff';
    this.swapArrow = this.add.text(ss.x, ss.y - 140, '▼', {
      fontSize: '22px', fontFamily: 'Arial, sans-serif', fontStyle: 'bold',
      color: arrowColor, stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(60);
    this.swapArrowTween = this.tweens.add({
      targets: this.swapArrow,
      y: { from: ss.y - 140, to: ss.y - 128 },
      duration: 450, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    // "1 left / 0 left" tag matching the hero arrow's per-wave-budget tag.
    const tagColor = outOfMoves ? '#ef4444' : '#fbbf24';
    const tagText = outOfMoves ? '0 left' : '1 left';
    this.swapArrowTag = this.add.text(ss.x, ss.y - 118, tagText, {
      fontSize: '10px', fontFamily: "'Consolas', 'Courier New', monospace",
      color: tagColor, stroke: '#000000', strokeThickness: 3, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(60);
    // Show the formation grid + slot zones so the player sees where to drop.
    this.formationGridGraphics?.setAlpha(1);
    for (const ch of this.frontRowChevrons) ch.setAlpha(1);
    for (const sz of this.formationSlotZones) {
      sz.zone.setDepth(60); // lift above sprites for click priority
    }
    this.showAnnouncement('Vorrak selected — click a slot to move');
  }

  /** Tear down a summon — plays the death anim, then destroys visuals after
   *  ~700 ms so the player sees the collapse instead of the body popping
   *  out. Frees the slot and clears the owner's save flag immediately so a
   *  re-summon during the death tween still works. */
  private despawnSummon(ss: SummonSprite): void {
    if (ss.isDead) return;                              // re-entrancy guard
    ss.isDead = true;
    ss.animState = 'dead';
    // Logical removal happens immediately so getOccupant / lookups stop
    // returning him as a live target. The sprite hangs around briefly to
    // play the death anim, then self-destroys.
    const idx = this.summonSprites.indexOf(ss);
    if (idx >= 0) this.summonSprites.splice(idx, 1);
    const owner = this.heroSprites.find((h) => h.heroRef.id === ss.ownerHeroId);
    if (owner) {
      delete (owner.heroRef as any)._summon;
      // Start the 40 s re-summon cooldown on the owner's Forge Vorrak
      // ability. CD doesn't run while he's alive; only kicks in here.
      const summonAbility = owner.heroRef.abilities?.find((a) => a.id === 'summon_copper_golem');
      if (summonAbility) summonAbility.currentCooldown = summonAbility.cooldown;
      this.gsm.save();
    }
    // Death anim + delayed teardown
    if (this.anims.exists('summon_copper_golem_dead_anim')) {
      try { ss.sprite.off('animationcomplete'); } catch { /* ignore */ }
      ss.sprite.play('summon_copper_golem_dead_anim');
    }
    try { ss.hpBar.destroy(); } catch { /* ignore */ }
    try { ss.hpBarBg.destroy(); } catch { /* ignore */ }
    try { ss.nameText.destroy(); } catch { /* ignore */ }
    this.time.delayedCall(700, () => {
      try { ss.sprite.destroy(); } catch { /* ignore */ }
      try { ss.shadow.destroy(); } catch { /* ignore */ }
    });
  }

  /** Damage application for summons — thin shell vs. the full hero pipeline.
   *  No shields, no Last Stand, no XP, no auto-revive. Just hp -= dmg, hurt
   *  anim + red tint flash, death check. Returns the actual damage dealt
   *  after defense mitigation. */
  private applyDamageToSummon(ss: SummonSprite, rawDmg: number): number {
    if (ss.isDead) return 0;
    // Same defense formula as enemies: defense scales mitigation, capped at 85 %.
    const defMit = 1 - Math.min(0.85, ss.defense / (ss.defense + 100));
    const dmg = Math.max(1, Math.round(rawDmg * defMit));
    ss.hp = Math.max(0, ss.hp - dmg);
    this.showDamageNumber(ss.x, ss.y - 60, `${dmg}`, '#fbbf24');
    // Red tint pulse on the body (cleared in updateSummons when animLockUntil
    // expires). Doesn't stomp the dead-state tint check below.
    if (ss.hp > 0) {
      ss.sprite.setTint(0xff6644);
      this.tweens.add({
        targets: { v: 0 }, v: 1, duration: 220,
        onComplete: () => { if (!ss.isDead) ss.sprite.clearTint(); },
      });
      // Play hurt anim if available; lock state for ~330 ms so attack/idle
      // don't stomp it mid-frame.
      if (this.anims.exists('summon_copper_golem_hurt_anim')) {
        try { ss.sprite.off('animationcomplete'); } catch { /* ignore */ }
        ss.sprite.play('summon_copper_golem_hurt_anim');
        ss.animState = 'hurt';
        ss.animLockUntil = Date.now() + 330;
      }
    }
    if (ss.hp <= 0) {
      this.showDamageNumber(ss.x, ss.y - 80, 'DESTROYED', '#9ca3af');
      this.despawnSummon(ss);
    }
    return dmg;
  }

  /** Damage every live summon inside a circular AoE radius. Used at boss-spell
   *  AoE call sites (Frost Nova, Quake Roar, Hellfire Blast, Stormcall, etc.)
   *  in parallel with the existing hero iteration so the same blast hits the
   *  Sorcerer's Copper Golem if he's standing in it. Returns the count of
   *  summons damaged so the caller can short-circuit if zero. */
  private applyAoeDamageToSummons(centerX: number, centerY: number, radius: number, dmg: number): number {
    let hit = 0;
    const r2 = radius * radius;
    for (let i = this.summonSprites.length - 1; i >= 0; i--) {
      const ss = this.summonSprites[i];
      if (ss.isDead) continue;
      const dx = ss.x - centerX;
      const dy = ss.y - centerY;
      if (dx * dx + dy * dy <= r2) {
        this.applyDamageToSummon(ss, dmg);
        hit++;
      }
    }
    return hit;
  }

  /** Find the nearest live enemy to a summon — same shape as findNearestEnemy
   *  for heroes, just keyed on the summon's x/y. */
  private findNearestEnemyForSummon(ss: SummonSprite): EnemySprite | null {
    let nearest: EnemySprite | null = null;
    let minDist = Infinity;
    for (const es of this.enemySprites) {
      if (!es.active || es.enemyRef.currentHp <= 0) continue;
      const dx = es.x - ss.x;
      const dy = es.y - ss.y;
      const d = dx * dx + dy * dy;
      if (d < minDist) { minDist = d; nearest = es; }
    }
    return nearest;
  }

  /** Per-frame update for all live summons — auto-attack the nearest enemy,
   *  keep hp bar / shadow positions in sync with the body, drive idle ↔ attack
   *  ↔ hurt ↔ dead anim transitions, handle death. */
  private updateSummons(time: number, delta: number): void {
    const now = Date.now();
    for (let i = this.summonSprites.length - 1; i >= 0; i--) {
      const ss = this.summonSprites[i];
      if (ss.isDead) {
        // Logical removal already happened in despawnSummon; this branch
        // catches any stragglers (defensive).
        this.summonSprites.splice(i, 1);
        continue;
      }
      // Mirror sprite x/y onto SummonSprite.x / .y so callers reading like
      // a HeroSprite (e.g. the AI loop's target.x lookups) see live values.
      ss.x = ss.sprite.x;
      ss.y = ss.sprite.y;
      // HP bar follow + width
      const pct = ss.maxHp > 0 ? Math.max(0, ss.hp / ss.maxHp) : 0;
      ss.hpBar.width = 50 * pct;
      ss.hpBar.x = ss.x - 24;
      ss.hpBar.y = ss.y - 96;
      ss.hpBarBg.x = ss.x; ss.hpBarBg.y = ss.y - 96;
      ss.nameText.x = ss.x; ss.nameText.y = ss.y - 106;
      ss.shadow.x = ss.x; ss.shadow.y = ss.y;
      // Anim state machine — idle is the default loop. attack/hurt are
      // one-shots that auto-return to idle when their lock expires AND the
      // sprite isn't currently in a non-idle anim.
      if (ss.animState !== 'idle' && now >= ss.animLockUntil) {
        ss.animState = 'idle';
        if (this.anims.exists('summon_copper_golem_idle_anim')) {
          ss.sprite.play('summon_copper_golem_idle_anim');
        }
      }
      // Attack tick — strikes the nearest live enemy when timer elapses.
      // Two-attack pattern:
      //   Light  — every interval (~1.9 s), Attack_1 anim, no crit, base damage
      //   Heavy  — every 4th swing (~7.6 s cadence), Attack_2 chain-slam,
      //            ×1.8 damage, 50 % crit chance, crit deals ×1.5 of heavy.
      ss.atkTimer += delta;
      if (ss.atkTimer >= ss.atkInterval) {
        ss.atkTimer = 0;
        const tgt = this.findNearestEnemyForSummon(ss);
        if (tgt) {
          // Hybrid ATK+SP scaling from the owning Sorcerer (per design, the
          // golem is "your spell that auto-attacks" — both stats matter).
          // Falls back to flat ss.atk if owner is dead/missing so the golem
          // still does something meaningful in those windows.
          const owner = this.heroSprites.find((h) => h.heroRef.id === ss.ownerHeroId && !h.heroRef.isDead);
          const ownerStats = owner ? this.effStats(owner.heroRef) : null;
          const ownerAtk = ownerStats?.attack ?? 0;
          const ownerSp  = ownerStats?.spellPower ?? 0;
          const golemAtk = ss.atk + 0.35 * ownerAtk + 0.35 * ownerSp;
          const defMit = 1 - Math.min(0.85, tgt.enemyRef.defense / (tgt.enemyRef.defense + 100));
          // Heavy-swing cadence — every 4th light swing rolls into Attack_2.
          ss.lightSwingCount += 1;
          const isHeavy = ss.lightSwingCount % 4 === 0;
          let dmg: number;
          let dmgColor = '#d4a060';
          let dmgSize = '12px';
          let isCrit = false;
          if (isHeavy) {
            const heavyBase = golemAtk * 1.80;
            isCrit = Math.random() < 0.50;
            const heavyFinal = isCrit ? heavyBase * 1.50 : heavyBase;
            dmg = Math.max(1, Math.round(heavyFinal * defMit));
            dmgColor = isCrit ? '#fff7b0' : '#ffae40';
            dmgSize = isCrit ? '20px' : '16px';
          } else {
            dmg = Math.max(1, Math.round(golemAtk * defMit));
          }
          // Face the target before the swing
          ss.sprite.setFlipX(tgt.x < ss.x);
          // Play the matching attack anim — locks state for the duration so
          // hurt-flash mid-swing doesn't stomp the swing arc. Heavy locks a
          // bit longer (700 ms vs 600 ms) since the chain wind-up is slower.
          const animKey = isHeavy ? 'summon_copper_golem_attack2_anim' : 'summon_copper_golem_attack_anim';
          if (this.anims.exists(animKey)) {
            try { ss.sprite.off('animationcomplete'); } catch { /* ignore */ }
            ss.sprite.play(animKey);
            ss.animState = isHeavy ? 'attack_heavy' : 'attack';
            ss.animLockUntil = now + (isHeavy ? 850 : 600);
          }
          tgt.enemyRef.currentHp -= dmg;
          const dmgText = isCrit ? `✦ ${dmg}` : `${dmg}`;
          this.showDamageNumber(tgt.x, tgt.y - 20, dmgText, dmgColor, dmgSize);
          this.flashSprite(tgt);
          ss.lastTargetId = tgt.enemyRef.instanceId;
          if (tgt.enemyRef.currentHp <= 0) {
            const credit = owner ?? this.heroSprites.find((h) => !h.heroRef.isDead);
            if (credit) this.killEnemy(tgt, credit);
          }
        }
      }
    }
    void time;
  }

  private findNearestHero(es: EnemySprite): HeroSprite | SummonSprite | null {
    // Taunted: must target the challenging hero while taunt is active
    const tauntTarget = (es.enemyRef as any)._tauntedTarget as HeroSprite | undefined;
    if (tauntTarget && !tauntTarget.heroRef.isDead) return tauntTarget;

    let nearest: HeroSprite | SummonSprite | null = null;
    let bestScore = Infinity;
    const isRanged = es.enemyRef.attackRange === 'ranged';
    // Heroes — original behavior
    for (const hs of this.heroSprites) {
      if (hs.heroRef.isDead) continue;
      if (isRanged) {
        const score = -hs.x;
        if (score < bestScore) { bestScore = score; nearest = hs; }
      } else {
        const dx = es.x - hs.x;
        const dy = es.y - hs.y;
        const d = dx * dx + dy * dy;
        if (d < bestScore) { bestScore = d; nearest = hs; }
      }
    }
    // Summons — picked using the SAME grid-position scoring as heroes for
    // ranged enemies (so a back-row Vorrak draws archer / caster aggro the
    // same way a back-row hero does), with a melee distance tax so a
    // central-slot summon doesn't soak both flanks of melee mobs at once.
    //
    //   Ranged: leftmost-x scoring with NO bias — placement on the grid
    //     decides aggro. Vorrak parked in the back row is the leftmost
    //     (deepest) target → ranged targets him just like a back-row hero.
    //     Vorrak in front → archers shoot past him to deeper heroes.
    //   Melee: 1.5× distance penalty (squared = 2.25× to score). A hero at
    //     ≤ 82 % of Vorrak's range still wins — only meaningfully-closer
    //     summons (body-blocking the line) draw aggro. Stops the original
    //     "all flank mobs funnel onto a central Vorrak" behavior.
    for (const ss of this.summonSprites) {
      if (ss.isDead) continue;
      if (isRanged) {
        const score = -ss.x;
        if (score < bestScore) { bestScore = score; nearest = ss; }
      } else {
        const SUMMON_DIST_PENALTY = 2.25; // 1.5² — applied to squared distance
        const dx = es.x - ss.x;
        const dy = es.y - ss.y;
        const d = (dx * dx + dy * dy) * SUMMON_DIST_PENALTY;
        if (d < bestScore) { bestScore = d; nearest = ss; }
      }
    }
    return nearest;
  }

  private trackDps(heroId: string, amount: number, source = 'auto'): void {
    if (amount <= 0) return;
    if (!this.dpsWaveDamage.has(heroId)) this.dpsWaveDamage.set(heroId, new Map());
    const src = this.dpsWaveDamage.get(heroId)!;
    src.set(source, (src.get(source) ?? 0) + amount);
    // Hero-quest hook — feed lifetime damage tracker keyed by heroKey so
    // the Heroes tab "Deal N damage with [hero]" quest tier progresses.
    // Resolve heroId → hero → heroKey once here so we don't have to thread
    // the heroKey through every call site of trackDps.
    const hero = this.gsm.player.heroes.find((h) => h.id === heroId);
    if (hero) {
      const heroKey = hero.heroKey ?? hero.heroClass;
      this.gsm.trackHeroDamage(heroKey, amount);
    }
  }

  /**
   * Build a gold-bordered tooltip container matching the Divine Wrath / Zone Cleared palette.
   * Replaces the old `this.add.text(..., { backgroundColor: '#0d0a0e', ... })` pattern so every
   * HUD tooltip shares the same look (dark-brown fill + gold border + bronze text).
   *
   * Returns a Container positioned at (x, y) with the requested origin. The container groups
   * a background rectangle and the text, so callers can `.destroy()` it like a single object.
   */
  private createHudTooltip(
    x: number, y: number, text: string,
    originX = 0, originY = 0,
    opts: { color?: string; fontSize?: string; wrap?: number; depth?: number } = {},
  ): Phaser.GameObjects.Container {
    const color = opts.color ?? '#c8973a';
    const fontSize = opts.fontSize ?? '11px';
    const wrap = opts.wrap ?? 240;
    const depth = opts.depth ?? 55;
    const pad = 8;
    const textObj = this.add.text(pad, pad, text, {
      fontSize, fontFamily: 'Georgia, serif', color,
      lineSpacing: 4, wordWrap: { width: wrap },
    }).setResolution(2);
    const w = Math.ceil(textObj.width + pad * 2);
    const h = Math.ceil(textObj.height + pad * 2);
    const rect = this.add.rectangle(0, 0, w, h, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0xc8973a, 0.95).setOrigin(0, 0);
    const innerBorder = this.add.rectangle(0, 0, w - 4, h - 4, 0x0d0a08, 0)
      .setStrokeStyle(1, 0x5a3600, 0.5).setOrigin(0, 0).setPosition(2, 2);
    const container = this.add.container(
      x - originX * w,
      y - originY * h,
      [rect, innerBorder, textObj],
    ).setDepth(depth);
    container.setSize(w, h);
    return container;
  }

  private showDamageNumber(x: number, y: number, text: string, color: string, fontSize = '12px'): void {
    const t = this.add.text(x, y, text, {
      fontSize, fontFamily: 'monospace', color, fontStyle: 'bold',
      stroke: fontSize !== '12px' ? '#000000' : undefined,
      strokeThickness: fontSize !== '12px' ? 3 : 0,
    }).setOrigin(0.5).setDepth(50);

    // Bigger text gets a scale pop effect
    if (fontSize !== '12px') {
      t.setScale(1.3);
      this.tweens.add({
        targets: t, scaleX: 1, scaleY: 1, duration: 150, ease: 'Back.easeOut',
      });
    }

    // Crit detection — gold tint (#fbbf24 / #ffd060) at the standard crit
    // size '27px' is the project-wide crit signature. When detected:
    //   • Subtle horizontal arc (random L/R per spawn, ±20 px) so back-
    //     to-back crits don't pile up at the same x.
    //   • Rainbow tint cycle through 6 hues over the floater lifetime so
    //     crits read distinctly without a separate explosion VFX.
    // Non-crit floaters stay straight-up + single color as before.
    const isCrit = (fontSize === '27px' && (color === '#fbbf24' || color === '#ffd060'));
    let endX = x;
    if (isCrit) {
      const arcDir = Math.random() < 0.5 ? -1 : 1;
      endX = x + arcDir * 20;
      const RAINBOW = ['#ef4444', '#f97316', '#fbbf24', '#22c55e', '#3b82f6', '#a855f7'];
      let hueIdx = 0;
      const hueTimer = this.time.addEvent({
        delay: 150, loop: true,
        callback: () => {
          if (!t.active || !t.scene) { hueTimer.remove(); return; }
          t.setColor(RAINBOW[hueIdx % RAINBOW.length]);
          hueIdx++;
        },
      });
      this.tweens.add({
        targets: t, y: y - 30, x: endX, alpha: 0, duration: 900,
        ease: 'Quad.easeOut',
        onComplete: () => { hueTimer.remove(); t.destroy(); },
      });
      return;
    }

    this.tweens.add({
      targets: t, y: y - 30, alpha: 0, duration: 900,
      onComplete: () => t.destroy(),
    });
  }

  /** Show a DoT / poison / burn proc indicator that drifts DOWNWARD instead
   *  of upward (the standard floater rises). Used for periodic-damage status
   *  effects so they read distinctly from direct hits — e.g. orange for burn,
   *  green for poison, purple for shadow rot. Color is whatever the caller
   *  passes; geometry + drift duration are shared across all DoT types. */
  private showDotProc(x: number, y: number, text: string, color: string, fontSize = '11px'): void {
    const t = this.add.text(x, y, text, {
      fontSize, fontFamily: 'monospace', color, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(50);
    this.tweens.add({
      targets: t, y: y + 22, alpha: 0, duration: 850, ease: 'Quad.easeIn',
      onComplete: () => t.destroy(),
    });
  }

  /** Show a small floating popup above a hero sprite when an item is auto-equipped */
  private showAutoEquipPopup(hs: HeroSprite, item: import('@roe/shared').Equipment): void {
    const rarityColor = RARITY_COLORS[item.rarity] || '#f97316';
    const slotLabel = item.slot.replace(/\d/, '').toUpperCase();
    const panel = this.add.container(hs.x, hs.y - 80).setDepth(52);

    const bg = this.add.rectangle(0, 0, 150, 36, 0x0a0806, 0.45)
      .setStrokeStyle(1, Phaser.Display.Color.HexStringToColor(rarityColor).color, 0.6);
    const itemIcon = item.spriteKey && this.textures.exists(item.spriteKey)
      ? this.add.image(-56, 0, item.spriteKey).setDisplaySize(24, 24)
      : null;
    const nameText = this.add.text(itemIcon ? -40 : 0, -6, item.name, {
      fontSize: '9px', fontFamily: 'monospace', color: rarityColor, fontStyle: 'bold',
      wordWrap: { width: 120 },
    }).setOrigin(itemIcon ? 0 : 0.5, 0);
    const slotText = this.add.text(itemIcon ? -40 : 0, 6, `⚔ Auto-equipped [${slotLabel}]`, {
      fontSize: '8px', fontFamily: 'monospace', color: '#94a3b8',
    }).setOrigin(itemIcon ? 0 : 0.5, 0);

    panel.add([bg, ...(itemIcon ? [itemIcon] : []), nameText, slotText]);
    panel.setAlpha(0.7);

    this.tweens.add({
      targets: panel,
      y: hs.y - 115,
      alpha: 0,
      duration: 900,
      delay: 600,
      ease: 'Quad.easeIn',
      onComplete: () => panel.destroy(),
    });
  }

  /** Generic yes/no confirmation popup */
  private showConfirmPopup(message: string, onYes: () => void, onNo?: () => void): void {
    const width = GAME_W, height = GAME_H;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6).setDepth(200).setInteractive();
    const bg = this.add.rectangle(width / 2, height / 2, 340, 150, 0x0d0a08, 0.98).setStrokeStyle(2, 0x5a3600).setDepth(201);
    const bgInner = this.add.rectangle(width / 2, height / 2, 334, 144, 0x0d0a08, 0).setStrokeStyle(1, 0xc8973a, 0.2).setDepth(201);
    const msg = this.add.text(width / 2, height / 2 - 32, message, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a', align: 'center',
      wordWrap: { width: 310 },
    }).setOrigin(0.5).setDepth(202);

    const yesBtnBg = this.add.rectangle(width / 2 - 62, height / 2 + 32, 96, 32, 0x1a0608, 1)
      .setStrokeStyle(2, 0x8b2000).setDepth(202).setInteractive({ useHandCursor: true });
    const yesBtn = this.add.text(width / 2 - 62, height / 2 + 32, 'YES', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    const yesOver = () => { yesBtn.setColor('#ffe8a0'); yesBtnBg.setStrokeStyle(2, 0xc84000); };
    const yesOut  = () => { yesBtn.setColor('#e8a030'); yesBtnBg.setStrokeStyle(2, 0x8b2000); };
    yesBtn.on('pointerover', yesOver).on('pointerout', yesOut);
    yesBtnBg.on('pointerover', yesOver).on('pointerout', yesOut);

    const noBtnBg = this.add.rectangle(width / 2 + 62, height / 2 + 32, 96, 32, 0x0d0a08, 1)
      .setStrokeStyle(2, 0x5a3600).setDepth(202).setInteractive({ useHandCursor: true });
    const noBtn = this.add.text(width / 2 + 62, height / 2 + 32, 'NO', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    const noOver = () => { noBtn.setColor('#c8973a'); noBtnBg.setStrokeStyle(2, 0x9a6020); };
    const noOut  = () => { noBtn.setColor('#a07840'); noBtnBg.setStrokeStyle(2, 0x5a3600); };
    noBtn.on('pointerover', noOver).on('pointerout', noOut);
    noBtnBg.on('pointerover', noOver).on('pointerout', noOut);

    const cleanup = () => { overlay.destroy(); bg.destroy(); bgInner.destroy(); msg.destroy(); yesBtnBg.destroy(); yesBtn.destroy(); noBtnBg.destroy(); noBtn.destroy(); };
    yesBtn.on('pointerdown', () => { cleanup(); onYes(); });
    yesBtnBg.on('pointerdown', () => { cleanup(); onYes(); });
    noBtn.on('pointerdown', () => { cleanup(); onNo?.(); });
    noBtnBg.on('pointerdown', () => { cleanup(); onNo?.(); });
  }

  private togglePause(): void {
    this.isPaused = !this.isPaused;
    this.showAnnouncement(this.isPaused ? 'PAUSED' : 'Resumed');
    this.refreshUI();
    this.events.emit('speedStateChanged');
  }

  /** Push a new entry to the persistent choice log, capped at 50. Mirrors to player state so F5/menu reload keeps history. */
  private addChoiceLog(entry: { kind: 'book' | 'talent'; icon: string; label: string; detail: string }): void {
    BattleScene.choiceLog.push({
      ...entry,
      wave: this.gsm.player.currentLevel ?? 0,
      time: Date.now(),
    });
    if (BattleScene.choiceLog.length > 50) BattleScene.choiceLog.shift();
    this.gsm.player.choiceLog = [...BattleScene.choiceLog];
    this.gsm.save();
  }

  /** Show a popup with the recent book & talent choices. */
  private showChoiceLog(): void {
    // Use game-coord dimensions — cameras.main.width/height return the zoomed viewport size,
    // which would place popups far off-center on high-DPR displays.
    const width = GAME_W, height = GAME_H;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.55)
      .setDepth(199).setInteractive();
    const popupW = 520, popupH = 420;
    const popup = this.add.rectangle(width / 2, height / 2, popupW, popupH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(200);
    const title = this.add.text(width / 2, height / 2 - popupH / 2 + 20, '📜 Choice Log', {
      fontSize: '15px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    const closeBtn = this.add.text(width / 2 + popupW / 2 - 18, height / 2 - popupH / 2 + 14, '×', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });

    const els: Phaser.GameObjects.GameObject[] = [overlay, popup, title, closeBtn];
    const entries = [...BattleScene.choiceLog].reverse(); // newest first
    if (entries.length === 0) {
      const empty = this.add.text(width / 2, height / 2, 'No books or talents taken yet this run.', {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a7040',
      }).setOrigin(0.5).setDepth(201);
      els.push(empty);
    } else {
      const startY = height / 2 - popupH / 2 + 52;
      entries.slice(0, 14).forEach((e, i) => {
        const y = startY + i * 26;
        const rowColor = e.kind === 'book' ? '#d4a040' : '#a0c080';
        const line = this.add.text(width / 2 - popupW / 2 + 16, y,
          `W${e.wave}  ${e.icon}  ${e.label}`, {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: rowColor,
        }).setOrigin(0, 0).setDepth(201);
        const detail = this.add.text(width / 2 - popupW / 2 + 16, y + 12,
          `    ${e.detail}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a6840',
          wordWrap: { width: popupW - 32 },
        }).setOrigin(0, 0).setDepth(201);
        els.push(line, detail);
      });
    }

    const cleanup = () => { for (const o of els) o.destroy(); };
    closeBtn.on('pointerdown', cleanup);
    overlay.on('pointerdown', cleanup);
  }

  /** Label shown on the combined Pause/x2/x5 button — shows all states, current bracketed. */
  private speedCycleLabel(): string {
    const states = ['1', '2', '5', '⏸'];
    let cur = 0;
    if (this.isPaused) cur = 3;
    else if (this.speedMultiplier === 5) cur = 2;
    else if (this.speedMultiplier === 2) cur = 1;
    return states.map((s, i) => i === cur ? `[${s}]` : s).join(' ');
  }

  /** Jump directly to a specific speed state (0=x1, 1=x2, 2=x5, 3=paused). */
  private gotoSpeedState(target: 0 | 1 | 2 | 3): void {
    if (target === 0) {
      this.isPaused = false; this.speedMultiplier = 1; this.speedPenaltyActive = false;
      this.showAnnouncement('Normal speed'); this.refreshUI();
      this.events.emit('speedStateChanged'); return;
    }
    if (target === 1) {
      if (this.speedMultiplier === 2 && !this.isPaused) { this.gotoSpeedState(0); return; }
      this.isPaused = false;
      this.showConfirmPopup('Activate x2 Speed?\n-10% Gold & Resources penalty', () => {
        this.speedMultiplier = 2; this.speedPenaltyActive = true; this.refreshUI();
        this.events.emit('speedStateChanged');
      }); return;
    }
    if (target === 2) {
      if (this.speedMultiplier === 5 && !this.isPaused) { this.gotoSpeedState(0); return; }
      this.isPaused = false;
      this.showConfirmPopup('Activate x5 Speed?\n-30% Gold & Resources penalty', () => {
        this.speedMultiplier = 5; this.speedPenaltyActive = true; this.refreshUI();
        this.events.emit('speedStateChanged');
      }); return;
    }
    if (target === 3) {
      if (this.isPaused) { this.gotoSpeedState(0); return; }
      this.isPaused = true; this.showAnnouncement('PAUSED'); this.refreshUI();
      this.events.emit('speedStateChanged'); return;
    }
  }

  /** Cycle through speed states: x1 → x2 → x5 → paused → x1. */
  private cycleSpeedState(): void {
    if (this.isPaused) {
      this.isPaused = false;
      this.speedMultiplier = 1;
      this.speedPenaltyActive = false;
      this.showAnnouncement('Resumed');
      this.refreshUI();
      return;
    }
    if (this.speedMultiplier === 1) {
      this.showConfirmPopup('Activate x2 Speed?\n-10% Gold & Resources penalty', () => {
        this.speedMultiplier = 2; this.speedPenaltyActive = true; this.refreshUI();
      });
      return;
    }
    if (this.speedMultiplier === 2) {
      this.showConfirmPopup('Activate x5 Speed?\n-30% Gold & Resources penalty', () => {
        this.speedMultiplier = 5; this.speedPenaltyActive = true; this.refreshUI();
      });
      return;
    }
    if (this.speedMultiplier === 5) {
      this.speedMultiplier = 1;
      this.speedPenaltyActive = false;
      this.isPaused = true;
      this.showAnnouncement('PAUSED');
      this.refreshUI();
      return;
    }
  }

  private showSpeed5Confirm(btn: Phaser.GameObjects.Text): void {
    this.showConfirmPopup(
      'Activate x5 Speed?\n-30% Gold & Resources penalty',
      () => {
        this.speedMultiplier = 5;
        this.speedPenaltyActive = true;
        // Deactivate x2 if it was on
        const x2Txt = this.children.getByName('actionTxt_speed') as Phaser.GameObjects.Text | null;
        x2Txt?.setText('x2');
        btn.setText('x5 ON');
        btn.setStyle({ color: '#ef8c2a' });
        this.refreshUI();
      },
    );
  }

  private showSpeedConfirm(btn: Phaser.GameObjects.Text, _mult: number, _penalty: number): void {
    this.showConfirmPopup(
      'Activate x2 Speed?\n-10% Gold & Resources penalty',
      () => {
        this.speedMultiplier = 2;
        this.speedPenaltyActive = true;
        // Deactivate x5 if it was on
        const x5Txt = this.children.getByName('actionTxt_speed5') as Phaser.GameObjects.Text | null;
        x5Txt?.setText('x5');
        btn.setText('x2 ON');
        btn.setStyle({ color: '#fbbf24' });
        this.refreshUI();
      },
    );
  }

  private showSpeed10Confirm(btn: Phaser.GameObjects.Text): void {
    this.showConfirmPopup(
      'Activate x10 Speed?\n-40% Gold & Resources penalty',
      () => {
        this.speedMultiplier = 10;
        this.speedPenaltyActive = true;
        btn.setText('x10 ON');
        btn.setStyle({ color: '#ef4444' });
      },
    );
  }

  private toggleMenu(): void {
    // Debounce: prevent rapid-fire toggling when spamming ESC
    const now = Date.now();
    if (now - this.lastToggleTime < 400) return;
    this.lastToggleTime = now;

    this.gsm.save();

    // If menu is already open (or still launching), close it and resume
    const menuActive = this.scene.isActive('MenuScene') || this.scene.isSleeping('MenuScene');
    if (menuActive) {
      this.scene.stop('MenuScene');
      this.resumeBattle();
      this.game.canvas.focus();
      return;
    }

    // Tome/talent/zone-cleared popups require a player decision — ESC must not dismiss them
    if (this.tomeChoiceActive || this.talentChoiceActive || this.zoneClearedActive) {
      this.game.canvas.focus();
      return;
    }

    // Inferno: quitting to menu abandons the run and restores pre-inferno heroes
    if (this.infernoMode && !this.gameOver) {
      this.showInfernoAbandonConfirm();
      return;
    }

    // Close any open overlay first — ESC acts as "back" button
    const overlays = ['HeroScene', 'InventoryScene', 'BaseScene', 'AchievementScene', 'ShopScene', 'RuneShopScene', 'TomesScene'];
    let closedOverlay = false;
    for (const s of overlays) {
      if (this.scene.isActive(s) || this.scene.isSleeping(s)) {
        this.scene.stop(s);
        closedOverlay = true;
      }
    }

    // Close optional vendor popups (goblino/merchant) — these don't require a decision.
    // Merchant drop-shadow sits at depth 69 (one below the overlay), so the sweep must
    // include 69 — otherwise the shadow rectangle lingers as a faint transparent window.
    const hadVendorPopup = this.goblinoShopActive || this.merchantShopActive;
    if (hadVendorPopup) {
      this.goblinoShopActive = false;
      this.merchantShopActive = false;
      // Collect first, then destroy — avoids iterator invalidation from live list mutation
      const toDestroy: Phaser.GameObjects.GameObject[] = [];
      this.children.each((child) => {
        const d = (child as any).depth;
        if (typeof d === 'number' && d >= 69 && d < 300) toDestroy.push(child);
      });
      toDestroy.forEach((c) => c.destroy());
      // Name-based sweep for safety (catches any merchant_popup_el outside the depth band).
      ['merchant_overlay', 'merchant_popup_el'].forEach((nm) => {
        this.children.list.filter((c: any) => c.name === nm).forEach((c) => c.destroy());
      });
    }

    // Only open menu if nothing else was closed
    if (!closedOverlay && !hadVendorPopup) {
      this.scene.launch('MenuScene', { gsm: this.gsm, battleScene: this });
      // Bring MenuScene above BattleScene in the render order (MenuScene is earlier in config list)
      this.scene.bringToTop('MenuScene');
      // Pause the battle update loop while the menu is visible
      this.scene.pause();
    }

    // Re-focus canvas so ESC keeps working
    this.game.canvas.focus();
  }

  /** Popup asking player to confirm abandoning an Inferno run */
  private showInfernoAbandonConfirm(): void {
    const width = GAME_W, height = GAME_H;
    const cx = width / 2, cy = height / 2;
    const els: Phaser.GameObjects.GameObject[] = [];
    const cleanup = () => els.forEach((o) => o.destroy());

    els.push(this.add.rectangle(cx, cy, width, height, 0x000000, 0.70).setDepth(200).setInteractive());
    els.push(this.add.rectangle(cx, cy, 360, 180, 0x0d0402, 0.99).setStrokeStyle(2, 0xcc2200).setDepth(201));
    els.push(this.add.rectangle(cx, cy, 354, 174, 0, 0).setStrokeStyle(1, 0xff4020, 0.2).setDepth(201));

    els.push(this.add.text(cx, cy - 60, 'LEAVE INFERNO?', {
      fontSize: '15px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(202));

    els.push(this.add.text(cx, cy - 32, 'Run score will not be saved.\nHero returns to the base game.', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', align: 'center',
    }).setOrigin(0.5).setDepth(202));

    const yesBg = this.add.rectangle(cx - 58, cy + 32, 120, 28, 0x1e0404, 1)
      .setStrokeStyle(1, 0x8b1a00).setDepth(202).setInteractive({ useHandCursor: true });
    const yesTxt = this.add.text(cx - 58, cy + 32, 'BACK TO MENU', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#cc2200', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    yesBg.on('pointerover', () => { yesBg.setFillStyle(0x2e0808); yesTxt.setColor('#ff4020'); });
    yesBg.on('pointerout',  () => { yesBg.setFillStyle(0x1e0404); yesTxt.setColor('#cc2200'); });
    const doAbandon = () => {
      cleanup();
      this.gsm.abandonInfernoRun();
      this.cleanupBattle();
      this.scene.stop('BattleScene');
      this.scene.start('MenuScene', {});
    };
    yesBg.on('pointerdown', doAbandon);
    yesTxt.on('pointerdown', doAbandon);
    els.push(yesBg, yesTxt);

    const noBg = this.add.rectangle(cx + 58, cy + 32, 96, 28, 0x0d0a08, 1)
      .setStrokeStyle(1, 0x5a3600).setDepth(202).setInteractive({ useHandCursor: true });
    const noTxt = this.add.text(cx + 58, cy + 32, 'KEEP FIGHTING', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    noBg.on('pointerover', () => { noBg.setStrokeStyle(1, 0x9a6020); noTxt.setColor('#c8973a'); });
    noBg.on('pointerout',  () => { noBg.setStrokeStyle(1, 0x5a3600); noTxt.setColor('#a07840'); });
    noBg.on('pointerdown', cleanup);
    noTxt.on('pointerdown', cleanup);
    els.push(noBg, noTxt);
  }

  /** Toggle an overlay scene: clicking same scene closes it; clicking a different one closes others first */
  /** Returns true when at least one boss-tier enemy is alive on the field.
   *  Used to gate tree-menu opens during boss fights so the player can't
   *  refresh cooldowns / swap nodes mid-encounter (exploit-tier abuse). */
  private isBossActive(): boolean {
    return this.enemySprites.some((es) =>
      es?.active && !!es.enemyRef && es.enemyRef.tier === 'boss' && es.enemyRef.currentHp > 0,
    );
  }

  private launchOverlay(key: string): void {
    const overlayKeys = ['HeroScene', 'InventoryScene', 'AchievementScene', 'TomesScene', 'PassiveTreeScene'];
    // HeroScene + InventoryScene are camera-clipped to the right half of
    // the canvas (`setupCamera(.., 'overlay')` — see dpr.ts). The merchant
    // panel sits on the LEFT half (panel center x≈130, width 240). Per
    // user request, allow the merchant to coexist with these two
    // overlays so the player can browse / equip while shopping; they
    // never visually overlap. All other overlays (Achievements, Tomes,
    // Tree) still tear the merchant down because they're full-canvas.
    const isRightHalfOverlay = key === 'HeroScene' || key === 'InventoryScene';
    if (this.scene.isActive(key)) {
      // Same menu — close it (toggle off)
      this.sfx?.play('close', { volume: 0.5 });
      this.scene.stop(key);
      // Tree pauses BattleScene on open (planning popup, no live combat
      // running underneath). Toggling off here also resumes — closeTree()
      // covers the X / ESC paths.
      if (key === 'PassiveTreeScene') this.resumeBattle();
      return;
    }
    // Boss-fight guard for the rune tree: opening it mid-boss would let
    // the player commit a fresh node (refreshing applied bonuses, swapping
    // keystones) right as the boss is in execute range — the kind of
    // exploit the popup design is supposed to prevent. Block the open
    // entirely and surface why.
    if (key === 'PassiveTreeScene' && this.isBossActive()) {
      this.showAnnouncement('Rune Tree locked during boss fights');
      return;
    }
    this.sfx?.play('open', { volume: 0.55 });
    // Close merchant popup when opening a FULL-CANVAS overlay scene. The
    // right-half overlays (Heroes / Inventory) coexist with the left-
    // anchored merchant panel since their cameras don't overlap, so we
    // skip the teardown for those keys.
    if (this.merchantShopActive && !isRightHalfOverlay) {
      this.merchantShopActive = false;
      const toDestroy: Phaser.GameObjects.GameObject[] = [];
      this.children.each((child) => {
        const d = (child as any).depth;
        if (typeof d === 'number' && d >= 69 && d < 300) toDestroy.push(child);
      });
      toDestroy.forEach((c) => c.destroy());
      ['merchant_overlay', 'merchant_popup_el'].forEach((nm) => {
        this.children.list.filter((c: any) => c.name === nm).forEach((c) => c.destroy());
      });
    }
    // Close any other open overlay
    for (const k of overlayKeys) {
      if (k !== key && this.scene.isActive(k)) {
        this.scene.stop(k);
        // If HeroScene was stopped externally (not via closeScene), ensure BattleScene resumes
        if (k === 'HeroScene') this.resumeBattle();
      }
    }
    this.time.delayedCall(50, () => {
      if (!this.scene.isActive(key)) {
        this.scene.launch(key, { gsm: this.gsm, battleScene: this, infernoMode: this.infernoMode, from: 'BattleScene' });
        // Tree menu pauses the live battle so it reads as a true planning
        // popup — heroes don't auto-attack while the player rearranges
        // nodes, enemies don't keep advancing, and cooldowns / wave timers
        // freeze. Resume happens in PassiveTreeScene.closeTree() on X/ESC,
        // and in launchOverlay's toggle-off branch when the player taps
        // the Tree button a second time.
        if (key === 'PassiveTreeScene') this.scene.pause();
      }
    });
  }

  // ==================== AUDIO ====================

  private startBgm(): void {
    // Use static reference so music persists across scene restarts (level transitions)
    if (BattleScene.battleBgm) {
      try {
        const ws = BattleScene.battleBgm as Phaser.Sound.WebAudioSound;
        if (ws.isPlaying || ws.isPaused) {
          // Already playing or paused — just assign local ref
          this.bgm = BattleScene.battleBgm;
          return;
        }
      } catch (_e) { /* sound was destroyed, recreate below */ }
    }

    // Shadowrealm BGM override — when the player is in the boss arena or
    // endless run, hard-pin to the dedicated tracks instead of joining the
    // standard rotation. Each track gracefully falls back to the default
    // pool if the audio file isn't shipped yet (cache.audio.exists check).
    let key = '';
    if (BattleScene.shadowrealmPending) {
      const sp = BattleScene.shadowrealmPending;
      const wantKey = sp.endlessActive ? 'bgm_shadowrealm_endless' : 'bgm_shadowrealm_boss';
      if (this.cache.audio.exists(wantKey)) key = wantKey;
    }
    if (!key) {
      // Battle BGM rotation — pick a random loaded track each time the music
      // starts so combat doesn't loop the same melody forever. Falls back to
      // bgm_main if neither battle track is loaded (early bootstrap path).
      const candidates = ['bgm_battle', 'bgm_censer'].filter((k) => this.cache.audio.exists(k));
      key = candidates.length > 0
        ? candidates[Math.floor(Math.random() * candidates.length)]
        : (this.cache.audio.exists('bgm_main') ? 'bgm_main' : '');
    }
    if (!key || !this.cache.audio.exists(key)) return;
    const bgm = this.sound.add(key, { loop: true, volume: BattleScene.savedVolume });
    BattleScene.battleBgm = bgm;
    this.bgm = bgm;
    if (BattleScene.savedMuted) {
      bgm.play();
      bgm.pause();
    } else {
      bgm.play();
    }
  }

  private stopBgm(): void {
    BattleScene.stopBattleMusic();
    this.bgm = null;
  }

  /** Clean quit to main menu — same flow as the death-recap "MENU" button */
  quitToMenu(): void {
    this.gsm.save();
    BattleScene.stopBattleMusic();
    this.cleanupBattle();
    this.scene.stop('BattleScene');
    this.scene.start('MenuScene', {});
  }

  /** Stop battle music globally (called from MenuScene on Save & Quit) */
  static stopBattleMusic(): void {
    if (BattleScene.battleBgm) {
      BattleScene.battleBgm.stop();
      BattleScene.battleBgm.destroy();
      BattleScene.battleBgm = null;
    }
  }

  // ==================== TOME CHOICE POPUP ====================

  /** Show the zone-cleared popup after pending tome/talent choices resolve.
   *  Was previously named `autoAdvanceOrInfernoPause` and auto-advanced when
   *  Auto-Run was on; the player asked for Auto-Run to only auto-pick books
   *  and talents, so the advance path is gone — the popup always shows. */
  private autoAdvanceOrInfernoPause(): void {
    this.zonePaused = true;
    this.showZoneClearedPopup();
  }

  private drainPendingAfterTome(): void {
    if (this.pendingTomeChoices > 0) {
      this.pendingTomeChoices--;
      this.time.delayedCall(300, () => this.showTomeChoice());
    } else if (this.pendingTalentChoices.length > 0) {
      this.time.delayedCall(300, () => this.processNextTalentChoice());
    } else if (this.pendingZoneCleared) {
      this.time.delayedCall(300, () => this.showZoneClearedPopup());
    } else if (this.pendingAutoAdvance) {
      this.pendingAutoAdvance = false;
      this.autoAdvanceOrInfernoPause();
    }
  }

  /** Key used to persist pending tome choices across F5 — prevents reroll exploit. */
  private pendingTomesKey(): string { return `roe_pending_tomes_${this.gsm.player.saveSlot}`; }

  private showTomeChoice(restoredChoices?: TomeId[]): void {
    if (this.gameOver) return;
    // Queue the choice if another mandatory popup is currently blocking, so it is never
    // silently dropped. Includes goblinoShopActive / merchantShopActive so the "CHOOSE A
    // BOOK" panel doesn't render on top of the Mimic's Chest trophy picker (the stacked
    // overlap with "MIMIC'S CHEST / CHOOSE A BOOK" both visible at once).
    if (this.tomeChoiceActive || this.talentChoiceActive || this.zoneClearedActive
        || this.goblinoShopActive || this.merchantShopActive) {
      this.pendingTomeChoices++;
      return;
    }

    // Skip if all chosen tomes are at max level — but still drain any pending queues
    if (allChosenTomesMaxed(this.gsm.player.tomes, this.gsm.player.chosenTomeTypes)) {
      localStorage.removeItem(this.pendingTomesKey());
      this.drainPendingAfterTome();
      return;
    }

    // Use restored choices from F5 if provided; otherwise roll fresh.
    // Pre-run banned books (from BookBanScene) are passed through so they never appear
    // as a roll option — the ban is enforced at roll time, not just at display time.
    const choices = restoredChoices ?? rollTomeChoices(
      this.gsm.player.chosenTomeTypes, 3, this.gsm.player.tomes,
      (id) => this.gsm.isTomeUnlocked(id),
      this.gsm.player.bannedTomes ?? [],
    );
    if (choices.length === 0) {
      localStorage.removeItem(this.pendingTomesKey());
      this.drainPendingAfterTome();
      return;
    }

    // Persist the choices so an F5 during the popup restores the same options (prevents reroll exploit)
    if (!restoredChoices) {
      localStorage.setItem(this.pendingTomesKey(), JSON.stringify(choices));
    }

    // Auto-run: auto-pick without showing any popup — do NOT close overlay scenes
    if (this.gsm.player.autoRun) {
      localStorage.removeItem(this.pendingTomesKey());
      const pick = choices[Math.floor(Math.random() * choices.length)];
      const def = TOME_DEFINITIONS[pick];
      const newLvl = this.gsm.pickTome(pick);
      if (pick === 'chaos') this.applyChaosBonus();
      if (newLvl >= def.maxLevel) { this.gsm.trackTomeMaxed(); this.gsm.checkAchievements(); }
      this.gsm.save();
      this.showAnnouncement(`${def.icon} ${def.name} → Level ${newLvl}!`);
      // Drain any further pending choices (more tomes, talent points, zone-clear advance)
      this.drainPendingAfterTome();
      return;
    }

    // Close any overlay scenes so the popup is clickable (only when popup will actually show)
    this.closeOverlayScenes();
    // Close vendor popups so they don't persist under mandatory choice popups.
    // Merchant's drop-shadow sits at depth 69 (one below the overlay), so the depth sweep
    // must include 69 — otherwise the shadow rectangle lingers behind the new popup.
    if (this.merchantShopActive || this.goblinoShopActive) {
      this.merchantShopActive = false;
      this.goblinoShopActive = false;
      const toDestroy: Phaser.GameObjects.GameObject[] = [];
      this.children.each((child) => {
        const d = (child as any).depth;
        if (typeof d === 'number' && d >= 69 && d < 300) toDestroy.push(child);
      });
      toDestroy.forEach((c) => c.destroy());
      // Also sweep by name in case any merchant element is outside the depth range.
      ['merchant_overlay', 'merchant_popup_el'].forEach((nm) => {
        this.children.list.filter((c: any) => c.name === nm).forEach((c) => c.destroy());
      });
    }

    this.tomeChoiceActive = true;

    const width = GAME_W, height = GAME_H;
    const elements: Phaser.GameObjects.GameObject[] = [];

    // Overlay background — fades in from 0 to 0.85 alpha so the popup
    // doesn't slam onto the screen.
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x050402, 0)
      .setDepth(70).setInteractive();
    elements.push(overlay);
    this.tweens.add({ targets: overlay, fillAlpha: 0.85, duration: 300, ease: 'Quad.easeOut' });

    // Title — engraved-rune feel: bronze with deep stroke + dim subtitle.
    // Pushed up to make room for taller (rectangular) cards below.
    const titleY = height / 2 - 180;
    const title = this.add.text(width / 2, titleY - 18, '◆  CHOOSE A BOOK  ◆', {
      fontSize: '20px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: '#c8973a', fontStyle: 'bold',
      stroke: '#0a0608', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(71).setAlpha(0);
    elements.push(title);
    this.tweens.add({
      targets: title, y: titleY, alpha: 1,
      duration: 400, delay: 80, ease: 'Back.easeOut',
    });

    // Build limit info — subtle fade-in just after title.
    const chosen = this.gsm.player.chosenTomeTypes.length;
    const limitText = chosen >= MAX_TOME_TYPES
      ? `Build locked: ${chosen}/${MAX_TOME_TYPES} types chosen`
      : `${chosen}/${MAX_TOME_TYPES} book types chosen — ${MAX_TOME_TYPES - chosen} slot(s) left`;
    const subtitle = this.add.text(width / 2, height / 2 - 154, limitText, {
      fontSize: '10px', fontFamily: 'Georgia, serif',
      color: chosen >= MAX_TOME_TYPES ? '#8a4020' : '#6b4020',
    }).setOrigin(0.5).setDepth(71).setAlpha(0);
    elements.push(subtitle);
    this.tweens.add({ targets: subtitle, alpha: 1, duration: 300, delay: 220, ease: 'Quad.easeOut' });

    // Tome choice cards — tighter layout so the row always fits + has comfortable margin.
    // Previously maxCardW=200 + gap=16 could put the rightmost card's edge 100 px from
    // the screen edge on wide scenes, but at certain scale factors the end card clipped
    // off-screen and text ("Book of Chaos") got cut. Also added a `maxTotalW` clamp so
    // 4+ choices don't overlap each other.
    const SIDE_PAD = 40;
    const availW = width - SIDE_PAD * 2;
    const maxCardW = 180;
    const gap = 14;
    const rawCardW = Math.floor((availW - (choices.length - 1) * gap) / choices.length);
    const cardW = Math.max(120, Math.min(maxCardW, rawCardW));
    const totalW = choices.length * cardW + (choices.length - 1) * gap;
    // If totalW somehow exceeds availW (tiny scene, many choices), re-derive cardW to
    // guarantee no overlap even at the cost of a very small card.
    const safeCardW = totalW > availW
      ? Math.floor((availW - (choices.length - 1) * gap) / choices.length)
      : cardW;
    const safeTotalW = choices.length * safeCardW + (choices.length - 1) * gap;
    const startX = (width - safeTotalW) / 2 + safeCardW / 2;

    // Shared pick-lock so a rapid second click on a different card during the
    // ~350 ms card-flash animation window is ignored — otherwise two pickTome
    // calls would land before the popup tore down.
    let pickLocked = false;

    choices.forEach((tomeId, i) => {
      const def = TOME_DEFINITIONS[tomeId];
      const currentLvl = getTomeLevel(this.gsm.player.tomes, tomeId);
      const isNew = !this.gsm.player.chosenTomeTypes.includes(tomeId);
      const cx = startX + i * (safeCardW + gap);
      const cy = height / 2 + 10;

      // ── Card-as-TCG-card — every visual element lives in a container so
      //    the whole thing can lift / scale / glow on hover as one unit.
      //    Rounded corners via Graphics.fillRoundedRect (no more sharp
      //    rectangles). Per-card container origin sits at scene origin
      //    so children retain their absolute positions; tweening
      //    container.y gives a uniform lift, and container.scale grows
      //    each child from the absolute origin (we counter that with a
      //    Container at (cx, cy) below — children get pos relative to
      //    card center so scale grows from the card center cleanly).
      // ── Diablo II "stone tablet" aesthetic. The bright tome color is now
      //    used as a restrained accent (rune chip + bonus number + hover
      //    glow) rather than the full card border. The banner is carved
      //    bronze on dark stone, not a flat candy-gold bar.
      const tomeColorRgb = Phaser.Display.Color.HexStringToColor(def.color).color;
      // Desaturated tome accent — half-mixed with dark bronze so it reads
      // as a rune-ink stain on stone, not a neon highlight.
      const accentRgb = Phaser.Display.Color.IntegerToColor(tomeColorRgb);
      const accentBlended = Phaser.Display.Color.Interpolate.ColorWithRGB(
        accentRgb, 0x3a, 0x28, 0x10, 100, 55,
      );
      const tomeAccent = Phaser.Display.Color.GetColor(accentBlended.r, accentBlended.g, accentBlended.b);
      const cardW = safeCardW;
      const cardH = 240;     // taller rectangular tablet — was 200 (close to square)
      const CARD_RADIUS    = 4;             // tighter — stone tablet, not parchment
      const STONE_DARK     = 0x080507;
      const BRONZE_DEEP    = 0x2a1d10;
      const BRONZE_MID     = 0x4a3220;
      const BRONZE_RIM     = 0x8a6420;
      const BRONZE_GLINT   = 0xb38a5a;
      const TEXT_GOLD      = 0xc8973a;
      // Container anchored at the card's centre — all children use
      // relative coords so container.scale grows everything from the
      // middle, not from scene origin.
      const cardContainer = this.add.container(cx, cy).setDepth(71);
      elements.push(cardContainer);
      // 0. Hover glow halo — soft tome-color rune-glow. Smaller than before
      //    so it reads as ambient inscription light, not a neon outline.
      const glowGfx = this.add.graphics();
      glowGfx.fillStyle(tomeColorRgb, 1);
      glowGfx.fillRoundedRect(-cardW / 2 - 8, -cardH / 2 - 8, cardW + 16, cardH + 16, CARD_RADIUS + 3);
      glowGfx.setAlpha(0);
      cardContainer.add(glowGfx);
      // 1. Drop shadow.
      const cardShadow = this.add.graphics();
      cardShadow.fillStyle(0x000000, 0.6);
      cardShadow.fillRoundedRect(-cardW / 2 + 2, -cardH / 2 + 6, cardW, cardH, CARD_RADIUS);
      cardContainer.add(cardShadow);
      // 2-3. Card body: flat deep-stone fill, wrapped in a double dark-
      //      bronze frame. Earlier version painted a half-fill highlight
      //      across the top to suggest carved relief, but it produced a
      //      visible horizontal seam at mid-card — removed; the frame +
      //      corner ornaments carry enough texture on their own.
      //      Fill alpha 0.82 (was 0.98) so the zone background bleeds
      //      through subtly, giving the cards a tablet-floating-in-the-
      //      gloom feel instead of opaque blackboards.
      const cardBgGfx = this.add.graphics();
      cardBgGfx.fillStyle(STONE_DARK, 0.82);
      cardBgGfx.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, CARD_RADIUS);
      cardBgGfx.lineStyle(2, BRONZE_MID, 1);
      cardBgGfx.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, CARD_RADIUS);
      cardBgGfx.lineStyle(1, BRONZE_DEEP, 0.85);
      cardBgGfx.strokeRoundedRect(-cardW / 2 - 2, -cardH / 2 - 2, cardW + 4, cardH + 4, CARD_RADIUS + 1);
      cardContainer.add(cardBgGfx);
      // Hit zone.
      const cardHit = this.add.rectangle(0, 0, cardW, cardH, 0x000000, 0).setInteractive({ useHandCursor: true });
      cardContainer.add(cardHit);
      // 4. Carved bronze plaque banner — dark bronze body with a bronze rim
      //    highlight at top and a tome-colored rune chip embedded at the
      //    bottom rim. Reads as "engraved tablet header", not "candy bar".
      const bannerH = 30;
      const bannerYRel = -cardH / 2 + bannerH / 2 + 5;
      const bannerW = cardW - 14;
      const bannerGfx = this.add.graphics();
      // Body
      bannerGfx.fillStyle(BRONZE_DEEP, 0.98);
      bannerGfx.fillRoundedRect(-bannerW / 2, bannerYRel - bannerH / 2, bannerW, bannerH, 2);
      // Top rim highlight (lit edge)
      bannerGfx.fillStyle(BRONZE_RIM, 0.55);
      bannerGfx.fillRect(-bannerW / 2 + 2, bannerYRel - bannerH / 2 + 1, bannerW - 4, 2);
      // Bronze stroke
      bannerGfx.lineStyle(1, BRONZE_RIM, 0.95);
      bannerGfx.strokeRoundedRect(-bannerW / 2, bannerYRel - bannerH / 2, bannerW, bannerH, 2);
      // Tome-colored rune accent strip across the bottom of the banner
      bannerGfx.fillStyle(tomeAccent, 0.95);
      bannerGfx.fillRect(-bannerW / 2 + 8, bannerYRel + bannerH / 2 - 2, bannerW - 16, 2);
      cardContainer.add(bannerGfx);
      // 5. Title text — bright bronze on dark bronze (NOT dark on light
      //    gold — too "casino"). Stroke for crispness against the relief.
      const title = this.add.text(0, bannerYRel, def.name, {
        fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#d4a84a', fontStyle: 'bold',
        stroke: '#0a0608', strokeThickness: 2,
        wordWrap: { width: bannerW - 8 }, align: 'center',
        padding: { y: 2 },
      }).setOrigin(0.5).setResolution(2);
      cardContainer.add(title);
      // 6. Carved inset frame — thin double bronze line below the banner,
      //    suggesting an inner panel cut into the stone tablet.
      const innerFrameTopY = bannerYRel + bannerH / 2 + 4;
      const innerFrameH = cardH - bannerH - 22;
      const frameGfx = this.add.graphics();
      frameGfx.lineStyle(1, BRONZE_MID, 0.85);
      frameGfx.strokeRoundedRect(-cardW / 2 + 6, innerFrameTopY, cardW - 12, innerFrameH, 2);
      frameGfx.lineStyle(0.5, BRONZE_DEEP, 0.6);
      frameGfx.strokeRoundedRect(-cardW / 2 + 8, innerFrameTopY + 2, cardW - 16, innerFrameH - 4, 2);
      cardContainer.add(frameGfx);
      // 7. Four L-shaped corner ornaments — dim bronze, not bright gold.
      const cornerGfx = this.add.graphics();
      cornerGfx.lineStyle(1, BRONZE_GLINT, 0.85);
      const fx0 = -cardW / 2 + 6,            fy0 = innerFrameTopY;
      const fx1 = cardW / 2 - 6,             fy1 = innerFrameTopY + innerFrameH;
      const cornerLen = 7;
      cornerGfx.lineBetween(fx0 + 2, fy0 + 2, fx0 + 2 + cornerLen, fy0 + 2);
      cornerGfx.lineBetween(fx0 + 2, fy0 + 2, fx0 + 2,             fy0 + 2 + cornerLen);
      cornerGfx.lineBetween(fx1 - 2, fy0 + 2, fx1 - 2 - cornerLen, fy0 + 2);
      cornerGfx.lineBetween(fx1 - 2, fy0 + 2, fx1 - 2,             fy0 + 2 + cornerLen);
      cornerGfx.lineBetween(fx0 + 2, fy1 - 2, fx0 + 2 + cornerLen, fy1 - 2);
      cornerGfx.lineBetween(fx0 + 2, fy1 - 2, fx0 + 2,             fy1 - 2 - cornerLen);
      cornerGfx.lineBetween(fx1 - 2, fy1 - 2, fx1 - 2 - cornerLen, fy1 - 2);
      cornerGfx.lineBetween(fx1 - 2, fy1 - 2, fx1 - 2,             fy1 - 2 - cornerLen);
      cardContainer.add(cornerGfx);
      // 8. Framed runic glyph — icon sits in a chiselled niche so it reads
      //    as an inscribed sigil, not a floating emoji. Niche has 12 px of
      //    breathing room below the banner (was 4) so the whole content
      //    stack reads as floating in the card body, not jammed against
      //    the banner / card top edge.
      const iconBoxSize = 44;
      const iconYRel = bannerYRel + bannerH / 2 + 12 + iconBoxSize / 2;
      const iconBoxGfx = this.add.graphics();
      iconBoxGfx.fillStyle(0x000000, 0.55);
      iconBoxGfx.fillRoundedRect(-iconBoxSize / 2, iconYRel - iconBoxSize / 2, iconBoxSize, iconBoxSize, 3);
      iconBoxGfx.lineStyle(1, BRONZE_MID, 0.95);
      iconBoxGfx.strokeRoundedRect(-iconBoxSize / 2, iconYRel - iconBoxSize / 2, iconBoxSize, iconBoxSize, 3);
      iconBoxGfx.lineStyle(0.5, tomeAccent, 0.7);
      iconBoxGfx.strokeRoundedRect(-iconBoxSize / 2 + 2, iconYRel - iconBoxSize / 2 + 2, iconBoxSize - 4, iconBoxSize - 4, 2);
      cardContainer.add(iconBoxGfx);
      const icon = this.add.text(0, iconYRel, def.icon, {
        fontSize: '28px', padding: { y: 4 },
        fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        fontStyle: 'bold', color: def.color, stroke: '#0a0608', strokeThickness: 3,
      }).setOrigin(0.5).setResolution(2);
      cardContainer.add(icon);
      // ── Hover animation — lift + scale + glow + brighten frame stroke.
      //    Smooth in (Back.easeOut) / out (Quad.easeIn) for the TCG feel.
      cardHit.on('pointerover', () => {
        this.tweens.killTweensOf(cardContainer);
        this.tweens.killTweensOf(glowGfx);
        // Force alpha back to 1 in case the entrance tween was killed
        // mid-fade (user hovered the card before its entrance completed —
        // killTweensOf would otherwise leave the container at an
        // intermediate alpha and the card would render translucent).
        this.tweens.add({
          targets: cardContainer,
          y: cy - 12,
          scale: 1.05,
          alpha: 1,
          duration: 220,
          ease: 'Back.easeOut',
        });
        this.tweens.add({
          targets: glowGfx,
          alpha: 0.5,
          duration: 220,
          ease: 'Quad.easeOut',
        });
        // Bring this card to the very top of the popup so neighbour cards
        // can't clip the lifted edges.
        cardContainer.setDepth(72);
      });
      cardHit.on('pointerout', () => {
        this.tweens.killTweensOf(cardContainer);
        this.tweens.killTweensOf(glowGfx);
        this.tweens.add({
          targets: cardContainer,
          y: cy,
          scale: 1.0,
          alpha: 1, // belt-and-braces: ensure card is fully visible after hover
          duration: 180,
          ease: 'Quad.easeIn',
        });
        this.tweens.add({
          targets: glowGfx,
          alpha: 0,
          duration: 180,
          ease: 'Quad.easeIn',
        });
        cardContainer.setDepth(71);
      });
      // ── Entrance animation — staggered fade-in + slide-up so the
      //    popup feels alive when it opens.
      cardContainer.setAlpha(0);
      cardContainer.y = cy + 24;
      this.tweens.add({
        targets: cardContainer,
        y: cy,
        alpha: 1,
        duration: 380,
        delay: 80 + i * 90,
        ease: 'Back.easeOut',
      });

      // Name — REMOVED. The book name is now displayed only in the gold
      // title banner at the top of the card; the previous mid-card name
      // text would just duplicate it.
      // Description — muted bronze italic, like a tooltip inscription
      // rather than headline text.
      const descTopYRel = iconYRel + iconBoxSize / 2 + 8;
      const desc = this.add.text(0, descTopYRel, def.description, {
        fontSize: '10px', fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#8a6a3a',
        wordWrap: { width: safeCardW - 20 }, align: 'center',
      }).setOrigin(0.5, 0);
      cardContainer.add(desc);

      const afterDescYRel = descTopYRel + desc.height + 5;

      // Current level + new-type indicator — dim by default, recruit-green for NEW.
      const lvlLabel = isNew
        ? 'NEW TYPE'
        : currentLvl > 0 ? `Level ${currentLvl} → ${currentLvl + 1}` : 'Level 0 → 1';
      const lvl = this.add.text(0, afterDescYRel, lvlLabel, {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: isNew ? '#7acc60' : '#7a6540',
      }).setOrigin(0.5, 0);
      cardContainer.add(lvl);

      // Bonus preview — the one place where the tome color shines through.
      // Per-pick increment, plus current→next total for existing tomes.
      const perPick = def.bonusPerLevel;
      const fmtBonus = (v: number) => {
        if (def.bonusUnit.startsWith('%')) {
          const pct = v * 100;
          const fmt = Number.isInteger(pct) ? String(pct) : parseFloat(pct.toFixed(1));
          return `+${fmt}${def.bonusUnit}`;
        }
        return `+${parseFloat(v.toFixed(2))}${def.bonusUnit}`;
      };
      const bonusStr = fmtBonus(perPick);
      const bonusYRel = afterDescYRel + 14;
      const bonus = this.add.text(0, bonusYRel, bonusStr, {
        fontSize: '13px', fontFamily: 'Georgia, serif', color: def.color, fontStyle: 'bold',
        stroke: '#0a0608', strokeThickness: 2,
      }).setOrigin(0.5, 0);
      cardContainer.add(bonus);

      // Current total → next total (only when already levelled) — muted, secondary.
      if (currentLvl > 0) {
        const currTotal = fmtBonus(currentLvl * perPick);
        const nextTotal = fmtBonus((currentLvl + 1) * perPick);
        const totalLine = this.add.text(0, bonusYRel + 16, `${currTotal} → ${nextTotal}`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a4a30',
        }).setOrigin(0.5, 0);
        cardContainer.add(totalLine);
      }

      // Pick this tome — wired to the invisible cardHit zone inside the
      // container (since the old `card` Rectangle is replaced by the
      // Graphics-based BG, which can't take pointer events directly).
      cardHit.on('pointerdown', () => {
        if (pickLocked) return;
        pickLocked = true;
        this.sfx.play('book_choice', { volume: 0.75 });
        // Card-flash animation — scale pulse on the WHOLE container so the
        // pick reads as one cohesive card beat. Sparkle burst + gold ring
        // come from the shared playTomePickFlash helper.
        const cardColor = Phaser.Display.Color.HexStringToColor(def.color).color;
        this.tweens.killTweensOf(cardContainer);
        this.tweens.add({
          targets: cardContainer,
          scale: 1.18, duration: 160, yoyo: true, ease: 'Sine.easeOut',
        });
        // null card → helper skips the per-card scale + stroke flash
        // (we own those via the container tween above).
        this.vfx.playTomePickFlash(null, cx, cy, cardColor);
        BattleScene.justPickedTomeId = tomeId;
        // Delay the actual pick + popup tear-down so the flash is visible —
        // player sees the card burst, THEN the popup closes into the HUD pulse.
        this.time.delayedCall(350, () => {
        const newLvl = this.gsm.pickTome(tomeId);
        let chaosRollLabel = '';
        if (tomeId === 'chaos') chaosRollLabel = this.applyChaosBonus();
        if (newLvl >= def.maxLevel) { this.gsm.trackTomeMaxed(); this.gsm.checkAchievements(); }
        // Log the book pick with stat detail — Chaos gets the rolled stat appended
        this.addChoiceLog({
          kind: 'book', icon: def.icon ?? '📖',
          label: `${def.name} → Lv${newLvl}`,
          detail: chaosRollLabel
            ? `${def.description ?? ''}  →  rolled ${chaosRollLabel}`
            : (def.description ?? ''),
        });
        // Apply Tome of Vitality delta to already-spawned heroes
        if (tomeId === 'health') {
          const newBonus = getTomeBonus(this.gsm.player.tomes, 'health');
          for (const hs of this.heroSprites) {
            // Strip the previous tome contribution, then compute the new tome bonus off the
            // PURE maxHp so vitality idol / elixir_fortitude can't compound with the tome.
            const prevAppliedHp = (hs.heroRef as any)._vitalityAppliedHp as number || 0;
            hs.heroRef.stats.maxHp -= prevAppliedHp;
            (hs.heroRef as any)._vitalityAppliedHp = 0;
            const pureBase = this.getPureMaxHp(hs.heroRef);
            const newVitalityHp = Math.floor(pureBase * newBonus);
            hs.heroRef.stats.maxHp += newVitalityHp;
            const hpDelta = newVitalityHp - prevAppliedHp;
            if (hpDelta !== 0) hs.heroRef.stats.hp = Math.max(1, Math.min(hs.heroRef.stats.hp + hpDelta, hs.heroRef.stats.maxHp));
            (hs.heroRef as any)._vitalityAppliedHp = newVitalityHp;
            (hs.heroRef as any)._vitalityTomeLevel = newBonus;
          }
        }
        this.gsm.save();

        // Destroy all popup elements
        for (const el of elements) el.destroy();

        localStorage.removeItem(this.pendingTomesKey());
        this.tomeChoiceActive = false;
        // Per-run reroll budget — DON'T reset on pick. Only resetRun zeroes
        // tomeRerollsUsed; this commit just keeps the persisted count.

        // Show pick confirmation above all popups (depth 200) so it's always readable
        // even when a talent/zone-clear popup opens immediately after
        {
          const { width: cw } = this.cameras.main;
          const confirmTxt = this.add.text(cw / 2, 48, `${def.icon} ${def.name} → Level ${newLvl}!`, {
            fontSize: '14px', fontFamily: 'Georgia, serif', color: '#f0e080', fontStyle: 'bold',
            backgroundColor: '#0d0a08cc', padding: { x: 14, y: 6 },
            stroke: '#3a2800', strokeThickness: 2,
          }).setResolution(2).setOrigin(0.5).setDepth(200);
          this.tweens.add({ targets: confirmTxt, alpha: 0, delay: 2200, duration: 400, onComplete: () => confirmTxt.destroy() });
        }

        // Process talent choices first; talent handler chains to zone-cleared/auto-advance when done.
        if (this.pendingTalentChoices.length > 0) {
          this.time.delayedCall(300, () => this.processNextTalentChoice());
        } else if (this.pendingTomeChoices > 0) {
          this.pendingTomeChoices--;
          this.time.delayedCall(300, () => this.showTomeChoice());
        } else if (this.pendingZoneCleared) {
          this.time.delayedCall(300, () => this.showZoneClearedPopup());
        } else if (this.pendingAutoAdvance) {
          this.pendingAutoAdvance = false;
          this.autoAdvanceOrInfernoPause();
        }
        }); // close delayedCall body
      });   // close card.on pointerdown handler
    });     // close choices.forEach

    // ── Reroll button — Tome Insight (bookRerolls) is now PER RUN, not
    // per-popup. Total available per RUN = 1 + bookRerolls (max 4 at full
    // rank). Persisted in player.tomeRerollsUsed; only resetRun zeroes it. ──
    const pbReroll = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    const totalRerolls = 1 + (pbReroll.bookRerolls ?? 0);
    // Migrate legacy boolean → count if save predates the count field.
    if (this.gsm.player.tomeRerollsUsed === undefined) {
      this.gsm.player.tomeRerollsUsed = this.gsm.player.tomeRerollUsed ? 1 : 0;
    }
    const usedRerolls = this.gsm.player.tomeRerollsUsed ?? 0;
    const rerollUsed = usedRerolls >= totalRerolls;
    const remaining = Math.max(0, totalRerolls - usedRerolls);
    // Carved stone secondary buttons — dim by default so they don't
    // compete with the cards, brighten + scale on hover. Sit ABOVE cards
    // by depth (90 > card 71) so pointer events reach them; the visual
    // recession comes from alpha (0.55 default → 1.0 hover) and a tighter
    // colour palette, NOT from being z-buried under the card hit zones
    // (that broke clicks + hover entirely in the previous attempt).
    // Vertical position is below card bottom (cards now 240 tall, anchor
    // at height/2 + 10 → bottom at height/2 + 130) plus 30 px clearance.
    const REROLL_Y = height / 2 + 160;
    const REROLL_DEPTH_BASE = 90;
    const REROLL_DEPTH_HOVER = 95;
    const REROLL_ALPHA_BASE = rerollUsed ? 0.35 : 0.55;
    const REROLL_ALPHA_HOVER = rerollUsed ? 0.45 : 1.0;
    const rerollOuter = this.add.rectangle(width / 2 - 78, REROLL_Y, 144, 32, 0x080507, 1)
      .setStrokeStyle(1, rerollUsed ? 0x2a1d10 : 0x4a3220, 0.9)
      .setDepth(REROLL_DEPTH_BASE).setAlpha(REROLL_ALPHA_BASE);
    elements.push(rerollOuter);
    const rerollBtnBg = this.add.rectangle(width / 2 - 78, REROLL_Y, 138, 26, 0x0a0608, 1)
      .setStrokeStyle(1, rerollUsed ? 0x2a1d10 : 0x3d5030, 0.85).setDepth(REROLL_DEPTH_BASE + 0.5).setAlpha(REROLL_ALPHA_BASE);
    elements.push(rerollBtnBg);
    const rerollLabel = rerollUsed ? '⟳ Rerolled' : `⟳ Reroll  ·  ${remaining}× left`;
    const rerollBtn = this.add.text(width / 2 - 78, REROLL_Y, rerollLabel, {
      fontSize: '11px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
      color: rerollUsed ? '#3a2800' : '#6a9050',
    }).setOrigin(0.5).setDepth(REROLL_DEPTH_BASE + 1).setAlpha(REROLL_ALPHA_BASE);
    elements.push(rerollBtn);
    if (!rerollUsed) {
      wireButton({
        bg: rerollBtnBg, text: rerollBtn,
        onHover: () => {
          rerollBtn.setColor('#a8d488');
          rerollBtnBg.setStrokeStyle(1.5, 0x7acc60, 1);
          rerollOuter.setStrokeStyle(1.5, 0x8a6420, 1);
          rerollOuter.setDepth(REROLL_DEPTH_HOVER);
          rerollBtnBg.setDepth(REROLL_DEPTH_HOVER + 0.5);
          rerollBtn.setDepth(REROLL_DEPTH_HOVER + 1);
          rerollOuter.setAlpha(REROLL_ALPHA_HOVER);
          rerollBtnBg.setAlpha(REROLL_ALPHA_HOVER);
          rerollBtn.setAlpha(REROLL_ALPHA_HOVER);
          rerollOuter.setScale(1.06);
          rerollBtnBg.setScale(1.06);
          rerollBtn.setScale(1.06);
        },
        onOut: () => {
          rerollBtn.setColor('#6a9050');
          rerollBtnBg.setStrokeStyle(1, 0x3d5030, 0.85);
          rerollOuter.setStrokeStyle(1, 0x4a3220, 0.9);
          rerollOuter.setDepth(REROLL_DEPTH_BASE);
          rerollBtnBg.setDepth(REROLL_DEPTH_BASE + 0.5);
          rerollBtn.setDepth(REROLL_DEPTH_BASE + 1);
          rerollOuter.setAlpha(REROLL_ALPHA_BASE);
          rerollBtnBg.setAlpha(REROLL_ALPHA_BASE);
          rerollBtn.setAlpha(REROLL_ALPHA_BASE);
          rerollOuter.setScale(1);
          rerollBtnBg.setScale(1);
          rerollBtn.setScale(1);
        },
        onDown:  () => {
          // Per-run counter: increment, save, never reset until resetRun.
          this.gsm.player.tomeRerollsUsed = (this.gsm.player.tomeRerollsUsed ?? 0) + 1;
          if ((this.gsm.player.tomeRerollsUsed ?? 0) >= totalRerolls) {
            this.gsm.player.tomeRerollUsed = true; // legacy flag aligned
          }
          this.gsm.save();
          for (const el of elements) el.destroy();
          this.tomeChoiceActive = false;
          this.time.delayedCall(50, () => this.showTomeChoice());
        },
      });
    }

    // ── Skip button (costs 20 ◆ first time, doubles each use) — placed BELOW cards ──
    const skipUses = this.gsm.player.tomeSkipCount ?? 0;
    const skipCost = 20 * Math.pow(2, skipUses);
    const canSkip = this.gsm.player.resources.loot >= skipCost;
    // Matching dim-stone frame for the Skip button — same recessed default
    // as Reroll but with blood-bronze accents (the "spend gems" colour
    // language). Pops fully on hover.
    const SKIP_DEPTH_BASE = 90;
    const SKIP_DEPTH_HOVER = 95;
    const SKIP_ALPHA_BASE = canSkip ? 0.55 : 0.3;
    const SKIP_ALPHA_HOVER = canSkip ? 1.0 : 0.4;
    const skipOuter = this.add.rectangle(width / 2 + 78, REROLL_Y, 144, 32, 0x080507, 1)
      .setStrokeStyle(1, canSkip ? 0x4a3220 : 0x2a1d10, 0.9)
      .setDepth(SKIP_DEPTH_BASE).setAlpha(SKIP_ALPHA_BASE);
    elements.push(skipOuter);
    const skipTomeBtnBg = this.add.rectangle(width / 2 + 78, REROLL_Y, 138, 26, 0x0a0608, 1)
      .setStrokeStyle(1, canSkip ? 0x6a2820 : 0x2a1d10, 0.85).setDepth(SKIP_DEPTH_BASE + 0.5).setAlpha(SKIP_ALPHA_BASE);
    elements.push(skipTomeBtnBg);
    const skipBtn = this.add.text(width / 2 + 78, REROLL_Y, `Skip  ·  ${skipCost} ◆`, {
      fontSize: '11px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
      color: canSkip ? '#a05030' : '#3a2800',
    }).setOrigin(0.5).setDepth(SKIP_DEPTH_BASE + 1).setAlpha(SKIP_ALPHA_BASE);
    elements.push(skipBtn);
    if (canSkip) {
      wireButton({
        bg: skipTomeBtnBg, text: skipBtn,
        onHover: () => {
          skipBtn.setColor('#e69060');
          skipTomeBtnBg.setStrokeStyle(1.5, 0xa03020, 1);
          skipOuter.setStrokeStyle(1.5, 0x8a6420, 1);
          skipOuter.setDepth(SKIP_DEPTH_HOVER);
          skipTomeBtnBg.setDepth(SKIP_DEPTH_HOVER + 0.5);
          skipBtn.setDepth(SKIP_DEPTH_HOVER + 1);
          skipOuter.setAlpha(SKIP_ALPHA_HOVER);
          skipTomeBtnBg.setAlpha(SKIP_ALPHA_HOVER);
          skipBtn.setAlpha(SKIP_ALPHA_HOVER);
          skipOuter.setScale(1.06);
          skipTomeBtnBg.setScale(1.06);
          skipBtn.setScale(1.06);
        },
        onOut: () => {
          skipBtn.setColor('#a05030');
          skipTomeBtnBg.setStrokeStyle(1, 0x6a2820, 0.85);
          skipOuter.setStrokeStyle(1, 0x4a3220, 0.9);
          skipOuter.setDepth(SKIP_DEPTH_BASE);
          skipTomeBtnBg.setDepth(SKIP_DEPTH_BASE + 0.5);
          skipBtn.setDepth(SKIP_DEPTH_BASE + 1);
          skipOuter.setAlpha(SKIP_ALPHA_BASE);
          skipTomeBtnBg.setAlpha(SKIP_ALPHA_BASE);
          skipBtn.setAlpha(SKIP_ALPHA_BASE);
          skipOuter.setScale(1);
          skipTomeBtnBg.setScale(1);
          skipBtn.setScale(1);
        },
        onDown:  () => {
          this.gsm.player.resources.loot -= skipCost;
          this.runLootSpent += skipCost;
          this.gsm.player.tomeSkipCount = skipUses + 1;
          // Per-run reroll budget — don't reset on skip either.
          this.gsm.save();
          for (const el of elements) el.destroy();
          localStorage.removeItem(this.pendingTomesKey());
          this.tomeChoiceActive = false;
          // Process talent choices first; talent handler chains to zone-cleared/auto-advance when done.
          if (this.pendingTalentChoices.length > 0) {
            this.time.delayedCall(300, () => this.processNextTalentChoice());
          } else if (this.pendingTomeChoices > 0) {
            this.pendingTomeChoices--;
            this.time.delayedCall(300, () => this.showTomeChoice());
          } else if (this.pendingZoneCleared) {
            this.time.delayedCall(300, () => this.showZoneClearedPopup());
          } else if (this.pendingAutoAdvance) {
            this.pendingAutoAdvance = false;
            this.autoAdvanceOrInfernoPause();
          }
        },
      });
    }
  }

  // ==================== TALENT CHOICE POPUP ====================

  private processNextTalentChoice(): void {
    if (this.talentChoiceActive || this.tomeChoiceActive || this.zoneClearedActive) return;
    if (this.pendingTalentChoices.length === 0) {
      // All talent choices resolved — trigger any deferred auto-advance
      if (this.pendingAutoAdvance && this.gsm.player.autoRun) {
        this.pendingAutoAdvance = false;
        this.autoAdvanceOrInfernoPause();
      }
      return;
    }
    const next = this.pendingTalentChoices.shift()!;
    // Verify the hero still has available talent points
    if (getAvailableTalentPoints(next.hero.level, next.hero.talentBonuses) > 0) {
      this.showTalentChoice(next.heroIndex, next.hero);
    } else {
      // Skip and try next
      this.processNextTalentChoice();
    }
  }

  private showTalentChoice(heroIndex: number, hero: Hero): void {
    if (this.talentChoiceActive || this.gameOver) return;

    // Exclude passive non-upgradeable abilities (maxLevel 1 with no talent definition)
    // Also exclude one-time talents that the hero already has (maxRank reached)
    const abilities = hero.abilities.filter((a) => {
      if (a.maxLevel <= 1 && !getTalentUpgradeInfo(a.id)) return false;
      const info = getTalentUpgradeInfo(a.id);
      if (info?.maxRank !== undefined && (hero.talentBonuses[a.id] || 0) >= info.maxRank) return false;
      return true;
    });
    if (abilities.length === 0) { this.drainPendingAfterTome(); return; }

    // Auto-run: auto-pick without showing any popup — do NOT close overlay scenes
    if (this.gsm.player.autoRun) {
      const pick = abilities[Math.floor(Math.random() * abilities.length)];
      this.gsm.applyTalent(heroIndex, pick.id);
      this.gsm.save();
      const info = getTalentUpgradeInfo(pick.id);
      const pts = hero.talentBonuses[pick.id] || 0;
      const label = info ? `${info.label} (${pts} pts)` : `+1 point`;
      this.showAnnouncement(`${hero.name}: ${pick.name} ${label}`);
      this.time.delayedCall(200, () => this.processNextTalentChoice());
      return;
    }

    // Close any overlay scenes so the popup is clickable (only when popup will actually show)
    this.closeOverlayScenes();
    // Close vendor popups so they don't persist under mandatory talent choice
    if (this.merchantShopActive || this.goblinoShopActive) {
      this.merchantShopActive = false;
      this.goblinoShopActive = false;
      const toDestroy: Phaser.GameObjects.GameObject[] = [];
      this.children.each((child) => {
        const d = (child as any).depth;
        if (typeof d === 'number' && d >= 70 && d < 300) toDestroy.push(child);
      });
      toDestroy.forEach((c) => c.destroy());
    }

    this.talentChoiceActive = true;

    const width = GAME_W, height = GAME_H;
    const elements: Phaser.GameObjects.GameObject[] = [];

    // ── Overlay + entrance animation. Same pattern as the book popup so
    //    the two share a coherent open/close language.
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x050402, 0)
      .setDepth(100).setInteractive();
    elements.push(overlay);
    this.tweens.add({ targets: overlay, fillAlpha: 0.92, duration: 300, ease: 'Quad.easeOut' });

    // Title block sits above the cards. Pushed up further than the book
    // popup because the talent cards are taller (240 px vs 200) — at the
    // old positions the spent-label clipped behind the card top edge.
    const titleY = height / 2 - 180;
    const titleText = this.add.text(width / 2, titleY - 18, '◆  TALENT POINT  ◆', {
      fontSize: '20px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: '#c8973a', fontStyle: 'bold',
      stroke: '#0a0608', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(101).setAlpha(0);
    elements.push(titleText);
    this.tweens.add({ targets: titleText, y: titleY, alpha: 1, duration: 400, delay: 80, ease: 'Back.easeOut' });

    const available = getAvailableTalentPoints(hero.level, hero.talentBonuses);
    const spent = getSpentTalentPoints(hero.talentBonuses);
    const subtitle = this.add.text(width / 2, height / 2 - 156,
      `${hero.name} Lv${hero.level} — ${available} point(s) available`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#7a6540',
    }).setOrigin(0.5).setDepth(101).setAlpha(0);
    elements.push(subtitle);
    this.tweens.add({ targets: subtitle, alpha: 1, duration: 300, delay: 220, ease: 'Quad.easeOut' });
    const spentLabel = this.add.text(width / 2, height / 2 - 140,
      `${spent} / ${MAX_TALENT_POINTS} total spent`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', fontStyle: 'italic',
      color: spent >= MAX_TALENT_POINTS ? '#a02020' : '#8a6a3a',
    }).setResolution(2).setOrigin(0.5).setDepth(101).setAlpha(0);
    elements.push(spentLabel);
    this.tweens.add({ targets: spentLabel, alpha: 1, duration: 300, delay: 260, ease: 'Quad.easeOut' });

    const cardW = 170;
    const cardH = 240;
    const CARD_RADIUS = 4;             // tighter — stone tablet, not parchment
    const gap = 14;
    const STONE_DARK   = 0x080507;
    const BRONZE_DEEP  = 0x2a1d10;
    const BRONZE_MID   = 0x4a3220;
    const BRONZE_RIM   = 0x8a6420;
    const BRONZE_GLINT = 0xb38a5a;
    const totalW = abilities.length * cardW + (abilities.length - 1) * gap;
    const startX = (width - totalW) / 2 + cardW / 2;

    // Shared lock so a double-click on the 350 ms flash window can't fire two
    // applyTalent calls. Mirrors the `pickLocked` used by the tome popup.
    let talentPickLocked = false;

    abilities.forEach((ability, i) => {
      const cx = startX + i * (cardW + gap);
      const cy = height / 2 + 10;
      const currentPoints = hero.talentBonuses[ability.id] || 0;
      const info = getTalentUpgradeInfo(ability.id);
      const maxRank = info?.maxRank ?? 1;
      const isOneTime = maxRank === 1;
      // Container holding the whole card so it lifts/scales/glows as one.
      const cardContainer = this.add.container(cx, cy).setDepth(101);
      elements.push(cardContainer);
      // Glow halo behind the card (alpha 0 by default; tweens to 0.5 on hover).
      // Uses bronze rim glow — talents share one identity colour, unlike
      // tomes which each have their own school tint.
      const glowGfx = this.add.graphics();
      glowGfx.fillStyle(BRONZE_RIM, 1);
      glowGfx.fillRoundedRect(-cardW / 2 - 8, -cardH / 2 - 8, cardW + 16, cardH + 16, CARD_RADIUS + 3);
      glowGfx.setAlpha(0);
      cardContainer.add(glowGfx);
      // Drop shadow.
      const cardShadow = this.add.graphics();
      cardShadow.fillStyle(0x000000, 0.6);
      cardShadow.fillRoundedRect(-cardW / 2 + 2, -cardH / 2 + 6, cardW, cardH, CARD_RADIUS);
      cardContainer.add(cardShadow);
      // Flat stone body + double bronze frame — same tablet aesthetic as
      // the book picker so the two popups read as a single family.
      // Fill alpha 0.82 lets the dimmed zone bleed through.
      const cardBgGfx = this.add.graphics();
      cardBgGfx.fillStyle(STONE_DARK, 0.82);
      cardBgGfx.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, CARD_RADIUS);
      cardBgGfx.lineStyle(2, BRONZE_MID, 1);
      cardBgGfx.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, CARD_RADIUS);
      cardBgGfx.lineStyle(1, BRONZE_DEEP, 0.85);
      cardBgGfx.strokeRoundedRect(-cardW / 2 - 2, -cardH / 2 - 2, cardW + 4, cardH + 4, CARD_RADIUS + 1);
      cardContainer.add(cardBgGfx);
      // Hit zone for clicks/hover.
      const cardHit = this.add.rectangle(0, 0, cardW, cardH, 0x000000, 0).setInteractive({ useHandCursor: true });
      cardContainer.add(cardHit);
      // ── Carved bronze plaque banner — same shape as the book popup.
      const bannerH = 30;
      const bannerYRel = -cardH / 2 + bannerH / 2 + 5;
      const bannerW = cardW - 14;
      const bannerGfx = this.add.graphics();
      bannerGfx.fillStyle(BRONZE_DEEP, 0.98);
      bannerGfx.fillRoundedRect(-bannerW / 2, bannerYRel - bannerH / 2, bannerW, bannerH, 2);
      bannerGfx.fillStyle(BRONZE_RIM, 0.55);
      bannerGfx.fillRect(-bannerW / 2 + 2, bannerYRel - bannerH / 2 + 1, bannerW - 4, 2);
      bannerGfx.lineStyle(1, BRONZE_RIM, 0.95);
      bannerGfx.strokeRoundedRect(-bannerW / 2, bannerYRel - bannerH / 2, bannerW, bannerH, 2);
      // Soft bronze glint at the bottom rim — replaces the tome-color
      // chip used by the book popup (talents have no school identity).
      bannerGfx.fillStyle(BRONZE_GLINT, 0.55);
      bannerGfx.fillRect(-bannerW / 2 + 8, bannerYRel + bannerH / 2 - 2, bannerW - 16, 2);
      cardContainer.add(bannerGfx);
      const titleTxt = this.add.text(0, bannerYRel, ability.name, {
        fontSize: '12px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
        color: '#d4a84a', fontStyle: 'bold',
        stroke: '#0a0608', strokeThickness: 2,
        wordWrap: { width: bannerW - 8 }, align: 'center',
        padding: { y: 2 },
      }).setOrigin(0.5).setResolution(2);
      cardContainer.add(titleTxt);
      // ── Framed icon niche — chiselled stone box, dim bronze border.
      const iconBoxSize = 44;
      const iconBoxYRel = bannerYRel + bannerH / 2 + 4 + iconBoxSize / 2;
      const iconBoxGfx = this.add.graphics();
      iconBoxGfx.fillStyle(0x000000, 0.55);
      iconBoxGfx.fillRoundedRect(-iconBoxSize / 2, iconBoxYRel - iconBoxSize / 2, iconBoxSize, iconBoxSize, 3);
      iconBoxGfx.lineStyle(1, BRONZE_MID, 0.95);
      iconBoxGfx.strokeRoundedRect(-iconBoxSize / 2, iconBoxYRel - iconBoxSize / 2, iconBoxSize, iconBoxSize, 3);
      iconBoxGfx.lineStyle(0.5, BRONZE_GLINT, 0.5);
      iconBoxGfx.strokeRoundedRect(-iconBoxSize / 2 + 2, iconBoxYRel - iconBoxSize / 2 + 2, iconBoxSize - 4, iconBoxSize - 4, 2);
      cardContainer.add(iconBoxGfx);
      if (ability.iconKey && this.textures.exists(ability.iconKey)) {
        const iconImg = this.add.image(0, iconBoxYRel, ability.iconKey).setDisplaySize(36, 36);
        cardContainer.add(iconImg);
      }
      // ── Rank pips — visual "how many ranks have I invested" indicator.
      //    Filled gold dots = ranks earned, hollow dots = unfilled.
      //    For non-one-time talents only (one-time is just a single flag).
      const pipsYRel = iconBoxYRel + iconBoxSize / 2 + 14;
      if (!isOneTime) {
        const pipsGfx = this.add.graphics();
        const cappedMax = Math.min(maxRank, 8); // visual cap so 99-rank talents don't sprawl
        const pipR = 4;
        const pipGap = 4;
        const pipsTotalW = cappedMax * (pipR * 2) + (cappedMax - 1) * pipGap;
        const pipsStartX = -pipsTotalW / 2 + pipR;
        for (let p = 0; p < cappedMax; p++) {
          const px = pipsStartX + p * (pipR * 2 + pipGap);
          const isFilled = p < currentPoints;
          if (isFilled) {
            pipsGfx.fillStyle(BRONZE_GLINT, 1);
            pipsGfx.fillCircle(px, pipsYRel, pipR);
            pipsGfx.lineStyle(1, BRONZE_RIM, 0.95);
            pipsGfx.strokeCircle(px, pipsYRel, pipR);
          } else {
            pipsGfx.lineStyle(1, BRONZE_DEEP, 0.85);
            pipsGfx.strokeCircle(px, pipsYRel, pipR);
          }
        }
        cardContainer.add(pipsGfx);
        // Overflow indicator if talent has more ranks than the visual cap.
        if (maxRank > cappedMax) {
          const overflow = this.add.text(pipsTotalW / 2 + 8, pipsYRel, `…/${maxRank}`, {
            fontSize: '8px', fontFamily: 'Georgia, serif', color: '#7a6540',
            padding: { y: 2 },
          }).setOrigin(0, 0.5).setResolution(2);
          cardContainer.add(overflow);
        }
      } else {
        // One-time talent — show a single bright pill instead of pips.
        const pillTxt = this.add.text(0, pipsYRel, currentPoints > 0 ? '✓ UNLOCKED' : 'ONE-TIME', {
          fontSize: '9px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
          color: currentPoints > 0 ? '#7acc60' : '#b38a5a',
          padding: { y: 2 },
        }).setOrigin(0.5).setResolution(2);
        cardContainer.add(pillTxt);
      }
      // ── "Next rank" preview — the most prominent line on the card per
      //    user feedback ("talent ranks are less visible. ... show
      //    actually how much you already put in each etc."). Bright
      //    green for "this is what you'll get", larger font.
      //    No "+ " prefix — `getNextRankLabel` already returns strings
      //    like "+25% per tick" with their own + sign, and the previous
      //    `+ ${...}` template produced "+ +25%" duplicates.
      const nextLabel = info
        ? (isOneTime ? `Unlock: ${info.label}` : getNextRankLabel(ability.id, currentPoints))
        : '+1 point';
      const nextYRel = pipsYRel + 16;
      const nextPreview = this.add.text(0, nextYRel, nextLabel, {
        fontSize: '12px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
        color: '#7acc60',
        wordWrap: { width: cardW - 14 }, align: 'center',
        padding: { y: 2 },
      }).setOrigin(0.5, 0).setResolution(2);
      cardContainer.add(nextPreview);
      // ── Description — small, italic, secondary so it doesn't compete
      //    with the rank pips / next-rank preview. Old version was the
      //    same gold as everything else; now muted bronze + italic.
      const descYRel = nextYRel + nextPreview.height + 6;
      const desc = this.add.text(0, descYRel, ability.description, {
        fontSize: '9px', fontFamily: 'Georgia, serif', fontStyle: 'italic',
        color: '#8a6420',
        wordWrap: { width: cardW - 16 }, align: 'center', maxLines: 4,
      }).setOrigin(0.5, 0);
      cardContainer.add(desc);
      // ── Footer removed — the rank pips at the top already show
      //    "how many ranks invested" so a redundant "X / Y pts" footer
      //    just clutters the card (per user feedback: "point at the
      //    bottom not very usefull now"). Split-talent hint moved to
      //    the bottom-anchor position instead.
      if (info && info.splitAtRank !== undefined && info.label2 && currentPoints < info.splitAtRank) {
        const hint = this.add.text(0, cardH / 2 - 18, `at rank ${info.splitAtRank}: ${info.label2}`, {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'italic',
          wordWrap: { width: cardW - 16 }, align: 'center',
          padding: { y: 2 },
        }).setOrigin(0.5).setResolution(2);
        cardContainer.add(hint);
      }
      // ── Hover lift / scale / glow.
      cardHit.on('pointerover', () => {
        this.tweens.killTweensOf(cardContainer);
        this.tweens.killTweensOf(glowGfx);
        this.tweens.add({
          targets: cardContainer,
          y: cy - 12, scale: 1.05, alpha: 1,
          duration: 220, ease: 'Back.easeOut',
        });
        this.tweens.add({
          targets: glowGfx, alpha: 0.5, duration: 220, ease: 'Quad.easeOut',
        });
        cardContainer.setDepth(102);
      });
      cardHit.on('pointerout', () => {
        this.tweens.killTweensOf(cardContainer);
        this.tweens.killTweensOf(glowGfx);
        this.tweens.add({
          targets: cardContainer,
          y: cy, scale: 1.0, alpha: 1,
          duration: 180, ease: 'Quad.easeIn',
        });
        this.tweens.add({
          targets: glowGfx, alpha: 0, duration: 180, ease: 'Quad.easeIn',
        });
        cardContainer.setDepth(101);
      });
      // Entrance animation — slide up + fade in, staggered.
      cardContainer.setAlpha(0);
      cardContainer.y = cy + 24;
      this.tweens.add({
        targets: cardContainer, y: cy, alpha: 1,
        duration: 380, delay: 80 + i * 90, ease: 'Back.easeOut',
      });

      cardHit.on('pointerdown', () => {
        if (talentPickLocked) return;
        talentPickLocked = true;
        // Dedicated "gear / engrenage" SFX for talent picks — different
        // timbre from the book-choice sound so the player feels the
        // talent-tree beat even when the card flash is identical.
        this.sfx.play('talent_choice', { volume: 0.75 });
        // Container scale-pulse + sparkle confirmation. Mirrors the tome
        // popup pick beat so the two share the same visual language.
        this.tweens.killTweensOf(cardContainer);
        this.tweens.add({
          targets: cardContainer,
          scale: 1.18, duration: 160, yoyo: true, ease: 'Sine.easeOut',
        });
        this.vfx.playTomePickFlash(null, cx, cy, BRONZE_RIM);
        this.time.delayedCall(350, () => {
        this.gsm.applyTalent(heroIndex, ability.id);
        // If this pick was owed from a recruit, decrement the persistent counter so F5 mid-popups
        // doesn't re-queue the pick on the next load.
        if ((hero.pendingRecruitTalents ?? 0) > 0) {
          hero.pendingRecruitTalents = (hero.pendingRecruitTalents ?? 0) - 1;
        }
        this.gsm.save();

        for (const el of elements) el.destroy();
        this.talentChoiceActive = false;

        const pts = hero.talentBonuses[ability.id] || 0;
        const ann = info ? `${hero.name}: ${ability.name} ${info.label} (${pts} pts)` : `${hero.name}: ${ability.name} +1`;
        this.showAnnouncement(ann);
        this.addChoiceLog({
          kind: 'talent', icon: '✦',
          label: `${hero.name}: ${ability.name}`,
          detail: info ? `${info.label} (${pts} pts)` : `+1 rank`,
        });

        // Show queued tome choices, then zone-cleared popup / auto-advance if deferred
        if (this.pendingTalentChoices.length === 0 && this.pendingTomeChoices > 0) {
          this.pendingTomeChoices--;
          this.time.delayedCall(300, () => this.showTomeChoice());
        } else if (this.pendingZoneCleared && this.pendingTalentChoices.length === 0) {
          this.time.delayedCall(300, () => this.showZoneClearedPopup());
        } else if (this.pendingAutoAdvance && this.pendingTalentChoices.length === 0) {
          this.pendingAutoAdvance = false;
          this.autoAdvanceOrInfernoPause();
        }
        this.time.delayedCall(300, () => this.processNextTalentChoice());
        }); // close delayedCall body
      });   // close card.on pointerdown
    });     // close abilities.forEach
  }

  // ==================== CHAOS TOME ====================

  /** Chaos Tome: each pick gives a random permanent stat boost to all deployed heroes */
  /** Applies chaos bonus and returns the rolled stat label so callers can log it. */
  private applyChaosBonus(): string {
    const heroes = this.gsm.getDeployedHeroes();
    const statOptions: { key: keyof import('@roe/shared').HeroStats; amount: number; label: string }[] = [
      { key: 'maxHp', amount: 22, label: '+22 HP' },
      { key: 'attack', amount: 4, label: '+4 ATK' },
      { key: 'defense', amount: 3, label: '+3 DEF' },
      { key: 'spellPower', amount: 4, label: '+4 SP' },
      { key: 'critChance', amount: 0.045, label: '+4.5% Crit' },
      { key: 'critDamage', amount: 0.15, label: '+15% CritDmg' },
    ];
    const pick = statOptions[Math.floor(Math.random() * statOptions.length)];
    for (const hero of heroes) {
      (hero.stats[pick.key] as number) += pick.amount;
      if (pick.key === 'maxHp') hero.stats.hp = Math.min(hero.stats.hp + pick.amount, hero.stats.maxHp);
    }
    this.showAnnouncement(`Chaos: ${pick.label} to all heroes!`);
    // Cache last chaos roll label so the choice log can include it.
    this._lastChaosLabel = pick.label;
    return pick.label;
  }
  private _lastChaosLabel = '';

  // ==================== TOME ICONS HUD ====================

  private createTomeIcons(): void {
    this.refreshTomeIcons();
  }

  // ── In-battle score panel — replaces the previous tracked-quest widget.
  // Half-transparent panel anchored to the top-right corner; lists the top
  // leaderboard entries (rank · name · score · stage) and the current
  // player's score updated in real time. Chosen mode (standard / inferno)
  // matches the active run.
  private scorePanelObjects: Phaser.GameObjects.GameObject[] = [];
  private scoreLeaderboardCache: Array<{ name: string; score: number; level?: number; infernoZone?: number; heroKey?: string }> = [];
  private scoreLeaderboardLoaded = false;
  private lastScorePanelSnapshot = '';
  /** When true, the panel collapses down to just the title row + YOU line.
   *  Toggled via the small ▼ / ▶ arrow next to the title. Default: open. */
  private scorePanelCollapsed = false;

  /** Fire-and-forget fetch of the top leaderboard for the active run mode.
   *  Cached for the lifetime of the BattleScene; the full Champions Board
   *  in MenuScene re-fetches fresh data each open. */
  private fetchScoreLeaderboard(): void {
    const apiBase = (import.meta as any).env?.VITE_API_URL
      || (typeof window !== 'undefined' && window.location?.hostname !== 'localhost' ? '' : 'http://localhost:4000');
    const mode = this.infernoMode ? 'inferno' : 'standard';
    fetch(`${apiBase}/api/leaderboard?mode=${mode}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((data) => {
        if (data?.entries && Array.isArray(data.entries)) {
          this.scoreLeaderboardCache = data.entries.slice(0, 6);
        }
        this.scoreLeaderboardLoaded = true;
        this.lastScorePanelSnapshot = ''; // force re-render
      });
  }

  private refreshScorePanel(): void {
    const ownScore = this.infernoMode ? this.infernoScore : this.runScore;
    const ownStage = this.infernoMode ? this.infernoZone : this.gsm.player.currentLevel;
    // Snapshot — re-render when the leaderboard cache changes, the player's
    // score / stage shifts, OR the collapsed state flips.
    const snapshot = `${this.scoreLeaderboardLoaded ? 'L' : 'l'}|${this.scoreLeaderboardCache.length}|${ownScore}|${ownStage}|${this.scorePanelCollapsed ? 'c' : 'o'}`;
    if (snapshot === this.lastScorePanelSnapshot) return;
    this.lastScorePanelSnapshot = snapshot;

    this.scorePanelObjects.forEach((o) => o.destroy());
    this.scorePanelObjects = [];

    const PANEL_W = 188;
    const ROW_H = 14;
    const HEADER_H = 18;
    const FOOTER_H = 22;
    const topN = Math.min(6, this.scoreLeaderboardCache.length);
    // Collapsed panel hides the top-N body and the divider — keeps just the
    // title row + the YOU footer. Saves vertical real estate when the player
    // doesn't want the leaderboard occupying the corner.
    const bodyH = this.scorePanelCollapsed ? 0 : (topN > 0 ? topN * ROW_H + 6 : 18);
    const PANEL_H = HEADER_H + bodyH + FOOTER_H + 8;
    const panelRight = 960 - 8;
    const panelLeft = panelRight - PANEL_W;
    const panelTop = 44;
    const panelCX = (panelLeft + panelRight) / 2;
    const panelCY = panelTop + PANEL_H / 2;

    // Half-transparent dark panel — non-interactive so clicks pass through
    // to the battlefield (player should be able to click anywhere the panel
    // sits without losing input).
    const bg = this.add.rectangle(panelCX, panelCY, PANEL_W, PANEL_H, 0x080508, 0.62)
      .setStrokeStyle(1, 0x5a3600, 0.65).setDepth(15);
    this.scorePanelObjects.push(bg);
    const inner = this.add.rectangle(panelCX, panelCY, PANEL_W - 6, PANEL_H - 6, 0, 0)
      .setStrokeStyle(1, 0xc8973a, 0.18).setDepth(15);
    this.scorePanelObjects.push(inner);

    // Header
    this.scorePanelObjects.push(this.add.text(panelCX, panelTop + 9,
      this.infernoMode ? '✦  Inferno Top  ✦' : '✦  Top Champions  ✦', {
        fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#c8973a', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(16));

    // Collapse / expand toggle — small triangle anchored to the right of
    // the title row. Clicking flips `scorePanelCollapsed` and forces a
    // re-render via the snapshot diff. Bronze resting, gold on hover.
    const collapseBtn = this.add.text(panelRight - 8, panelTop + 9,
      this.scorePanelCollapsed ? '▶' : '▼', {
        fontSize: '10px', fontFamily: 'Arial, sans-serif',
        color: '#8a6420', fontStyle: 'bold',
      }).setOrigin(1, 0.5).setDepth(17).setInteractive({ useHandCursor: true });
    collapseBtn.on('pointerover', () => collapseBtn.setColor('#f0c040'));
    collapseBtn.on('pointerout',  () => collapseBtn.setColor('#8a6420'));
    collapseBtn.on('pointerdown', () => {
      this.scorePanelCollapsed = !this.scorePanelCollapsed;
      this.lastScorePanelSnapshot = ''; // force redraw on the next refresh tick
      this.refreshScorePanel();
    });
    this.scorePanelObjects.push(collapseBtn);

    // Skip the body when collapsed — jump straight to the YOU footer.
    if (this.scorePanelCollapsed) {
      const divY = panelTop + HEADER_H + 4;
      this.scorePanelObjects.push(
        this.add.rectangle(panelCX, divY, PANEL_W - 16, 1, 0x5a3600, 0.85).setDepth(16),
      );
      const ownStageStr = this.infernoMode ? `I-${ownStage}` : `z${ownStage}`;
      this.scorePanelObjects.push(this.add.text(panelLeft + 6, divY + 12, 'You', {
        fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#fde68a', fontStyle: 'bold',
      }).setOrigin(0, 0.5).setDepth(16));
      this.scorePanelObjects.push(this.add.text(panelRight - 30, divY + 12, ownScore.toLocaleString(), {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#fde68a', fontStyle: 'bold',
      }).setOrigin(1, 0.5).setDepth(16));
      this.scorePanelObjects.push(this.add.text(panelRight - 6, divY + 12, ownStageStr, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
      }).setOrigin(1, 0.5).setDepth(16));
      return;
    }

    // Top N rows
    let rowY = panelTop + HEADER_H + 4;
    if (!this.scoreLeaderboardLoaded) {
      this.scorePanelObjects.push(this.add.text(panelCX, rowY + 4, 'Loading…', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3c1a', fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(16));
    } else if (topN === 0) {
      this.scorePanelObjects.push(this.add.text(panelCX, rowY + 4, 'No scores yet', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3c1a', fontStyle: 'italic',
      }).setOrigin(0.5).setDepth(16));
    } else {
      const fmtShort = (n: number): string => {
        if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
        if (n >= 10_000)    return (n / 1_000).toFixed(0) + 'K';
        if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
        return `${n}`;
      };
      for (let i = 0; i < topN; i++) {
        const e = this.scoreLeaderboardCache[i];
        const rank = i + 1;
        const isTop3 = rank <= 3;
        const rowColor = isTop3 ? ['#ffe8a0', '#e8e8e8', '#f0c890'][rank - 1] : '#a07840';
        const stage = this.infernoMode ? `I-${(e as any).infernoZone ?? '?'}` : `z${(e as any).level ?? '?'}`;
        // Compact row: "#1 DrShadar    372K  z27"
        this.scorePanelObjects.push(this.add.text(panelLeft + 6, rowY + ROW_H / 2, `#${rank}`, {
          fontSize: '9px', fontFamily: "'Cinzel Decorative', Georgia, serif",
          color: rowColor, fontStyle: 'bold',
        }).setOrigin(0, 0.5).setDepth(16));
        this.scorePanelObjects.push(this.add.text(panelLeft + 26, rowY + ROW_H / 2, e.name.slice(0, 9), {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: rowColor,
          fontStyle: isTop3 ? 'bold' : 'normal',
        }).setOrigin(0, 0.5).setDepth(16));
        this.scorePanelObjects.push(this.add.text(panelRight - 30, rowY + ROW_H / 2, fmtShort(e.score), {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: rowColor, fontStyle: 'bold',
        }).setOrigin(1, 0.5).setDepth(16));
        this.scorePanelObjects.push(this.add.text(panelRight - 6, rowY + ROW_H / 2, stage, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: rowColor,
        }).setOrigin(1, 0.5).setDepth(16));
        rowY += ROW_H;
      }
    }

    // Divider above the player's row
    const divY = panelTop + PANEL_H - FOOTER_H - 4;
    this.scorePanelObjects.push(
      this.add.rectangle(panelCX, divY, PANEL_W - 16, 1, 0x5a3600, 0.85).setDepth(16),
    );

    // Player's live score — full opacity, brighter to draw the eye
    const ownStageStr = this.infernoMode ? `I-${ownStage}` : `z${ownStage}`;
    this.scorePanelObjects.push(this.add.text(panelLeft + 6, divY + 12, 'You', {
      fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#fde68a', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(16));
    this.scorePanelObjects.push(this.add.text(panelRight - 30, divY + 12, ownScore.toLocaleString(), {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#fde68a', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setDepth(16));
    this.scorePanelObjects.push(this.add.text(panelRight - 6, divY + 12, ownStageStr, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(1, 0.5).setDepth(16));
  }

  // ==================== POTION BAR ====================

  /** Consume the potion in the given slot index (0..3). Shared by click handler and 1-4 keybinds. */
  private useConsumableSlot(slotIdx: number): void {
    const bar = this.gsm.player.potionBar ?? [];
    const slot = bar[slotIdx];
    if (!slot || slot.count <= 0) return;
    const maxSlots = this.gsm.getPotionBarMaxSlots();
    if (slotIdx >= maxSlots) return; // slot is locked
    // 8 s cooldown between Vitality / Mana (instant-restore) potions so
    // the player can't burst-tap multiple charges through a damage spike
    // for free. Other consumables aren't gated by this — they have
    // wave-duration buffs that already self-throttle. The CD ticker
    // is pause-aware: counts down only while `isPaused` is false (see
    // `update()`), so menus / pause / scene transitions don't burn it.
    if (slot.id === 'potion_vitality') {
      if (this.vitalityCdRemainingMs > 0) {
        const remaining = (this.vitalityCdRemainingMs / 1000).toFixed(1);
        this.showAnnouncement(`Vitality on cooldown — ${remaining}s left`);
        return;
      }
      this.vitalityCdRemainingMs = BattleScene.POTION_CD_MS;
    } else if (slot.id === 'potion_mana') {
      if (this.manaCdRemainingMs > 0) {
        const remaining = (this.manaCdRemainingMs / 1000).toFixed(1);
        this.showAnnouncement(`Mana on cooldown — ${remaining}s left`);
        return;
      }
      this.manaCdRemainingMs = BattleScene.POTION_CD_MS;
    }
    // Clear any hover tooltip — hotkey use doesn't fire pointerout, and
    // refreshPotionBar below will destroy the slot bg so the tooltip has
    // nothing to follow. Without this the tip stays pinned over the bar.
    this.potionBarTooltip?.destroy();
    this.potionBarTooltip = null;
    const pendingDef = CONSUMABLE_DEFS.find((c) => c.id === slot.id);
    const alreadyActive = this.gsm.hasActiveConsumable(slot.id as import('@roe/shared').ConsumableId);
    const usedId = this.gsm.usePotionFromBar(slotIdx);
    if (!usedId) return;
    if (!alreadyActive || (pendingDef && pendingDef.durationWaves <= 1)) {
      this.applyConsumableStatBuff(usedId);
    }
    // Daily-quest hook — Battle Mixer counts every potion consumption.
    this.gsm.trackPotionUsed();
    this.gsm.save();
    this.showAnnouncement(`Used ${pendingDef?.name ?? 'potion'}!`);
    this.refreshPotionBar();
    this.refreshConsumableIcons();
  }

  /** Render the in-battle potion bar: slots in the bottom-left strip below the merchant. */
  private refreshPotionBar(): void {
    const bar = this.gsm.player.potionBar ?? [];
    const maxSlots = this.gsm.getPotionBarMaxSlots();
    const HARD_MAX = 4; // always render 4 slots; locked ones shown greyed
    const snapshot = `${maxSlots}:` + bar.map((s) => `${s.id}x${s.count}`).join(',');
    if (snapshot === this.lastPotionBarSnapshot) return;
    this.lastPotionBarSnapshot = snapshot;

    for (const obj of this.potionBarObjects) obj.destroy();
    this.potionBarObjects = [];
    // Null out CD overlay refs — they pointed at the now-destroyed text /
    // dim rects. The render loop below re-creates them per slot if a
    // matching potion (vitality / mana) is still in the bar.
    this.vitalityCdOverlay = null;
    this.manaCdOverlay = null;
    // Drop any vitality-CD overlay refs — they belong to the destroyed
    // potionBarObjects above; keeping the field would leak references
    // that updateVitalityCooldownOverlay() then reads as live objects.
    this.vitalityCdOverlay = null;
    // Clear any stale hover tooltip left over from the previous render so
    // it doesn't float over the rebuilt bar (happens on hotkey use, since
    // pointerout never fires during a keyboard action).
    this.potionBarTooltip?.destroy();
    this.potionBarTooltip = null;

    const slotW = 34;
    const slotH = 34;
    const gap = 4;
    // Aligned horizontally with the Divine Wrath button (which sits at y=517)
    const startX = 108;
    const y = 517;

    const clearPotionTooltip = () => { this.potionBarTooltip?.destroy(); this.potionBarTooltip = null; };

    for (let i = 0; i < HARD_MAX; i++) {
      const isLocked = i >= maxSlots;
      const cx = startX + i * (slotW + gap) + slotW / 2;
      const slot = bar[i];
      const isEmpty = !slot || slot.count <= 0;

      // Locked slot — greyed out, drawn lock icon, no emoji
      if (isLocked) {
        const lockedBg = this.add.rectangle(cx, y, slotW, slotH, 0x080604, 1)
          .setStrokeStyle(1, 0x1e1610, 0.7)
          .setDepth(12)
          .setInteractive({ useHandCursor: false });
        this.potionBarObjects.push(lockedBg);
        // Drawn padlock — grey stone style
        const lockGfx = this.add.graphics().setDepth(13);
        const lx = cx; const ly = y + 1;
        // Shackle (arch)
        lockGfx.lineStyle(2, 0x3a3830, 0.65);
        lockGfx.beginPath();
        lockGfx.moveTo(lx - 4, ly - 1);
        lockGfx.lineTo(lx - 4, ly - 5);
        lockGfx.arc(lx, ly - 5, 4, Math.PI, 0, false);
        lockGfx.lineTo(lx + 4, ly - 1);
        lockGfx.strokePath();
        // Body
        lockGfx.fillStyle(0x252220, 0.8);
        lockGfx.fillRect(lx - 6, ly - 1, 12, 8);
        lockGfx.lineStyle(1, 0x3a3530, 0.6);
        lockGfx.strokeRect(lx - 6, ly - 1, 12, 8);
        // Keyhole
        lockGfx.fillStyle(0x070504, 1);
        lockGfx.fillCircle(lx, ly + 2, 1.5);
        lockGfx.fillRect(lx - 1, ly + 2, 2, 3);
        this.potionBarObjects.push(lockGfx);
        // Slot number badge (dimmed)
        const slotNumLabel = this.add.text(cx + slotW / 2 - 2, y - slotH / 2 + 2, `${i + 1}`, {
          fontSize: '7px', fontFamily: 'monospace', color: '#3a2e1a',
        }).setOrigin(1, 0).setDepth(13);
        this.potionBarObjects.push(slotNumLabel);
        const reqStage = i === 2 ? 30 : 50;
        const nodeName = i === 2 ? 'Alchemy Satchel' : 'Master Alchemist';
        lockedBg.on('pointerover', () => {
          clearPotionTooltip();
          this.potionBarTooltip = this.createHudTooltip(cx, y - 18,
            `Slot ${i + 1} — Locked\nUnlock "${nodeName}" in the Rune Tree\n(Stage ${reqStage} required)`,
            0.5, 1, { fontSize: '9px', color: '#4a3820' },
          );
        });
        lockedBg.on('pointerout', () => clearPotionTooltip());
        continue;
      }

      const bg = this.add.rectangle(cx, y, slotW, slotH, 0x0a0806, 1)
        .setStrokeStyle(1, isEmpty ? 0x2a1500 : 0x3a2200)
        .setDepth(12)
        .setInteractive({ useHandCursor: !isEmpty });
      this.potionBarObjects.push(bg);

      // Slot number badge (top-right) — always shown so keybinds 1/2/3/4 are discoverable.
      // AZERTY layouts show the physical key label the same way (above ONE is & on French) — the
      // keybind itself uses the physical Digit1-4 keys so both layouts work.
      const slotKeyLabel = this.add.text(cx + slotW / 2 - 2, y - slotH / 2 + 2, `${i + 1}`, {
        fontSize: '8px', fontFamily: 'monospace', color: isEmpty ? '#2e2415' : '#7a5a2a', fontStyle: 'bold',
      }).setOrigin(1, 0).setDepth(14);
      this.potionBarObjects.push(slotKeyLabel);

      if (!isEmpty) {
        const def = CONSUMABLE_DEFS.find((c) => c.id === slot.id);

        // Potion icon — use sprite if available, else emoji fallback
        if (def?.spriteKey && this.textures.exists(def.spriteKey)) {
          const icon = this.add.image(cx, y, def.spriteKey)
            .setDisplaySize(slotW - 4, slotH - 4).setDepth(13);
          this.potionBarObjects.push(icon);
        } else {
          const icon = this.add.text(cx, y - 2, def?.icon ?? '?', {
            fontSize: '14px', padding: { y: 2 },
          }).setOrigin(0.5).setDepth(13);
          this.potionBarObjects.push(icon);
        }

        // Count badge (bottom-right corner) — always shown so player sees the stack
        {
          const badge = this.add.text(cx + slotW / 2 - 3, y + slotH / 2 - 3, `${slot.count}`, {
            fontSize: '8px', fontFamily: 'monospace', color: '#ffffff', fontStyle: 'bold',
            backgroundColor: slot.count > 1 ? '#8b3a00' : '#3a2010', padding: { x: 2, y: 0 },
          }).setOrigin(1, 1).setDepth(14);
          this.potionBarObjects.push(badge);
        }

        // Tooltip on hover
        bg.on('pointerover', () => {
          clearPotionTooltip();
          bg.setStrokeStyle(1, 0xc8973a);
          const tipLines = [
            `${def?.icon ?? ''} ${def?.name ?? slot.id}`,
            def?.description ?? '',
            `  ${slot.count} stocked — click to use`,
          ];
          const tipX = Phaser.Math.Clamp(cx, 80, GAME_W - 80);
          this.potionBarTooltip = this.createHudTooltip(tipX, y - 18, tipLines.join('\n'),
            0.5, 1, { fontSize: '10px', wrap: 200 },
          );
        });
        bg.on('pointerout', () => { clearPotionTooltip(); bg.setStrokeStyle(1, 0x5a3600); });

        // Click to use — shared with the 1/2/3/4 keybind path
        const slotIdx = i;
        bg.on('pointerdown', () => {
          clearPotionTooltip();
          this.useConsumableSlot(slotIdx);
        });

        // Vitality / Mana cooldown overlays — dim rect + remaining-seconds
        // text sitting over the slot. Hidden by default; per-frame
        // updateVitalityCooldownOverlay() flips visibility + updates the
        // numeral while the matching `*CdRemainingMs` field is positive.
        if (slot.id === 'potion_vitality' || slot.id === 'potion_mana') {
          const dim = this.add.rectangle(cx, y, slotW - 2, slotH - 2, 0x000000, 0.6)
            .setDepth(14).setVisible(false);
          const cdText = this.add.text(cx, y, '', {
            fontSize: '13px', fontFamily: 'Georgia, serif', fontStyle: 'bold',
            color: slot.id === 'potion_vitality' ? '#ffe0a0' : '#a0c0ff',
            stroke: '#1a0a00', strokeThickness: 3,
          }).setOrigin(0.5).setDepth(15).setResolution(2).setVisible(false);
          this.potionBarObjects.push(dim, cdText);
          if (slot.id === 'potion_vitality') this.vitalityCdOverlay = { dim, text: cdText };
          else this.manaCdOverlay = { dim, text: cdText };
        }
      } else {
        // Empty slot — barely-visible rune glyph (matches the hub glyph in the talent tree).
        const rune = this.add.text(cx, y + 1, 'ᚠ', {
          fontSize: '18px',
          fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
          color: '#3a2a18', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(12.5).setAlpha(0.35);
        this.potionBarObjects.push(rune);
      }
    }
  }

  // ── Rune Buttons (sprite-based) ────────────────────────────────────────────

  /** Tint applied to inactive (not-yet-set) rune crystals — grey-blue wash */
  private readonly RUNE_TINT_INACTIVE = 0x5a7080;
  /** Tint applied to locked (stage requirement not met) rune crystals — dim grey */
  private readonly RUNE_TINT_LOCKED   = 0x2a3340;
  /** Tint applied to skull when divine wrath is on cooldown — dim grey-blue */
  private readonly RUNE_TINT_CD       = 0x5a7090;

  private createCheckpointButton(): void {
    for (const obj of this.checkpointBtnGroup) obj.destroy();
    this.checkpointBtnGroup = [];
    if (this.checkpointGlowTween) { this.checkpointGlowTween.stop(); this.checkpointGlowTween = null; }

    const level = this.gsm.player.currentLevel;
    const isLocked = level < 10;
    const isActive = !isLocked && !!this.gsm.player.runeCheckpoint;
    // Pilgrim's Tithe also discounts the Rune Stone setup cost (per-rank −10%,
    // floored at 0.1× so it can't go free). Same `shrineDiscountPct` field
    // that drives the Gilded Altar reduction.
    const tithePctBase = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).shrineDiscountPct;
    const titheMult = Math.max(0.1, 1 - tithePctBase);
    const cost = Math.floor(50000 * titheMult);

    // 36 px to match invocations. Section 4 of the bottom bar (right of the
    // separator at x=842). Stride 44; positioned with 12 px gap from the
    // separator. 26 px right margin so the checkpoints don't crowd the
    // canvas edge per user feedback.
    const btnSize = 36;
    const bx = 872; const by = 556;
    const frame = this.add.graphics().setDepth(12);
    const glyph = this.add.graphics().setDepth(13);
    const redrawBaseStone = (hover: boolean): void => {
      frame.clear();
      drawRuneStoneButtonFrame(frame, bx, by, btnSize, {
        active: isActive, locked: isLocked,
        accent: isActive ? 0xd4a060 : hover ? 0xa8c0d8 : 0x3a2200,
      });
      glyph.clear();
      const glyphTint = isActive ? 0xf5d28a : isLocked ? 0x506878 : hover ? 0xc8b080 : 0x8a7040;
      drawBaseRuneStoneSymbol(glyph, bx, by, btnSize * 0.40, glyphTint);
      glyph.setAlpha(isLocked ? 0.45 : 1);
    };
    redrawBaseStone(false);
    // Invisible hit target sized to the frame so the pointer events fire on
    // the whole button surface, not just the painted pixels of the glyph.
    const hit = this.add.rectangle(bx, by, btnSize, btnSize, 0x000000, 0)
      .setDepth(14).setInteractive({ useHandCursor: !isLocked && !isActive });
    this.checkpointBtnGroup.push(frame, glyph, hit);

    // Active glow pulse — pulse the glyph's alpha so the active stone throbs.
    if (isActive) {
      this.checkpointGlowTween = this.tweens.add({
        targets: glyph, alpha: { from: 0.80, to: 1.0 },
        duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }

    hit.on('pointerover', () => {
      if (!isActive && !isLocked) redrawBaseStone(true);
      const tipText = isLocked ? `Rune Stone\nUnlocks at Stage 10`
        : isActive ? `Rune Stone active\nReturns to Stage 10 on death`
        : `Set Rune Stone\n${cost.toLocaleString()} gold\nReturn to Stage 10 on death`;
      const tip = this.createHudTooltip(bx - 56, by - 36, tipText,
        0.5, 1, { fontSize: '10px', color: isLocked ? '#4a4a46' : '#c8973a' },
      ).setName('cp_tip');
      this.checkpointBtnGroup.push(tip);
    });
    hit.on('pointerout', () => {
      redrawBaseStone(false);
      const tip = this.checkpointBtnGroup.find(o => (o as any).name === 'cp_tip');
      if (tip) { tip.destroy(); this.checkpointBtnGroup.splice(this.checkpointBtnGroup.indexOf(tip), 1); }
    });

    hit.on('pointerdown', () => {
      if (isLocked || this.gsm.player.runeCheckpoint) return;
      if (this.gsm.player.resources.gold < cost) {
        this.showAnnouncement('Not enough gold for the Rune Stone!');
        return;
      }
      this.gsm.player.resources.gold -= cost;
      try {
        this.gsm.saveRunCheckpoint(this.runScore);
      } catch (e) {
        this.gsm.player.resources.gold += cost;
        this.showAnnouncement('Rune Stone failed to set — gold refunded.');
        console.error('saveRunCheckpoint failed:', e);
        return;
      }
      this.vfx.playRuneStoneActivate(bx, by);
      this.showAnnouncement('Rune Stone set — your progress is anchored.');
      // Hero Armory: re-snapshot perks on checkpoint take so any mid-run
      // respec applies from this point onward (defeat → re-snapshot is
      // implicit via the next run's spawnHeroes call).
      this.gsm.snapshotActiveArmoryPassives();
      this.refreshUI();
      this.createCheckpointButton();
      this.createDeepCheckpointButton();
    });
  }

  private createDeepCheckpointButton(): void {
    for (const obj of this.deepCheckpointBtnGroup) obj.destroy();
    this.deepCheckpointBtnGroup = [];
    if (this.deepCheckpointGlowTween) { this.deepCheckpointGlowTween.stop(); this.deepCheckpointGlowTween = null; }

    const level = this.gsm.player.currentLevel;
    const passives = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    const hasUnlock = passives.deepCheckpoint;

    const isActive = hasUnlock && !!this.gsm.player.runeDeepCheckpoint;
    const isLocked = !hasUnlock;
    const isTooEarly = hasUnlock && level < 30;
    // Pilgrim's Tithe discount applies here too — 100k base, scaled by
    // shrineDiscountPct (floored at 0.1× so it can't go free).
    const titheDeepMult = Math.max(0.1, 1 - passives.shrineDiscountPct);
    const cost = Math.floor(100000 * titheDeepMult);
    const isGreyed = isLocked || isTooEarly;

    // 36 px to match invocations. Section 4 of the bottom bar — right slot,
    // 44 px stride from the base stone at x=872. Right edge at 934 so the
    // canvas keeps a 26 px margin (matches the bar's left margin balance
    // per user feedback).
    const btnSize = 36;
    const bx = 916; const by = 556;
    const frame = this.add.graphics().setDepth(12);
    const glyph = this.add.graphics().setDepth(13);
    const redrawEternal = (hover: boolean): void => {
      frame.clear();
      drawRuneStoneButtonFrame(frame, bx, by, btnSize, {
        active: isActive, locked: isGreyed,
        accent: isActive ? 0xe04030 : hover ? 0xa8c0d8 : 0x3a2200,
      });
      glyph.clear();
      const glyphTint = isActive ? 0xff7050 : isGreyed ? 0x506878 : hover ? 0xd85040 : 0x803020;
      drawEternalRuneStoneSymbol(glyph, bx, by, btnSize * 0.42, glyphTint);
      glyph.setAlpha(isGreyed ? 0.40 : 1);
    };
    redrawEternal(false);
    const hit = this.add.rectangle(bx, by, btnSize, btnSize, 0x000000, 0)
      .setDepth(14).setInteractive({ useHandCursor: !isGreyed && !isActive });
    this.deepCheckpointBtnGroup.push(frame, glyph, hit);

    if (isActive) {
      this.deepCheckpointGlowTween = this.tweens.add({
        targets: glyph, alpha: { from: 0.80, to: 1.0 },
        duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }

    hit.on('pointerover', () => {
      if (!isActive && !isGreyed) redrawEternal(true);
      const tipText = isLocked ? 'Eternal Rune Stone\nUnlock in Rune Tree\n(Stage 50 required)'
        : isTooEarly ? 'Eternal Rune Stone\nUnlocks at Stage 30'
        : isActive ? 'Eternal Rune Stone active\nReturns to Stage 30 on death'
        : `Set Eternal Rune Stone\n${cost.toLocaleString()} gold\nReturn to Stage 30 on death`;
      const tip = this.createHudTooltip(bx - 60, by - 38, tipText,
        0.5, 1, { fontSize: '10px', color: isGreyed ? '#4a4a46' : '#e04030' },
      ).setName('dcp_tip');
      this.deepCheckpointBtnGroup.push(tip);
    });
    hit.on('pointerout', () => {
      redrawEternal(false);
      const tip = this.deepCheckpointBtnGroup.find(o => (o as any).name === 'dcp_tip');
      if (tip) { tip.destroy(); this.deepCheckpointBtnGroup.splice(this.deepCheckpointBtnGroup.indexOf(tip), 1); }
    });

    hit.on('pointerdown', () => {
      if (isLocked || isTooEarly || this.gsm.player.runeDeepCheckpoint) return;
      if (this.gsm.player.resources.gold < cost) {
        this.showAnnouncement('Not enough gold for the Eternal Rune Stone!');
        return;
      }
      this.gsm.player.resources.gold -= cost;
      try {
        this.gsm.saveDeepRunCheckpoint(this.runScore);
      } catch (e) {
        this.gsm.player.resources.gold += cost;
        this.showAnnouncement('Eternal Rune Stone failed to set — gold refunded.');
        console.error('saveDeepRunCheckpoint failed:', e);
        return;
      }
      this.vfx.playRuneStoneActivate(bx, by);
      this.showAnnouncement('Eternal Rune Stone set — Stage 30 anchored.');
      // Hero Armory: re-snapshot perks on Eternal Rune Stone take too.
      this.gsm.snapshotActiveArmoryPassives();
      this.refreshUI();
      this.createCheckpointButton();
      this.createDeepCheckpointButton();
    });
  }

  /** Resolve the current Wrath name + sprite + damage scalar based on whether
   *  the `arc_divine` keystone is taken. Keystone upgrades the ability to
   *  "Divine Wrath" — new glowing-skull sprite + 2× damage. */
  private getWrathProfile(): { label: string; sprite: string; scalar: number; minHpFrac: number } {
    const divine = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).keystoneDivineWrath;
    return divine
      ? { label: 'Divine Wrath', sprite: 'obj_skull_glowing', scalar: 2.0, minHpFrac: 0.24 }
      : { label: 'Wrath',        sprite: 'obj_skull',         scalar: 1.0, minHpFrac: 0.12 };
  }

  private createDivineWrathButton(): void {
    for (const obj of this.divineWrathBtnGroup) obj.destroy();
    this.divineWrathBtnGroup = [];
    if (this.divineWrathGlowTween) { this.divineWrathGlowTween.stop(); this.divineWrathGlowTween = null; }
    this.divineWrathCdLabel = null;

    const bx = 310; const by = 517;
    const onCd = this.divineWrathCooldown > 0;
    const { label, sprite: spriteKey } = this.getWrathProfile();

    const sprite = this.add.image(bx, by, spriteKey)
      .setDisplaySize(52, 52).setDepth(12).setFlipX(true);
    // Tight circular hit area — the rune icon is ~40 px of visible art inside
    // the 52 × 52 display, so the default full-sprite rect was catching dead
    // pixels on the corners. Phaser's hit area uses the SOURCE texture space
    // (usually 128×128 for our rune icons); radius = 34 % of source ≈ 18 px
    // on-screen — tight to the sigil ring.
    const sourceW = sprite.width || 128;
    const sourceH = sprite.height || 128;
    sprite.setInteractive({
      hitArea: new Phaser.Geom.Circle(sourceW / 2, sourceH / 2, sourceW * 0.34),
      hitAreaCallback: Phaser.Geom.Circle.Contains,
      useHandCursor: !onCd,
    });
    if (onCd) { sprite.setTint(this.RUNE_TINT_CD); } else { sprite.clearTint(); }
    this.divineWrathBtnGroup.push(sprite);

    if (onCd) {
      const cdSec = Math.ceil(this.divineWrathCooldown / 1000);
      const cdLbl = this.add.text(bx, by + 2, `${cdSec}s`, {
        fontSize: '12px', fontFamily: 'Arial, sans-serif', color: '#ffffff',
        stroke: '#000000', strokeThickness: 3,
      }).setResolution(2).setOrigin(0.5, 0.5).setDepth(14);
      this.divineWrathBtnGroup.push(cdLbl);
      this.divineWrathCdLabel = cdLbl;
    }

    sprite.on('pointerover', () => {
      if (!onCd) sprite.setTint(0xdddddd);
      const tipText = onCd
        ? `${label}  [Q]\nOn cooldown: ${Math.ceil(this.divineWrathCooldown / 1000)}s`
        : `${label}  [Q]\nSummon judgement — obliterates\nall enemies. Wrath grows fiercer\nas you carry it deeper.\n210 s cooldown.`;
      const tip = this.createHudTooltip(bx - 64, by - 30, tipText,
        0.5, 1, { fontSize: '10px', color: onCd ? '#6b7a8a' : '#c8973a' },
      ).setName('dw_tip');
      this.divineWrathBtnGroup.push(tip);
    });
    sprite.on('pointerout', () => {
      if (onCd) { sprite.setTint(this.RUNE_TINT_CD); } else { sprite.clearTint(); }
      const tip = this.divineWrathBtnGroup.find(o => (o as any).name === 'dw_tip');
      if (tip) { tip.destroy(); this.divineWrathBtnGroup.splice(this.divineWrathBtnGroup.indexOf(tip), 1); }
    });
    sprite.on('pointerdown', () => { if (onCd) return; this.fireDivineWrath(); });
  }

  private fireDivineWrath(): void {
    // Defensive cooldown guard — the bottom-bar Wrath icon's pointer
    // handler caches `onCd` from build time, so a stale closure could
    // re-trigger this method after the cooldown was set. Bail early here so
    // the cast can never run twice without the timer expiring.
    if (this.divineWrathCooldown > 0) return;
    // Inferno mode no longer blocks Divine Wrath casts — endgame players
    // get the keystone too. Cooldown is the only gate now.
    const level = this.gsm.player.currentLevel;
    const { label, scalar, minHpFrac } = this.getWrathProfile();
    // Superlinear scaling so the ability stays meaningful as enemy HP compounds
    // at 1.15× per stage. The `minHpFrac * maxHp` floor prevents the flat term
    // from becoming a scratch at extreme stages against bosses.
    const baseDmg = level * level * 10 + level * 200 + 400;

    const enemies = this.enemySprites.filter(e => e.active && e.enemyRef.currentHp > 0);
    for (const es of enemies) {
      const dmg = Math.floor(Math.max(baseDmg, es.enemyRef.maxHp * minHpFrac) * scalar);
      es.enemyRef.currentHp -= dmg;
      this.showDamageNumber(
        es.x + Phaser.Math.Between(-12, 12),
        es.y - 30 + Phaser.Math.Between(-10, 10),
        `ᚨ ${dmg.toLocaleString()}`,
        '#ffe060',
        '20px',
      );
      // Blue flame hit effect from Combat FX row 23
      if (this.textures.exists('combat_fx_sheet')) {
        const hit = this.add.sprite(es.x, es.y, 'combat_fx_sheet')
          .setDisplaySize(72, 72).setDepth(18).setOrigin(0.5, 0.9);
        if (this.anims.exists('fx_divine_wrath_hit')) hit.play('fx_divine_wrath_hit');
        hit.once('animationcomplete', () => hit.destroy());
        this.time.delayedCall(700, () => { if (hit.active) hit.destroy(); });
      } else {
        this.vfx.playExplosionBurst(es.x, es.y - 20);
      }
      if (es.enemyRef.currentHp <= 0 && this.heroSprites[0]) {
        this.killEnemy(es, this.heroSprites[0]);
      }
    }

    this.cameras.main.shake(40, 0.0006);
    this.cameras.main.flash(200, 255, 240, 180, false);
    this.showAnnouncement(`ᚨ  ${label.toUpperCase()}  ᚨ`);

    // Start cooldown — persist expiry to BOTH the player state (survives F5)
    // and the static mirror (survives scene.restart even if the underscore
    // field gets stripped mid-save).
    this.divineWrathCooldown = this.DIVINE_WRATH_CD;
    const expiry = Date.now() + this.DIVINE_WRATH_CD;
    (this.gsm.player as any)._dwCdExpiry = expiry;
    BattleScene.persistedDwCdExpiry = expiry;
    // Rebuild the invocation row so the Wrath icon picks up the fresh CD
    // (greys + paints the countdown label). The legacy
    // `createDivineWrathButton` was the standalone bottom-left button —
    // retired in part 246. Calling that here was the spam-bug source: the
    // bottom-bar icon's stale closure stayed clickable.
    this.createInvocationButtons();
  }

  // ====================== INVOCATION CAST BAR ======================
  // Two stage-50 keystones from the rune tree (Aegis of Light, Time Pull) that
  // surface as MMO-style icon buttons in the bottom-right cluster, sitting just
  // left of the rune-stone checkpoint icons. Locked + greyed if the talent
  // isn't picked, on cooldown after a cast. Behaviour stubs are intentionally
  // simple (clean party heal vs. cooldown wipe) — easy to balance later via
  // the CD constants.

  /** Render both invocation buttons. Called once at scene init and again whenever
   *  cooldown state changes (cast → tick to zero) so the icon redraws as ready. */
  private createInvocationButtons(): void {
    for (const obj of this.invocationBtnGroup) obj.destroy();
    this.invocationBtnGroup = [];
    this.avengingWrathCdLabel = null;
    this.aegisCdLabel = null;
    this.timePullCdLabel = null;

    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    // Wrath is always rendered (no gate yet — quest unlock TBD), so this row
    // is always present; the per-invocation `unlocked` flag controls each
    // icon's lit / locked state.

    // Layout — section 3 of the bottom bar (between separators at x=662 and
    // x=842). 4 invocations stride 40, span 674-830 with 12 px gap to each
    // separator (tightened from 18 per user feedback so the menu/section
    // gaps stop dominating the layout).
    //   [☠ Wrath 692] [⚔ Avenging 732] [☀ Aegis 772] [⧗ TimePull 812]
    const BTN_SIZE = 36;
    const wrathX = 692;
    const avengeX = 732;
    const aegisX = 772;
    const timeX = 812;
    const btnY = 556;

    // Border accent unified to bronze (0x8b6914) per user feedback so the
    // invocations stop reading like talent-tree buttons (which use bright
    // branch-tinted borders). Glyph colours keep just enough hue to identify
    // each invocation without competing with the rest of the HUD.

    // Wrath — restored per user request. Currently always available with no
    // gate: a quest-gated unlock is planned but not yet designed. The
    // `keystoneDivineWrath` passive (if taken) still upgrades the cast to
    // the higher-damage "Divine Wrath" variant (see fireDivineWrath).
    // Glyph: twin lightning bolts (Phaser-drawn) per user reference image —
    // two crossed jagged bolts in warm gold, reads as "sky judgement".
    const wrathProfile = this.getWrathProfile();
    this.invocationBtnGroup.push(
      ...this.buildInvocationIcon(wrathX, btnY, BTN_SIZE, {
        unlocked: true,
        drawGlyph: (g, gx, gy, gs, color) => this.drawTwinLightningBolts(g, gx, gy, gs, color),
        glyphColor: '#f8d878',
        accent: 0x8b6914,
        label: wrathProfile.label,
        tooltipUnlocked: `${wrathProfile.label}  [Q]\nSummon judgement — obliterates\nall enemies. Wrath grows fiercer\nas you carry it deeper.\n210 s cooldown.`,
        tooltipLocked: 'Wrath  (LOCKED)\nQuest unlock coming soon.',
        cooldown: this.divineWrathCooldown,
        onCast: () => this.fireDivineWrath(),
        cdLabelSlot: 'wrath',
      }),
    );

    // Avenge / Aegis / Time desaturated per user feedback so the action bar
    // isn't a wall of gold. Wrath keeps its bronze accent (it's the player's
    // signature invocation); the others get muted school-tinted borders +
    // cooler glyph colours so the row reads as varied dark-fantasy slots.
    this.invocationBtnGroup.push(
      ...this.buildInvocationIcon(avengeX, btnY, BTN_SIZE, {
        unlocked: pb.avengingWrathFlag,
        glyph: '⚔',
        glyphColor: '#b85040', // muted blood red instead of warm bronze
        accent: 0x4a2018,      // dark red-bronze
        label: 'Avenging Wrath',
        tooltipUnlocked: 'Avenging Wrath  [W]\n+50 % damage to every hero for 8 s.\nRestores 25 % rage / mana / energy.\n150 s cooldown.',
        tooltipLocked: 'Avenging Wrath  (LOCKED)\nUnlock in the Rune Tree at stage 40\n— 1000 runes + 30 branch ranks.',
        cooldown: this.avengingWrathCooldown,
        onCast: () => this.castAvengingWrath(),
        cdLabelSlot: 'avenge',
      }),
    );

    this.invocationBtnGroup.push(
      ...this.buildInvocationIcon(aegisX, btnY, BTN_SIZE, {
        unlocked: pb.aegisOfLightFlag,
        glyph: '☀',
        glyphColor: '#a0c0d0', // pale steel instead of gold
        accent: 0x3a4a5a,      // dark steel
        label: 'Aegis of Light',
        tooltipUnlocked: 'Aegis of Light  [E]\nHeal every hero for 30 % of max HP\nand grant a 5 s damage-reduction halo.\n120 s cooldown.',
        tooltipLocked: 'Aegis of Light  (LOCKED)\nUnlock in the Rune Tree at stage 40\n— 1000 runes + 30 branch ranks.',
        cooldown: this.aegisOfLightCooldown,
        onCast: () => this.castAegisOfLight(),
        cdLabelSlot: 'aegis',
      }),
    );

    this.invocationBtnGroup.push(
      ...this.buildInvocationIcon(timeX, btnY, BTN_SIZE, {
        unlocked: pb.timePullFlag,
        glyph: '⧗',
        glyphColor: '#a890b8', // muted lavender
        accent: 0x3a3050,      // dark indigo-purple
        label: 'Time Pull',
        tooltipUnlocked: 'Time Pull  [R]\nReset every hero ability cooldown.\nOnce per zone. 180 s cooldown.',
        tooltipLocked: 'Time Pull  (LOCKED)\nUnlock in the Rune Tree at stage 40\n— 1000 runes + 30 branch ranks.',
        cooldown: this.timePullCooldown,
        onCast: () => this.castTimePull(),
        cdLabelSlot: 'time',
        oncePerZone: this.timePullUsedThisZone,
      }),
    );
  }

  /** Builds one invocation icon (square frame + glyph + cooldown label + hover
   *  tooltip + click handler). Returns the GameObjects so the caller can drop
   *  them in the cleanup group. The glyph can be either a unicode `glyph`
   *  string OR a `drawGlyph` graphics callback when a richer icon is needed
   *  (e.g. Wrath's twin lightning bolts). */
  private buildInvocationIcon(
    bx: number, by: number, size: number,
    cfg: {
      unlocked: boolean; glyph?: string; glyphColor: string; accent: number;
      drawGlyph?: (g: Phaser.GameObjects.Graphics, bx: number, by: number, size: number, color: number) => void;
      label: string; tooltipUnlocked: string; tooltipLocked: string;
      cooldown: number; onCast: () => void;
      cdLabelSlot: 'wrath' | 'avenge' | 'aegis' | 'time'; oncePerZone?: boolean;
    },
  ): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const onCd = cfg.cooldown > 0 || (cfg.oncePerZone === true);
    const greyed = !cfg.unlocked || onCd;

    // Frame — matches the rune-stone checkpoints next door: dark stone fill,
    // bronze border (1 px when ready, 0.5 alpha when locked / on CD). Locked
    // invocations render blacked-out — almost-pitch fill, washed-out border,
    // 0.4 alpha on the whole icon stack so the slot reads "owned-but-empty"
    // like an unfilled consumable. The space stays fixed regardless.
    const frameFill = cfg.unlocked ? 0x0d0a08 : 0x020204;
    const borderCol = !cfg.unlocked ? 0x2a1c08 : onCd ? 0x4a3010 : cfg.accent;
    const frame = this.add.rectangle(bx, by, size, size, frameFill, 0.95)
      .setStrokeStyle(1, borderCol).setDepth(12).setInteractive({ useHandCursor: !greyed });
    out.push(frame);
    // Inner bevel — 1 px highlight 2 px in, mirroring the runestone frame so
    // the two icon types share a chiselled-stone look.
    const bevel = this.add.graphics().setDepth(12);
    bevel.lineStyle(1, 0x5a3a1a, cfg.unlocked ? 0.55 : 0.18);
    bevel.strokeRect(bx - size / 2 + 2, by - size / 2 + 2, size - 4, size - 4);
    out.push(bevel);

    // Glyph — either a Phaser-drawn graphic (drawGlyph) or a unicode text
    // character (glyph). Graphics path used for Wrath's twin lightning bolts;
    // the rest of the invocations use simple unicode glyphs.
    let glyph: Phaser.GameObjects.Text | Phaser.GameObjects.Graphics;
    if (cfg.drawGlyph) {
      const g = this.add.graphics().setDepth(13);
      const tint = greyed
        ? 0x2a2a2a
        : Phaser.Display.Color.HexStringToColor(cfg.glyphColor).color;
      cfg.drawGlyph(g, bx, by, size, tint);
      glyph = g;
    } else {
      const glyphFontPx = Math.round(size * 0.5);
      glyph = this.add.text(bx, by, cfg.glyph ?? '', {
        fontSize: `${glyphFontPx}px`, fontFamily: 'Georgia, serif',
        color: greyed ? '#2a2a2a' : cfg.glyphColor, fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(13);
    }
    out.push(glyph);

    // Locked treatment — drop the whole stack to 0.55 alpha and overlay a
    // tiny Phaser-drawn padlock in the bottom-right corner so the slot reads
    // as "owned-but-empty" (consumable-style). No emoji per project rule.
    if (!cfg.unlocked) {
      frame.setAlpha(0.55);
      bevel.setAlpha(0.55);
      glyph.setAlpha(0.55);

      const lockGfx = this.add.graphics().setDepth(14);
      const lx = bx + size / 2 - 7;
      const ly = by + size / 2 - 7;
      // Shackle (arch)
      lockGfx.lineStyle(1, 0x8a7048, 0.85);
      lockGfx.beginPath();
      lockGfx.arc(lx, ly - 2, 2.2, Math.PI, 0, false);
      lockGfx.strokePath();
      // Body
      lockGfx.fillStyle(0x8a7048, 0.85);
      lockGfx.fillRect(lx - 3, ly - 1, 6, 4.5);
      // Keyhole dot
      lockGfx.fillStyle(0x1a1108, 1);
      lockGfx.fillCircle(lx, ly + 1.2, 0.6);
      out.push(lockGfx);
    }

    // Cooldown label — same "Ns" white-on-dark style as Divine Wrath.
    if (onCd && cfg.unlocked) {
      const cdSec = cfg.oncePerZone ? '⌛' : `${Math.ceil(cfg.cooldown / 1000)}s`;
      const cdLbl = this.add.text(bx, by + 1, cdSec, {
        fontSize: '13px', fontFamily: 'Arial, sans-serif', color: '#ffffff',
        stroke: '#000000', strokeThickness: 3, fontStyle: 'bold',
      }).setResolution(2).setOrigin(0.5).setDepth(14);
      out.push(cdLbl);
      if (cfg.cdLabelSlot === 'wrath') this.divineWrathCdLabel = cdLbl;
      else if (cfg.cdLabelSlot === 'avenge') this.avengingWrathCdLabel = cdLbl;
      else if (cfg.cdLabelSlot === 'aegis') this.aegisCdLabel = cdLbl;
      else this.timePullCdLabel = cdLbl;
    }

    // Hover tooltip — bronze panel anchored above the icon. Same tooltip
    // shape as Divine Wrath so the HUD reads consistently.
    let tipObj: Phaser.GameObjects.GameObject | null = null;
    frame.on('pointerover', () => {
      if (cfg.unlocked && !onCd) frame.setStrokeStyle(1, 0xffe8a0);
      const t = this.createHudTooltip(bx, by - size / 2 - 6,
        cfg.unlocked ? cfg.tooltipUnlocked : cfg.tooltipLocked,
        0.5, 1, { fontSize: '10px', color: cfg.unlocked ? '#c8973a' : '#6b5040' });
      tipObj = t as Phaser.GameObjects.GameObject;
      this.invocationBtnGroup.push(tipObj);
    });
    frame.on('pointerout', () => {
      frame.setStrokeStyle(1, borderCol);
      if (tipObj) {
        tipObj.destroy();
        const idx = this.invocationBtnGroup.indexOf(tipObj);
        if (idx >= 0) this.invocationBtnGroup.splice(idx, 1);
        tipObj = null;
      }
    });
    frame.on('pointerdown', () => {
      if (greyed) return;
      cfg.onCast();
    });

    return out;
  }

  /** Twin lightning bolts — Phaser-drawn glyph for the Wrath invocation,
   *  matching the user's reference (two zig-zag bolts striking from above).
   *  Bolts are stacked side-by-side and offset vertically so they look like
   *  the sky's about to crack. `color` carries the greyed-vs-active tint. */
  private drawTwinLightningBolts(
    g: Phaser.GameObjects.Graphics,
    bx: number, by: number, size: number, color: number,
  ): void {
    const drawBolt = (cx: number, cy: number, scale: number): void => {
      // Path traces a 6-point zig-zag silhouette: top-wide → upper-left
      // shoulder → narrow waist → lower-right kink → bottom-tip → back up
      // the right edge to close the polygon.
      const s = scale;
      const pts: Array<[number, number]> = [
        [cx + 1.6 * s,  cy - 7.0 * s], // top-right
        [cx - 2.4 * s,  cy - 1.2 * s], // upper-left shoulder
        [cx - 0.4 * s,  cy - 0.6 * s], // waist-left
        [cx - 2.0 * s,  cy + 6.8 * s], // bottom-tip
        [cx + 2.4 * s,  cy + 0.4 * s], // lower-right kink
        [cx + 0.6 * s,  cy - 0.2 * s], // waist-right
        [cx + 3.2 * s,  cy - 5.4 * s], // upper-right shoulder
      ];
      g.fillStyle(color, 1);
      g.beginPath();
      g.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
      g.closePath();
      g.fillPath();
      // Dark edge so the bolt reads on warm-coloured backgrounds too.
      g.lineStyle(Math.max(1, s * 0.18), 0x1a0e04, 0.85);
      g.strokePath();
    };
    // Two bolts, slightly offset so they look like a paired strike. Scale
    // tuned for the 36 px invocation slot — each bolt ~14 px tall.
    const scale = size * 0.055;
    drawBolt(bx - size * 0.18, by - size * 0.04, scale);
    drawBolt(bx + size * 0.18, by + size * 0.04, scale);
  }

  /** Avenging Wrath — every alive hero deals +50 % damage for 8 s and gets
   *  25 % of their resource pool (rage / mana / energy) refunded. Read in
   *  damage-dealt code paths via the `avengingWrathUntil` timestamp; auto-
   *  expires by comparison without needing a tween or timer. */
  private castAvengingWrath(): void {
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    if (!pb.avengingWrathFlag) return;
    if (this.avengingWrathCooldown > 0) return;

    const now = Date.now();
    this.avengingWrathUntil = now + this.AVENGING_WRATH_DURATION;
    let buffedCount = 0;
    for (const hs of this.heroSprites) {
      const hero = hs.heroRef;
      if (!hero || hero.isDead || hero.permanentlyDead) continue;
      if (hero.stats.maxRage > 0) {
        hero.stats.rage = Math.min(hero.stats.maxRage, hero.stats.rage + Math.floor(hero.stats.maxRage * 0.25));
      }
      if (hero.stats.maxMp > 0) {
        hero.stats.mp = Math.min(hero.stats.maxMp, hero.stats.mp + Math.floor(hero.stats.maxMp * 0.25));
      }
      if (hero.stats.maxEnergy > 0) {
        hero.stats.energy = Math.min(hero.stats.maxEnergy, hero.stats.energy + Math.floor(hero.stats.maxEnergy * 0.25));
      }
      this.vfx.playRallyBuff(hs.x, hs.y);
      // Sustained DBZ-style flame pillar for the full 8s buff window. Foot
      // ring suppressed per user feedback so the body reads as ablaze, not
      // ringed at the ankles.
      this.vfx.playInvocationAura(hs, this.AVENGING_WRATH_DURATION, 0xb83820, { withFootRing: false });
      buffedCount++;
    }
    this.cameras.main.flash(140, 184, 56, 32, false);
    this.showAnnouncement(`⚔ Avenging Wrath — the line answers in fury`);
    // Top-left buff strip entry so allies see the timer alongside other party buffs.
    this.registerPartyBuff('inv_avenging_wrath', 'Avenging Wrath', 'spell_war_cry',
      `+50% damage · refunded resource (${buffedCount} hero${buffedCount === 1 ? '' : 'es'})`,
      this.AVENGING_WRATH_DURATION);

    this.avengingWrathCooldown = this.AVENGING_WRATH_CD;
    this.createInvocationButtons();
  }

  /** Aegis of Light — heals every alive hero for 30 % of their max HP and
   *  applies a transient damage-reduction flag (`_aegisDrUntil`) consumed by
   *  the damage code path. Stub-simple for now: any incoming damage while the
   *  flag is set is reduced by 50 %. Long shared cooldown. */
  private castAegisOfLight(): void {
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    if (!pb.aegisOfLightFlag) return;
    if (this.aegisOfLightCooldown > 0) return;

    const now = Date.now();
    const HALO_DURATION_MS = 5000;
    let healedCount = 0;
    for (const hs of this.heroSprites) {
      const hero = hs.heroRef;
      if (!hero || hero.isDead || hero.permanentlyDead) continue;
      const heal = Math.floor(hero.stats.maxHp * 0.30);
      hero.stats.hp = Math.min(hero.stats.maxHp, hero.stats.hp + heal);
      (hero as any)._aegisDrUntil = now + HALO_DURATION_MS;
      this.showDamageNumber(hs.x, hs.y - 30, `+${heal}`, '#a0c0d0', '14px');
      this.vfx.playHealGlow(hs.x, hs.y);
      // Pale-steel halo aura for the 5s damage-reduction window.
      this.vfx.playInvocationAura(hs, HALO_DURATION_MS, 0x7090b0);
      healedCount++;
    }
    this.cameras.main.flash(120, 160, 192, 208, false);
    this.showAnnouncement(`☀ Aegis of Light — the host stands sheltered`);
    this.registerPartyBuff('inv_aegis_of_light', 'Aegis of Light', 'spell_valka_shield_bash',
      `+30% HP healed · 50% dmg reduction (${healedCount} hero${healedCount === 1 ? '' : 'es'})`,
      HALO_DURATION_MS);

    this.aegisOfLightCooldown = this.AEGIS_OF_LIGHT_CD;
    this.createInvocationButtons();
  }

  /** Time Pull — reset every alive hero ability's currentCooldown to 0. Once
   *  per zone (gate clears at zone advance, wired in startNextWave wave-1
   *  branch). Combined with the 90 s baseline CD, the player gets at most one
   *  cast per zone, possibly skipped if they descend before the CD expires. */
  private castTimePull(): void {
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    if (!pb.timePullFlag) return;
    if (this.timePullCooldown > 0) return;
    if (this.timePullUsedThisZone) return;

    let resetCount = 0;
    const TIME_AURA_MS = 1800;
    for (const hs of this.heroSprites) {
      const hero = hs.heroRef;
      if (!hero || hero.isDead || hero.permanentlyDead) continue;
      for (const ab of hero.abilities) {
        if (ab.currentCooldown > 0) {
          ab.currentCooldown = 0;
          resetCount++;
        }
      }
      this.vfx.playRallyBuff(hs.x, hs.y);
      // Brief lavender pulse — Time Pull is instantaneous, but a short aura
      // marks the rewind moment so the player sees who got their CDs reset.
      this.vfx.playInvocationAura(hs, TIME_AURA_MS, 0x6050a0);
    }
    this.cameras.main.flash(120, 100, 80, 160, false);
    this.showAnnouncement(`⧗ Time Pull — the hourglass turns back`);
    if (resetCount > 0) {
      this.registerPartyBuff('inv_time_pull', 'Time Pull', 'spell_valka_whirlwind',
        `${resetCount} cooldown${resetCount === 1 ? '' : 's'} rewound`,
        TIME_AURA_MS);
    }

    this.timePullCooldown = this.TIME_PULL_CD;
    this.timePullUsedThisZone = true;
    BattleScene.persistedTimePullStage = this.gsm.player.currentLevel;
    this.createInvocationButtons();
  }

  private showCheckpointDeathOverlay(
    finalScore: number, finalLevel: number, finalWave: number,
    finalGold: number, finalKills: number, finalTokens: number,
    isNewBest: boolean,
  ): void {
    const width = GAME_W, height = GAME_H;
    const cx = width / 2;
    const cy = height / 2;

    this.refreshUI();

    const hasDeep = !!this.gsm.player.runeDeepCheckpoint;
    const hasShallow = !!this.gsm.player.runeCheckpoint;
    const panelH = hasDeep && hasShallow ? 380 : 340;

    // Overlay
    this.add.rectangle(cx, cy, width, height, 0x050402, 0.88).setDepth(60).setInteractive();

    // Panel — amber-rune theme
    this.add.rectangle(cx, cy, 420, panelH, 0x0d0a06, 0.98).setStrokeStyle(2, 0x8b6020).setDepth(60);
    this.add.rectangle(cx, cy, 414, panelH - 6, 0, 0).setStrokeStyle(1, 0xd4a855, 0.25).setDepth(61);

    // Title
    this.add.text(cx, cy - panelH / 2 + 18, hasDeep ? 'ᚱ' : 'ᛟ', {
      fontSize: '22px', fontFamily: 'Georgia, serif', color: hasDeep ? '#e04030' : '#c8973a',
    }).setOrigin(0.5).setDepth(62);
    this.add.text(cx, cy - panelH / 2 + 44, 'THE RUNE REMEMBERS', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
      stroke: '#1a0c00', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(62);
    this.add.text(cx, cy - panelH / 2 + 64, '— ᚱ —', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy - panelH / 2 + 84, 'All heroes have fallen.', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(62);

    const lines = [
      `Score: ${finalScore}${isNewBest ? '  ✦ NEW BEST!' : ''}`,
      `Level Reached: ${finalLevel}  ·  Waves: ${finalWave}`,
      `◆ Runes Earned: ${finalTokens}`,
    ];
    lines.forEach((line, i) => {
      this.add.text(cx, cy - panelH / 2 + 108 + i * 18, line, {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: i === 0 && isNewBest ? '#c8973a' : '#7a5a28',
      }).setOrigin(0.5).setDepth(62);
    });

    // Button row Y
    let btnY = cy + panelH / 2 - 70;
    const defX = cx + (hasDeep && hasShallow ? 148 : 96);

    // ── ETERNAL RUNE STONE button (deep, stage 30) — only if active ──
    if (hasDeep) {
      const deepBg = this.add.rectangle(cx - 120, btnY, 140, 38, 0x040e08, 1)
        .setStrokeStyle(2, 0xc03020).setDepth(62);
      const deepTxt = this.add.text(cx - 120, btnY, 'ᚱ  ETERNAL STONE', {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#e04030', fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(63);
      this.add.text(cx - 120, btnY + 18, 'Return to Stage 30', {
        fontSize: '8px', fontFamily: 'Georgia, serif', color: '#206040',
      }).setOrigin(0.5).setDepth(63);
      deepTxt.on('pointerover', () => { deepTxt.setColor('#ff6050'); deepBg.setStrokeStyle(2, 0xff6050); });
      deepTxt.on('pointerout',  () => { deepTxt.setColor('#e04030'); deepBg.setStrokeStyle(2, 0xc03020); });
      deepTxt.on('pointerdown', () => {
        const savedScore = this.gsm.player.runeDeepCheckpoint?.runScore ?? 0;
        this.gsm.restoreDeepRunCheckpoint();
        this.gsm.clearDeepRunCheckpoint();
        this.gsm.clearRunCheckpoint(); // also clear stage 10 checkpoint
        BattleScene.persistedRunScore = savedScore;
        // Same score-integrity safeguards as the stage-10 restore path
        // above — explicitly reset live runScore + clear stale server
        // session + reset zone/wave bonus idempotency keys so neither
        // can leak the pre-restore high score and the rebuilt run can
        // re-earn the zone/wave bonuses normally.
        this.runScore = savedScore;
        this.gsm.clearRunSession();
        this._zoneScoreCountedKey = null;
        this._waveScoreCountedKey = null;
        this.gsm.save(true);
        this.cleanupBattle();
        this.scene.restart();
      });
    }

    // ── RUNE STONE button (stage 10) — only if active ──
    if (hasShallow) {
      const retX = hasDeep ? cx + 10 : cx - 86;
      const retBg = this.add.rectangle(retX, btnY, 140, 38, 0x1a1204, 1)
        .setStrokeStyle(2, 0xc8973a).setDepth(62);
      const retTxt = this.add.text(retX, btnY, 'ᛟ  RUNE STONE', {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: '#d4a855', fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(63);
      this.add.text(retX, btnY + 18, 'Return to Stage 10', {
        fontSize: '8px', fontFamily: 'Georgia, serif', color: '#6a4820',
      }).setOrigin(0.5).setDepth(63);
      retTxt.on('pointerover', () => { retTxt.setColor('#ffe8a0'); retBg.setStrokeStyle(2, 0xffe060); });
      retTxt.on('pointerout',  () => { retTxt.setColor('#d4a855'); retBg.setStrokeStyle(2, 0xc8973a); });
      retTxt.on('pointerdown', () => {
        const savedScore = this.gsm.player.runeCheckpoint?.runScore ?? 0;
        this.gsm.restoreRunCheckpoint();
        this.gsm.clearRunCheckpoint();
        this.gsm.clearDeepRunCheckpoint();
        BattleScene.persistedRunScore = savedScore;
        // Score-integrity safeguards. Even though scene.restart() will
        // re-initialize this.runScore from BattleScene.persistedRunScore,
        // we also overwrite the local + session state explicitly so any
        // intermediate read (cleanupBattle hooks, save flush) sees the
        // correct checkpoint score — and the SERVER session no longer
        // holds the pre-restore "high score" run that just ended.
        this.runScore = savedScore;
        this.gsm.clearRunSession();
        // Reset zone / wave score-counter idempotency keys — without this,
        // refighting stage 10's zone after restore would no-op the bonus
        // (the key still matches the pre-restore "10_10_std" entry).
        this._zoneScoreCountedKey = null;
        this._waveScoreCountedKey = null;
        this.gsm.save(true);
        this.cleanupBattle();
        this.scene.restart();
      });
    }

    // ── ACCEPT DEFEAT button ──
    const defBg = this.add.rectangle(defX, btnY, 110, 38, 0x0d0a08, 1)
      .setStrokeStyle(2, 0x5a3600).setDepth(62);
    const defTxt = this.add.text(defX, btnY, 'ACCEPT DEFEAT', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#7a5a28',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(63);
    defTxt.on('pointerover', () => { defTxt.setColor('#c8973a'); defBg.setStrokeStyle(2, 0x9a6020); });
    defTxt.on('pointerout',  () => { defTxt.setColor('#7a5a28'); defBg.setStrokeStyle(2, 0x5a3600); });
    defTxt.on('pointerdown', () => {
      this.gsm.clearRunCheckpoint();
      this.gsm.clearDeepRunCheckpoint();
      this.gsm.resetRun();
      this.gsm.save(true);
      this.cleanupBattle();
      this.scene.restart();
    });
  }


  private refreshTomeIcons(): void {
    // Build a snapshot string to detect changes — only rebuild when tomes change
    const tomes = this.gsm.player.tomes;
    const snapshot = TOME_IDS.map((id) => getTomeLevel(tomes, id)).join(',');
    if (snapshot === this.lastTomeSnapshot) return;
    this.lastTomeSnapshot = snapshot;

    // Clear old icons
    for (const obj of this.tomeIconObjects) obj.destroy();
    this.tomeIconObjects = [];

    const activeTomes = TOME_IDS.filter((id) => getTomeLevel(tomes, id) > 0);
    if (activeTomes.length === 0) return;

    const width = GAME_W;
    const iconSize = 34;
    const gap = 6;
    const totalW = activeTomes.length * iconSize + (activeTomes.length - 1) * gap;
    const startX = (width - totalW) / 2 + iconSize / 2;
    const y = 498; // Above the bottom nav bar with clear gap

    activeTomes.forEach((tomeId, i) => {
      const def = TOME_DEFINITIONS[tomeId];
      const level = getTomeLevel(tomes, tomeId);
      const cx = startX + i * (iconSize + gap);

      // Icon background
      const bg = this.add.rectangle(cx, y, iconSize, iconSize, 0x1e293b, 0.9)
        .setStrokeStyle(1, Phaser.Display.Color.HexStringToColor(def.color).color)
        .setDepth(12);
      this.tomeIconObjects.push(bg);

      // Unified gold color for HUD tome icons
      const icon = this.add.text(cx, y, def.icon, {
        fontSize: '20px', padding: { y: 4 },
        fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif", fontStyle: 'bold',
        color: '#d4a84a', stroke: '#1a0f00', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(13).setResolution(2);
      this.tomeIconObjects.push(icon);

      // Stack count
      const count = this.add.text(cx + 10, y + 8, `${level}`, {
        fontSize: '8px', fontFamily: 'monospace', color: '#ffffff', fontStyle: 'bold',
        backgroundColor: def.color, padding: { x: 2, y: 0 },
      }).setOrigin(0.5).setDepth(14);
      this.tomeIconObjects.push(count);

      // Just-picked pulse — if this tome is the one the player just selected in
      // showTomeChoice, fire a scale-up + sparkle burst here so the HUD icon
      // reads as "this is what just levelled up". Static flag is cleared after
      // fire so the pulse plays once per pick, not every snapshot rebuild.
      if (BattleScene.justPickedTomeId === tomeId) {
        BattleScene.justPickedTomeId = null;
        const tint = Phaser.Display.Color.HexStringToColor(def.color).color;
        this.time.delayedCall(20, () => this.vfx.playTomeIconPulse([bg, icon, count], cx, y, tint));
      }

      // Tooltip on hover — full info
      bg.setInteractive();
      bg.on('pointerover', () => {
        this.tomeTooltip?.destroy();
        const bonus = level * def.bonusPerLevel;
        const fmtPct = (v: number) => parseFloat((v * 100).toFixed(1));
        const bonusStr = def.bonusUnit.startsWith('%')
          ? `+${fmtPct(bonus)}${def.bonusUnit}`
          : `+${parseFloat(bonus.toFixed(2))}${def.bonusUnit}`;
        const nextBonus = (level + 1) * def.bonusPerLevel;
        const nextStr = def.bonusUnit.startsWith('%')
          ? `+${fmtPct(nextBonus)}${def.bonusUnit}`
          : `+${parseFloat(nextBonus.toFixed(2))}${def.bonusUnit}`;
        const lines = [
          `${def.icon} ${def.name} — Level ${level}/${def.maxLevel}`,
          `${def.description}`,
          `Current: ${bonusStr}`,
          level < def.maxLevel ? `Next level: ${nextStr}` : 'MAX LEVEL',
        ];
        // Position tooltip just above icon, clamped to screen edges
        const tooltipX = Phaser.Math.Clamp(cx, 80, width - 80);
        this.tomeTooltip = this.createHudTooltip(tooltipX, y - 16, lines.join('\n'),
          0.5, 1, { fontSize: '11px', wrap: 240 },
        );
      });
      bg.on('pointerout', () => {
        this.tomeTooltip?.destroy();
        this.tomeTooltip = null;
      });
    });
  }

  /** Render active consumable buff icons top-left (2x tome icon size) with duration & tooltip */
  private refreshConsumableIcons(): void {
    const actives = this.gsm.player.activeConsumables || [];
    // Include equipped unique relics on alive heroes in snapshot so icons update on equip/death
    const relicSnap = this.heroSprites
      .filter((h) => !h.heroRef.isDead)
      .flatMap((h) => (['relic1', 'relic2', 'relic3', 'relic4'] as const).map((s) => h.heroRef.equipment[s]))
      .filter((r) => r?.rarity === 'unique')
      .map((r) => r!.name).join(',');
    // Party buffs: only track presence (added/removed), NOT timer value — timer is updated separately
    // via refreshPartyBuffTimers() to avoid rebuilding icons every 500ms (which breaks tooltips)
    const partySnap = this.partyBuffs.map((b) => b.id).join(',');
    const trophySnap = this.gsm.player.deployedHeroes
      .map((idx) => this.gsm.player.heroes[idx])
      .filter((h) => !!h?.equipment.reserved)
      .map((h) => h!.equipment.reserved!.name).join(',');
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    const keystoneSnap = [pb.keystoneBerserk ? 'B' : '', pb.keystoneDivineWrath ? 'D' : '', pb.keystoneAncientPact ? 'A' : ''].join('');
    const sigilSnap = (this.gsm.player.sigilsEquipped ?? []).join(',');
    const banSnap = (this.gsm.player.bannedTomes ?? []).join(',');
    const boonSnap = (this.gsm.player.activeBoons ?? []).map((b) => `${b.id}:${b.stacks ?? 0}`).join(',');
    const snapshot = actives.map((c) => `${c.id}:${c.remainingWaves}`).join(',') + '|' + relicSnap + '|' + partySnap + '|' + trophySnap + '|' + keystoneSnap + '|' + sigilSnap + '|' + banSnap + '|' + boonSnap;
    if (snapshot === this.lastConsumableSnapshot) return;
    this.lastConsumableSnapshot = snapshot;

    // Clear old icons (consumables, relics, party buffs, and trophies — all unified)
    for (const obj of this.consumableIconObjects) obj.destroy();
    this.consumableIconObjects = [];
    this.consumableTooltip?.destroy();
    this.consumableTooltip = null;
    this.partyBuffBadges = [];
    for (const ov of this.partyBuffOverlays) ov.destroy();
    this.partyBuffOverlays = [];
    for (const el of this.trophyDisplayElements) el.destroy();
    this.trophyDisplayElements = [];
    this.trophyTooltip?.destroy();
    this.trophyTooltip = null;

    const hasUniqueRelics = this.heroSprites.some((h) =>
      !h.heroRef.isDead && (['relic1', 'relic2', 'relic3', 'relic4'] as const).some((s) => h.heroRef.equipment[s]?.rarity === 'unique'),
    );
    const hasTrophies = this.gsm.player.deployedHeroes.some((idx) => !!this.gsm.player.heroes[idx]?.equipment.reserved);
    const hasKeystones = pb.keystoneBerserk || pb.keystoneDivineWrath || pb.keystoneAncientPact;
    const hasSigils = (this.gsm.player.sigilsEquipped?.length ?? 0) > 0;
    const hasBannedBooks = (this.gsm.player.bannedTomes?.length ?? 0) > 0;
    const hasBoons = (this.gsm.player.activeBoons?.length ?? 0) > 0;
    if (actives.length === 0 && this.partyBuffs.length === 0 && !hasUniqueRelics && !hasTrophies && !hasKeystones && !hasSigils && !hasBannedBooks && !hasBoons) return;

    const iconSize = 38;
    const gap = 5;
    const startX = 10 + iconSize / 2;
    // Row 1 (y1 — TOP):    Keystones + Unique relics + Trophies   — sticky/permanent
    // Row 2 (y2 — BOTTOM): Consumables + Party buffs              — duration-based
    const y1 = 60;
    const y2 = y1 + iconSize + gap;

    // Buff icon rarity backgrounds
    const buffRarityBg: Record<string, number> = {
      common: 0x131517, uncommon: 0x0d1f0a, rare: 0x0a1228,
      epic: 0x1a0a28, legendary: 0x281a05, unique: 0x280a0a,
    };

    // ── ROW 1 (TOP): Book-ban debuff first, then keystones ─────────────────────
    let permSlotIdx = 0; // slot counter for row 1
    // Book-ban debuff — always first in the row. Red tint + "DEBUFF" corner tag so the
    // player can't miss it. Tooltip lists the banned tomes and the -1% atk/maxHp malus.
    const bannedTomes = this.gsm.player.bannedTomes ?? [];
    if (bannedTomes.length > 0) {
      const bannedNames = bannedTomes.map((id) => TOME_DEFINITIONS[id]?.name ?? id).join(', ');
      const cx = startX + permSlotIdx * (iconSize + gap);
      const banBg = this.add.rectangle(cx, y1, iconSize, iconSize, 0x200608, 0.95)
        .setStrokeStyle(2, 0xc03030).setDepth(12);
      this.consumableIconObjects.push(banBg);
      // Phaser-drawn banned-book glyph: small book silhouette with red slash across it.
      // Scaled up ~1.4× from the base ~22 px footprint so it fills the buff tile.
      const banIconGfx = this.add.graphics().setDepth(13);
      banIconGfx.translateCanvas(cx, y1);
      banIconGfx.scaleCanvas(1.4, 1.4);
      drawBanIcon(banIconGfx, 0, 0, 0xb08830, 0xe03030);
      this.consumableIconObjects.push(banIconGfx);
      const banBadge = this.add.text(cx - iconSize / 2 + 1, y1 - iconSize / 2 + 1, 'BAN', {
        fontSize: '5px', fontFamily: 'monospace', color: '#ff8080', fontStyle: 'bold',
        backgroundColor: '#2a0608', padding: { x: 1, y: 0 },
      }).setOrigin(0, 0).setDepth(14);
      this.consumableIconObjects.push(banBadge);
      banBg.setInteractive({ useHandCursor: true });
      banBg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const tooltipX = Math.max(120, cx);
        this.consumableTooltip = this.createHudTooltip(
          tooltipX, y1 + iconSize / 2 + 6,
          `⊘ Banned Books\n${bannedNames}\n\nRemoved from the random pick pool this run.`,
          0, 0, { fontSize: '11px', wrap: 240 },
        );
      });
      banBg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });
      permSlotIdx++;
    }

    // Active Shrine of Boons — show one icon per active boon, coloured by tier
    // (blessing gold / curse red). Tooltip shows the boon's full description
    // plus current stack count for stacking boons (Inner Flame, Blood Price).
    const activeBoons = this.gsm.player.activeBoons ?? [];
    for (const ab of activeBoons) {
      const def = getBoonOrAltarDef(ab.id);
      if (!def) continue;
      // `tier` only exists on shrine BoonDefs — altar rewards (pure upside) are
      // treated as blessings for the HUD colour treatment.
      const isCurse = 'tier' in def && def.tier === 'curse';
      // Blessing/altar frame muted from 0xf0c040 → 0x8a6020 per user feedback.
      const frame = isCurse ? 0xef4444 : 0x8a6020;
      const cx = startX + permSlotIdx * (iconSize + gap);
      const bBg = this.add.rectangle(cx, y1, iconSize, iconSize, 0x120a04, 0.95)
        .setStrokeStyle(2, frame).setDepth(12);
      this.consumableIconObjects.push(bBg);
      // Runic glyph in place of the old pixel-art icon — matches the shrine
      // popup's vibe so a boon on the HUD reads as the same artefact.
      const runeGfx = this.add.graphics().setDepth(13);
      drawBoonRune(runeGfx, cx, y1, def as import('../utils/glyphs').BoonRuneSource, iconSize - 8, { forceAltar: !('tier' in def) });
      this.consumableIconObjects.push(runeGfx);
      // Stack badge for stacking boons — shows "N" in the corner so the player
      // can see Inner Flame / Blood Price progress at a glance.
      if (def.effects.stackPerKill && (ab.stacks ?? 0) > 0) {
        const stackT = this.add.text(cx + iconSize / 2 - 2, y1 + iconSize / 2 - 2, `${ab.stacks}`, {
          fontSize: '7px', fontFamily: 'monospace', color: '#fde68a', fontStyle: 'bold',
          backgroundColor: '#1a0800', padding: { x: 1, y: 0 },
        }).setOrigin(1, 1).setDepth(14);
        this.consumableIconObjects.push(stackT);
      }
      bBg.setInteractive({ useHandCursor: true });
      bBg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const tipX = Math.max(120, cx);
        // Altar rewards are tier-less — show "⚜ OFFERING" instead of BLESSING/CURSE.
        const isAltar = !('tier' in def);
        const tipLabel = isAltar ? '⚜ OFFERING' : (isCurse ? '💀 CURSE' : '✦ BLESSING');
        this.consumableTooltip = this.createHudTooltip(
          tipX, y1 + iconSize / 2 + 6,
          `${tipLabel}  ${def.name}\n${def.description}`,
          0, 0, { fontSize: '11px', wrap: 240 },
        );
      });
      bBg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });
      permSlotIdx++;
    }

    const KEYSTONE_DEFS = [
      { active: pb.keystoneBerserk,     icon: 'ᚹ', label: 'Berserk Rite',  desc: 'Heroes below 30% HP deal +40% dmg (take +15% more)', color: 0xc03020 },
      { active: pb.keystoneDivineWrath, icon: 'ᚨ', label: 'Divine Wrath',  desc: 'Wrath upgraded to Divine Wrath (2× dmg, glowing skull). Lightning strikes strongest enemy every 28s for 80 dmg.', color: 0x204878 },
      { active: pb.keystoneAncientPact, icon: 'ᚠ', label: 'Ancient Pact',  desc: '+1 gold permanently per kill this run', color: 0xb07820 },
    ];
    for (const kd of KEYSTONE_DEFS) {
      if (!kd.active) continue;
      const cx = startX + permSlotIdx * (iconSize + gap);
      const kBg = this.add.rectangle(cx, y1, iconSize, iconSize, 0x060402, 0.95)
        .setStrokeStyle(2, kd.color).setDepth(12);
      this.consumableIconObjects.push(kBg);
      const kIcon = this.add.text(cx, y1, kd.icon, {
        fontSize: '20px', fontFamily: 'Georgia, serif',
      }).setOrigin(0.5).setDepth(13);
      this.consumableIconObjects.push(kIcon);
      const kBadge = this.add.text(cx - iconSize / 2 + 1, y1 - iconSize / 2 + 1, 'KEY', {
        fontSize: '5px', fontFamily: 'monospace', color: '#c8973a', fontStyle: 'bold',
        backgroundColor: '#0f172a', padding: { x: 1, y: 0 },
      }).setOrigin(0, 0).setDepth(14);
      this.consumableIconObjects.push(kBadge);
      kBg.setInteractive({ useHandCursor: true });
      kBg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const tooltipX = Math.max(120, cx);
        this.consumableTooltip = this.createHudTooltip(
          tooltipX, y1 + iconSize / 2 + 6,
          `${kd.icon} ${kd.label}\n${kd.desc}`,
          0, 0, { fontSize: '11px', wrap: 240 },
        );
      });
      kBg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });
      permSlotIdx++;
    }

    // ── ROW 1 (TOP): Unique relic icons ──────────────────────────────────────
    const UNIQUE_COLOR = 0xf0d060;
    for (const hs of this.heroSprites) {
      if (hs.heroRef.isDead) continue;
      for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        const relic = hs.heroRef.equipment[slot];
        if (!relic || relic.rarity !== 'unique') continue;
        const cx = startX + permSlotIdx * (iconSize + gap);

        const bg = this.add.rectangle(cx, y1, iconSize, iconSize, 0x1a1005, 0.9)
          .setStrokeStyle(2, UNIQUE_COLOR).setDepth(12);
        this.consumableIconObjects.push(bg);

        if (this.textures.exists(relic.spriteKey)) {
          const icon = this.add.image(cx, y1, relic.spriteKey)
            .setDisplaySize(iconSize - 4, iconSize - 4).setDepth(13);
          this.consumableIconObjects.push(icon);
        } else {
          const icon = this.add.text(cx, y1, '✦', { fontSize: '16px', color: '#f0d060' })
            .setOrigin(0.5).setDepth(13);
          this.consumableIconObjects.push(icon);
        }

        const nameBadge = this.add.text(
          cx - iconSize / 2 + 1, y1 - iconSize / 2 + 1,
          hs.heroRef.name.slice(0, 3).toUpperCase(),
          { fontSize: '6px', fontFamily: 'monospace', color: '#f0d060', fontStyle: 'bold',
            backgroundColor: '#0f172a', padding: { x: 1, y: 0 } },
        ).setOrigin(0, 0).setDepth(14);
        this.consumableIconObjects.push(nameBadge);

        bg.setInteractive();
        bg.on('pointerover', () => {
          this.consumableTooltip?.destroy();
          const effect = getRelicScaledEffect(relic.name, 1);
          const lines = [`✦ ${relic.name}`, effect || relic.specialEffect || ''].filter(Boolean);
          const tooltipX = Math.max(120, cx);
          this.consumableTooltip = this.createHudTooltip(
            tooltipX, y1 + iconSize / 2 + 6, lines.join('\n'),
            0, 0, { fontSize: '11px', color: '#f0d060', wrap: 240 },
          );
        });
        bg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });

        permSlotIdx++;
      }
    }

    // ── ROW 1 (TOP): Equipped sigil icons — placed BEFORE trophies so sigils always
    // anchor leftmost in the permanent buff strip (per user preference: sigils > mimics). ──
    // Sigil frame desaturated 0xc0912a → 0x7a5418 so sigils read as ancient
    // engraved stones instead of hot gold leaf.
    const SIGIL_COLOR = 0x7a5418;
    const equippedSigils = this.gsm.player.sigilsEquipped ?? [];
    const sigilBag = this.gsm.player.sigilBag ?? [];
    const sigilCollection = this.gsm.account.sigilCollection ?? [];
    for (const sigilId of equippedSigils) {
      const sigil = sigilBag.find((s) => s.id === sigilId)
        ?? sigilCollection.find((s) => s.id === sigilId);
      if (!sigil) continue;
      const cx = startX + permSlotIdx * (iconSize + gap);

      const sBg = this.add.rectangle(cx, y1, iconSize, iconSize, 0x1a1005, 0.9)
        .setStrokeStyle(2, SIGIL_COLOR).setDepth(12);
      this.consumableIconObjects.push(sBg);

      if (this.textures.exists(sigil.special.iconKey)) {
        const sIcon = this.add.image(cx, y1, sigil.special.iconKey)
          .setDisplaySize(iconSize - 4, iconSize - 4).setDepth(13);
        this.consumableIconObjects.push(sIcon);
      } else {
        const sIcon = this.add.text(cx, y1, '◆', { fontSize: '18px', color: '#f0c040', padding: { y: 4 } })
          .setOrigin(0.5).setDepth(13);
        this.consumableIconObjects.push(sIcon);
      }

      const sBadge = this.add.text(cx - iconSize / 2 + 1, y1 - iconSize / 2 + 1, 'SIG', {
        fontSize: '5px', fontFamily: 'monospace', color: '#f0c040', fontStyle: 'bold',
        backgroundColor: '#0f172a', padding: { x: 1, y: 0 },
      }).setOrigin(0, 0).setDepth(14);
      this.consumableIconObjects.push(sBadge);

      sBg.setInteractive({ useHandCursor: true });
      sBg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const affixLine = sigil.affixes.map(formatAffix).join(', ');
        const lines = [sigil.name, sigil.special.description, affixLine].filter(Boolean);
        const tooltipX = Math.max(120, cx);
        this.consumableTooltip = this.createHudTooltip(
          tooltipX, y1 + iconSize / 2 + 6, lines.join('\n'),
          0, 0, { fontSize: '11px', color: '#f0c040', wrap: 240 },
        );
      });
      sBg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });

      permSlotIdx++;
    }

    // ── ROW 1 (TOP): Trophy icons (granted by Mimic chest) ───────────────────
    const TROPHY_RARITY_BG: Record<string, number> = { common: 0x131517, uncommon: 0x0d1f0a, rare: 0x0a1228, epic: 0x1a0a28, legendary: 0x281a05, unique: 0x280a0a };
    for (const heroIdx of this.gsm.player.deployedHeroes) {
      const hero = this.gsm.player.heroes[heroIdx];
      if (!hero?.equipment.reserved) continue;
      const trophy = hero.equipment.reserved;
      const cx = startX + permSlotIdx * (iconSize + gap);

      const tBg = this.add.rectangle(cx, y1, iconSize, iconSize, TROPHY_RARITY_BG[trophy.rarity] ?? 0x131517, 0.92)
        .setStrokeStyle(1, 0x6b4010).setDepth(12);
      this.consumableIconObjects.push(tBg);

      if (trophy.spriteKey && this.textures.exists(trophy.spriteKey)) {
        const tIcon = this.add.image(cx, y1, trophy.spriteKey)
          .setDisplaySize(iconSize - 4, iconSize - 4).setDepth(13);
        this.consumableIconObjects.push(tIcon);
      } else {
        const tIcon = this.add.text(cx, y1, '🏆', { fontSize: '18px', padding: { y: 4 } })
          .setOrigin(0.5).setDepth(13);
        this.consumableIconObjects.push(tIcon);
      }

      const tNameBadge = this.add.text(
        cx - iconSize / 2 + 1, y1 - iconSize / 2 + 1,
        hero.name.slice(0, 3).toUpperCase(),
        { fontSize: '6px', fontFamily: 'monospace', color: '#c8973a', fontStyle: 'bold',
          backgroundColor: '#0f172a', padding: { x: 1, y: 0 } },
      ).setOrigin(0, 0).setDepth(14);
      this.consumableIconObjects.push(tNameBadge);

      tBg.setInteractive({ useHandCursor: true });
      tBg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const lines = [`🏆 ${trophy.name}`, trophy.specialEffect || '', `(${hero.name})`].filter(Boolean);
        const tooltipX = Math.max(120, cx);
        this.consumableTooltip = this.createHudTooltip(
          tooltipX, y1 + iconSize / 2 + 6, lines.join('\n'),
          0, 0, { fontSize: '11px', wrap: 240 },
        );
      });
      tBg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });

      permSlotIdx++;
    }

    // ── ROW 2 (BOTTOM): Consumables (duration-based) ─────────────────────────
    let durSlotIdx = 0; // slot counter for row 2
    actives.forEach((active) => {
      const def = getConsumableDef(active.id);
      if (!def) return;
      const cx = startX + durSlotIdx * (iconSize + gap);

      const bg = this.add.rectangle(cx, y2, iconSize, iconSize, buffRarityBg[def.rarity] ?? 0x131517, 0.92)
        .setStrokeStyle(1, 0x6b4010).setDepth(12);
      this.consumableIconObjects.push(bg);

      if (def.spriteKey && this.textures.exists(def.spriteKey)) {
        const icon = this.add.image(cx, y2, def.spriteKey)
          .setDisplaySize(iconSize - 4, iconSize - 4).setOrigin(0.5).setDepth(13);
        this.consumableIconObjects.push(icon);
      } else {
        const icon = this.add.text(cx, y2, def.icon, {
          fontSize: '22px', padding: { y: 4 },
        }).setOrigin(0.5).setDepth(13);
        this.consumableIconObjects.push(icon);
      }

      // Timer drain overlay — dark fill from top, drains as duration runs out
      const frac = def.durationWaves > 1 ? Math.max(0, 1 - active.remainingWaves / def.durationWaves) : 0;
      if (frac > 0.02) {
        const ovH = Math.round((iconSize - 2) * frac);
        const ovY = y2 - (iconSize / 2 - 1) + ovH / 2;
        const ov = this.add.graphics()
          .fillStyle(0x000000, 0.35)
          .fillRect(cx - (iconSize / 2 - 1), y2 - (iconSize / 2 - 1), iconSize - 2, ovH)
          .setDepth(13.5);
        this.consumableIconObjects.push(ov);
        void ovY; // suppress unused warning
      }

      const badge = this.add.text(cx + iconSize / 2 - 1, y2 + iconSize / 2 - 1, `${active.remainingWaves}`, {
        fontSize: '7px', fontFamily: 'monospace', color: '#ffffff', fontStyle: 'bold',
        backgroundColor: '#0f172a', padding: { x: 2, y: 0 },
      }).setOrigin(1, 1).setDepth(14);
      this.consumableIconObjects.push(badge);

      bg.setInteractive();
      bg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const lines = [`${def.icon} ${def.name}`, def.description, `Remaining: ${active.remainingWaves} waves`];
        const tooltipX = Math.max(120, cx);
        this.consumableTooltip = this.createHudTooltip(
          tooltipX, y2 + iconSize / 2 + 6, lines.join('\n'),
          0, 0, { fontSize: '11px', wrap: 240 },
        );
      });
      bg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });

      durSlotIdx++;
    });

    // ── ROW 2 (BOTTOM): Party buff icons (hero abilities — duration) ──────────
    // Desaturated 0xf59e0b → 0x6b5028 per user feedback so the buff strip
    // doesn't drown the HUD in saturated gold/yellow. Tooltip text follows.
    const PARTY_BUFF_COLOR = 0x6b5028;
    for (let pi = 0; pi < this.partyBuffs.length; pi++) {
      const pbuff = this.partyBuffs[pi];
      const cx = startX + (durSlotIdx + pi) * (iconSize + gap);

      const pbBg = this.add.rectangle(cx, y2, iconSize, iconSize, 0x1a1005, 0.9)
        .setStrokeStyle(2, PARTY_BUFF_COLOR).setDepth(12);
      this.consumableIconObjects.push(pbBg);

      // Support sprite-key icons (e.g. 'spell_war_cry') or fallback to emoji text
      const isSprite = this.textures.exists(pbuff.icon);
      const pbIcon: Phaser.GameObjects.Image | Phaser.GameObjects.Text = isSprite
        ? this.add.image(cx, y2, pbuff.icon).setDisplaySize(iconSize - 4, iconSize - 4).setDepth(13)
        : this.add.text(cx, y2, pbuff.icon, {
            fontSize: '20px', padding: { y: 4 },
          }).setOrigin(0.5).setDepth(13);
      if (!isSprite) (pbIcon as Phaser.GameObjects.Text).setOrigin(0.5);
      this.consumableIconObjects.push(pbIcon);

      // Timer drain overlay for party buff
      const pbFrac = pbuff.totalMs > 0 ? Math.max(0, 1 - pbuff.remainingMs / pbuff.totalMs) : 0;
      const pbOv = this.add.graphics().setDepth(13.5);
      if (pbFrac > 0.02) {
        const ovH = Math.round((iconSize - 2) * pbFrac);
        pbOv.fillStyle(0x000000, 0.35).fillRect(cx - (iconSize / 2 - 1), y2 - (iconSize / 2 - 1), iconSize - 2, ovH);
      }
      this.consumableIconObjects.push(pbOv);
      this.partyBuffOverlays.push(pbOv);

      const secLeft = Math.ceil(pbuff.remainingMs / 1000);
      const pbBadge = this.add.text(cx + iconSize / 2 - 1, y2 + iconSize / 2 - 1, `${secLeft}s`, {
        fontSize: '7px', fontFamily: 'monospace', color: '#ffffff', fontStyle: 'bold',
        backgroundColor: '#0f172a', padding: { x: 2, y: 0 },
      }).setOrigin(1, 1).setDepth(14);
      this.consumableIconObjects.push(pbBadge);
      this.partyBuffBadges.push(pbBadge);

      pbBg.setInteractive();
      pbBg.on('pointerover', () => {
        this.consumableTooltip?.destroy();
        const sLeft = Math.ceil(pbuff.remainingMs / 1000);
        const iconPrefix = this.textures.exists(pbuff.icon) ? '' : `${pbuff.icon} `;
        const lines = [`${iconPrefix}${pbuff.name}`, pbuff.description, `Remaining: ${sLeft}s`];
        const tooltipX = Math.max(120, cx);
        this.consumableTooltip = this.createHudTooltip(
          tooltipX, y2 + iconSize / 2 + 6, lines.join('\n'),
          0, 0, { fontSize: '11px', color: '#f59e0b', wrap: 240 },
        );
      });
      pbBg.on('pointerout', () => { this.consumableTooltip?.destroy(); this.consumableTooltip = null; });
    }
  }

  /** Update party buff countdown badges and drain overlays in-place (no icon rebuild needed) */
  private refreshPartyBuffTimers(): void {
    for (let i = 0; i < this.partyBuffBadges.length && i < this.partyBuffs.length; i++) {
      const badge = this.partyBuffBadges[i];
      if (badge.active) badge.setText(`${Math.ceil(this.partyBuffs[i].remainingMs / 1000)}s`);
      // Update drain overlay
      const ov = this.partyBuffOverlays[i];
      if (ov?.active) {
        const pbuff = this.partyBuffs[i];
        const frac = pbuff.totalMs > 0 ? Math.max(0, 1 - pbuff.remainingMs / pbuff.totalMs) : 0;
        const iconSize = 38; const startX = 10 + iconSize / 2;
        const y2 = 60 + iconSize + 5; // matches refreshConsumableIcons y2
        const durSlotCount = this.gsm.player.activeConsumables?.length ?? 0;
        const cx = startX + (durSlotCount + i) * (iconSize + 5);
        ov.clear();
        if (frac > 0.02) {
          const ovH = Math.round((iconSize - 2) * frac);
          ov.fillStyle(0x000000, 0.35).fillRect(cx - (iconSize / 2 - 1), y2 - (iconSize / 2 - 1), iconSize - 2, ovH);
        }
      }
    }
  }

  /** Register or refresh a party-wide buff in the Heroes buffs display */
  private registerPartyBuff(id: string, name: string, icon: string, description: string, duration: number): void {
    const existing = this.partyBuffs.find((b) => b.id === id);
    if (existing) {
      existing.remainingMs = duration;
      existing.totalMs = Math.max(existing.totalMs, duration); // keep the max seen
    } else {
      this.partyBuffs.push({ id, name, icon, description, remainingMs: duration, totalMs: duration });
    }
    this.lastConsumableSnapshot = ''; // force icon refresh
  }

  /** Active announcement texts shown on screen */
  private activeAnnouncements: Phaser.GameObjects.Text[] = [];
  /** Queued announcements waiting for a slot to free up */
  private announcementQueue: string[] = [];

  private showAnnouncement(text: string): void {
    const BASE_Y = 68;   // just below zone name at y=48
    const LINE_H = 26;
    const MAX_VISIBLE = 3;

    if (this.activeAnnouncements.length >= MAX_VISIBLE) {
      this.announcementQueue.push(text);
      return;
    }

    const slotY = BASE_Y + this.activeAnnouncements.length * LINE_H;

    const t = this.add.text(480, slotY, text, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
      backgroundColor: '#0a0608ee', padding: { x: 12, y: 4 },
      stroke: '#5a3600', strokeThickness: 1,
    }).setOrigin(0.5, 0).setDepth(49); // depth 49 = below biome title (depth 50+)

    this.activeAnnouncements.push(t);

    this.tweens.add({
      targets: t, alpha: 0, delay: 2000, duration: 350,
      onComplete: () => {
        t.destroy();
        this.activeAnnouncements = this.activeAnnouncements.filter((a) => a !== t);
        // Re-position remaining active announcements
        this.activeAnnouncements.forEach((a, idx) => {
          a.setPosition(480, BASE_Y + idx * LINE_H);
        });
        // Dequeue next if any
        if (this.announcementQueue.length > 0) {
          const next = this.announcementQueue.shift()!;
          this.showAnnouncement(next);
        }
      },
    });
  }

  /**
   * Pre-wave telegraph banner. Larger and more dramatic than `showAnnouncement` —
   * scale-punches in from 0.6, holds for ~1.4 s, fades out. Boss variant uses
   * blood-red palette + a red glow rectangle behind the text so the player reads
   * "boss approaching" at a glance.
   *
   * Skips in event modes (telurionPending / hordePending / elitePackPending) —
   * those flows have their own bespoke announcements.
   */
  private showWaveTelegraph(text: string, isBoss: boolean): void {
    if (BattleScene.telurionPending || BattleScene.hordePending || BattleScene.elitePackPending) {
      return;
    }
    // Layout: dedicated dark panel behind the text + (boss only) a red halo
    // strip behind the panel. Previously the text had its own
    // `backgroundColor: '#0a0608cc'` which painted an opaque rectangle on
    // top of the red glow, creating visible dark gaps inside the red strip.
    // Decoupling the panel and the halo lets the boss colour read as a clean
    // outer glow with the dark panel cleanly contained inside.
    const cx = 480;
    const cy = 90;
    const color = isBoss ? '#ef4444' : '#c8973a';
    const strokeColor = isBoss ? '#5a0a0a' : '#3a2a14';
    const fontSize = isBoss ? '18px' : '15px';

    // Estimate panel size from text length — keeps the panel snug to the
    // glyph row without depending on a follow-up Text.getBounds() call.
    const charW = isBoss ? 11 : 9;
    const panelW = Math.max(180, Math.min(380, text.length * charW + 32));
    const panelH = isBoss ? 32 : 26;
    const haloW = panelW + 32;
    const haloH = panelH + 12;

    let halo: Phaser.GameObjects.Rectangle | undefined;
    if (isBoss) {
      halo = this.add.rectangle(cx, cy, haloW, haloH, 0xef4444, 0.18)
        .setDepth(47)
        .setStrokeStyle(1, 0xef4444, 0.4);
      this.tweens.add({
        targets: halo,
        alpha: { from: 0.18, to: 0.32 },
        duration: 600,
        yoyo: true,
        repeat: 1,
      });
    }

    const panel = this.add.rectangle(cx, cy, panelW, panelH, 0x0a0608, 0.85)
      .setDepth(48)
      .setStrokeStyle(1, isBoss ? 0x5a1010 : 0x3a2810, 0.9);

    const t = this.add.text(cx, cy, text, {
      fontSize, fontFamily: 'Georgia, serif', color,
      stroke: strokeColor, strokeThickness: 3,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(50);

    // Punch the whole stack in/out together. Targeting an array keeps the
    // panel + text + halo synced; tweening alpha on the boss halo is fine
    // because its yoyo above runs in parallel and settles before fade-out.
    const layers: Array<Phaser.GameObjects.Rectangle | Phaser.GameObjects.Text> =
      halo ? [halo, panel, t] : [panel, t];
    for (const layer of layers) layer.setAlpha(0);
    t.setScale(0.6);

    this.tweens.add({
      targets: t,
      scale: 1.0,
      duration: 220,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: layers,
      alpha: 1,
      duration: 220,
      onComplete: () => {
        this.tweens.add({
          targets: layers,
          alpha: 0,
          delay: 1400,
          duration: 280,
          onComplete: () => {
            for (const layer of layers) layer.destroy();
          },
        });
      },
    });
  }

  /**
   * Biome transition cinematic — fires once when the player enters a new biome
   * (different `themeKey` from the last rendered biome). Centered card with the
   * biome name + a one-line lore stinger. Plays a soft black-flash overlay so
   * the bg "fades in" on the cut. ~3 s total runtime; non-blocking — wave 1
   * starts on its normal timer underneath.
   *
   * Animation sequence (per user feedback that the previous version had
   * mis-aligned rules + felt static):
   *   0 ms     — title scales in from 0.7→1.0 with a fade, slight Y drop (-12 → 0)
   *   180 ms   — gold rules (above + below title) wipe outward from the title's
   *              centre to match the rendered title's width + 20 px padding,
   *              so the rules always align with the title regardless of biome
   *              name length (was hardcoded 380 px before — too wide for short
   *              names, mis-aligned visually).
   *   480 ms   — subtitle (lore stinger) fades in from below
   *   2000 ms  — everything fades out together
   */
  private playBiomeCinematic(themeKey: string, biomeName: string): void {
    const cx = GAME_W / 2; // 480 — explicit so it stays in sync with any future canvas-width change
    const cy = GAME_H / 2; // 288

    const LORE: Record<string, string> = {
      zone_forest:    'The trees lean in, watching.',
      zone_crypt:     'Bone dust on the wind. Something stirs below.',
      zone_wastes:    'War drums echo across the dunes.',
      zone_rift:      'The air burns. Reality thins.',
      zone_shadow:    'All light bends and dies here.',
      zone_labyrinth: 'Lava cracks the stone. Heat hammers the chest.',
    };
    const stinger = LORE[themeKey] ?? '';

    // Soft black flash that fades the bg in over the title card.
    const flash = this.add.rectangle(cx, cy, GAME_W, GAME_H, 0x000000, 0.55).setDepth(48);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 1100,
      onComplete: () => flash.destroy(),
    });

    // ── Title — scales in from 0.7 with a fade, settles on (cx, cy - 14).
    const title = this.add.text(cx, cy - 14, biomeName.toUpperCase(), {
      fontSize: '28px', fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif",
      color: '#c8973a', stroke: '#1a0e04', strokeThickness: 4, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(51).setAlpha(0).setScale(0.7);
    this.tweens.add({
      targets: title, alpha: 1, scale: 1.0,
      duration: 320, ease: 'Back.easeOut',
    });

    // ── Gold rules — drawn via Graphics with an animated `progress` data
    // value (0 → 1). The previous implementation tweened `Rectangle.width`
    // which Phaser resolves inconsistently and left "stuck" rules at the
    // canvas edge per user report. Graphics + clear+redraw on every tween
    // step paints a clean line that grows symmetrically from cx.
    // Width estimated from the biome-name length so we never depend on the
    // custom font having loaded — kills the "rules too wide" failure mode
    // that triggered when Cinzel hadn't initialised yet.
    const estCharW = 18; // Cinzel Decorative bold uppercase, font-size 28
    const ruleW = Math.max(180, Math.min(440, biomeName.length * estCharW + 32));
    const rulesGfx = this.add.graphics().setDepth(50).setAlpha(0);
    rulesGfx.setData('progress', 0);
    const redrawRules = (): void => {
      const p = (rulesGfx.getData('progress') as number) ?? 0;
      const halfW = (ruleW / 2) * p;
      rulesGfx.clear();
      rulesGfx.lineStyle(1, 0xc8973a, 0.9);
      rulesGfx.beginPath();
      rulesGfx.moveTo(cx - halfW, cy - 44); rulesGfx.lineTo(cx + halfW, cy - 44);
      rulesGfx.moveTo(cx - halfW, cy + 16); rulesGfx.lineTo(cx + halfW, cy + 16);
      rulesGfx.strokePath();
    };
    redrawRules();
    this.tweens.add({
      targets: rulesGfx, alpha: 1, duration: 200, delay: 180,
    });
    this.tweens.addCounter({
      from: 0, to: 1, duration: 360, delay: 180, ease: 'Quad.easeOut',
      onUpdate: (tween) => {
        rulesGfx.setData('progress', tween.getValue());
        redrawRules();
      },
    });

    // ── Subtitle — fades in from below the title after the rules settle.
    const sub = this.add.text(cx, cy + 24, stinger, {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#a07840',
      fontStyle: 'italic', stroke: '#0d0a0e', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(51).setAlpha(0).setY(cy + 30);
    this.tweens.add({
      targets: sub, alpha: 1, y: cy + 24,
      duration: 360, delay: 480, ease: 'Quad.easeOut',
    });

    // ── Fade everything out after the hold window.
    this.time.delayedCall(2000, () => {
      this.tweens.add({
        targets: [title, sub, rulesGfx],
        alpha: 0,
        duration: 500,
        onComplete: () => {
          title.destroy();
          sub.destroy();
          rulesGfx.destroy();
        },
      });
    });
  }

  // ==================== STORY QUEST BOSS GATING ====================

  /** Wrapper around `generateWaveEnemies` that applies story-quest boss gating: quest-locked
   *  bosses are stripped from the random pool, and when a story quest's active beat targets
   *  a specific boss in the current zone, that boss is force-spawned on the boss wave. */
  private generateWaveEnemiesForStory() {
    const { locked, forced } = this.computeStoryBossGating();
    return generateWaveEnemies(
      this.battleLevel,
      this.gsm.battle.waveNumber,
      this.infernoZoneOverride,
      locked,
      forced,
    );
  }

  /** Build an all-elite enemy list for an Elite Pack wave. Filters the current
   *  zone's enemy pool down to tier === 'elite' (falling back to minions if the
   *  zone has no elite defs), picks `count` at random with repetition allowed,
   *  and scales each via `scaleEnemy`. Each elite also gets a +10% HP / +10%
   *  attack / +10% move speed buff stamped AFTER scaling so the pack genuinely
   *  feels elite-tier, not just "more minions". Stay-count scaling is applied
   *  on top so an elite pack picked after several stays still matches the rest
   *  of the run's difficulty curve (without it, packs felt soft compared to
   *  normal waves at stay 2+). */
  private generateElitePackWaveEnemies(count: number): EnemyInstance[] {
    const zone = this.infernoZoneOverride ?? getZoneForLevel(this.battleLevel);
    // Event-only mobs must NOT enter the elite pack. They share the
    // 'elite' tier with the regular pool so the original filter happily
    // picked them up — and `rot_bomber` (atk 0, suicide on death) +
    // `skeleton_elite_gold` (Telurion lair sentinel) made the pack read
    // as trivially soft when a demon- or undead-faction zone rolled
    // them in. User report: "players were doing way too much damage to
    // elite pack". Exclude event-only ids from the eligible pool here
    // before the tier filter runs. Mirrors the EVENT_ONLY_IDS exclusion
    // already in waves.ts buildWave.
    const ELITE_PACK_EVENT_ONLY = new Set<string>(['rot_bomber', 'skeleton_elite_gold']);
    const eligible = BASE_ENEMIES.filter(
      (e) => zone.factions.includes(e.faction) && !ELITE_PACK_EVENT_ONLY.has(e.id),
    );
    const elitePool = eligible.filter((e) => e.tier === 'elite');
    const pool = elitePool.length > 0 ? elitePool : eligible.filter((e) => e.tier === 'minion');
    if (pool.length === 0) return [];
    const out: EnemyInstance[] = [];
    const BUFF = 1.10; // +10% overall applied once per enemy after tier scaling.
    const stayBonus = getStayBonuses(this.gsm.player.stayCount || 0);
    // Crossroads enemy modifiers stack onto elite-pack scaling too — otherwise
    // an elite pack picked while Riches/Iron Path is active would feel softer
    // than the normal waves on the same zone.
    const crossroadsModForPack = this.gsm.player.activeCrossroadsMod
      ? scaleCrossroadsModifier(
          CROSSROADS_MODIFIERS[this.gsm.player.activeCrossroadsMod],
          this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel,
        )
      : null;
    const crAtk   = crossroadsModForPack?.enemyAtkMult   ?? 1;
    const crSpeed = crossroadsModForPack?.enemySpeedMult ?? 1;
    for (let i = 0; i < count; i++) {
      const base = pool[Math.floor(Math.random() * pool.length)];
      const inst = scaleEnemy(base, this.battleLevel, false);
      inst.maxHp     = Math.max(1, Math.floor(inst.maxHp     * BUFF * stayBonus.enemyHpMult));
      inst.currentHp = inst.maxHp;
      inst.attack    = Math.max(1, Math.floor(inst.attack    * BUFF * stayBonus.enemyAtkMult * crAtk));
      inst.speed     = inst.speed * BUFF * stayBonus.enemySpeedMult * crSpeed;
      inst.attackSpeedMult = (inst.attackSpeedMult ?? 1) * stayBonus.enemyAtkSpeedMult;
      out.push(inst);
    }
    return out;
  }

  /** Grant the Elite Pack completion reward — guaranteed sigil drop(s) + bonus
   *  gold. Sigil count scales with elite count faced:
   *    14-19 → 1 sigil
   *    20-25 → 2 sigils
   *    26-28 → 3 sigils
   *  Sigils go to the run bag (not the account collection). Quest-gated
   *  specials respect `isSigilUnlocked` so early runs don't get story rewards. */
  private grantElitePackReward(count: number): void {
    // Stamp the cooldown so the random-event roller stops re-offering
    // Elite Pack on the next zone clear. Re-arms after
    // ELITE_PACK_COOLDOWN_STAGES zones (see the gating block in the zone-
    // cleared popup). User-reported "elite pack after each elite pack" bug.
    this.gsm.player.lastElitePackClearedAtStage = this.gsm.player.currentLevel;
    const sigilCount = count >= 26 ? 3 : count >= 20 ? 2 : 1;
    if (!this.gsm.player.sigilBag) this.gsm.player.sigilBag = [];
    const rolledSigils: { name: string; iconKey: string; description: string }[] = [];
    for (let i = 0; i < sigilCount; i++) {
      const sigil = generateSigil((sp) => this.gsm.isSigilUnlocked(sp));
      this.gsm.player.sigilBag.push(sigil);
      rolledSigils.push({
        name: sigil.special.name,
        iconKey: sigil.special.iconKey,
        description: sigil.special.description ?? 'Sigil',
      });
    }
    this.gsm.player.newSigilCount = (this.gsm.player.newSigilCount ?? 0) + sigilCount;
    // Bonus gold — user feedback: 270 for clearing a 27-elite pack with 2 bosses
    // was reading as low for a "hard event." Bumped to 20/elite + 150 flat per
    // boss kill so a max-size pack (28 elites + 2 bosses) lands ~860 gold —
    // meaningful reward for pushing the pack. Matches the "side-quest feel."
    const ELITE_PACK_BOSS_COUNT = 2;
    const bonusGold = count * 20 + ELITE_PACK_BOSS_COUNT * 150;
    this.gsm.player.resources.gold += bonusGold;
    this.runGold += bonusGold;
    this.vfx.playGoldShower(GAME_W / 2, GAME_H / 2 + 30);
    // Flying-coin deposit into the HUD gold counter — same animation as horde
    // so every side-event clear reads the same "your purse fills up" beat.
    const goldX = this.goldText.x + 10;
    const goldY = this.goldText.y;
    const pulse = (): void => {
      this.tweens.add({
        targets: this.goldText, scale: { from: 1, to: 1.25 }, duration: 80, yoyo: true, ease: 'Quad.easeOut',
      });
    };
    this.vfx.playGoldFlightTo(GAME_W / 2, GAME_H / 2 + 30, goldX, goldY, {
      count: 18, stagger: 50, onArrive: pulse,
    });
    this.sfx.play('levelup', { volume: 0.8 });
    // Reusable event-summary modal — matches the Horde clear layout with
    // crimson accent + up to 3 sigil reward cards.
    this.showEventSummary({
      title: 'ELITE PACK WIPED',
      accentColor: 0xd04040,
      stats: [
        { label: 'Elites defeated',  value: `${count}`,          color: '#6ab04a' },
        { label: 'Gold earned',      value: `+${bonusGold}`,     color: '#fbbf24' },
        { label: 'Sigils dropped',   value: `x${sigilCount}`,    color: '#c8973a' },
      ],
      rewards: rolledSigils.map((s) => ({
        iconKey: s.iconKey,
        name: s.name,
        rarity: 'legendary' as const,
        subtitle: s.description,
      })),
      onClose: () => this.showZoneClearedPopup(),
    });
  }

  /** Returns the set of boss def IDs that are locked (future story beats or quests still
   *  waiting on a prerequisite) and optionally a specific boss ID that must spawn (active
   *  story beat in the current zone). */
  private computeStoryBossGating(): { locked: Set<string>; forced?: string } {
    const locked = new Set<string>();
    let forced: string | undefined;
    // Strip the "(Tier N)" suffix so a Tier-2+ Shadow Realm / Labyrinth still matches
    // quest.zone. Without this, the Abyssal Demon / Magmarath wouldn't force-spawn on
    // subsequent cycles and the quest would stall even while the player is standing in
    // the correct biome.
    const rawZone = this.infernoMode
      ? getZoneForInferno(this.infernoZone, this.infernoEntryLevel).name
      : this.resolveActiveZone().name;
    const currentZone = rawZone.replace(/ \(Tier \d+\)$/, '');
    const quests = this.gsm.player.storyQuests ?? [];
    for (const q of quests) {
      if (q.claimed) continue;
      const unlocked = isStoryQuestUnlocked(q, quests);
      for (let i = 0; i < q.beats.length; i++) {
        const beat = q.beats[i];
        if (beat.trigger.kind !== 'kill_boss') continue;
        const bossId = beat.trigger.defId;
        // Wildcard kill_boss (no defId) is a "defeat any zone boss" beat — no
        // boss is locked or forced for it. Skip the gating logic entirely.
        if (!bossId) continue;
        // Locked while: prerequisite quest still incomplete, OR this beat is a future beat
        // we haven't reached yet.
        if (!unlocked || i > q.currentBeat) {
          locked.add(bossId);
        } else if (i === q.currentBeat && !beat.done && q.zone === currentZone) {
          // Active beat in the matching zone — guarantee the boss shows up.
          forced = bossId;
        }
      }
    }
    return { locked, forced };
  }

  // ==================== STORY QUEST LORE POPUPS ====================

  /** Serialized queue of lore popups — one on screen at a time so the player can read each.
   *  Feed a StoryQuestAdvanceResult in and the queue will surface:
   *    - started: "new beat" popup (blue-ish accent)
   *    - completed: "beat complete" (gold accent)
   *    - finished: "quest complete + reward" (big reward callout) */
  private storyLoreQueue: Array<{ title: string; body: string; kind: 'start' | 'done' | 'finish' }> = [];
  private storyLoreActive = false;

  private queueStoryLorePopups(result: import('@roe/shared').StoryQuestAdvanceResult): void {
    for (const { quest, beat } of result.started) {
      this.storyLoreQueue.push({
        title: `${quest.name} — ${beat.name}`,
        body: beat.loreText + `\n\n${beat.description}`,
        kind: 'start',
      });
    }
    for (const { quest, beat } of result.completed) {
      this.storyLoreQueue.push({
        title: `✦ ${beat.name} — complete`,
        body: `${quest.name}\nThe path opens further.`,
        kind: 'done',
      });
    }
    for (const quest of result.finished) {
      // Build a reward-specific body line so the popup tells the player exactly what
      // just unlocked — generic "a unique sigil waits in your sigil bag" missed tome
      // and relic-slot unlocks entirely.
      let rewardLine = 'You have earned a new reward.';
      if (quest.id === 'labyrinths_heart') {
        rewardLine = '✦ Unlocked: Magmarath\'s Ember sigil.';
      } else if (quest.id === 'archive_beneath') {
        rewardLine = '✦ Unlocked: Book of the Archivist tome.';
      } else if (quest.id === 'shadow_veil') {
        rewardLine = '✦ Unlocked: Relic Slot 3.';
      } else if (quest.id === 'voidelf_rite') {
        rewardLine = '✦ Unlocked: Relic Slot 4.';
      } else if (quest.id === 'vampirism_unlock') {
        rewardLine = '✦ Unlocked: Bloody Book (lifesteal).';
      } else if (quest.id === 'chaos_unlock') {
        rewardLine = '✦ Unlocked: Book of Chaos (random stat).';
      } else if (quest.id === 'bloodthirst_unlock') {
        rewardLine = '✦ Unlocked: Book of Bloodthirst (HP per kill).';
      }
      this.storyLoreQueue.push({
        title: `✦ ${quest.name} — Complete`,
        body: rewardLine,
        kind: 'finish',
      });
    }
    this.drainStoryLoreQueue();
  }

  private drainStoryLoreQueue(): void {
    if (this.storyLoreActive) return;
    const next = this.storyLoreQueue.shift();
    if (!next) return;
    this.storyLoreActive = true;
    this.showStoryLorePopup(next.title, next.body, next.kind, () => {
      this.storyLoreActive = false;
      this.drainStoryLoreQueue();
    });
  }

  /** Gold-bordered center popup that pauses briefly and dismisses on click/auto-timeout.
   *  Palette matches the shared HUD tooltip — dark-brown fill, gold outer, bronze text —
   *  so it feels native to the rest of the game. */
  private showStoryLorePopup(
    title: string, body: string, kind: 'start' | 'done' | 'finish',
    onClose: () => void,
  ): void {
    const W = 420;
    const PAD = 16;
    const titleColor = kind === 'finish' ? '#fbbf24' : kind === 'done' ? '#e0c080' : '#c8973a';
    const titleText = this.add.text(0, 0, title, {
      fontSize: '15px', fontFamily: 'Georgia, serif', color: titleColor, fontStyle: 'bold',
      wordWrap: { width: W - PAD * 2 }, align: 'center',
    }).setOrigin(0.5, 0).setResolution(2);
    const bodyText = this.add.text(0, 0, body, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
      wordWrap: { width: W - PAD * 2 }, align: 'center', lineSpacing: 4,
    }).setOrigin(0.5, 0).setResolution(2);
    const hint = this.add.text(0, 0, 'click to continue', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a5a30', fontStyle: 'italic',
    }).setOrigin(0.5, 0).setResolution(2);

    const innerH = titleText.height + 10 + bodyText.height + 14 + hint.height;
    const H = innerH + PAD * 2;
    const cx = 480;
    const cy = 300;

    const bg = this.add.rectangle(cx, cy, W, H, 0x0d0a08, 0.97)
      .setStrokeStyle(2, 0xc8973a, 1).setDepth(120);
    const inner = this.add.rectangle(cx, cy, W - 6, H - 6, 0x0d0a08, 0)
      .setStrokeStyle(1, 0x5a3600, 0.6).setDepth(121);

    const topY = cy - H / 2 + PAD;
    titleText.setPosition(cx, topY).setDepth(122);
    bodyText.setPosition(cx, topY + titleText.height + 10).setDepth(122);
    hint.setPosition(cx, topY + titleText.height + 10 + bodyText.height + 14).setDepth(122);

    // On the "finish" popup specifically, play a quick scale-in pop and a fading radial
    // gold glow so the unlock feels celebratory (not just another text box). Start, done
    // popups stay subtle so the finish beat stands out.
    const popupObjects: Phaser.GameObjects.GameObject[] = [bg, inner, titleText, bodyText, hint];
    let finishGlow: Phaser.GameObjects.Arc | undefined;
    if (kind === 'finish') {
      bg.setScale(0.7);
      inner.setScale(0.7);
      this.tweens.add({
        targets: [bg, inner], scale: { from: 0.7, to: 1 },
        duration: 260, ease: 'Back.easeOut',
      });
      finishGlow = this.add.circle(cx, cy, Math.max(W, H) * 0.7, 0xc8973a, 0.25).setDepth(119);
      popupObjects.push(finishGlow);
      this.tweens.add({
        targets: finishGlow, alpha: { from: 0.35, to: 0.1 },
        scale: { from: 0.6, to: 1.15 },
        duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
      // Sparkle burst around the popup — four ✦ glyphs drift outward and fade.
      for (let i = 0; i < 4; i++) {
        const ang = (i / 4) * Math.PI * 2;
        const sx = cx + Math.cos(ang) * (W / 2 - 20);
        const sy = cy + Math.sin(ang) * (H / 2 - 20);
        const spark = this.add.text(sx, sy, '✦', {
          fontSize: '16px', fontFamily: 'Georgia, serif', color: '#ffe8a0',
        }).setOrigin(0.5).setDepth(123).setAlpha(0);
        popupObjects.push(spark);
        this.tweens.add({
          targets: spark, alpha: { from: 0, to: 1 },
          scale: { from: 0.4, to: 1.2 },
          x: sx + Math.cos(ang) * 24,
          y: sy + Math.sin(ang) * 24,
          duration: 480, ease: 'Sine.easeOut',
          onComplete: () => {
            this.tweens.add({
              targets: spark, alpha: 0, duration: 700, ease: 'Sine.easeIn',
            });
          },
        });
      }
    }

    // Catch clicks anywhere on the popup body to dismiss.
    bg.setInteractive({ useHandCursor: true });

    let closed = false;
    const close = (): void => {
      if (closed) return;
      closed = true;
      for (const obj of popupObjects) obj.destroy();
      onClose();
    };
    bg.on('pointerdown', close);
    // Fail-safe auto-dismiss so a forgotten popup can't softlock the scene.
    this.time.delayedCall(12000, close);
  }

  // ==================== TROPHY HELPERS ====================

  /**
   * Accumulate all relic passive bonuses for a hero, scaling each by item level (+4% per level).
   * Multiple relics of the same name stack additively.
   */
  private getRelicBonuses(hero: Hero) {
    const b = {
      attackPct: 0,        // Warblade Sigil: +15% auto-attack damage
      spellPct: 0,         // Orb of Arcane Fury: +12% spell power
      critDmgBonus: 0,     // Emblem of Precision: +25% crit damage (additive)
      speedPct: 0,         // Idol of Swiftness: +8% attack speed
      fifthAtkStacks: 0,   // Talisman of the Fifth: stacks reduce trigger interval (1→every5, 2→every4, 3→every3, 4→every2)
      twinChance: 0,       // Charm of Twin Strikes: 10% chance to hit twice
      hasBloodlust: false, // Totem of Bloodlust: +20% damage for 5s on kill
      dmgReduction: 0,     // Aegis of Stoneskin: reduce incoming damage %
      guardianChance: 0,   // Ward of the Guardian: 15% chance to shield 40 HP on hit
      hpRegen: 0,          // Chalice of Renewal: % of max HP per second (multiple copies take MAX, not stack — additive % regen would push high-HP heroes to invulnerable)
      bulwarkPct: 0,       // Bulwark Emblem: block 15% when above 50% HP
      vampChance: 0,       // Sigil of Vampirism: 10% to heal 5% maxHP on hit
      goldPct: 0,          // Goldseer Idol: +20% gold
      xpPct: 0,            // Tome of Enlightenment: +15% XP
      lootPct: 0,          // Scavenger Charm +10%, Fortunekeeper Orb +10%
      manaCostReduce: 0,   // Mindstone Talisman: 10% less resource cost
      cdrPct: 0,           // Chronoshard: 8% CDR
      soulspark: 0,        // Soulspark Sigil: +5 resource on crit
      manafount: 0,        // Manafount Charm: +3 mana on auto-attack
      frostgraspSlowPct: 0,// Frostgrasp Totem: enemies move slower; 1% per relic level, capped at 50% at lv 50. Sums max across equipped copies.
      hasSlayer: false,    // Slayer Emblem: +25% atk speed for 5s after kill
      vanguardShield: 0,   // Vanguard Ward: 60 HP shield at wave start
      hasCrescendo: false, // Crescendo Idol: +1% dmg per wave cleared
      crescendoStacksTotal: 0,   // Sum of runtimeStacks across all equipped Crescendo Idols
      hasVitality: false,  // Vitality Idol: +5 maxHP per kill
      vitalityStacksTotal: 0,    // Sum of runtimeStacks across all equipped Vitality Idols
      hasArchonFocus: false, // Archon's Focus: +1% SP per kill stack (max 99)
      archonFocusStacksTotal: 0, // Sum of runtimeStacks across all equipped Archon's Focus relics
      spellbinderChance: 0,      // Spellbinder's Ring: 10% chance per equipped copy that a spell double-casts
      // Unique-tier relics
      hasResonantProng: false,    // Resonant Prong: crit deals 50% echo damage
      bloodtallyBonus: 0,         // Bloodtally Knife: accumulated +1% atk per kill (stored on hero)
      hasTimestopPendant: false,  // Timestop Pendant: prevent lethal once per 90s
      hasStormCapacitor: false,   // Storm Capacitor: 25% chain lightning on auto-hit
      hasOverflowCrystal: false,  // Overflow Crystal: +80% resource regen
      hasForgemasterIngot: false, // Forgemaster's Ingot: +40% weapon stat bonuses
      hasSpellfireLens: false,    // Spellfire Lens: spell dmg * (1 + SP/200)
      hasEchoCatalyst: false,     // Echo Catalyst: 25% spell echo for 50% SP damage
      hasRadiantSurge: false,     // Radiant Surge: +80% damage after heal for 3s
      hasManaFluxCrystal: false,  // Mana Flux Crystal: next ability +80% when resource full
    };
    for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
      const item = hero.equipment[slot];
      if (!item) continue;
      const s = 1 + (item.level - 1) * 0.04; // +4% per item level above 1
      switch (item.name) {
        case 'Warblade Sigil':        b.attackPct        += 0.15 * s; break;
        case 'Orb of Arcane Fury':    b.spellPct         += 0.12 * s; break;
        case 'Emblem of Precision':   b.critDmgBonus     += 0.25 * s; break;
        case 'Idol of Swiftness':     b.speedPct         += 0.08 * s; break;
        case 'Talisman of the Fifth': b.fifthAtkStacks++;              break;
        case 'Charm of Twin Strikes': b.twinChance       += 0.10 * s; break;
        case 'Totem of Bloodlust':    b.hasBloodlust      = true;     break;
        case 'Aegis of Stoneskin':    b.dmgReduction     += 0.08 * s; break;
        case 'Ward of the Guardian':  b.guardianChance   += 0.15 * s; break;
        case 'Chalice of Renewal': {
          // Scales 0.5% maxHp/s at lv 1 → 2% maxHp/s at lv 50.
          // Multiple copies take MAX, not stack additively.
          const lvlPct = Math.min(0.02, Math.max(0.005, 0.005 + (item.level - 1) * 0.0003));
          if (lvlPct > b.hpRegen) b.hpRegen = lvlPct;
          break;
        }
        case 'Bulwark Emblem':        b.bulwarkPct       += 0.15 * s; break;
        case 'Sigil of Vampirism':    b.vampChance       += 0.10 * s; break;
        case 'Goldseer Idol':         b.goldPct          += 0.20 * s; break;
        case 'Book of Enlightenment': b.xpPct            += 0.15 * s; break;
        case 'Scavenger Charm':       b.lootPct          += 0.10 * s; break;
        case 'Fortunekeeper Orb':     b.lootPct          += 0.10 * s; break;
        case 'Mindstone Talisman':    b.manaCostReduce   += 0.10 * s; break;
        case 'Chronoshard':           b.cdrPct           += 0.08 * s; break;
        case 'Soulspark Sigil':       b.soulspark        += 5 * s;   break;
        case 'Manafount Charm':       b.manafount        += 3 * s;   break;
        case 'Frostgrasp Totem': {
          // 10 % floor at level 1, +1 % per level beyond 10, capped at 50 % at lv 50.
          // If the player has multiple Frostgrasp copies equipped, take the
          // strongest — they don't stack additively (the slow auras would
          // multiply weirdly otherwise).
          const lvlSlow = Math.min(0.50, Math.max(0.10, item.level * 0.01));
          if (lvlSlow > b.frostgraspSlowPct) b.frostgraspSlowPct = lvlSlow;
          break;
        }
        case 'Slayer Emblem':         b.hasSlayer         = true;     break;
        case 'Vanguard Ward':         b.vanguardShield   += 0.12 * s; break;
        case 'Crescendo Idol':        b.hasCrescendo      = true;
                                      b.crescendoStacksTotal += item.runtimeStacks ?? 0; break;
        case 'Vitality Idol':         b.hasVitality       = true;
                                      b.vitalityStacksTotal += item.runtimeStacks ?? 0; break;
        case "Archon's Focus":        b.hasArchonFocus    = true;
                                      b.archonFocusStacksTotal += item.runtimeStacks ?? 0; break;
        case "Spellbinder's Ring":    b.spellbinderChance += 0.10 * s; break;
        // Unique-tier
        case 'Resonant Prong':        b.hasResonantProng    = true;   break;
        case 'Bloodtally Knife':      b.bloodtallyBonus    += (item.runtimeStacks ?? 0) / 100; break;
        case 'Timestop Pendant':      b.hasTimestopPendant  = true;   break;
        case 'Storm Capacitor':       b.hasStormCapacitor   = true;   break;
        case 'Overflow Crystal':      b.hasOverflowCrystal  = true;   break;
        case "Forgemaster's Ingot":   b.hasForgemasterIngot = true;   break;
        case 'Spellfire Lens':        b.hasSpellfireLens    = true;   break;
        case 'Echo Catalyst':         b.hasEchoCatalyst     = true;   break;
        case 'Radiant Surge':         b.hasRadiantSurge     = true;   break;
        case 'Mana Flux Crystal':     b.hasManaFluxCrystal  = true;   break;
      }
    }
    return b;
  }

  /** True if the given hero has a relic with the specified name equipped in any relic slot. */
  private hasRelic(hero: Hero, name: string): boolean {
    return (['relic1', 'relic2', 'relic3', 'relic4'] as const).some((s) => hero.equipment[s]?.name === name);
  }

  /** Apply consumable stat boosts to alive deployed heroes only (call on activation). */
  private applyConsumableStatBuff(id: string): void {
    if (id === 'elixir_fortitude') {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.isDead) continue;
        // +50% of PURE maxHp — never of a vitality/idol-inflated pool. Without this the
        // elixir would give +50% of an already-buffed HP total and then vitality idol
        // would recompute its own bonus off the elixir-inflated pool on the next frame,
        // producing a runaway HP spiral (reported as 600k+ HP at lvl 49).
        const pureMaxHp = this.getPureMaxHp(hs.heroRef);
        const boost = Math.floor(pureMaxHp * 0.5);
        (hs.heroRef as any)._fortitudeBoost = ((hs.heroRef as any)._fortitudeBoost || 0) + boost;
        hs.heroRef.stats.maxHp += boost;
        hs.heroRef.stats.hp = Math.min(hs.heroRef.stats.hp + boost, hs.heroRef.stats.maxHp);
      }
    } else if (id === 'potion_vitality') {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.isDead) continue;
        const heal = Math.floor(hs.heroRef.stats.maxHp * 0.4);
        hs.heroRef.stats.hp = Math.min(hs.heroRef.stats.hp + heal, hs.heroRef.stats.maxHp);
        this.showDamageNumber(hs.x, hs.y - 60, `+${heal} HP`, '#4ade80', '18px');
      }
    } else if (id === 'potion_mana') {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.isDead) continue;
        // Restores 40% of the appropriate resource: mana for casters, rage for warriors, energy for assassins
        const h = hs.heroRef;
        if (h.heroClass === 'warrior') {
          const gain = Math.floor(h.stats.maxRage * 0.4);
          h.stats.rage = Math.min(h.stats.rage + gain, h.stats.maxRage);
          this.showDamageNumber(hs.x, hs.y - 60, `+${gain} Rage`, '#ef4444', '18px');
        } else if (h.heroClass === 'assassin') {
          const gain = Math.floor(h.stats.maxEnergy * 0.4);
          h.stats.energy = Math.min(h.stats.energy + gain, h.stats.maxEnergy);
          this.showDamageNumber(hs.x, hs.y - 60, `+${gain} Energy`, '#eab308', '18px');
        } else {
          const gain = Math.floor(h.stats.maxMp * 0.4);
          h.stats.mp = Math.min(h.stats.mp + gain, h.stats.maxMp);
          this.showDamageNumber(hs.x, hs.y - 60, `+${gain} Mana`, '#3b82f6', '18px');
        }
      }
    } else if (id === 'elixir_resistance') {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.isDead) continue;
        (hs.heroRef as any)._resistanceBuff = ((hs.heroRef as any)._resistanceBuff || 0) + 20;
      }
    }
  }

  /** Revert consumable stat boosts from alive deployed heroes (call on expiry). */
  private removeConsumableStatBuff(id: string): void {
    if (id === 'elixir_fortitude') {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.isDead) continue;
        const boost = (hs.heroRef as any)._fortitudeBoost || 0;
        if (boost > 0) {
          hs.heroRef.stats.maxHp = Math.max(1, hs.heroRef.stats.maxHp - boost);
          hs.heroRef.stats.hp = Math.min(hs.heroRef.stats.hp, hs.heroRef.stats.maxHp);
          (hs.heroRef as any)._fortitudeBoost = 0;
        }
      }
    } else if (id === 'elixir_resistance') {
      for (const hs of this.heroSprites) {
        if (hs.heroRef.isDead) continue;
        (hs.heroRef as any)._resistanceBuff = Math.max(0, ((hs.heroRef as any)._resistanceBuff || 0) - 20);
      }
    }
  }

  /** Flat +% all-resist from Arcane Ward Stone trophy + sigil allResist affix.
   *  School-agnostic — sigil per-school resists (fireResist etc.) are not yet piped through
   *  the individual damage sites, but allResist reaches everything. */
  private getTrophyAllResist(): number {
    const trophy = this.hasTrophy('all resist') ? 5 : 0;
    const sigilAll = this.getSigilEffects().allResist;
    return trophy + sigilAll;
  }

  /** Magmarath's Ember — applies (or refreshes) an 8 s burn DoT on the target whenever a
   *  crit lands. Total damage is 3 % of the target's current max HP spread across the
   *  full 8 s (was 3 % per second × 3 s = 9 % total — "kinda busted" per user feedback;
   *  same shape as Crimson Wound now: total = 3 %, duration = 8 s, per-second = 0.375 %).
   *  The existing DoT pipeline accumulates `value × (delta/1000)` per frame, so we store
   *  `maxHp * 0.00375` as the per-second value. Re-applying refreshes duration and picks
   *  up any intervening max-HP changes. */
  private maybeApplyEmberBurn(target: EnemySprite, sourceHero?: HeroSprite): void {
    if (!target.active || target.enemyRef.currentHp <= 0) return;
    if (!this.getSigilEffects().specials.has('magmaraths_ember')) return;
    const burnPerSec = Math.max(1, Math.round(target.enemyRef.maxHp * 0.00375));
    applyDebuff(target.enemyRef, {
      type: 'dot', value: burnPerSec, duration: 8000, remaining: 8000,
      sourceHeroId: sourceHero?.heroRef.id, sourceLabel: 'magmarath_ember',
    });
    // No "BURN" proc text — the standard DoT pipeline shows the per-second
    // damage as a downward-drifting number (e.g. "232") instead, which reads
    // both as a proc indicator and the actual chip damage in one floater.
  }

  /** Stormcoil Charm — Telurion's Lair drop. On crit, lightning chains to one
   *  nearby enemy (within 180 px) for 60 % of the crit damage. Pure on-hit
   *  effect; no debuff applied. Mirrors maybeApplyEmberBurn shape so the
   *  caller doesn't have to know which sigil is equipped. */
  private maybeApplyStormcoilChain(source: EnemySprite, critDamage: number, attacker: HeroSprite): void {
    if (!this.getSigilEffects().specials.has('stormcoil_charm')) return;
    if (critDamage <= 0) return;
    const range = 180;
    const candidates = this.enemySprites.filter((e) =>
      e !== source && e.active && e.enemyRef.currentHp > 0
      && Phaser.Math.Distance.Between(source.x, source.y, e.x, e.y) <= range,
    );
    if (candidates.length === 0) return;
    const next = candidates[Math.floor(Math.random() * candidates.length)];
    const chainDmg = Math.max(1, Math.round(critDamage * 0.60));
    next.enemyRef.currentHp -= chainDmg;
    this.showDamageNumber(next.x, next.y - 26, `⚡ ${chainDmg}`, '#80c8ff', '13px');
    this.flashSprite(next);
    // Light VFX — bolt drop on the chained target.
    this.vfx.playLightningBolt(next.x, next.y - 20);
    if (next.enemyRef.currentHp <= 0) this.killEnemy(next, attacker);
  }

  /** Compute effective hero stats (base + equipment) with sigil affix bumps folded in.
   *  Wraps the shared getEffectiveStats and layers on the 3 party-wide stat affixes that
   *  would otherwise be aggregated-but-unread: hpPct → maxHp, maxMpPct → maxMp, defPct → defense.
   *  All other reads (attack, crit, speed, resists) already pipe through their own sigil hooks. */
  private effStats(hero: Hero): ReturnType<typeof getEffectiveStats> {
    const base = getEffectiveStats(hero);
    const eff = this.getSigilEffects();
    // v2 talent tree (WARD branch): flat HP / max-HP% / flat defense layered on top
    // of base stats so every stat consumer sees them.
    const pb = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
    if (pb.hpFlat > 0)     base.maxHp   = base.maxHp + pb.hpFlat;
    if (pb.hpPctBonus > 0) base.maxHp   = Math.floor(base.maxHp * (1 + pb.hpPctBonus));
    if (pb.defFlat > 0)    base.defense = base.defense + pb.defFlat;
    if (eff.hpPct > 0)    base.maxHp   = Math.floor(base.maxHp   * (1 + eff.hpPct    / 100));
    if (eff.maxMpPct > 0) base.maxMp   = Math.floor(base.maxMp   * (1 + eff.maxMpPct / 100));
    if (eff.defPct > 0)   base.defense = Math.floor(base.defense * (1 + eff.defPct   / 100));
    // Valka — Warmaiden's Grit: +15 % max HP while equipped.
    if (this.hasArmoryPassive(hero, 'passive_valka_warmaidens_grit')) {
      base.maxHp = Math.floor(base.maxHp * 1.15);
    }
    // Crossroads modifier — Iron Path / Cursed Riches scale every hero's max HP
    // for the duration of one zone. Read-once-per-effStats since the modifier
    // is a single static value cleared on zone advance.
    {
      const modId = this.gsm.player.activeCrossroadsMod;
      if (modId && CROSSROADS_MODIFIERS[modId]?.hpMult) {
        const scaled = scaleCrossroadsModifier(
          CROSSROADS_MODIFIERS[modId],
          this.gsm.player.crossroadsModAppliedAtStage ?? this.gsm.player.currentLevel,
        );
        const mult = scaled.hpMult!;
        base.maxHp = Math.max(1, Math.floor(base.maxHp * mult));
      }
    }
    // Valka — Rally Standard: any ally inside Valka's banner ring picks up
    // +30 % crit damage while the `_rallyCritDmgBuff` flag is set (flag
    // is stamped by the banner's 500 ms tick, cleared when banner expires).
    const rallyBuff = (hero as any)._rallyCritDmgBuff as number | undefined;
    if (rallyBuff && rallyBuff > 0) {
      base.critDamage = (base.critDamage ?? 1) + rallyBuff;
    }
    // Valka — Last Rally: transient +30 defence window (10 s after trigger).
    const lastRallyDef = (hero as any)._lastRallyDefBonus as number | undefined;
    if (lastRallyDef && lastRallyDef > 0) {
      base.defense += lastRallyDef;
    }
    // Book-ban malus REMOVED 2026-04-24. Bans are now a paid choice (5 runes
    // each, max 3 — see BookBanScene) instead of a free stat trade-off. The
    // HUD debuff icon still shows the banned books for reference but no longer
    // reports a damage/HP penalty in its tooltip.
    // Shrine of Boons — apply the aggregated stat modifiers. Handles both
    // positive blessings (+HP, +DEF, +crit dmg) and negative curses (−HP,
    // −DEF, −auto-attack dmg, −atk speed) via Math.floor on the multiplier.
    // No-op when no boons active (hasAny is false, skip the branch).
    const boonEff = this.getBoonEffects();
    if (boonEff.hasAny) {
      if (boonEff.maxHpPct     !== 0) base.maxHp        = Math.max(1, Math.floor(base.maxHp   * (1 + boonEff.maxHpPct)));
      if (boonEff.maxMpPct     !== 0) base.maxMp        = Math.max(0, Math.floor(base.maxMp   * (1 + boonEff.maxMpPct)));
      if (boonEff.defPct       !== 0) base.defense      = Math.max(0, Math.floor(base.defense * (1 + boonEff.defPct)));
      if (boonEff.critDamagePct !== 0) base.critDamage  = Math.max(1.0, (base.critDamage ?? 1) + boonEff.critDamagePct);
      if (boonEff.attackSpeedPct !== 0) base.speed      = Math.max(0.1, base.speed * (1 + boonEff.attackSpeedPct));
    }
    return base;
  }

  /** Roll a per-CC-type resist chance from equipped sigils.
   *  Returns true if the CC should be blocked this roll. 'root' maps to stunResist
   *  (frost root is a movement-stun) so the sigil line has a real combat source. */
  private rollCcResist(kind: 'stun' | 'petrify' | 'fear' | 'root'): boolean {
    const eff = this.getSigilEffects();
    const sigilPct = kind === 'petrify' ? eff.petrifyResist
      : kind === 'fear' ? eff.fearResist
      : /* stun | root */ eff.stunResist;
    // v2 talent: Resolute (WARD T3) — flat % CC resist on every kind.
    const treePct = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).ccResistPct * 100;
    const totalPct = Math.min(75, sigilPct + treePct);
    if (totalPct <= 0) return false;
    return Math.random() * 100 < totalPct;
  }

  /** Per-school resist from sigils + Book of the Archivist tome. The allResist affix still
   *  flows through getTrophyAllResist separately. Combined here so damage call sites don't
   *  need to mix in a second helper. */
  private getSigilResist(kind: 'fire' | 'frost' | 'shadow' | 'nature'): number {
    const eff = this.getSigilEffects();
    const archivist = getArchivistResistBonus(this.gsm.player.tomes)[kind];
    const sigilAmt = kind === 'fire' ? eff.fireResist
      : kind === 'frost' ? eff.frostResist
      : kind === 'shadow' ? eff.shadowResist
      : eff.natureResist;
    return sigilAmt + archivist;
  }

  /** Effective per-school resist (uncapped) for a hero, summing every source combat honours:
   *  equipment statBonuses (Dragonheart/Frostborn/Shadewarden/Venomthorn trophies + epic+ armor
   *  resist rolls — via getEffectiveStats), _arcaneMightResistBuff (already folded into
   *  getEffectiveStats), Resistance Elixir (_resistanceBuff), party-wide Arcane Ward Stone +
   *  sigil allResist (getTrophyAllResist), and per-school sigil affix + Archivist tome
   *  (getSigilResist). Callers still cap with Math.min for their element. */
  private getHeroResist(hero: Hero, kind: 'fire' | 'frost' | 'shadow' | 'nature'): number {
    const eff = getEffectiveStats(hero);
    const base = kind === 'fire' ? (eff.fireResist ?? 0)
      : kind === 'frost' ? (eff.frostResist ?? 0)
      : kind === 'shadow' ? (eff.shadowResist ?? 0)
      : (eff.natureResist ?? 0);
    const resistBuff = (hero as any)._resistanceBuff || 0;
    // Shrine of Boons — per-school ±% resist. Blessings of fire/frost/nature/shadow
    // grant +20% to their school and −30% to the opposing one. Bonuses can
    // stack to negative totals on multiple curse-side trades — min-clamped to
    // -75 so the player isn't completely exposed to DoT chip damage.
    const boonEff = this.getBoonEffects();
    const boonBonus = kind === 'fire' ? boonEff.fireResistBonus
      : kind === 'frost' ? boonEff.frostResistBonus
      : kind === 'shadow' ? boonEff.shadowResistBonus
      : boonEff.natureResistBonus;
    const total = base + resistBuff + this.getTrophyAllResist() + this.getSigilResist(kind) + boonBonus;
    return Math.max(-75, total);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Sigil effects aggregator — reads player.sigilsEquipped + bag and returns the
  // summed affix totals and special-id flags. Cached per-frame via _sigilEffectsCache.
  // ══════════════════════════════════════════════════════════════════════════
  private _sigilEffectsCache: {
    // Summed affix totals (percentages are integers 0–100)
    atkPct: number; spellPowerPct: number; critChance: number; critDmgPct: number;
    attackSpeedPct: number; defPct: number; hpPct: number; maxMpPct: number;
    mpRegenPct: number;
    fireResist: number; frostResist: number; shadowResist: number; natureResist: number; allResist: number;
    stunResist: number; petrifyResist: number; fearResist: number;
    goldPct: number; lootPct: number; xpPct: number;
    ignoreArmorPct: number;
    vsFactionPct: Record<string, number>;
    vsBossPct: number;
    // Special-effect flags (set true if any equipped sigil has that id)
    specials: Set<string>;
  } | null = null;

  /** Cached per-frame view of the aggregated active-boon effects. The cache
   *  is cleared by invalidateSigilCache (reused naming pattern). Read via
   *  getBoonEffects() and check `.hasAny` before branching on specific fields
   *  so the zero path stays cheap when no boons are active. */
  private _boonEffectsCache: ActiveBoonEffects | null = null;

  private getBoonEffects(): ActiveBoonEffects {
    if (this._boonEffectsCache) return this._boonEffectsCache;
    this._boonEffectsCache = aggregateBoonEffects(this.gsm.player.activeBoons);
    return this._boonEffectsCache;
  }

  private getSigilEffects(): NonNullable<BattleScene['_sigilEffectsCache']> {
    if (this._sigilEffectsCache) return this._sigilEffectsCache;
    const agg = {
      atkPct: 0, spellPowerPct: 0, critChance: 0, critDmgPct: 0,
      attackSpeedPct: 0, defPct: 0, hpPct: 0, maxMpPct: 0,
      mpRegenPct: 0,
      fireResist: 0, frostResist: 0, shadowResist: 0, natureResist: 0, allResist: 0,
      stunResist: 0, petrifyResist: 0, fearResist: 0,
      goldPct: 0, lootPct: 0, xpPct: 0,
      ignoreArmorPct: 0,
      vsFactionPct: {} as Record<string, number>,
      vsBossPct: 0,
      specials: new Set<string>(),
    };
    const bag = this.gsm.player.sigilBag ?? [];
    const collection = this.gsm.account.sigilCollection ?? [];
    const equipped = this.gsm.player.sigilsEquipped ?? [];
    for (const id of equipped) {
      const s = bag.find((b) => b.id === id) ?? collection.find((b) => b.id === id);
      if (!s) continue;
      agg.specials.add(s.special.id);
      for (const a of s.affixes) {
        switch (a.key) {
          case 'atkPct':          agg.atkPct += a.value; break;
          case 'spellPowerPct':   agg.spellPowerPct += a.value; break;
          case 'critChance':      agg.critChance += a.value; break;
          case 'critDmgPct':      agg.critDmgPct += a.value; break;
          case 'attackSpeedPct':  agg.attackSpeedPct += a.value; break;
          case 'defPct':          agg.defPct += a.value; break;
          case 'hpPct':           agg.hpPct += a.value; break;
          case 'maxMpPct':        agg.maxMpPct += a.value; break;
          case 'fireResist':      agg.fireResist += a.value; break;
          case 'frostResist':     agg.frostResist += a.value; break;
          case 'shadowResist':    agg.shadowResist += a.value; break;
          case 'natureResist':    agg.natureResist += a.value; break;
          case 'allResist':       agg.allResist += a.value; break;
          case 'stunResist':      agg.stunResist += a.value; break;
          case 'petrifyResist':   agg.petrifyResist += a.value; break;
          case 'fearResist':      agg.fearResist += a.value; break;
          case 'mpRegenPct':      agg.mpRegenPct += a.value; break;
          case 'goldPct':         agg.goldPct += a.value; break;
          case 'lootPct':         agg.lootPct += a.value; break;
          case 'xpPct':           agg.xpPct += a.value; break;
          case 'ignoreArmorPct':  agg.ignoreArmorPct += a.value; break;
          case 'vsBossPct':       agg.vsBossPct += a.value; break;
          case 'vsBeastPct':      agg.vsFactionPct.beast    = (agg.vsFactionPct.beast    ?? 0) + a.value; break;
          case 'vsDemonPct':      agg.vsFactionPct.demon    = (agg.vsFactionPct.demon    ?? 0) + a.value; break;
          case 'vsUndeadPct':     agg.vsFactionPct.undead   = (agg.vsFactionPct.undead   ?? 0) + a.value; break;
          case 'vsOrcPct':        agg.vsFactionPct.orc      = (agg.vsFactionPct.orc      ?? 0) + a.value; break;
          case 'vsDragonPct':     agg.vsFactionPct.dragon   = (agg.vsFactionPct.dragon   ?? 0) + a.value; break;
          case 'vsMinotaurPct':   agg.vsFactionPct.minotaur = (agg.vsFactionPct.minotaur ?? 0) + a.value; break;
          case 'vsVoidPct':       agg.vsFactionPct.void     = (agg.vsFactionPct.void     ?? 0) + a.value; break;
        }
      }
    }
    this._sigilEffectsCache = agg;
    return agg;
  }

  /** Drop any cached sigil totals — called when the player equips/unequips or buys a sigil. */
  private invalidateSigilCache(): void { this._sigilEffectsCache = null; }
  /** Boon cache invalidation — call whenever player.activeBoons mutates. */
  private invalidateBoonCache(): void { this._boonEffectsCache = null; }

  /** Returns the main-hero sprite — heroes[0] is the first pick from HeroSelect and carries the
   *  "MAIN HERO" tag in the Heroes panel. Null if that hero isn't spawned or is dead. Affix stats
   *  (%ATK/%HP/etc.) are shared across the party via the aggregator; combat-behavior specials
   *  are gated on this hero only. */
  private getMainHeroSprite(): HeroSprite | null {
    const heroRef = this.gsm.player.heroes[0];
    if (!heroRef || heroRef.isDead) return null;
    const hs = this.heroSprites.find((h) => h.heroRef === heroRef);
    return hs && !hs.heroRef.isDead ? hs : null;
  }

  /** True if the given sprite is the main (first-deployed) hero. */
  private isMainHero(hs: HeroSprite): boolean {
    return this.getMainHeroSprite() === hs;
  }

  /**
   * Bramble Totem sigil: copy a stun/fear debuff from the source enemy to any
   * adjacent enemies within ~60px. No-op when the sigil isn't equipped.
   */
  private maybeBrambleSpread(sourceEs: EnemySprite, debuff: Parameters<typeof applyDebuff>[1]): void {
    if (!this.getSigilEffects().specials.has('bramble_totem')) return;
    // Combat-behavior sigil — only active while the main (first-deployed) hero is alive.
    if (!this.getMainHeroSprite()) return;
    if (debuff.type !== 'stun' && debuff.type !== 'fear' && debuff.type !== 'root') return;
    const RADIUS_SQ = 60 * 60;
    for (const es of this.enemySprites) {
      if (!es.active || es.enemyRef.currentHp <= 0) continue;
      if (es === sourceEs) continue;
      const dx = es.x - sourceEs.x;
      const dy = es.y - sourceEs.y;
      if (dx * dx + dy * dy > RADIUS_SQ) continue;
      applyDebuff(es.enemyRef, { ...debuff });
      this.showDamageNumber(es.x, es.y - 30, '🌿', '#86efac', '12px');
    }
  }

  /** Extra faction damage multiplier vs a specific enemy faction + boss flag. */
  private getSigilFactionMult(es: EnemySprite): number {
    const eff = this.getSigilEffects();
    const factionPct = eff.vsFactionPct[es.enemyRef.faction] ?? 0;
    const bossPct = es.enemyRef.tier === 'boss' ? eff.vsBossPct : 0;
    return 1 + (factionPct + bossPct) / 100;
  }

  /** True if any alive deployed hero has a trophy whose specialEffect contains the given substring. */
  private hasTrophy(substr: string): boolean {
    return this.gsm.player.deployedHeroes.some((idx) => {
      const h = this.gsm.player.heroes[idx];
      if (!h?.equipment.reserved?.specialEffect?.includes(substr)) return false;
      const hs = this.heroSprites.find((s) => s.heroRef === h);
      return hs ? !hs.heroRef.isDead : false;
    });
  }

  /**
   * Apply Divine Reflect and Book of Thorns back to an enemy after it deals spell damage.
   * Call this after every boss spell hit (same as the melee reflect block).
   */

  /** Hero Armory — does this hero have `passiveId` active this run? Reads the
   *  run-snapshot `activePassiveIds` populated by `gsm.snapshotActiveArmoryPassives()`
   *  at run start, so mid-run respecs don't apply. */
  private hasArmoryPassive(hero: Hero, passiveId: string): boolean {
    return !!hero.activePassiveIds?.includes(passiveId);
  }

  /**
   * Apply damage to a hero — shield absorbs first, then HP.
   * Returns actual HP damage dealt (0 if fully absorbed by shield).
   */
  /** Hard ceiling on total combined dodge chance. Without this, a Sylvari with
   *  Forest's Favor (25 %) + Wild Step (7 %) + Evasion tome at rank 99 (50 %) +
   *  Kael's Evasion ability (60 %) would chain-roll to ~86 % dodge, effectively
   *  invincible for the 4 s ability window. Clamping the combined probability
   *  keeps heroes takeable while still rewarding a stacked dodge build. */
  private static readonly MAX_DODGE_CHANCE = 0.80;

  /** Compute the combined dodge chance for a hero against an incoming hit,
   *  folding every source into one probability and clamping at MAX_DODGE_CHANCE.
   *  `isMelee` gates Kael's Evasion ability (melee-only by design) — ranged /
   *  projectile / DoT-routable hits pass `false`. Thorn of Reckoning sigil (main
   *  hero only) zeros dodge out entirely as a kill-switch. */
  private computeDodgeChance(hs: HeroSprite, isMelee: boolean): number {
    if (this.gsm.godMode) return 0;
    const thornOfReckoning = this.getSigilEffects().specials.has('thorn_of_reckoning')
      && this.isMainHero(hs);
    if (thornOfReckoning) return 0;
    const h = hs.heroRef as any;
    const now = Date.now();
    // Miss-probability product — combining independent sources this way keeps the
    // chain-roll math honest while letting us cap the final number.
    let pMiss = 1;
    // Kael Evasion ability — melee-only, 60 % for 4 s.
    if (isMelee && h._evasionActive) pMiss *= 1 - 0.6;
    // Forest's Favor — flat 25 % dodge.
    if (this.hasArmoryPassive(hs.heroRef, 'passive_sylvari_forests_favor')) {
      pMiss *= 1 - 0.25;
    }
    // Wild Step — 7 % dodge while the post-move buff is active.
    if (this.hasArmoryPassive(hs.heroRef, 'passive_sylvari_wild_step')) {
      const wildStepUntil = (h._wildStepBuffUntil as number) ?? 0;
      if (now < wildStepUntil) pMiss *= 1 - 0.07;
    }
    // Evasion Tome — passive dodge % (self-capped 50 %).
    const evasionTomeChance = getTomeBonus(this.gsm.player.tomes, 'evasion');
    if (evasionTomeChance > 0) pMiss *= 1 - Math.min(evasionTomeChance, 0.50);
    // v2 talent: Evasion (WARD T3) — flat dodge stack.
    const treeDodge = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).dodgePct;
    if (treeDodge > 0) pMiss *= 1 - Math.min(treeDodge, 0.40);
    return Math.min(1 - pMiss, BattleScene.MAX_DODGE_CHANCE);
  }

  /** Single-roll dodge gate. Returns true and surfaces the "DODGE" popup if the
   *  hero successfully avoids the hit. `isMelee=false` is the ranged / projectile
   *  caller (ranged auto-attacks); the melee path uses `isMelee=true`. */
  private rollGeneralDodge(hs: HeroSprite, isMelee = false): boolean {
    const p = this.computeDodgeChance(hs, isMelee);
    if (p <= 0 || Math.random() >= p) return false;
    this.showDamageNumber(hs.x, hs.y - 20, 'DODGE', '#eab308');
    return true;
  }

  private applyDamageToHero(hs: HeroSprite, dmg: number, attacker?: EnemySprite, isAoe = false): number {
    const h = hs.heroRef as any;
    const now = Date.now();
    // Kael Dirty Blades invulnerability — while the assassin is mid-
    // teleport (out → strike → in), every damage source is ignored. Per
    // user "make Kael invincible while using it" — the ability used to
    // leave Kael exposed at his old spot during the 750 ms teleport
    // window, which made it self-destructive against waves with AoE
    // pressure. The flag flips false in the existing teleport-in
    // callbacks so the immunity automatically ends with the animation.
    if ((h as any)._wraithStriking) {
      return 0;
    }
    // v2 talent: Eternal Hearth — flat % damage reduction against AOE attacks.
    if (isAoe && dmg > 0) {
      const aoeDr = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).aoeDamageReductionPct;
      if (aoeDr > 0) dmg = Math.max(1, Math.round(dmg * (1 - Math.min(0.5, aoeDr))));
    }

    // ── Pre-damage Elite Perks ─────────────────────────────────────────────

    // Dodge is deliberately NOT called here — this path handles boss specials,
    // DoT ticks, magic projectiles (hellfire / frost nova / void lance / stone
    // gaze / burn ticks / etc.). Per the design rule: dodge applies only to
    // "normal" attacks (melee auto-attacks + basic ranged arrows) which route
    // through the enemyAttackHero path (see updateEnemies line ~7780). Boss
    // specials and DoTs bypass dodge entirely.
    // Plate armor (heavy sub-type) — Block: chance to halve a melee hit. Only
    // fires when an enemy attacker is present and is melee (skipped on DoT
    // ticks, environmental damage, ranged hits). blockPct is a 0-1 chance
    // accumulated across equipped Plate pieces.
    if (dmg > 0 && attacker && attacker.enemyRef.attackRange !== 'ranged') {
      const blockPct = this.effStats(hs.heroRef).blockPct ?? 0;
      if (blockPct > 0 && Math.random() < blockPct) {
        const blocked = Math.max(1, Math.round(dmg * 0.5));
        this.showDamageNumber(hs.x, hs.y - 30, 'BLOCK', '#a8d8ff', '11px');
        dmg = blocked;
      }
    }
    // Zyphara Magma Shield — retaliatory stun. While the shield is up, any
    // enemy that lands a hit gets stunned for 0.5 s (+0.1 s per talent rank).
    // Stun-resist / CC-resist rolls are honoured via rollCcResist so bosses
    // with high stun-resist still ignore the stun.
    if (h._magmaShieldActive && attacker?.active && dmg > 0) {
      const stunMs = (h._magmaShieldStunMs as number) || 500;
      applyDebuff(attacker.enemyRef, { type: 'stun', value: 1, duration: stunMs, remaining: stunMs });
      this.showDamageNumber(attacker.x, attacker.y - 20, 'STUNNED', '#f97316', '11px');
      this.flashSprite(attacker);
    }
    // Ironheld Unyielding Bulwark — when an incoming attack would exceed the
    // current shield, gain a 2 s, 15 % DR window. 12 s internal cooldown so
    // it can't trivialise sustained damage.
    if (this.hasArmoryPassive(hs.heroRef, 'passive_ironheld_unyielding_bulwark') && dmg > 0) {
      const cdExpiry = (h._unyieldingCdExpiry as number) ?? 0;
      const shield = h._shieldHp ?? 0;
      if (now >= cdExpiry && dmg > shield) {
        h._unyieldingDrUntil = now + 2000;
        h._unyieldingCdExpiry = now + 12000;
        this.showDamageNumber(hs.x, hs.y - 30, 'ᛟ BULWARK', '#a8d8ff', '11px');
      }
    }
    // Apply Unyielding Bulwark DR window if active.
    if (dmg > 0 && now < ((h._unyieldingDrUntil as number) ?? 0)) {
      dmg = Math.max(1, Math.round(dmg * 0.85));
    }
    // Ironheld Sanctified Duty — when ANOTHER ally hero has Challenge active
    // (taunt buff flag), this hero gets +10 % DR. The taunt-active flag is
    // set on the tank when Challenge fires; expires automatically with the buff.
    if (this.hasArmoryPassive(hs.heroRef, 'passive_ironheld_sanctified_duty') === false && dmg > 0) {
      const tauntActive = this.heroSprites.some((o) => {
        if (o === hs || o.heroRef.isDead) return false;
        if (!this.hasArmoryPassive(o.heroRef, 'passive_ironheld_sanctified_duty')) return false;
        const tEnd = (o.heroRef as any)._tauntActiveUntil as number | undefined;
        return tEnd !== undefined && now < tEnd;
      });
      if (tauntActive) dmg = Math.max(1, Math.round(dmg * 0.90));
    }
    // Ironheld Aegis of Light — first time HP would drop below 30 % per
    // encounter, grant a (100 + SP) HP shield for 4 s. Resolved BEFORE the
    // shield-absorb branch so the new shield can soak the incoming hit.
    if (this.hasArmoryPassive(hs.heroRef, 'passive_ironheld_aegis_of_light') && dmg > 0) {
      const maxHp = hs.heroRef.stats.maxHp ?? 1;
      const projectedHp = hs.heroRef.stats.hp - dmg;
      if (!h._aegisOfLightUsed && projectedHp < maxHp * 0.30) {
        h._aegisOfLightUsed = true;
        const sp = this.effStats(hs.heroRef).spellPower ?? 0;
        const shieldHp = 100 + sp;
        h._shieldHp = Math.max(h._shieldHp ?? 0, shieldHp);
        h._shieldMaxHp = Math.max(h._shieldMaxHp ?? 0, shieldHp);
        this.showDamageNumber(hs.x, hs.y - 40, `ᛞ +${shieldHp} aegis`, '#f0c040', '12px');
        this.time.delayedCall(4000, () => {
          if ((hs.heroRef as any)._shieldHp > 0) {
            (hs.heroRef as any)._shieldHp = Math.max(0, (hs.heroRef as any)._shieldHp - shieldHp);
            if ((hs.heroRef as any)._shieldHp <= 0) (hs.heroRef as any)._shieldMaxHp = 0;
          }
        });
      }
    }

    // Row-based incoming damage reduction (vanguard: -5%)
    const incomingMult = this.getActiveIncMult(hs.getData('slotIdx') as number | undefined);
    if (incomingMult !== 1 && dmg > 0) {
      dmg = Math.max(1, Math.round(dmg * incomingMult));
    }
    // v2 talent tree (WARD branch): flat damage reduction + front-row defense bonus
    // + Last Vow conditional DR when hero is below 30 % HP + Ironhide vs-boss DR.
    if (dmg > 0) {
      const pbWard = getPassiveBonuses(this.gsm.player.passiveTree ?? {});
      let v2DrMult = 1;
      if (pbWard.drPct > 0) v2DrMult *= (1 - Math.min(0.5, pbWard.drPct));
      if (pbWard.lastVowDrPct > 0) {
        const maxHp = hs.heroRef.stats.maxHp ?? 1;
        if ((hs.heroRef.stats.hp ?? 0) / maxHp < 0.30) {
          v2DrMult *= (1 - Math.min(0.5, pbWard.lastVowDrPct));
        }
      }
      // Ironhide — flat % DR but only against boss-tier attackers.
      if (pbWard.bossDrPct > 0 && attacker?.enemyRef.tier === 'boss') {
        v2DrMult *= (1 - Math.min(0.5, pbWard.bossDrPct));
      }
      // Front-row +X% defense — convert into an incoming-damage modifier so it stacks
      // multiplicatively with the existing slot row mult (no re-running the combat
      // formula). Only applies if hero sits in a front-row slot.
      if (pbWard.frontDefPct > 0) {
        const slotIdx = hs.getData('slotIdx') as number | undefined;
        if (slotIdx !== undefined && this.isFrontRow(slotIdx)) {
          // Treat +X% defense as ~X% damage reduction for simplicity (defense
          // → damage reduction is a non-linear curve; this is a soft proxy).
          v2DrMult *= (1 - Math.min(0.4, pbWard.frontDefPct * 0.6));
        }
      }
      if (v2DrMult < 1) dmg = Math.max(1, Math.round(dmg * v2DrMult));
    }
    // v2 Thornwall — reflect a fraction of incoming melee damage back to attacker.
    if (attacker?.active && attacker.enemyRef.currentHp > 0 && dmg > 0
        && attacker.enemyRef.attackRange !== 'ranged') {
      const pbThorns = getPassiveBonuses(this.gsm.player.passiveTree ?? {}).thornsPct;
      if (pbThorns > 0) {
        const reflect = Math.max(1, Math.round(dmg * pbThorns));
        attacker.enemyRef.currentHp -= reflect;
        this.showDamageNumber(attacker.x, attacker.y - 30, `${reflect}`, '#7fdc6c', '11px');
        if (attacker.enemyRef.currentHp <= 0) this.killEnemy(attacker, hs);
      }
    }
    // Bear Form damage reduction (Thorne — druid)
    if (h._bearFormActive && dmg > 0) {
      const drPct = Math.min(0.8, h._bearFormDrPct || 0);
      dmg = Math.max(1, Math.round(dmg * (1 - drPct)));
    }
    // Lootfi Mountain Roots — after standing still 10 s+, +5 % DR.
    if (this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_mountain_roots') && dmg > 0) {
      const stillSince = (h._stillSinceMs as number) ?? Infinity;
      if (now - stillSince >= 10_000) {
        dmg = Math.max(1, Math.round(dmg * 0.95));
      }
    }
    // Aegis of Light — 5 s damage-reduction halo (50 %) on every hero after a cast.
    // Set by `castAegisOfLight`; consumed multiplicatively here so it stacks with
    // every other DR layer above. Auto-expires by timestamp comparison.
    const aegisUntil = (h._aegisDrUntil as number) ?? 0;
    if (aegisUntil > now && dmg > 0) {
      dmg = Math.max(1, Math.round(dmg * 0.5));
    }
    const hpBefore = hs.heroRef.stats.hp;
    const shield = h._shieldHp ?? 0;
    let hpDmg = 0;
    let blockedByShield = 0;
    let shieldJustBroke = false;
    if (shield > 0) {
      if (shield >= dmg) {
        h._shieldHp = shield - dmg;
        if (h._shieldHp <= 0) { h._shieldHp = 0; h._shieldMaxHp = 0; shieldJustBroke = true; }
        blockedByShield = dmg;
      } else {
        blockedByShield = shield;
        const remainder = dmg - shield;
        h._shieldHp = 0; h._shieldMaxHp = 0;
        shieldJustBroke = true;
        hs.heroRef.stats.hp -= remainder;
        hpDmg = remainder;
      }
    } else {
      hs.heroRef.stats.hp -= dmg;
      hpDmg = dmg;
    }

    // ── Valka Counter-Strike (passive) ────────────────────────────────────
    // 30 % base chance on melee hit taken to retaliate at the attacker.
    // Base damage 80 % ATK (120 % with Plains Veteran). Counter-Strike talent:
    //   Rank 1 → +15 % chance (45 % melee)
    //   Rank 2 → unlock ranged-attacker coverage
    //   Rank 3-6 → +5 % chance each (70 % at rank 6)
    // Read rank directly because the chance curve is non-uniform per rank
    // (15 %/rank-1 + 5 %/rank past 2) and doesn't fit the helper's
    // single-perLevel split-talent shape.
    const hkForCounter = hs.heroRef.heroKey ?? hs.heroRef.heroClass;
    if (hkForCounter === 'valkyrie' && attacker?.active && attacker.enemyRef.currentHp > 0 && hpDmg > 0) {
      const counterRank = hs.heroRef.talentBonuses?.['counter_strike'] ?? 0;
      let counterRankBonus = 0;
      if (counterRank >= 1) counterRankBonus += 0.15;          // rank 1: +15 %
      if (counterRank >= 3) counterRankBonus += (counterRank - 2) * 0.05; // ranks 3-6: +5 % per
      const allowRanged = counterRank >= 2;                     // rank 2: ranged unlock
      const isMelee = attacker.enemyRef.attackRange === 'melee';
      const eligible = isMelee || allowRanged;
      const counterChance = 0.30 + counterRankBonus;
      if (eligible && Math.random() < counterChance) {
        const counterStats = this.effStats(hs.heroRef);
        const counterPct = this.hasArmoryPassive(hs.heroRef, 'passive_valka_plains_veteran') ? 1.08 : 0.72;
        const counterDmg = Math.max(1, Math.round(counterStats.attack * counterPct));
        attacker.enemyRef.currentHp -= counterDmg;
        this.showDamageNumber(attacker.x, attacker.y - 24, `⚔ ${counterDmg}`, '#c084fc', '14px');
        this.flashSprite(attacker);
        this.vfx.playValkaCounter(attacker.x, attacker.y, isMelee);
        if (attacker.enemyRef.currentHp <= 0) this.killEnemy(attacker, hs);
      }
    }
    // ── Valka Shieldbearer's Oath — reflect 10 % of blocked damage ───────
    if (blockedByShield > 0 && attacker?.active && attacker.enemyRef.currentHp > 0
        && (hs.heroRef.heroKey === 'valkyrie')
        && this.hasArmoryPassive(hs.heroRef, 'passive_valka_shieldbearers_oath')) {
      const reflectDmg = Math.max(1, Math.floor(blockedByShield * 0.10));
      attacker.enemyRef.currentHp -= reflectDmg;
      this.showDamageNumber(attacker.x, attacker.y - 30, `ᛟ ${reflectDmg}`, '#ffd060', '11px');
      if (attacker.enemyRef.currentHp <= 0) this.killEnemy(attacker, hs);
    }
    // ── Valka Last Rally — once per encounter, when HP drops below 30 %,
    // give allies a 10 s banner-grade buff (+15 % atk speed) plus +30 flat
    // defence via the `_lastRallyDefBonus` transient consumed by `effStats`.
    if (hs.heroRef.heroKey === 'valkyrie' && hpDmg > 0
        && this.hasArmoryPassive(hs.heroRef, 'passive_valka_last_rally')
        && !(hs.heroRef as any)._lastRallyUsed) {
      const maxHp = hs.heroRef.stats.maxHp ?? 1;
      if (hs.heroRef.stats.hp > 0 && hs.heroRef.stats.hp / maxHp < 0.30) {
        (hs.heroRef as any)._lastRallyUsed = true;
        const rallyDur = 10000;
        for (const ally of this.heroSprites) {
          if (ally.heroRef.isDead) continue;
          if (Phaser.Math.Distance.Between(ally.x, ally.y, hs.x, hs.y) > 200) continue;
          const a = ally.heroRef as any;
          a._warCryBuff = Math.max(a._warCryBuff ?? 0, 0.15);
          a._lastRallyDefBonus = 30;
          this.time.delayedCall(rallyDur, () => {
            if ((a._warCryBuff ?? 0) === 0.15) a._warCryBuff = 0;
            a._lastRallyDefBonus = 0;
          });
        }
        this.vfx.playValkaBanner(hs.x, hs.y);
        this.showDamageNumber(hs.x, hs.y - 50, 'ᛉ LAST RALLY', '#ffd060', '14px');
      }
    }

    // Morveth Bone Grip: on shield break, explode for 40 % of the absorbed
    // damage to enemies within 180 px. Only fires if the shield was from a
    // Bone-Shield cast with Bone Grip armed (flag set at cast time).
    if (shieldJustBroke && h._boneGripArmed && blockedByShield > 0) {
      h._boneGripArmed = false;
      const boomDmg = Math.max(1, Math.floor(blockedByShield * 0.40));
      for (const es of this.enemySprites) {
        if (!es.active || es.enemyRef.currentHp <= 0) continue;
        if (Phaser.Math.Distance.Between(hs.x, hs.y, es.x, es.y) > 180) continue;
        es.enemyRef.currentHp -= boomDmg;
        this.showDamageNumber(es.x, es.y - 20, `☠ ${boomDmg}`, '#a855f7', '11px');
        if (es.enemyRef.currentHp <= 0) this.killEnemy(es, hs);
      }
    }

    // ── Post-damage Elite Perks ────────────────────────────────────────────

    // Ironheld Retributive Faith — when the shield FULLY absorbed the hit,
    // reflect 10 % of the blocked damage back to the attacker.
    if (blockedByShield > 0 && hpDmg === 0 && attacker?.active
      && this.hasArmoryPassive(hs.heroRef, 'passive_ironheld_retributive_faith')) {
      const reflectDmg = Math.max(1, Math.floor(blockedByShield * 0.10));
      attacker.enemyRef.currentHp -= reflectDmg;
      this.showDamageNumber(attacker.x, attacker.y - 24, `ᚷ ${reflectDmg}`, '#a8d8ff', '11px');
      if (attacker.enemyRef.currentHp <= 0 && this.heroSprites[0]) this.killEnemy(attacker, this.heroSprites[0]);
    }

    if (hpDmg > 0) {
      // Goomar: Second Wind (already wired — first <20 % HP cross / wave heal).
      if (this.hasArmoryPassive(hs.heroRef, 'passive_goomar_second_wind')) {
        const maxHp = hs.heroRef.stats.maxHp ?? 1;
        const crossedThreshold = hpBefore > maxHp * 0.20 && hs.heroRef.stats.hp <= maxHp * 0.20;
        if (crossedThreshold && !h._secondWindUsed) {
          h._secondWindUsed = true;
          const heal = Math.floor(maxHp * 0.20);
          hs.heroRef.stats.hp = Math.min(maxHp, hs.heroRef.stats.hp + heal);
          this.showDamageNumber(hs.x, hs.y - 40, `ᚨ +${heal}`, '#8bde6b', '14px');
        }
      }
      // Lumiel: Martyrdom — already wired (20 % redirect to lowest-HP ally).
      if (this.hasArmoryPassive(hs.heroRef, 'passive_lumiel_martyrdom')) {
        const healAmt = Math.floor(hpDmg * 0.20);
        if (healAmt > 0) {
          const ally = this.heroSprites
            .filter((o) => o !== hs && !o.heroRef.isDead)
            .sort((a, b) => (a.heroRef.stats.hp / Math.max(1, a.heroRef.stats.maxHp))
                          - (b.heroRef.stats.hp / Math.max(1, b.heroRef.stats.maxHp)))[0];
          if (ally) {
            const aMax = ally.heroRef.stats.maxHp ?? 1;
            ally.heroRef.stats.hp = Math.min(aMax, ally.heroRef.stats.hp + healAmt);
            this.showDamageNumber(ally.x, ally.y - 40, `ᛟ +${healAmt}`, '#f0c040', '12px');
          }
        }
      }
      // Ironheld Bulwark of Devotion (reflect) — 5 % of HP damage taken back
      // to the attacker.
      if (attacker?.active
        && this.hasArmoryPassive(hs.heroRef, 'passive_ironheld_bulwark_of_devotion')) {
        const reflectDmg = Math.max(1, Math.floor(hpDmg * 0.05));
        attacker.enemyRef.currentHp -= reflectDmg;
        this.showDamageNumber(attacker.x, attacker.y - 30, `ᛒ ${reflectDmg}`, '#c0d0e0', '10px');
        if (attacker.enemyRef.currentHp <= 0 && this.heroSprites[0]) this.killEnemy(attacker, this.heroSprites[0]);
      }
      // Ironheld Retaliatory Strike — gives the tank a real damage hook. When
      // struck by a melee attacker, 25 % chance to swing back for 1.5× his
      // effective ATK (mitigated by the attacker's defense). Ranged attackers
      // are out of melee reach so they skip the proc.
      if (attacker?.active
        && attacker.enemyRef.attackRange !== 'ranged'
        && this.hasArmoryPassive(hs.heroRef, 'passive_ironheld_retaliatory_strike')
        && Math.random() < 0.25) {
        const atk = this.effStats(hs.heroRef).attack;
        const raw = Math.round(atk * 1.5);
        const retalDmg = Math.max(1, Math.round(raw * (1 - calculateMitigation(attacker.enemyRef.defense))));
        attacker.enemyRef.currentHp -= retalDmg;
        this.showDamageNumber(attacker.x, attacker.y - 38, `ᚦ ${retalDmg}`, '#f0c040', '11px');
        this.flashSprite(attacker);
        if (attacker.enemyRef.currentHp <= 0) this.killEnemy(attacker, hs);
      }
      // Zyphara Mana Siphon — being hit restores 1 % max mana, capped 1/sec.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_zyphara_mana_siphon')) {
        const lastTick = (h._manaSiphonLastMs as number) ?? 0;
        if (now - lastTick >= 1000) {
          const maxMp = hs.heroRef.stats.maxMp ?? 0;
          const restore = Math.max(1, Math.floor(maxMp * 0.01));
          hs.heroRef.stats.mp = Math.min(maxMp, (hs.heroRef.stats.mp ?? 0) + restore);
          h._manaSiphonLastMs = now;
        }
      }
      // Hero Armory — Kael Phantom Reflexes: while Evasion is active, each
      // hit Kael takes adds a 5%-damage stack (5s lifetime). Capped at 4 live
      // stacks = +20% total — additional hits don't push past the cap.
      if (h._evasionActive
        && this.hasArmoryPassive(hs.heroRef, 'passive_kael_phantom_reflexes')) {
        const stacks = ((h._phantomReflexStacks as { exp: number }[] | undefined) ?? [])
          .filter((s) => s.exp > now);
        if (stacks.length < 4) {
          stacks.push({ exp: now + 5000 });
          this.showDamageNumber(hs.x, hs.y - 30, `ᛒ +${5 * stacks.length}%`, '#a8d8ff', '10px');
        }
        h._phantomReflexStacks = stacks;
      }
      // Lootfi Dwarven Resolve — on hit, 4 % chance to stun the attacker for
      // 1 s with a 1.5 s per-attacker internal cooldown.
      if (attacker?.active
        && this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_dwarven_resolve')) {
        const lastStun = (attacker.getData('dwarvenResolveCd') as number | undefined) ?? 0;
        if (now - lastStun >= 1500 && Math.random() < 0.04) {
          applyDebuff(attacker.enemyRef, { type: 'stun', value: 1, duration: 1000, remaining: 1000 });
          attacker.setData('dwarvenResolveCd', now);
          this.showDamageNumber(attacker.x, attacker.y - 38, 'ᛝ STUN', '#fbbf24', '11px');
        }
      }
      // Lootfi Stout Heart — first lethal hit each encounter leaves Lootfi at
      // 1 HP instead, with a 120 s post-trigger cooldown to prevent chaining.
      if (this.hasArmoryPassive(hs.heroRef, 'passive_lootfi_stout_heart')) {
        const cdExpiry = (h._stoutHeartCdExpiry as number) ?? 0;
        if (hs.heroRef.stats.hp <= 0 && !h._stoutHeartUsedInWave && now >= cdExpiry) {
          h._stoutHeartUsedInWave = true;
          h._stoutHeartCdExpiry = now + 120_000;
          hs.heroRef.stats.hp = 1;
          hpDmg = hpBefore - 1;
          this.showDamageNumber(hs.x, hs.y - 40, 'ᚦ STOUT HEART', '#fbbf24', '13px');
        }
      }
    }

    this.maybeTriggerDeathsPromise();
    return hpDmg;
  }

  /** Clear run-scoped per-encounter passive flags. Called at wave-start so
   *  once-per-encounter procs (Second Wind, Aegis Shield, Divine Intervention)
   *  refresh for each new fight. */
  private resetPerEncounterArmoryFlags(): void {
    for (const hs of this.heroSprites) {
      const h = hs.heroRef as any;
      h._secondWindUsed = false;
      h._aegisShieldUsed = false;
      h._divineInterventionUsed = false;
      h._aegisOfLightUsed = false;
      h._stoutHeartUsedInWave = false;
      h._elementalInsightUsed = false;
      // v2 talent: Last Breath is now per-RUN (not per-encounter) — cleared in resetRun only.
    }
  }

  /** Prism of Splitting sigil: when a single-target spell hits its primary target, also strike
   *  the nearest OTHER enemy for 30% of the damage. Main-hero-only combat behavior. */
  private maybeTriggerPrismSplit(caster: HeroSprite, primary: EnemySprite, damage: number): void {
    if (damage <= 0) return;
    if (!this.getSigilEffects().specials.has('prism_of_splitting')) return;
    if (!this.isMainHero(caster)) return;
    const others = this.enemySprites.filter((e) => e.active && e !== primary && e.enemyRef.currentHp > 0);
    if (others.length === 0) return;
    // Pick nearest secondary target by distance to primary.
    let best: EnemySprite = others[0];
    let bestDistSq = Infinity;
    for (const e of others) {
      const dx = e.x - primary.x, dy = e.y - primary.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDistSq) { bestDistSq = d2; best = e; }
    }
    const splash = Math.max(1, Math.floor(damage * 0.3));
    best.enemyRef.currentHp -= splash;
    this.showDamageNumber(best.x, best.y - 20, `${splash}`, '#a78bfa', '12px');
    this.flashSprite(best);
    if (best.enemyRef.currentHp <= 0) this.killEnemy(best, caster);
  }

  /** Sigil of the Echo: repeat the last single-target spell hit 1s later at 20% damage.
   *  Main-hero-only combat behavior. Skips the repeat if the original target has died. */
  private maybeTriggerEcho(caster: HeroSprite, primary: EnemySprite, damage: number): void {
    if (damage <= 0) return;
    if (!this.getSigilEffects().specials.has('sigil_of_echo')) return;
    if (!this.isMainHero(caster)) return;
    const echoDmg = Math.max(1, Math.floor(damage * 0.2));
    this.time.delayedCall(1000, () => {
      if (!primary.active || primary.enemyRef.currentHp <= 0) return;
      primary.enemyRef.currentHp -= echoDmg;
      this.showDamageNumber(primary.x, primary.y - 30, `${echoDmg} ECHO`, '#c084fc', '11px');
      this.flashSprite(primary);
      if (primary.enemyRef.currentHp <= 0 && caster.active) this.killEnemy(primary, caster);
    });
  }

  /** Convenience: fire both Prism + Echo after a single-target spell resolves. */
  private applySpellSigils(caster: HeroSprite, target: EnemySprite, damage: number): void {
    this.maybeTriggerPrismSplit(caster, target, damage);
    this.maybeTriggerEcho(caster, target, damage);
  }

  /**
   * Death's Promise sigil: when the MAIN (first-deployed) hero drops to ≤1 HP, heal for 50%
   * of max HP. Twice per run total (not per wave) — the counter lives on player state and
   * resets with resetRun(). Main-hero-only.
   */
  private maybeTriggerDeathsPromise(): void {
    if (!this.getSigilEffects().specials.has('deaths_promise')) return;
    const p = this.gsm.player as unknown as { _deathsPromiseUsesLeft?: number };
    if (p._deathsPromiseUsesLeft === undefined) p._deathsPromiseUsesLeft = 2;
    if (p._deathsPromiseUsesLeft <= 0) return;
    const mainHs = this.getMainHeroSprite();
    if (!mainHs) return;
    if (mainHs.heroRef.stats.hp <= 0 || mainHs.heroRef.stats.hp > 1) return;
    p._deathsPromiseUsesLeft -= 1;
    const maxHp = this.effStats(mainHs.heroRef).maxHp;
    const heal = Math.floor(maxHp * 0.50);
    mainHs.heroRef.stats.hp = Math.min(maxHp, mainHs.heroRef.stats.hp + heal);
    this.showDamageNumber(mainHs.x, mainHs.y - 40, `+${heal}`, '#f0c040', '16px');
    this.vfx.playHealGlow(mainHs.x, mainHs.y);
  }

  private applySpellReflect(target: HeroSprite, es: EnemySprite, dmg: number): void {
    if (!es.active || es.enemyRef.currentHp <= 0) return; // already dead
    if ((target.heroRef as any)._reflectActive) {
      const reflectDmg = Math.floor(dmg * ((target.heroRef as any)._reflectPct || 0.4));
      es.enemyRef.currentHp -= reflectDmg;
      this.showDamageNumber(es.x, es.y - 20, `↩${reflectDmg}`, '#5090c0');
      if (es.enemyRef.currentHp <= 0 && es.active) { this.killEnemy(es, target); return; }
    }
    const thornsPct = getTomeBonus(this.gsm.player.tomes, 'thorns');
    const thornsDmgSpell = thornsPct > 0 ? Math.max(1, Math.floor(dmg * thornsPct)) : 0;
    if (thornsDmgSpell > 0) {
      es.enemyRef.currentHp -= thornsDmgSpell;
      this.showDamageNumber(es.x, es.y - 30, `↩${thornsDmgSpell}`, '#be123c');
      if (es.enemyRef.currentHp <= 0 && es.active) { this.killEnemy(es, target); return; }
    }
  }

  /**
   * Returns the faction damage multiplier from the attacking hero's trophy.
   * e.g. 1.55 for "beasts" trophy vs a beast enemy, 1.0 otherwise.
   */
  private getTrophyFactionMult(heroRef: Hero, faction: string): number {
    const eff = heroRef.equipment.reserved?.specialEffect;
    if (!eff) return 1;
    if (eff.includes('beasts') && faction === 'beast') return 1.55;
    if (eff.includes('undead') && faction === 'undead') return 1.55;
    if (eff.includes('orcs') && faction === 'orc') return 1.55;
    if (eff.includes('demons') && faction === 'demon') return 1.55;
    if (eff.includes('dragons') && faction === 'dragon') return 1.40;
    return 1;
  }

  /** Refresh trophy buff icons — delegates to refreshConsumableIcons (unified buff row) */
  private refreshTrophyDisplay(): void {
    this.lastConsumableSnapshot = ''; // force refresh so trophy changes are picked up
    this.refreshConsumableIcons();
  }

  // ── Cheat API ──────────────────────────────────────────────────────────────────

  /** Cheat: set all enemy HP to 0 — the update loop will call killEnemy() on the next tick. */
  public killAllEnemies(): void {
    for (const es of this.enemySprites) {
      if (es.active && es.enemyRef.currentHp > 0) {
        es.enemyRef.currentHp = 0;
      }
    }
  }

  /** Cheat: set the speed multiplier and matching penalty flag. */
  public setSpeedMultiplier(n: number): void {
    this.speedMultiplier = n;
    this.speedPenaltyActive = n > 1;
  }

  private flashSprite(sprite: Phaser.GameObjects.Sprite): void {
    // Defensive guard — flashSprite can be called from delayed-call /
    // animationcomplete callbacks (e.g. Amazon's leap strike) where the
    // target may have been destroyed mid-flight by another damage source
    // (DoT tick, hero auto-attack, focus AoE). On destroy Phaser clears
    // the GameObject's `anims` and `texture` refs, so accessing them
    // throws "Cannot read properties of undefined". Bail out cleanly
    // rather than throwing a runtime error and tearing down the scene.
    if (!sprite || !sprite.scene || !sprite.active || !sprite.anims || !sprite.texture) return;
    // Play hurt animation if available, then resume idle
    const hurtKey = `${sprite.texture.key}_hurt`;
    const curKey = sprite.anims.currentAnim?.key ?? '';
    // Don't let the hurt anim stomp an in-flight attack swing. Wolves
    // bite for 0.5 s but take damage every ~200 ms in normal combat;
    // playing hurt would overwrite the attack anim before any frame of
    // the bite ever renders, so the player never sees the attack pose.
    // The alpha-yoyo flash below + the damage number still convey the
    // hit, so the visual feedback isn't lost — we just preserve the
    // attack swing through incoming damage. Applies to any sprite with
    // an `_attack` anim playing (heroes mid-swing too).
    const isMidAttack = curKey.includes('_attack');
    if (!isMidAttack && this.anims.exists(hurtKey) && curKey !== hurtKey) {
      sprite.play(hurtKey);
      const baseKey = sprite.texture.key;
      sprite.once('animationcomplete', () => {
        if (!sprite.active) return;
        // For tank: if still walking, return to walk; otherwise idle
        const heroRef = (sprite as any).heroRef;
        if (heroRef?.heroClass === 'tank' && (heroRef as any)._challengeWalking) {
          if (this.anims.exists(`${baseKey}_walk`)) sprite.play(`${baseKey}_walk`);
        } else {
          const idleKey = `${baseKey}_idle`;
          if (this.anims.exists(idleKey)) sprite.play(idleKey);
        }
      });
    }
    this.tweens.add({ targets: sprite, alpha: 0.5, duration: 60, yoyo: true });
  }

  private showLootIcon(x: number, y: number, textureKey: string): void {
    const icon = this.add.sprite(x, y, textureKey).setScale(0.5).setDepth(48);
    this.tweens.add({
      targets: icon, y: y - 20, alpha: 0, duration: 2000,
      ease: 'Sine.easeOut', onComplete: () => icon.destroy(),
    });
  }

  /** High-rarity loot flourish — fires for legendary + unique drops.
   *  Goal: "noticeable but not intrusive" per user — the player sees the
   *  drop is special without losing any control of the scene. Layout:
   *    1. Expanding ring + spark burst at the drop position (rarity colour).
   *    2. Larger floating item banner (rarity-coloured name + slot tag)
   *       that drifts up over 2.5 s and fades — visible mid-screen height
   *       so the player can read it before it slides into the bag.
   *  Uniques get a slightly bigger ring + a second pulse so they read
   *  louder than legendaries without forcing a modal popup. */
  private playRareLootFlourish(x: number, y: number, item: import('@roe/shared').Equipment): void {
    const isUnique = item.rarity === 'unique';
    const ringColor = isUnique ? 0xef4444 : 0xf97316; // red for unique, orange for legendary
    const ringRadius = isUnique ? 90 : 70;
    const ringDuration = isUnique ? 720 : 560;
    // Outer ring
    const ring = this.add.graphics().setDepth(52);
    ring.lineStyle(3, ringColor, 0.95);
    ring.strokeCircle(0, 0, ringRadius);
    ring.setPosition(x, y).setScale(0.05).setAlpha(0.95);
    this.tweens.add({
      targets: ring, scaleX: 1, scaleY: 1, alpha: 0,
      duration: ringDuration, ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
    // Inner pulse — uniques only, gives a layered "double-ring" tell.
    if (isUnique) {
      this.time.delayedCall(140, () => {
        const inner = this.add.graphics().setDepth(52);
        inner.lineStyle(2, 0xffd060, 0.85);
        inner.strokeCircle(0, 0, ringRadius * 0.55);
        inner.setPosition(x, y).setScale(0.05).setAlpha(0.85);
        this.tweens.add({
          targets: inner, scaleX: 1, scaleY: 1, alpha: 0,
          duration: 540, ease: 'Cubic.easeOut',
          onComplete: () => inner.destroy(),
        });
      });
    }
    // Spark burst — rarity-tinted particles flying outward.
    if (this.textures.exists('projectile')) {
      const particleTints = isUnique
        ? [0xef4444, 0xff8a3c, 0xffd060]
        : [0xf97316, 0xffd060, 0xffe8a0];
      const emitter = this.add.particles(x, y, 'projectile', {
        speed: { min: 90, max: 200 }, angle: { min: 0, max: 360 },
        scale: { start: 0.85, end: 0 }, alpha: { start: 0.95, end: 0 },
        lifespan: isUnique ? 620 : 520,
        tint: particleTints,
        emitting: false,
      }).setDepth(52);
      emitter.explode(isUnique ? 24 : 16);
      this.time.delayedCall(900, () => emitter.destroy());
    }
    // Floating banner — drop name + slot tag. Drifts up over 2.5 s, then
    // fades. Anchored above the drop so it doesn't overlap the loot icon
    // (which is already drifting up too).
    const colorHex = isUnique ? '#ef4444' : '#f97316';
    const slotLabel = item.slot.startsWith('relic') ? 'Relic'
      : item.slot === 'armor' ? 'Armor' : 'Weapon';
    const rarityLabel = isUnique ? 'UNIQUE' : 'LEGENDARY';
    const bannerY = Math.max(80, y - 60);
    const bannerBg = this.add.rectangle(x, bannerY, 200, 36, 0x0d0a08, 0.9)
      .setStrokeStyle(2, isUnique ? 0xef4444 : 0xf97316, 1)
      .setDepth(52).setAlpha(0.95);
    const bannerTitle = this.add.text(x, bannerY - 7, `✦ ${rarityLabel} ✦`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: colorHex, fontStyle: 'bold',
      stroke: '#0a0402', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(53);
    const bannerName = this.add.text(x, bannerY + 7, `${item.name} · ${slotLabel}`, {
      fontSize: '11px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: colorHex, fontStyle: 'bold',
      stroke: '#0a0402', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(53);
    const banners: Phaser.GameObjects.GameObject[] = [bannerBg, bannerTitle, bannerName];
    this.tweens.add({
      targets: banners, y: '-=18', alpha: 0,
      duration: 2500, delay: 400, ease: 'Sine.easeOut',
      onComplete: () => banners.forEach((o) => o.destroy()),
    });
  }
}
